// 📊 NEW SEGMENTED DATABASE STRUCTURE
// Instead of one giant "master" document, we split data by time period
// This keeps each document < 1MB and makes the app infinitely scalable

// 📦 Velden die zelden veranderen → apart Firestore-document
// Dit halveert de grootte van het live-document
const STATIC_FIELDS = new Set([
  "staffList",
  "settings",
  "activityLibrary",
  "activityTemplates",
  "checklistTemplates",
  "springkussenTasks",
  "phoneDirectory",
  "manuals",
  "urencodes",
  "dienstTemplates",
  "staffSkills",
  "staffCharacters",
  "staffContractInfo",
]);

// 🛟 Namen die de FM BEWUST heeft verwijderd via de prullenbakknop.
// De staffList-merge verwijdert alleen nog namen die hier in staan. Zonder
// dit vangnet wiste elke tab met een verouderde lijst (PWA uit IndexedDB,
// automatische lastLogin-save bij inloggen) stilzwijgend de nieuwste
// collega's van de server — inclusief hun profielfoto.
const staffRemovalIntent = new Set();

// 🛟 Bestelling-IDs die BEWUST zijn verwijderd/gewist (knop of 2-weken auto-clear).
// De 'orders'-array werd voorheen bij elke save volledig overschreven door de
// lokale versie ("laatste schrijver wint voor de hele array"). Bestelde iemand
// vanaf een tab die de real-time update van een collega nog niet had ontvangen,
// dan verdween diens net-toegevoegde bestelling stilzwijgend bij de eerstvolgende
// save — dit was de oorzaak van "alle bestellingen zijn verdwenen". Nu wordt
// per bestelling-id gemerged (zelfde aanpak als staffList) en alleen bewust
// verwijderde ids verdwijnen echt.
const orderRemovalIntent = new Set();
const registerOrderRemoval = (id) => {
  if (id != null) orderRemovalIntent.add(id);
};

// ✅ PER-ITEM OPSLAAN — deze velden werden als geheel opgeslagen
// ("laatste schrijver wint"). Werkten twee collega's tegelijk, dan verdween
// het werk van de één bij de save van de ander: vinkjes (checklists),
// @meldingen (notifications), urencorrecties en springkussen-checks.
// Nu wordt per save bepaald welke items JIJ echt gewijzigd hebt (diff t.o.v.
// de huidige lokale stand) en worden alleen die op de server-versie gezet.
const idKey = (x) => (x && x.id != null ? String(x.id) : null);
const PATCH_FIELDS = {
  // sleutel = id + datum (zelfde taak-id komt op meerdere dagen voor)
  checklists: {
    key: (c) => (c && c.id != null ? `${c.id}|${c.date == null ? "" : c.date}` : null),
  },
  notifications: {
    key: idKey,
    // Twee collega's die dezelfde melding tegelijk lezen: beide "gelezen" bewaren
    merge: (srv, mine) =>
      Array.isArray(srv?.readBy) && Array.isArray(mine?.readBy)
        ? { ...mine, readBy: [...new Set([...srv.readBy, ...mine.readBy])] }
        : mine,
  },
  hourCorrections: { key: idKey },
  // { "YYYY-MM-DD": [check] } → plat gemaakt naar items met __d (datum)
  springkussenChecks: {
    key: (c) => (c && c.id != null ? `${c.__d}|${c.id}` : null),
    flatten: (obj) => {
      const out = [];
      for (const [d, arr] of Object.entries(isPlainObj(obj) ? obj : {})) {
        if (Array.isArray(arr)) arr.forEach((c) => c && out.push({ ...c, __d: d }));
      }
      return out;
    },
    unflatten: (items, base) => {
      const out = {};
      // datums met een afwijkende (niet-array) waarde ongemoeid laten
      for (const [d, v] of Object.entries(isPlainObj(base) ? base : {})) {
        if (!Array.isArray(v)) out[d] = v;
      }
      for (const it of items) {
        const { __d, ...c } = it;
        if (!out[__d] || !Array.isArray(out[__d])) out[__d] = [];
        out[__d].push(c);
      }
      return out;
    },
  },
};
const fieldPatches = {}; // veld → Map(sleutel → item | null)
for (const f of Object.keys(PATCH_FIELDS)) fieldPatches[f] = new Map();
const toItems = (field, val) => {
  const cfg = PATCH_FIELDS[field];
  if (cfg.flatten) return cfg.flatten(val);
  return Array.isArray(val) ? val : [];
};
const registerFieldDiff = (field, oldVal, newVal) => {
  const cfg = PATCH_FIELDS[field];
  const patches = fieldPatches[field];
  const oldMap = new Map();
  toItems(field, oldVal).forEach((c) => {
    const k = cfg.key(c);
    if (k) oldMap.set(k, c);
  });
  const newMap = new Map();
  toItems(field, newVal).forEach((c) => {
    const k = cfg.key(c);
    if (k) newMap.set(k, c);
  });
  for (const [k, c] of newMap) {
    const o = oldMap.get(k);
    if (!o || (o !== c && JSON.stringify(o) !== JSON.stringify(c))) patches.set(k, c);
  }
  for (const k of oldMap.keys()) {
    if (!newMap.has(k)) patches.set(k, null);
  }
};
const applyFieldPatches = (field, val) => {
  const cfg = PATCH_FIELDS[field];
  const patches = fieldPatches[field];
  if (patches.size === 0) return cfg.flatten ? (isPlainObj(val) ? val : {}) : Array.isArray(val) ? val : [];
  const base = toItems(field, val);
  const out = [];
  const seen = new Set();
  for (const c of base) {
    const k = cfg.key(c);
    if (k && patches.has(k)) {
      if (seen.has(k)) continue;
      seen.add(k);
      const pch = patches.get(k);
      if (pch) out.push(cfg.merge ? cfg.merge(c, pch) : pch);
    } else {
      out.push(c);
    }
  }
  for (const [k, pch] of patches) {
    if (!seen.has(k) && pch) out.push(pch);
  }
  return cfg.unflatten ? cfg.unflatten(out, val) : out;
};


// ═══════════════════════════════════════════════════════════════════
// 📦 DATA PRUNER — Houdt live doc onder ~600KB door oude data te archiveren
// ═══════════════════════════════════════════════════════════════════

// Datum-geindexeerde objecten (keys = YYYY-MM-DD)
const DATE_KEYED_FIELDS = [
  "workSchedule",
  "dailyStats",
  "roosterPrognose",
  "roosterDiensten",
  "dailyRoles",
  "dailySpringkussen",
  "dailyCrea",
  "dailyHotspot",
  "dailyFmPresent",
  "dailyDv",
  "handover",
  "planningStatus",
  "springkussenChecks",
  "timeClock",
  "dagbord",
];

// 🧩 DAGBORD / OPEN PUNTEN — velden die recursief (per sleutel) gemerged
// worden. Elk bericht, verslag, leesbevestiging en reactie heeft een eigen
// sleutel, dus twee collega's die tegelijk opslaan overschrijven elkaar nooit.
// Verwijderen gaat via een vlag (deleted: true), nooit via het weghalen van
// een sleutel — een merge kan alleen toevoegen/overschrijven.
const DEEP_MERGE_FIELDS = new Set(["dagbord", "openPunten", "feestdagen", "afscheid", "pietenservice"]);
// timeClock: per persoon per veld samenvoegen ({in, out, correctedBy, …}),
// zodat "afsluiten" van een medewerker een FM-correctie van de starttijd niet
// overschrijft (en andersom).
const mergeKlokDag = (base, patch) => {
  const out = { ...(base || {}) };
  for (const [naam, v] of Object.entries(patch || {})) {
    out[naam] =
      v && typeof v === "object" && !Array.isArray(v) && out[naam] && typeof out[naam] === "object"
        ? { ...out[naam], ...v }
        : v;
  }
  return out;
};
const isPlainObj = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v);
const deepMergePlain = (base, patch) => {
  if (!isPlainObj(base) || !isPlainObj(patch)) return patch;
  const out = { ...base };
  for (const [k, v] of Object.entries(patch)) {
    out[k] = isPlainObj(v) && isPlainObj(base[k]) ? deepMergePlain(base[k], v) : v;
  }
  return out;
};

// ⚡ SNELLE DAGLAAG ──────────────────────────────────────────────────────
// Het master-document is groot (~1 MB): elke wijziging moet eerst gelezen +
// geschreven worden en elke telefoon krijgt daarna het hele document opnieuw.
// Daarom schrijven we de "hete" dingen van vandaag (taken afvinken, in/uitklokken,
// dagbord) óók meteen naar een klein dagdocument (cp_dag/<datum>). Andere
// telefoons luisteren daar live op en zien de wijziging binnen een tel.
// Het master-document blijft de bron van waarheid (zelfde opslag als altijd);
// de daglaag wordt er alleen overheen gelegd zolang hij nieuwer is.
const DAG_HETE_DAGEN = () => {
  const d = new Date();
  const uit = [];
  for (const off of [-1, 0, 1]) {
    const x = new Date(d);
    x.setDate(d.getDate() + off);
    uit.push(toIsoDate(x));
  }
  return uit;
};
const tsMs = (v) =>
  v && typeof v.toMillis === "function" ? v.toMillis() : typeof v === "number" ? v : 0;
const zonderTs = (v) => {
  if (!isPlainObj(v)) return v;
  const { _ts, ...rest } = v; // eslint-disable-line no-unused-vars
  return rest;
};
// Bouw per dag de payload voor het dagdocument uit een save
const bouwDaglaag = (changes, checklistPatches) => {
  const heet = new Set(DAG_HETE_DAGEN());
  const vandaag = toIsoDate(new Date());
  const perDag = {};
  const zet = (dk, veld, sleutel, waarde) => {
    perDag[dk] = perDag[dk] || {};
    perDag[dk][veld] = perDag[dk][veld] || {};
    perDag[dk][veld][sleutel] = { ...removeUndefined(waarde), _ts: serverTimestamp() };
  };
  if (changes.checklists !== undefined && checklistPatches) {
    for (const [k, v] of checklistPatches) {
      const i = String(k).lastIndexOf("|");
      const id = String(k).slice(0, i);
      const datum = String(k).slice(i + 1);
      const dk = datum === "global" || !datum ? vandaag : datum;
      if (!heet.has(dk)) continue;
      zet(dk, "checklists", k, v ? v : { _weg: true, id, date: datum });
    }
  }
  for (const veld of ["timeClock", "dagbord"]) {
    const delta = changes[veld];
    if (!isPlainObj(delta)) continue;
    for (const [dk, perSleutel] of Object.entries(delta)) {
      if (!heet.has(dk) || !isPlainObj(perSleutel)) continue;
      for (const [sleutel, waarde] of Object.entries(perSleutel)) {
        if (isPlainObj(waarde)) zet(dk, veld, sleutel, waarde);
      }
    }
  }
  return perDag;
};
// Leg de daglaag over de master-data heen (alleen wat nieuwer is dan master)
const pasDaglaagToe = (data, dagDocs) => {
  if (!data || !dagDocs) return data;
  const masterT = tsMs(data._serverAt);
  let out = data;
  for (const [dk, dd] of Object.entries(dagDocs)) {
    if (!isPlainObj(dd)) continue;
    const nieuwer = (v) => isPlainObj(v) && tsMs(v._ts) > masterT;
    // Taken
    const cl = Object.entries(dd.checklists || {}).filter(([, v]) => nieuwer(v));
    if (cl.length) {
      const lijst = Array.isArray(out.checklists) ? [...out.checklists] : [];
      for (const [k, v] of cl) {
        const { _weg, ...item } = zonderTs(v);
        const i = String(k).lastIndexOf("|");
        const id = String(k).slice(0, i);
        const datum = String(k).slice(i + 1);
        const idx = lijst.findIndex(
          (c) => c && String(c.id) === id && String(c.date == null ? "" : c.date) === datum
        );
        if (_weg) {
          if (idx >= 0) lijst.splice(idx, 1);
        } else if (idx >= 0) {
          lijst[idx] = { ...lijst[idx], ...item };
        } else {
          lijst.push(item);
        }
      }
      out = { ...out, checklists: lijst };
    }
    // In-/uitklokken en dagbord
    for (const veld of ["timeClock", "dagbord"]) {
      const items = Object.entries(dd[veld] || {}).filter(([, v]) => nieuwer(v));
      if (!items.length) continue;
      const dag = { ...((out[veld] || {})[dk] || {}) };
      for (const [sleutel, v] of items) {
        dag[sleutel] = deepMergePlain(isPlainObj(dag[sleutel]) ? dag[sleutel] : {}, zonderTs(v));
      }
      out = { ...out, [veld]: { ...(out[veld] || {}), [dk]: dag } };
    }
  }
  return out;
};

// Arrays met een .date property (YYYY-MM-DD)
const DATE_ARRAY_FIELDS = [
  "schedule",
  "checklists",
  "characters",
  "qualityVisits",
  "hoursReports",
  "activityRatings",
  "guestFeedback",
  "bingoWinners",
  "hourCorrections",
  "shiftSwapRequests",
];

const PRUNE_DAYS = 30; // Houd 30 dagen in live doc, archiveer de rest

// 📚 PROGRAMMA ↔ BIBLIOTHEEK — teksten en foto staan maar één keer in de database.
// Bij het wegschrijven laten we deze velden weg bij ingeplande activiteiten als ze
// gelijk zijn aan de bibliotheek (zelfde titel); in de app vullen we ze weer aan.
// Wijkt een tekst af (bewust aangepast), of staat de activiteit niet in de
// bibliotheek, dan blijft hij gewoon bij de activiteit staan.
const SCHEDULE_LIB_VELDEN = ["photo", "infoText", "infoTextEn", "promoInfoNL", "promoInfoEN", "promoInfoIconen"];
const libNorm = (v) =>
  v == null || v === "" || (Array.isArray(v) && (v.length === 0 || v.every((x) => !x))) ? "" : JSON.stringify(v);
const libZoeker = (lib) => {
  const m = new Map();
  Object.values(lib || {}).forEach((lijst) => {
    if (!Array.isArray(lijst)) return;
    lijst.forEach((t) => {
      const k = String((t && (t.title || t.name)) || "").trim().toLowerCase();
      if (k && !m.has(k)) m.set(k, t);
    });
  });
  return m;
};
const scheduleZonderLibTeksten = (schedule, lib) => {
  if (!Array.isArray(schedule) || !lib) return schedule;
  const z = libZoeker(lib);
  if (!z.size) return schedule;
  return schedule.map((item) => {
    if (!item || !item.title) return item;
    const t = z.get(String(item.title).trim().toLowerCase());
    if (!t) return item;
    let uit = null;
    for (const f of SCHEDULE_LIB_VELDEN) {
      if (!(f in item)) continue;
      const v = libNorm(item[f]);
      if (v === "" || v === libNorm(t[f])) {
        if (!uit) uit = { ...item };
        delete uit[f];
      }
    }
    return uit || item;
  });
};
const scheduleMetLibTeksten = (schedule, lib) => {
  if (!Array.isArray(schedule) || !lib) return schedule;
  const z = libZoeker(lib);
  if (!z.size) return schedule;
  let gewijzigd = false;
  const uit = schedule.map((item) => {
    if (!item || !item.title) return item;
    const t = z.get(String(item.title).trim().toLowerCase());
    if (!t) return item;
    let nieuw = null;
    for (const f of SCHEDULE_LIB_VELDEN) {
      if (item[f] === undefined && t[f] !== undefined && t[f] !== null && t[f] !== "") {
        if (!nieuw) nieuw = { ...item };
        nieuw[f] = t[f];
      }
    }
    if (nieuw) gewijzigd = true;
    return nieuw || item;
  });
  return gewijzigd ? uit : schedule;
};

// 📦 Grote velden met veel verleden-data korter in het live-document houden.
// Niets verdwijnt: het staat in het maand-archief en wordt bij het openen van
// de app op de achtergrond weer ingeladen (zie laadArchiefMaand), zodat alle
// schermen dezelfde 30 dagen blijven zien. Toekomstige data blijft altijd live.
const FIELD_KEEP_DAYS = {
  checklists: 7,
  staffAvailability: 7,
  staffAvailabilityLocks: 7,
  schedule: 14,
  notifications: 14,
  // Oude overdracht (vervangen door het Dagbord): alleen nog naslag
  handover: 7,
};
// Schermen die genoeg hebben aan het live-document (geen archief nodig)
const LICHTE_VIEWS = new Set([
  "login", "force_pin_change", "dagbord", "rooster", "planner", "schedule",
  "chat", "checks", "phonelist", "handboek", "user_settings", "change_pin",
  "springkussen", "orders", "bingo", "promo_screen", "promo_designer", "promo_bediening", "dj_night", "afscheid", "polaroids", "pietenservice",
]);
const LIVE_MIN_KEEP_DAYS = Math.min(PRUNE_DAYS, ...Object.values(FIELD_KEEP_DAYS));

// 📅 Vanaf deze datum moet ALLE beschikbaarheid door de FM worden goedgekeurd
// — ook data die al bestond vóór de goedkeuringsworkflow (die kreeg tot nu
// toe impliciet "approved" omdat er geen status-veld op stond).
const APPROVAL_CUTOFF_DATE = "2026-09-01";

// 📦 Geneste datum-objecten: { outerKey: { "YYYY-MM-DD": val } }
// Deze velden werden voorheen nooit gesnoeid en vulden het live-doc.
const NESTED_DATE_KEYED_FIELDS = [
  "extraUren",
  "staffAvailability",
  "staffAvailabilityLocks",
];

// Arrays met een afwijkend datumveld (geen .date / .dateKey)
const CUSTOM_DATE_ARRAY_FIELDS = {
  notifications: "createdAt",
  // ⚠️ Gebruikt "dateKey" (ISO YYYY-MM-DD), NIET "date": dat laatste is de
  // Nederlandse weergavestring ("wo 30 sep", geen jaar) en was te kort om
  // hier als datum herkend te worden — bestellingen werden dus nooit
  // gearchiveerd en het live-document bleef onnodig groeien.
  orders: "dateKey",
};

// Max grootte per archief-shard (veilig onder de 1 MB Firestore-limiet)
const ARCHIVE_SHARD_MAX = 800000;

// "2025-03-14" -> "2025-03"
const getMonthKeyFromDateStr = (dateStr) => dateStr.substring(0, 7);

// 🔢 Firestore heeft naast 1 MB ook een limiet van 40.000 indexregels per
// document ("too many index entries"). Elke losse waarde in een (geneste) map
// telt 2× (oplopend + aflopend), elk array-element 1× (array-contains).
// Ruwe schatting (iets aan de hoge kant) om ruim onder die grens te blijven.
const INDEX_LIMIET_VEILIG = 28000;
const telIndexRegels = (v, diepte = 0) => {
  if (Array.isArray(v)) return v.length;
  if (v && typeof v === "object") {
    if (diepte > 20 || Object.getPrototypeOf(v) !== Object.prototype) return 2;
    let n = diepte > 0 ? 2 : 0; // de map zelf telt ook mee
    for (const k in v) n += telIndexRegels(v[k], diepte + 1);
    return n;
  }
  return v === undefined ? 0 : 2;
};

// Verdeel een object over shards van < maxSize (en < INDEX_LIMIET_VEILIG
// indexregels), hele top-level velden bij elkaar.
const splitObjectIntoShards = (obj, maxSize = ARCHIVE_SHARD_MAX) => {
  const shards = [];
  let current = {};
  let currentSize = 2; // "{}"
  let currentIdx = 0;
  for (const [key, val] of Object.entries(obj)) {
    const piece = new Blob([JSON.stringify({ [key]: val })]).size;
    const idx = telIndexRegels(val);
    if (
      (currentSize + piece > maxSize || currentIdx + idx > INDEX_LIMIET_VEILIG) &&
      Object.keys(current).length > 0
    ) {
      shards.push(current);
      current = {};
      currentSize = 2;
      currentIdx = 0;
    }
    current[key] = val;
    currentSize += piece;
    currentIdx += idx;
  }
  if (Object.keys(current).length > 0) shards.push(current);
  return shards.length ? shards : [{}];
};

// Sleutel van een archief-item. Checklist-items hergebruiken hetzelfde taak-id
// op elke dag, dus daar hoort de datum bij de sleutel (anders ging de
// afvinkhistorie van alle dagen na de eerste verloren).
const archiveItemKey = (field, item) => {
  if (!item || item.id == null) return null;
  if (field === "checklists") return `${item.id}|${item.date == null ? "" : item.date}`;
  return String(item.id);
};

// Voeg nieuw te archiveren data verliesvrij samen met bestaande maand-data.
// Bij hetzelfde item wint de nieuwe (inkomende) versie.
const mergeArchiveInto = (existing, incoming) => {
  const out = { ...existing };
  for (const [field, val] of Object.entries(incoming)) {
    if (NESTED_DATE_KEYED_FIELDS.includes(field)) {
      const base = { ...(out[field] || {}) };
      for (const [outerKey, inner] of Object.entries(val || {})) {
        base[outerKey] = { ...(base[outerKey] || {}), ...inner };
      }
      out[field] = base;
    } else if (DATE_KEYED_FIELDS.includes(field)) {
      out[field] = { ...(out[field] || {}), ...val };
    } else if (Array.isArray(val)) {
      const byKey = new Map();
      const zonderId = [];
      const seenJson = new Set();
      for (const i of [...(Array.isArray(out[field]) ? out[field] : []), ...val]) {
        const k = archiveItemKey(field, i);
        if (k == null) {
          const j = JSON.stringify(i);
          if (!seenJson.has(j)) {
            seenJson.add(j);
            zonderId.push(i);
          }
        } else {
          byKey.set(k, i); // latere (inkomende) versie overschrijft
        }
      }
      out[field] = [...byKey.values(), ...zonderId];
    } else if (val && typeof val === "object") {
      out[field] = { ...(out[field] || {}), ...val };
    } else {
      out[field] = val;
    }
  }
  return out;
};

// Geeft YYYY-QN terug voor een YYYY-MM-DD string
const getQuarterKeyFromDateStr = (dateStr) => {
  const year = parseInt(dateStr.substring(0, 4));
  const month = parseInt(dateStr.substring(5, 7));
  const q = Math.floor((month - 1) / 3) + 1;
  return `${year}-Q${q}`;
};

// Splitst data in live (recent) + archief per kwartaal
// Puur: muteert niets, geeft { liveData, archiveByMonth } terug
const pruneDataForLiveDoc = (data, cutoffDays = PRUNE_DAYS) => {
  const cutoffFor = (field) => {
    const days = Math.min(cutoffDays, FIELD_KEEP_DAYS[field] ?? cutoffDays);
    const c = new Date();
    c.setDate(c.getDate() - days);
    return c.toISOString().split("T")[0];
  };

  const liveData = { ...data };
  const archiveByMonth = {};
  const ensureMonth = (mKey) => {
    if (!archiveByMonth[mKey]) archiveByMonth[mKey] = {};
    return archiveByMonth[mKey];
  };

  // 1. Platte datum-objecten: { "YYYY-MM-DD": val }
  for (const field of DATE_KEYED_FIELDS) {
    const obj = data[field];
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) continue;
    const cutoffStr = cutoffFor(field);
    const live = {};
    for (const [dateKey, val] of Object.entries(obj)) {
      if (dateKey.length >= 10 && dateKey < cutoffStr) {
        const m = ensureMonth(getMonthKeyFromDateStr(dateKey));
        if (!m[field]) m[field] = {};
        m[field][dateKey] = val;
      } else {
        live[dateKey] = val;
      }
    }
    liveData[field] = live;
  }

  // 2. Geneste datum-objecten: { outerKey: { "YYYY-MM-DD": val } }
  for (const field of NESTED_DATE_KEYED_FIELDS) {
    const obj = data[field];
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) continue;
    const cutoffStr = cutoffFor(field);
    const live = {};
    for (const [outerKey, inner] of Object.entries(obj)) {
      if (!inner || typeof inner !== "object" || Array.isArray(inner)) {
        live[outerKey] = inner;
        continue;
      }
      const liveInner = {};
      for (const [dateKey, val] of Object.entries(inner)) {
        if (dateKey.length >= 10 && dateKey < cutoffStr) {
          const m = ensureMonth(getMonthKeyFromDateStr(dateKey));
          if (!m[field]) m[field] = {};
          if (!m[field][outerKey]) m[field][outerKey] = {};
          m[field][outerKey][dateKey] = val;
        } else {
          liveInner[dateKey] = val;
        }
      }
      live[outerKey] = liveInner;
    }
    liveData[field] = live;
  }

  // 3. Datum-arrays (item.date of item.dateKey)
  for (const field of DATE_ARRAY_FIELDS) {
    const arr = data[field];
    if (!Array.isArray(arr)) continue;
    const cutoffStr = cutoffFor(field);
    const live = [];
    for (const item of arr) {
      const dateStr = item.date || item.dateKey;
      // Bingo-winnaar zonder verzonden voucher blijft live tot CO hem afhandelt
      if (field === "bingoWinners" && item && !item.voucherSent && item.prijs !== "voucher25") {
        live.push(item);
        continue;
      }
      if (dateStr && dateStr.length >= 10 && dateStr < cutoffStr) {
        const m = ensureMonth(getMonthKeyFromDateStr(dateStr));
        if (!m[field]) m[field] = [];
        m[field].push(item);
      } else {
        live.push(item);
      }
    }
    liveData[field] = live;
  }

  // 4. Datum-arrays met afwijkend datumveld (notifications.createdAt, orders.date)
  for (const [field, dateField] of Object.entries(CUSTOM_DATE_ARRAY_FIELDS)) {
    const arr = data[field];
    if (!Array.isArray(arr)) continue;
    const cutoffStr = cutoffFor(field);
    const live = [];
    for (const item of arr) {
      const raw = item?.[dateField];
      let dateStr = null;
      if (typeof raw === "number") {
        dateStr = new Date(raw).toISOString().split("T")[0];
      } else if (typeof raw === "string" && raw.length >= 10) {
        dateStr = raw.slice(0, 10);
      }
      if (dateStr && dateStr < cutoffStr) {
        const m = ensureMonth(getMonthKeyFromDateStr(dateStr));
        if (!m[field]) m[field] = [];
        m[field].push(item);
      } else {
        live.push(item);
      }
    }
    liveData[field] = live;
  }

  // 5. Chats: houd max 200 berichten of 30 dagen
  if (Array.isArray(data.chats)) {
    const chatCutoffTs = Date.now() - 30 * 24 * 60 * 60 * 1000;
    liveData.chats = data.chats
      .filter((c) => !c.timestamp || c.timestamp > chatCutoffTs)
      .slice(-200);
  }

  return { liveData, archiveByMonth };
};

// Helper: Get month key from date
const getMonthKey = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

// Helper: Get week key from date

import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  startTransition,
} from "react";
import { CpLogoAnimatie } from "./cpLogoAnimatie";
import { weekPrintHtml } from "./weekPrint";
import { cpIcoonVoor } from "./cpIconenApp";
import { CpVorm, CP_STORY } from "./cpVormen";
import { opgemaakteFoto } from "./polaroidFoto";
import "./cpModus.css";
import { createPortal } from "react-dom";
import {
  Calendar,
  Calendar as CalendarIcon,
  CalendarDays,
  CalendarRange,
  Hash,
  Link,
  Users,
  CheckSquare,
  Lock,
  Unlock,
  LogOut,
  Smile,
  Printer,
  FileDown,
  ChevronRight,
  ChevronLeft,
  Palette,
  Mic,
  Trash2,
  Globe,
  Plus,
  MapPin,
  Map as MapIcon,
  User,
  CheckCircle,
  LayoutDashboard,
  Clipboard,
  WifiOff,
  Sun,
  Cloud,
  CloudSun,
  CloudRain,
  CloudSnow,
  CloudLightning,
  AlertOctagon,
  ArrowLeft,
  Clock,
  Home,
  ShoppingCart,
  Camera, // 📷 NIEUW voor foto's maken
  Settings,
  KeyRound,
  Baby,
  PieChart,
  BookOpen,
  Menu,
  X as CloseIcon,
  Edit2,
  AlertCircle,
  ListChecks,
  Send,
  Megaphone,
  Euro,
  MonitorCheck,
  Gift,
  Check,
  CheckCheck,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  Phone,
  PartyPopper,
  Music,
  Clock3,
  Moon,
  Power,
  Info,
  Search,
  UserCheck,
  ShieldAlert,
  Shield, // 👈 NIEUW
  AlertTriangle, // 👈 NIEUW
  Lightbulb,
  GripVertical,
  Castle,
  Copy,
  Save,
  MessageSquare,
  UserPlus,
  Trophy,
  Ticket,
  UserCog,
  Repeat,
  Music2,
  Book,
  HelpCircle,
  Users2,
  StickyNote,
  FileText,
  Eye,
  EyeOff,
  Database,
  Download,
  RotateCcw,
  Star,
  ChevronUp,
  ChevronDown,
  MoreHorizontal,
  ClipboardCheck,
  RefreshCw,
  Zap,
  Monitor,
  ExternalLink,
  Maximize2,
  Coffee,
  Utensils,
  Image as ImageIcon,
  Images,
  BarChart3,
  Languages,
  FileImage,
  Layout,
  Palette as PaletteIcon,
  Upload,
  TrendingUp,
  Activity,
  Layers,
  Sparkles,
  UserX,
  Mail,
  X,
  Award,
  CalendarCheck2,
  LayoutGrid,
  ClipboardList,
  LayoutList,
  Ban as BanIcon,
  Wind,
  Wand2,
  Type,
  Loader2,
  MessageCircle,
  History,
  SlidersHorizontal,
} from "lucide-react";

// ============================================================================
// 📴 OFFLINE-FIRST FUNCTIONALITY
// ============================================================================

// 💾 OFFLINE MANAGER CLASS
class OfflineManager {
  constructor() {
    this.db = null;
    this.isOnline = navigator.onLine;
    this.syncInProgress = false;
    this.listeners = [];
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open("CPOfflineDB", 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        console.log("✅ Offline DB ready");
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        if (!db.objectStoreNames.contains("appData")) {
          const appDataStore = db.createObjectStore("appData", {
            keyPath: "key",
          });
          appDataStore.createIndex("timestamp", "timestamp", { unique: false });
        }

        if (!db.objectStoreNames.contains("mutations")) {
          const mutationsStore = db.createObjectStore("mutations", {
            keyPath: "id",
            autoIncrement: true,
          });
          mutationsStore.createIndex("timestamp", "timestamp", {
            unique: false,
          });
          mutationsStore.createIndex("synced", "synced", { unique: false });
        }
      };
    });
  }

  async saveData(key, data) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(["appData"], "readwrite");
      const store = transaction.objectStore("appData");
      const record = { key, data, timestamp: Date.now() };
      const request = store.put(record);

      request.onsuccess = () => resolve(record);
      request.onerror = () => reject(request.error);
    });
  }

  async getData(key) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(["appData"], "readonly");
      const store = transaction.objectStore("appData");
      const request = store.get(key);

      request.onsuccess = () =>
        resolve(request.result ? request.result.data : null);
      request.onerror = () => reject(request.error);
    });
  }

  async addMutation(mutation) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(["mutations"], "readwrite");
      const store = transaction.objectStore("mutations");
      const record = {
        ...mutation,
        timestamp: Date.now(),
        synced: false,
        retries: 0,
      };
      const request = store.add(record);

      request.onsuccess = () => {
        console.log("📴 Mutation queued:", mutation.type);
        if (this.isOnline) this.syncMutations();
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingMutations() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(["mutations"], "readonly");
      const store = transaction.objectStore("mutations");
      const index = store.index("synced");
      const request = index.getAll(false);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteMutation(id) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(["mutations"], "readwrite");
      const store = transaction.objectStore("mutations");
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async syncMutations() {
    if (!this.isOnline || this.syncInProgress) return;

    this.syncInProgress = true;
    console.log("🔄 Syncing offline mutations...");

    try {
      const mutations = await this.getPendingMutations();

      if (mutations.length === 0) {
        console.log("✅ No mutations to sync");
        this.syncInProgress = false;
        return;
      }

      console.log(`🔄 Syncing ${mutations.length} mutations...`);

      for (const mutation of mutations) {
        try {
          // Firebase sync gebeurt in App component via executeMutation callback
          // Wacht tot de app de wijziging heeft overgenomen; pas dan verwijderen
          await this.notifyListeners("mutationReady", mutation);
          await this.deleteMutation(mutation.id);
          console.log(`✅ Synced mutation ${mutation.id}`);
        } catch (error) {
          console.error(`❌ Failed to sync mutation ${mutation.id}:`, error);
        }
      }

      this.notifyListeners("syncComplete");
    } catch (error) {
      console.error("❌ Sync error:", error);
    } finally {
      this.syncInProgress = false;
    }
  }

  setOnlineStatus(isOnline) {
    const wasOffline = !this.isOnline;
    this.isOnline = isOnline;

    console.log(`🌐 ${isOnline ? "ONLINE" : "OFFLINE"}`);

    if (isOnline && wasOffline) {
      console.log("🔄 Back online! Starting sync...");
      this.syncMutations();
    }

    this.notifyListeners("connectionChange", { isOnline });
  }

  addListener(callback) {
    this.listeners.push(callback);
  }

  removeListener(callback) {
    this.listeners = this.listeners.filter((l) => l !== callback);
  }

  async notifyListeners(event, data) {
    for (const callback of [...this.listeners]) {
      await callback(event, data);
    }
  }
}

const offlineManager = new OfflineManager();

// 🎨 OFFLINE INDICATOR COMPONENT
const OfflineIndicator = ({ isOnline, pendingMutations, isSyncing }) => {
  if (isOnline && pendingMutations === 0 && !isSyncing) {
    return null;
  }

  return (
    <div
      className="fixed top-0 left-0 right-0 z-50 px-4 py-2 text-center text-sm font-semibold shadow-lg animate-in slide-in-from-top"
      style={{
        backgroundColor: isOnline ? "#045E51" : "#700606",
        color: "white",
      }}
    >
      {!isOnline && (
        <div className="flex items-center justify-center gap-2">
          <WifiOff size={16} />
          <span>Offline - Wijzigingen worden opgeslagen</span>
        </div>
      )}

      {isOnline && isSyncing && (
        <div className="flex items-center justify-center gap-2">
          <RefreshCw size={16} className="animate-spin" />
          <span>Synchroniseren... ({pendingMutations} items)</span>
        </div>
      )}

      {isOnline && !isSyncing && pendingMutations > 0 && (
        <div className="flex items-center justify-center gap-2">
          <AlertCircle size={16} />
          <span>{pendingMutations} wijzigingen wachten op sync</span>
        </div>
      )}
    </div>
  );
};

// Firebase Imports
import { initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import {
  getAuth,
  signInAnonymously,
  onAuthStateChanged,
  signInWithCustomToken,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  collection,
  deleteDoc,
  runTransaction,
  increment,
  arrayUnion,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

// Officiële CP-iconen voor navigatie en afdelingen (vervangen de lijn-iconen)
const IcDagbord = cpIcoonVoor("Calendar kiosk");
const IcDashboard = cpIcoonVoor("View All");
const IcRooster = cpIcoonVoor("Friends People");
const IcRoostermaker = cpIcoonVoor("Checklist MyCP");
const IcProgramma = cpIcoonVoor("Activities");
const IcBezetting = cpIcoonVoor("Percentage web");
const IcOverdracht = cpIcoonVoor("Doc Kiosk and Challenge");
const IcTaken = cpIcoonVoor("Check Kiosk");
const IcKwaliteit = cpIcoonVoor("Cup and recognize");
const IcUren = cpIcoonVoor("Clock My CP");
const IcBestellen = cpIcoonVoor("Shoppingcart MyCP");
const IcTelefoon = cpIcoonVoor("Phone");
const IcHandboek = cpIcoonVoor("Sheets web");
const IcBingo = cpIcoonVoor("Cup and recognize"); // Prize Night (beker)
const IcDj = cpIcoonVoor("Entertainement");
const IcChat = cpIcoonVoor("Text MyCP");
const IcFeest = cpIcoonVoor("Calendar kiosk"); // Feestdagen
const IcKaarten = cpIcoonVoor("E-Tickets MyCP"); // Kaarten printen (CO)
const IcAfscheid = cpIcoonVoor("Cup and recognize"); // Afscheidsboek
const IcPieten = cpIcoonVoor("Activities gift"); // Pietenservice
const IcPolaroid = cpIcoonVoor("Image CRM"); // Polaroids printen
const IcThema = cpIcoonVoor("Profile MyCP");
const IcDisplay = cpIcoonVoor("Window CRM");
const IcPromo = cpIcoonVoor("Inspiring");
const IcPincode = cpIcoonVoor("Keys MyCP");
const IcBeheer = cpIcoonVoor("Filter");
const IcMeer = cpIcoonVoor("More MyCP");
const IcCrea = cpIcoonVoor("Stars web");
const IcLente = cpIcoonVoor("Spring web");
const IcHotspot = cpIcoonVoor("Location CP web");
const IcAlles = cpIcoonVoor("View All");
const IcZomer = cpIcoonVoor("Summer");
const IcHerfst = cpIcoonVoor("Automne");
const IcWinter = cpIcoonVoor("Snow web");
const IcOrry = cpIcoonVoor("Kids activities");
const IcEntertainment = cpIcoonVoor("Entertainement");
const IcSport = cpIcoonVoor("Sports activities");
const IcSpringkussen = cpIcoonVoor("Kids activities");

// ── Onderdelen die pas laden als ze nodig zijn (sneller opstarten) ──
// Na een nieuwe versie kan een oud onderdeel-bestand ontbreken: dan één
// keer de pagina verversen zodat de nieuwste versie geladen wordt.
// Onderdeel niet te laden (meestal: er is net een nieuwe versie online gezet
// en het oude bestand bestaat niet meer)? Dan één keer per halve minuut
// automatisch herladen, ook als er later op de avond wéér een update komt.
const isVersieFout = (err) =>
  /ChunkLoadError|Loading (CSS )?chunk|dynamically imported module|Importing a module script failed|error loading dynamically/i.test(
    `${err?.name || ""} ${err?.message || ""}`
  );
const herlaadVoorNieuweVersie = () => {
  try {
    const vorige = Number(sessionStorage.getItem("onderdeelReloadAt") || 0);
    if (Date.now() - vorige > 30000) {
      sessionStorage.setItem("onderdeelReloadAt", String(Date.now()));
      window.location.reload();
      return true;
    }
  } catch (e) {}
  return false;
};
const laadOnderdeel = (loader) =>
  loader().catch((err) => {
    if (herlaadVoorNieuweVersie()) return new Promise(() => {});
    throw err;
  });
const SkeletonBlokken = ({ rijen = 4 }) => (
  <div className="w-full max-w-3xl mx-auto px-4 py-6 space-y-3" role="status" aria-live="polite" aria-label="Laden">
    <div className="cp-skeleton h-8 w-1/2" />
    {Array.from({ length: rijen }).map((_, i) => (
      <div key={i} className="cp-skeleton h-20 w-full" style={{ opacity: 1 - i * 0.15 }} />
    ))}
  </div>
);
const OnderdeelLaden = () => <SkeletonBlokken />;
const lazyView = (loader, name) => {
  const Lazy = React.lazy(() => laadOnderdeel(loader).then((m) => ({ default: m[name] })));
  const Wrapped = React.forwardRef((props, ref) => (
    <React.Suspense fallback={<OnderdeelLaden />}>
      <Lazy ref={ref} {...props} />
    </React.Suspense>
  ));
  Wrapped.displayName = name;
  return Wrapped;
};
const PromoScreenView = lazyView(() => import("./onderdelen/promo"), "PromoScreenView");
const PromoDesigner = lazyView(() => import("./onderdelen/promo"), "PromoDesigner");
const PromoBediening = lazyView(() => import("./onderdelen/promobediening"), "PromoBediening");
const DJNightTool = lazyView(() => import("./onderdelen/dj"), "DJNightTool");
const DJGuestView = lazyView(() => import("./onderdelen/dj"), "DJGuestView");
const DJSchermView = lazyView(() => import("./onderdelen/dj"), "DJSchermView");
const DJSpotifyKoppel = lazyView(() => import("./onderdelen/dj"), "DJSpotifyKoppel");
// 🎵 Eigen webadres voor DJ Night-gasten (bijv. djnight-limburgsepeel.vercel.app):
// op zo'n adres bestaat alleen de gastenpagina — nooit de personeelsapp.
const isDjGastAdres = () =>
  typeof window !== "undefined" && /dj-?night/i.test(window.location.hostname || "");
const QualityVisitPage = lazyView(() => import("./onderdelen/kwaliteit"), "QualityVisitPage");
const KwaliteitDashboard = lazyView(() => import("./onderdelen/kwaliteit"), "KwaliteitDashboard");
const ScheduleBuilderView = lazyView(() => import("./onderdelen/planning"), "ScheduleBuilderView");
const RoosterMakerView = lazyView(() => import("./onderdelen/planning"), "RoosterMakerView");


const EditSettings = lazyView(() => import("./onderdelen/beheer"), "EditSettings");
const AdminTasksView = lazyView(() => import("./onderdelen/beheer"), "AdminTasksView");
const DataCleanupView = lazyView(() => import("./onderdelen/beheer"), "DataCleanupView");
const PersoneelView = lazyView(() => import("./onderdelen/beheer"), "PersoneelView");
const EditSchedule = lazyView(() => import("./onderdelen/beheer"), "EditSchedule");
const EditPhoneDirectory = lazyView(() => import("./onderdelen/beheer"), "EditPhoneDirectory");
const EditStats = lazyView(() => import("./onderdelen/beheer"), "EditStats");
const EditHours = lazyView(() => import("./onderdelen/beheer"), "EditHours");
const AdminMenu = lazyView(() => import("./onderdelen/beheer"), "AdminMenu");
const TijdkaartenView = lazyView(() => import("./onderdelen/kaarten"), "TijdkaartenView");
const FeestdagenView = lazyView(() => import("./onderdelen/feestdagen"), "FeestdagenView");
const AfscheidView = lazyView(() => import("./onderdelen/afscheid"), "AfscheidView");
const PietenserviceView = lazyView(() => import("./onderdelen/pietenservice"), "PietenserviceView");
const ProfielFotoKaart = lazyView(() => import("./onderdelen/profielfoto"), "ProfielFotoKaart");
const PolaroidsView = lazyView(() => import("./onderdelen/polaroids"), "PolaroidsView");
const HandboekView = lazyView(() => import("./onderdelen/naslag"), "HandboekView");
const ActivityLibraryView = lazyView(() => import("./onderdelen/naslag"), "ActivityLibraryView");
const SpringkussenView = lazyView(() => import("./onderdelen/naslag"), "SpringkussenView");
const FMChecklistView = lazyView(() => import("./onderdelen/naslag"), "FMChecklistView");


const UserHoursView = lazyView(() => import("./onderdelen/naslag"), "UserHoursView");
const OccupancyView = lazyView(() => import("./onderdelen/naslag"), "OccupancyView");
const RoosterView = lazyView(() => import("./onderdelen/rooster"), "RoosterView");
const ShiftChangesView = lazyView(() => import("./onderdelen/rooster"), "ShiftChangesView");
const HandoverView = lazyView(() => import("./onderdelen/overdracht"), "HandoverView");
const DashboardContent = lazyView(() => import("./onderdelen/dashboard"), "DashboardContent");
const ChatView = lazyView(() => import("./onderdelen/overig"), "ChatView");
const UserOrderView = lazyView(() => import("./onderdelen/overig"), "UserOrderView");
const BingoWinnerView = lazyView(() => import("./onderdelen/overig"), "BingoWinnerView");
const TakenlijstView = lazyView(() => import("./onderdelen/takenlijst"), "TakenlijstView");
const ProgrammaView = lazyView(() => import("./onderdelen/programmaview"), "ProgrammaView");
// Veelgebruikte onderdelen alvast op de achtergrond binnenhalen zodra de app
// rustig is, zodat Rooster, Programma, Chat en Takenlijst daarna direct openen.
if (typeof window !== "undefined") {
  const voorladen = () =>
    [
      () => import("./onderdelen/rooster"),
      () => import("./onderdelen/programmaview"),
      () => import("./onderdelen/overig"),
      () => import("./onderdelen/takenlijst"),
      () => import("./onderdelen/dashboard"),
    ].forEach((l, i) => setTimeout(() => l().catch(() => {}), i * 400));
  const start = () =>
    "requestIdleCallback" in window
      ? window.requestIdleCallback(voorladen, { timeout: 6000 })
      : setTimeout(voorladen, 3000);
  if (document.readyState === "complete") setTimeout(start, 2500);
  else window.addEventListener("load", () => setTimeout(start, 2500), { once: true });
}


// --- CONFIGURATIE & FIREBASE ---
let firebaseConfig;

if (typeof __firebase_config !== "undefined") {
  firebaseConfig = JSON.parse(__firebase_config);
} else {
  firebaseConfig = {
    apiKey: "AIzaSyASpWTp5gsym3S0OXsbj8DVLX5hATABuBg",
    authDomain: "cp-app-final.firebaseapp.com",
    projectId: "cp-app-final",
    storageBucket: "cp-app-final.firebasestorage.app",
    messagingSenderId: "543451160778",
    appId: "1:543451160778:web:b7667edb254daf72e21433",
  };
}

let app, auth, db, storage;
try {
  app = initializeApp(firebaseConfig);
  // 🛡️ App Check: Firebase accepteert dan alleen verzoeken uit deze app.
  // Staat pas aan als REACT_APP_APPCHECK_SITE_KEY (reCAPTCHA v3) in Vercel staat.
  // Lokaal testen: zet in de console localStorage.cpAppCheckDebug = "1" en herlaad;
  // de debugcode die dan in de console verschijnt, registreer je in Firebase.
  const appCheckSleutel = process.env.REACT_APP_APPCHECK_SITE_KEY;
  if (appCheckSleutel) {
    try {
      if (window.location.hostname === "localhost" || localStorage.getItem("cpAppCheckDebug") === "1")
        self.FIREBASE_APPCHECK_DEBUG_TOKEN = true; // eslint-disable-line no-restricted-globals
      initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(appCheckSleutel),
        isTokenAutoRefreshEnabled: true,
      });
    } catch (e) {
      console.warn("App Check niet gestart:", e);
    }
  }
  auth = getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);
  // Noodherstel via de console alleen voor de FM (zie AdminMenu); niet meer
  // standaard voor iedereen op window.
} catch (error) {
  console.error("Firebase config error:", error);
}

const appId = typeof __app_id !== "undefined" ? __app_id : "default-app-id";
const DATA_PROTECTION = {
  KEYS: {
    CURRENT: "cp_app_data",
    LAST_GOOD: "cp_last_known_good",
    EMERGENCY: "cp_emergency_backup",
    SAVE_TIME: "cp_last_save_time",
    RECOVERY_LOG: "cp_recovery_log",
    SNAPSHOT_DATE: "cp_last_snapshot_date",
  },

  // LAAG 1: Auto-save every 10 seconds
  startAutoSave: (getData) => {
    return setInterval(() => {
      try {
        const data = getData();
        if (data && Object.keys(data).length > 5) {
          localStorage.setItem(
            DATA_PROTECTION.KEYS.CURRENT,
            JSON.stringify(data)
          );
          localStorage.setItem(
            DATA_PROTECTION.KEYS.SAVE_TIME,
            new Date().toISOString()
          );
        }
      } catch (e) {
        console.error("⚠️ Auto-save failed:", e);
      }
    }, 10000);
  },

  // LAAG 2: Emergency snapshot before destructive operations
  createEmergencySnapshot: (data) => {
    try {
      const snapshot = {
        data: data,
        timestamp: Date.now(),
        reason: "pre-operation-safety",
      };
      localStorage.setItem(
        DATA_PROTECTION.KEYS.EMERGENCY,
        JSON.stringify(snapshot)
      );
    } catch (e) {
      console.error("⚠️ Failed to create emergency snapshot:", e);
    }
  },

  // Uitgesteld + samengevoegd: niet bij elke klik de hele app naar localStorage
  saveLastKnownGoodLater: (data) => {
    clearTimeout(DATA_PROTECTION._lkgTimer);
    DATA_PROTECTION._lkgTimer = setTimeout(() => DATA_PROTECTION.saveLastKnownGood(data), 2000);
  },

  // LAAG 3: Save last known good state
  saveLastKnownGood: (data) => {
    try {
      localStorage.setItem(
        DATA_PROTECTION.KEYS.LAST_GOOD,
        JSON.stringify(data)
      );
      console.log("✅ Last known good state saved");
    } catch (e) {
      console.error("⚠️ Failed to save last known good:", e);
    }
  },

  // Recovery waterfall: try all sources in order
  recoverFromLocal: () => {
    const sources = [
      { key: DATA_PROTECTION.KEYS.CURRENT, name: "Current" },
      { key: DATA_PROTECTION.KEYS.LAST_GOOD, name: "Last Known Good" },
      {
        key: DATA_PROTECTION.KEYS.EMERGENCY,
        name: "Emergency",
        isSnapshot: true,
      },
    ];

    for (const source of sources) {
      try {
        const stored = localStorage.getItem(source.key);
        if (!stored) continue;

        const parsed = source.isSnapshot
          ? JSON.parse(stored).data
          : JSON.parse(stored);

        if (parsed && Object.keys(parsed).length > 5) {
          console.log(`✅ Recovered from ${source.name}`);
          DATA_PROTECTION.logRecovery(source.name, true);
          return sanitizeData(parsed);
        }
      } catch (e) {
        console.error(`⚠️ Failed to recover from ${source.name}:`, e);
      }
    }

    console.warn("⚠️ No valid local backups found");
    DATA_PROTECTION.logRecovery("local-storage", false);
    return null;
  },

  // Log recovery attempts for debugging
  logRecovery: (method, success) => {
    try {
      const log = JSON.parse(
        localStorage.getItem(DATA_PROTECTION.KEYS.RECOVERY_LOG) || "[]"
      );
      log.push({
        method,
        success,
        timestamp: new Date().toISOString(),
      });
      localStorage.setItem(
        DATA_PROTECTION.KEYS.RECOVERY_LOG,
        JSON.stringify(log.slice(-50))
      );
    } catch (e) {
      console.error("Failed to log recovery:", e);
    }
  },

  // Get info about available backups
  getBackupInfo: () => {
    try {
      return {
        current: {
          exists: !!localStorage.getItem(DATA_PROTECTION.KEYS.CURRENT),
          time: localStorage.getItem(DATA_PROTECTION.KEYS.SAVE_TIME),
        },
        lastGood: {
          exists: !!localStorage.getItem(DATA_PROTECTION.KEYS.LAST_GOOD),
        },
        emergency: {
          exists: !!localStorage.getItem(DATA_PROTECTION.KEYS.EMERGENCY),
        },
        logs: JSON.parse(
          localStorage.getItem(DATA_PROTECTION.KEYS.RECOVERY_LOG) || "[]"
        ),
      };
    } catch (e) {
      return null;
    }
  },
};

// --- FOTO UPLOAD HELPER FUNCTIES ---
/**
 * Comprimeert een foto voor het uploaden
 * @param {File} file - Het originele foto bestand
 * @param {number} maxWidth - Maximale breedte (default 1200px)
 * @param {number} quality - Compressie kwaliteit 0-1 (default 0.8)
 * @returns {Promise<Blob>} - Gecomprimeerde foto als Blob
 */
const compressImage = async (file, maxWidth = 1200, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const img = new window.Image(); // Use window.Image to avoid conflict with lucide-react Image import
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    img.onload = () => {
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            console.log(
              `Foto gecomprimeerd van ${(file.size / 1024 / 1024).toFixed(
                2
              )}MB naar ${(blob.size / 1024 / 1024).toFixed(2)}MB`
            );
            resolve(blob);
          } else {
            reject(new Error("Compressie gefaald"));
          }
        },
        "image/jpeg",
        quality
      );
    };

    img.onerror = () => reject(new Error("Foto kan niet geladen worden"));
    img.src = URL.createObjectURL(file);
  });
};


/**
 * Verwijder foto van Firebase Storage
 * @param {string} storagePath - Pad in Firebase Storage
 */
const deleteHandoverPhoto = async (storagePath) => {
  try {
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
    console.log("✅ Foto verwijderd:", storagePath);
  } catch (error) {
    console.error("⚠️ Kon foto niet verwijderen:", error);
  }
};


// --- CSV SCHEDULE PARSER FUNCTIES ---
/**
 * Parse CSV line met quotes handling
 */
const parseCSVLine = (line) => {
  const result = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current.trim());

  return result;
};

/**
 * Parse datum formaat
 */
const parseCsvDate = (dateStr) => {
  if (!dateStr || !dateStr.includes("-")) return null;
  // "1-2-2026" -> "2026-02-01"
  const parts = dateStr.split("-");
  if (parts.length !== 3) return null;

  const [day, month, year] = parts;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

/**
 * Bepaal type o.b.v. location
 */
const determineScheduleType = (location) => {
  if (!location) return "other";
  const loc = location.toLowerCase();

  if (loc.includes("crea")) return "crea";
  if (loc.includes("springkussen")) return "springkussen";
  if (loc.includes("fm")) return "fm";
  // O&F, DJ, Quiz, Bingo, Live, KKO allemaal entertainment
  return "entertainment";
};

/**
 * Match CSV naam naar app user
 */
const findMatchingUser = (csvName, staffList, fmLastName = null) => {
  if (!csvName || !csvName.includes(",")) return null;

  // csvName = "Billekens, L.M.J." of "Broek van den, M.P.R."
  const lastName = csvName.split(",")[0].trim();

  // Check eerst of het Maik (FM) is
  if (fmLastName && fmLastName.toLowerCase() === lastName.toLowerCase()) {
    return "Maik";
  }

  // Zoek in staffList
  const match = staffList.find(
    (s) => s.lastName && s.lastName.toLowerCase() === lastName.toLowerCase()
  );

  return match ? match.name : null;
};

/**
 * Parse volledige schedule CSV
 */
const parseScheduleCSV = (csvText, staffList, fmLastName = null) => {
  const lines = csvText.split("\n");
  const schedule = {};
  let currentEmployee = null;
  const unmatchedEmployees = new Set();
  // Sla pauzes op die vóór de werkregel staan
  const pendingBreaks = {}; // employeeName+date → [{start, end}]

  for (let i = 1; i < lines.length; i++) {
    // Skip header
    const line = lines[i];
    if (!line.trim()) continue;

    const cols = parseCSVLine(line);

    // Nieuwe medewerker?
    if (cols[0] && cols[0].includes(",")) {
      currentEmployee = cols[0].trim();
    }

    // Shift data?
    if (cols[3] && cols[5] && currentEmployee) {
      const date = parseCsvDate(cols[3]);
      if (!date) continue;

      const timeRange = cols[5]; // "09:45 - 15:45"
      const location = cols[6] || "";
      const workTimeRange = cols[8] || "";
      const description = (cols[9] || "").toLowerCase();

      // Match naar app user - geef fmLastName mee!
      const matchedUser = findMatchingUser(
        currentEmployee,
        staffList,
        fmLastName
      );
      if (!matchedUser) {
        unmatchedEmployees.add(currentEmployee);
      }

      if (!schedule[date]) schedule[date] = [];

      // Zoek bestaande shift voor deze employee
      let existingShift = schedule[date].find(
        (s) => s.employeeName === currentEmployee
      );

      const isPauze =
        description.includes("pauze") ||
        description.includes("break") ||
        description.includes("lunch");
      const isWerk =
        description.includes("werk") || description.includes("work");

      if (isPauze) {
        // Voeg pauze toe aan bestaande shift, of sla op als pending
        if (workTimeRange.includes(" - ")) {
          const [breakStart, breakEnd] = workTimeRange.split(" - ");
          const brk = { start: breakStart.trim(), end: breakEnd.trim() };
          if (existingShift) {
            if (!existingShift.breaks) existingShift.breaks = [];
            existingShift.breaks.push(brk);
          } else {
            // Pauze komt vóór werkregel — bewaar voor later
            const key = `${currentEmployee}|${date}`;
            if (!pendingBreaks[key]) pendingBreaks[key] = [];
            pendingBreaks[key].push(brk);
          }
        }
      } else if (isWerk) {
        // Dit is een werk segment
        if (!existingShift) {
          // Maak nieuwe shift
          if (timeRange.includes(" - ")) {
            const [start, end] = timeRange.split(" - ");
            const key = `${currentEmployee}|${date}`;
            schedule[date].push({
              employeeName: currentEmployee,
              matchedUser: matchedUser,
              start: start.trim(),
              end: end.trim(),
              location: location.trim(),
              type: determineScheduleType(location),
              breaks: pendingBreaks[key] || [],
            });
            delete pendingBreaks[key];
          }
        }
      }
    }
  }

  return {
    schedule,
    unmatchedEmployees: Array.from(unmatchedEmployees),
  };
};


// --- KLEURENPALET ---
// 🙈 Verborgen voor team: medewerker is actief (FM ziet haar overal, ook in
// beschikbaarheid en roostermaker), maar het team ziet haar niet in lijsten.
let CP_IS_FM = false;
const zetCpRol = (rol) => {
  CP_IS_FM = rol === "fm" || rol === "admin";
};
const teamVerborgen = (s) => !!(s && s.verborgen) && !CP_IS_FM;

const C = {
  Pine: "#045E51",
  Bark: "#7A5226",
  Lagoon: "#00192F",
  Sunset: "#700606",
  Grass: "#B1EDA6",
  Honey: "#FFCA55",
  Sky: "#9DB4F6",
  Blossom: "#FF699B",
  White: "#FFFFFF",
  Bg: "#F0FDF4",
  // 🆕 NIEUWE KLEUREN - voeg deze toe:
  PineLight: "#DAFFD1", // Officiële webtint (Grass) bij Pine
  PineMedium: "#0A7B6A", // Medium pine voor gradients
  BarkLight: "#FFE2A1", // Officiële webtint (Honey) bij Bark
  GrassLight: "#DAFFD1", // Officiële webtint Grass
  HoneyLight: "#FFE2A1", // Officiële webtint Honey
  SkyLight: "#C4D4FF", // Officiële webtint Sky
  BlossomLight: "#FFB0D6", // Officiële webtint Blossom

  // Neutrale kleuren
  TextPrimary: "#1F2937",
  TextSecondary: "#6B7280",
  TextMuted: "#9CA3AF",
  Border: "#E5E7EB",
  BorderLight: "#F3F4F6",
};


const THEMES = {
  general: {
    id: "general",
    label: "Groen",
    primary: "#045E51",
    secondary: "#B1EDA6",
  },
  winter: {
    id: "winter",
    label: "Blauw",
    primary: "#00192F",
    secondary: "#9DB4F6",
  },
  spring: {
    id: "spring",
    label: "Roze",
    primary: "#FF699B",
    secondary: "#700606",
  },
  summer: {
    id: "summer",
    label: "Geel",
    primary: "#FFCA55",
    secondary: "#7A5226",
  },
  autumn: {
    id: "autumn",
    label: "Geel (oud: herfst)",
    verborgen: true, // zelfde kleurpaar als Geel; wordt automatisch Geel
    primary: "#7A5226",
    secondary: "#FFCA55",
  },
};

// CP logo color per theme (matching official CP color SVGs)
const CP_LOGO_COLORS = {
  general: "#b4d6a1", // Grass
  winter: "#9fb2dc", // Sky
  spring: "#700606", // Sunset
  summer: "#7A5226", // Bark
  autumn: "#fdc957", // Honey
};

const PATTERN_BACKGROUNDS = {
  general:
    "data:image/svg+xml;base64,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",
  winter:
    "data:image/svg+xml;base64,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",
  spring:
    "data:image/svg+xml;base64,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",
  summer:
    "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz48c3ZnIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgdmlld0JveD0iMCAwIDE0NjMgMTIzMyI+PGRlZnM+PHN0eWxlPi5jbHMtMXtmaWxsOiNmZmNhNTU7fS5jbHMtMntmaWxsOiM3YTUyMjY7fTwvc3R5bGU+PC9kZWZzPjxnIGlkPSJCYWNrZ3JvdW5kIj48cmVjdCBjbGFzcz0iY2xzLTEiIHdpZHRoPSIxNDYzIiBoZWlnaHQ9IjEyMzMiLz48L2c+PGcgaWQ9IlBhdHRlcm4iPjxnPjxnPjxwYXRoIGNsYXNzPSJjbHMtMiIgZD0iTTEwOC4wNSwyMjMuNDhoMGM1My43NCwwLDk3LjM4LDQzLjYzLDk3LjM4LDk3LjM4djI2LjRIMTAuNjd2LTI2LjRjMC01My43NCw0My42My05Ny4zOCw5Ny4zOC05Ny4zOFoiLz48cGF0aCBjbGFzcz0iY2xzLTIiIGQ9Ik0zOS4xNCw1NzcuMDNoMGMtMzguMDItMzguMzYtMzguMDItMTAwLjU0LDAtMTM4LjlsNjguODktNjkuNTEsNjguODksNjkuNTFjMzguMDIsMzguMzYsMzguMDIsMTAwLjU0LDAsMTM4LjloMGMtMzguMDQsMzguMzktOTkuNzMsMzguMzktMTM3Ljc3LDBaIi8+PHBvbHlnb24gY2xhc3M9ImNscy0yIiBwb2ludHM9IjEwLjY3IDIwMi4xMiAxMC42NyAxMC42OCAxMDkuNTkgMTAuNjggMjA1LjQzIDEwNi40IDEwOS41OSAyMDIuMTIgMTAuNjcgMjAyLjEyIi8+PHBhdGggY2xhc3M9ImNscy0yIiBkPSJNNTE0Ljg1LDI4Ny45M0gyMjYuNzZ2LTEyNy43M2MwLTgyLjU4LDYyLjI1LTE0OS41MiwxMzkuMDMtMTQ5LjUyaDEwLjAzYzc2Ljc5LDAsMTM5LjAzLDY2Ljk0LDEzOS4wMywxNDkuNTJ2MTI3LjczWiIvPjxwYXRoIGNsYXNzPSJjbHMtMiIgZD0iTTcxOS40LDQxMC42M2gtMTgyLjA5Yy0uNywwLTEuMTMtLjM1LTEuMTMtLjkzdi0xMjQuMzJjMC0uMywuMTItLjU5LC4zMy0uOGw5MS4wNC05MS40Yy40NC0uNDQsMS4xNi0uNDQsMS42LDBsOTEuMDQsOTEuNGMuMjEsLjIxLC4zMywuNSwuMzMsLjh2MTI0LjMyYzAsLjYyLS40OSwuOTMtMS4xMywuOTNaIi8+PHBhdGggY2xhc3M9ImNscy0yIiBkPSJNNzIwLjUzLDQ3NS40NWgwYzAtMjQtMTkuNC00My40Ni00My4zMi00My40NkgyNzAuMDhjLTIzLjkzLDAtNDMuMzIsMTkuNDYtNDMuMzIsNDMuNDZoMGMwLDIwLjcyLDE0LjQ1LDM4LjA1LDMzLjgsNDIuNCwwLDAtLjAxLDAtLjAxLDAsLjQ4LC4xMSwuODUsLjU0LC44NSwxLjA2cy0uMzYsLjk0LS44NCwxLjA1YzAsMCwwLDAsLjAxLDAtMTkuMzUsNC4zNS0zMy44MSwyMS42OC0zMy44MSw0Mi40aDBjMCwyNCwxOS40LDQzLjQ2LDQzLjMyLDQzLjQ2aDQwNy4xMmMyMy45MywwLDQzLjMyLTE5LjQ2LDQzLjMyLTQzLjQ2aDBjMC0yMC43Mi0xNC40Ni0zOC4wNS0zMy44LTQyLjQsMCwwLC4wMSwwLC4wMSwwLS40OC0uMTEtLjg1LS41NC0uODUtMS4wNnMuMzYtLjk0LC44NC0xLjA1YzAsMCwwLDAtLjAxLDAsMTkuMzUtNC4zNSwzMy44MS0yMS42OCwzMy44MS00Mi40WiIvPjxyZWN0IGNsYXNzPSJjbHMtMiIgeD0iMjI2Ljc2IiB5PSIzMDkuMjkiIHdpZHRoPSIxODMuMDciIGhlaWdodD0iMTAxLjM1Ii8+PHJlY3QgY2xhc3M9ImNscy0yIiB4PSI1MzYuMTkiIHk9IjEwLjY4IiB3aWR0aD0iMTg0LjM0IiBoZWlnaHQ9IjE2MC44MiIvPjxwYXRoIGNsYXNzPSJjbHMtMiIgZD0iTTUwMi42LDMzOC45N2wtMjkuNTktMjkuNjgtMjkuNTksMjkuNjhjLTE2LjM0LDE2LjM5LTE2LjM0LDQyLjk3LDAsNTkuMzdoMGMxNi4zNCwxNi4zOSw0Mi44NCwxNi4zOSw1OS4xOCwwaDBjMTYuMzQtMTYuMzksMTYuMzQtNDIuOTcsMC01OS4zN1oiLz48L2c+PGc+PHBhdGggY2xhc3M9ImNscy0yIiBkPSJNMTIzMi43NCwxMC43MWgwYzM2LjQyLDAsNjUuOTksMjkuNTcsNjUuOTksNjUuOTlWMjUwLjYxaC0xMzEuOThWNzYuN2MwLTM2LjQyLDI5LjU3LTY1Ljk5LDY1Ljk5LTY1Ljk5WiIvPjxwYXRoIGNsYXNzPSJjbHMtMiIgZD0iTTE0MTkuMjUsMjUwLjYxaDBjMTguMjcsMCwzMy4wNy0xNC44MSwzMy4wNy0zMy4wN1Y0My43NWMwLTE4LjI3LTE0LjgxLTMzLjA3LTMzLjA3LTMzLjA3aDBjLTE1Ljc3LDAtMjguOTYsMTEuMDQtMzIuMjcsMjUuOCwwLDAsMC0uMDEsMCwwLS4wOCwuMzctLjQxLC42NS0uOCwuNjVzLS43Mi0uMjctLjgtLjY0YzAsMCwwLDAsMCwwLTMuMzEtMTQuNzctMTYuNS0yNS44MS0zMi4yNy0yNS44MWgwYy0xOC4yNywwLTMzLjA3LDE0LjgxLTMzLjA3LDMzLjA3VjIxNy41M2MwLDE4LjI3LDE0LjgxLDMzLjA3LDMzLjA3LDMzLjA3aDBjMTUuNzcsMCwyOC45Ni0xMS4wNCwzMi4yNy0yNS44LDAsMCwwLC4wMSwwLDAsLjA4LS4zNywuNDEtLjY1LC44LS42NXMuNzIsLjI3LC44LC42NGMwLDAsMCwwLDAsMCwzLjMxLDE0Ljc3LDE2LjUsMjUuODEsMzIuMjcsMjUuODFaIi8+PHBhdGggY2xhc3M9ImNscy0yIiBkPSJNMTQ1Mi4zMywzNTUuNDFoMGMwLTQ2LjEtMzcuMjktODMuNDctODMuMjgtODMuNDdoLTExOS4wMmMtNDUuOTksMC04My4yOCwzNy4zNy04My4yOCw4My40N2gwYzAsMzkuNzksMjcuNzksNzMuMDgsNjQuOTcsODEuNDQsMCwwLS4wMywwLS4wMiwwLC45MywuMiwxLjYzLDEuMDMsMS42MywyLjAzcy0uNjksMS44MS0xLjYxLDIuMDJjMCwwLC4wMiwwLC4wMiwwLTM3LjIsOC4zNS02NSw0MS42NC02NSw4MS40NWgwYzAsNDYuMSwzNy4yOSw4My40Nyw4My4yOCw4My40N2gxMTkuMDJjNDUuOTksMCw4My4yOC0zNy4zNyw4My4yOC04My40N2gwYzAtMzkuNzktMjcuNzktNzMuMDgtNjQuOTctODEuNDQsMCwwLC4wMywwLC4wMiwwLS45My0uMi0xLjYzLTEuMDMtMS42My0yLjAzcy42OS0xLjgxLDEuNjEtMi4wMmMwLDAtLjAyLDAtLjAyLDAsMzcuMi04LjM1LDY0Ljk5LTQxLjY0LDY0Ljk5LTgxLjQ1WiIvPjxwb2x5Z29uIGNsYXNzPSJjbHMtMiIgcG9pbnRzPSIxMTQ1LjQ1IDQ3OC40NCA5OTguNjEgNDc4LjQ0IDk5OC42MSAyNTEuOTUgMTA3Mi4wMyAxNzguMjEgMTE0NS40NSAyNTEuOTUgMTE0NS40NSA0NzguNDQiLz48cGF0aCBjbGFzcz0iY2xzLTIiIGQ9Ik0xMTQ1LjQ1LDQ3LjIzaDBjMC0yMC4xOS0xNi4zMy0zNi41NS0zNi40Ny0zNi41NWgtNzMuODljLTIwLjE0LDAtMzYuNDcsMTYuMzYtMzYuNDcsMzYuNTVoMGMwLDE3LjQzLDEyLjE3LDMyLDI4LjQ1LDM1LjY2LDAsMC0uMDEsMC0uMDEsMCwuNDEsLjA5LC43MSwuNDUsLjcxLC44OXMtLjMsLjc5LS43LC44OWMwLDAsMCwwLC4wMSwwLTE2LjI5LDMuNjYtMjguNDYsMTguMjMtMjguNDYsMzUuNjZoMGMwLDIwLjE5LDE2LjMzLDM2LjU1LDM2LjQ3LDM2LjU1aDczLjg5YzIwLjE0LDAsMzYuNDctMTYuMzYsMzYuNDctMzYuNTVoMGMwLTE3LjQzLTEyLjE3LTMyLTI4LjQ1LTM1LjY2LDAsMCwuMDEsMCwuMDEsMC0uNDEtLjA5LS43MS0uNDUtLjcxLS44OXMuMy0uNzksLjctLjg5YzAsMCwwLDAsMCwwLDE2LjI5LTMuNjYsMjguNDYtMTguMjMsMjguNDYtMzUuNjZaIi8+PHJlY3QgY2xhc3M9ImNscy0yIiB4PSI5OTguNjEiIHk9IjQ5OS43OCIgd2lkdGg9IjE0Ni44MyIgaGVpZ2h0PSIxMDYuMDQiLz48cGF0aCBjbGFzcz0iY2xzLTIiIGQ9Ik04NTkuODcsNDYwLjAxaDBjNjQuODIsMCwxMTcuNDQsNTIuNjIsMTE3LjQ0LDExNy40NHYyOC4zN2gtMjM0Ljg5di0yOC4zN2MwLTY0LjgyLDUyLjYyLTExNy40NCwxMTcuNDQtMTE3LjQ0WiIvPjxwYXRoIGNsYXNzPSJjbHMtMiIgZD0iTTk3Ny4zMSwzNDYuMjNoMGMwLTE3LjAyLTEzLjc1LTMwLjgxLTMwLjcxLTMwLjgxaC0xNzMuNDdjLTE2Ljk2LDAtMzAuNzEsMTMuOC0zMC43MSwzMC44MWgwYzAsMTQuNjksMTAuMjUsMjYuOTgsMjMuOTYsMzAuMDcsMCwwLDAsMCwwLDAsLjM0LC4wNywuNiwuMzgsLjYsLjc1cy0uMjUsLjY3LS41OSwuNzVjMCwwLDAsMCwwLDAtMTMuNzIsMy4wOC0yMy45NiwxNS4zNy0yMy45NiwzMC4wN2gwYzAsMTcuMDIsMTMuNzUsMzAuODEsMzAuNzEsMzAuODFoMTczLjQ3YzE2Ljk2LDAsMzAuNzEtMTMuOCwzMC43MS0zMC44MWgwYzAtMTQuNjktMTAuMjUtMjYuOTgtMjMuOTYtMzAuMDcsMCwwLDAsMCwwLDAtLjM0LS4wNy0uNi0uMzgtLjYtLjc1cy4yNS0uNjcsLjU5LS43NWMwLDAsMCwwLDAsMCwxMy43Mi0zLjA4LDIzLjk2LTE1LjM3LDIzLjk2LTMwLjA3WiIvPjxwYXRoIGNsYXNzPSJjbHMtMiIgZD0iTTk0Mi45Miw5My42OEw4NTkuODcsMTAuNjhsLTgzLjA0LDgzYy00NS44Niw0NS44NC00NS44NiwxMjAuMTcsMCwxNjYuMDFoMGM0NS44Niw0NS44NCwxMjAuMjIsNDUuODQsMTY2LjA5LDBoMGM0NS44Ni00NS44NCw0NS44Ni0xMjAuMTcsMC0xNjYuMDFaIi8+PC9nPjxnPjxwYXRoIGNsYXNzPSJjbHMtMiIgZD0iTTEyNTcuNTcsODM5Ljk4aDE5NC43NXYyNi40YzAsNTMuNzQtNDMuNjMsOTcuMzgtOTcuMzgsOTcuMzhoMGMtNTMuNzQsMC05Ny4zOC00My42My05Ny4zOC05Ny4zOHYtMjYuNGgwWiIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoMjcwOS45IDE4MDMuNzMpIHJvdGF0ZSgtMTgwKSIvPjxwYXRoIGNsYXNzPSJjbHMtMiIgZD0iTTEyODYuMDksMTE5My41M2gwYy0zOC4wMi0zOC4zNi0zOC4wMi0xMDAuNTQsMC0xMzguOWw2OC44OS02OS41MSw2OC44OSw2OS41MWMzOC4wMiwzOC4zNiwzOC4wMiwxMDAuNTQsMCwxMzguOWgwYy0zOC4wNCwzOC4zOS05OS43MywzOC4zOS0xMzcuNzcsMFoiLz48cG9seWdvbiBjbGFzcz0iY2xzLTIiIHBvaW50cz0iMTQ1Mi4zMyA4MTguNjIgMTQ1Mi4zMyA2MjcuMTggMTM1My40MSA2MjcuMTggMTI1Ny41NyA3MjIuOSAxMzUzLjQxIDgxOC42MiAxNDUyLjMzIDgxOC42MiIvPjxwYXRoIGNsYXNzPSJjbHMtMiIgZD0iTTk0OC4xNSw5MDQuNDNoMjg4LjFzMC0xMjcuNzMsMC0xMjcuNzNjMC04Mi41OC02Mi4yNS0xNDkuNTItMTM5LjAzLTE0OS41MmgtMTAuMDNjLTc2Ljc5LDAtMTM5LjAzLDY2Ljk0LTEzOS4wMywxNDkuNTJ2MTI3LjczWiIvPjxwYXRoIGNsYXNzPSJjbHMtMiIgZD0iTTc0My42LDEwMjcuMTNoMTgyLjA5Yy43LDAsMS4xMy0uMzUsMS4xMy0uOTN2LTEyNC4zMmMwLS4zLS4xMi0uNTktLjMzLS44bC05MS4wNC05MS40Yy0uNDQtLjQ0LTEuMTYtLjQ0LTEuNiwwbC05MS4wNCw5MS40Yy0uMjEsLjIxLS4zMywuNS0uMzMsLjh2MTI0LjMyYzAsLjYyLC40OSwuOTMsMS4xMywuOTNaIi8+PHBhdGggY2xhc3M9ImNscy0yIiBkPSJNNzQyLjQ3LDEwOTEuOTVoMGMwLTI0LDE5LjQtNDMuNDYsNDMuMzItNDMuNDZoNDA3LjEyYzIzLjkzLDAsNDMuMzIsMTkuNDYsNDMuMzIsNDMuNDZoMGMwLDIwLjcyLTE0LjQ1LDM4LjA1LTMzLjgsNDIuNCwwLDAsLjAxLDAsLjAxLDAtLjQ4LC4xMS0uODUsLjU0LS44NSwxLjA2cy4zNiwuOTQsLjg0LDEuMDVjMCwwLDAsMC0uMDEsMCwxOS4zNSw0LjM1LDMzLjgxLDIxLjY4LDMzLjgxLDQyLjRoMGMwLDI0LTE5LjQsNDMuNDYtNDMuMzIsNDMuNDZoLTQwNy4xMmMtMjMuOTMsMC00My4zMi0xOS40Ni00My4zMi00My40NmgwYzAtMjAuNzIsMTQuNDYtMzguMDUsMzMuOC00Mi40LDAsMC0uMDEsMC0uMDEsMCwuNDgtLjExLC44NS0uNTQsLjg1LTEuMDYsMC0uNTEtLjM2LS45NC0uODQtMS4wNSwwLDAsMCwwLC4wMSwwLTE5LjM1LTQuMzUtMzMuODEtMjEuNjgtMzMuODEtNDIuNFoiLz48cmVjdCBjbGFzcz0iY2xzLTIiIHg9IjEwNTMuMTciIHk9IjkyNS43OSIgd2lkdGg9IjE4My4wNyIgaGVpZ2h0PSIxMDEuMzUiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDIyODkuNDEgMTk1Mi45Mikgcm90YXRlKC0xODApIi8+PHJlY3QgY2xhc3M9ImNscy0yIiB4PSI3NDIuNDciIHk9IjYyNy4xOCIgd2lkdGg9IjE4NC4zNCIgaGVpZ2h0PSIxNjAuODIiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDE2NjkuMjkgMTQxNS4xNykgcm90YXRlKC0xODApIi8+PHBhdGggY2xhc3M9ImNscy0yIiBkPSJNOTYwLjQsOTU1LjQ3bDI5LjU5LTI5LjY4LDI5LjU5LDI5LjY4YzE2LjM0LDE2LjM5LDE2LjM0LDQyLjk3LDAsNTkuMzdoMGMtMTYuMzQsMTYuMzktNDIuODQsMTYuMzktNTkuMTgsMGgwYy0xNi4zNC0xNi4zOS0xNi4zNC00Mi45NywwLTU5LjM3WiIvPjwvZz48Zz48cGF0aCBjbGFzcz0iY2xzLTIiIGQ9Ik0xNjQuMjcsNjI3LjIxaDEzMS45OHYxNzMuOTFjMCwzNi40Mi0yOS41Nyw2NS45OS02NS45OSw2NS45OWgwYy0zNi40MiwwLTY1Ljk5LTI5LjU3LTY1Ljk5LTY1Ljk5di0xNzMuOTFoMFoiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDQ2MC41MyAxNDk0LjMyKSByb3RhdGUoLTE4MCkiLz48cGF0aCBjbGFzcz0iY2xzLTIiIGQ9Ik00My43NSw4NjcuMTFoMGMtMTguMjcsMC0zMy4wNy0xNC44MS0zMy4wNy0zMy4wN3YtMTczLjc4YzAtMTguMjcsMTQuODEtMzMuMDcsMzMuMDctMzMuMDdoMGMxNS43NywwLDI4Ljk2LDExLjA0LDMyLjI3LDI1LjgsMCwwLDAtLjAxLDAsMCwuMDgsLjM3LC40MSwuNjUsLjgsLjY1cy43Mi0uMjcsLjgtLjY0YzAsMCwwLDAsMCwwLDMuMzEtMTQuNzcsMTYuNS0yNS44MSwzMi4yNy0yNS44MWgwYzE4LjI3LDAsMzMuMDcsMTQuODEsMzMuMDcsMzMuMDd2MTczLjc4YzAsMTguMjctMTQuODEsMzMuMDctMzMuMDcsMzMuMDdoMGMtMTUuNzcsMC0yOC45Ni0xMS4wNC0zMi4yNy0yNS44LDAsMCwwLC4wMSwwLDAtLjA4LS4zNy0uNDEtLjY1LS44LS42NXMtLjcyLC4yNy0uOCwuNjRjMCwwLDAsMCwwLDAtMy4zMSwxNC43Ny0xNi41LDI1LjgxLTMyLjI3LDI1LjgxWiIvPjxwYXRoIGNsYXNzPSJjbHMtMiIgZD0iTTEwLjY3LDk3MS45MWgwYzAtNDYuMSwzNy4yOS04My40Nyw4My4yOC04My40N2gxMTkuMDJjNDUuOTksMCw4My4yOCwzNy4zNyw4My4yOCw4My40N2gwYzAsMzkuNzktMjcuNzksNzMuMDgtNjQuOTcsODEuNDQsMCwwLC4wMywwLC4wMiwwLS45MywuMi0xLjYzLDEuMDMtMS42MywyLjAzLDAsLjk5LC42OSwxLjgxLDEuNjEsMi4wMiwwLDAtLjAyLDAtLjAyLDAsMzcuMiw4LjM1LDY1LDQxLjY0LDY1LDgxLjQ1aDBjMCw0Ni4xLTM3LjI5LDgzLjQ3LTgzLjI4LDgzLjQ3SDkzLjk1Yy00NS45OSwwLTgzLjI4LTM3LjM3LTgzLjI4LTgzLjQ3aDBjMC0zOS43OSwyNy43OS03My4wOCw2NC45Ny04MS40NCwwLDAtLjAzLDAtLjAyLDAsLjkzLS4yLDEuNjMtMS4wMywxLjYzLTIuMDNzLS42OS0xLjgxLTEuNjEtMi4wMmMwLDAsLjAyLDAsLjAyLDAtMzcuMi04LjM1LTY0Ljk5LTQxLjY0LTY0Ljk5LTgxLjQ1WiIvPjxwb2x5Z29uIGNsYXNzPSJjbHMtMiIgcG9pbnRzPSIzMTcuNTUgMTA5NC45NCA0NjQuMzkgMTA5NC45NCA0NjQuMzkgODY4LjQ1IDM5MC45NyA3OTQuNzEgMzE3LjU1IDg2OC40NSAzMTcuNTUgMTA5NC45NCIvPjxwYXRoIGNsYXNzPSJjbHMtMiIgZD0iTTMxNy41NSw2NjMuNzNoMGMwLTIwLjE5LDE2LjMzLTM2LjU1LDM2LjQ3LTM2LjU1aDczLjg5YzIwLjE0LDAsMzYuNDcsMTYuMzYsMzYuNDcsMzYuNTVoMGMwLDE3LjQzLTEyLjE3LDMyLTI4LjQ1LDM1LjY2LDAsMCwuMDEsMCwuMDEsMC0uNDEsLjA5LS43MSwuNDUtLjcxLC44OXMuMywuNzksLjcsLjg5YzAsMCwwLDAtLjAxLDAsMTYuMjksMy42NiwyOC40NiwxOC4yMywyOC40NiwzNS42NmgwYzAsMjAuMTktMTYuMzMsMzYuNTUtMzYuNDcsMzYuNTVoLTczLjg5Yy0yMC4xNCwwLTM2LjQ3LTE2LjM2LTM2LjQ3LTM2LjU1aDBjMC0xNy40MywxMi4xNy0zMiwyOC40NS0zNS42NiwwLDAtLjAxLDAtLjAxLDAsLjQxLS4wOSwuNzEtLjQ1LC43MS0uODlzLS4zLS43OS0uNy0uODljMCwwLDAsMCwwLDAtMTYuMjktMy42Ni0yOC40Ni0xOC4yMy0yOC40Ni0zNS42NloiLz48cmVjdCBjbGFzcz0iY2xzLTIiIHg9IjMxNy41NSIgeT0iMTExNi4yOCIgd2lkdGg9IjE0Ni44MyIgaGVpZ2h0PSIxMDYuMDQiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDc4MS45NCAyMzM4LjYpIHJvdGF0ZSgtMTgwKSIvPjxwYXRoIGNsYXNzPSJjbHMtMiIgZD0iTTQ4NS42OSwxMDc2LjUxaDIzNC44OXYyOC4zN2MwLDY0LjgyLTUyLjYyLDExNy40NC0xMTcuNDQsMTE3LjQ0aDBjLTY0LjgyLDAtMTE3LjQ0LTUyLjYyLTExNy40NC0xMTcuNDR2LTI4LjM3aDBaIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgxMjA2LjI2IDIyOTguODMpIHJvdGF0ZSgtMTgwKSIvPjxwYXRoIGNsYXNzPSJjbHMtMiIgZD0iTTQ4NS42OSw5NjIuNzNoMGMwLTE3LjAyLDEzLjc1LTMwLjgxLDMwLjcxLTMwLjgxaDE3My40N2MxNi45NiwwLDMwLjcxLDEzLjgsMzAuNzEsMzAuODFoMGMwLDE0LjY5LTEwLjI1LDI2Ljk4LTIzLjk2LDMwLjA3LDAsMCwwLDAsMCwwLS4zNCwuMDctLjYsLjM4LS42LC43NSwwLC4zNiwuMjUsLjY3LC41OSwuNzUsMCwwLDAsMCwwLDAsMTMuNzIsMy4wOCwyMy45NiwxNS4zNywyMy45NiwzMC4wN2gwYzAsMTcuMDItMTMuNzUsMzAuODEtMzAuNzEsMzAuODFoLTE3My40N2MtMTYuOTYsMC0zMC43MS0xMy44LTMwLjcxLTMwLjgxaDBjMC0xNC42OSwxMC4yNS0yNi45OCwyMy45Ni0zMC4wNywwLDAsMCwwLDAsMCwuMzQtLjA3LC42LS4zOCwuNi0uNzVzLS4yNS0uNjctLjU5LS43NWMwLDAsMCwwLDAsMC0xMy43Mi0zLjA4LTIzLjk2LTE1LjM3LTIzLjk2LTMwLjA3WiIvPjxwYXRoIGNsYXNzPSJjbHMtMiIgZD0iTTUyMC4wOCw3MTAuMThsODMuMDQtODMsODMuMDQsODNjNDUuODYsNDUuODQsNDUuODYsMTIwLjE3LDAsMTY2LjAxaDBjLTQ1Ljg2LDQ1Ljg0LTEyMC4yMiw0NS44NC0xNjYuMDksMGgwYy00NS44Ni00NS44NC00NS44Ni0xMjAuMTcsMC0xNjYuMDFaIi8+PC9nPjwvZz48L2c+PC9zdmc+",
  autumn:
    "data:image/svg+xml;base64,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",
};

const CP_PATTERN_BG = {
  general:
    "data:image/svg+xml;base64,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",
  winter:
    "data:image/svg+xml;base64,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",
  spring:
    "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz48c3ZnIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgdmlld0JveD0iMCAwIDE0NjMgMTIzMyI+PGRlZnM+PHN0eWxlPi5jbHMtMXtmaWxsOiNmZjY5OWI7fS5jbHMtMntmaWxsOiM3MDA2MDY7fTwvc3R5bGU+PC9kZWZzPjxnIGlkPSJCYWNrZ3JvdW5kIj48cmVjdCBjbGFzcz0iY2xzLTIiIHdpZHRoPSIxNDYzIiBoZWlnaHQ9IjEyMzMiLz48L2c+PGcgaWQ9IlBhdHRlcm4iPjxnPjxnPjxwYXRoIGNsYXNzPSJjbHMtMSIgZD0iTTEwOC4wNSwyMjMuNDhoMGM1My43NCwwLDk3LjM4LDQzLjYzLDk3LjM4LDk3LjM4djI2LjRIMTAuNjd2LTI2LjRjMC01My43NCw0My42My05Ny4zOCw5Ny4zOC05Ny4zOFoiLz48cGF0aCBjbGFzcz0iY2xzLTEiIGQ9Ik0zOS4xNCw1NzcuMDNoMGMtMzguMDItMzguMzYtMzguMDItMTAwLjU0LDAtMTM4LjlsNjguODktNjkuNTEsNjguODksNjkuNTFjMzguMDIsMzguMzYsMzguMDIsMTAwLjU0LDAsMTM4LjloMGMtMzguMDQsMzguMzktOTkuNzMsMzguMzktMTM3Ljc3LDBaIi8+PHBvbHlnb24gY2xhc3M9ImNscy0xIiBwb2ludHM9IjEwLjY3IDIwMi4xMiAxMC42NyAxMC42OCAxMDkuNTkgMTAuNjggMjA1LjQzIDEwNi40IDEwOS41OSAyMDIuMTIgMTAuNjcgMjAyLjEyIi8+PHBhdGggY2xhc3M9ImNscy0xIiBkPSJNNTE0Ljg1LDI4Ny45M0gyMjYuNzZ2LTEyNy43M2MwLTgyLjU4LDYyLjI1LTE0OS41MiwxMzkuMDMtMTQ5LjUyaDEwLjAzYzc2Ljc5LDAsMTM5LjAzLDY2Ljk0LDEzOS4wMywxNDkuNTJ2MTI3LjczWiIvPjxwYXRoIGNsYXNzPSJjbHMtMSIgZD0iTTcxOS40LDQxMC42M2gtMTgyLjA5Yy0uNywwLTEuMTMtLjM1LTEuMTMtLjkzdi0xMjQuMzJjMC0uMywuMTItLjU5LC4zMy0uOGw5MS4wNC05MS40Yy40NC0uNDQsMS4xNi0uNDQsMS42LDBsOTEuMDQsOTEuNGMuMjEsLjIxLC4zMywuNSwuMzMsLjh2MTI0LjMyYzAsLjYyLS40OSwuOTMtMS4xMywuOTNaIi8+PHBhdGggY2xhc3M9ImNscy0xIiBkPSJNNzIwLjUzLDQ3NS40NWgwYzAtMjQtMTkuNC00My40Ni00My4zMi00My40NkgyNzAuMDhjLTIzLjkzLDAtNDMuMzIsMTkuNDYtNDMuMzIsNDMuNDZoMGMwLDIwLjcyLDE0LjQ1LDM4LjA1LDMzLjgsNDIuNCwwLDAtLjAxLDAtLjAxLDAsLjQ4LC4xMSwuODUsLjU0LC44NSwxLjA2cy0uMzYsLjk0LS44NCwxLjA1YzAsMCwwLDAsLjAxLDAtMTkuMzUsNC4zNS0zMy44MSwyMS42OC0zMy44MSw0Mi40aDBjMCwyNCwxOS40LDQzLjQ2LDQzLjMyLDQzLjQ2aDQwNy4xMmMyMy45MywwLDQzLjMyLTE5LjQ2LDQzLjMyLTQzLjQ2aDBjMC0yMC43Mi0xNC40Ni0zOC4wNS0zMy44LTQyLjQsMCwwLC4wMSwwLC4wMSwwLS40OC0uMTEtLjg1LS41NC0uODUtMS4wNnMuMzYtLjk0LC44NC0xLjA1YzAsMCwwLDAtLjAxLDAsMTkuMzUtNC4zNSwzMy44MS0yMS42OCwzMy44MS00Mi40WiIvPjxyZWN0IGNsYXNzPSJjbHMtMSIgeD0iMjI2Ljc2IiB5PSIzMDkuMjkiIHdpZHRoPSIxODMuMDciIGhlaWdodD0iMTAxLjM1Ii8+PHJlY3QgY2xhc3M9ImNscy0xIiB4PSI1MzYuMTkiIHk9IjEwLjY4IiB3aWR0aD0iMTg0LjM0IiBoZWlnaHQ9IjE2MC44MiIvPjxwYXRoIGNsYXNzPSJjbHMtMSIgZD0iTTUwMi42LDMzOC45N2wtMjkuNTktMjkuNjgtMjkuNTksMjkuNjhjLTE2LjM0LDE2LjM5LTE2LjM0LDQyLjk3LDAsNTkuMzdoMGMxNi4zNCwxNi4zOSw0Mi44NCwxNi4zOSw1OS4xOCwwaDBjMTYuMzQtMTYuMzksMTYuMzQtNDIuOTcsMC01OS4zN1oiLz48L2c+PGc+PHBhdGggY2xhc3M9ImNscy0xIiBkPSJNMTIzMi43NCwxMC43MWgwYzM2LjQyLDAsNjUuOTksMjkuNTcsNjUuOTksNjUuOTlWMjUwLjYxaC0xMzEuOThWNzYuN2MwLTM2LjQyLDI5LjU3LTY1Ljk5LDY1Ljk5LTY1Ljk5WiIvPjxwYXRoIGNsYXNzPSJjbHMtMSIgZD0iTTE0MTkuMjUsMjUwLjYxaDBjMTguMjcsMCwzMy4wNy0xNC44MSwzMy4wNy0zMy4wN1Y0My43NWMwLTE4LjI3LTE0LjgxLTMzLjA3LTMzLjA3LTMzLjA3aDBjLTE1Ljc3LDAtMjguOTYsMTEuMDQtMzIuMjcsMjUuOCwwLDAsMC0uMDEsMCwwLS4wOCwuMzctLjQxLC42NS0uOCwuNjVzLS43Mi0uMjctLjgtLjY0YzAsMCwwLDAsMCwwLTMuMzEtMTQuNzctMTYuNS0yNS44MS0zMi4yNy0yNS44MWgwYy0xOC4yNywwLTMzLjA3LDE0LjgxLTMzLjA3LDMzLjA3VjIxNy41M2MwLDE4LjI3LDE0LjgxLDMzLjA3LDMzLjA3LDMzLjA3aDBjMTUuNzcsMCwyOC45Ni0xMS4wNCwzMi4yNy0yNS44LDAsMCwwLC4wMSwwLDAsLjA4LS4zNywuNDEtLjY1LC44LS42NXMuNzIsLjI3LC44LC42NGMwLDAsMCwwLDAsMCwzLjMxLDE0Ljc3LDE2LjUsMjUuODEsMzIuMjcsMjUuODFaIi8+PHBhdGggY2xhc3M9ImNscy0xIiBkPSJNMTQ1Mi4zMywzNTUuNDFoMGMwLTQ2LjEtMzcuMjktODMuNDctODMuMjgtODMuNDdoLTExOS4wMmMtNDUuOTksMC04My4yOCwzNy4zNy04My4yOCw4My40N2gwYzAsMzkuNzksMjcuNzksNzMuMDgsNjQuOTcsODEuNDQsMCwwLS4wMywwLS4wMiwwLC45MywuMiwxLjYzLDEuMDMsMS42MywyLjAzcy0uNjksMS44MS0xLjYxLDIuMDJjMCwwLC4wMiwwLC4wMiwwLTM3LjIsOC4zNS02NSw0MS42NC02NSw4MS40NWgwYzAsNDYuMSwzNy4yOSw4My40Nyw4My4yOCw4My40N2gxMTkuMDJjNDUuOTksMCw4My4yOC0zNy4zNyw4My4yOC04My40N2gwYzAtMzkuNzktMjcuNzktNzMuMDgtNjQuOTctODEuNDQsMCwwLC4wMywwLC4wMiwwLS45My0uMi0xLjYzLTEuMDMtMS42My0yLjAzcy42OS0xLjgxLDEuNjEtMi4wMmMwLDAtLjAyLDAtLjAyLDAsMzcuMi04LjM1LDY0Ljk5LTQxLjY0LDY0Ljk5LTgxLjQ1WiIvPjxwb2x5Z29uIGNsYXNzPSJjbHMtMSIgcG9pbnRzPSIxMTQ1LjQ1IDQ3OC40NCA5OTguNjEgNDc4LjQ0IDk5OC42MSAyNTEuOTUgMTA3Mi4wMyAxNzguMjEgMTE0NS40NSAyNTEuOTUgMTE0NS40NSA0NzguNDQiLz48cGF0aCBjbGFzcz0iY2xzLTEiIGQ9Ik0xMTQ1LjQ1LDQ3LjIzaDBjMC0yMC4xOS0xNi4zMy0zNi41NS0zNi40Ny0zNi41NWgtNzMuODljLTIwLjE0LDAtMzYuNDcsMTYuMzYtMzYuNDcsMzYuNTVoMGMwLDE3LjQzLDEyLjE3LDMyLDI4LjQ1LDM1LjY2LDAsMC0uMDEsMC0uMDEsMCwuNDEsLjA5LC43MSwuNDUsLjcxLC44OXMtLjMsLjc5LS43LC44OWMwLDAsMCwwLC4wMSwwLTE2LjI5LDMuNjYtMjguNDYsMTguMjMtMjguNDYsMzUuNjZoMGMwLDIwLjE5LDE2LjMzLDM2LjU1LDM2LjQ3LDM2LjU1aDczLjg5YzIwLjE0LDAsMzYuNDctMTYuMzYsMzYuNDctMzYuNTVoMGMwLTE3LjQzLTEyLjE3LTMyLTI4LjQ1LTM1LjY2LDAsMCwuMDEsMCwuMDEsMC0uNDEtLjA5LS43MS0uNDUtLjcxLS44OXMuMy0uNzksLjctLjg5YzAsMCwwLDAsMCwwLDE2LjI5LTMuNjYsMjguNDYtMTguMjMsMjguNDYtMzUuNjZaIi8+PHJlY3QgY2xhc3M9ImNscy0xIiB4PSI5OTguNjEiIHk9IjQ5OS43OCIgd2lkdGg9IjE0Ni44MyIgaGVpZ2h0PSIxMDYuMDQiLz48cGF0aCBjbGFzcz0iY2xzLTEiIGQ9Ik04NTkuODcsNDYwLjAxaDBjNjQuODIsMCwxMTcuNDQsNTIuNjIsMTE3LjQ0LDExNy40NHYyOC4zN2gtMjM0Ljg5di0yOC4zN2MwLTY0LjgyLDUyLjYyLTExNy40NCwxMTcuNDQtMTE3LjQ0WiIvPjxwYXRoIGNsYXNzPSJjbHMtMSIgZD0iTTk3Ny4zMSwzNDYuMjNoMGMwLTE3LjAyLTEzLjc1LTMwLjgxLTMwLjcxLTMwLjgxaC0xNzMuNDdjLTE2Ljk2LDAtMzAuNzEsMTMuOC0zMC43MSwzMC44MWgwYzAsMTQuNjksMTAuMjUsMjYuOTgsMjMuOTYsMzAuMDcsMCwwLDAsMCwwLDAsLjM0LC4wNywuNiwuMzgsLjYsLjc1cy0uMjUsLjY3LS41OSwuNzVjMCwwLDAsMCwwLDAtMTMuNzIsMy4wOC0yMy45NiwxNS4zNy0yMy45NiwzMC4wN2gwYzAsMTcuMDIsMTMuNzUsMzAuODEsMzAuNzEsMzAuODFoMTczLjQ3YzE2Ljk2LDAsMzAuNzEtMTMuOCwzMC43MS0zMC44MWgwYzAtMTQuNjktMTAuMjUtMjYuOTgtMjMuOTYtMzAuMDcsMCwwLDAsMCwwLDAtLjM0LS4wNy0uNi0uMzgtLjYtLjc1cy4yNS0uNjcsLjU5LS43NWMwLDAsMCwwLDAsMCwxMy43Mi0zLjA4LDIzLjk2LTE1LjM3LDIzLjk2LTMwLjA3WiIvPjxwYXRoIGNsYXNzPSJjbHMtMSIgZD0iTTk0Mi45Miw5My42OEw4NTkuODcsMTAuNjhsLTgzLjA0LDgzYy00NS44Niw0NS44NC00NS44NiwxMjAuMTcsMCwxNjYuMDFoMGM0NS44Niw0NS44NCwxMjAuMjIsNDUuODQsMTY2LjA5LDBoMGM0NS44Ni00NS44NCw0NS44Ni0xMjAuMTcsMC0xNjYuMDFaIi8+PC9nPjxnPjxwYXRoIGNsYXNzPSJjbHMtMSIgZD0iTTEyNTcuNTcsODM5Ljk4aDE5NC43NXYyNi40YzAsNTMuNzQtNDMuNjMsOTcuMzgtOTcuMzgsOTcuMzhoMGMtNTMuNzQsMC05Ny4zOC00My42My05Ny4zOC05Ny4zOHYtMjYuNGgwWiIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoMjcwOS45IDE4MDMuNzMpIHJvdGF0ZSgtMTgwKSIvPjxwYXRoIGNsYXNzPSJjbHMtMSIgZD0iTTEyODYuMDksMTE5My41M2gwYy0zOC4wMi0zOC4zNi0zOC4wMi0xMDAuNTQsMC0xMzguOWw2OC44OS02OS41MSw2OC44OSw2OS41MWMzOC4wMiwzOC4zNiwzOC4wMiwxMDAuNTQsMCwxMzguOWgwYy0zOC4wNCwzOC4zOS05OS43MywzOC4zOS0xMzcuNzcsMFoiLz48cG9seWdvbiBjbGFzcz0iY2xzLTEiIHBvaW50cz0iMTQ1Mi4zMyA4MTguNjIgMTQ1Mi4zMyA2MjcuMTggMTM1My40MSA2MjcuMTggMTI1Ny41NyA3MjIuOSAxMzUzLjQxIDgxOC42MiAxNDUyLjMzIDgxOC42MiIvPjxwYXRoIGNsYXNzPSJjbHMtMSIgZD0iTTk0OC4xNSw5MDQuNDNoMjg4LjFzMC0xMjcuNzMsMC0xMjcuNzNjMC04Mi41OC02Mi4yNS0xNDkuNTItMTM5LjAzLTE0OS41MmgtMTAuMDNjLTc2Ljc5LDAtMTM5LjAzLDY2Ljk0LTEzOS4wMywxNDkuNTJ2MTI3LjczWiIvPjxwYXRoIGNsYXNzPSJjbHMtMSIgZD0iTTc0My42LDEwMjcuMTNoMTgyLjA5Yy43LDAsMS4xMy0uMzUsMS4xMy0uOTN2LTEyNC4zMmMwLS4zLS4xMi0uNTktLjMzLS44bC05MS4wNC05MS40Yy0uNDQtLjQ0LTEuMTYtLjQ0LTEuNiwwbC05MS4wNCw5MS40Yy0uMjEsLjIxLS4zMywuNS0uMzMsLjh2MTI0LjMyYzAsLjYyLC40OSwuOTMsMS4xMywuOTNaIi8+PHBhdGggY2xhc3M9ImNscy0xIiBkPSJNNzQyLjQ3LDEwOTEuOTVoMGMwLTI0LDE5LjQtNDMuNDYsNDMuMzItNDMuNDZoNDA3LjEyYzIzLjkzLDAsNDMuMzIsMTkuNDYsNDMuMzIsNDMuNDZoMGMwLDIwLjcyLTE0LjQ1LDM4LjA1LTMzLjgsNDIuNCwwLDAsLjAxLDAsLjAxLDAtLjQ4LC4xMS0uODUsLjU0LS44NSwxLjA2LDAsLjUxLC4zNiwuOTQsLjg0LDEuMDUsMCwwLDAsMC0uMDEsMCwxOS4zNSw0LjM1LDMzLjgxLDIxLjY4LDMzLjgxLDQyLjRoMGMwLDI0LTE5LjQsNDMuNDYtNDMuMzIsNDMuNDZoLTQwNy4xMmMtMjMuOTMsMC00My4zMi0xOS40Ni00My4zMi00My40NmgwYzAtMjAuNzIsMTQuNDYtMzguMDUsMzMuOC00Mi40LDAsMC0uMDEsMC0uMDEsMCwuNDgtLjExLC44NS0uNTQsLjg1LTEuMDYsMC0uNTEtLjM2LS45NC0uODQtMS4wNSwwLDAsMCwwLC4wMSwwLTE5LjM1LTQuMzUtMzMuODEtMjEuNjgtMzMuODEtNDIuNFoiLz48cmVjdCBjbGFzcz0iY2xzLTEiIHg9IjEwNTMuMTciIHk9IjkyNS43OSIgd2lkdGg9IjE4My4wNyIgaGVpZ2h0PSIxMDEuMzUiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDIyODkuNDEgMTk1Mi45Mikgcm90YXRlKC0xODApIi8+PHJlY3QgY2xhc3M9ImNscy0xIiB4PSI3NDIuNDciIHk9IjYyNy4xOCIgd2lkdGg9IjE4NC4zNCIgaGVpZ2h0PSIxNjAuODIiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDE2NjkuMjkgMTQxNS4xNykgcm90YXRlKC0xODApIi8+PHBhdGggY2xhc3M9ImNscy0xIiBkPSJNOTYwLjQsOTU1LjQ3bDI5LjU5LTI5LjY4LDI5LjU5LDI5LjY4YzE2LjM0LDE2LjM5LDE2LjM0LDQyLjk3LDAsNTkuMzdoMGMtMTYuMzQsMTYuMzktNDIuODQsMTYuMzktNTkuMTgsMGgwYy0xNi4zNC0xNi4zOS0xNi4zNC00Mi45NywwLTU5LjM3WiIvPjwvZz48Zz48cGF0aCBjbGFzcz0iY2xzLTEiIGQ9Ik0xNjQuMjcsNjI3LjIxaDEzMS45OHYxNzMuOTFjMCwzNi40Mi0yOS41Nyw2NS45OS02NS45OSw2NS45OWgwYy0zNi40MiwwLTY1Ljk5LTI5LjU3LTY1Ljk5LTY1Ljk5di0xNzMuOTFoMFoiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDQ2MC41MyAxNDk0LjMyKSByb3RhdGUoLTE4MCkiLz48cGF0aCBjbGFzcz0iY2xzLTEiIGQ9Ik00My43NSw4NjcuMTFoMGMtMTguMjcsMC0zMy4wNy0xNC44MS0zMy4wNy0zMy4wN3YtMTczLjc4YzAtMTguMjcsMTQuODEtMzMuMDcsMzMuMDctMzMuMDdoMGMxNS43NywwLDI4Ljk2LDExLjA0LDMyLjI3LDI1LjgsMCwwLDAtLjAxLDAsMCwuMDgsLjM3LC40MSwuNjUsLjgsLjY1cy43Mi0uMjcsLjgtLjY0YzAsMCwwLDAsMCwwLDMuMzEtMTQuNzcsMTYuNS0yNS44MSwzMi4yNy0yNS44MWgwYzE4LjI3LDAsMzMuMDcsMTQuODEsMzMuMDcsMzMuMDd2MTczLjc4YzAsMTguMjctMTQuODEsMzMuMDctMzMuMDcsMzMuMDdoMGMtMTUuNzcsMC0yOC45Ni0xMS4wNC0zMi4yNy0yNS44LDAsMCwwLC4wMSwwLDAtLjA4LS4zNy0uNDEtLjY1LS44LS42NXMtLjcyLC4yNy0uOCwuNjRjMCwwLDAsMCwwLDAtMy4zMSwxNC43Ny0xNi41LDI1LjgxLTMyLjI3LDI1LjgxWiIvPjxwYXRoIGNsYXNzPSJjbHMtMSIgZD0iTTEwLjY3LDk3MS45MWgwYzAtNDYuMSwzNy4yOS04My40Nyw4My4yOC04My40N2gxMTkuMDJjNDUuOTksMCw4My4yOCwzNy4zNyw4My4yOCw4My40N2gwYzAsMzkuNzktMjcuNzksNzMuMDgtNjQuOTcsODEuNDQsMCwwLC4wMywwLC4wMiwwLS45MywuMi0xLjYzLDEuMDMtMS42MywyLjAzLDAsLjk5LC42OSwxLjgxLDEuNjEsMi4wMiwwLDAtLjAyLDAtLjAyLDAsMzcuMiw4LjM1LDY1LDQxLjY0LDY1LDgxLjQ1aDBjMCw0Ni4xLTM3LjI5LDgzLjQ3LTgzLjI4LDgzLjQ3SDkzLjk1Yy00NS45OSwwLTgzLjI4LTM3LjM3LTgzLjI4LTgzLjQ3aDBjMC0zOS43OSwyNy43OS03My4wOCw2NC45Ny04MS40NCwwLDAtLjAzLDAtLjAyLDAsLjkzLS4yLDEuNjMtMS4wMywxLjYzLTIuMDMsMC0uOTktLjY5LTEuODEtMS42MS0yLjAyLDAsMCwuMDIsMCwuMDIsMC0zNy4yLTguMzUtNjQuOTktNDEuNjQtNjQuOTktODEuNDVaIi8+PHBvbHlnb24gY2xhc3M9ImNscy0xIiBwb2ludHM9IjMxNy41NSAxMDk0Ljk0IDQ2NC4zOSAxMDk0Ljk0IDQ2NC4zOSA4NjguNDUgMzkwLjk3IDc5NC43MSAzMTcuNTUgODY4LjQ1IDMxNy41NSAxMDk0Ljk0Ii8+PHBhdGggY2xhc3M9ImNscy0xIiBkPSJNMzE3LjU1LDY2My43M2gwYzAtMjAuMTksMTYuMzMtMzYuNTUsMzYuNDctMzYuNTVoNzMuODljMjAuMTQsMCwzNi40NywxNi4zNiwzNi40NywzNi41NWgwYzAsMTcuNDMtMTIuMTcsMzItMjguNDUsMzUuNjYsMCwwLC4wMSwwLC4wMSwwLS40MSwuMDktLjcxLC40NS0uNzEsLjg5cy4zLC43OSwuNywuODljMCwwLDAsMC0uMDEsMCwxNi4yOSwzLjY2LDI4LjQ2LDE4LjIzLDI4LjQ2LDM1LjY2aDBjMCwyMC4xOS0xNi4zMywzNi41NS0zNi40NywzNi41NWgtNzMuODljLTIwLjE0LDAtMzYuNDctMTYuMzYtMzYuNDctMzYuNTVoMGMwLTE3LjQzLDEyLjE3LTMyLDI4LjQ1LTM1LjY2LDAsMC0uMDEsMC0uMDEsMCwuNDEtLjA5LC43MS0uNDUsLjcxLS44OXMtLjMtLjc5LS43LS44OWMwLDAsMCwwLDAsMC0xNi4yOS0zLjY2LTI4LjQ2LTE4LjIzLTI4LjQ2LTM1LjY2WiIvPjxyZWN0IGNsYXNzPSJjbHMtMSIgeD0iMzE3LjU1IiB5PSIxMTE2LjI4IiB3aWR0aD0iMTQ2LjgzIiBoZWlnaHQ9IjEwNi4wNCIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoNzgxLjk0IDIzMzguNikgcm90YXRlKC0xODApIi8+PHBhdGggY2xhc3M9ImNscy0xIiBkPSJNNDg1LjY5LDEwNzYuNTFoMjM0Ljg5djI4LjM3YzAsNjQuODItNTIuNjIsMTE3LjQ0LTExNy40NCwxMTcuNDRoMGMtNjQuODIsMC0xMTcuNDQtNTIuNjItMTE3LjQ0LTExNy40NHYtMjguMzdoMFoiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDEyMDYuMjYgMjI5OC44Mykgcm90YXRlKC0xODApIi8+PHBhdGggY2xhc3M9ImNscy0xIiBkPSJNNDg1LjY5LDk2Mi43M2gwYzAtMTcuMDIsMTMuNzUtMzAuODEsMzAuNzEtMzAuODFoMTczLjQ3YzE2Ljk2LDAsMzAuNzEsMTMuOCwzMC43MSwzMC44MWgwYzAsMTQuNjktMTAuMjUsMjYuOTgtMjMuOTYsMzAuMDcsMCwwLDAsMCwwLDAtLjM0LC4wNy0uNiwuMzgtLjYsLjc1LDAsLjM2LC4yNSwuNjcsLjU5LC43NSwwLDAsMCwwLDAsMCwxMy43MiwzLjA4LDIzLjk2LDE1LjM3LDIzLjk2LDMwLjA3aDBjMCwxNy4wMi0xMy43NSwzMC44MS0zMC43MSwzMC44MWgtMTczLjQ3Yy0xNi45NiwwLTMwLjcxLTEzLjgtMzAuNzEtMzAuODFoMGMwLTE0LjY5LDEwLjI1LTI2Ljk4LDIzLjk2LTMwLjA3LDAsMCwwLDAsMCwwLC4zNC0uMDcsLjYtLjM4LC42LS43NSwwLS4zNi0uMjUtLjY3LS41OS0uNzUsMCwwLDAsMCwwLDAtMTMuNzItMy4wOC0yMy45Ni0xNS4zNy0yMy45Ni0zMC4wN1oiLz48cGF0aCBjbGFzcz0iY2xzLTEiIGQ9Ik01MjAuMDgsNzEwLjE4bDgzLjA0LTgzLDgzLjA0LDgzYzQ1Ljg2LDQ1Ljg0LDQ1Ljg2LDEyMC4xNywwLDE2Ni4wMWgwYy00NS44Niw0NS44NC0xMjAuMjIsNDUuODQtMTY2LjA5LDBoMGMtNDUuODYtNDUuODQtNDUuODYtMTIwLjE3LDAtMTY2LjAxWiIvPjwvZz48L2c+PC9nPjwvc3ZnPg==",
  summer:
    "data:image/svg+xml;base64,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",
  autumn:
    "data:image/svg+xml;base64,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",
};

// Promo Theme backgrounds voor fullscreen promo
const PROMO_THEMES = {
  default: {
    id: "default",
    name: "Standaard (Groen)",
    colors: { primary: "#68A357", secondary: "#00192F" },
    background:
      "linear-gradient(135deg, #1a472a 0%, #2d5016 50%, #1a472a 100%)",
    pattern:
      "data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E",
  },
  spring: {
    id: "spring",
    name: "Spring Celebrations (Roze)",
    colors: { primary: "#E691B7", secondary: "#700606" },
    background:
      "linear-gradient(135deg, #ff6b9d 0%, #ffa8c5 50%, #ff6b9d 100%)",
    pattern:
      "data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1' fill-rule='evenodd'%3E%3Cpath d='M0 38.59l2.83-2.83 1.41 1.41L1.41 40H0v-1.41zM0 1.4l2.83 2.83 1.41-1.41L1.41 0H0v1.41zM38.59 40l-2.83-2.83 1.41-1.41L40 38.59V40h-1.41zM40 1.41l-2.83 2.83-1.41-1.41L38.59 0H40v1.41zM20 18.6l2.83-2.83 1.41 1.41L21.41 20l2.83 2.83-1.41 1.41L20 21.41l-2.83 2.83-1.41-1.41L18.59 20l-2.83-2.83 1.41-1.41L20 18.59z'/%3E%3C/g%3E%3C/svg%3E",
  },
  summer: {
    id: "summer",
    name: "Summer/Autumn (Geel/Oranje)",
    colors: { primary: "#EE9C3F", secondary: "#7A5226" },
    background:
      "linear-gradient(135deg, #ff9a3c 0%, #ffb84d 50%, #ff9a3c 100%)",
    pattern:
      "data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.08'%3E%3Cpath d='M50 50c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10s-10-4.477-10-10 4.477-10 10-10zM10 10c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10S0 25.523 0 20s4.477-10 10-10zm10 8c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8zm40 40c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E",
  },
  winter: {
    id: "winter",
    name: "Winter Wonders (Blauw)",
    colors: { primary: "#9DB4F6", secondary: "#00192F" },
    background:
      "linear-gradient(135deg, #4a7ba7 0%, #9DB4F6 50%, #4a7ba7 100%)",
    pattern:
      "data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 20.5V18H0v-2h20v-2H0v-2h20v-2H0V8h20v-2H0V4h20v-2H0V0h22v20h2V0h2v20h2V0h2v20h2V0h2v20h2V0h2v20h2V0h2v20h2v2H20v18h-2V22h-2v18h-2V22h-2v18h-2V22h-2v18h-2V22H8v18H6V22H4v18H2V22H0v-2h20z' fill='%23ffffff' fill-opacity='0.06' fill-rule='evenodd'/%3E%3C/svg%3E",
  },
};

const TASK_CATS = {
  // 🔴 DAGSPECIFIEKE CATEGORIEËN (bovenaan, Sunset rood)
  daySpecificEntertainment: {
    label: "Dagspecifiek Entertainment",
    icon: Star,
    color: C.Sunset, // 🔴 Rood
    sortOrder: -3,
  },
  daySpecificCrea: {
    label: "Dagspecifiek Crea",
    icon: Star,
    color: C.Sunset, // 🔴 Rood
    sortOrder: -2,
  },
  daySpecificSpringkussen: {
    label: "Dagspecifiek Springkussen",
    icon: Star,
    color: C.Sunset, // 🔴 Rood
    sortOrder: -1,
  },

  // 🌲 OPSTART (Pine groen)
  startup: {
    label: "Opstart",
    icon: Sun,
    color: C.Pine, // 🌲 Groen
    sortOrder: 1,
  },

  // 🪵 TIJDENS DIENST (Bark bruin)
  during: {
    label: "Tijdens Dienst",
    icon: Clock,
    color: C.Bark, // 🪵 Bruin
    sortOrder: 2,
  },

  // 🌊 AFSLUITING (Lagoon teal)
  closing: {
    label: "Afsluiting",
    icon: Moon,
    color: C.Lagoon, // 🌊 Teal
    sortOrder: 3,
  },

  // 🌿 EXTRA TAKEN (Pine groen)
  extra: {
    label: "Extra Taken",
    icon: Repeat,
    color: C.Pine, // 🌿 Groen
    sortOrder: 4,
  },
};

const COLOR_OPTIONS = [
  { value: C.Pine, label: "Pine" },
  { value: C.Grass, label: "Grass" },
  { value: C.Sunset, label: "Sunset" },
  { value: C.Sky, label: "Sky" },
  { value: C.Lagoon, label: "Lagoon" },
  { value: C.Honey, label: "Honey" },
];

// 🌅 Zonsopkomst en -ondergang bij het park (NOAA-benadering, geen internet nodig)
const zonTijden = (datum = new Date(), lat = 51.4485, lon = 5.9627) => {
  const rad = Math.PI / 180;
  const y = datum.getFullYear();
  const begin = Date.UTC(datum.getFullYear(), datum.getMonth(), datum.getDate());
  const dag = Math.round((begin - Date.UTC(y, 0, 0)) / 864e5);
  const g = ((2 * Math.PI) / 365) * (dag - 1);
  const vgl = 229.18 * (0.000075 + 0.001868 * Math.cos(g) - 0.032077 * Math.sin(g) - 0.014615 * Math.cos(2 * g) - 0.040849 * Math.sin(2 * g));
  const decl = 0.006918 - 0.399912 * Math.cos(g) + 0.070257 * Math.sin(g) - 0.006758 * Math.cos(2 * g) + 0.000907 * Math.sin(2 * g) - 0.002697 * Math.cos(3 * g) + 0.00148 * Math.sin(3 * g);
  const ha = Math.acos(Math.cos(90.833 * rad) / (Math.cos(lat * rad) * Math.cos(decl)) - Math.tan(lat * rad) * Math.tan(decl)) / rad;
  return {
    op: new Date(begin + (720 - 4 * (lon + ha) - vgl) * 60000),
    onder: new Date(begin + (720 - 4 * (lon - ha) - vgl) * 60000),
  };
};
const isDonkerBuiten = (nu = new Date()) => {
  const { op, onder } = zonTijden(nu);
  return nu < op || nu >= onder;
};

const DEFAULT_STAFF_NAMES = [
  "Quincy",
  "Rens",
  "Mieke",
  "Daan",
  "Bo",
  "Eline",
  "Daphne",
  "Noor",
  "Louka",
  "Milou",
  "Kim",
  "Jeanine",
  "Amée",
  "Amara",
];
const LOCATIONS = ["Crea Club", "Podium Market Dome", "Overig"];
const ACTIVITY_OPTIONS = [
  "Springkussenfestival",
  "Kids Disco",
  "Voorleesverhaaltjes",
  "Op Avontuur...",
  "Natuurbingo",
  "Meet & Greet",
  "Show: de Muziekmachine",
  "Live Muziek",
  "Crazy Bingo Game",
  "Family Quiz Night",
  "DJ Night",
  "Walk-In Kids Workshops",
  "Schminken",
  "Glittertattoo & Hairbeads",
  "Workshop: Nachtlampje",
  "Workshop: Maak je eigen Knuffel",
  "Workshop: Armbandjes",
  "Wannabe",
  "Knutselen",
  "Wax Hands",
];
const ACTIVITY_TYPES = [
  {
    id: "crea",
    label: "Crea Club",
    bg: C.Sky,
    text: C.Lagoon,
    icon: IcCrea,
  },
  {
    id: "orry",
    label: "Orry & Friends",
    bg: C.Blossom,
    text: C.Sunset,
    icon: IcOrry,
  },
  {
    id: "entertainment",
    label: "Entertainment",
    bg: C.Honey,
    text: C.Bark,
    icon: IcEntertainment,
  },
  {
    id: "sport",
    label: "Sport & Spel",
    bg: C.Grass,
    text: C.Pine,
    icon: IcSport,
  },
];
const BASE_ROLES = ["Orry", "Bing", "Woops", "Rep"];

const NATIONALITIES = [
  { k: "nl", l: "Nederland", i: "🇳🇱", color: C.Honey },
  { k: "be_nl", l: "België (VL)", i: "🇧🇪", color: C.Sky },
  { k: "be_fr", l: "België (WA)", i: "🇧🇪", color: C.Lagoon },
  { k: "de", l: "Duitsland", i: "🇩🇪", color: C.Grass },
  { k: "en", l: "Engeland", i: "🇬🇧", color: C.Pine },
  { k: "fr", l: "Frankrijk", i: "🇫🇷", color: C.Blossom },
  { k: "ch", l: "Zwitserland", i: "🇨🇭", color: C.Sunset },
  { k: "other", l: "Overig", i: "🌍", color: C.Bark },
];

// --- DATA HELPERS ---
// 🔥 Remove undefined values for Firebase compatibility
// Firebase does not accept undefined values - only null or omit the field
const removeUndefined = (obj) => {
  if (obj === null || obj === undefined) {
    return null;
  }

  if (Array.isArray(obj)) {
    return obj.map(removeUndefined).filter((item) => item !== null);
  }

  if (typeof obj === "object") {
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        const cleanedValue = removeUndefined(value);
        if (cleanedValue !== null || value === null) {
          cleaned[key] = cleanedValue;
        }
      }
    }
    return cleaned;
  }

  return obj;
};

// 🗜️ Strip default/empty fields from schedule items before saving
// Velden met default-waarden worden weggelaten; bij inladen vul je ze terug met || default
const SCHEDULE_FIELD_DEFAULTS = {
  refhd: "",
  paris: false,
  villageMap: false,
  linkedManuals: [],
  remarks: "",
  infoText: "",
  infoTextEn: "",
  promoInfoNL: "",
  promoInfoEN: "",
  photo: null,
  price: "",
};

const stripScheduleDefaults = (item) => {
  const stripped = { ...item };
  for (const [field, defaultVal] of Object.entries(SCHEDULE_FIELD_DEFAULTS)) {
    const val = stripped[field];
    if (val === defaultVal) {
      delete stripped[field];
    } else if (
      Array.isArray(defaultVal) &&
      Array.isArray(val) &&
      val.length === 0
    ) {
      delete stripped[field];
    } else if (val === null && defaultVal === null) {
      delete stripped[field];
    } else if (val === "" && defaultVal === "") {
      delete stripped[field];
    }
  }
  return stripped;
};

const SEED_PROFILE_PHOTOS = {
  Jarno:
    "data:image/jpeg;base64,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",
  Amara:
    "data:image/jpeg;base64,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",
  Amée: "data:image/jpeg;base64,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",
  Bo: "data:image/jpeg;base64,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",
  Daan: "data:image/jpeg;base64,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",
  Daphne:
    "data:image/jpeg;base64,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",
  Eline:
    "data:image/jpeg;base64,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",
  Jeanine:
    "data:image/jpeg;base64,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",
  Louka:
    "data:image/jpeg;base64,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",
  Maik: "data:image/jpeg;base64,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",
  Mieke:
    "data:image/jpeg;base64,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",
  Milou:
    "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAgGBgcGBQgHBwcJCQgKDBQNDAsLDBkSEw8UHRofHh0aHBwgJC4nICIsIxwcKDcpLDAxNDQ0Hyc5PTgyPC4zNDL/2wBDAQkJCQwLDBgNDRgyIRwhMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjL/wAARCADIAMgDASIAAhEBAxEB/8QAHAAAAQUBAQEAAAAAAAAAAAAAAAMEBQYHAgEI/8QAOxAAAgEDAgMGAwYGAQQDAAAAAQIDAAQRBSESMUEGEyJRYXEUMoEHQpGhscEjM1Ji0fAVFnKC8UNj4f/EABoBAAIDAQEAAAAAAAAAAAAAAAACAQMFBAb/xAAnEQADAAICAgEDBAMAAAAAAAAAAQIDESExBBJBBRMiMlFhcRQjQv/aAAwDAQACEQMRAD8A1OiiiskygooooAKKKKACiiigAoo5czRkedGmT6sKKM0UEaaCiiigAooooAKKKKACiiigAooooAKKKKACiiigAooooAKKKKACiiigApITBmKoC2OZpK4Z3/hptxc28hQXSCECMdNs9fWnlGj4vjLXvRzOwLqJHK+imkZdU0+xUIvET/Uylq4h8RkkkBY/2neo++SKVieG6T1XB/I074NGYXwh+mtwS/y3jbfkRinCajG5A2U9M8j7GqXPZlWLR3IJ/vBQ/wCPzpKO+u7I8NxGWU9fP68j/u9IyXil8NGhpMjbZwfI0pVXstTWZA6nvI/vDqP3qZt7rbJbijPJvL3pDky/T5rmOB/RXCSBiR1Fd0GTcVFetdhRRRQKFFFFABRRRQAUUUUAFFFFABRRRQAUUUUAFeGvaYanfLZWkkmfFg8PvQi3BjeS1Ix1DVUt2ZVcZBwfLPlTC1vLzUZCYGbu844kBwfxFRMMhvr2y01AGe4zJMxGSqjnj3rQoLeCzt1iiQAKMbVZE+3J6DShaQxs7SSIZkZmP9xpZo0J3ANLsSaRcY58q6FKQvY2ms7eUENGpHtUZcdm4nRvhJ2hJ+4w4kP/AIn9sVOqF+tM59W0+1bhkuY+L+kHJ/CoqF8jJtdFGvbHUNGn78Rd042DqS0TjyPVfry86ldK1yKWMSpshPBPG3OJvX0NWT4+wv07tX3PRlxVD7R6bNoeoDUtPjAUjE0AHhkX09fSueoXwOm/kvEVyIWDMf4J2BP3D/ipUHNZ7pWvxyQpuJLWUeHPNfNT6irNpd9wOtsz8a8PFC5PzL5H1FVJ64OXy/F+7Psu0TtFeAgjIr2mMEKKKKACiiigAooooAKKKKACiiigAooooA8Y4FUvtZqHCXhVsHAX8edXGZgqEnkKyfX7w3N8w/qk5+ec/sKhmr9NhNuiU7HP33aS6lG4gt0jB9960tE8HG1Zh9nUim81OQnAabBJ8lGKtup9pJHc2+mW7zEbGTHhBq/G0lyaVpt8E68qjrTZ7jiHhXI61VP+Uuosm9mXi/pHSrJpMkd5p5mjbO/OrptN6Iceq2yJ1GeSTijM/Ajc8VEQX2haeveloe8zjvpnA4j6Z3P0GKktU0yS8uJASQrLhSNwD5kdaYQ9irJzE1zbLcyRjHG54eLfO+Of7VHLe2M+uBW17S2N3cqltLHKzdIfHt64G1WK4tYNW05opU5j6ik7HSo7RAI4Y4lAwEiXhAqUjAVKVy+w6MW1qyu+x+ovLKDJp0z+JgPkPQ+lS+ma4F7uRJA0JYEEfdPmPer52hsItQ0meGSNXyp2I5isUsrOTRbkRgkwmQqVJzjfb8qpySmt/I0b3o3bT7tZY1wcqwypqQqqdl7hZ9OCfehOAR5cxVmhk40GedVpmN5/j+le89MVoooqTOCiiigAooooAKKKKACiiigAooooAaaixSzkYf01ivaG4+Hms5GOOOXi+mcVtWoAGzkydsY96w37RYxA0IU/Io39c00rdaNnwU5wtkx2InLahqFqp5sXx5jI/wA1P6jrFxCHt7O3aabGEiTbjPqRyH61Q/s5vu87WEZzxRn67b1uVvpEAVih4Hfdyo3P1qxS1WjQVJyZ/p3Z/VL6Xj1EpG5IIihAUpv122z5Ek1oGmRxWVi8ZYDfHCOppZo7fToGESjiPWqfqt3qMklv8GFVOLEjOpOR1xjr707ensmJ9lonrq9tYZBx3CwhjjxMBk/XrXMWqEFtg6KccQFQS6fHdXEU13Cjyxg8DMMlc+WeVS0KwRLwK6D0zTLnkZ6XBKpfRyxhlYe1CXAHXNQ0yrG4KsAT5cjS8IbbPWldvoj1XwSjnvI2HMEYrPda0juGmiwXWTxrtnhPPar9C2MA01ntre/gmQhQW8JbqcdKXSfZE7TKvoM/wTwScQ7udQMjkf8Ac1d7SQEkj5W3FZvptxHdw6jaRMGNpcv3TIdiAenpzq49ndQFxbrxn036VzNetaE8jGsmNosgorwV7THmaTT0wooooICiiigAooooAKKKKACuXOATXVJzHEZNCLMMe9qSF1C7ZUkZuag4HlWP/aCxld15lQM1p2ozcfGq75fFZ12thEnxDkffA3oxP8tnpXKUaRVOxM76friXmwSPAbJ5g19GW1+r2iSRsCHAII8q+ftJWG3uhJIR3TjgbPJQeRrU9Dnkh06KHi4lweEE8sdK6Lb9thjlepY725Zkbfc0zW7t7C1aaZQSNlB6mkcyTDA+byqB12y1i4urYWkSSQRycUilwCR1FKqdMt4XZM2X/J67xS28QS34gCz7DHmBzNPb7QrKyLC51GeWfYrHFjJHUYoS/wCGyS3eRoU4eDuINjj1bn+FcopdwkaLEnXqTV8pNcsrfu3+yIux0GRtUN38TdLboDwQySZDN5kdParKNlX0ruNAsYCjYChgAAc0lJb4F2c8WGzWEdspL+z7VXUFveXEUN0TL3aSsFOSc7ZrbrifhUqvM7Csq7e2YGv6dMR80bR59cjH60stbZGRPXA67EQmzYx9Gi3q06Hdoly4RsAnPD69aqOkXYiu75wfDEgRfxrvTdS7vWCucZY8vWuXJtsuhaWjZba5EkQ8xTkHIzVXsL3IAztirBZzd7AredEvaMj6h4+v9iHNFFFMZIUUUUAFFFFABRRRQAUy1CQpGAObHFPt+Q60xupY/Fwji4QfEf2o6RoeBibye37FRyZbwL0DM2apPa6QCynbPzy4HsK0NolV2IG5B6eQzWZdrUlH8NgcK2CMc+pNLif5I3K/SypQXGBwk7Y3FXnsLqjXN4LCVs8ALoW8uWKzuNCJGUnmeVO1tbtAssMjxyjbjRiCPqPOu2kc0U09m6xXULXEscMyO8LYcKckdd/Kn6uk54lOG5EV856dq2pdmtVW8hkZ+9/mo5JEg9fX1rY9C7R2+q2cd0gaPi5q3Q+VVXDjldF0ZFfHTLctoX+6PcCnKWSxLkDifyJqLi1hYti4rmXXYypxIM1MWl2M0yXkYRnJIAxyqPub1VGFNRTapLNsNh5mlIImkId6i79ugU67HdsjSycb/Sql9o1oRY2t4o3hnXJ9DV2iAUCoztZp51Hs7eQKPGYyyf8AcNx+lRKIrky6wJUXGOcrZz6U1Fw0WsOeREm30qU0hFvLBZVGGUZZevrUVrFrLaXwkKko+4YCq/8ApobfCZpGiXyyxAE74wauukEfBqVOck1j+g35jvu6yeFkDD8K1vROM2ETYHLcetVzxRT5k7xkwD0Ne1y2wzXQORVjR5/NicchRRRUFIUUUUAFeZxXtctyNCGhbZw8y5KZwcEn2pjclY4cnGOZ/wAUlIjpN37g7vj3HSlXdGjLsAeEZApKfOj0eDGohJEYxAY7AALueu9UPtsEkVsLjOx/L/NXbOUkdycyPwrn8zVH7Vyqbjul3xjPuf8A1Rj/AFIvrplMsdI+KlVeSjxO3oKlYLcW99HEVJjOAxPrv+W1dWr/AA9hKwXBZj/+CnkxXuu84fnUkH3Xar6ptlcykiG17R40twxGOFifzqT7B3lrNJc2CHiUAMdsb+lLl11TQTKd3C8Lj1HWqx2bD6R2mLE4RjwHy35VYuYaYnVpo0+50+ZNo2yvkaQjtJgeQH0qfhZZol9RmvDDvyqnR0bG1taYwWOak4lwMUjGhxypwgxtTJCti6bb126h4WBHMUkNqUyeGmFbMpWzk03UrmOE8LxyMyoeToTUzbi3v7V45EDAc1dc8J9ccvepvV9BW+zIuUlHJhVWa2vLCUJdJIoXZLiMZKehHUVVabHnjoUTSLS0uo5VdreRT4QxypB6A+VX3RdWEUQjYAry9jWey30qKYL6ISI/yyRdfUf7mu9F1N7fUkt+MPDLnDcvbPrVLT7LKmanTRrvxiueFBg+p50tE4Ox2NV22nYxBT8wGV3qXtLlZ0DKRxLsw6g08vaMrzMCU6S4JCigbiimMQKKKKAOWOBmk7cvK/Ft3eSMnqfSubiVI4yWY77YXnXFvextAFRQudt+npUOtGx4fjaXs0J6xew26FVXjk5YG+KhJLwJAiAjjdst6AU61mReAxxEAsOf71UtRvVg4eFiSIGYt9cfrVO26NaZSlIWvtVUSsAxxGcDHl1qn3jtey95If5r8vKnkTNNfSQOSe8Qkf7+FRt+5gZPCQyKdvrV2OeRcj0j2QK800AwWQ8WByyRyrqIPLaheqZAHt0psC8OpJcYyrbn2qQlaOOXEWwkQyKfXIqxlcjbTA9peT2pGYbiMlP+5d/0qJE0ct7KFUEI/wAynJFTCy8d7aShcESDI8snhNUe9ZtP1ycRSFSrbHzBq/ClbeynM3GtG0aFfCazQcQYctqmg4J9ayrs7rjCQFSFl+8gPhetI065j1CDjiYhxs6HmpqLxOWPjzq+PkkQQBSiNk7HakRG1KqhVc0uizZ2CS2acxjI8qax5JwaexpketMkJTO1g4s5GRXp0+OQYZQacRKQAKdIop/Qrdsr112QsL5SsiFQf6ag7r7M3iZ5bG8Z8/8Axvsfoa0FRilY23xSvFLJWakZ7YSS2JSz1ImGZNv4nh4hy2PI1YrNUW4zFxeIb5FWC9sLLUrVoLyBZIz58x7HpVNvdC1vs/KLrQpX1GyT5rGUjjA/tP8Aj8Kpfj6fDDLkdw1otg2UV7UJofaew1sGOMvb3abPazjhkUjnt1qbpGmnpnnaTT0woooqCCMmdWkJZuBFO7dT6Coe67yO4hRchACcefXekdUlmN/HKsiiBVBMfDkseoz0p/EjSo1xKfEcYUDl61RX5HrMcfblEZqTytbAKrPJNuSOg6CqtrMgS0tbdSFDMySMN99jjPuTVl1aeSOApCOKYjYZ5VWdO0yCe54tV4pYoclolJwx6D896J47LH0N4raRIlKMzTQ7o/DlSPIn96ju0VzBPOEiOCUyVH51O6/r129r3drY/Dwr4UUjPsMVn8jyNfFpS3GBhz5Hyq/HtvZXSWi+6PoS3unpcXDhUwFwTzpnrVk0PCizhEQ7BV3xXPZnW/hTJHLsM7M+/DTLXdYl1C5aKzDNHjLSscflyA9TRKqqBuZQwtWPezTEkRwJnc9dyM+uxNVnX4T/AMnFKAT3tujmrNGFudEuGssTLkxuy5wx+8R6AYA96hdej77UVjjbEkEEcZH0z+9d2CNM4M+T2Iu1laCVXjYo6nIz51drXVrv4ZNU0+YpcW4xLFzVh126iqIWnT+4U90zWn0+6WQqeA7OvmK6mlrk5v5RtXZvtrp2tBIJwttdnYKx8Ln+0/sas7Q5BxWAXwigmW7tGBtpvEuOQPUVbuzfby5swkF1xXNuNsE+NPY9fY1z3h+ZOrHn3+o1FIQvPnTqJAPOmemanZatbiezmWRfvDkynyI6VKIAByqpJlraFEHWllGRSSuByroPVhWxbBO1KonCtJxEs2aVkPCMVIonJJXCy8Jrh2351wMnJqtlskbr/ZjT+0QE3eSWOop/KvrY8Min1/qHv9Kpb9qe0XYK6is+1cJvtOdwkWpQ8yPXzPod/LNaMSV3waRuorXUbKWyvoEuLWZeGSKQZDD/AHrS8dUhMuCcqFbS8t7+0iurSZJoJV4kkQ5DCisnSe5+y/tYtl3ss/Z+9PHGrHJUZwf/ACX8xRVdYaXXKMfJ49RWtFp1B302GS+uJP4C8j5k03HaaCaKKWVHRG/loBufWnXb+GT/AKXdiiycEqHh+6o5VW4Yor5bSSM4CxLjHTbcVy+p6fHStbLLZPDPFdu4AwSRk52qJt7uzs2up7hcsWBA8weVLXCvb22E5EbgdaqV9dOsrFskHhYb/eB5Uvrt6GfBZLqKO9A7kgvgs7nknoPL3qg30cbXTLAP4SE+LHzGpMX80B+H7093K3jx1FNXMUdtNIw8MZ/E+VXY0+hMhGXeqy6fAixqjM5JPGPlA5cqbW9/carGbWQKSxwsaLwq+fTrTWdPip2YnOW2GOVSNtbpZRHgdPiiMcZYARA8/r+ldnqlP8kQudssWhvb6bb3thxKLSC34g/m2fGf2+grNr+8lm1Sa+U4aVy306CrVqdykmhNDaydQkkgGC454Hp+tVFkKjhYbdDXTjW1szM8+mRyPGnEkaSuhRmGTjk3qKREkMm3EufXapWW27vs6rsBxBMgnmN9qgAY3wJF4T0IqzbKUTFiUjzBK2baTmuflPmKL+0u9HnHGC0LfJIORFRDRyxbxuSPepW17RuLH4C/j76DGFbqoqCSR0ntPNYTrNFK8cq7CSM7+x8xWkaL9qtu6LHqcaseXewbH6qf2NYrJbl3ZrQ98nMcHzD3HOm3fMDg8/WkaTHm2uj6et+3PZqdc/8AJpEcfLKjKf0pC4+0Ps/BkRTT3DeUUJx+LYr5tjvZY/ldh7GnCatOOcjH3qPRDfcZv7farYxbRaVcv6vKq/pmmU/2rzyE9zpcC+XHMzfoBWKDWZORY/hXY1cnm5/CmUSL7s1O6+0TXbhSITbW4/8ArjyfxbNREvabXpyS+q3O+54ZCv6VR11Y4x3hH0pVNSHVwR70yiUQ7otg7Q6zEeIandA9f4xP71IWX2jaxZOBcGO8jHMOMN9GFUgX0DDxE/jmuvibcjZx9aHjlrlELJS6ZovafWdN7b9kZfg2Mep2R+IS3l2ZgB4wp67fpRWcCWNZA6XCg+WaKRQ44kesirln0drFsl5ZS2ku6SLwn/P41QrPSLrR7hlLCSHO2K0K83Y5qImQPzrIrs14SSK1rV08U1sw2jWJ5HHngbD86hJHtL2JZWjIIYg45E+v5VZNdsTPasoDAFSuV5jNVFUcLLFFLGSTkeLBDYwdvpUSS0MPhydKmnKM5gnIkI5hT1/GmVzc27wMnfg8K5/9irZpdzCkcscpj71jwuyEFeP+k+9Nb/SdLu4myGt3HUD/AHNWxaVciVDa4KZbwyzKxjYKgGWZVxt7865iiQscYIFS2pvDawJZ2rKU+Z3HNjUVbjDmtCNUtot/CNTIuLRprSaJB4mHhx5jlT7RezionxWoqvhGeBjsvq1dWZVSXYZCDi4R1x0qE1HWb3UMxu3dw52iTYfXzq7Hv1M76kl9xa/YV1/UY79jbWmBbqd2xu5/xVda2YbYyOtOeBs5BxXhRz1wfSn0cC4G6RyoMc16Z6V66J97c0sC67GQfUV3nOxCmjRIzUJG4dGZWHIqcGpGa8sb2xZbqFvjFHgnjGOL/u86QKrn5Vrzgj8hUaI7GYt1b5X/ABr02jdDTvusnKrSixN1wKPUnZH/AAr1w0LoM4qZEK9WJ9hXYiQH+Xn3NHoGyCVJGOERifQU7j067l5qEHmxqVEndj7qj0FNprsDYEk+Zo9Ug2zldNgiGZrkk/0pXvFZxfJGM+b+Km+ZJCcda6Fvtk86P6D+z1rlmJ4W4B/YgFFelUT5j9KKA2fUt4SQaimJzvRRWHXZuz0JTjMZzVM1+BI0eSNFVyPmA3oop57JfRT9DaW1Sbhk41kJY5Gd/I08udTvoIsDgMjdOHYCiiupxLrlF2KF9vZBS99PN3kuAfIDArpBg70UV1yklpHG+GOlnMKl/TAHnSVroTy/xbubuVO/CBlj9KKKsf4ztHMp/wAjP638EpFp2lxAD4R5sfekkxn6CpfT9B0DUAQ9jIjDylNFFEZKZ0ZPCwzO0h9N2A0CeMiLv7d+jcXEPwqp6h2C1KzdjCiXMY5GNtyPaiirlyuTH8lfapKSvTWbW8hjmjZHHNWGK4CgchRRSMhHo513gmiioJOiVjGWNISXTbhBgedFFACBEjnJNdJbjmaKKNA2KFlQbVwTK+yjHtRRQQeG3WMcU8ir6Hn+FFFFKMf/2Q==",
  Quincy:
    "data:image/jpeg;base64,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",
  Rens: "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAgGBgcGBQgHBwcJCQgKDBQNDAsLDBkSEw8UHRofHh0aHBwgJC4nICIsIxwcKDcpLDAxNDQ0Hyc5PTgyPC4zNDL/2wBDAQkJCQwLDBgNDRgyIRwhMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjL/wAARCADIAMgDASIAAhEBAxEB/8QAHAABAAIDAQEBAAAAAAAAAAAAAAUGAwQHAgEI/8QAOxAAAgEDAgQEAgoBAwMFAAAAAQIDAAQRBSEGEjFBEyJRYXGBBxQyQlKRobHB0SMVYvAzQ+EkcpKisv/EABoBAAEFAQAAAAAAAAAAAAAAAAABAgMEBQb/xAApEQADAAIBAwQCAQUBAAAAAAAAAQIDEQQSITEFEyJRMkEzFCNSYXHw/9oADAMBAAIRAxEAPwC60pSuhKApSlAClKUAKUqvcScVQaE8NsoV7ubcA9EXOMn59qZkyTjnqp9h0y6ekT0kscQBdguTge9at1q+nWSM1zewRhRkguM/l1rll7xDq+sXP+W7WMRty8tuoUx7HOCc7nH6dq92CF3WeVZLxA6l7qcEAb4VgPTbYk9azcnqSX4ItTxP8mWifj1UnBht1kgI6nII+ff8tqmV4lt3hSVYndGHNlSCQMZziuW3tultqxW0gaR2BMSmUEEY6gY6e1SWhXEszBZSkUoBARMqW2xuMkEds9c4+NU/63Ou6on9jG+2jpEevWkkqRFJ1eQZX/GSD8xUhFcQz58KRXx1xXPbiFUEUswCRuoHO+SFJIPx9u3Wt/T5RBd26i4PPzhAirjrnA26bdvapI9UyJ/NbQ2uHLXxZd6VijuYZy3hN0OCD1z3H6GstbOLJOWVcPsULhw9UKUpUg0UpSgBSlKAFKUoAUpSgBSlKAFKUoAUJwMnYUqvccXb2XB2oyxvyOY+QEHfc4pt10y6f6FlbaRB8RceYR4NFKuAxR7jGd+/IPkd6oDXK3119cuZ5JJnXEgxkKBjBBPeoYXfK3hxswiGcKT+dZILktOCq4TlI2+97/tXPZs15XujUx45haROSTQ2y+JDOxG26pue/wDz41kS/wCeGR3dEjVObwubZ17dc7j29ah2umCqUXBjAGx7968+IZp2RuVfEHKQowCDv8j/AFUOiQ3bXUJbfVZApLRAjmyccnpg9R09aySFZbhbm3LLzEmUxv8Aez19sioy1Dwz5fchhhSDhsA7kd9qmYhLcWkZh8GMu4RhnrnJ39qHoRFhs73wrH7KKLlQ4durNnGRnoe+3vUlp5hZHm1HJmT/AKE2TzK2fLsOo3+NV0QS2lsqyoW5PLzA5ye2D6dPyret3ka35XkLAebwy+QCDn51C19EpaNNurmZQ4CTTvyjxebBZtyWx8NqtUUgljVxkZHQ1QdPlmhZZjbMsK5eOKIH/KwOcnPQCrFZ3VytxAkSLMTFmZcEHHN1T4b5q5weT7N9L8Mr8nD7k7XlE/SlK6MyhSlKAFKUoAUpSgBSlKAFKUoAUpSgDzJIsUTyOcIilmPoBua4dxdxlLxJdNHEHSwjP+OPP2v9x/5tXa7+PxtOuovxwuu3upr8xqxTKNsRsTms31C6SUrwy1xpTbZ9IJwO/atiFGk8pOCu/wAa+2MBll5j9kVMxWwzgY3rGq9Gljxuu5HJCQ2FBOd6koLCSRS4hyO5apKysI4yWfHMd6slral41CoAtRVm0WY42/JTfAdrgO4kAXvy7sat3DvC012oe4yAxDAKMHH8VJQaSskgeRVwD9mrfp7JDEq4GcdaZWbaJo4qT2yOj4VVpOTzGINlQR9n/wA1tPwWnhc0PliQ85bHRhuM1ZLJ432bHN2qSCQvCYpGIDdd96am2NypT2SOTtp2o6kyyrEFjh6RDOMr9rfbAx8e1af+rC0vBHPlYbjxLcDmGVJGxz17YrsawQeCyeUBjsq9BXKOKuEJrriiNEYrCpEqHOF9OnrTopN6ZXtftFo0ebx9Kt2JywUKfl/wVvVCcLI8eksjggiZzk98nrU3XW8enWKW/owci1bQpSlTDBSlKAFKUoAUpSgBSlKAFKUoAYB2PQ7V+ZdWtfA1q8thkeHO6f8A2Nfpqvz9xfam1471OIjZpvEHwYA/zWd6ivgqLXF/Jow2kapCoA2qRjXABFakAwoHQVuRnaufZuwtIkLYGRgM1bdMQeGoAP8AdVOzcRyAlc+wq76SUkjTHeorLeNG0YSoBANZoZWBAzvW+0fKn719a3VSJFG9RaJdm3ZySEbA5qQUTIQXyB7mtCzKg5YZ9a32nDp0GOwzSt9iOl3NpZcqN6i+JFBtILkY54ZBkkdjtW5C3XOax6lCLjT5Y8Z5lximS9UQ5IIDQowmnFgVIkldxy+hNSdRPDo5dKC75WRwfjmpau3438M/8Ry2X82KUpUxGKUpQApSlAClKUAKUpQApSlACuKfSLFy/SET08S3jb9CP4rtdcg+k2MDjWwfu1qP0Y1S56/ssscX+RFfwFGAazRyKPtMBt3rXmJWMkdawJbySNygkk/ezXO62bvU14JhLqKPzGQCpzS9YVCpVwV9QapjaDI/nFwQR2YVngtLiADEqbehpXEteRZy3L8HWrfW1ljA5uoraGqpunNnHrXPbK4ZY1y2T3qVWRpIm8xGOhqB4+5cV7RPy8X2Nm/hyzefc4QZres+P9GDBecFz2YYrnN1b2aK0k0+MnOeuaw2q6dF5o0YnO7shJp/tzor1dtncLfXtLuUJc+CD0c9PyrcmHiWcjRsGHKeUjvXL9KvrUIuQHVe3vXS9FuI7u0DK2VIwRjpUFykGnK2Vnh4FbO4Q/duH/Xf+al60NNj8GS+iYf5Fumz7jAwa366/hUq48s5zky5ytMUpSrRAKUpQApSlAClKUAKUpQApSlACuU/SmgHEejSY3MTA/8Ayrq1cz+lZAt1o0/oWX9R/dVOct4KLHF/lRUeUEjNJbqO3jYxxlpewUZo4I61mhhDYUYz8K5rejfSb7Igbm51KV1IiflJ6Yzj+KyoLmOJWMcnMxOQFwV+NWAJMjDHhsPUqa+SCRkJlPyG1SK1rwM9mk97I7Sp5vrscbEkMw2Ndd1jQVl4fims0xMq+YL94Vy3TYR/qUTBNgwyMdq7lpw8TSEVgdtgfaocj0+xaxLc9ziepadciYRhlUDYhjhj7e1Y9H4OlmuVa41BY15s5wc9PWuzajw9ZarAy3Mfnx5XHWoCLgazhYsJ5pAOiMBinLLpDXxlT7mto3Clms0XgcQ+NP0xJGpB9uu9XzSLSTTmkjcKNuiqAD77Vo2ulrb2pjMbBT0BGVrfsg6LylmIXZeY749Kq5af7JHCS0vBqyoi6neEEcxZWIHoR/YNfayzWZS4nvGYYkCoF9SO9Yq6j0lt8Zb+2c/6kks/b6QpSlaRnilKUAKUpQApSlAClKUAKUpQAqj/AEm6f9a0S2uAN4Jv/wBD+xV4qO1zTjqujXNmpAd1yhPQMNxUPIh3iqUSYa6cibOLg8yKT6b1tWg3G1YntJrS4e2uI2SVG5SrDBFbUETIdt871yldnpnSYnsnbLTFmAMkgAx2Ga1NetoLJVSNi0h9e1btk7wxc7HYVAaxdPPcl+YEg5370kruWLaSPekGOK4Bc5Ytv8K6/ouoQTWHh+IOgIri8eoxtPGotVRejHnyflVng1aOyty1rJzSFfKp6Z96KWxMdTrR0TVZfC06a4ik5WjHMCDULpfEgkcJcLyyA9exrHw/9c1TS5W1V1ZJdo1VeXb1P8fCq9cQvb3LpghkbDCo9EvWjpiaqsyou29Zg65EirgHriqNp18eUKWyOxq12U3iwoMbHtUGRfYOZ18Tau7lJQIlQhom3PrkVq17lGJpP/dXiuy4OL2uPMv/ANs5Pl5FkzU0KUpVsrilKUAKUpQApSlAClKUAKUqO1LUxZjw48GYjv0X41Hlyzil3b7Doh2+lEjXsQysvMInI9QpqFsuML6FI4liti4kyJWj3xjcYHrXQNH1qLV9PinlXwXdTzIT5VYdR/NZr9UlvUyWXxHPlnFPpAs3h1q0uGjZRNDjJUjJB/8AIqCt8MyKcb4rpXHekXt7w4bto5pRZymcSNuPDbZvl0NcwAZQGGxByKyOTfuZHetbNfi6WNL6MmryuD4HimJcZyDjIqG+rmT7Myye4YGpfVkW4hhkxnK8rZqrvpDQ3AmtsA91PSmRrRNe97JSPT28TLEY6/Cp3S4rKKYPcOxjXcqB9r4n0qLsrmBHVrvT5GYMrEx4PberXp8ts7S+Fo906uNuaMANt6k0rT+iSNfpFgteIdK5VCB0wN+hAFQmq6xol3qDCG+iklcBeRGyc/Kpb/SJtVslTwLfT4yiI2X8RyBnIwAPbFaUPANppKSTWdxzyyDzGVQD+YqPUrySV1NeCL0V2l1uW2UkxhQdx3q96VJghG652xvVY0KxNnc3U8gHMdqtOk28s92EhQu+C2B1qFy7tJA6URtm655nY+przX0gqSGBBHUGvldvK1KRyFPbbFKUpwgpSlAClKUAKUpQApSlAGve3S2dnJOfujYep7VTnmeZnkdsuxyTUxxVccsUEAP2iWPyqAhbNc/6nmdZeheEanDx6jq+zBdyToiNBglWHMpOMjPWvV7qdyyMXmcqxyVB2/LpWWWIkMoHUbVF3gPIMg7VnItaOlcEapHqfDsumGZXkDMr27Hdo2XBwO4rl97bf6bql5pzklraZo8nqR2P5YqNkv3splkhdkdTkFTgj51q6rqFxJrTXczOxmRWLMcltsZ/SnNbWh0Jw20SbkGJoyfcVoAGNRzeYk+lZlkE0QIOcCvUahgVPXtmma0WE9me1ZUYMBuKmINSdXGHYH1rT03To5WBlkIBP2V61arLTtGjKh4CxPdnNDrRYnq/RsaZdyzBQzF8fkKnFPiZUnYDJr7b21jGg8FAvwG9ZJRFbROw7jqPSoMj2yRvt3IiSMKAoGN8kirfoWnPZ2f1jYTsAeQ/h7fnVat1FxcAsMr9r02610FFN7pcFzEB46ICuPvDutS4Z7/7M/k5H0r6I/VrNLy1+vQD/IozIPxD1+I71X6uOmhJHlI3jk82PZhv+1VO5h8C6lhP3HK10fp2Z3HTX6MPkQpraMVKUrRK4pSlAClKUAKUpQApSvMjiONnJACjOTSN6W2CW3oqHE8wl1JUU5EScpPv1qKhYq1Sf1GS5LuzqXYkkVgl064g8xTK9yu+K5PNk9zI7+zcxSolSbENu91HhMFxuATj5Vp3tg4gMjIfDJwxH3TWzaT+G4IPxqYMkfMZ+UNFIOWZT0+NRoc2ca10tbaktvLzBPt8wHUe1atxfSX1yXk2CqEjX8CjoK6FxZwv49uGgxy5zC5+6fwk1zOWOW1uGilQpIhwyntUvZoWWSVrctCwK/kan7YxXCCRTgd/Y1V4nzg5retZ5LdudDhv3pjRKtl0tbKVSHRgQd+tbkttcPIhXOfbpUPp2rxEgPmJz3XdT8qtVldwtHhzzDH2vf4VG3onVs2dPW4QASSdsgGtm5nZlAJ8o3C+vpmsDXi/9pAAOp7mvMANxPnqB0NRvS7i/K+zN6KVrTS7i6brsq57FiB/NdH4fYNpiYPlGwrnms27zcM3FpAP803LHHj8ZYY/WuhaZbtYaWtvnzhQgPqemf5p2Bt11EPMSnGpMmjRkRO+NmY4+GTiqrqUgl1O5dehkOP2q7xIIIUjQY7Cqhrll9T1Fiv/AE5fOvt6it301pW0/JicjutkbSlK2CoKUpQApSlAClKUAKhOJXmFlHHAjOzP5lXrgCtqW8klGYwY4/X7x/qscVqGw5yxO+WOax+Zz4qXjjvv9l7BxqTV0VaK4u7Y88tpcon4gOYfpU5Z36XEfMpDgdSvb4jtU7BaDPMetQuv6KB/660zFKPtFNj8axtF/Z9ksoLjzJhW9RXmOGW3JVl5kOx9DUJb65c2r8l5CJAP+5H5W+Y6H9KsVlfQ3SgxyBgR0Ox/KlFPMMiwgwTqHtZNhzb49j/dVXjDg2OeH6xb9B9iTG6f7W9R71dzChBHKCD1Br7HG8MZTHjQkYKHcgfzSoTej89vbz2U5huI2jcHGGHX4HvW7bsCMGuyX3DFnqUZESpJHneKTt+fSud6/wAK3el3GozxW2IYEjljiTcsp2bHrg/vTvJKsiS7mjboCQw2+FWCzcgDzMB6VV9KvI75SYA7FN2GN1+NWWxDMoPvsKhybXktYumu6Jy2jMrgknHuasVnEEUADb96h7KB48Fl/OrPpGkXWoyARLiIHzSMPKP7qs26epLT6cc9Vdia0Gw+uXSTOP8AFA3MNurY2/KrcFy3PjHoB2rSgFtpNgIlYcqDLM22fU1UND+k204m4kn0rT7d/qUMbM98xwDggbDGMHpuc1exY+idGFyMzy26L3HIGJcbgHlBHc96xXWnWupFWnViVB5SGxtWtDN/qEqx24K2ibF+nMPQfH1qTYYIYVZi3L3JWa32ZAz8LxkE287K3o4yPzqEvNMu7I5miPJ+Nd1/OrxIxwGBxX1WWVCCAc7EVbxc7JP5dyKsMvwc7pVp1Ph2ORWlswEk6+H2Pw9Kq7KyMVYEMDgg9q1MWacq3JWqHPk+UpSphopSlAGpfQgWbRoOm9YrGXniUHqNqlb22aCZ4nHwPrUXaCK0vl8ZeaLOSK479m6vBMQKMV4nHUY2Pasts4kBIAAJ7UnTelGsq99oMUxZocIx+6elRcelukot5h4eTs39VbmG9R+px5RZF6oc0oqZoSWWr2EfiW1w0sQ6qfMB8jvXu2119hPbebuY2x+hqb06fnjAJ7V5u9LtbhizR8rn7y7UIQ1V1ixkI8RpIH7FkwfzG1bq6lp7qBNMjL6shH8YqObSSoPIwlA+6wwa8Qx2kbcjQLC/vlachGULiS1seFOL7TiTQZVFs8oS7t0+yA3UjHY7/A4rtFvw7oeoW8Vw9lKGkQOtzyhDgjIO3x71TtW4dj1Hh3VbYIZpZ4WMLGXPIwHMu3fcVvfRrr8t5wNp5aJC0KmFs9SB0zn2IHyqR6pbZH1VL+LLHb8L2NrMZZ5nukB8iEco+frUhdazaWahWljhUDCxj+AK8/WLKVT41t168rkfsay22m6W7c8VhGnfmwf+GmzEr8RcmW8j3b2Ql7q9xcWcr2+nu8BBV5pwVXB26VX+AOHrbTuMOJNIeOPw7d4p40Q+UB1Bx8Bmujqq3EElvIoaJlKlRsMVUNO5LH6ZrqKKPljvNIjbbplDy/soFSSiJs6BFEsKBVAA9AOlelPUGvoryThqUaeG/D1rBG/K53PzrZlU4yK0ZpOVwx6+opAJEHIzUbqeiwagC4/xz42cDr8a3Ld+Ydaz1JjyVD3IjlPszn13Zz2UxinTlbsexHtWCugXdlDexGOdAw7HuPhVN1TTn0268MnmjbdG9RWxxuUsvxfkq5MbnuvBo0pSrZEWu4gjnbEsYYjpkVUNZgWK6k5QAqt27ClK5GjYxmTSpDkoe1SFwN6UpqHsj36mtWdeeMr7UpQCNWwkMchQ9jU59tM5pSlEZrE8rVvDSJJrD61JHzRd0A83L+IClKdIy3ow2HDk4USW7w3UDHKMG5W+BB6Gqp9GOmyxX3EehM3hCwvm+0M4UsQP2FKVMl2ZG22dPh0y1hIzmV/U/wBVvLbOQeiKevrSlJoRs9OFjCKi4wcVRtUcW30qcK3QJH1q1nt2/wB2CSB+tKU6RrOidq8tvSlIB6HmXFaNypU8wA29e9KUCo+WsgzgbDtUjSlKhGDUPxHCJdLMmPNEwP8AFKVNx21ln/o218WU+lKV0BRP/9k=",
  Noor: "data:image/jpeg;base64,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",
  Kim: "data:image/png;base64,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",
  Giulia:
    "data:image/png;base64,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",
};

// 🏷️ "Crea Atelier" heet nu "Crea Club": oude namen in opgeslagen gegevens
// worden bij het laden vervangen (alleen tekstwaarden, geen sleutels/id's).
// Alleen in de weergave; bij de eerstvolgende normale save gaat de nieuwe naam
// vanzelf mee. (Geen aparte terugschrijf-actie: die kon hele velden incl.
// archief in één keer wegschrijven en bleef dan mislukken → "Offline".)
const CREA_ATELIER_RE = /\b(?:crea(?:tive|tief)?[\s\-_]+)?atelier\b/gi;
// Objecten die al gecontroleerd zijn (zelfde referentie = ongewijzigd) slaan we
// over; anders werd bij elke wijziging de hele app-data opnieuw doorlopen.
const creaClubSchoon = new WeakSet();
const vervangCreaAtelier = (v, diepte = 0) => {
  if (typeof v === "string") {
    if (v.length > 5000 || v.startsWith("data:") || /^[a-z_]+$/.test(v)) return v;
    CREA_ATELIER_RE.lastIndex = 0;
    if (!CREA_ATELIER_RE.test(v)) return v;
    CREA_ATELIER_RE.lastIndex = 0;
    return v
      .replace(/\b(?:in|op|naar|uit) het (?=(?:crea(?:tive|tief)?[\s\-_]+)?atelier\b)/gi, (m) => m.replace(/het $/i, "de "))
      .replace(CREA_ATELIER_RE, "Crea Club");
  }
  if (!v || typeof v !== "object" || diepte > 12) return v;
  if (creaClubSchoon.has(v)) return v;
  if (Array.isArray(v)) {
    let uit = null;
    for (let i = 0; i < v.length; i++) {
      const n = vervangCreaAtelier(v[i], diepte + 1);
      if (n !== v[i]) { if (!uit) uit = v.slice(); uit[i] = n; }
    }
    const res = uit || v;
    creaClubSchoon.add(res);
    return res;
  }
  if (Object.getPrototypeOf(v) !== Object.prototype) return v;
  let uit = null;
  for (const k of Object.keys(v)) {
    const n = vervangCreaAtelier(v[k], diepte + 1);
    if (n !== v[k]) {
      if (!uit) uit = { ...v };
      uit[k] = n;
    }
  }
  const res = uit || v;
  creaClubSchoon.add(res);
  return res;
};

// 🔗 Programma-activiteit koppelen aan de meest lijkende bibliotheek-activiteit.
// Programma, promoscherm enz. koppelen op (exacte) titel, dus een titel uit een
// Excel die net anders is ("Orry &Friends Kids Concert", "Kids Disco" vs
// "Orry & Friends Kids Disco") krijgt hiermee de bibliotheektitel.
const bibNorm = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " en ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
const bibKern = (s) => {
  let t = ` ${bibNorm(s)} `;
  for (const voor of [" orry en friends show ", " orry en friends ", " orry friends ", " orry ", " o en f ", " crea club ", " show "]) {
    if (t.startsWith(voor) && t.length > voor.length + 2) t = " " + t.slice(voor.length);
  }
  return t.trim();
};
const bigrammen = (s) => {
  const t = s.replace(/ /g, "");
  const m = new Map();
  for (let i = 0; i < t.length - 1; i++) m.set(t.slice(i, i + 2), (m.get(t.slice(i, i + 2)) || 0) + 1);
  return { m, n: Math.max(0, t.length - 1) };
};
// Algemene woorden zeggen weinig ("Nachtlampje knutselen" ≠ "Kerststukje knutselen")
const BIB_ALGEMEEN = new Set("knutselen knutsel maken maak je jouw eigen de het een en met voor van in op kids kinderen show club night nacht workshop crea orry friends live family familie fun party feest".split(" "));
const titelGelijkenis = (a, b) => {
  if (bibNorm(a) === bibNorm(b)) return 1;
  const ka = bibKern(a), kb = bibKern(b);
  if (!ka || !kb) return 0;
  if (ka === kb) return 0.95;
  const wa = ka.split(" ").filter(Boolean), wb = kb.split(" ").filter(Boolean);
  const bijzonder = (w) => w.filter((x) => !BIB_ALGEMEEN.has(x) && x.length > 2);
  const ba = bijzonder(wa), bb = bijzonder(wb);
  // Bijna gelijke woorden: "nachtlampje" ~ "nachtlampjes", "tattoo" ~ "tattoos"
  const lijkt = (x, y) => x === y || (x.length >= 5 && y.length >= 5 && (x.startsWith(y) || y.startsWith(x)));
  const gewicht = (w) => (BIB_ALGEMEEN.has(w) || w.length <= 2 ? 1 : w.length);
  const deel = (A, B) => A.reduce((n, x) => n + (B.some((y) => lijkt(x, y)) ? gewicht(x) : 0), 0);
  const totaal = (A) => A.reduce((n, x) => n + gewicht(x), 0) || 1;
  const fa = deel(wa, wb) / totaal(wa), fb = deel(wb, wa) / totaal(wb);
  let score = Math.min(fa, fb) * 0.6 + Math.max(fa, fb) * 0.35;
  // Aan elkaar vs los geschreven ("Glittertattoo" / "Glitter tattoo's")
  const za = ka.replace(/ /g, ""), zb = kb.replace(/ /g, "");
  if (za === zb || (za.length >= 6 && zb.length >= 6 && (za.startsWith(zb) || zb.startsWith(za)))) score = Math.max(score, 0.92);
  const A = bigrammen(ba.join(" ") || ka), B = bigrammen(bb.join(" ") || kb);
  if (A.n && B.n) {
    let x = 0;
    A.m.forEach((c, k) => (x += Math.min(c, B.m.get(k) || 0)));
    score = Math.max(score, ((2 * x) / (A.n + B.n)) * 0.9);
  }
  // Geen enkel bijzonder woord gemeen → nooit automatisch koppelen
  if (ba.length && bb.length && !ba.some((x) => bb.some((y) => lijkt(x, y))) && za !== zb) score = Math.min(score, 0.5);
  return score;
};
// → { tpl, afd, score } of null. hint: { loc, title } om bij twijfel de juiste afdeling te kiezen
const zoekLijkendeBibActiviteit = (titel, lib, hint = {}) => {
  let best = null;
  const isOrry = /orry/i.test(titel || "");
  const isCrea = /crea/i.test(hint.loc || "");
  Object.entries(lib || {}).forEach(([afd, lijst]) => {
    if (!Array.isArray(lijst)) return;
    lijst.forEach((t) => {
      const naam = t && (t.title || t.name);
      if (!naam) return;
      let sc = titelGelijkenis(titel, naam);
      const type = t.type || afd;
      if (sc < 1) {
        if (isOrry && (type === "orry" || /orry/i.test(naam))) sc += 0.05;
        if (isCrea && type === "crea") sc += 0.05;
        if (!isCrea && hint.loc && type === "crea") sc -= 0.05;
      }
      if (!best || sc > best.score) best = { tpl: t, afd: type, score: sc };
    });
  });
  return best && best.score >= 0.6 ? best : null;
};

// 🎄 Programma kerstvakantie 2026 (18 dec – 3 jan, uit "Programma_kerstvakantie_2026_1.xlsx").
// De FM-app zet dit in Programmabeheer zolang het grotendeels ontbreekt (zie effect in App).
// Wat er al staat (zelfde dag + begintijd + titel) wordt niet dubbel toegevoegd.
// [datum, tijd, titel, locatie, max, prijs]
const KERST_2026_PROGRAMMA = [
  ["2026-12-18", "18:45 - 19:15", "Orry & Friends Kids Disco", "Podium Market Dome", "", ""],
  ["2026-12-18", "19:15 - 19:30", "Orry & Friends Voorleesverhaaltje", "Podium Market Dome", "", ""],
  ["2026-12-18", "20:30 - 22:00", "Family Quiz Night", "Podium Market Dome", "", ""],
  ["2026-12-19", "10:00 - 12:00", "Crea Club Walk-In", "Crea Club", "", "2,50"],
  ["2026-12-19", "13:30 - 14:30", "Maak je eigen Knuffel", "Crea Club", "20", "25,00"],
  ["2026-12-19", "15:00 - 16:00", "Glittertattoo", "Crea Club", "2 per kwartier", "2,50"],
  ["2026-12-19", "15:00 - 16:00", "Hairbeads", "Crea Club", "4 per kwartier", "5,00"],
  ["2026-12-19", "16:15 - 17:15", "Schminken", "Crea Club", "", ""],
  ["2026-12-19", "16:00 - 16:45", "Orry & Friends Natuurbingo", "Podium Market Dome", "", ""],
  ["2026-12-19", "16:45 - 17:00", "Orry & Friends Meet & Greet", "Podium Market Dome", "", ""],
  ["2026-12-19", "18:45 - 19:15", "Orry & Friends Kids Disco", "Podium Market Dome", "", ""],
  ["2026-12-19", "20:30 - 22:00", "Prize Night Bazar Bizarre", "Podium Market Dome", "", "13,00"],
  ["2026-12-20", "10:00 - 12:00", "Crea Club Walk-In", "Crea Club", "", "2,50"],
  ["2026-12-20", "13:30 - 14:30", "Nachtlampje Knutselen", "Crea Club", "20", "5,00"],
  ["2026-12-20", "15:00 - 16:00", "Schminken", "Crea Club", "", ""],
  ["2026-12-20", "16:15 - 17:15", "Glittertattoo", "Crea Club", "2 per kwartier", "2,50"],
  ["2026-12-20", "16:15 - 17:15", "Hairbeads", "Crea Club", "4 per kwartier", "5,00"],
  ["2026-12-20", "16:00 - 16:45", "Orry & Friends show: Muziekmachine", "Podium Market Dome", "", ""],
  ["2026-12-20", "16:30 - 16:45", "Orry & Friends Meet & Greet", "Podium Market Dome", "", ""],
  ["2026-12-20", "18:45 - 19:15", "Orry & Friends Kids Concert", "Podium Market Dome", "", ""],
  ["2026-12-20", "20:30 - 22:00", "Live Muziek", "Podium Market Dome", "", ""],
  ["2026-12-21", "18:45 - 19:15", "Orry & Friends Kids Disco", "Podium Market Dome", "", ""],
  ["2026-12-21", "19:15 - 19:30", "Orry & Friends Voorleesverhaaltje", "Podium Market Dome", "", ""],
  ["2026-12-21", "20:30 - 22:00", "Family Quiz Night", "Podium Market Dome", "", ""],
  ["2026-12-22", "10:00 - 12:00", "Crea Club Walk-In", "Crea Club", "", "2,50"],
  ["2026-12-22", "13:30 - 14:30", "Maak je eigen Knuffel", "Crea Club", "20", "28,00"],
  ["2026-12-22", "15:00 - 16:00", "Glittertattoo", "Crea Club", "2 per kwartier", "2,50"],
  ["2026-12-22", "15:00 - 16:00", "Hairbeads", "Crea Club", "4 per kwartier", "5,00"],
  ["2026-12-22", "16:15 - 17:15", "Schminken", "Crea Club", "", ""],
  ["2026-12-22", "16:00 - 16:45", "Orry & Friends Op Avontuur...", "Podium Market Dome", "", ""],
  ["2026-12-22", "16:45 - 17:00", "Orry & Friends Meet & Greet", "Podium Market Dome", "", ""],
  ["2026-12-22", "18:45 - 19:15", "Orry & Friends Kids Disco", "Podium Market Dome", "", ""],
  ["2026-12-22", "20:30 - 22:00", "Live Muziek", "Podium Market Dome", "", ""],
  ["2026-12-23", "10:00 - 12:00", "Crea Club Walk-In", "Crea Club", "", "2,50"],
  ["2026-12-23", "13:30 - 14:30", "Nachtlampje Knutselen", "Crea Club", "20", "5,00"],
  ["2026-12-23", "15:00 - 16:00", "Schminken", "Crea Club", "", ""],
  ["2026-12-23", "16:15 - 17:15", "Glittertattoo", "Crea Club", "2 per kwartier", "2,50"],
  ["2026-12-23", "16:15 - 17:15", "Hairbeads", "Crea Club", "4 per kwartier", "5,00"],
  ["2026-12-23", "16:00 - 16:45", "Orry & Friends Natuurbingo", "Podium Market Dome", "", ""],
  ["2026-12-23", "17:00 - 17:30", "Orry & Friends Kids Disco", "Podium Market Dome", "", ""],
  ["2026-12-23", "20:30 - 22:00", "Prize Night Bazar Bizarre", "Podium Market Dome", "", "13,00"],
  ["2026-12-24", "10:00 - 12:00", "Crea Club Walk-In", "Crea Club", "", "2,50"],
  ["2026-12-24", "13:30 - 14:30", "Maak je eigen Knuffel", "Crea Club", "20", "28,00"],
  ["2026-12-24", "15:00 - 16:00", "Glittertattoo", "Crea Club", "2 per kwartier", "2,50"],
  ["2026-12-24", "15:00 - 16:00", "Hairbeads", "Crea Club", "4 per kwartier", "5,00"],
  ["2026-12-24", "16:15 - 17:15", "Schminken", "Crea Club", "", ""],
  ["2026-12-24", "16:00 - 16:45", "Orry & Friends show: Muziekmachine", "Podium Market Dome", "", ""],
  ["2026-12-24", "16:30 - 16:45", "Orry & Friends Meet & Greet", "Podium Market Dome", "", ""],
  ["2026-12-24", "18:45 - 19:15", "Orry & Friends Kids Concert", "Podium Market Dome", "", ""],
  ["2026-12-24", "20:30 - 22:00", "Live Muziek", "Podium Market Dome", "", ""],
  ["2026-12-25", "10:00 - 12:00", "Crea Club Walk-In", "Crea Club", "", "2,50"],
  ["2026-12-25", "13:30 - 14:30", "Nachtlampje Knutselen", "Crea Club", "20", "5,00"],
  ["2026-12-25", "15:00 - 16:00", "Schminken", "Crea Club", "", ""],
  ["2026-12-25", "16:15 - 17:15", "Glittertattoo", "Crea Club", "2 per kwartier", "2,50"],
  ["2026-12-25", "16:15 - 17:15", "Hairbeads", "Crea Club", "4 per kwartier", "5,00"],
  ["2026-12-25", "16:00 - 16:45", "Orry & Friends Op Avontuur...", "Podium Market Dome", "", ""],
  ["2026-12-25", "16:30 - 16:45", "Orry & Friends Meet & Greet", "Podium Market Dome", "", ""],
  ["2026-12-25", "18:45 - 19:15", "Orry & Friends Kids Disco", "Podium Market Dome", "", ""],
  ["2026-12-25", "20:30 - 22:00", "Live Muziek", "Podium Market Dome", "", ""],
  ["2026-12-26", "10:00 - 12:00", "Crea Club Walk-In", "Crea Club", "", "2,50"],
  ["2026-12-26", "13:30 - 14:30", "Maak je eigen Knuffel", "Crea Club", "20", "25,00"],
  ["2026-12-26", "15:00 - 16:00", "Glittertattoo", "Crea Club", "2 per kwartier", "2,50"],
  ["2026-12-26", "15:00 - 16:00", "Hairbeads", "Crea Club", "4 per kwartier", "5,00"],
  ["2026-12-26", "16:15 - 17:15", "Schminken", "Crea Club", "", ""],
  ["2026-12-26", "16:00 - 16:45", "Orry & Friends Natuurbingo", "Podium Market Dome", "", ""],
  ["2026-12-26", "16:45 - 17:00", "Orry & Friends Meet & Greet", "Podium Market Dome", "", ""],
  ["2026-12-26", "18:45 - 19:15", "Orry & Friends Kids Disco", "Podium Market Dome", "", ""],
  ["2026-12-26", "20:30 - 22:00", "Prize Night Bazar Bizarre", "Podium Market Dome", "", ""],
  ["2026-12-27", "10:00 - 12:00", "Crea Club Walk-In", "Crea Club", "", "2,50"],
  ["2026-12-27", "13:30 - 14:30", "Nachtlampje Knutselen", "Crea Club", "20", "5,00"],
  ["2026-12-27", "15:00 - 16:00", "Schminken", "Crea Club", "", ""],
  ["2026-12-27", "16:15 - 17:15", "Glittertattoo", "Crea Club", "2 per kwartier", "2,50"],
  ["2026-12-27", "16:15 - 17:15", "Hairbeads", "Crea Club", "4 per kwartier", "5,00"],
  ["2026-12-27", "16:00 - 16:45", "Orry & Friends show: Muziekmachine", "Podium Market Dome", "", ""],
  ["2026-12-27", "16:30 - 16:45", "Orry & Friends Meet & Greet", "Podium Market Dome", "", ""],
  ["2026-12-27", "18:45 - 19:15", "Orry & Friends Kids Concert", "Podium Market Dome", "", ""],
  ["2026-12-27", "20:30 - 22:00", "Live Muziek", "Podium Market Dome", "", ""],
  ["2026-12-28", "18:45 - 19:15", "Orry & Friends Kids Disco", "Podium Market Dome", "", ""],
  ["2026-12-28", "19:15 - 19:30", "Orry & Friends Voorleesverhaaltje", "Podium Market Dome", "", ""],
  ["2026-12-28", "20:30 - 22:00", "Family Quiz Night", "Podium Market Dome", "", ""],
  ["2026-12-29", "10:00 - 12:00", "Crea Club Walk-In", "Crea Club", "", "2,50"],
  ["2026-12-29", "13:30 - 14:30", "Maak je eigen Knuffel", "Crea Club", "20", "28,00"],
  ["2026-12-29", "15:00 - 16:00", "Glittertattoo", "Crea Club", "2 per kwartier", "2,50"],
  ["2026-12-29", "15:00 - 16:00", "Hairbeads", "Crea Club", "4 per kwartier", "5,00"],
  ["2026-12-29", "16:15 - 17:15", "Schminken", "Crea Club", "", ""],
  ["2026-12-29", "16:00 - 16:45", "Orry & Friends Op Avontuur...", "Podium Market Dome", "", ""],
  ["2026-12-29", "16:30 - 16:45", "Orry & Friends Meet & Greet", "Podium Market Dome", "", ""],
  ["2026-12-29", "18:45 - 19:15", "Orry & Friends Kids Disco", "Podium Market Dome", "", ""],
  ["2026-12-29", "20:30 - 22:00", "Live Muziek", "Podium Market Dome", "", ""],
  ["2026-12-30", "10:00 - 12:00", "Crea Club Walk-In", "Crea Club", "", "2,50"],
  ["2026-12-30", "13:30 - 14:30", "Nachtlampje Knutselen", "Crea Club", "20", "5,00"],
  ["2026-12-30", "15:00 - 16:00", "Schminken", "Crea Club", "", ""],
  ["2026-12-30", "16:15 - 17:15", "Glittertattoo", "Crea Club", "2 per kwartier", "2,50"],
  ["2026-12-30", "16:15 - 17:15", "Hairbeads", "Crea Club", "4 per kwartier", "5,00"],
  ["2026-12-30", "16:00 - 16:45", "Orry & Friends Natuurbingo", "Podium Market Dome", "", ""],
  ["2026-12-30", "16:30 - 16:45", "Orry & Friends Meet & Greet", "Podium Market Dome", "", ""],
  ["2026-12-30", "18:45 - 19:15", "Orry & Friends Kids Disco", "Podium Market Dome", "", ""],
  ["2026-12-30", "20:30 - 22:00", "Prize Night Bazar Bizarre", "Podium Market Dome", "", ""],
  ["2026-12-31", "10:00 - 12:00", "Crea Club Walk-In", "Crea Club", "", "2,50"],
  ["2026-12-31", "13:30 - 14:30", "Maak je eigen Knuffel", "Crea Club", "20", "28,00"],
  ["2026-12-31", "15:00 - 16:00", "Schminken", "Crea Club", "", ""],
  ["2026-12-31", "16:15 - 17:15", "Glittertattoo", "Crea Club", "2 per kwartier", "2,50"],
  ["2026-12-31", "16:15 - 17:15", "Hairbeads", "Crea Club", "4 per kwartier", "5,00"],
  ["2026-12-31", "16:00 - 16:45", "Orry & Friends show: Muziekmachine", "Podium Market Dome", "", ""],
  ["2026-12-31", "16:45 - 17:00", "Orry & Friends Meet & Greet", "Podium Market Dome", "", ""],
  ["2026-12-31", "18:45 - 19:15", "Orry & Friends Kids Concert", "Podium Market Dome", "", ""],
  ["2026-12-31", "20:30 - 00:30", "Live Muziek", "Podium Market Dome", "", "13,00"],
  ["2027-01-01", "13:30 - 14:30", "Nachtlampje Knutselen", "Crea Club", "20", "5,00"],
  ["2027-01-01", "15:00 - 16:00", "Glittertattoo", "Crea Club", "2 per kwartier", "2,50"],
  ["2027-01-01", "15:00 - 16:00", "Hairbeads", "Crea Club", "4 per kwartier", "5,00"],
  ["2027-01-01", "16:15 - 17:15", "Schminken", "Crea Club", "", ""],
  ["2027-01-01", "16:00 - 16:45", "Orry & Friends Op Avontuur...", "Podium Market Dome", "", ""],
  ["2027-01-01", "16:30 - 16:45", "Orry & Friends Meet & Greet", "Podium Market Dome", "", ""],
  ["2027-01-01", "18:45 - 19:15", "Orry & Friends Kids Disco", "Podium Market Dome", "", ""],
  ["2027-01-01", "20:30 - 22:00", "Family Quiz Night", "Podium Market Dome", "", ""],
  ["2027-01-02", "10:00 - 12:00", "Crea Club Walk-In", "Crea Club", "", "2,50"],
  ["2027-01-02", "13:30 - 14:30", "Maak je eigen Knuffel", "Crea Club", "20", "28,00"],
  ["2027-01-02", "15:00 - 16:00", "Glittertattoo", "Crea Club", "2 per kwartier", "2,50"],
  ["2027-01-02", "15:00 - 16:00", "Hairbeads", "Crea Club", "4 per kwartier", "5,00"],
  ["2027-01-02", "16:15 - 17:15", "Schminken", "Crea Club", "", ""],
  ["2027-01-02", "16:00 - 16:45", "Orry & Friends Natuurbingo", "Podium Market Dome", "", ""],
  ["2027-01-02", "16:45 - 17:00", "Orry & Friends Meet & Greet", "Podium Market Dome", "", ""],
  ["2027-01-02", "18:45 - 19:15", "Orry & Friends Kids Disco", "Podium Market Dome", "", ""],
  ["2027-01-02", "20:30 - 22:00", "Prize Night Bazar Bizarre", "Podium Market Dome", "", "13,00"],
  ["2027-01-03", "10:00 - 12:00", "Crea Club Walk-In", "Crea Club", "", "2,50"],
  ["2027-01-03", "13:30 - 14:30", "Nachtlampje Knutselen", "Crea Club", "20", "5,00"],
  ["2027-01-03", "15:00 - 16:00", "Schminken", "Crea Club", "", ""],
  ["2027-01-03", "16:15 - 17:15", "Glittertattoo", "Crea Club", "2 per kwartier", "2,50"],
  ["2027-01-03", "16:15 - 17:15", "Hairbeads", "Crea Club", "4 per kwartier", "5,00"],
  ["2027-01-03", "16:00 - 16:45", "Orry & Friends show: Muziekmachine", "Podium Market Dome", "", ""],
  ["2027-01-03", "16:30 - 16:45", "Orry & Friends Meet & Greet", "Podium Market Dome", "", ""],
  ["2027-01-03", "18:45 - 19:15", "Orry & Friends Kids Concert", "Podium Market Dome", "", ""],
  ["2027-01-03", "20:30 - 22:00", "Live Muziek", "Podium Market Dome", "", ""],
];
const KERST_2026_SPRINGKUSSEN = {"2026-12-19": ["10:00", "17:00"], "2026-12-20": ["10:00", "17:00"], "2026-12-22": ["10:00", "17:00"], "2026-12-23": ["10:00", "17:00"], "2026-12-24": ["10:00", "17:00"], "2026-12-25": ["10:00", "17:00"], "2026-12-26": ["10:00", "17:00"], "2026-12-27": ["10:00", "17:00"], "2026-12-29": ["10:00", "17:00"], "2026-12-30": ["10:00", "17:00"], "2026-12-31": ["10:00", "17:00"], "2027-01-01": ["13:00", "17:00"], "2027-01-02": ["10:00", "17:00"], "2027-01-03": ["10:00", "17:00"]};
// Vaste velden die een programma-activiteit uit de bibliotheek overneemt
// (foto/teksten niet: die vult scheduleMetLibTeksten bij het tonen aan).
const bibVasteVelden = (t) => ({
  refhd: t.refhd || "",
  paris: t.paris || false,
  villageMap: t.villageMap || false,
  remarks: t.remarks || "",
  linkedManuals: Array.isArray(t.linkedManuals) ? t.linkedManuals : [],
  priceVanaf: !!t.priceVanaf,
});

let kerst2026Pogingen = 0; // per keer openen van de app hooguit één poging
const bouwKerst2026Import = (appData) => {
  const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
  const lib = new Map();
  Object.entries(appData.activityLibrary || {}).forEach(([afd, lijst]) => {
    if (!Array.isArray(lijst)) return;
    lijst.forEach((t) => {
      const k = norm(t && (t.title || t.name));
      if (k && !lib.has(k)) lib.set(k, { ...t, type: t.type || afd });
    });
  });
  const schedule = Array.isArray(appData.schedule) ? appData.schedule : [];
  const bestaat = new Set(
    schedule.map((a) => `${a && a.date}|${String((a && a.time) || "").slice(0, 5)}|${norm(a && a.title)}`)
  );
  const basisId = Date.now();
  const nieuw = [];
  let aanwezig = 0;
  KERST_2026_PROGRAMMA.forEach(([date, time, title, loc, maxPax, price], i) => {
    const gevonden = lib.get(norm(title)) || zoekLijkendeBibActiviteit(title, appData.activityLibrary, { loc })?.tpl;
    const t = gevonden ? { ...gevonden, type: gevonden.type || lib.get(norm(gevonden.title || gevonden.name))?.type } : {};
    const start = time.slice(0, 5);
    if (
      bestaat.has(`${date}|${start}|${norm(title)}`) ||
      (gevonden && bestaat.has(`${date}|${start}|${norm(gevonden.title || gevonden.name)}`))
    ) {
      aanwezig++;
      return;
    }
    const type =
      t.type || (/^orry/i.test(title) ? "orry" : loc === "Crea Club" ? "crea" : "entertainment");
    nieuw.push({
      id: basisId + i,
      date,
      time,
      title: t.title || t.name || title,
      loc,
      locs: Array.isArray(t.locs) && t.locs.length ? t.locs : [loc],
      type,
      staff: [],
      maxPax: maxPax || t.maxPax || "",
      price,
      ...bibVasteVelden(t),
      // Foto/teksten niet kopiëren: die komen bij het tonen uit de bibliotheek
      // (scheduleMetLibTeksten) — scheelt veel ruimte in het live-document.
    });
  });
  const spring = { ...(appData.dailySpringkussen || {}) };
  let springAantal = 0;
  Object.entries(KERST_2026_SPRINGKUSSEN).forEach(([d, [start, end]]) => {
    const oud = spring[d];
    if (oud && oud.open) return;
    spring[d] = { ...(oud || { staff: [] }), open: true, start, end };
    springAantal++;
  });
  return { nieuw, spring, springAantal, aanwezig };
};

// Geïmporteerde kerstactiviteiten (18 dec – 3 jan) (opnieuw) koppelen.
// Uitgangspunt is steeds de oorspronkelijke Excel-titel (zelfde dag + begintijd),
// zodat een eerder verkeerde koppeling ook rechtgezet wordt.
const koppelKerst2026 = (appData) => {
  const lib = appData.activityLibrary || {};
  const perSlot = new Map();
  KERST_2026_PROGRAMMA.forEach(([date, time, title, loc]) => {
    const k = `${date}|${time.slice(0, 5)}`;
    if (!perSlot.has(k)) perSlot.set(k, []);
    perSlot.get(k).push({ title, loc });
  });
  let aantal = 0;
  const schedule = (appData.schedule || []).map((a) => {
    if (!a || !a.title || a.date < "2026-12-18" || a.date > "2027-01-03") return a;
    const kandidaten = perSlot.get(`${a.date}|${String(a.time || "").slice(0, 5)}`);
    if (!kandidaten) return a;
    const rij =
      kandidaten.length === 1
        ? kandidaten[0]
        : kandidaten.reduce((best, r) => {
            const sc = Math.max(titelGelijkenis(a.title, r.title), (zoekLijkendeBibActiviteit(r.title, lib, { loc: r.loc })?.tpl?.title || "") === a.title ? 1 : 0);
            return !best || sc > best.sc ? { ...r, sc } : best;
          }, null);
    const m = zoekLijkendeBibActiviteit(rij.title, lib, { loc: rij.loc });
    const doelTitel = m ? m.tpl.title || m.tpl.name : rij.title;
    const t = m ? m.tpl : null;
    const vast = t ? bibVasteVelden(t) : null;
    const veldenOntbreken =
      vast &&
      Object.entries(vast).some(([k, v]) => (Array.isArray(v) ? v.length : v) && JSON.stringify(a[k] ?? "") !== JSON.stringify(v));
    if (doelTitel === a.title && !veldenOntbreken) return a;
    aantal++;
    return {
      ...a,
      ...(vast || {}),
      title: doelTitel,
      type: t ? t.type || m.afd || a.type : /^orry/i.test(rij.title) ? "orry" : rij.loc === "Crea Club" ? "crea" : "entertainment",
      locs: t && Array.isArray(t.locs) && t.locs.length ? [...new Set([a.loc, ...t.locs].filter(Boolean))] : [a.loc].filter(Boolean),
    };
  });
  return { schedule, aantal };
};

// 🎨 Crea Club open/dicht: automatisch open als er die dag een Crea-activiteit
// in het programma staat, anders dicht. Zet de FM het in Programmabeheer zelf
// om (afwijkend van automatisch), dan geldt dat (handmatig: true).
const heeftCreaActiviteit = (appData, dag) =>
  (appData?.schedule || []).some((a) => a && a.type === "crea" && String(a.date || "").slice(0, 10) === dag);
const creaOpenOp = (appData, dag) => {
  const d = appData?.dailyCrea?.[dag];
  if (d && d.handmatig && typeof d.open === "boolean") return d.open;
  return heeftCreaActiviteit(appData, dag);
};
// 🎮 Hotspot standaard dicht; alleen open als hij voor die dag open is gezet
const hotspotOpenOp = (appData, dag) => appData?.dailyHotspot?.[dag]?.open === true;

const sanitizeData = (data) => {
  const safe = data || {};
  let safeStaffList = Array.isArray(safe.staffList)
    ? safe.staffList.map((item) => {
        const base =
          typeof item === "string"
            ? { name: item, pin: "0000", forcePinChange: true }
            : { ...item };
        if (!base.pin) base.pin = "0000";
        return base;
      })
    : DEFAULT_STAFF_NAMES.map((name) => ({
        name,
        pin: "0000",
        forcePinChange: true,
      }));

  // Auto-apply seed profile photos for staff without a custom photo
  safeStaffList = safeStaffList.map((s) => {
    if (!s.profilePhoto && SEED_PROFILE_PHOTOS[s.name]) {
      return { ...s, profilePhoto: SEED_PROFILE_PHOTOS[s.name] };
    }
    return s;
  });

  const safeSettings = safe.settings || {
    pin: "2412",
    coPin: "1234",
    dvList: [],
    theme: "general",
    customPrimary: C.Pine,
    customSecondary: C.Grass,
    fmTheme: "general",
  };
  if (!Array.isArray(safeSettings.dvList)) safeSettings.dvList = [];

  return {
    settings: safeSettings,
    dailyStats: safe.dailyStats || {},
    dailyRoles: safe.dailyRoles || {},
    dailySpringkussen: safe.dailySpringkussen || {},
    dailyCrea: safe.dailyCrea || {},
    dailyHotspot: safe.dailyHotspot || {},
    dailyFmPresent: safe.dailyFmPresent || {}, // ✅ NIEUW
    dailyDv: safe.dailyDv || {}, // ✅ NIEUW
    schedule: Array.isArray(safe.schedule) ? safe.schedule : [],
    planningStatus: safe.planningStatus || {},
    characters: Array.isArray(safe.characters) ? safe.characters : [],
    checklists: Array.isArray(safe.checklists) ? safe.checklists : [],
    checklistTemplates: safe.checklistTemplates || {},
    activityTemplates: Array.isArray(safe.activityTemplates)
      ? safe.activityTemplates
      : [],
    handover: safe.handover || {},
    hourCorrections: Array.isArray(safe.hourCorrections)
      ? safe.hourCorrections
      : [],
    orders: Array.isArray(safe.orders) ? safe.orders : [],
    staffList: safeStaffList,
    notifications: Array.isArray(safe.notifications) ? safe.notifications : [],
    phoneDirectory: Array.isArray(safe.phoneDirectory)
      ? safe.phoneDirectory
      : [],

    chats: Array.isArray(safe.chats) ? safe.chats : [],
    bingoWinners: Array.isArray(safe.bingoWinners) ? safe.bingoWinners : [],
    springkussenChecks: safe.springkussenChecks || {},
    springkussenTasks: Array.isArray(safe.springkussenTasks)
      ? safe.springkussenTasks
      : [],
    manuals: Array.isArray(safe.manuals) ? safe.manuals : [],
    activityLibrary: safe.activityLibrary || {
      crea: [],
      entertainment: [],
      orry: [],
    },
    qualityVisits: Array.isArray(safe.qualityVisits) ? safe.qualityVisits : [], // 🔥 Quality Visits
    hoursReports: Array.isArray(safe.hoursReports) ? safe.hoursReports : [], // 📄 Urenlijsten (PDF)
    activityRatings: Array.isArray(safe.activityRatings)
      ? safe.activityRatings
      : [], // 🔥 Activity Ratings
    guestFeedback: Array.isArray(safe.guestFeedback) ? safe.guestFeedback : [], // 🔥 Guest Feedback
    fmChecklist: Array.isArray(safe.fmChecklist) ? safe.fmChecklist : [],
    workSchedule: safe.workSchedule || {}, // 🔥 KRITIEK: Behoud workSchedule data!
    shiftSwapRequests: Array.isArray(safe.shiftSwapRequests)
      ? safe.shiftSwapRequests
      : [], // 🔄 NIEUW: Shift ruil aanvragen

    staffSkills: safe.staffSkills || {},
    staffCharacters: safe.staffCharacters || {},
    staffAvailability: safe.staffAvailability || {},
    staffAvailabilityLocks: safe.staffAvailabilityLocks || {},
    staffContractInfo: safe.staffContractInfo || {},
    urencodes: safe.urencodes || {
      entertainment: 258,
      crea: 243,
      springkussen: 279,
      overig: "",
    },
    maandInstellingen: safe.maandInstellingen || {},
    roosterDiensten: safe.roosterDiensten || {}, // { "YYYY-MM-DD": [dienst,...] }
    extraUren: safe.extraUren || {}, // { "name": { "YYYY-MM-DD": [{ type, hours, label }] } }
    dienstTemplates: safe.dienstTemplates || [], // vaste dienst-templates
    vakantieplannen: safe.vakantieplannen || {}, // { naam: [{from, to, note}] }
    userPrefs: safe.userPrefs || {}, // 🔒 persoonlijke voorkeuren (dashboardindeling, snelkoppelingen) — cross-device

    npsScores: safe.npsScores || {},
    // ⏱️ Dienst gestart/afgesloten — ontbrak in deze lijst, waardoor
    // starttijden na elke server-update uit de app werden weggefilterd
    timeClock: isPlainObj(safe.timeClock) ? safe.timeClock : {}, // { "YYYY-MM-DD": { naam: { in, out } } }
    roosterPrognose: isPlainObj(safe.roosterPrognose) ? safe.roosterPrognose : {},
    dagbord: isPlainObj(safe.dagbord) ? safe.dagbord : {}, // 🧩 { "YYYY-MM-DD": { sleutel: item } }
    openPunten: isPlainObj(safe.openPunten) ? safe.openPunten : {}, // 🧩 { id: punt }
    feestdagen: isPlainObj(safe.feestdagen) ? safe.feestdagen : {}, // 🎄 voorkeursrooster feestdagen
    afscheid: isPlainObj(safe.afscheid) ? safe.afscheid : {}, // 💐 afscheidsboeken { boeken, berichten }
    pietenservice: isPlainObj(safe.pietenservice) ? safe.pietenservice : {}, // 🎁 { avonden: { datum: { inst, slots } } }
    lastUpdate: safe.lastUpdate || new Date().toISOString(),
  };
};

const INITIAL_DATA = sanitizeData({});

const toIsoDate = (date) => {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().split("T")[0];
};

const toDutchDate = (dateObj) => {
  if (!dateObj) return "";
  return dateObj.toLocaleDateString("nl-NL", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

// Parse dateKey string "2025-01-15" naar Date object
const parseDateKey = (dateKey) => {
  if (!dateKey) return new Date();
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Date(year, month - 1, day);
};

// Haal alleen voornaam op uit volledige naam (bijv. "Daphne Weijs, D.M.M." -> "Daphne")
// 👥 ONLINE AANWEZIGHEID BALK
const OnlinePresenceBar = ({
  onlineUsers,
  staffList,
  theme,
  loggedInUserName,
}) => {
  const t = theme || THEMES.general;
  const logoColor = CP_LOGO_COLORS[t?.id] || "#b4d6a1";

  if (!onlineUsers || onlineUsers.length === 0) return null;

  // Sorteer: huidige gebruiker eerst, daarna alfabetisch
  const sorted = [...onlineUsers].sort((a, b) => {
    if (a.name === loggedInUserName) return -1;
    if (b.name === loggedInUserName) return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <div
      style={{
        backgroundColor: "rgba(0,0,0,0.18)",
        borderTop: `1px solid rgba(255,255,255,0.08)`,
        padding: "5px 12px",
        display: "flex",
        alignItems: "center",
        gap: 6,
        overflowX: "auto",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          fontSize: 9,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: `${logoColor}99`,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        Nu online
      </span>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          flexWrap: "nowrap",
        }}
      >
        {sorted.map((u) => {
          const isMe = u.name === loggedInUserName;
          return (
            <div
              key={u.name}
              title={u.name}
              style={{ position: "relative", flexShrink: 0 }}
            >
              <div
                style={{
                  borderRadius: "50%",
                  border: isMe
                    ? `2px solid ${logoColor}`
                    : "2px solid rgba(255,255,255,0.25)",
                  boxShadow: isMe ? `0 0 0 1px rgba(0,0,0,0.2)` : "none",
                }}
              >
                <ProfileAvatar
                  name={u.name}
                  staffList={staffList}
                  size={26}
                  color={t.secondary || t.primary}
                />
              </div>
              {/* Groen stipje */}
              <span
                style={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  width: 7,
                  height: 7,
                  backgroundColor: "#4ade80",
                  borderRadius: "50%",
                  border: "1.5px solid rgba(0,0,0,0.3)",
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Ronde profielfoto of initialen-fallback
 * Props: name, staffList, size (px), color (hex), className
 */
// 🎨 Profielfoto in de kleur van iemands eigen thema: de effen CP-achtergrond van de
// uitgeknipte foto wordt vervangen (alleen in de browser, niets wordt opgeslagen).
let CP_FM_THEMA = null; // thema van de FM (settings.fmTheme), gezet door App
let CP_FM_FOTO = null; // eigen profielfoto van de FM (settings.fmFoto)
const zetFmThema = (t, foto) => {
  CP_FM_THEMA = t || null;
  CP_FM_FOTO = foto || null;
};
const FOTO_KLEUR_PAAR = { groen: "#B1EDA6", blauw: "#9DB4F6", roze: "#FF699B", geel: "#FFCA55" };
const THEMA_FOTO_KLEUR = { general: "#B1EDA6", winter: "#9DB4F6", spring: "#FF699B", summer: "#FFCA55", autumn: "#FFCA55" };
const themaKleurVan = (name, staffList) => {
  const s = (staffList || []).find((x) => x && x.name === name);
  // Zelf gekozen achtergrondkleur gaat voor het thema
  const keuze = name === "Maik" && CP_FM_FOTO?.fotoKleur ? CP_FM_FOTO.fotoKleur : s?.fotoKleur;
  if (keuze && FOTO_KLEUR_PAAR[keuze]) return FOTO_KLEUR_PAAR[keuze];
  // Maik (FM) gebruikt zijn FM-thema, ook als er in de teamlijst nog een oud thema bij hem staat
  const id = (name === "Maik" && CP_FM_THEMA) || s?.theme || null;
  return THEMA_FOTO_KLEUR[id] || null;
};
const avatarGeheugen = new Map();
// Sleutel over de hele foto (hash), zodat een nieuwe foto nooit de oude uit de cache krijgt
const avatarHash = (s) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return (h >>> 0).toString(36);
};
const avatarSleutel = (src, kleur) => `${src.length}_${avatarHash(src)}_${kleur.slice(1)}`;
const avatarUitCache = (k) => {
  if (avatarGeheugen.has(k)) return avatarGeheugen.get(k);
  try {
    const v = localStorage.getItem("cp_av:" + k);
    if (v) {
      avatarGeheugen.set(k, v);
      return v;
    }
  } catch (e) {}
  return null;
};
const avatarInCache = (k, v) => {
  avatarGeheugen.set(k, v);
  try {
    localStorage.setItem("cp_av:" + k, v);
  } catch (e) {}
};
let avatarRij = Promise.resolve(); // één voor één opmaken, zodat de app soepel blijft
const useThemaFoto = (src, kleur) => {
  const k = src && kleur ? avatarSleutel(src, kleur) : null;
  const [klaar, setKlaar] = useState(() => (k ? avatarUitCache(k) : null));
  useEffect(() => {
    if (!k) return;
    const c = avatarUitCache(k);
    if (c) {
      setKlaar(c);
      return;
    }
    let levend = true;
    const taak = () => opgemaakteFoto(src, { achtergrond: kleur, maat: 256 });
    avatarRij = avatarRij.then(taak, taak).then((u) => {
      avatarInCache(k, u || "-");
      if (levend) setKlaar(u || "-");
      return u;
    });
    return () => {
      levend = false;
    };
  }, [k]); // eslint-disable-line
  return k && klaar && klaar !== "-" ? klaar : src;
};

// 📸 Eenmalige pop-up: "maak zelf je profielfoto" (tot iemand er een gemaakt heeft)
const ProfielFotoPopup = ({ naam, staffList, onMaken }) => {
  const sleutel = `cp_pf_pop_${String(naam || "").replace(/[^A-Za-z0-9]/g, "_")}`;
  const [toon, setToon] = useState(false);
  useEffect(() => {
    let tot = 0;
    try {
      tot = Number(localStorage.getItem(sleutel) || 0);
      if (sessionStorage.getItem(sleutel)) return;
    } catch (e) {}
    if (tot === -1 || Date.now() < tot) return;
    const t = setTimeout(() => setToon(true), 2500);
    return () => clearTimeout(t);
  }, [sleutel]);
  const later = (dagen) => {
    try {
      localStorage.setItem(sleutel, String(dagen < 0 ? -1 : Date.now() + dagen * 864e5));
      sessionStorage.setItem(sleutel, "1");
    } catch (e) {}
    setToon(false);
  };
  if (!toon) return null;
  return (
    <div className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center" data-noswipe>
      <div className="absolute inset-0" style={{ backgroundColor: "rgba(0,25,47,0.45)" }} onClick={() => later(3)} />
      <div className="relative bg-white w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl shadow-xl p-6 flex flex-col items-center text-center gap-3" style={{ paddingBottom: "max(24px, env(safe-area-inset-bottom))" }}>
        <div className="relative">
          <ProfileAvatar name={naam} staffList={staffList} size={88} />
          <span className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: C.Pine }}>
            <Camera size={17} color="white" />
          </span>
        </div>
        <p className="text-lg font-semibold" style={{ color: C.Lagoon }}>
          Nieuw: maak zelf je profielfoto
        </p>
        <p className="text-sm text-gray-600">
          Maak een selfie of kies een foto. De app haalt de achtergrond er automatisch af en jij kiest een CP-kleur. Zo sta je overal mooi in de app.
        </p>
        <button
          onClick={() => {
            later(30);
            onMaken();
          }}
          className="w-full h-11 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 active:scale-[0.98]"
          style={{ backgroundColor: C.Pine }}
        >
          <Camera size={16} /> Nu maken
        </button>
        <div className="flex gap-2 w-full">
          <button onClick={() => later(3)} className="flex-1 h-10 rounded-xl text-sm font-semibold" style={{ backgroundColor: C.BorderLight, color: C.TextMuted }}>
            Later
          </button>
          <button onClick={() => later(-1)} className="flex-1 h-10 rounded-xl text-sm font-semibold" style={{ color: C.TextMuted }}>
            Niet meer tonen
          </button>
        </div>
      </div>
    </div>
  );
};

const ProfileAvatar = ({
  name,
  staffList = [],
  size = 32,
  color = C.Pine,
  className = "",
}) => {
  const staff = (staffList || []).find((s) => s.name === name);
  // Check stored photo first, then seed by exact name, then seed by first name
  const firstName = name ? name.trim().split(" ")[0] : "";
  const photoBron =
    (name === "Maik" && CP_FM_FOTO?.profilePhoto) ||
    staff?.profilePhoto ||
    SEED_PROFILE_PHOTOS[name] ||
    SEED_PROFILE_PHOTOS[firstName];
  // Zelfgemaakte foto (profielfotomaker) staat al op de gekozen kleur: direct tonen, niet opnieuw bewerken
  const zelfGemaakt = !!((name === "Maik" && CP_FM_FOTO?.fotoVrij) || staff?.fotoVrij);
  const photoUrl = useThemaFoto(zelfGemaakt ? null : photoBron, themaKleurVan(name, staffList)) || photoBron;
  const initial = name ? name.trim().charAt(0).toUpperCase() : "?";
  const fontSize = Math.round(size * 0.42);

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name}
        className={`rounded-full object-cover flex-shrink-0 ${className}`}
        style={{ width: size, height: size, minWidth: size }}
      />
    );
  }
  return (
    <div
      className={`rounded-full flex items-center justify-center font-semibold text-white flex-shrink-0 select-none ${className}`}
      style={{
        width: size,
        height: size,
        minWidth: size,
        backgroundColor: color,
        fontSize,
      }}
    >
      {initial}
    </div>
  );
};

const getFirstName = (fullName) => {
  if (!fullName) return "";
  return fullName.split(" ")[0];
};

const calculateAge = (dob) => {
  if (!dob) return 0;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  return age;
};


const getGreeting = () => {
  const h = new Date().getHours();
  return h < 6
    ? "Goedenacht"
    : h < 12
    ? "Goedemorgen"
    : h < 18
    ? "Goedemiddag"
    : "Goedenavond";
};

const getWeatherIcon = (code) => {
  if (code === 0) return { icon: Sun, desc: "Zon" };
  if (code >= 1 && code <= 3) return { icon: CloudSun, desc: "Bewolkt" };
  if (code >= 45 && code <= 48) return { icon: Cloud, desc: "Mist" };
  if (code >= 51 && code <= 67) return { icon: CloudRain, desc: "Regen" };
  if (code >= 71 && code <= 77) return { icon: CloudSnow, desc: "Sneeuw" };
  if (code >= 80 && code <= 82) return { icon: CloudRain, desc: "Buien" };
  if (code >= 95) return { icon: CloudLightning, desc: "Onweer" };
  return { icon: CloudSun, desc: "Wisselvallig" };
};

const getCharColorInfo = (role) => {
  const r = role.toLowerCase();
  if (r.includes("orry")) return { bg: C.Blossom, text: C.Sunset }; // 🔴 Roze/Rood
  if (r.includes("rep")) return { bg: C.Honey, text: C.Bark }; // 🟡 Geel
  if (r.includes("woops")) return { bg: C.Sky, text: C.Lagoon }; // 🔵 Blauw
  if (r.includes("bing")) return { bg: C.Grass, text: C.Pine }; // 🟢 Groen
  return { bg: C.Bg, text: C.Bark };
};

const safeRenderText = (item) => {
  if (typeof item === "string") return item;
  if (typeof item === "object" && item !== null) return item.text || "";
  return "";
};

const formatStaffList = (staff) => {
  if (!staff) return [];
  if (Array.isArray(staff))
    return staff.map((s) =>
      typeof s === "object" ? s.name || "Error" : String(s)
    );
  if (typeof staff === "object") return [staff.name || "Error"];
  return [String(staff)];
};

const getFridayOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  let daysToSubtract = 0;
  if (day === 5) daysToSubtract = 0;
  else if (day === 6) daysToSubtract = 1;
  else if (day === 0) daysToSubtract = 2;
  else if (day === 1) daysToSubtract = 3;
  else if (day === 2) daysToSubtract = 4;
  else if (day === 3) daysToSubtract = 5;
  else if (day === 4) daysToSubtract = 6;
  d.setDate(d.getDate() - daysToSubtract);
  return d;
};

const useWeather = (date, isLoggedIn) => {
  const [weather, setWeather] = useState({
    temp: "--",
    desc: "...",
    icon: Cloud,
  });
  useEffect(() => {
    // America (Horst aan de Maas): lat 51.4408, lon 6.0536
    // Actueel weer via current_weather + hourly feel, ververs elke 15 min
    const fetchWeather = async () => {
      try {
        const url =
          "https://api.open-meteo.com/v1/forecast?latitude=51.4408&longitude=6.0536&current=temperature_2m,weather_code&timezone=Europe%2FAmsterdam";
        const response = await fetch(url);
        const data = await response.json();
        if (data.current) {
          const code = data.current.weather_code;
          const temp = Math.round(data.current.temperature_2m);
          setWeather({ temp, ...getWeatherIcon(code) });
        } else {
          setWeather({ temp: "--", desc: "Geen data", icon: Cloud });
        }
      } catch (error) {
        setWeather({ temp: "--", desc: "Offline", icon: WifiOff });
      }
    };
    fetchWeather();
    const interval = setInterval(fetchWeather, 900000); // elke 15 min
    return () => clearInterval(interval);
  }, [isLoggedIn]);
  return weather;
};

const isActivityPast = (timeStr, date) => {
  if (!timeStr) return false;
  const todayStr = toIsoDate(new Date());
  const checkDateStr = toIsoDate(date);
  if (checkDateStr < todayStr) return true;
  if (checkDateStr > todayStr) return false;
  const now = new Date();
  const parts = timeStr.split("-");
  const timeToParse = parts.length > 1 ? parts[1] : parts[0];
  const [hours, minutes] = timeToParse.trim().split(":").map(Number);
  if (isNaN(hours) || isNaN(minutes)) return false;
  const activityTime = new Date(now);
  activityTime.setHours(hours, minutes, 0, 0);
  return now > activityTime;
};

const isActivityNow = (timeStr, date) => {
  if (!timeStr) return false;
  if (toIsoDate(date) !== toIsoDate(new Date())) return false;
  const parts = timeStr.split("-");
  const sp = parts[0].trim().split(":").map(Number);
  const ep = (parts[1] || parts[0]).trim().split(":").map(Number);
  if (isNaN(sp[0]) || isNaN(ep[0])) return false;
  const now = new Date();
  const cur = now.getHours() * 60 + now.getMinutes();
  const start = sp[0] * 60 + (sp[1] || 0);
  const end = ep[0] * 60 + (ep[1] || 0);
  return cur >= start && cur <= end;
};

const sortActivities = (a, b, currentDay) => {
  const pastA = isActivityPast(a.time, currentDay);
  const pastB = isActivityPast(b.time, currentDay);
  if (pastA && !pastB) return 1;
  if (!pastA && pastB) return -1;
  const getStartTime = (t) => {
    const parts = t.split("-")[0].trim().split(":");
    return parseInt(parts[0]) * 60 + parseInt(parts[1]);
  };
  return getStartTime(a.time) - getStartTime(b.time);
};

const groupTasksByCategory = (tasks) => {
  const groups = {
    startup: [],
    during: [],
    closing: [],
    extra: [],
    // Voeg dagspecifieke categorieën toe
    daySpecificEntertainment: [],
    daySpecificCrea: [],
    daySpecificSpringkussen: [],
  };

  // ✨ Koppel elke dagspecifieke categorie aan het type waar ze bij hoort —
  // voorkomt dat een taak van bv. type "hotspot" met een verkeerd
  // meegesleepte categorie zoals "daySpecificEntertainment" onder de
  // verkeerde kop terechtkomt.
  const DAY_SPECIFIC_CAT_FOR_TYPE = {
    entertainment: "daySpecificEntertainment",
    crea: "daySpecificCrea",
    springkussen: "daySpecificSpringkussen",
  };

  tasks.forEach((t) => {
    // Bepaal category op basis van type EN category
    let cat = t.type === "extra" ? "extra" : t.category || "startup";

    // Als category niet is gezet, bepaal op basis van type
    if (!cat) {
      if (t.type === "extra") {
        cat = "extra";
      } else {
        cat = "startup"; // Default voor andere taken
      }
    }

    // 🩹 SELF-HEALING: een dagspecifieke categorie die niet bij dit
    // taak-type hoort (bv. "daySpecificEntertainment" op een Hotspot-taak)
    // valt terug op "startup" in plaats van de verkeerde kop te tonen.
    if (
      cat.startsWith("daySpecific") &&
      DAY_SPECIFIC_CAT_FOR_TYPE[t.type] !== cat
    ) {
      cat = "startup";
    }

    if (groups[cat]) {
      groups[cat].push(t);
    } else {
      groups["startup"].push(t);
    }
  });
  return groups;
};

// --- SUB COMPONENTS ---
const DateControls = ({
  currentDay,
  onPrev,
  onNext,
  onCalendar,
  theme,
  className = "",
  size = "default", // "compact", "default", "large"
  showWeekRange = false, // Toon week range ipv enkele dag
}) => {
  const t = theme || THEMES.general;

  // 🔧 FIX: Return null als currentDay undefined is
  if (!currentDay) return null;

  const isToday = new Date().toDateString() === currentDay.toDateString();

  // 📅 Weekbalk (zelfde als op het dagbord) — dag kiezen met één tik,
  // vegen of pijltjes voor een andere week. Zet de globale datum via een event.
  if (!showWeekRange && size !== "compact") {
    const zetDag = (d) => {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      window.dispatchEvent(new CustomEvent("datecontrols-set-day", { detail: x.toISOString() }));
    };
    const ma = new Date(currentDay);
    ma.setHours(0, 0, 0, 0);
    ma.setDate(ma.getDate() - ((ma.getDay() + 6) % 7));
    const dagen = Array.from({ length: 7 }, (_, i) => {
      const x = new Date(ma);
      x.setDate(ma.getDate() + i);
      return x;
    });
    const vandaagStr = new Date().toDateString();
    const verschuif = (n) => {
      const x = new Date(currentDay);
      x.setDate(x.getDate() + n);
      zetDag(x);
    };
    let sx = null;
    const maand = currentDay.toLocaleDateString("nl-NL", { month: "long", year: "numeric" });
    return (
      <div className={`w-full md:w-[340px] ${className}`} data-noswipe>
        <div className="flex items-center justify-between mb-1">
          <button
            onClick={onCalendar}
            className="flex items-center gap-1 text-sm font-semibold capitalize min-h-[40px]"
            style={{ color: C.Lagoon }}
            aria-label="Kalender openen"
          >
            {maand} <ChevronDown size={15} />
          </button>
          {!isToday && (
            <button
              onClick={() => zetDag(new Date())}
              className="h-8 px-3 rounded-lg text-xs font-semibold"
              style={{ backgroundColor: C.PineLight, color: C.Pine }}
            >
              Vandaag
            </button>
          )}
        </div>
        <div
          className="flex items-center gap-0.5"
          onTouchStart={(e) => (sx = e.touches[0].clientX)}
          onTouchEnd={(e) => {
            if (sx == null) return;
            const dx = e.changedTouches[0].clientX - sx;
            sx = null;
            if (Math.abs(dx) > 50) verschuif(dx < 0 ? 7 : -7);
          }}
        >
          <button
            onClick={() => verschuif(-7)}
            className="w-8 h-12 flex items-center justify-center text-gray-500 flex-shrink-0 rounded-xl active:bg-gray-100"
            aria-label="Vorige week"
          >
            <ChevronLeft size={16} />
          </button>
          {dagen.map((d) => {
            const gekozen = d.toDateString() === currentDay.toDateString();
            const isVandaag = d.toDateString() === vandaagStr;
            return (
              <button
                key={d.toDateString()}
                onClick={() => zetDag(d)}
                aria-pressed={gekozen}
                aria-label={d.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" })}
                className="flex-1 h-12 rounded-xl flex flex-col items-center justify-center"
                style={
                  gekozen
                    ? { backgroundColor: C.Pine, color: "white" }
                    : { color: isVandaag ? C.Pine : C.Lagoon, boxShadow: isVandaag ? `inset 0 0 0 2px ${C.Pine}` : undefined }
                }
              >
                <span className="text-xs" style={{ opacity: gekozen ? 0.85 : 0.7 }}>
                  {d.toLocaleDateString("nl-NL", { weekday: "short" }).slice(0, 2)}
                </span>
                <span className="text-[15px] font-semibold leading-tight">{d.getDate()}</span>
              </button>
            );
          })}
          <button
            onClick={() => verschuif(7)}
            className="w-8 h-12 flex items-center justify-center text-gray-500 flex-shrink-0 rounded-xl active:bg-gray-100"
            aria-label="Volgende week"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  // Voor week range: bereken start (vrijdag) en eind (donderdag)
  let displayText;
  if (showWeekRange) {
    const friday = getFridayOfWeek(currentDay);
    const thursday = new Date(friday);
    thursday.setDate(friday.getDate() + 6);

    const fridayDay = friday.getDate();
    const thursdayDay = thursday.getDate();
    const fridayMonth = friday.toLocaleDateString("nl-NL", { month: "short" });
    const thursdayMonth = thursday.toLocaleDateString("nl-NL", {
      month: "short",
    });

    // Als zelfde maand: "13-19 feb", anders: "28 jan - 3 feb"
    if (fridayMonth === thursdayMonth) {
      displayText = `${fridayDay}-${thursdayDay} ${fridayMonth}`;
    } else {
      displayText = `${fridayDay} ${fridayMonth} - ${thursdayDay} ${thursdayMonth}`;
    }
  } else {
    // Weekdag en datum (oude gedrag)
    const weekday = currentDay.toLocaleDateString("nl-NL", {
      weekday: "short",
    });
    const dayMonth = currentDay.toLocaleDateString("nl-NL", {
      day: "numeric",
      month: "short",
    });
    displayText = (
      <>
        <span className="font-semibold capitalize">{weekday}</span>
        <span className="opacity-70">{dayMonth}</span>
        {isToday && <span className="text-xs opacity-80">• Nu</span>}
      </>
    );
  }

  const sizeClasses = {
    compact: {
      button: "p-1.5",
      icon: 16,
      text: "text-xs",
      minWidth: "min-w-[90px]",
    },
    default: {
      button: "p-2",
      icon: 18,
      text: "text-sm",
      minWidth: "min-w-[120px]",
    },
    large: {
      button: "p-2.5",
      icon: 20,
      text: "text-base",
      minWidth: "min-w-[140px]",
    },
  };
  const s = sizeClasses[size] || sizeClasses.default;

  return (
    <div
      className={`
        flex items-center gap-1
        rounded-xl p-1
        shadow-soft
        w-full
        ${className}
      `}
      style={{
        backgroundColor: "#F9FAFB",
        border: "1px solid #F3F4F6",
      }}
    >
      <button
        onClick={onPrev}
        className="w-11 h-11 flex items-center justify-center flex-shrink-0 rounded-xl transition-transform active:scale-95"
        style={{
          backgroundColor: C.PineLight,
          color: C.Pine,
        }}
        aria-label={showWeekRange ? "Vorige week" : "Vorige dag"}
      >
        <ChevronLeft size={s.icon} />
      </button>

      <button
        onClick={onCalendar}
        className={`flex-1 px-3 h-11 ${s.text} font-semibold text-center flex items-center justify-center gap-1.5 rounded-lg transition-all duration-200 hover:opacity-90 active:scale-95`}
        style={{
          backgroundColor: isToday ? C.Pine : "#FFFFFF",
          color: isToday ? "#FFFFFF" : C.Pine,
          boxShadow: isToday ? `0 2px 8px ${C.Pine}30` : "none",
        }}
      >
        <CalendarIcon size={s.icon - 4} className="opacity-70" />
        {showWeekRange ? (
          <span className="font-semibold">{displayText}</span>
        ) : (
          displayText
        )}
      </button>

      <button
        onClick={onNext}
        className="w-11 h-11 flex items-center justify-center flex-shrink-0 rounded-xl transition-transform active:scale-95"
        style={{
          backgroundColor: C.PineLight,
          color: C.Pine,
        }}
        aria-label={showWeekRange ? "Volgende week" : "Volgende dag"}
      >
        <ChevronRight size={s.icon} />
      </button>
      {onCalendar && (
        <button
          onClick={() => {
            if (isToday) return;
            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent("datecontrols-goto-today"));
            }
          }}
          className={`${s.button} rounded-lg transition-all duration-200 active:scale-95 hover:opacity-80 text-xs font-semibold px-2`}
          style={{
            backgroundColor: `${C.Pine}20`,
            color: C.Pine,
            visibility: isToday ? "hidden" : "visible",
          }}
          tabIndex={isToday ? -1 : 0}
          aria-hidden={isToday}
          aria-label="Ga naar vandaag"
        >
          Nu
        </button>
      )}
    </div>
  );
};

const ActivityItem = ({
  item,
  dateObj,
  userRole,
  loggedInUserName,
  appData,
  setView,
}) => {
  const past = isActivityPast(item.time, dateObj);
  const catInfo = ACTIVITY_TYPES.find((t) => t.id === item.type);
  const dateKey = toIsoDate(dateObj);
  const isFMorCO = userRole === "fm" || userRole === "co";

  // Medewerkers die via roostermaker zijn ingepland op deze dag
  const scheduledNames = new Set();
  (appData.roosterDiensten?.[dateKey] || []).forEach((d) =>
    (d.assigned || []).forEach((n) => scheduledNames.add(n))
  );
  // Ook CSV-geïmporteerde shifts meenemen
  (appData.workSchedule?.[dateKey] || []).forEach((shift) => {
    if (shift.matchedUser) scheduledNames.add(shift.matchedUser);
  });

  // Rooster is gepubliceerd als er from-roostermaker shifts zijn
  const isPublished = (appData.workSchedule?.[dateKey] || []).some(
    (s) => s.source === "from-roostermaker"
  );

  // Alleen ingeplande medewerkers tonen; voor medewerkers alleen als gepubliceerd
  const staffMembers =
    isFMorCO || isPublished
      ? formatStaffList(item.staff)
          .filter((n) => scheduledNames.has(n) || (item.staffExtra || []).includes(n))
          .sort()
      : [];

  return (
    <div
      key={item.id}
      className={`p-2 md:p-3 flex flex-col gap-1.5 overflow-hidden ${
        past ? "opacity-50 grayscale bg-gray-50" : "hover:bg-gray-50"
      } transition-colors`}
    >
      <div className="flex justify-between items-start gap-1.5 flex-wrap">
        <div className="font-semibold text-sm text-gray-500 shrink-0">
          {item.time}
        </div>
        {catInfo && (
          <div
            className="text-[9px] md:text-xs font-semibold uppercase truncate max-w-[90px]"
            style={{
              color: catInfo.text,
              backgroundColor: catInfo.bg,
              display: "inline-block",
              padding: "2px 5px",
              borderRadius: "4px",
            }}
          >
            {catInfo.label}
          </div>
        )}
      </div>
      <div>
        <div className="font-semibold text-sm md:text-base text-gray-800 whitespace-normal break-words leading-tight">
          {item.title}
        </div>
        <div className="text-xs md:text-xs text-gray-500 mt-1 space-y-0.5">
          <div className="flex items-center gap-1 flex-wrap">
            <MapPin size={12} className="shrink-0" />{" "}
            <span className="break-words">{item.loc}</span>
          </div>
          {item.maxPax && (
            <div className="flex items-center gap-1 font-semibold text-gray-800 flex-wrap">
              <Users size={12} className="shrink-0" /> Max. {item.maxPax}
            </div>
          )}
        </div>
        {(userRole === "fm" || userRole === "co") && (
          <div className="text-[10px] md:text-xs text-gray-500 mt-2 bg-gray-50 p-1.5 md:p-2 rounded-xl border border-gray-100 flex flex-wrap gap-2 md:gap-3">
            {item.maxPax && (
              <span className="flex items-center gap-0.5">
                <Users size={10} className="inline shrink-0" /> Max:{" "}
                {item.maxPax}
              </span>
            )}
            {item.price && (
              <span className="flex items-center gap-0.5">
                <Euro size={10} className="inline shrink-0" /> {prijsTekst(item)}
              </span>
            )}
            {item.refhd && (
              <span className="font-mono bg-gray-200 px-1 rounded text-[10px] md:text-[10px]">
                REFHD: {item.refhd}
              </span>
            )}
            {item.paris && (
              <span
                className="font-semibold flex items-center gap-0.5"
                style={{ color: C.Lagoon }}
              >
                <CheckCircle size={10} className="shrink-0" /> PARIS
              </span>
            )}
            {item.villageMap && (
              <span
                className="font-semibold flex items-center gap-0.5"
                style={{ color: C.Pine }}
              >
                <MapIcon size={10} className="shrink-0" /> VillageMap
              </span>
            )}
            {item.remarks && (
              <div className="basis-full text-gray-500 border-t pt-1 mt-1 break-words overflow-hidden">
                Opmerking: {item.remarks}
              </div>
            )}
          </div>
        )}
        {staffMembers.length > 0 && (
          <div className="flex gap-1.5 flex-wrap mt-2">
            {staffMembers.map((staffName, idx) => {
              const isMe = staffName === loggedInUserName;
              return (
                <span
                  key={idx}
                  className={`flex items-center gap-1 text-[10px] md:text-xs pl-0.5 pr-2 py-0.5 rounded-full border truncate max-w-[140px] ${
                    isMe
                      ? "font-semibold"
                      : "bg-gray-50 border-gray-200 text-gray-500"
                  }`}
                  style={
                    isMe
                      ? {
                          backgroundColor: C.GrassLight,
                          borderColor: `${C.Pine}40`,
                          color: C.Pine,
                        }
                      : undefined
                  }
                >
                  <ProfileAvatar
                    name={staffName}
                    staffList={appData.staffList || []}
                    size={16}
                    color={isMe ? C.Pine : "#6b7280"}
                  />
                  {staffName}
                </span>
              );
            })}
          </div>
        )}
        {item.linkedManuals && item.linkedManuals.length > 0 && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <div className="text-[9px] md:text-xs font-semibold text-gray-500 mb-1 uppercase">
              Scripts & Handleidingen:
            </div>
            <div className="flex gap-1 flex-wrap">
              {item.linkedManuals.map((manualId) => {
                const manual = appData.manuals?.find((m) => m.id === manualId);
                if (!manual) return null;
                return (
                  <button
                    key={manualId}
                    onClick={() => setView("handboek")}
                    className="text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded-full transition-colors flex items-center gap-1 truncate max-w-[140px]"
                    style={{ backgroundColor: C.SkyLight, color: C.Lagoon }}
                  >
                    <Book size={10} className="flex-shrink-0" />{" "}
                    <span className="truncate">{manual.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── EmptyState — consistent lege staat in de hele app ──────────────────────
// Lege staat — overal hetzelfde: icoon, één zin en (optioneel) een knop
// Seizoen van vandaag: kleurpaar (licht vlak + donker icoon) en een korte zin
const SEIZOEN_LEEG = {
  lente: { licht: "#FFB0D6", donker: "#700606", zin: "Lente in de lucht." },
  zomer: { licht: "#FFE2A1", donker: "#7A5226", zin: "Zomer. Zon. Zin." },
  herfst: { licht: "#FFCA55", donker: "#7A5226", zin: "Herfst. Knus. Gezellig." },
  winter: { licht: "#C4D4FF", donker: "#00192F", zin: "Winterwonder in de maak." },
};
const seizoenNu = (d = new Date()) => {
  const m = d.getMonth();
  return m >= 2 && m <= 4 ? "lente" : m >= 5 && m <= 7 ? "zomer" : m >= 8 && m <= 10 ? "herfst" : "winter";
};
const EmptyState = ({
  icon: Icon = Coffee,
  message = "Niets te tonen",
  sub,
  compact = false,
  actie, // { label, onClick }
}) => {
  const sz = SEIZOEN_LEEG[seizoenNu()];
  return (
  <div
    className={`flex flex-col items-center justify-center text-center ${
      compact ? "py-4 gap-1.5" : "py-8 gap-2"
    }`}
  >
    {/* Officiële Pebbles-vorm met het icoon erin */}
    <div className={`cp-op-licht relative flex items-center justify-center mb-1 ${compact ? "w-12 h-12" : "w-16 h-16"}`}>
      <CpVorm vorm="pebbles" kleur={sz.licht} zweef className="absolute inset-0 w-full h-full" />
      <Icon size={compact ? 20 : 26} style={{ color: sz.donker, position: "relative" }} />
    </div>
    <p className={`font-semibold ${compact ? "text-xs" : "text-sm"}`} style={{ color: C.Lagoon }}>{message}</p>
    {sub && <p className="text-xs text-gray-500">{sub}</p>}
    {!compact && <p className="text-xs font-semibold" style={{ color: sz.donker }}>{sz.zin}</p>}
    {actie && (
      <button
        onClick={actie.onClick}
        className="mt-2 min-h-[40px] px-4 rounded-xl text-sm font-semibold text-white"
        style={{ backgroundColor: C.Pine }}
      >
        {actie.label}
      </button>
    )}
  </div>
  );
};

// Label als pil (zoals op het promoscherm): licht vlak + donkere tekst uit één kleurpaar
const PIL_PAAR = {
  pine: [C.PineLight, C.Pine],
  honey: [C.HoneyLight, C.Bark],
  sky: [C.SkyLight, C.Lagoon],
  blossom: [C.BlossomLight, C.Sunset],
  oranje: ["#FFE0C7", "#8A3B00"],
  grijs: ["#F3F4F6", "#4B5563"],
};
const AFD_PIL = { entertainment: "honey", crea: "sky", springkussen: "blossom", hotspot: "oranje" };
const CpPil = ({ kleur = "pine", icon: I, children, className = "", title }) => {
  const [bg, fg] = PIL_PAAR[kleur] || PIL_PAAR.pine;
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap ${className}`}
      style={{ backgroundColor: bg, color: fg }}
    >
      {I && <I size={11} />}
      {children}
    </span>
  );
};

const Card = ({
  children,
  className = "",
  borderColor = "transparent",
  accentColor, // 🆕 Accent kleur voor linker/top rand
  accentPosition = "left", // 🆕 "left", "top", "bottom"
  style = {},
  onClick,
  glass = false,
  hover = true,
  elevated = false, // 🆕 Voor extra shadow
  animate = false, // 🆕 Fade-in animatie
}) => {
  const accentStyles = accentColor
    ? {
        borderLeftWidth: accentPosition === "left" ? "4px" : undefined,
        borderTopWidth: accentPosition === "top" ? "4px" : undefined,
        borderBottomWidth: accentPosition === "bottom" ? "4px" : undefined,
        borderColor: accentPosition === "left" ? accentColor : undefined,
        borderTopColor: accentPosition === "top" ? accentColor : undefined,
        borderBottomColor:
          accentPosition === "bottom" ? accentColor : undefined,
        borderStyle: "solid",
      }
    : {};

  return (
    <div
      onClick={onClick}
      className={`
        ${glass ? "glass-card" : "bg-white"} 
        rounded-2xl 
        ${elevated ? "shadow-medium" : "shadow-soft"}
        border border-gray-100
        ${hover && onClick ? "smooth-hover cursor-pointer" : ""}
        ${animate ? "animate-fade-in-up" : ""}
        ${className}
      `}
      style={{
        borderColor: borderColor !== "transparent" ? borderColor : undefined,
        ...accentStyles,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

const Progress = ({
  value = 0,
  max = 100,
  showLabel = false,
  color = "pine", // pine, honey, grass
  size = "md", // sm, md, lg
  className = "",
}) => {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));

  const colors = {
    pine: C.Pine,
    honey: C.Honey,
    grass: C.Pine,
  };

  const sizes = {
    sm: "h-1.5",
    md: "h-2.5",
    lg: "h-4",
  };

  return (
    <div className={className}>
      <div
        className={`bg-gray-100 rounded-full overflow-hidden ${sizes[size]}`}
      >
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{
            width: `${percentage}%`,
            background: colors[color],
          }}
        />
      </div>
      {showLabel && (
        <div className="flex justify-between text-xs text-gray-500 mt-1.5">
          <span>
            {value} / {max}
          </span>
          <span className="font-medium">{Math.round(percentage)}%</span>
        </div>
      )}
    </div>
  );
};

const Badge = ({
  children,
  variant = "pine", // pine, honey, sky, blossom, grass, sunset
  size = "md", // sm, md, lg
  icon: Icon,
  className = "",
}) => {
  const variants = {
    pine: { bg: C.PineLight, text: C.Pine },
    honey: { bg: `${C.Honey}30`, text: C.Bark },
    sky: { bg: `${C.Sky}40`, text: C.Lagoon },
    blossom: { bg: `${C.Blossom}30`, text: C.Sunset },
    grass: { bg: C.GrassLight, text: C.Pine },
    sunset: { bg: `${C.Sunset}20`, text: C.Sunset },
  };

  const sizes = {
    sm: "px-2 py-0.5 text-[11px]",
    md: "px-3 py-1 text-xs",
    lg: "px-4 py-1.5 text-sm",
  };

  const style = variants[variant];

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 font-medium rounded-full
        ${sizes[size]}
        ${className}
      `}
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {Icon && <Icon size={size === "sm" ? 10 : 12} />}
      {children}
    </span>
  );
};

const SkeletonCard = () => (
  <div className="bg-white rounded-xl shadow-soft border border-gray-100 p-4 animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
    <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
    <div className="h-3 bg-gray-200 rounded w-2/3"></div>
  </div>
);

const SkeletonWidget = () => (
  <div className="bg-white rounded-xl shadow-soft border border-gray-100 p-6 animate-pulse">
    <div className="flex justify-between items-center mb-4">
      <div className="h-5 bg-gray-200 rounded w-32"></div>
      <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
    </div>
    <div className="h-12 bg-gray-200 rounded w-20 mx-auto mb-3"></div>
    <div className="flex justify-center gap-4">
      <div className="h-4 bg-gray-200 rounded w-16"></div>
      <div className="h-4 bg-gray-200 rounded w-16"></div>
      <div className="h-4 bg-gray-200 rounded w-16"></div>
    </div>
  </div>
);
const SidebarItem = ({
  id,
  icon: Icon,
  label,
  onClick,
  active,
  theme, // eslint-disable-line no-unused-vars
  badge,
}) => (
  // Dagbord-stijl: rij van 44 px, icoon in een tegel, actief = Pine-vlak
  <button
    onClick={onClick}
    aria-current={active ? "page" : undefined}
    className="w-full flex items-center gap-2.5 px-1.5 min-h-[44px] rounded-xl transition-colors active:scale-[0.98] hover:bg-gray-50 text-left"
    style={
      active
        ? { backgroundColor: C.Pine, color: "white" }
        : { color: C.Lagoon }
    }
  >
    <span
      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
      style={{
        backgroundColor: active ? "rgba(255,255,255,0.16)" : C.PineLight,
        color: active ? "white" : C.Pine,
      }}
    >
      <Icon size={16} />
    </span>
    <span className="flex-1 min-w-0 truncate text-sm font-semibold">{label}</span>
    {badge > 0 && (
      <span
        className="min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-semibold rounded-full flex-shrink-0"
        style={{ backgroundColor: C.Blossom, color: "white" }}
      >
        {badge > 9 ? "9+" : badge}
      </span>
    )}
  </button>
);

// Twee gelijke statusvakjes per taak: ✓ gedaan en ✕ niet gedaan.
// Actief vakje is gevuld (gedaan = Pine, niet gedaan = Honey); nogmaals tikken zet terug.
const TaakStatusKnoppen = ({ done, nvt, onDone, onNiet, pop = false, tekst = "" }) => {
  const basis =
    "w-10 h-10 rounded-xl border-2 flex items-center justify-center transition-colors active:scale-95";
  return (
    <div className="flex items-center gap-1.5 flex-shrink-0" data-noswipe>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDone && onDone();
        }}
        aria-pressed={!!done}
        aria-label={done ? `Toch niet gedaan: ${tekst}` : `Gedaan: ${tekst}`}
        title={done ? "Gedaan — tik om terug te zetten" : "Gedaan"}
        className={`${basis} ${pop ? "db-pop" : ""}`}
        style={
          done
            ? { backgroundColor: C.Pine, borderColor: C.Pine, color: "white" }
            : { backgroundColor: "white", borderColor: "#D1D5DB", color: "#9CA3AF" }
        }
      >
        <Check size={18} strokeWidth={3} />
      </button>
      {onNiet && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNiet();
          }}
          aria-pressed={!!nvt}
          aria-label={nvt ? `Weer openzetten: ${tekst}` : `Niet gedaan: ${tekst}`}
          title={nvt ? "Niet gedaan — tik om terug te zetten" : "Niet gedaan (met reden)"}
          className={basis}
          style={
            nvt
              ? { backgroundColor: C.Honey, borderColor: C.Honey, color: C.Bark }
              : { backgroundColor: "white", borderColor: "#D1D5DB", color: "#9CA3AF" }
          }
        >
          <X size={18} strokeWidth={3} />
        </button>
      )}
    </div>
  );
};

// Kopje boven een groep in de zijbalk
const SidebarKop = ({ children }) => (
  <div className="px-2 pt-3 pb-1 text-[9px] font-semibold uppercase tracking-wide text-gray-500">
    {children}
  </div>
);

const AlertModal = ({ isOpen, message, onClose, type = "info" }) => {
  // Melding als toast: verdwijnt vanzelf (langere tekst blijft wat langer staan).
  const lengte = String(message || "").length;
  useEffect(() => {
    if (!isOpen) return undefined;
    const t = setTimeout(onClose, Math.min(9000, 3500 + lengte * 45));
    return () => clearTimeout(t);
  }, [isOpen, message]); // eslint-disable-line
  if (!isOpen) return null;

  const types = {
    info: { icon: Info, color: C.Pine, bgColor: C.PineLight },
    success: { icon: CheckCircle, color: C.Pine, bgColor: C.GrassLight },
    warning: { icon: AlertTriangle, color: C.Honey, bgColor: C.HoneyLight },
    error: { icon: AlertOctagon, color: C.Sunset, bgColor: `${C.Sunset}15` },
  };
  const { icon: Icon, color, bgColor } = types[type] || types.info;

  return (
    <div
      className="fixed left-1/2 z-[70] w-[calc(100%-24px)] max-w-md cp-toast"
      style={{ bottom: "calc(88px + env(safe-area-inset-bottom))" }}
      role="status"
      aria-live="polite"
    >
      <div
        className="flex items-start gap-3 bg-white rounded-2xl shadow-xl border border-gray-100 p-3 cursor-pointer"
        onClick={onClose}
      >
        <div className="w-9 h-9 shrink-0 rounded-xl flex items-center justify-center" style={{ backgroundColor: bgColor }}>
          <Icon size={18} style={{ color }} />
        </div>
        <p className="flex-1 min-w-0 text-sm text-gray-800 leading-snug py-1.5">{message}</p>
        <button onClick={onClose} className="shrink-0 p-1 text-gray-500" aria-label="Sluiten">
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

const NotificationPopup = ({ notification, onClose }) => {
  if (!notification) return null;
  const urgencyColor =
    notification.urgency === "critical"
      ? C.Sunset
      : notification.urgency === "warning"
      ? C.Honey
      : C.Pine;
  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-[#00192F]/45 sm:p-6 animate-in fade-in">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-xl max-w-md w-full overflow-hidden animate-in zoom-in duration-300">
        <div
          className="p-6 text-white flex justify-center"
          style={{ backgroundColor: urgencyColor }}
        >
          <Megaphone size={48} className="animate-bounce" />
        </div>
        <div className="p-4 text-center">
          <h2
            className="text-xl font-semibold mb-2"
            style={{ color: urgencyColor }}
          >
            {notification.title}
          </h2>
          <p className="text-gray-500 mb-8 text-lg leading-relaxed whitespace-pre-wrap">
            {notification.message}
          </p>
          <button
            onClick={onClose}
            className="w-full text-white font-semibold py-3 rounded-xl text-lg shadow-sm hover:opacity-90 active:scale-95 transition-opacity"
            style={{ backgroundColor: urgencyColor }}
          >
            Gelezen ✓
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── @MENTION HELPERS ────────────────────────────────────────────────────────

const MENTION_EXCLUDE_FROM_TEAM = ["Maik", "Jarno"];

const extractMentions = (text, staffList) => {
  if (!text) return [];
  const matches = text.match(/@([A-Za-zÀ-ÖØ-öø-ÿ\-]+)/g) || [];
  const names = matches.map((m) => m.slice(1));
  const result = [];
  for (const name of names) {
    if (name.toLowerCase() === "team") {
      // @team → iedereen behalve uitgesloten namen
      const teamMembers = (staffList || [])
        .map((s) => s.name)
        .filter((n) => !MENTION_EXCLUDE_FROM_TEAM.includes(n));
      result.push(...teamMembers);
    } else if (name.toLowerCase() === "maik") {
      // @Maik altijd herkennen (FM staat niet in staffList)
      result.push("Maik");
    } else if (
      (staffList || []).some((s) => s.name.toLowerCase() === name.toLowerCase())
    ) {
      result.push(name);
    }
  }
  return [...new Set(result)];
};

// Reusable textarea with @mention autocomplete
const MentionTextarea = ({
  value,
  onChange,
  staffList = [],
  loggedInUserName,
  className,
  placeholder,
  onKeyDown,
  autoFocus,
}) => {
  const textareaRef = useRef(null);
  const [query, setQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const showTeam = "team".startsWith(query.toLowerCase());
  // Maik (FM) toevoegen als taggable optie als hij niet zelf ingelogd is
  const maikVisible =
    loggedInUserName !== "Maik" && "maik".startsWith(query.toLowerCase());
  const filteredStaff = [
    ...(maikVisible ? [{ name: "Maik" }] : []),
    ...(staffList || []).filter(
      (s) =>
        s.name !== "Maik" &&
        s.name !== loggedInUserName &&
        s.name.toLowerCase().startsWith(query.toLowerCase())
    ),
  ].slice(0, 6);
  const hasOptions = showTeam || filteredStaff.length > 0;

  const handleChange = (e) => {
    onChange(e);
    const text = e.target.value;
    const cursor = e.target.selectionStart;
    const upToCursor = text.slice(0, cursor);
    const atMatch = upToCursor.match(/@([A-Za-zÀ-ÖØ-öø-ÿ\-]*)$/);
    if (atMatch) {
      setQuery(atMatch[1]);
      setShowDropdown(true);
      setSelectedIndex(0);
    } else {
      setShowDropdown(false);
    }
  };

  const insertMention = (name) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const cursor = textarea.selectionStart;
    const text = value;
    const upToCursor = text.slice(0, cursor);
    const atIndex = upToCursor.lastIndexOf("@");
    const newText = text.slice(0, atIndex) + `@${name} ` + text.slice(cursor);
    onChange({ target: { value: newText } });
    setShowDropdown(false);
    setTimeout(() => {
      textarea.focus();
      const newPos = atIndex + name.length + 2;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (showDropdown && hasOptions) {
      const total = (showTeam ? 1 : 0) + filteredStaff.length;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, total - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        if (showTeam && selectedIndex === 0) {
          insertMention("team");
        } else {
          const staffIndex = showTeam ? selectedIndex - 1 : selectedIndex;
          if (filteredStaff[staffIndex])
            insertMention(filteredStaff[staffIndex].name);
        }
        return;
      }
      if (e.key === "Escape") {
        setShowDropdown(false);
        return;
      }
    }
    if (onKeyDown) onKeyDown(e);
  };

  return (
    <div style={{ position: "relative" }}>
      <textarea
        ref={textareaRef}
        className={className}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        autoFocus={autoFocus}
      />
      {showDropdown && hasOptions && (
        <div
          style={{
            position: "absolute",
            bottom: "100%",
            left: 0,
            zIndex: 200,
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            boxShadow: "0 4px 24px rgba(0,0,0,0.13)",
            minWidth: 180,
            overflow: "hidden",
            marginBottom: 4,
          }}
        >
          <div
            style={{
              padding: "4px 8px 2px",
              fontSize: 10,
              color: "#9ca3af",
              fontWeight: 600,
              letterSpacing: 0.5,
            }}
          >
            COLLEGA TAGGEN
          </div>
          {/* @team shortcut */}
          {showTeam ? (
            <div
              onMouseDown={(e) => {
                e.preventDefault();
                insertMention("team");
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "7px 12px",
                cursor: "pointer",
                background: selectedIndex === 0 ? "#f0fdf4" : "white",
                fontSize: 13,
                color: "#111827",
                borderBottom: "1px solid #f3f4f6",
              }}
              onMouseEnter={() => setSelectedIndex(0)}
            >
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: C.Lagoon,
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                <Users2 size={13} color="white" />
              </span>
              <span style={{ fontWeight: 600 }}>team</span>
              <span
                style={{ marginLeft: "auto", fontSize: 10, color: "#9ca3af" }}
              >
                iedereen
              </span>
            </div>
          ) : null}
          {filteredStaff.map((s, i) => {
            const idx = showTeam ? i + 1 : i;
            return (
              <div
                key={s.name}
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertMention(s.name);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "7px 12px",
                  cursor: "pointer",
                  background: idx === selectedIndex ? "#f0fdf4" : "white",
                  fontWeight: idx === selectedIndex ? 600 : 400,
                  fontSize: 13,
                  color: "#111827",
                  transition: "background 0.1s",
                }}
                onMouseEnter={() => setSelectedIndex(idx)}
              >
                <span
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: C.Pine,
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {s.name[0]}
                </span>
                <span>{s.name}</span>
                <span
                  style={{ marginLeft: "auto", fontSize: 10, color: "#9ca3af" }}
                >
                  @
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Mention notification popup – shown when someone is tagged in a handover
const MentionNotificationPopup = ({ notification, onClose, onGoTo }) => {
  if (!notification) return null;
  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
    >
      <div
        className="w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in duration-300"
        style={{ background: "white", border: `2px solid ${C.Pine}` }}
      >
        {/* Header */}
        <div
          className="flex items-center gap-3 px-4 py-3"
          style={{ background: C.Pine }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <MessageSquare size={22} color="white" />
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold text-sm">{notification.source === "orders" ? "Reactie op bestelling" : "Je bent getagd!"}</p>
            <p className="text-white/80 text-xs">
              {notification.author} {notification.source === "orders" ? "heeft gereageerd" : "heeft je vermeld"}
            </p>
          </div>
          <button aria-label="Sluiten"
            onClick={onClose}
            className="text-white/70 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        {/* Body */}
        <div className="px-4 py-3">
          <div className="text-xs font-semibold mb-1" style={{ color: C.Pine }}>
            {notification.sourceLabel}{notification.source === "orders" ? "" : ` · ${notification.dateKey}`}
          </div>
          <p className="text-sm text-gray-800 leading-relaxed line-clamp-3 whitespace-pre-wrap">
            {highlightMentions(notification.text, notification.mentionedUser)}
          </p>
        </div>
        {/* Actions */}
        <div className="flex gap-2 px-4 pb-4">
          <button
            onClick={() => {
              if (notification.source !== "dagbord" && notification.source !== "orders")
              window.dispatchEvent(
                new CustomEvent("handover-goto", {
                  detail: {
                    tab: notification.source,
                    dateKey: notification.dateKey,
                  },
                })
              );
              onGoTo(notification);
              onClose();
            }}
            className="flex-1 text-sm font-semibold py-2 rounded-xl text-white flex items-center justify-center gap-1"
            style={{ background: C.Pine }}
          >
            <ArrowRight size={14} />{" "}
            {notification.source === "orders"
              ? "Naar bestellingen"
              : notification.source === "dagbord"
              ? notification.tab === "actiepunten"
                ? "Naar actiepunt"
                : notification.tab === "planning"
                ? "Naar dagbord"
                : "Naar overdracht"
              : "Ga naar overdracht"}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
};

// Open dienst – melding voor een medewerker aan wie de FM een dienst aanbiedt.
const OpenDienstPopup = ({ notification, onPak, onNee }) => {
  if (!notification) return null;
  const d = new Date(notification.date + "T12:00:00");
  const datum = d.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" });
  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center p-4"
      style={{ background: "rgba(0,25,47,0.6)" }}
    >
      <div className="w-full max-w-sm rounded-2xl overflow-hidden bg-white" style={{ border: `2px solid ${C.Pine}` }}>
        <div className="px-4 py-3" style={{ background: C.Pine }}>
          <p className="text-white font-semibold text-sm">Open dienst</p>
          <p className="text-white/80 text-xs">Wie het eerst reageert, staat erop.</p>
        </div>
        <div className="px-4 py-4">
          <p className="text-base font-semibold capitalize" style={{ color: C.Pine }}>{datum}</p>
          <p className="text-sm text-gray-800 mt-0.5">
            {notification.title} · {notification.start}–{notification.end}
          </p>
        </div>
        <div className="flex gap-2 px-4 pb-4">
          <button
            onClick={onPak}
            className="flex-1 text-sm font-semibold py-2.5 rounded-xl text-white"
            style={{ background: C.Pine }}
          >
            Ik pak hem
          </button>
          <button
            onClick={onNee}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold"
            style={{ background: C.Bg, color: C.TextMuted }}
          >
            Nee, dank je
          </button>
        </div>
      </div>
    </div>
  );
};

// Verlofaanvraag-melding – shown to de FM zodra een medewerker een nieuwe
// "niet beschikbaar"-dag (verlof) indient. Toont meteen hoeveel mensen er
// die dag al vrij/beschikbaar zijn, zodat de FM in 1 oogopslag kan
// goedkeuren of afwijzen zonder eerst naar Beschikbaarheid te hoeven gaan.
const VerlofAanvraagPopup = ({
  notification,
  appData,
  onClose,
  onApprove,
  onReject,
}) => {
  if (!notification) return null;
  const staffList = (appData.staffList || []).filter(
    (s) => !s.inactive && !teamVerborgen(s) && s.name !== "Jarno"
  );
  const allAvail = appData.staffAvailability || {};
  const contractInfo = appData.staffContractInfo || {};

  const formatDay = (date) => {
    const d = new Date(date + "T12:00:00");
    return `${
      dutchDaysShort[d.getDay() === 0 ? 6 : d.getDay() - 1]
    } ${d.getDate()} ${dutchMonths[d.getMonth()]}`;
  };

  const dateStats = (date) => {
    let offApproved = 0,
      offPending = 0,
      availApproved = 0,
      availPending = 0;
    // Hulpkrachten op hun niet-vaste dagen tellen niet mee
    const telt = telendeStaf(staffList, contractInfo, date, allAvail);
    telt.forEach((s) => {
      const entry = allAvail[s.name]?.[date];
      if (!entry) return;
      const isPending = entry.status === "pending";
      if (entry.available === false) {
        if (isPending) offPending++;
        else offApproved++;
      } else if (entry.available === true) {
        if (isPending) availPending++;
        else availApproved++;
      }
    });
    return {
      offTotal: offApproved + offPending,
      offApproved,
      offPending,
      availApproved,
      availPending,
      total: telt.length,
    };
  };

  const dates = notification.dates || [];
  const stillPending = (date) =>
    allAvail[notification.employeeName]?.[date]?.status === "pending";

  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
    >
      <div
        className="w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in duration-300"
        style={{ background: "white", border: `2px solid ${C.Honey}` }}
      >
        {/* Header */}
        <div
          className="flex items-center gap-3 px-4 py-3"
          style={{ background: C.Honey }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CalendarDays size={22} color="white" />
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold text-sm">
              Nieuwe verlofaanvraag
            </p>
            <p className="text-white/80 text-xs">
              {notification.employeeName} heeft vrij gevraagd
            </p>
          </div>
          <button aria-label="Sluiten"
            onClick={onClose}
            className="text-white/70 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body: per dag stats + goedkeuren/afwijzen */}
        <div
          className="divide-y max-h-[60vh] overflow-y-auto"
          style={{ borderColor: C.BorderLight }}
        >
          {dates.map((date) => {
            const stats = dateStats(date);
            const pending = stillPending(date);
            return (
              <div key={date} className="px-4 py-3">
                <div className="flex items-center justify-between mb-1.5">
                  <p
                    className="text-sm font-semibold"
                    style={{ color: C.TextPrimary }}
                  >
                    {formatDay(date)}
                  </p>
                  {notification.note && (
                    <span
                      className="text-xs opacity-70"
                      style={{ color: C.TextMuted }}
                    >
                      {notification.note}
                    </span>
                  )}
                </div>
                <div className="flex items-center flex-wrap gap-1.5 mb-2">
                  <span
                    className="text-xs font-semibold px-1.5 py-0.5 rounded-full"
                    style={{
                      backgroundColor: C.Sunset + "20",
                      color: C.Sunset,
                    }}
                  >
                    {stats.offTotal}/{stats.total} vrij gevraagd
                    {stats.offPending > 0 &&
                      ` (${stats.offApproved} goedgekeurd, ${stats.offPending} wacht)`}
                  </span>
                  {stats.availApproved + stats.availPending > 0 && (
                    <span
                      className="text-xs font-semibold px-1.5 py-0.5 rounded-full"
                      style={{
                        backgroundColor: C.Grass + "25",
                        color: C.Pine,
                      }}
                    >
                      {stats.availApproved + stats.availPending}/{stats.total}{" "}
                      beschikbaar
                    </span>
                  )}
                </div>
                {pending ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => onReject(notification.employeeName, date)}
                      className="flex-1 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1"
                      style={{
                        backgroundColor: C.BlossomLight,
                        color: C.Sunset,
                      }}
                    >
                      <X size={12} /> Afwijzen
                    </button>
                    <button
                      onClick={() => onApprove(notification.employeeName, date)}
                      className="flex-1 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1"
                      style={{
                        backgroundColor: C.Grass + "25",
                        color: C.Pine,
                      }}
                    >
                      <Check size={12} /> Goedkeuren
                    </button>
                  </div>
                ) : (
                  <p
                    className="text-xs flex items-center gap-1"
                    style={{ color: C.Pine }}
                  >
                    <CheckCircle size={12} /> Al afgehandeld
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="flex gap-2 px-4 py-3">
          <button
            onClick={onClose}
            className="flex-1 py-2 rounded-xl text-sm font-semibold text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
};

// Highlight @mentions in text for display
const highlightMentions = (text, highlightName) => {
  if (!text) return text;
  const parts = text.split(/(@[A-Za-zÀ-ÖØ-öø-ÿ\-]+)/g);
  return parts.map((part, i) => {
    if (part.startsWith("@")) {
      const name = part.slice(1);
      const isTeam = name.toLowerCase() === "team";
      const isHighlighted = name === highlightName;
      return (
        <span
          key={i}
          style={{
            background: isTeam
              ? `${C.Lagoon}22`
              : isHighlighted
              ? `${C.Pine}22`
              : "#f3f4f6",
            color: isTeam ? C.Lagoon : C.Pine,
            fontWeight: 700,
            borderRadius: 4,
            padding: "0 3px",
          }}
        >
          {part}
        </span>
      );
    }
    return part;
  });
};

const ConfirmModal = ({ isOpen, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-[#00192F]/45 sm:p-4">
      <div
        className="w-full sm:max-w-sm p-6 text-center animate-fade-in-up bg-white rounded-t-3xl sm:rounded-3xl shadow-xl"
        style={{ paddingBottom: "max(24px, env(safe-area-inset-bottom))" }}
      >
        {/* 🆕 Waarschuwings icoon */}
        <div
          className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center"
          style={{ backgroundColor: `${C.Sunset}15` }}
        >
          <AlertOctagon size={32} style={{ color: C.Sunset }} />
        </div>

        <p className="mb-6 text-sm text-gray-800 leading-relaxed">
          {message}
        </p>

        <div className="flex gap-3">
          <button onClick={onCancel} className="cp-btn-secondary flex-1">
            Annuleer
          </button>
          <button
            onClick={() => {
              if (onConfirm) onConfirm();
              onCancel();
            }}
            className="flex-1 h-11 rounded-xl text-sm font-semibold text-white transition-transform active:scale-[0.98]"
            style={{ backgroundColor: C.Sunset }}
          >
            Bevestig
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// 🔔 App-brede dialogs & toasts (CP-stijl) — vervangt native
// alert() / confirm() / prompt(). Aanroepbaar vanuit elke
// component via cpToast() / cpConfirm() / cpPrompt().
// ============================================================
const _cpDialogStore = {
  listeners: new Set(),
  state: { confirm: null, prompt: null, toasts: [] },
  emit() {
    this.listeners.forEach((l) => l(this.state));
  },
  set(patch) {
    this.state = { ...this.state, ...patch };
    this.emit();
  },
  subscribe(l) {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  },
};

let _cpToastId = 0;
const cpToast = (message, type = "info", duration = 3400) => {
  const id = ++_cpToastId;
  _cpDialogStore.set({
    toasts: [..._cpDialogStore.state.toasts, { id, message, type }],
  });
  setTimeout(() => {
    _cpDialogStore.set({
      toasts: _cpDialogStore.state.toasts.filter((t) => t.id !== id),
    });
  }, duration);
  return id;
};

const cpConfirm = (opts) =>
  new Promise((resolve) => {
    const o = typeof opts === "string" ? { message: opts } : opts || {};
    // Tone of Voice: knoppen zeggen wat er gebeurt ("Ja, weg ermee" / "Toch niet").
    // Rood alleen bij iets dat echt weg of overschreven wordt.
    const tekst = String(o.message || "");
    const weg = /verwijder|weghalen|wissen|\bwis\b|reset|overschrijf|overschreven/i.test(tekst);
    _cpDialogStore.set({
      confirm: {
        message: o.message || "Weet je het zeker?",
        confirmLabel: o.confirmLabel || (weg ? "Ja, weg ermee" : "Ja, doen"),
        cancelLabel: o.cancelLabel || "Toch niet",
        danger: o.danger !== undefined ? o.danger : weg,
        resolve: (val) => {
          _cpDialogStore.set({ confirm: null });
          resolve(val);
        },
      },
    });
  });

const cpPrompt = (opts) =>
  new Promise((resolve) => {
    const o = typeof opts === "string" ? { message: opts } : opts || {};
    _cpDialogStore.set({
      prompt: {
        message: o.message || "",
        defaultValue: o.defaultValue || "",
        placeholder: o.placeholder || "",
        confirmLabel: o.confirmLabel || "Oké",
        cancelLabel: o.cancelLabel || "Annuleer",
        resolve: (val) => {
          _cpDialogStore.set({ prompt: null });
          resolve(val);
        },
      },
    });
  });

// Dagbord-stijl: donkere melding onderin; icoonkleur leesbaar op Lagoon
const CP_TOAST_STYLE = {
  success: { color: C.Grass, bg: C.Lagoon, icon: CheckCircle },
  error: { color: C.Blossom, bg: C.Lagoon, icon: AlertOctagon },
  warning: { color: C.Honey, bg: C.Lagoon, icon: AlertTriangle },
  info: { color: C.Sky, bg: C.Lagoon, icon: Info },
};

const CpDialogHost = () => {
  const [state, setState] = React.useState(_cpDialogStore.state);
  React.useEffect(() => _cpDialogStore.subscribe(setState), []);
  const { confirm, prompt, toasts } = state;
  const [promptVal, setPromptVal] = React.useState("");
  React.useEffect(() => {
    if (prompt) setPromptVal(prompt.defaultValue || "");
  }, [prompt]);

  return (
    <>
      {/* Toast-stack */}
      <div role="status" aria-live="polite" className="fixed bottom-[176px] lg:bottom-8 left-4 right-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 z-[120] flex flex-col items-center gap-2 pointer-events-none">
        {toasts.map((t) => {
          const s = CP_TOAST_STYLE[t.type] || CP_TOAST_STYLE.info;
          const Icon = s.icon;
          return (
            <div
              key={t.id}
              className="pointer-events-auto w-full sm:w-auto sm:max-w-md flex items-center gap-2.5 pl-3 pr-4 py-2.5 min-h-[48px] rounded-2xl shadow-lg animate-fade-in-up"
              style={{ backgroundColor: s.bg }}
            >
              <span
                className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
              >
                <Icon size={16} style={{ color: s.color }} />
              </span>
              <span className="text-sm text-white leading-snug whitespace-pre-line">
                {t.message}
              </span>
            </div>
          );
        })}
      </div>

      {/* Bevestig-dialoog */}
      {confirm && (
        <div className="fixed inset-0 z-[130] flex items-end sm:items-center justify-center bg-[#00192F]/45 sm:p-4">
          <div
            className="w-full sm:max-w-sm p-6 text-center animate-fade-in-up bg-white rounded-t-3xl sm:rounded-3xl shadow-xl"
            style={{ paddingBottom: "max(24px, env(safe-area-inset-bottom))" }}
          >
            <div
              className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center"
              style={{
                backgroundColor: confirm.danger ? `${C.Sunset}15` : C.PineLight,
              }}
            >
              {confirm.danger ? (
                <AlertOctagon size={32} style={{ color: C.Sunset }} />
              ) : (
                <IcTaken size={32} color={C.Pine} />
              )}
            </div>
            <p className="mb-6 text-sm text-gray-800 leading-relaxed whitespace-pre-line">
              {confirm.message}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => confirm.resolve(false)}
                className="cp-btn-secondary flex-1"
              >
                {confirm.cancelLabel}
              </button>
              <button
                onClick={() => confirm.resolve(true)}
                className="flex-1 h-11 rounded-xl text-sm font-semibold text-white transition-transform active:scale-[0.98]"
                style={{
                  backgroundColor: confirm.danger ? C.Sunset : C.Pine,
                }}
              >
                {confirm.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prompt-dialoog */}
      {prompt && (
        <div className="fixed inset-0 z-[130] flex items-end sm:items-center justify-center bg-[#00192F]/45 sm:p-4">
          <div className="w-full sm:max-w-sm p-6 animate-fade-in-up bg-white rounded-t-3xl sm:rounded-3xl shadow-xl" style={{ paddingBottom: "max(24px, env(safe-area-inset-bottom))" }}>
            {prompt.message && (
              <p className="mb-3 text-sm text-gray-800 leading-relaxed">
                {prompt.message}
              </p>
            )}
            <input
              autoFocus
              value={promptVal}
              placeholder={prompt.placeholder}
              onChange={(e) => setPromptVal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") prompt.resolve(promptVal);
                if (e.key === "Escape") prompt.resolve(null);
              }}
              className="w-full border rounded-xl px-3 h-11 text-sm mb-5 focus:outline-none"
              style={{ borderColor: "#E5E7EB" }}
            />
            <div className="flex gap-3">
              <button
                onClick={() => prompt.resolve(null)}
                className="cp-btn-secondary flex-1"
              >
                {prompt.cancelLabel}
              </button>
              <button
                onClick={() => prompt.resolve(promptVal)}
                className="flex-1 h-11 rounded-xl text-sm font-semibold text-white transition-transform active:scale-[0.98]"
                style={{ backgroundColor: C.Pine }}
              >
                {prompt.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// 🆕 Herbruikbare select in CP-stijl. Drop-in voor <select>: geeft alle
// props (value, onChange, disabled, style, ...) en <option>-children door,
// en voegt enkel de .cp-select styling toe (appearance + CP-chevron).
const StyledSelect = ({ className = "", children, ...props }) => (
  <select {...props} className={`cp-select ${className}`}>
    {children}
  </select>
);

const Confetti = () => {
  const [active, setActive] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setActive(false), 5000);
    return () => clearTimeout(timer);
  }, []);
  if (!active) return null;
  return (
    <div className="fixed inset-0 pointer-events-none z-[100] overflow-hidden">
      {[...Array(50)].map((_, i) => (
        <div
          key={i}
          className="absolute top-0 w-3 h-3 rounded-full animate-fall"
          style={{
            left: `${Math.random() * 100}%`,
            backgroundColor: [C.Pine, C.Grass, C.Sunset, C.Sky, C.Blossom][
              Math.floor(Math.random() * 5)
            ],
            animationDuration: `${Math.random() * 3 + 2}s`,
            animationDelay: `${Math.random() * 2}s`,
          }}
        />
      ))}
      <style>
        {`
  @keyframes fall { 
    0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; } 
    100% { transform: translateY(110vh) rotate(360deg); opacity: 0; } 
  } 
  .animate-fall { 
    animation-name: fall; 
    animation-timing-function: linear; 
    animation-iteration-count: infinite; 
  }
  @keyframes scale-in {
    from {
      transform: scale(0.9);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
  .animate-scale-in {
    animation: scale-in 0.2s ease-out;
  }
  
  /* ✓ Checkbox check-in animation */
  @keyframes checkIn {
    0% {
      transform: scale(0) rotate(-45deg);
      opacity: 0;
    }
    50% {
      transform: scale(1.2) rotate(5deg);
      opacity: 1;
    }
    100% {
      transform: scale(1) rotate(0deg);
      opacity: 1;
    }
  }
  
  /* Custom scrollbar - invisible by default, subtle on hover */
  .custom-scrollbar::-webkit-scrollbar {
    width: 0px;
    height: 0px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: transparent;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: transparent;
  }

  /* Sidebar scrollbar - invisible until hover */
  .scrollbar-theme::-webkit-scrollbar {
    width: 6px;
  }
  
  .scrollbar-theme::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .scrollbar-theme::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 3px;
  }
  
  .scrollbar-theme:hover::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
  }
  
  .scrollbar-theme::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.25);
  }

  /* Line clamp utilities */
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  /* Smooth page transition animations */
  @keyframes fadeInSlideUp {
    from {
      opacity: 0;
      transform: translateY(30px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes fadeOut {
    from {
      opacity: 1;
      transform: scale(1);
    }
    to {
      opacity: 0;
      transform: scale(0.98);
    }
  }

  .animate-in {
    animation: fadeInSlideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .animate-out {
    animation: fadeOut 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }

  /* Enhanced bounce for Zap icon */
  @keyframes bounceSubtle {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-8px);
    }
  }

  .animate-bounce {
    animation: bounceSubtle 2s ease-in-out infinite;
  }

  /* Ken Burns - rustige zoom/pan (promoscherm gebruikt .promo-kb in promo.js) */
  @keyframes kenBurns {
    from { transform: scale(1) translate(0, 0); }
    to { transform: scale(1.08) translate(-2%, -1.5%); }
  }

  .promo-photo-animate {
    animation: kenBurns 9s ease-in-out infinite alternate;
    transform-origin: center center;
  }

  /* 📅 DATE PICKER STYLING MET THEME KLEUREN */
  input[type="date"] {
    color-scheme: light;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  
  input[type="date"]:focus {
    outline: none;
    border-color: #00192F;
    box-shadow: 0 0 0 3px rgba(0, 25, 47, 0.1);
  }
  
  input[type="date"]::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.2s;
    filter: brightness(0) saturate(100%) invert(7%) sepia(30%) saturate(6547%) hue-rotate(186deg) brightness(96%) contrast(108%);
  }
  
  input[type="date"]::-webkit-calendar-picker-indicator:hover {
    opacity: 1;
  }

  /* ══════════════════════════════════════
     LOGIN SCREEN ANIMATIONS
  ══════════════════════════════════════ */

  @keyframes loginLogoIn {
    0% { opacity: 0; transform: translateY(-28px) scale(0.92); }
    60% { opacity: 1; transform: translateY(4px) scale(1.02); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }

  @keyframes loginSubtitleIn {
    0% { opacity: 0; transform: translateY(-10px); }
    100% { opacity: 0.7; transform: translateY(0); }
  }

  @keyframes loginCardPop {
    0% { opacity: 0; transform: translateY(24px) scale(0.85); }
    65% { opacity: 1; transform: translateY(-4px) scale(1.03); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }

  @keyframes loginPinSlideUp {
    0% { opacity: 0; transform: translateY(40px) scale(0.96); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }

  @keyframes loginAvatarBig {
    0% { opacity: 0; transform: scale(0.7) rotate(-6deg); }
    60% { opacity: 1; transform: scale(1.06) rotate(2deg); }
    100% { opacity: 1; transform: scale(1) rotate(0deg); }
  }

  @keyframes loginNumpadIn {
    0% { opacity: 0; transform: translateY(20px); }
    100% { opacity: 1; transform: translateY(0); }
  }

  @keyframes loginBlobFloat {
    0%, 100% { transform: translateY(0) scale(1); }
    50% { transform: translateY(-18px) scale(1.04); }
  }

  @keyframes loginBlobFloat2 {
    0%, 100% { transform: translateY(0) scale(1) rotate(0deg); }
    50% { transform: translateY(14px) scale(0.96) rotate(8deg); }
  }

  .login-logo-animate {
    animation: loginLogoIn 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  }

  .login-subtitle-animate {
    animation: loginSubtitleIn 0.5s ease-out 0.35s both;
  }

  .login-card-animate {
    animation: loginCardPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  }

  .login-pin-animate {
    animation: loginPinSlideUp 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  }

  .login-avatar-big-animate {
    animation: loginAvatarBig 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.05s both;
  }

  .login-numpad-animate {
    animation: loginNumpadIn 0.4s ease-out 0.15s both;
  }

  .login-blob-1 {
    animation: loginBlobFloat 7s ease-in-out infinite;
  }

  .login-blob-2 {
    animation: loginBlobFloat2 9s ease-in-out infinite;
  }

  .login-blob-3 {
    animation: loginBlobFloat 11s ease-in-out 2s infinite;
  }
`}
      </style>
    </div>
  );
};

const AdminContainer = ({
  title,
  children,
  backAction,
  currentDay,
  onPrev,
  onNext,
  onCalendar,
  hideDateControls,
  theme,
}) => {
  const t = theme || THEMES.general;
  return (
    <div className="w-full max-w-7xl mx-auto flex flex-col min-h-0 h-full">
      <ViewHeader
        title={title}
        icon={Settings}
        theme={t}
        onBack={backAction}
        backLabel="FM Beheer"
      >
        {!hideDateControls && currentDay && (
          <DateControls
            currentDay={currentDay}
            onPrev={onPrev}
            onNext={onNext}
            onCalendar={onCalendar}
            theme={t}
          />
        )}
      </ViewHeader>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 overflow-y-auto flex-1 h-full">
        {children}
      </div>
    </div>
  );
};


// 🎚️ Drukte-slider — vaste kleuren binnen de CP-huisstijl (Sky → Lagoon),
// vloeiend interpoleerbaar op elk punt. Module-level gedefinieerd (dus niet
// opnieuw aangemaakt bij elke render van het formulier) — anders verliest
// de slider zijn sleeppositie bij elke wijziging en lijkt hij vast te zitten
// op 5 vaste punten.
const hexToRgb = (hex) => {
  const v = hex.replace("#", "");
  return [
    parseInt(v.slice(0, 2), 16),
    parseInt(v.slice(2, 4), 16),
    parseInt(v.slice(4, 6), 16),
  ];
};
// Kleurenpaar (licht → donker) per overdracht-onderdeel, zodat de slider
// altijd meekleurt met de tint van dat tabblad.
const CROWD_TYPE_COLORS = {
  ent: [C.Honey, C.Bark],
  crea: [C.Sky, C.Lagoon],
  springkussen: ["#FFC2D9", C.Sunset],
  hotspot: ["#FFC896", "#FF8A3D"],
  fm: [C.Grass, C.Pine],
};
const interpolateCrowdColor = (pos, type) => {
  const [lightHex, darkHex] = CROWD_TYPE_COLORS[type] || [C.Sky, C.Lagoon];
  const [r1, g1, b1] = hexToRgb(lightHex);
  const [r2, g2, b2] = hexToRgb(darkHex);
  const frac = Math.min(1, Math.max(0, (pos - 1) / 4));
  const r = Math.round(r1 + (r2 - r1) * frac);
  const g = Math.round(g1 + (g2 - g1) * frac);
  const b = Math.round(b1 + (b2 - b1) * frac);
  return `rgb(${r}, ${g}, ${b})`;
};

const CrowdBar = ({ value, onChange, compact = false, type = "crea" }) => {
  // Losse, vloeiende sleeppositie die alleen bij binnenkomende data van
  // buitenaf gelijkgezet wordt aan de opgeslagen waarde — NIET bij elke
  // onChange, anders "springt" de duim terug naar het dichtstbijzijnde
  // vaste punt zodra je sleept.
  const [pos, setPos] = useState(value || 3);
  const lastEmitted = useRef(value || null);
  useEffect(() => {
    if (value && value !== lastEmitted.current) {
      setPos(value);
      lastEmitted.current = value;
    }
  }, [value]);
  const liveColor = interpolateCrowdColor(pos, type);
  const [lightHex, darkHex] = CROWD_TYPE_COLORS[type] || [C.Sky, C.Lagoon];
  return (
    <div className={compact ? "" : "bg-gray-50 p-3 rounded-xl border"}>
      <input
        type="range"
        min={1}
        max={5}
        step={0.01}
        value={pos}
        onChange={(e) => {
          const raw = Number(e.target.value);
          setPos(raw);
          const rounded = Math.round(raw);
          lastEmitted.current = rounded;
          onChange(rounded);
        }}
        className={`w-full ${
          compact ? "h-1.5" : "h-2"
        } rounded-full appearance-none cursor-pointer`}
        style={{
          accentColor: liveColor,
          background: `linear-gradient(to right, ${lightHex}, ${darkHex})`,
        }}
      />
      <div className="flex justify-between text-xs text-gray-500 mt-0.5 px-0.5">
        <span>Rustig</span>
        <span>Druk</span>
      </div>
    </div>
  );
};


// --- PHOTOGALLERY COMPONENT ---
const PhotoGallery = ({
  photos,
  entryId,
  onDelete,
  userRole,
  loggedInUserName,
  author,
}) => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  if (!photos || photos.length === 0) return null;

  const canDelete = userRole === "fm" || author === loggedInUserName;

  const handleDeletePhoto = async (photo, index) => {
    if (!(await cpConfirm("Deze foto weghalen?")))
      return;

    try {
      await deleteHandoverPhoto(photo.storagePath);
      onDelete(entryId, index);
    } catch (error) {
      console.error("❌ Foto verwijderen mislukt:", error);
      cpToast("Foto weghalen lukt even niet.");
    }
  };

  return (
    <div className="mt-3">
      <div className="flex items-center gap-1 mb-2">
        <Images size={14} className="text-gray-500" />
        <span className="text-xs font-semibold text-gray-500">
          Foto's ({photos.length})
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {photos.map((photo, index) => (
          <div key={photo.id} className="relative group">
            <button
              onClick={() => setLightboxIndex(index)}
              className="aspect-square rounded-lg overflow-hidden bg-gray-100 border hover:ring-2 hover:ring-[#045E51] transition-all w-full"
            >
              <img
                src={photo.url}
                alt={photo.caption || `Foto ${index + 1}`}
                loading="lazy"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </button>

            {photo.caption && (
              <p className="text-xs text-gray-500 mt-0.5 truncate">
                {photo.caption}
              </p>
            )}

            {canDelete && (
              <button aria-label="Verwijderen"
                onClick={() => handleDeletePhoto(photo, index)}
                className="absolute top-1 right-1 bg-[#700606] text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#700606]"
              >
                <Trash2 size={10} />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Lightbox modal */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-[#00192F]/45 flex items-end sm:items-center justify-center sm:p-4"
          onClick={() => setLightboxIndex(null)}
        >
          <div
            className="relative max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button aria-label="Sluiten"
              onClick={() => setLightboxIndex(null)}
              className="absolute top-0 right-0 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full m-2 z-10"
            >
              <CloseIcon size={24} />
            </button>

            <img
              src={photos[lightboxIndex].url}
              alt={photos[lightboxIndex].caption || ""}
              className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
            />

            {photos[lightboxIndex].caption && (
              <div className="bg-black/50 text-white p-3 rounded-b-lg mt-2">
                <p className="text-sm">{photos[lightboxIndex].caption}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Geupload door {photos[lightboxIndex].uploadedBy} om{" "}
                  {new Date(
                    photos[lightboxIndex].uploadedAt
                  ).toLocaleTimeString("nl-NL", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            )}

            {photos.length > 1 && (
              <>
                <button aria-label="Vorige"
                  onClick={() =>
                    setLightboxIndex(Math.max(0, lightboxIndex - 1))
                  }
                  disabled={lightboxIndex === 0}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full disabled:opacity-30"
                >
                  <ChevronLeft size={24} />
                </button>
                <button aria-label="Volgende"
                  onClick={() =>
                    setLightboxIndex(
                      Math.min(photos.length - 1, lightboxIndex + 1)
                    )
                  }
                  disabled={lightboxIndex === photos.length - 1}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full disabled:opacity-30"
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const HandoverSection = ({
  title,
  icon: Icon,
  colorBg,
  colorText,
  logs,
  onUpdate,
  type,
  userRole,
  loggedInUserName,
  staffList,
  fmOnly = false,
  dayActivities = [],
}) => {
  const [editingRemarkId, setEditingRemarkId] = useState(null);
  const [remarkDraft, setRemarkDraft] = useState("");
  const [editingEntry, setEditingEntry] = useState(null);

  const handleDelete = (id) => {
    const updatedLogs = logs.filter((msg) => msg.id !== id);
    onUpdate(updatedLogs);
  };

  const handleEditEntry = (entry) => setEditingEntry(entry);

  const handleUpdateEntry = (updatedEntry) => {
    const updatedLogs = logs.map((l) =>
      l.id === updatedEntry.id ? updatedEntry : l
    );
    onUpdate(updatedLogs);
    setEditingEntry(null);
  };

  const startEditingRemark = (entry) => {
    setEditingRemarkId(entry.id);
    setRemarkDraft(entry.fmRemark || "");
  };

  const saveRemark = (entryId) => {
    const updatedLogs = logs.map((l) =>
      l.id === entryId ? { ...l, fmRemark: remarkDraft } : l
    );
    onUpdate(updatedLogs);
    setEditingRemarkId(null);
  };

  // Markeer alle entries in dit tabblad in één keer als gelezen
  const handleReadAll = () => {
    const needsUpdate = logs.some(
      (l) => !(l.readBy || []).includes(loggedInUserName)
    );
    if (!needsUpdate) return;
    const updatedLogs = logs.map((l) => {
      const reads = l.readBy || [];
      if (reads.includes(loggedInUserName)) return l;
      return { ...l, readBy: [...reads, loggedInUserName] };
    });
    onUpdate(updatedLogs);
  };

  // Welke logs tellen voor de gelezen-status:
  // voor FM: alle logs (ook eigen); voor staff: alleen logs van anderen
  const logsForReadCheck =
    userRole === "fm"
      ? logs
      : logs.filter((l) => l.author !== loggedInUserName);
  const isFullySectionRead =
    logsForReadCheck.length === 0 ||
    logsForReadCheck.every((l) => (l.readBy || []).includes(loggedInUserName));
  const allOtherLogs = logsForReadCheck;
  const allStaffNames = [
    ...new Set([
      ...(staffList || []).map((s) => s.name),
      loggedInUserName,
      "Maik",
    ]),
  ].filter(Boolean);
  const sectionReadNames = allStaffNames.filter(
    (n) =>
      allOtherLogs.length > 0 &&
      allOtherLogs.every((l) => (l.readBy || []).includes(n))
  );
  const sectionNotReadNames = allStaffNames.filter((n) =>
    allOtherLogs.some((l) => !(l.readBy || []).includes(n))
  );

  const getCrowdLabel = (lvl) =>
    ["Erg Rustig", "Rustig", "Normaal", "Druk", "Erg Druk"][lvl - 1] ||
    "Onbekend";
  const getCrowdColor = (lvl) =>
    [
      "bg-[#B1EDA6] text-[#045E51]",
      "bg-[#B1EDA6] text-[#045E51]",
      "bg-[#FFCA55]/40 text-[#7A5226]",
      "bg-[#FFCA55]/40 text-[#7A5226]",
      "bg-[#FF699B]/30 text-[#700606]",
    ][lvl - 1] || "bg-gray-100";

  const renderFMSection = (entry, msgId) => (
    <>
      {entry.fmRemark && (
        <div className="mt-3 p-2 bg-[#FFE2A1] border border-[#FFCA55] rounded text-xs text-[#7A5226] relative">
          <span className="font-semibold block mb-1 flex items-center gap-1">
            <MessageSquare size={10} /> Opmerking FM:
          </span>
          {entry.fmRemark}
        </div>
      )}
      {userRole === "fm" && (
        <div className="mt-2">
          {editingRemarkId === msgId ? (
            <div className="flex gap-2 items-start mt-2 animate-in fade-in">
              <textarea
                className="w-full border p-2 rounded text-xs outline-none focus:ring-1 focus:ring-[#045E51] bg-[#FFE2A1]"
                value={remarkDraft}
                onChange={(e) => setRemarkDraft(e.target.value)}
                placeholder="Schrijf een opmerking..."
                rows={2}
                autoFocus
              />
              <div className="flex flex-col gap-1">
                <button aria-label="Bevestigen"
                  onClick={() => saveRemark(msgId)}
                  className="bg-[#045E51] text-white p-1.5 rounded hover:bg-[#045E51]"
                >
                  <Check size={14} />
                </button>
                <button aria-label="Sluiten"
                  onClick={() => setEditingRemarkId(null)}
                  className="bg-gray-200 text-gray-500 p-1.5 rounded hover:bg-gray-300"
                >
                  <CloseIcon size={14} />
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => startEditingRemark(entry)}
              className="text-xs text-gray-500 hover:text-[#045E51] font-semibold flex items-center gap-1 transition-colors mt-1"
            >
              {entry.fmRemark ? (
                <>
                  <Edit2 size={10} /> Bewerk FM Opmerking
                </>
              ) : (
                <>
                  <MessageSquare size={10} /> + FM Opmerking
                </>
              )}
            </button>
          )}
        </div>
      )}
    </>
  );

  return (
    <div
      className={`flex flex-col h-full rounded-2xl overflow-hidden ${logs.length === 0 ? "border border-gray-100" : ""}`}
      style={{ backgroundColor: logs.length === 0 ? "#FFFFFF" : colorBg }}
    >
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[400px]">
        {/* 📦 Archief: nieuwe overdrachten schrijf je in het Dagbord */}
        {logs.length === 0 && (
          <EmptyState icon={IcChat} message={`Nog geen ${fmOnly ? "mededelingen" : "overdrachten"} voor deze dag.`} sub="Nieuwe overdrachten schrijf je in het Dagbord." />
        )}

        {/* ── Gecombineerde overdracht-kaart ── */}
        {logs.length > 0 && (
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
            {/* Kaart-header met één gelezen-knop */}
            <div
              className="px-4 py-3 border-b flex items-center justify-between"
              style={{ backgroundColor: `${colorBg}18` }}
            >
              <span
                className="font-semibold text-sm flex items-center gap-1.5"
                style={{ color: colorText || "#374151" }}
              >
                <ClipboardList size={14} />
                Overdracht ({logs.length}{" "}
                {logs.length === 1 ? "bericht" : "berichten"})
              </span>

              {/* Gelezen-knop / status */}
              {allOtherLogs.length > 0 && (
                <div className="flex items-center gap-2">
                  {isFullySectionRead ? (
                    <span className="text-xs text-[#045E51] font-semibold flex items-center gap-1">
                      <CheckCheck size={13} /> Gelezen
                    </span>
                  ) : (
                    <button
                      onClick={handleReadAll}
                      className="text-xs px-3 py-1 rounded-lg font-semibold flex items-center gap-1 transition-colors"
                      style={{ backgroundColor: C.SkyLight, color: C.Lagoon }}
                    >
                      <Eye size={12} /> Markeer als gelezen
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Alle entries als tijdlijn */}
            {[...logs].reverse().map((entry, index) => {
              const isSimple =
                !entry.actFields &&
                !entry.creaActs &&
                !entry.remarks &&
                !entry.colleagues &&
                entry.text;
              const msgId = entry.id || `msg-${index}`;
              const isAuthor =
                entry.author === loggedInUserName || userRole === "fm";
              const crowdLabels = [
                "Erg Rustig",
                "Rustig",
                "Normaal",
                "Druk",
                "Erg Druk",
              ];
              const CrowdBadge = ({ lvl }) => {
                if (!lvl) return null;
                const [lightHex, darkHex] = CROWD_TYPE_COLORS[type] || [
                  C.Sky,
                  C.Lagoon,
                ];
                const dotColor = interpolateCrowdColor(lvl, type);
                return (
                  <div className="inline-flex items-center gap-1.5">
                    <span
                      className="text-xs font-semibold whitespace-nowrap"
                      style={{ color: dotColor }}
                    >
                      {crowdLabels[lvl - 1] || lvl}
                    </span>
                    <div
                      className="relative w-14 h-1.5 rounded-full shrink-0"
                      style={{
                        background: `linear-gradient(to right, ${lightHex}, ${darkHex})`,
                      }}
                    >
                      <div
                        className="absolute top-1/2 w-2.5 h-2.5 rounded-full border-2 border-white shadow-sm"
                        style={{
                          left: `${((lvl - 1) / 4) * 100}%`,
                          transform: "translate(-50%, -50%)",
                          backgroundColor: dotColor,
                        }}
                      />
                    </div>
                  </div>
                );
              };

              const timeLabel = entry.timestamp
                ? (() => {
                    const d = new Date(entry.timestamp);
                    const today = new Date();
                    const yesterday = new Date();
                    yesterday.setDate(today.getDate() - 1);
                    const timeStr = d.toLocaleTimeString("nl-NL", {
                      hour: "2-digit",
                      minute: "2-digit",
                    });
                    if (d.toDateString() === today.toDateString())
                      return `Vandaag ${timeStr}`;
                    if (d.toDateString() === yesterday.toDateString())
                      return `Gisteren ${timeStr}`;
                    return (
                      d.toLocaleDateString("nl-NL", {
                        day: "numeric",
                        month: "short",
                      }) +
                      " " +
                      timeStr
                    );
                  })()
                : entry.time;

              return (
                <div
                  key={msgId}
                  className={`relative group border-b last:border-0 ${
                    isSimple ? "p-3" : "p-4"
                  }`}
                  style={{
                    backgroundColor: index % 2 === 0 ? "#fff" : `${colorBg}08`,
                    // Afdelingskleur als rand links, overal hetzelfde paar
                    borderLeft: `3px solid ${
                      { ent: C.Honey, entertainment: C.Honey, crea: C.Sky, springkussen: C.Blossom, hotspot: "#FF8A3D", fm: C.Pine }[type] || C.Pine
                    }`,
                  }}
                >
                  {/* Entry header */}
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-semibold text-xs text-gray-500 flex items-center gap-1.5">
                      <ProfileAvatar
                        name={entry.author}
                        staffList={staffList}
                        size={22}
                        color={colorBg}
                      />
                      {entry.author || "Archief"}
                    </span>
                    {timeLabel ? (
                      <CpPil kleur="grijs" className="flex-shrink-0">{timeLabel}</CpPil>
                    ) : null}
                  </div>

                  {isSimple ? (
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">
                      {highlightMentions(entry.text, loggedInUserName)}
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {/* Activiteiten met drukte (nieuw formaat) */}
                      {entry.actFields &&
                        Object.keys(entry.actFields).length > 0 && (
                          <div className="space-y-2">
                            {dayActivities
                              .filter((a) => entry.actFields[String(a.id)])
                              .map((act) => {
                                const af = entry.actFields[String(act.id)];
                                if (!af) return null;
                                const actType = (() => {
                                  const t = (act.title || "").toLowerCase();
                                  if (t.includes("disco")) return "disco";
                                  if (
                                    t.includes("bingo") &&
                                    !t.includes("natuur")
                                  )
                                    return "bingo";
                                  if (t.includes("quiz")) return "quiz";
                                  if (
                                    t.includes("verhaal") ||
                                    t.includes("voorlees")
                                  )
                                    return "verhaal";
                                  if (t.includes("prize")) return "prizenight";
                                  return "generic";
                                })();
                                const acColor =
                                  type === "ent"
                                    ? {
                                        bg: "bg-[#FFE2A1]",
                                        border: "border-[#FFCA55]",
                                      }
                                    : {
                                        bg: "bg-[#C4D4FF]",
                                        border: "border-[#9DB4F6]/60",
                                      };
                                return (
                                  <div
                                    key={act.id}
                                    className={`rounded-xl border p-3 space-y-2 ${acColor.bg} ${acColor.border}`}
                                  >
                                    {/* Titel + tijd */}
                                    <div className="flex items-start justify-between gap-2">
                                      <span
                                        className="font-semibold text-sm leading-snug"
                                        style={{
                                          color:
                                            type === "ent" ? C.Bark : C.Lagoon,
                                        }}
                                      >
                                        {act.title}
                                      </span>
                                      {act.time && (
                                        <span className="text-xs text-gray-500 flex-shrink-0">
                                          {act.time}
                                        </span>
                                      )}
                                    </div>

                                    {/* Collega's + drukte in één rij */}
                                    <div className="flex items-center gap-2 flex-wrap">
                                      {/* Collega's */}
                                      {af.colleagues ? (
                                        <div className="flex items-center gap-1 flex-wrap">
                                          <span className="text-xs text-gray-500 font-medium">
                                            met
                                          </span>
                                          {af.colleagues
                                            .split(", ")
                                            .filter(Boolean)
                                            .map((name) => (
                                              <div
                                                key={name}
                                                className="flex items-center gap-1"
                                              >
                                                <ProfileAvatar
                                                  name={name}
                                                  staffList={staffList}
                                                  size={18}
                                                  color={
                                                    type === "ent"
                                                      ? C.Bark
                                                      : C.Lagoon
                                                  }
                                                />
                                                <span
                                                  className="text-xs font-semibold"
                                                  style={{
                                                    color:
                                                      type === "ent"
                                                        ? C.Bark
                                                        : C.Lagoon,
                                                  }}
                                                >
                                                  {name}
                                                </span>
                                              </div>
                                            ))}
                                        </div>
                                      ) : (
                                        <span className="text-xs text-gray-500">
                                          solo
                                        </span>
                                      )}
                                      {/* Drukte — altijd tonen */}
                                      <div className="ml-auto">
                                        {af.crowd ? (
                                          <CrowdBadge lvl={af.crowd} />
                                        ) : (
                                          <span className="px-2 py-0.5 rounded-lg text-xs font-semibold bg-[#FFB0D6] text-[#700606] border border-[#FF699B]/50">
                                            Drukte niet ingevuld
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    {actType === "disco" && af.songs && (
                                      <p className="text-xs text-gray-800">
                                        <span className="font-semibold">
                                          🎵 Liedjes:
                                        </span>{" "}
                                        {af.songs}
                                      </p>
                                    )}
                                    {actType === "bingo" && af.bingoQs && (
                                      <p className="text-xs text-gray-800">
                                        <span className="font-semibold">
                                          ❓ Vragen:
                                        </span>{" "}
                                        {af.bingoQs}
                                      </p>
                                    )}
                                    {actType === "quiz" && af.quiz && (
                                      <p className="text-xs text-gray-800">
                                        <span className="font-semibold">
                                          🧠 Quiz:
                                        </span>{" "}
                                        {af.quiz}
                                      </p>
                                    )}
                                    {actType === "prizenight" &&
                                      af.finaleQs && (
                                        <p className="text-xs text-gray-800">
                                          <span className="font-semibold">
                                            🏆 Finale:
                                          </span>{" "}
                                          {af.finaleQs}
                                        </p>
                                      )}
                                    {actType === "verhaal" && af.story && (
                                      <p className="text-xs text-gray-800">
                                        <span className="font-semibold">
                                          📖 Verhaal:
                                        </span>{" "}
                                        {af.story}
                                      </p>
                                    )}
                                    {af.notes && (
                                      <p className="text-xs text-gray-800 whitespace-pre-wrap">
                                        {highlightMentions(
                                          af.notes,
                                          loggedInUserName
                                        )}
                                      </p>
                                    )}
                                  </div>
                                );
                              })}
                            {Object.entries(entry.actFields)
                              .filter(
                                ([actId]) =>
                                  !dayActivities.find(
                                    (a) => String(a.id) === actId
                                  )
                              )
                              .map(([actId, af]) => (
                                <div
                                  key={actId}
                                  className="rounded-xl border border-gray-100 bg-white p-3 space-y-1.5"
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500">
                                      Activiteit
                                    </span>
                                    {af.crowd && <CrowdBadge lvl={af.crowd} />}
                                  </div>
                                  {af.songs && (
                                    <p className="text-xs">
                                      <span className="font-semibold">🎵</span>{" "}
                                      {af.songs}
                                    </p>
                                  )}
                                  {af.bingoQs && (
                                    <p className="text-xs">
                                      <span className="font-semibold">❓</span>{" "}
                                      {af.bingoQs}
                                    </p>
                                  )}
                                  {af.quiz && (
                                    <p className="text-xs">
                                      <span className="font-semibold">🧠</span>{" "}
                                      {af.quiz}
                                    </p>
                                  )}
                                  {af.finaleQs && (
                                    <p className="text-xs">
                                      <span className="font-semibold">🏆</span>{" "}
                                      {af.finaleQs}
                                    </p>
                                  )}
                                  {af.story && (
                                    <p className="text-xs">
                                      <span className="font-semibold">📖</span>{" "}
                                      {af.story}
                                    </p>
                                  )}
                                  {af.notes && (
                                    <p className="text-xs text-gray-800">
                                      {af.notes}
                                    </p>
                                  )}
                                </div>
                              ))}
                          </div>
                        )}

                      {/* Crea handmatige activiteiten */}
                      {entry.creaActs && entry.creaActs.length > 0 && (
                        <div className="space-y-2">
                          {entry.creaActs.map((ca, i) => (
                            <div
                              key={i}
                              className="rounded-xl border border-[#9DB4F6]/60 bg-[#C4D4FF] p-3 space-y-1"
                            >
                              <div className="flex items-center justify-between">
                                <span
                                  className="font-semibold text-sm"
                                  style={{ color: C.Lagoon }}
                                >
                                  {ca.name || "Activiteit"}
                                </span>
                                {ca.crowd && <CrowdBadge lvl={ca.crowd} />}
                              </div>
                              {ca.notes && (
                                <p className="text-xs text-gray-800 whitespace-pre-wrap">
                                  {highlightMentions(
                                    ca.notes,
                                    loggedInUserName
                                  )}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Legacy velden */}
                      {!entry.actFields && entry.remarks && (
                        <div>
                          <span className="text-xs font-semibold uppercase text-gray-500 block mb-1">
                            Bijzonderheden
                          </span>
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">
                            {highlightMentions(entry.remarks, loggedInUserName)}
                          </p>
                        </div>
                      )}
                      {!entry.actFields && entry.extraCrea && (
                        <div>
                          <span className="text-xs font-semibold uppercase text-gray-500 block mb-1">
                            Activiteiten
                          </span>
                          <p className="text-xs text-gray-800">
                            {entry.extraCrea}
                          </p>
                        </div>
                      )}
                      {!entry.actFields &&
                        (entry.songs ||
                          entry.quiz ||
                          entry.story ||
                          entry.bingoQs ||
                          entry.finaleQs) && (
                          <div className="bg-[#FFE2A1] p-2 rounded-lg border border-[#FFCA55] grid grid-cols-2 gap-2">
                            {entry.songs && (
                              <div>
                                <span className="text-xs font-semibold text-[#7A5226] block">
                                  Disco
                                </span>
                                <span className="text-xs">{entry.songs}</span>
                              </div>
                            )}
                            {entry.quiz && (
                              <div>
                                <span className="text-xs font-semibold text-[#7A5226] block">
                                  Quiz
                                </span>
                                <span className="text-xs">{entry.quiz}</span>
                              </div>
                            )}
                            {entry.story && (
                              <div>
                                <span className="text-xs font-semibold text-[#7A5226] block">
                                  Verhaal
                                </span>
                                <span className="text-xs">{entry.story}</span>
                              </div>
                            )}
                            {entry.bingoQs && (
                              <div>
                                <span className="text-xs font-semibold text-[#7A5226] block">
                                  Bingo
                                </span>
                                <span className="text-xs">{entry.bingoQs}</span>
                              </div>
                            )}
                            {entry.finaleQs && (
                              <div>
                                <span className="text-xs font-semibold text-[#7A5226] block">
                                  Finale
                                </span>
                                <span className="text-xs">
                                  {entry.finaleQs}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      {entry.extraNotes && entry.actFields && (
                        <div>
                          <span className="text-xs font-semibold uppercase text-gray-500 block mb-1">
                            Extra opmerkingen
                          </span>
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">
                            {highlightMentions(
                              entry.extraNotes,
                              loggedInUserName
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {renderFMSection(entry, msgId)}
                  {entry.photos && entry.photos.length > 0 && (
                    <PhotoGallery
                      photos={entry.photos}
                      entryId={entry.id}
                      onDelete={(entryId, photoIndex) => {
                        const updatedLogs = logs.map((log) => {
                          if (log.id === entryId) {
                            return {
                              ...log,
                              photos: log.photos.filter(
                                (_, i) => i !== photoIndex
                              ),
                            };
                          }
                          return log;
                        });
                        onUpdate(updatedLogs);
                      }}
                      userRole={userRole}
                      loggedInUserName={loggedInUserName}
                      author={entry.author}
                    />
                  )}

                  {/* Edit / Delete knoppen (hover) */}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {(userRole === "fm" || userRole === "co") && (
                      <button aria-label="Verwijderen"
                        onClick={() => handleDelete(msgId)}
                        className="text-gray-500 hover:text-[#700606]"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Sectie-brede read receipt footer */}
            {allOtherLogs.length > 0 && (
              <div className="px-4 py-2 bg-gray-50 border-t flex flex-wrap gap-1 items-center">
                {sectionReadNames.map((n) => (
                  <span
                    key={n}
                    className="text-xs px-1.5 py-0.5 rounded-full bg-[#DAFFD1] text-[#045E51] flex items-center gap-0.5"
                  >
                    <CheckCheck size={9} /> {getFirstName(n)}
                  </span>
                ))}
                {sectionNotReadNames.map((n) => (
                  <span
                    key={n}
                    className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500"
                  >
                    {getFirstName(n)}
                  </span>
                ))}
                {sectionReadNames.length === 0 &&
                  sectionNotReadNames.length === 0 && (
                    <span className="text-xs text-gray-500">
                      Nog niemand gelezen
                    </span>
                  )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// --- ZWEVEND CHATBOLLETJE ---
const FloatingChatBubble = ({
  chatUnreadCount,
  onClick,
  theme,
  loggedInUserName,
  appData,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [previewMsg, setPreviewMsg] = useState(null);

  // Haal het laatste ongelezen bericht op als preview
  useEffect(() => {
    const chats = appData.chats || [];
    const unreadMsgs = chats
      .filter((c) => c.participants?.includes(loggedInUserName))
      .flatMap((c) =>
        c.messages
          .filter(
            (m) =>
              !m.readBy?.includes(loggedInUserName) &&
              m.sender !== loggedInUserName
          )
          .map((m) => ({
            ...m,
            chatWith: c.participants.find((p) => p !== loggedInUserName),
          }))
      )
      .sort((a, b) => new Date(b.time) - new Date(a.time));

    setPreviewMsg(unreadMsgs[0] || null);
  }, [appData.chats, loggedInUserName]);

  return (
    <div className="fixed bottom-20 right-4 z-40 flex flex-col items-end gap-2 md:bottom-6">
      {/* Preview popup bij ongelezen berichten */}
      {isExpanded && previewMsg && (
        <button
          onClick={onClick}
          className="bg-white rounded-2xl shadow-sm border p-3 max-w-[220px] text-left animate-in slide-in-from-bottom-2 fade-in duration-200"
          style={{ borderColor: theme.secondary }}
        >
          <div
            className="text-xs font-semibold uppercase tracking-wide mb-1"
            style={{ color: theme.primary }}
          >
            {previewMsg.chatWith}
          </div>
          <div className="text-xs text-gray-800 line-clamp-2">
            {previewMsg.text}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {new Date(previewMsg.time).toLocaleTimeString("nl-NL", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        </button>
      )}

      {/* Het bolletje zelf */}
      <button
        onClick={() => {
          if (chatUnreadCount > 0 && !isExpanded) {
            setIsExpanded(true);
          } else {
            setIsExpanded(false);
            onClick();
          }
        }}
        onMouseEnter={() => chatUnreadCount > 0 && setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
        className="w-14 h-14 rounded-full shadow-sm flex items-center justify-center transition-all hover:scale-110 active:scale-95 relative"
        style={{ backgroundColor: theme.primary }}
      >
        <MessageSquare size={26} className="text-white" />
        {chatUnreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-[#700606] text-white text-xs font-semibold w-5 h-5 rounded-full flex items-center justify-center border border-white animate-pulse">
            {chatUnreadCount > 9 ? "9+" : chatUnreadCount}
          </span>
        )}
      </button>
    </div>
  );
};


const TaskProgressWidget = ({ checklists, onViewTasks, theme }) => {
  const t = theme || THEMES.general;
  const getStats = (list) => {
    const total = list.length;
    const done = list.filter((t) => t.done).length;
    const pct = total === 0 ? 0 : Math.round((done / total) * 100);
    return { total, done, pct };
  };
  const ent = getStats(checklists.entertainment);
  const crea = getStats(checklists.crea);
  const extra = getStats(checklists.extra);
  return (
    <Card
      className="p-0 h-full flex flex-col"
      borderColor={t.primary}
      onClick={onViewTasks}
      hover={true}
    >
      <div
        className="px-4 py-3 font-semibold text-white flex justify-between items-center rounded-t-xl"
        style={{ backgroundColor: t.primary }}
      >
        <span className="flex items-center gap-2">
          <ListChecks size={20} /> Taken Voortgang
        </span>
        <span className="text-xs bg-white/20 px-2 py-1 rounded flex items-center gap-1">
          Ga naar Taken <ChevronRight size={12} />
        </span>
      </div>
      <div className="p-4 space-y-3 flex-1">
        <div className="flex items-center gap-2">
          <div
            className="p-1.5 rounded-lg text-white"
            style={{ backgroundColor: C.Honey }}
          >
            <IcEntertainment size={16} color={C.Bark} />
          </div>
          <div className="flex-1">
            <div className="flex justify-between text-sm mb-0.5 font-semibold text-gray-800">
              <span>Ent</span>
              <span>{ent.pct}%</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-500"
                style={{ backgroundColor: C.Honey, width: `${ent.pct}%` }}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="p-1.5 rounded-lg text-white"
            style={{ backgroundColor: C.Sky }}
          >
            <IcCrea size={16} color={C.Lagoon} />
          </div>
          <div className="flex-1">
            <div className="flex justify-between text-sm mb-0.5 font-semibold text-gray-800">
              <span>Crea</span>
              <span>{crea.pct}%</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-500"
                style={{ backgroundColor: C.Sky, width: `${crea.pct}%` }}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="p-1.5 rounded-lg text-white"
            style={{ backgroundColor: C.Grass }}
          >
            <Clipboard size={16} color={C.Pine} />
          </div>
          <div className="flex-1">
            <div className="flex justify-between text-sm mb-0.5 font-semibold text-gray-800">
              <span>Extra</span>
              <span>{extra.pct}%</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-500"
                style={{ backgroundColor: C.Grass, width: `${extra.pct}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

const StatsWidget = ({ dailyStats, currentDay, onViewDetails, theme }) => {
  const dateKey = toIsoDate(currentDay);
  const stats = dailyStats?.[dateKey] || {
    total: 0,
    adults: 0,
    kids: 0,
    babies: 0,
  };
  const displayTotal =
    stats.total > 0
      ? stats.total
      : (stats.adults || 0) + (stats.kids || 0) + (stats.babies || 0);
  return (
    <Card
      className="p-0 h-full flex flex-col animate-in fade-in cursor-pointer hover:shadow-sm overflow-hidden relative"
      borderColor={C.Sky}
      onClick={onViewDetails}
    >
      <div
        className="px-4 py-3 font-semibold text-white flex justify-between items-center rounded-t-xl"
        style={{ backgroundColor: C.Lagoon }}
      >
        <span className="flex items-center gap-2">
          <Users size={20} /> Gasten Vandaag
        </span>
        <span
          className="text-xs px-2 py-1 rounded flex items-center gap-1"
          style={{ backgroundColor: C.Sky, color: C.Lagoon }}
        >
          Ga naar Bezetting <ChevronRight size={12} />
        </span>
      </div>
      <div className="p-3 flex-1 flex flex-col justify-center items-center text-center z-10">
        <span
          className="text-4xl font-semibold leading-tight"
          style={{ color: C.Lagoon }}
        >
          {displayTotal || "--"}
        </span>
        <div className="flex gap-3 mt-3 text-xs font-medium text-gray-500">
          <div className="flex flex-col items-center">
            <span className="font-semibold text-sm" style={{ color: C.Sunset }}>
              {stats.adults || 0}
            </span>
            <span>Volw</span>
          </div>
          <div className="w-px bg-gray-200"></div>
          <div className="flex flex-col items-center">
            <span className="font-semibold text-sm" style={{ color: C.Sunset }}>
              {stats.kids || 0}
            </span>
            <span>Kind</span>
          </div>
          <div className="w-px bg-gray-200"></div>
          <div className="flex flex-col items-center">
            <span className="font-semibold text-sm" style={{ color: C.Sunset }}>
              {stats.babies || 0}
            </span>
            <span>Baby</span>
          </div>
        </div>
      </div>
      <div
        className="p-1.5 text-center text-xs font-semibold border-t rounded-b-xl"
        style={{
          backgroundColor: `${C.Sky}20`,
          color: C.Lagoon,
          borderColor: C.Sky,
        }}
      >
        Bekijk Details
      </div>
    </Card>
  );
};

const CharactersWidget = ({ todaysCharacters, onViewCharacters, theme }) => {
  const t = theme || THEMES.general;
  return (
    <Card className="p-0 h-full flex flex-col" borderColor={t.primary}>
      <div
        className="px-4 py-3 font-semibold text-white flex items-center gap-2 rounded-t-xl"
        style={{ backgroundColor: t.primary }}
      >
        <Smile size={20} /> Karakters Vandaag
      </div>
      <div className="flex-1 overflow-y-auto space-y-1 pr-1 custom-scrollbar p-3">
        {todaysCharacters.length > 0 ? (
          todaysCharacters.map((c) => (
            <div
              key={c.role + c.id}
              className="flex items-center justify-between p-1.5 bg-gray-50 rounded-lg border border-gray-100"
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white shadow-sm"
                  style={{ backgroundColor: getCharColorInfo(c.role).bg }}
                >
                  {c.role[0]}
                </div>
                <div>
                  <div className="font-semibold text-sm text-gray-800">
                    {c.role}
                  </div>
                  <div className="text-xs text-gray-500">{c.actor}</div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-gray-500 text-xs">
            <Smile size={24} className="mb-2 opacity-20" />
            Geen karakters ingepland
          </div>
        )}
      </div>
    </Card>
  );
};

const BirthdayWidget = ({ staffList, currentDay, fmDob }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const getBirthdays = () => {
    const birthdays = staffList.filter((s) => {
      if (!s.dob) return false;
      const d = new Date(s.dob);
      return (
        d.getDate() === currentDay.getDate() &&
        d.getMonth() === currentDay.getMonth()
      );
    });

    // Check ook Maik (FM)
    if (fmDob) {
      const fmDate = new Date(fmDob);
      if (
        fmDate.getDate() === currentDay.getDate() &&
        fmDate.getMonth() === currentDay.getMonth()
      ) {
        birthdays.push({ name: "Maik", dob: fmDob });
      }
    }

    return birthdays;
  };

  const birthdays = getBirthdays();
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    // Alleen confetti op VANDAAG en alleen bij eerste keer vandaag
    const isToday = currentDay.toDateString() === today.toDateString();

    if (birthdays.length > 0 && isToday) {
      // Check localStorage of confetti al getoond is vandaag
      const confettiKey = `birthday_confetti_shown_${toIsoDate(today)}`;
      const alreadyShown = localStorage.getItem(confettiKey);

      if (!alreadyShown) {
        setShowConfetti(true);
        // Sla op dat confetti getoond is
        localStorage.setItem(confettiKey, "true");

        // Stop confetti na 5 seconden
        setTimeout(() => setShowConfetti(false), 5000);
      }
    } else {
      setShowConfetti(false);
    }
  }, [birthdays.length, currentDay]);

  if (birthdays.length === 0) return null;

  // Check of de geselecteerde dag in het verleden, heden of toekomst is
  const selectedDate = new Date(currentDay);
  selectedDate.setHours(0, 0, 0, 0);

  const isPast = selectedDate < today;
  const isToday = selectedDate.getTime() === today.getTime();
  const isFuture = selectedDate > today;

  const getMessage = (person) => {
    const age = calculateAge(person.dob);

    if (isToday) {
      return `${person.name} is vandaag ${age} jaar geworden!`;
    } else if (isPast) {
      return `${person.name} was jarig en werd ${age} jaar`;
    } else {
      // Toekomst - bereken leeftijd op die datum
      const futureAge =
        age +
        Math.ceil((selectedDate - today) / (365.25 * 24 * 60 * 60 * 1000));
      return `${person.name} wordt ${futureAge} jaar`;
    }
  };

  const getTitle = () => {
    if (isToday) return "Hieperdepiep Hoera!";
    if (isPast) return "Was Jarig";
    return "Binnenkort Jarig";
  };

  const getThemeColor = () => {
    if (isToday) return C.Forest; // Donkergroen voor vandaag
    if (isPast) return C.Pine; // Groen voor verleden
    return C.Forest; // Donkergroen voor toekomst
  };

  const themeColor = getThemeColor();

  return (
    <>
      {showConfetti && <Confetti />}
      <div
        className="bg-white rounded-2xl shadow-sm overflow-hidden animate-fade-in-up border-2"
        style={{ borderColor: themeColor }}
      >
        <div className="cp-pattern-dots">
          <div className="p-6">
            <div className="flex items-start gap-4">
              {/* Icoon */}
              <div
                className="p-3 rounded-xl shadow-sm"
                style={{
                  backgroundColor: `${themeColor}20`,
                  color: themeColor,
                }}
              >
                {isToday ? (
                  <Gift size={28} />
                ) : isPast ? (
                  <CalendarIcon size={28} />
                ) : (
                  <PartyPopper size={28} />
                )}
              </div>

              {/* Content */}
              <div className="flex-1">
                <h3
                  className="text-base font-semibold mb-2"
                  style={{ color: themeColor }}
                >
                  {getTitle()}
                </h3>

                <div className="space-y-1">
                  {birthdays.map((b) => (
                    <p
                      key={b.name}
                      className="text-sm font-medium"
                      style={{ color: themeColor }}
                    >
                      {getMessage(b)}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};


// Vaste volgorde op mobiel — ongeacht de desktop-layout instelling
const MOBILE_ORDER = [
  "quicklinks", // 1. Snelkoppelingen — direct navigeren
  "timeline", // 2. Mijn dag — wat doe ik vandaag?
  "ai", // 3. Dagbriefing — context van de dag
  "announcements", // Mededelingen — prikbord
  "handover", // 4. Overdrachten — lezen voor dienst
  "tasks", // 5. Taken — wat moet er gedaan?
  "schedule", // 6. Programma — wanneer wat?
  "team", // 7. Team — wie werkt er?
  "occupancy", // 8. Bezetting — hoeveel gasten?
  "checklist", // 9. FM Checklist
  "swaps", // 10. Ruilverzoeken
];


const colSpanClass = (size) => {
  // In columns-layout werkt col-span niet; column-span via inline style
  return "";
};

const colSpanStyle = (size) => {
  if (size === 3) return { columnSpan: "all" };
  return {};
};


// ============================================================
// 📅 BESCHIKBAARHEIDSSYSTEEM
// Medewerkers geven per dag een tijdvak + opmerking op.
// FM ziet maandoverzicht + genereert automatisch rooster.
//
// Data: staffAvailability[naam]["2026-03"][15] =
//   { start: "09:00", end: "17:00", note: "..." } | null (= niet beschikbaar)
// ============================================================

// ─── Utility helpers ───────────────────────────────────────

const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();
const getFirstWeekday = (year, month) => {
  const d = new Date(year, month - 1, 1).getDay();
  return d === 0 ? 6 : d - 1; // 0=Ma
};
const dutchMonths = [
  "januari",
  "februari",
  "maart",
  "april",
  "mei",
  "juni",
  "juli",
  "augustus",
  "september",
  "oktober",
  "november",
  "december",
];
const dutchDaysShort = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

// Parse "HH:MM" naar minuten
const timeToMins = (t) => {
  if (!t) return 0;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + (m || 0);
};
const minsToHours = (m) => (m / 60).toFixed(1).replace(".0", "");

// ─── HELPER: maandsleutel "YYYY-MM" ───────────────────────
const toMonthKey = (y, m) => `${y}-${String(m).padStart(2, "0")}`;
const parseMonthKey = (mk) => ({
  year: parseInt(mk.slice(0, 4)),
  month: parseInt(mk.slice(5, 7)),
});
const nextMonthKey = (mk) => {
  const { year, month } = parseMonthKey(mk);
  return month === 12 ? toMonthKey(year + 1, 1) : toMonthKey(year, month + 1);
};
const currentMonthKey = () => {
  const n = new Date();
  return toMonthKey(n.getFullYear(), n.getMonth() + 1);
};
const formatMonthLabel = (mk) => {
  const { year, month } = parseMonthKey(mk);
  return `${dutchMonths[month - 1]} ${year}`;
};


// ============================================================
// 🎯 GEDEELDE BESCHIKBAARHEID-HELPERS
// Eén bron van waarheid voor alle FM/RoosterMaker-weergaven die
// staffAvailability samenvatten of labelen — zodat elke view (matrix,
// kalendercellen, exportrij, dienstruil-check, ...) exact dezelfde
// tellingen, tekst en kleuren gebruikt i.p.v. het zelf opnieuw te
// berekenen.
// ============================================================

// Status van 1 opgave: "approved" | "pending" | "rejected" | null.
// Oude entries zonder status-veld gelden vanaf APPROVAL_CUTOFF_DATE
// alsnog als "pending" — zie de FM-goedkeuringsflow hieronder.
const getAvailStatus = (a, date) => {
  if (!a) return null;
  if (a.status) return a.status;
  if (date && date >= APPROVAL_CUTOFF_DATE) return "pending";
  return "approved";
};

// Hulpkrachten: op hun "niet vaste" weekdagen (standaard ma t/m vr) zijn ze
// standaard niet beschikbaar en tellen ze niet mee in de bezetting/telling
// van beschikbaarheid en vrij-aanvragen. Instelling per medewerker in
// staffContractInfo[naam] = { hulpkracht: true, nietVasteDagen: [1..5] }
// (JS getDay: 0 = zondag, 1 = maandag … 6 = zaterdag).
// Heeft de hulpkracht voor zo'n dag zelf iets ingevuld (bv. in een
// schoolvakantie), dan telt die dag gewoon mee: geef dan allAvail mee.
const HULP_STANDAARD_DAGEN = [1, 2, 3, 4, 5];
const isHulpkracht = (contractInfo, naam) => !!contractInfo?.[naam]?.hulpkracht;
const isHulpkrachtVrijeDag = (contractInfo, naam, datum, allAvail) => {
  const info = contractInfo?.[naam];
  if (!info?.hulpkracht || !datum) return false;
  if (allAvail?.[naam]?.[String(datum).slice(0, 10)]) return false;
  const dagen = Array.isArray(info.nietVasteDagen)
    ? info.nietVasteDagen
    : HULP_STANDAARD_DAGEN;
  const [y, m, d] = String(datum).slice(0, 10).split("-").map(Number);
  return dagen.includes(new Date(y, m - 1, d).getDay());
};
// Alleen de medewerkers die op deze dag meetellen
const telendeStaf = (staff, contractInfo, datum, allAvail) =>
  (staff || []).filter((s) => !isHulpkrachtVrijeDag(contractInfo, s.name, datum, allAvail));

const ROOSTER_SOURCE = "from-roostermaker";

// Projecteer een dienst-array (roosterDiensten[date]) naar
// workSchedule-shifts. Spiegelt exact de publish-logica.
const dienstenToShifts = (diensten) =>
  (diensten || []).flatMap((d) =>
    (d.assigned || []).map((name) => ({
      matchedUser: name,
      employeeName: name,
      type: d.type,
      start: d.start,
      end: d.end,
      location: d.title || RM_TYPES[d.type]?.label || d.type,
      source: ROOSTER_SOURCE,
      breaks: d.breaks || [],
      title: d.title || RM_TYPES[d.type]?.label || d.type,
    }))
  );

// Publiceer de diensten van opgegeven datums naar workSchedule.
// Behoudt niet-maker shifts; vervangt enkel de from-roostermaker
// entries. Retourneert { workSchedule }.
const publishDienstenPatch = (appData, dates) => {
  const ws = { ...(appData.workSchedule || {}) };
  (dates || []).forEach((date) => {
    const diensten = appData.roosterDiensten?.[date] || [];
    const kept = (ws[date] || []).filter((s) => s.source !== ROOSTER_SOURCE);
    ws[date] = [...kept, ...dienstenToShifts(diensten)];
  });
  return { workSchedule: ws };
};


// ── OPEN DIENST ───────────────────────────────────────────
// De FM biedt een dienst met een vrije plek aan. Beschikbare collega's
// krijgen een melding; wie het eerst "Ik pak hem" kiest, staat erop.
// Gegevens: roosterDiensten[datum][i].open = { sinds, aangebodenAan: [...] }
// en een melding { type: "open_dienst", date, dienstId, aangebodenAan }.

// Wie kan deze dienst oppakken? Beschikbaar (goedgekeurd of wachtend),
// geen hulpkracht op een niet-vaste dag, nog niet in een dienst die dag.
const openDienstKandidaten = (appData, date, dienst) => {
  const ci = appData.staffContractInfo || {};
  const dagDiensten = appData.roosterDiensten?.[date] || [];
  const alIngepland = new Set(dagDiensten.flatMap((d) => d.assigned || []));
  return (appData.staffList || [])
    .filter((s) => !s.inactive && !teamVerborgen(s))
    .map((s) => s.name)
    .filter((naam) => {
      if (alIngepland.has(naam)) return false;
      if (isHulpkrachtVrijeDag(ci, naam, date, appData.staffAvailability)) return false;
      const a = appData.staffAvailability?.[naam]?.[date];
      if (!a || a.available === false) return false;
      return getAvailStatus(a, date) !== "rejected";
    });
};

const openDienstAanbiedenPatch = (appData, date, dienstId, namen, door) => {
  const nu = new Date().toISOString();
  const diensten = (appData.roosterDiensten?.[date] || []).map((d) =>
    d.id === dienstId ? { ...d, open: { sinds: nu, door, aangebodenAan: namen } } : d
  );
  const dienst = diensten.find((d) => d.id === dienstId);
  const melding = {
    id: `open_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    type: "open_dienst",
    date,
    dienstId,
    title: dienst?.title || RM_TYPES[dienst?.type]?.label || "Dienst",
    start: dienst?.start,
    end: dienst?.end,
    aangebodenAan: namen,
    door,
    createdAt: nu,
    readBy: [],
  };
  return {
    roosterDiensten: { [date]: diensten },
    notifications: [...(appData.notifications || []), melding],
  };
};

const openDienstIntrekkenPatch = (appData, date, dienstId) => {
  const diensten = (appData.roosterDiensten?.[date] || []).map((d) => {
    if (d.id !== dienstId) return d;
    const { open, ...rest } = d;
    return { ...rest, open: null };
  });
  return {
    roosterDiensten: { [date]: diensten },
    notifications: (appData.notifications || []).filter(
      (n) => !(n.type === "open_dienst" && n.date === date && n.dienstId === dienstId)
    ),
  };
};

// Medewerker pakt de open dienst. Geeft { ok, reden, patch }.
const openDienstOppakken = (appData, date, dienstId, naam) => {
  const diensten = appData.roosterDiensten?.[date] || [];
  const dienst = diensten.find((d) => d.id === dienstId);
  if (!dienst || !dienst.open) return { ok: false, reden: "Deze dienst is niet meer open." };
  const assigned = dienst.assigned || [];
  if (assigned.includes(naam)) return { ok: false, reden: "Je staat al op deze dienst." };
  if (assigned.length >= (dienst.slots || 1))
    return { ok: false, reden: "Te laat, iemand anders heeft deze dienst al gepakt." };
  const nieuwAssigned = [...assigned, naam];
  const vol = nieuwAssigned.length >= (dienst.slots || 1);
  const nieuweDiensten = diensten.map((d) =>
    d.id === dienstId
      ? { ...d, assigned: nieuwAssigned, open: vol ? null : d.open }
      : d
  );
  const nu = new Date().toISOString();
  const notifs = (appData.notifications || [])
    .filter(
      (n) =>
        !(vol && n.type === "open_dienst" && n.date === date && n.dienstId === dienstId)
    )
    .map((n) =>
      n.type === "open_dienst" && n.date === date && n.dienstId === dienstId
        ? { ...n, readBy: [...(n.readBy || []), naam] }
        : n
    );
  notifs.push({
    id: `opengepakt_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    type: "open_dienst_gepakt",
    date,
    dienstId,
    title: dienst.title || RM_TYPES[dienst.type]?.label || "Dienst",
    start: dienst.start,
    end: dienst.end,
    employeeName: naam,
    createdAt: nu,
    readBy: [],
  });
  const patch = {
    roosterDiensten: { [date]: nieuweDiensten },
    notifications: notifs,
  };
  // Was deze dag al gepubliceerd? Dan meteen in het rooster zetten.
  const ws = appData.workSchedule?.[date] || [];
  if (ws.some((s) => s.source === ROOSTER_SOURCE)) {
    const kept = ws.filter((s) => s.source !== ROOSTER_SOURCE);
    patch.workSchedule = { [date]: [...kept, ...dienstenToShifts(nieuweDiensten)] };
  }
  return { ok: true, patch };
};

// Open diensten die aan deze medewerker zijn aangeboden en nog een plek hebben
const openDienstenVoor = (appData, naam) =>
  Object.entries(appData.roosterDiensten || {})
    .flatMap(([date, ds]) =>
      (ds || [])
        .filter(
          (d) =>
            d.open &&
            (d.open.aangebodenAan || []).includes(naam) &&
            !(d.assigned || []).includes(naam) &&
            (d.assigned || []).length < (d.slots || 1)
        )
        .map((d) => ({ date, dienst: d }))
    )
    .filter(({ date }) => date >= toIsoDate(new Date()))
    .sort((x, y) => x.date.localeCompare(y.date));

// Wanneer is deze vrij-aanvraag ingediend? Nieuwe aanvragen hebben een
// vaste requestedAt; oudere vallen terug op het moment van indienen of
// goedkeuren. null = onbekend (oude opgave).
const vrijAangevraagdOp = (a) =>
  (a && (a.requestedAt || a.submittedAt || a.approvedAt)) || null;

// Eén-regel label + categorie ("full" | "partial" | "off" | "none")
// voor 1 persoon op 1 dag.
const getPersonAvailLabel = (allAvail, naam, date) => {
  const a = allAvail?.[naam]?.[date];
  if (!a) return { tekst: "Nog niet ingevuld", kind: "none", status: null };
  const status = getAvailStatus(a, date);
  if (a.available === false) {
    return { tekst: "Niet beschikbaar", kind: "off", status, note: a.note };
  }
  if (a.start) {
    return {
      tekst: `${a.start}–${a.end}`,
      kind: "partial",
      status,
      note: a.note,
    };
  }
  return {
    tekst: "Hele dag beschikbaar",
    kind: "full",
    status,
    note: a.note,
  };
};


// Eén kleurenschema voor heel de app: Grass = beschikbaar,
// Honey = deels/pending, Sunset = niet beschikbaar/afgekeurd, grijs = geen opgave.
const AVAIL_COLORS = {
  full: { bg: C.Grass + "35", border: C.Pine, text: C.Pine, symbol: "✓" },
  partial: { bg: C.Honey + "35", border: C.Bark, text: C.Bark, symbol: "~" },
  off: { bg: C.Sunset + "25", border: C.Sunset, text: C.Sunset, symbol: "✗" },
  none: { bg: "#f9fafb", border: C.BorderLight, text: C.TextMuted, symbol: "" },
};

// Klein symbool voor krappe cellen (kalender/grid).
const AvailDot = ({ kind, size = 10 }) => {
  const c = AVAIL_COLORS[kind] || AVAIL_COLORS.none;
  if (!c.symbol) return null;
  return (
    <span className="font-semibold leading-none" style={{ color: c.text, fontSize: size }}>
      {c.symbol}
    </span>
  );
};

// Chip met tekst voor lijsten/kaarten, optioneel met goedkeuringsstatus.
const AvailBadge = ({ kind, label, status }) => {
  const c = AVAIL_COLORS[kind] || AVAIL_COLORS.none;
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded-full"
      style={{ backgroundColor: c.bg, color: c.text }}
    >
      {label}
      {status === "pending" && " · wacht op goedkeuring"}
      {status === "rejected" && " · afgekeurd"}
    </span>
  );
};


// ============================================================
// 🗓️ ROOSTERMAKER — FM maakt maandrooster op basis van beschikbaarheid
// ============================================================
// ─────────────────────────────────────────────────────────────────────────────
// 🗓️ ROOSTERMAKER — FM plant het maandrooster op basis van beschikbaarheid
// ─────────────────────────────────────────────────────────────────────────────
const RM_TYPES = {
  entertainment: {
    label: "Entertainment",
    color: C.Bark,
    bg: C.Honey + "30",
    border: C.Honey,
    icon: IcEntertainment,
  },
  crea: {
    label: "Crea",
    color: C.Lagoon,
    bg: C.Sky + "30",
    border: C.Sky,
    icon: IcCrea,
  },
  springkussen: {
    label: "Springkussen",
    color: C.Blossom,
    bg: C.Blossom + "18",
    border: C.Blossom + "60",
    icon: IcSpringkussen,
  },
  overig: {
    label: "Overig",
    color: C.TextSecondary,
    bg: C.Bg,
    border: C.Border,
    icon: LayoutGrid,
  },
  snipper: {
    label: "Snipperdag",
    color: C.Lagoon,
    bg: C.Sky + "30",
    border: C.Sky,
    icon: Sun,
    fixedHours: 8,
  },
  verlof: {
    label: "Verlof",
    color: "#15803d",
    bg: "#f0fdf4",
    border: "#86efac",
    icon: Calendar,
    fixedHours: 8,
  },
  at: {
    label: "Arbeidstherapeut.",
    color: C.Bark,
    bg: C.Honey + "30",
    border: C.Honey,
    icon: Activity,
    fixedHours: 8,
  },
  comp: {
    label: "Comp.-uren",
    color: "#7c3aed",
    bg: "#f5f3ff",
    border: "#c4b5fd",
    icon: Clock,
    fixedHours: 8,
  },
  ziek: {
    label: "Ziekmelding",
    color: C.Sunset,
    bg: C.Blossom + "18",
    border: C.Blossom + "60",
    icon: Home,
    fixedHours: 0,
    noCount: true,
  },
};


// Signaleer programma-items (activiteiten) die na een mutatie naar
// iemand verwijzen die niet meer op die dag is ingeroosterd. Geeft
// een lijst terug zodat de aanroeper de FM kan waarschuwen.
const orphanedActivities = (appData, date, name) => {
  const stillScheduled = (appData.workSchedule?.[date] || []).some(
    (s) => s.matchedUser === name
  );
  if (stillScheduled) return [];
  return (appData.schedule || []).filter(
    (a) => a.date === date && (a.staff || []).includes(name)
  );
};

const dienstHours = (d) => {
  if (!d) return 0;
  // Verloftypes met vaste uren
  const tc = RM_TYPES[d.type];
  if (tc?.fixedHours !== undefined && !tc.noCount) {
    // Als de gebruiker een custom uren-waarde heeft meegegeven, gebruik die
    return d.fixedHours != null ? Number(d.fixedHours) : tc.fixedHours;
  }
  if (!d.start || !d.end) return 0;
  let mins = timeToMins(d.end) - timeToMins(d.start);
  if (mins <= 0) mins += 24 * 60; // overnight shift
  // Trek pauze af
  if (d.breaks && d.breaks.length > 0) {
    d.breaks.forEach((brk) => {
      if (brk.start && brk.end) {
        let bMins = timeToMins(brk.end) - timeToMins(brk.start);
        if (bMins < 0) bMins += 24 * 60;
        mins -= bMins;
      }
    });
  }
  return +(Math.max(0, mins) / 60).toFixed(2);
};
// Format decimal hours as h:mm (e.g. 6.25 → "6:15")
const formatHours = (h) => {
  const totalMins = Math.round(h * 60);
  const hrs = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return mins === 0 ? `${hrs}u` : `${hrs}:${String(mins).padStart(2, "0")}u`;
};


// --- MY SHIFT WIDGET (Dashboard) ---
const MyShiftWidget = ({ appData, currentDay, loggedInUserName, theme }) => {
  const dateKey = toIsoDate(currentDay);
  const allShifts = appData.workSchedule?.[dateKey] || [];
  const myShifts = allShifts.filter((s) => s.matchedUser === loggedInUserName);

  if (myShifts.length === 0) return null;

  const nextShift = myShifts[0];

  return (
    <div
      className="bg-white rounded-2xl shadow-sm border-2 overflow-hidden"
      style={{ borderColor: theme.primary }}
    >
      <div className="cp-pattern-dots">
        <div className="p-6">
          <div className="flex items-center gap-3">
            <div
              className="p-3 rounded-xl shadow-sm"
              style={{
                backgroundColor: `${theme.primary}20`,
                color: theme.primary,
              }}
            >
              <Clock size={28} />
            </div>
            <div className="flex-1">
              <h3
                className="text-base font-semibold"
                style={{ color: theme.primary }}
              >
                Je shift vandaag
              </h3>
              <p
                className="text-sm font-medium"
                style={{ color: theme.primary }}
              >
                {nextShift.start} - {nextShift.end} •{" "}
                {nextShift.title || nextShift.location}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


// ===== SIMPELE ROOSTER UITBREIDINGEN =====

// 1️⃣ TEAM VANDAAG WIDGET (Dashboard)

// 2️⃣ MAANDELIJKS ROOSTER BEHEER (Admin - Simpel)
const SimpleMonthlyRooster = ({
  appData,
  updateData,
  showAlert,
  showConfirm,
  theme,
  setAdminSubView,
}) => {
  const [selectedMonth, setSelectedMonth] = useState("");
  const [uploading, setUploading] = useState(false);

  const getMonths = () => {
    const months = [];
    const year = new Date().getFullYear();
    for (let y of [year, year + 1]) {
      for (let m = 0; m < 12; m++) {
        const key = `${y}-${String(m + 1).padStart(2, "0")}`;
        const date = new Date(y, m, 1);
        months.push({
          key,
          label: date.toLocaleDateString("nl-NL", {
            month: "long",
            year: "numeric",
          }),
        });
      }
    }
    return months;
  };

  const months = getMonths();

  const getMonthStats = (monthKey) => {
    if (!appData.workSchedule) return null;
    const shifts = Object.entries(appData.workSchedule)
      .filter(([dateKey]) => dateKey.startsWith(monthKey))
      .flatMap(([, shifts]) => shifts);
    return shifts.length > 0 ? { shifts: shifts.length } : null;
  };

  const handleUpload = async (e, monthKey) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setSelectedMonth(monthKey);

    try {
      const text = await file.text();
      const { schedule } = parseScheduleCSV(
        text,
        appData.staffList || [],
        appData.settings.fmLastName || null
      );

      const monthSchedule = {};
      Object.entries(schedule).forEach(([dateKey, shifts]) => {
        if (dateKey.startsWith(monthKey)) monthSchedule[dateKey] = shifts;
      });

      updateData({
        workSchedule: { ...appData.workSchedule, ...monthSchedule },
      });
      showAlert(
        `Rooster voor ${
          months.find((m) => m.key === monthKey)?.label
        } geüpload!`
      );
    } catch (error) {
      showAlert("Upload mislukt!");
    }
    setUploading(false);
    setSelectedMonth("");
  };

  const deleteMonth = (monthKey) => {
    const monthLabel = months.find((m) => m.key === monthKey)?.label;
    showConfirm(`Verwijder rooster voor ${monthLabel}?`, () => {
      const newSchedule = {};
      Object.entries(appData.workSchedule || {}).forEach(
        ([dateKey, shifts]) => {
          if (!dateKey.startsWith(monthKey)) newSchedule[dateKey] = shifts;
        }
      );
      updateData({ workSchedule: newSchedule });
      showAlert(`${monthLabel} verwijderd!`);
    });
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button aria-label="Terug"
          onClick={() => setAdminSubView("menu")}
          className="w-11 h-11 flex items-center justify-center flex-shrink-0 rounded-xl hover:bg-gray-100"
        >
          <ArrowLeft size={24} style={{ color: theme.primary }} />
        </button>
        <div className="flex items-center gap-2">
          <CalendarDays size={28} style={{ color: theme.primary }} />
          <h2 className="text-xl font-semibold" style={{ color: theme.primary }}>
            Rooster per Maand
          </h2>
        </div>
      </div>

      <div className="bg-[#C4D4FF] border border-[#9DB4F6]/60 rounded-lg p-4 mb-6">
        <p className="text-sm text-[#00192F]">
          <strong>Stap 1:</strong> Kies een maand en upload het CSV bestand
          <br />
          <strong>Stap 2:</strong> Verwijder een maand met de prullenbak knop
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {months.map((month) => {
          const stats = getMonthStats(month.key);
          const hasData = stats !== null;

          return (
            <div
              key={month.key}
              className="bg-white rounded-xl border-2 p-4"
              style={{ borderColor: hasData ? theme.primary : C.Border }}
            >
              <div className="flex justify-between items-center mb-3">
                <h3
                  className="font-semibold"
                  style={{ color: hasData ? theme.primary : C.TextPrimary }}
                >
                  {month.label}
                </h3>
                {hasData && (
                  <button aria-label="Verwijderen"
                    onClick={() => deleteMonth(month.key)}
                    className="text-[#700606] hover:bg-[#FFB0D6] p-1 rounded"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              {hasData && (
                <p className="text-sm text-gray-500 mb-3">
                  {stats.shifts} shifts
                </p>
              )}

              <input
                type="file"
                accept=".csv"
                onChange={(e) => handleUpload(e, month.key)}
                className="hidden"
                id={`file-${month.key}`}
              />

              <label
                htmlFor={`file-${month.key}`}
                className="block w-full py-2 rounded-lg font-semibold text-center cursor-pointer text-sm"
                style={{
                  backgroundColor: hasData
                    ? `${theme.primary}20`
                    : theme.primary,
                  color: hasData ? theme.primary : "white",
                }}
              >
                {uploading && selectedMonth === month.key
                  ? "⏳ Uploaden..."
                  : hasData
                  ? "Vervang"
                  : "Upload"}
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
};


// 📄 Urenlijst-PDF's worden als base64-chunks in Firestore opgeslagen
// (net als het Handboek) i.p.v. via Firebase Storage — Storage-URLs worden
// op sommige bedrijfsnetwerken geblokkeerd. Nieuwe tabbladen/vensters
// (window.open) blijken op datzelfde netwerk óók stilletjes geblokkeerd te
// worden, dus "bekijken" gebeurt in-app via een <iframe>-modal i.p.v. een
// nieuw venster — geen nieuwe browsing-context nodig, kan dus niet worden
// tegengehouden.
const fetchReportBlob = async (report) => {
  const pdfChunksRef = collection(db, "pdf_chunks");
  const q = query(pdfChunksRef, where("pdfId", "==", report.pdfId));
  const snapshot = await getDocs(q);
  const chunks = [];
  snapshot.forEach((docSnap) => {
    const data = docSnap.data();
    chunks[data.chunkIndex] = data.data;
  });
  const base64 = chunks.join("");
  if (!base64) throw new Error("Bestand niet gevonden");
  const byteString = atob(base64.split(",")[1]);
  const bytes = Uint8Array.from(byteString, (c) => c.charCodeAt(0));
  return new Blob([bytes], { type: "application/pdf" });
};

// Simpele full-screen PDF-viewer modal, herbruikbaar in beide schermen.
const PdfViewerModal = ({ report, blobUrl, onClose }) => (
  <div className="fixed inset-0 z-[200] bg-[#00192F]/45 flex flex-col">
    <div className="flex items-center justify-between px-4 py-3 bg-white shrink-0">
      <span className="text-sm font-semibold truncate">{report?.filename}</span>
      <button aria-label="Sluiten" onClick={onClose} className="w-11 h-11 flex items-center justify-center flex-shrink-0 rounded-xl hover:bg-gray-100">
        <X size={20} />
      </button>
    </div>
    <iframe
      src={blobUrl}
      className="flex-1 w-full border-none bg-white"
      title={report?.filename || "PDF"}
    />
  </div>
);


const EditOrders = ({
  setAdminSubView,
  setView,
  appData,
  updateData,
  showConfirm,
  onPrev,
  onNext,
  onCalendar,
  currentDay,
  theme,
}) => {
  const orders = appData.orders || [];
  const [activeTab, setActiveTab] = useState("open");

  const STATUS_TABS = [
    { id: "open", label: "Nieuw", color: C.Lagoon, bg: C.Sky },
    { id: "ordered", label: "Besteld", color: C.Bark, bg: C.Honey },
    { id: "received", label: "Binnen", color: C.Pine, bg: C.Grass },
    { id: "all", label: "Alles", color: C.Stone, bg: "#f3f4f6" },
  ];

  const filtered =
    activeTab === "all" ? orders : orders.filter((o) => o.status === activeTab);

  const updateStatus = (id, status) =>
    updateData({
      orders: orders.map((x) => (x.id === id ? { ...x, status } : x)),
    });

  const deleteOrder = (id) =>
    showConfirm("Bestelling wissen?", () => {
      registerOrderRemoval(id);
      updateData({ orders: orders.filter((x) => x.id !== id) });
    });

  const receivedCount = orders.filter((o) => o.status === "received").length;
  const clearAllReceived = () =>
    showConfirm(
      `${receivedCount} bestelling${
        receivedCount === 1 ? "" : "en"
      } die "Binnen" staan wissen?`,
      () => {
        orders
          .filter((x) => x.status === "received")
          .forEach((x) => registerOrderRemoval(x.id));
        updateData({
          orders: orders.filter((x) => x.status !== "received"),
        });
      }
    );

  return (
    <AdminContainer
      title="Bestellingen"
      backAction={() => setAdminSubView("menu")}
      currentDay={currentDay}
      onPrev={onPrev}
      onNext={onNext}
      onCalendar={onCalendar}
      theme={theme}
    >
      {/* Status tabs met tellers */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {STATUS_TABS.map((tab) => {
          const count =
            tab.id === "all"
              ? orders.length
              : orders.filter((o) => o.status === tab.id).length;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="py-2.5 rounded-xl text-sm font-semibold border-2 transition-all flex flex-col items-center gap-0.5"
              style={{
                backgroundColor: active ? tab.bg : "white",
                borderColor: tab.color,
                color: tab.color,
              }}
            >
              <span className="text-xl font-semibold">{count}</span>
              <span className="text-xs">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {receivedCount > 0 && (
        <button
          onClick={clearAllReceived}
          className="w-full mb-4 py-2.5 rounded-xl text-sm font-semibold border-2 flex items-center justify-center gap-2"
          style={{
            borderColor: C.Pine,
            color: C.Pine,
            backgroundColor: C.Grass,
          }}
        >
          <Trash2 size={15} />
          Alle "Binnen" wissen ({receivedCount})
        </button>
      )}

      {/* Lijst */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <ShoppingCart size={32} className="mx-auto mb-2 opacity-30" />
          <p>Geen bestellingen.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((o) => {
            const tab =
              STATUS_TABS.find((s) => s.id === o.status) || STATUS_TABS[0];
            return (
              <div
                key={o.id}
                className="p-3 border rounded-xl bg-white flex justify-between items-center gap-3 shadow-sm"
                style={{ borderColor: tab.color + "50" }}
              >
                <div className="flex-1 min-w-0">
                  <div
                    className="font-semibold text-sm break-words"
                    style={{ color: C.Pine }}
                  >
                    {o.item}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5 flex-wrap">
                    <ProfileAvatar
                      name={o.who}
                      staffList={appData.staffList || []}
                      size={14}
                      color={C.Pine}
                    />
                    {o.who} · {o.date}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <StyledSelect
                    className="text-xs border rounded-lg px-2 py-1 font-semibold"
                    style={{
                      borderColor: tab.color,
                      color: tab.color,
                      backgroundColor: tab.bg,
                    }}
                    value={o.status}
                    onChange={(e) => updateStatus(o.id, e.target.value)}
                  >
                    <option value="open">Nieuw</option>
                    <option value="ordered">Besteld</option>
                    <option value="received">Binnen</option>
                  </StyledSelect>
                  <button aria-label="Verwijderen"
                    onClick={() => deleteOrder(o.id)}
                    style={{ color: C.Sunset }}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminContainer>
  );
};

// 🎯 WEEKFOCUS ADMIN — beheer focuspunten + tips (index-gebaseerd, stabiel)
const normalizeFocus = (raw) =>
  (raw || []).map((item) =>
    typeof item === "string"
      ? { title: item, tips: [], commits: [] }
      : {
          title: item.title || "",
          tips: item.tips || [],
          commits: item.commits || [],
        }
  );


// 📱 Vaste tabbalk onderin (mobiel) op alle pagina's — zelfde stijl als het dagbord
// 📲 Tip: zet de app op je beginscherm (alleen op telefoon, buiten de app-modus)
let _installPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    _installPrompt = e;
    window.dispatchEvent(new Event("cp-install-klaar"));
  });
}
const isStandalone = () =>
  (typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true)) ||
  false;
const InstallTip = () => {
  const [, setTick] = useState(0);
  const [weg, setWeg] = useState(() => {
    try {
      const t = Number(localStorage.getItem("cp_install_tip_weg") || 0);
      return Date.now() - t < 1000 * 60 * 60 * 24 * 14; // 14 dagen niet meer tonen
    } catch (e) {
      return false;
    }
  });
  const [uitleg, setUitleg] = useState(false);
  useEffect(() => {
    const f = () => setTick((x) => x + 1);
    window.addEventListener("cp-install-klaar", f);
    return () => window.removeEventListener("cp-install-klaar", f);
  }, []);
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isMobiel = /Android|iPhone|iPad|iPod/i.test(ua);
  if (weg || isStandalone() || !isMobiel || (!isIOS && !_installPrompt)) return null;
  const sluit = () => {
    setWeg(true);
    try {
      localStorage.setItem("cp_install_tip_weg", String(Date.now()));
    } catch (e) {}
  };
  const installeer = async () => {
    if (_installPrompt) {
      _installPrompt.prompt();
      try {
        await _installPrompt.userChoice;
      } catch (e) {}
      _installPrompt = null;
      sluit();
    } else setUitleg(true);
  };
  return (
    <div
      className="lg:hidden fixed left-3 right-3 z-[38] rounded-2xl bg-white border border-gray-100 shadow-lg px-3 py-2.5"
      style={{ bottom: "calc(164px + env(safe-area-inset-bottom, 0px))" }}
      data-noswipe
      role="dialog"
      aria-label="App op je beginscherm zetten"
    >
      <div className="flex items-center gap-3">
        <img src="/logo192.png" alt="" className="w-10 h-10 rounded-xl flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold" style={{ color: C.Lagoon }}>
            Zet de app op je beginscherm
          </p>
          <p className="text-xs text-gray-500">Opent sneller, zonder browserbalk</p>
        </div>
        <button
          onClick={installeer}
          className="h-10 px-3 rounded-xl text-sm font-semibold text-white flex-shrink-0 active:scale-95"
          style={{ backgroundColor: C.Pine }}
        >
          {_installPrompt ? "Installeer" : "Hoe?"}
        </button>
        <button
          onClick={sluit}
          aria-label="Sluiten"
          className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-500 flex-shrink-0"
        >
          <X size={18} />
        </button>
      </div>
      {uitleg && (
        <ol className="mt-2 pt-2 border-t border-gray-100 text-sm text-gray-800 space-y-1 list-decimal pl-5">
          <li>Open deze pagina in Safari</li>
          <li>
            Tik onderin op <span className="font-semibold">Deel</span> (vierkantje met pijl omhoog)
          </li>
          <li>
            Kies <span className="font-semibold">Zet op beginscherm</span> en tik op Voeg toe
          </li>
        </ol>
      )}
    </div>
  );
};

const AppTabBar = ({ view, setView, setCurrentDay, userRole, onMenu, meerOpen = false, chatCount = 0, dagbordBadge = 0, roosterBadge = 0, meerBadge = 0 }) => {
  const vandaag = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  };
  const items =
    userRole === "co"
      ? [
          { id: "schedule", label: "Programma", icon: IcProgramma },
          { id: "bingo", label: "Prize Night", icon: IcBingo },
          { id: "chat", label: "Chat", icon: IcChat, badge: chatCount },
          { id: "__meer", label: "Meer", icon: IcMeer },
        ]
      : [
          // Dagelijks gebruik: het Dagbord (taken, overdracht, briefing zitten daarin),
          // je eigen rooster, het weekprogramma en de chat. De rest staat onder Meer.
          { id: "dagbord", label: "Dagbord", icon: IcDagbord, badge: dagbordBadge, vandaag: true },
          { id: "rooster", label: "Rooster", icon: IcRooster, badge: roosterBadge },
          { id: "schedule", label: "Programma", icon: IcProgramma },
          { id: "chat", label: "Chat", icon: IcChat, badge: chatCount },
          { id: "__meer", label: "Meer", icon: IcMeer, badge: meerBadge },
        ];
  return (
    <nav
      aria-label="Hoofdmenu"
      className="md:hidden fixed bottom-0 inset-x-0 z-40 rounded-t-3xl"
      style={{
        backgroundColor: C.Pine,
        paddingBottom: "max(16px, env(safe-area-inset-bottom))",
        boxShadow: "0 -6px 24px rgba(0,25,47,0.22)",
      }}
      data-noswipe
    >
      <div className="flex items-end justify-around px-1 h-16">
        {items.map((t) => {
          const Icon = t.icon;
          const active = meerOpen ? t.id === "__meer" : t.id === view;
          return (
            <button
              key={t.id}
              onClick={() => {
                if (t.id === "__meer") return onMenu && onMenu();
                if (t.vandaag && setCurrentDay) setCurrentDay(vandaag());
                if (t.id === "schedule" && setCurrentDay) setCurrentDay(getFridayOfWeek(new Date()));
                setView(t.id);
              }}
              aria-current={active ? "page" : undefined}
              aria-label={t.badge > 0 ? `${t.label}, ${t.badge} nieuw` : t.label}
              className="flex-1 h-14 flex flex-col items-center justify-center gap-0.5 text-xs font-semibold"
              style={{ color: active ? "white" : "rgba(255,255,255,0.8)" }}
            >
              <span className="relative">
                <span
                  className="w-12 h-7 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: active ? "rgba(255,255,255,0.18)" : "transparent" }}
                >
                  <Icon size={19} />
                </span>
                {t.badge > 0 && (
                  <span
                    className="absolute -top-1 right-0 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold flex items-center justify-center"
                    style={{ backgroundColor: C.Blossom, color: "white", boxShadow: `0 0 0 2px ${C.Pine}` }}
                  >
                    {t.badge}
                  </span>
                )}
              </span>
              {t.label}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

const MobileMenu = ({
  isOpen,
  onClose,
  setView,
  handleLogout,
  isLoggedIn,
  setCurrentDay,
  userRole,
  theme, // eslint-disable-line no-unused-vars
  chatCount,
  bingoBadge,
  handoverBadge,
  dagbordBadge = 0,
  onGoToAdmin,
  appData, // eslint-disable-line no-unused-vars
  roostermakerRecht = null,
  afscheidZichtbaar = false,
  pietenZichtbaar = false,
}) => {
  if (!isOpen) return null;
  const co = userRole === "co";
  const ga = (target) => {
    setView(target);
    if (["dashboard", "dagbord", "checks", "hours"].includes(target)) setCurrentDay(new Date());
    if (target === "schedule") setCurrentDay(getFridayOfWeek(new Date()));
    onClose();
  };
  // "Meer": alles wat niet al in de tabbalk staat, in rustige groepen
  const groepen = [
    {
      kop: "Werk",
      items: [
        !co && { icon: IcDashboard, label: "Dashboard", target: "dashboard" },
        !co && (appData?.feestdagen?.instellingen?.open || userRole === "fm" || userRole === "admin") && { icon: IcFeest, label: "Feestdagen", target: "feestdagen" },
        !co && { icon: IcTaken, label: "Takenlijst", target: "checks" },
        roostermakerRecht && { icon: IcRoostermaker, label: "Roostermaker", target: "planner" },
        !co && { icon: IcUren, label: "Uren", target: "hours" },
        !co && { icon: IcBestellen, label: "Bestellen", target: "orders" },
        !co && { icon: IcBezetting, label: "Bezetting", target: "occupancy" },
        !co && { icon: IcOverdracht, label: "Overdracht archief", target: "handover", badge: handoverBadge },
      ],
    },
    {
      kop: "Team",
      items: [
        afscheidZichtbaar && { icon: IcAfscheid, label: "Afscheid", target: "afscheid" },
        pietenZichtbaar && { icon: IcPieten, label: "Pietenservice", target: "pietenservice" },
        userRole === "fm" && { icon: IcPolaroid, label: "Polaroids", target: "polaroids" },
        !co && { icon: IcTelefoon, label: "Telefoonlijst", target: "phonelist" },
        !co && { icon: IcHandboek, label: "Handboek", target: "handboek" },
        !co && { icon: IcBingo, label: "Prize Night", target: "bingo", badge: bingoBadge },
        co && { icon: IcKaarten, label: "Kaarten printen", target: "kaarten" },
        !co && { icon: IcKwaliteit, label: "Kwaliteit", target: "quality_visits" },
      ],
    },
    {
      kop: "Instellingen",
      items: [
        (userRole === "staff" || userRole === "fm") && { icon: IcThema, label: "Foto & thema", target: "user_settings" },
        userRole === "staff" && { icon: IcPincode, label: "Pincode", target: "change_pin" },
        (userRole === "fm" || userRole === "admin") && { icon: IcDisplay, label: "Display", target: "promo_screen" },
        (userRole === "fm" || userRole === "admin") && { icon: IcDisplay, label: "Schermen bedienen", target: "promo_bediening" },
        userRole === "fm" && { icon: IcPromo, label: "Promo", target: "promo_designer" },
      ],
    },
  ]
    .map((g) => ({ ...g, items: g.items.filter(Boolean) }))
    .filter((g) => g.items.length);

  return (
    // Ligt ónder de tabbalk (z-40), zodat die zichtbaar en bruikbaar blijft
    <div className="md:hidden fixed inset-0 z-[35]" data-noswipe>
      <div className="absolute inset-0 bg-[#00192F]/45" onClick={onClose} />
      <div
        role="dialog"
        aria-label="Meer"
        className="absolute inset-x-0 bg-white rounded-t-3xl shadow-xl flex flex-col animate-fade-in-up"
        style={{
          bottom: "calc(56px + max(16px, env(safe-area-inset-bottom)))",
          maxHeight: "calc(100vh - 140px)",
          paddingBottom: 36,
        }}
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <p className="text-base font-semibold" style={{ color: C.Lagoon }}>
            Meer
          </p>
          <button
            aria-label="Sluiten"
            onClick={onClose}
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: "#F3F4F6", color: C.Lagoon }}
          >
            <CloseIcon size={18} />
          </button>
        </div>
        <div className="overflow-y-auto px-3">
          {groepen.map((g) => (
            <div key={g.kop} className="mb-2">
              <div className="px-2 pt-1 pb-1 text-[9px] font-semibold uppercase tracking-wide text-gray-500">
                {g.kop}
              </div>
              <div className="grid grid-cols-4 gap-1">
                {g.items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <button
                      key={it.target}
                      onClick={() => ga(it.target)}
                      className="relative flex flex-col items-center gap-1.5 py-2 px-1 rounded-2xl active:bg-gray-50 active:scale-95 transition-transform"
                    >
                      <span
                        className="w-12 h-12 rounded-2xl flex items-center justify-center"
                        style={{ backgroundColor: C.PineLight, color: C.Pine }}
                      >
                        <Icon size={20} />
                      </span>
                      <span
                        className="text-xs font-semibold text-center leading-tight"
                        style={{ color: C.Lagoon }}
                      >
                        {it.label}
                      </span>
                      {it.badge > 0 && (
                        <span
                          className="absolute top-1 right-2 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold flex items-center justify-center text-white"
                          style={{ backgroundColor: C.Blossom }}
                        >
                          {it.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="px-4 pt-3 mt-1 border-t border-gray-100 flex gap-2">
          {!isLoggedIn ? (
            <button
              onClick={() => {
                setView("login");
                onClose();
              }}
              className="flex-1 h-11 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 active:scale-[0.98]"
              style={{ backgroundColor: C.Pine }}
            >
              <Lock size={16} /> Inloggen
            </button>
          ) : (
            <>
              {userRole === "fm" && (
                <button
                  onClick={() => {
                    onGoToAdmin();
                    onClose();
                  }}
                  className="flex-1 h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 bg-white border active:scale-[0.98]"
                  style={{ color: C.Pine, borderColor: `${C.Pine}55` }}
                >
                  <Settings size={16} /> Beheer
                </button>
              )}
              <button
                onClick={() => {
                  handleLogout();
                  onClose();
                }}
                className="flex-1 h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 border bg-white active:scale-[0.98]"
                style={{ borderColor: `${C.Sunset}55`, color: C.Sunset }}
              >
                <LogOut size={16} /> Uitloggen
              </button>
            </>
          )}
        </div>
        <p className="text-center text-[11px] text-gray-400 mt-2">{APP_VERSIE}</p>
      </div>
    </div>
  );
};

// 📸 Inlogfoto's: direct een kleine, bewaarde versie van dit toestel tonen;
// de echte foto via de eigen server (werkt ook op het CP-netwerk waar Firebase
// Storage geblokkeerd is) en daarna weer klein bewaren voor de volgende keer.
const INLOG_FOTO_SLEUTEL = "cp_loginFotos";
const inlogFotoCache = (() => {
  try {
    return JSON.parse(localStorage.getItem(INLOG_FOTO_SLEUTEL) || "{}") || {};
  } catch (e) {
    return {};
  }
})();
const fotoViaServer = (src) =>
  /^https:\/\/(firebasestorage\.googleapis\.com|storage\.googleapis\.com|[a-z0-9-]+\.firebasestorage\.app)\//i.test(src || "")
    ? `/api/foto?url=${encodeURIComponent(src)}`
    : src;
const bewaarInlogFoto = (name, src, img) => {
  try {
    const n = 128;
    const cv = document.createElement("canvas");
    cv.width = n;
    cv.height = n;
    const ctx = cv.getContext("2d");
    const z = Math.min(img.naturalWidth, img.naturalHeight);
    ctx.drawImage(img, (img.naturalWidth - z) / 2, (img.naturalHeight - z) / 2, z, z, 0, 0, n, n);
    inlogFotoCache[name] = { src: String(src).slice(0, 300), klein: cv.toDataURL("image/jpeg", 0.8) };
    localStorage.setItem(INLOG_FOTO_SLEUTEL, JSON.stringify(inlogFotoCache));
  } catch (e) {
    /* geen opslag of beveiligde afbeelding: dan niet bewaren */
  }
};
const InlogFoto = ({ name, photo, maat, groot, lp }) => {
  const bewaard = inlogFotoCache[name];
  const klein = bewaard && bewaard.src === String(photo).slice(0, 300) ? bewaard.klein : null;
  const [geladen, setGeladen] = React.useState(false);
  return (
    <div className={`${maat} rounded-full overflow-hidden relative flex items-center justify-center font-semibold ${groot ? "text-3xl" : "text-xl"}`} style={{ backgroundColor: lp.kaart2, color: lp.tekst }}>
      {klein ? (
        <img src={klein} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <span>{name.charAt(0)}</span>
      )}
      <img
        src={fotoViaServer(photo)}
        alt={name}
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
        style={{ opacity: geladen ? 1 : 0 }}
        onLoad={(e) => {
          setGeladen(true);
          if (!klein) bewaarInlogFoto(name, photo, e.currentTarget);
        }}
      />
    </div>
  );
};

// Inlogfoto in de kleur van iemands eigen thema (eigen component, zodat de hook veilig is)
const ThemaInlogFoto = ({ photo, kleur, ...rest }) => {
  const p = useThemaFoto(photo, kleur);
  return <InlogFoto photo={p} {...rest} />;
};

const LoginScreen = ({
  pin,
  setPin,
  handleLogin,
  staffList,
  loginMode,
  setLoginMode,
  selectedStaff,
  setSelectedStaff,
  theme,
  pinLoginError,
}) => {
  const t = theme || THEMES.general;
  const [showPin, setShowPin] = React.useState(false);
  const [buitenDonker, setBuitenDonker] = React.useState(() => isDonkerBuiten());
  React.useEffect(() => {
    const tik = setInterval(() => setBuitenDonker(isDonkerBuiten()), 60000);
    return () => clearInterval(tik);
  }, []);

  // Herstel laatste gebruiker
  React.useEffect(() => {
    const lastUser = localStorage.getItem("cp_loggedInUser");
    if (lastUser && !loginMode) {
      if (lastUser === "Maik") {
        setLoginMode("fm");
      } else if (lastUser === "Central Office") {
        setLoginMode("co");
      } else {
        setLoginMode("staff");
        setSelectedStaff(lastUser);
      }
    }
  }, []);

  // Toon numpad zodra iemand geselecteerd is
  React.useEffect(() => {
    if (loginMode === "fm" || loginMode === "co" || selectedStaff) {
      setShowPin(true);
    }
  }, [loginMode, selectedStaff]);

  // Auto-login bij 4 cijfers
  React.useEffect(() => {
    if (
      pin.length === 4 &&
      (loginMode === "fm" || loginMode === "co" || selectedStaff)
    ) {
      handleLogin();
    }
  }, [pin]);

  const addDigit = (digit) => {
    if (pin.length >= 4) return;
    setPin(pin + digit);
  };

  const selectPerson = (name) => {
    if (name === "Maik") {
      setLoginMode("fm");
      setSelectedStaff("");
    } else if (name === "Central Office") {
      setLoginMode("co");
      setSelectedStaff("");
    } else {
      setLoginMode("staff");
      setSelectedStaff(name);
    }
    setPin("");
  };

  const currentName =
    loginMode === "fm"
      ? "Maik"
      : loginMode === "co"
      ? "Central Office"
      : selectedStaff;

  // Bouw personen-lijst: Maik eerst, dan actieve medewerkers alfabetisch, CO als laatste
  const allPersons = [
    { name: "Maik", isSpecial: true },
    ...staffList
      .filter((s) => s.name !== "Maik" && !s.inactive && !teamVerborgen(s))
      .sort((a, b) => a.name.localeCompare(b.name)),
    { name: "Central Office", isSpecial: true },
  ];

  const getPhoto = (name) => {
    const s = staffList.find((x) => x.name === name);
    const firstName = name ? name.trim().split(" ")[0] : "";
    return (
      (name === "Maik" && CP_FM_FOTO?.profilePhoto) ||
      s?.profilePhoto ||
      SEED_PROFILE_PHOTOS[name] ||
      SEED_PROFILE_PHOTOS[firstName] ||
      null
    );
  };

  // ── Kleuren: toon-op-toon in het laatst gebruikte thema, licht of donker zoals ingesteld ──
  const lp = (() => {
    let id = "general";
    try {
      const x = localStorage.getItem("cp_laatste_thema");
      if (x && DONKER_PALET[x]) id = x;
    } catch (e) { /* geen opslag */ }
    const stand = leesDonker();
    let donker = stand === "aan";
    if (stand === "auto") {
      try { donker = window.matchMedia("(prefers-color-scheme: dark)").matches; } catch (e) { donker = false; }
    }
    // Na zonsondergang (tot zonsopkomst) is het inlogscherm altijd donker
    if (buitenDonker) donker = true;
    const acc = THEMA_ACCENT[id] || { donker: C.Pine, licht: C.PineLight, mid: C.Grass };
    if (donker) {
      const d = DONKER_PALET[id];
      return { id, donker, bg: d.bg, kaart: d.kaart, kaart2: d.kaart2, tekst: d.tekst, tekst2: d.tekst2, accent: d.accent, knop: d.knop, knopTekst: "#FFFFFF", vorm: d.kaart, rand: d.rand };
    }
    const l = TOONLICHT_PALET[id];
    return { id, donker, bg: l.bg, kaart: l.kaart, kaart2: l.kaart2, tekst: l.tekst, tekst2: l.tekst2, accent: acc.mid, knop: acc.donker, knopTekst: "#FFFFFF", vorm: acc.mid, rand: l.rand };
  })();
  const uur = new Date().getHours();
  const groet = uur < 6 ? "Goedenacht" : uur < 12 ? "Goedemorgen" : uur < 18 ? "Goedemiddag" : "Goedenavond";
  const [zoek, setZoek] = React.useState("");
  // Medewerkers die (nog) niet zichtbaar zijn voor het team (uit dienst /
  // verborgen) staan niet in de lijst. Ze verschijnen alleen als je je eigen
  // voornaam of volledige naam helemaal intypt, of op je eigen toestel.
  const verborgenGevonden = (() => {
    const q = zoek.trim().toLowerCase();
    let laatst = "";
    try {
      laatst = localStorage.getItem("cp_loggedInUser") || "";
    } catch (e) {}
    return staffList.filter((s) => {
      if (!(s.inactive || s.verborgen) || !s.name || s.name === "Maik") return false;
      const naam = s.name.trim().toLowerCase();
      return s.name === laatst || (q.length >= 2 && (naam === q || naam.split(" ")[0] === q));
    });
  })();
  const zichtbaar = [
    ...(zoek.trim()
      ? allPersons.filter((p) => p.name.toLowerCase().includes(zoek.trim().toLowerCase()))
      : allPersons),
    ...verborgenGevonden.filter((s) => !allPersons.some((p) => p.name === s.name)),
  ];
  // Let op: Avatar wordt als functie aangeroepen (geen hooks hierin!)
  const Avatar = ({ name, groot }) => {
    const photo = getPhoto(name);
    const maat = groot ? "w-16 h-16 sm:w-20 sm:h-20" : "w-16 h-16 md:w-20 md:h-20";
    if (photo) return <ThemaInlogFoto key={name} name={name} photo={photo} kleur={themaKleurVan(name, staffList)} maat={maat} groot={groot} lp={lp} />;
    return (
      <div className={`${maat} rounded-full flex items-center justify-center font-semibold ${groot ? "text-3xl" : "text-xl"}`} style={{ backgroundColor: lp.kaart2, color: lp.tekst }}>
        {name === "Central Office" ? <Monitor size={groot ? 36 : 26} color={lp.tekst} /> : name.charAt(0)}
      </div>
    );
  };
  const toetsStijl = { backgroundColor: lp.kaart2, color: lp.tekst };

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden" style={{ backgroundColor: lp.bg, color: lp.tekst }}>
      {/* Eén officiële Storytelling Shape (Pebbles), niet uitgerekt, zacht zwevend */}
      <div className="absolute pointer-events-none" style={{ width: 520, height: 520, right: -190, top: -170, zIndex: 0 }} aria-hidden="true">
        <CpVorm vorm="pebbles" kleur={lp.vorm} zweef className="w-full h-full" style={{ opacity: lp.donker ? 0.9 : 0.55 }} />
      </div>

      {/* Kop: geanimeerd logo + pil */}
      {/* Op het pincodescherm compacter (kleine telefoons: anders valt de 0 buiten beeld) */}
      <div className={`flex-shrink-0 flex flex-col items-center px-6 ${showPin ? "pt-5 pb-2" : "pt-10 pb-5"}`} style={{ position: "relative", zIndex: 1 }}>
        <CpLogoAnimatie kleur={lp.tekst} style={showPin ? { width: 44, height: 37 } : { width: 64, height: 54 }} />
        <p className={`login-subtitle-animate font-semibold leading-tight text-center ${showPin ? "mt-1.5 text-lg" : "mt-3 text-2xl"}`} style={{ color: lp.tekst }}>
          {groet}!
        </p>
        {!showPin && (
          <span className="login-subtitle-animate mt-2 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold" style={{ backgroundColor: lp.kaart, color: lp.tekst, animationDelay: "0.45s" }}>
            Kids & Entertainment · Limburgse Peel
          </span>
        )}
      </div>

      {!showPin ? (
        /* ── Wie ben je? ── */
        <div className="flex-1 min-h-0 px-4 pb-4 flex flex-col" style={{ position: "relative", zIndex: 1 }}>
          <div className="login-card-animate flex-1 min-h-0 w-full max-w-sm md:max-w-3xl lg:max-w-5xl mx-auto rounded-3xl flex flex-col overflow-hidden" style={{ backgroundColor: lp.kaart, animationDelay: "0.35s" }}>
            <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-3">
              <p className="text-lg font-semibold" style={{ color: lp.tekst }}>Wie ben jij?</p>
              {allPersons.length > 9 && (
                <input
                  value={zoek}
                  onChange={(e) => setZoek(e.target.value)}
                  placeholder="Zoek je naam"
                  className="h-10 w-36 md:w-56 rounded-full px-4 text-sm outline-none"
                  style={{ backgroundColor: lp.kaart2, color: lp.tekst, border: `1px solid ${lp.rand}` }}
                />
              )}
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-5">
              <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-x-3 gap-y-4 md:gap-6">
                {zichtbaar.map((person, personIdx) => {
                  const name = person.name;
                  const firstName = name.split(" ")[0];
                  const isSelected = currentName === name;
                  return (
                    <button
                      key={name}
                      onClick={() => selectPerson(name)}
                      className="login-card-animate flex flex-col items-center gap-2 active:scale-95 transition-transform"
                      style={{ animationDelay: `${0.45 + Math.min(personIdx, 18) * 0.04}s` }}
                    >
                      <div className="relative">
                        <div className="rounded-full p-1" style={{ backgroundColor: isSelected ? lp.knop : lp.kaart2 }}>
                          {Avatar({ name })}
                        </div>
                        {isSelected && (
                          <div className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: lp.knop }}>
                            <Check size={14} color={lp.knopTekst} />
                          </div>
                        )}
                      </div>
                      <span
                        className="rounded-full px-2.5 py-0.5 text-xs font-semibold text-center leading-tight max-w-full truncate"
                        style={{ backgroundColor: isSelected ? lp.knop : lp.kaart2, color: isSelected ? lp.knopTekst : lp.tekst }}
                      >
                        {name === "Central Office" ? "Central Office" : firstName}
                      </span>
                    </button>
                  );
                })}
              </div>
              {!zichtbaar.length && (
                <p className="text-sm text-center py-8" style={{ color: lp.tekst2 }}>Niemand gevonden. Check je spelling even.</p>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* ── Pincode ── */
        <div className="login-pin-animate flex-1 min-h-0 overflow-y-auto flex flex-col items-center px-4 pb-4" style={{ position: "relative", zIndex: 1 }}>
          <div className="my-auto w-full max-w-xs rounded-3xl px-5 pt-4 pb-4 flex flex-col items-center flex-shrink-0" style={{ backgroundColor: lp.kaart }}>
            <button
              onClick={() => {
                setShowPin(false);
                setPin("");
              }}
              className="flex flex-col items-center mb-3"
            >
              <div className="login-avatar-big-animate rounded-full p-1 mb-1.5" style={{ backgroundColor: lp.accent }}>
                {Avatar({ name: currentName || "", groot: true })}
              </div>
              <span className="font-semibold text-base sm:text-lg" style={{ color: lp.tekst }}>
                Hoi {currentName?.split(" ")[0]}!
              </span>
              <span className="mt-1 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold" style={{ backgroundColor: lp.kaart2, color: lp.tekst2 }}>
                <ChevronLeft size={12} /> Niet jij? Kies opnieuw
              </span>
            </button>

            <p className="text-sm mb-2" style={{ color: lp.tekst2 }}>
              {pinLoginError ? "Oeps, die klopt niet. Nog een keer!" : "Vul je pincode in"}
            </p>
            {/* Pincode-bolletjes */}
            <div className={`flex justify-center gap-4 mb-4 ${pinLoginError ? "pin-shake" : ""}`}>
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="w-4 h-4 rounded-full transition-all duration-200"
                  style={{
                    backgroundColor: pinLoginError ? C.Blossom : i < pin.length ? lp.knop : lp.kaart2,
                    border: `2px solid ${pinLoginError ? C.Blossom : i < pin.length ? lp.knop : lp.rand}`,
                    transform: i < pin.length && !pinLoginError ? "scale(1.2)" : "scale(1)",
                  }}
                />
              ))}
            </div>

            {/* Numpad */}
            <div className="login-numpad-animate grid grid-cols-3 gap-2 sm:gap-2.5 w-full">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <button key={n} onClick={() => addDigit(String(n))} className="h-11 sm:h-14 rounded-2xl text-xl font-semibold transition-transform active:scale-90" style={toetsStijl}>
                  {n}
                </button>
              ))}
              <button onClick={() => setPin("")} className="h-11 sm:h-14 rounded-2xl text-sm font-semibold transition-transform active:scale-90" style={{ backgroundColor: lp.kaart2, color: lp.tekst2 }}>
                Wis
              </button>
              <button onClick={() => addDigit("0")} className="h-11 sm:h-14 rounded-2xl text-xl font-semibold transition-transform active:scale-90" style={toetsStijl}>
                0
              </button>
              <button
                onClick={() => setPin(pin.slice(0, -1))}
                className="h-11 sm:h-14 rounded-2xl flex items-center justify-center transition-transform active:scale-90"
                style={{ backgroundColor: lp.knop, color: lp.knopTekst }}
                aria-label="Verwijder"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
                  <line x1="18" y1="9" x2="12" y2="15" />
                  <line x1="12" y1="9" x2="18" y2="15" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {!showPin && (
        <p className="flex-shrink-0 text-xs text-center pb-4" style={{ color: lp.tekst2, position: "relative", zIndex: 1 }}>
          © Center Parcs Limburgse Peel
        </p>
      )}
    </div>
  );
};


const ChangePinView = ({
  setView,
  appData,
  updateData,
  loggedInUserName,
  showAlert,
  theme,
}) => {
  const t = theme || THEMES.general;
  const [step, setStep] = useState("verify");
  const [inputPin, setInputPin] = useState("");
  const [tempNewPin, setTempNewPin] = useState("");
  const handlePress = (num) => {
    if (inputPin.length < 4) {
      const nextPin = inputPin + num;
      setInputPin(nextPin);
      if (nextPin.length === 4) setTimeout(() => processPin(nextPin), 100);
    }
  };
  const processPin = (pin) => {
    const user = appData.staffList.find((s) => s.name === loggedInUserName);
    if (step === "verify") {
      if (user.pin === pin) {
        setStep("new");
        setInputPin("");
      } else {
        showAlert("Huidige pincode is onjuist");
        setInputPin("");
      }
    } else if (step === "new") {
      setTempNewPin(pin);
      setStep("confirm");
      setInputPin("");
    } else if (step === "confirm") {
      if (pin === tempNewPin) {
        const newList = appData.staffList.map((s) =>
          s.name === loggedInUserName
            ? { ...s, pin: pin, forcePinChange: false }
            : s
        );
        updateData({ staffList: newList });
        showAlert("Pincode succesvol gewijzigd!");
        setView("dagbord");
      } else {
        showAlert("Pincodes komen niet overeen. Probeer opnieuw.");
        setStep("new");
        setTempNewPin("");
        setInputPin("");
      }
    }
  };
  return (
    <div className="flex flex-col items-center justify-center h-full p-4">
      <Card className="p-6 max-w-sm w-full shadow-2xl" borderColor={t.primary}>
        <h2
          className="text-xl font-semibold mb-2 text-center"
          style={{ color: t.primary }}
        >
          {step === "verify" && "Verifieer Huidige Code"}
          {step === "new" && "Kies Nieuwe Pincode"}
          {step === "confirm" && "Bevestig Nieuwe Pincode"}
        </h2>
        <div className="flex justify-center gap-2 h-4 mb-6">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className={`w-3 h-3 rounded-full transition-colors duration-200`}
              style={{
                backgroundColor: i < inputPin.length ? t.primary : "#e5e7eb",
              }}
            ></div>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3 w-full mb-2">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <button
              key={n}
              onClick={() => handlePress(n.toString())}
              className="h-12 border rounded-lg text-lg font-semibold hover:bg-white active:bg-gray-100 transition-colors"
              style={{ borderColor: t.secondary, color: t.primary }}
            >
              {n}
            </button>
          ))}
          <button
            onClick={() => setInputPin("")}
            className="h-12 rounded-lg font-semibold flex items-center justify-center border"
            style={{
              backgroundColor: "#FFB0D6",
              borderColor: "#FFB0D6",
              color: C.Sunset,
            }}
          >
            C
          </button>
          <button
            onClick={() => handlePress("0")}
            className="h-12 border rounded-lg text-lg font-semibold hover:bg-white active:bg-gray-100 transition-colors"
            style={{ borderColor: t.secondary, color: t.primary }}
          >
            0
          </button>
          <div className="w-full h-12"></div>
        </div>
        <button
          onClick={() => setView("dashboard")}
          className="w-full mt-4 py-2 text-sm text-gray-500 underline text-center"
        >
          Annuleren
        </button>
      </Card>
    </div>
  );
};


// 🌙 Weergave per apparaat: "toon" (licht, standaard) | "aan" (donker) | "auto" (volgt telefoon)
const DONKER_SLEUTEL = "cp_donker";
// Standaard is "toon" (toon-op-toon licht); de oude stand "uit" (gewoon licht) bestaat niet meer.
const leesDonker = () => {
  try {
    const v = localStorage.getItem(DONKER_SLEUTEL);
    return v === "aan" || v === "auto" ? v : "toon";
  } catch (e) {
    return "toon";
  }
};
const zetDonker = (stand) => {
  try { localStorage.setItem(DONKER_SLEUTEL, stand); } catch (e) { /* geen opslag */ }
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("cp-donker", { detail: stand }));
};
// Lichte modi: gedempte themakleur i.p.v. grijs (tekst/tekst2/tekst3) en, voor
// "toon licht", achtergrond + vakken in tinten van het thema.
const LICHT_PALET = {
  general: { tekst: "#10322C", tekst2: "#4B7268", tekst3: "#7FA197" },
  winter: { tekst: "#0B1F36", tekst2: "#4D5F7A", tekst3: "#8494AD" },
  spring: { tekst: "#3A1018", tekst2: "#85525C", tekst3: "#B08890" },
  summer: { tekst: "#2E2215", tekst2: "#7A6448", tekst3: "#A8957A" },
  autumn: { tekst: "#2E2215", tekst2: "#7A6448", tekst3: "#A8957A" },
};
const TOONLICHT_PALET = {
  general: { bg: "#DAFFD1", kaart: "#F4FFF0", kaart2: "#E7FBE0", kaart3: "#D2F3C9", rand: "rgba(4,94,81,.13)", tekst: "#045E51", tekst2: "#3D7468", tekst3: "#6E9A8F" },
  winter: { bg: "#D6E2FF", kaart: "#F5F8FF", kaart2: "#E8EEFF", kaart3: "#D0DCFF", rand: "rgba(0,25,47,.12)", tekst: "#00192F", tekst2: "#46597C", tekst3: "#7486A6" },
  spring: { bg: "#FFD3E6", kaart: "#FFF5F9", kaart2: "#FFE6F0", kaart3: "#FFCCE0", rand: "rgba(112,6,6,.12)", tekst: "#700606", tekst2: "#91495A", tekst3: "#B27A88" },
  summer: { bg: "#FFE7B0", kaart: "#FFFAEE", kaart2: "#FFF1D2", kaart3: "#FFE2A1", rand: "rgba(122,82,38,.15)", tekst: "#5C3B17", tekst2: "#85663F", tekst3: "#A88C66" },
  autumn: { bg: "#FFE7B0", kaart: "#FFFAEE", kaart2: "#FFF1D2", kaart3: "#FFE2A1", rand: "rgba(122,82,38,.15)", tekst: "#5C3B17", tekst2: "#85663F", tekst3: "#A88C66" },
};
// Andere thema's dan groen: Pine-familie → donker / licht / mid van het thema
const THEMA_ACCENT = {
  winter: { donker: "#00192F", licht: "#C4D4FF", mid: "#9DB4F6" },
  spring: { donker: "#700606", licht: "#FFB0D6", mid: "#FF699B" },
  summer: { donker: "#7A5226", licht: "#FFE2A1", mid: "#FFCA55" },
  autumn: { donker: "#7A5226", licht: "#FFE2A1", mid: "#FFCA55" },
};
const MODUS_VARS = ["--dk-accent", "--dk-knop", "--m-donker", "--m-licht", "--m-mid", "--dk-bg", "--dk-kaart", "--dk-kaart2", "--dk-kaart3", "--dk-rand", "--dk-tekst", "--dk-tekst2", "--dk-op-licht", "--lt-tekst", "--lt-tekst2", "--lt-tekst3", "--tl-bg", "--tl-kaart", "--tl-kaart2", "--tl-kaart3", "--tl-rand"];
const DONKER_PALET = {
  general: { accent: "#B1EDA6", knop: "#0A7B6A", bg: "#022C26", kaart: "#06463D", kaart2: "#0B5549", kaart3: "#12665A", rand: "rgba(218,255,209,.16)", tekst: "#F1FFEE", tekst2: "#A8D8B4", opLicht: "#022C26" },
  winter: { accent: "#9DB4F6", knop: "#2B4F86", bg: "#00111F", kaart: "#0A2440", kaart2: "#12305A", kaart3: "#1B3C6B", rand: "rgba(196,212,255,.18)", tekst: "#EEF3FF", tekst2: "#A9BCEB", opLicht: "#00192F" },
  spring: { accent: "#FFB0D6", knop: "#9A1B2B", bg: "#2A0405", kaart: "#470B10", kaart2: "#5A1219", kaart3: "#6B1A22", rand: "rgba(255,176,214,.18)", tekst: "#FFF0F5", tekst2: "#F2B3CB", opLicht: "#4A0404" },
  summer: { accent: "#FFCA55", knop: "#8F6230", bg: "#2B1B0B", kaart: "#472E15", kaart2: "#58391B", kaart3: "#694522", rand: "rgba(255,226,161,.18)", tekst: "#FFF6E0", tekst2: "#E9CF98", opLicht: "#3A240E" },
  autumn: { accent: "#FFCA55", knop: "#8F6230", bg: "#2B1B0B", kaart: "#472E15", kaart2: "#58391B", kaart3: "#694522", rand: "rgba(255,226,161,.18)", tekst: "#FFF6E0", tekst2: "#E9CF98", opLicht: "#3A240E" },
};

const UserSettingsView = ({
  setView,
  appData,
  updateData,
  loggedInUserName,
  theme,
}) => {
  const t = theme || THEMES.general;
  const currentUser = appData.staffList.find(
    (s) => s.name === loggedInUserName
  );
  const isFM = loggedInUserName === "Maik";
  const [donker, setDonker] = useState(leesDonker);
  const gekozen = isFM
    ? appData.settings.fmTheme || "general"
    : currentUser?.theme || "general";
  const currentTheme = gekozen === "autumn" ? "summer" : gekozen;

  const updateTheme = (newTheme) => {
    if (isFM) {
      updateData({ settings: { ...appData.settings, fmTheme: newTheme } });
    } else {
      const newList = appData.staffList.map((s) =>
        s.name === loggedInUserName ? { ...s, theme: newTheme } : s
      );
      updateData({ staffList: newList });
    }
  };

  return (
    <AdminContainer
      title="Foto & thema"
      backAction={() => setView("dashboard")}
      hideDateControls={true}
      theme={theme}
    >
      <ProfielFotoKaart appData={appData} updateData={updateData} loggedInUserName={loggedInUserName} />
      <Card className="p-4" borderColor={t.secondary}>
        <h3 className="font-semibold mb-4 text-gray-800">Kies je kleur</h3>
        <div className="grid grid-cols-1 gap-3">
          {Object.values(THEMES).filter((th) => !th.verborgen).map((th) => (
            <button
              key={th.id}
              onClick={() => updateTheme(th.id)}
              className={`p-4 rounded-xl border-2 flex items-center justify-between transition-all ${
                currentTheme === th.id
                  ? "border-[#045E51] bg-[#DAFFD1] ring-2 ring-[#DAFFD1]"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                  style={{ backgroundColor: th.primary }}
                >
                  Aa
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-800">{th.label}</div>
                  <div className="text-xs text-gray-500">
                    Toon-op-toon, licht en donker
                  </div>
                </div>
              </div>
              {currentTheme === th.id && (
                <CheckCircle className="text-[#045E51]" size={24} />
              )}
            </button>
          ))}
        </div>
      </Card>
      <Card className="p-4 mt-4" borderColor={t.secondary}>
        <h3 className="font-semibold mb-1 text-gray-800">Weergave</h3>
        <p className="text-xs text-gray-500 mb-3">Toon-op-toon in je themakleur: licht, of donker voor 's avonds. Automatisch volgt je telefoon. Geldt voor dit apparaat.</p>
        <div className="grid grid-cols-3 gap-2">
          {[["toon", "Licht"], ["aan", "Donker"], ["auto", "Automatisch"]].map(([k, l]) => (
            <button
              key={k}
              onClick={() => { zetDonker(k); setDonker(k); }}
              className={`py-3 rounded-xl border-2 text-sm font-semibold transition-all ${donker === k ? "border-[#045E51] bg-[#DAFFD1] text-[#045E51]" : "border-gray-200 text-gray-700"}`}
            >
              {l}
            </button>
          ))}
        </div>
      </Card>
    </AdminContainer>
  );
};


// 🎨 VIEW HEADER
const ViewHeader = ({
  title,
  subtitle,
  icon: Icon,
  theme,
  children,
  onBack,
  backLabel = "Terug",
  focusPunten = [],
}) => {
  // Zelfde opmaak als het dagbord: witte kaart, rustige rand, icoon in een
  // getint vlak, titel in Lagoon.
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-4">
      <div className="px-4 py-3 flex flex-col gap-2.5 md:flex-row md:items-center md:justify-between md:gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {onBack && (
            <button
              aria-label={backLabel || "Terug"}
              onClick={onBack}
              className="w-11 h-11 rounded-xl flex items-center justify-center active:scale-95 transition-transform flex-shrink-0"
              style={{ backgroundColor: C.PineLight, color: C.Pine }}
            >
              <ArrowLeft size={18} />
            </button>
          )}
          {Icon && (
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${C.Pine}1A` }}
            >
              <Icon size={18} style={{ color: C.Pine }} />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="text-[17px] font-semibold leading-tight truncate" style={{ color: C.Lagoon }}>
              {title}
            </h2>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5 truncate">{subtitle}</p>}
            {focusPunten && focusPunten.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {focusPunten.map((punt, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: C.PineLight, color: C.Pine }}
                  >
                    ▸ {punt}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        {children && <div className="w-full md:w-auto md:flex-shrink-0">{children}</div>}
      </div>
    </div>
  );
};

// 📅 IN-APP KALENDER MODAL
const CalendarModal = ({ currentDay, onSelect, onClose, theme }) => {
  const t = theme || THEMES.general;
  const logoColor = CP_LOGO_COLORS[t?.id] || "#b4d6a1";
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [viewMonth, setViewMonth] = React.useState(() => {
    const d = new Date(currentDay || new Date());
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const { year, month } = viewMonth;

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  // Maandag = 0
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = lastDay.getDate();

  const monthName = firstDay.toLocaleDateString("nl-NL", {
    month: "long",
    year: "numeric",
  });

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const prevMonth = () => {
    setViewMonth((v) => {
      if (v.month === 0) return { year: v.year - 1, month: 11 };
      return { year: v.year, month: v.month - 1 };
    });
  };
  const nextMonth = () => {
    setViewMonth((v) => {
      if (v.month === 11) return { year: v.year + 1, month: 0 };
      return { year: v.year, month: v.month + 1 };
    });
  };

  const selected = new Date(currentDay);
  selected.setHours(0, 0, 0, 0);

  const handleDay = (day) => {
    const d = new Date(year, month, day);
    onSelect(d);
    onClose();
  };

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-[#00192F]/45 p-0 md:p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-t-3xl md:rounded-3xl shadow-xl overflow-hidden animate-in slide-in-from-bottom-4 md:zoom-in-95 duration-200 bg-white"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {/* Header */}
        <div
          className="relative overflow-hidden border-b border-gray-100"
          style={{ backgroundColor: "white" }}
        >
          {false && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: `url(${PATTERN_BACKGROUNDS[t?.id]})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                opacity: 0.1,
              }}
            />
          )}
          <div className="relative z-10 flex items-center justify-between px-4 py-3">
            <button aria-label="Vorige"
              onClick={prevMonth}
              className="w-11 h-11 flex items-center justify-center flex-shrink-0 rounded-xl active:scale-95 transition-transform"
              style={{ backgroundColor: C.PineLight }}
            >
              <ChevronLeft size={18} style={{ color: C.Pine }} />
            </button>
            <div className="text-center">
              <div
                className="font-semibold text-base capitalize"
                style={{ color: C.Lagoon }}
              >
                {monthName}
              </div>
            </div>
            <button aria-label="Volgende"
              onClick={nextMonth}
              className="w-11 h-11 flex items-center justify-center flex-shrink-0 rounded-xl active:scale-95 transition-transform"
              style={{ backgroundColor: C.PineLight }}
            >
              <ChevronRight size={18} style={{ color: C.Pine }} />
            </button>
          </div>
        </div>

        {/* Weekdagen header */}
        <div className="grid grid-cols-7 px-3 pt-3 pb-1">
          {["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"].map((d) => (
            <div
              key={d}
              className="text-center text-xs font-semibold py-1"
              style={{ color: "#6B7280" }}
            >
              {d}
            </div>
          ))}
        </div>

        {/* Kalender grid */}
        <div className="px-3 pb-3">
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7">
              {week.map((day, di) => {
                if (!day) return <div key={di} />;
                const d = new Date(year, month, day);
                d.setHours(0, 0, 0, 0);
                const isToday = d.getTime() === today.getTime();
                const isSelected = d.getTime() === selected.getTime();
                const isWeekend =
                  (firstDay.getDay() === 0
                    ? (di + 1) % 7
                    : (di + (7 - ((firstDay.getDay() + 6) % 7))) % 7) >= 5;
                return (
                  <button
                    key={di}
                    onClick={() => handleDay(day)}
                    className="aspect-square flex items-center justify-center rounded-xl m-0.5 text-sm font-semibold transition-all active:scale-90"
                    style={{
                      backgroundColor: isSelected
                        ? C.Pine
                        : isToday
                        ? "transparent"
                        : "transparent",
                      color: isSelected
                        ? "white"
                        : isToday
                        ? C.Pine
                        : isWeekend
                        ? "#6B7280"
                        : C.Lagoon,
                      boxShadow:
                        isToday && !isSelected
                          ? `inset 0 0 0 2px ${C.Pine}`
                          : "none",
                    }}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Snelknoppen */}
        <div className="grid grid-cols-3 gap-2 px-3 pb-3">
          {[
            { label: "Gisteren", offset: -1 },
            { label: "Vandaag", offset: 0 },
            { label: "Morgen", offset: 1 },
          ].map(({ label, offset }) => {
            const d = new Date();
            d.setDate(d.getDate() + offset);
            d.setHours(0, 0, 0, 0);
            const isActive = d.getTime() === selected.getTime();
            return (
              <button
                key={label}
                onClick={() => {
                  onSelect(d);
                  onClose();
                }}
                className="h-11 rounded-xl text-sm font-semibold transition-all active:scale-95"
                style={{
                  backgroundColor: isActive ? C.Pine : C.PineLight,
                  color: isActive ? "white" : C.Pine,
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};


const QualityVisitWidget = ({ setView, theme }) => {
  return (
    <button
      onClick={() => setView("quality_visits")}
      className="bg-white rounded-xl shadow-sm border-2 hover:shadow-sm transition-all p-6 text-left w-full h-full flex flex-col justify-between"
      style={{ borderColor: theme.primary }}
    >
      <div className="flex items-center gap-3 mb-3">
        <div
          className="p-3 rounded-xl"
          style={{ backgroundColor: `${theme.primary}15` }}
        >
          <ClipboardCheck size={24} style={{ color: theme.primary }} />
        </div>
        <div>
          <h3 className="font-semibold text-gray-800">Quality Visits</h3>
          <p className="text-sm text-gray-500">Beoordelingen</p>
        </div>
      </div>

      <div className="text-xs font-semibold" style={{ color: theme.primary }}>
        Bekijk alle →
      </div>
    </button>
  );
};


const PROMO_TYPES = [
  {
    value: "entertainment",
    label: "Entertainment",
    icon: Music2,
    bg: "#B1EDA6",
    fg: "#045E51",
  },
  {
    value: "crea",
    label: "Crea",
    icon: PaletteIcon,
    bg: "#9DB4F6",
    fg: "#00192F",
  },
  {
    value: "springkussen",
    label: "Springkussen",
    icon: Wind,
    bg: "#FFCA55",
    fg: "#7A5226",
  },
  {
    value: "overig",
    label: "Overig",
    icon: Star,
    bg: "#FF699B",
    fg: "#700606",
  },
];

const CP_PAL = [
  { k: "Pine", h: "#045E51" },
  { k: "Grass", h: "#B1EDA6" },
  { k: "Lagoon", h: "#00192F" },
  { k: "Bark", h: "#7A5226" },
  { k: "Honey", h: "#FFCA55" },
  { k: "Sky", h: "#9DB4F6" },
  { k: "Blossom", h: "#FF699B" },
  { k: "Sunset", h: "#700606" },
  { k: "Zwart", h: "#0A0A0A" },
];


const PD_ColorRow = ({ label, value, onChange }) => (
  <div style={{ marginBottom: 11 }}>
    <p
      style={{
        fontSize: 10,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: C.TextSecondary,
        margin: "0 0 5px",
      }}
    >
      {label}
    </p>
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      {CP_PAL.map((c) => (
        <button
          key={c.k}
          aria-label={c.k} title={c.k}
          onClick={() => onChange(c.h)}
          style={{
            width: 24,
            height: 24,
            borderRadius: 6,
            backgroundColor: c.h,
            padding: 0,
            cursor: "pointer",
            border: value === c.h ? `3px solid ${C.Lagoon}` : `2px solid #ccc`,
            transform: value === c.h ? "scale(1.18)" : "scale(1)",
            boxShadow: value === c.h ? `0 0 0 2px ${C.Grass}` : "none",
            transition: "all 0.12s",
          }}
        />
      ))}
    </div>
  </div>
);


// CP Symbol (tree icon only) - themed per active theme
const CP_SYMBOL_COLORS = {
  general: "#b4d6a1", // Grass
  winter: "#9fb2dc", // Sky
  spring: "#700606", // Sunset
  summer: "#7A5226", // Bark
  autumn: "#fdc957", // Honey
};

const CPSymbol = ({
  color,
  themeId,
  size = 32,
  className = "",
  style = {},
}) => {
  const fill = color || CP_SYMBOL_COLORS[themeId] || "#b4d6a1";
  const svgContent =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 456 400"><path fill="__FILL__" d="M163.72,269.63c-43.71-7.42-76.13-45.41-76.13-90.23s32.42-82.81,76.13-90.23c.74-.13,1.29-.76,1.29-1.51V48.38c0-.93-.81-1.66-1.73-1.55-31.41,3.73-60.41,18.48-82.11,41.91-22.94,24.77-35.57,56.97-35.57,90.66s12.63,65.89,35.57,90.66c21.68,23.41,50.66,38.15,82.11,41.91,.92,.11,1.73-.62,1.73-1.55v-39.27c0-.75-.55-1.39-1.29-1.51Z"/><path fill="__FILL__" d="M275.78,45.85h-67.23c-.85,0-1.55,.69-1.55,1.55V199.74c0,.82-.64,1.5-1.47,1.55h-.13c-.74,.04-1.4-.46-1.58-1.17-8.98-36.16-23.56-50.96-49.97-50.96h-15.3c-.98,0-1.71,.9-1.52,1.86l12.45,45.01c.15,.72,.78,1.24,1.52,1.24h6.05c35.28,0,49.95,12.34,49.95,45.55v66.93c-.02,1.1-.04,2.26-.06,3.46-.19,7.72-1.03,26.65-4.65,39-.29,.98,.46,1.96,1.49,1.96h48.45c1.02,0,1.77-.98,1.49-1.96-3.32-11.31-4.3-28.13-4.59-36.81-.05-2.62-.09-5.08-.12-7.26v-65.39c.02-33.16,14.69-45.49,49.95-45.49h6.06c.73,0,1.37-.52,1.52-1.24l12.45-45.01c.2-.96-.54-1.86-1.52-1.86h-15.31c-26.41,0-40.99,14.8-49.97,50.96-.18,.72-.84,1.22-1.58,1.18h-.13c-.82-.05-1.45-.72-1.46-1.53h.01V89.39c-.01-.85,.68-1.55,1.53-1.55h25.23c51.05,0,92.61,41.05,92.63,91.5,.01,24.47-9.51,47.47-26.8,64.77-13.62,13.63-30.59,22.39-49.33,25.53-.74,.12-1.29,.76-1.29,1.51v39.26c0,.93,.81,1.66,1.73,1.55,29.61-3.51,57.36-16.93,78.59-38.17,25.23-25.24,39.11-58.79,39.09-94.48-.03-73.6-60.43-133.48-134.63-133.48Z"/></svg>`.replace(
      /__FILL__/g,
      fill
    );
  return (
    <div
      className={className}
      style={{
        width: size,
        height: size * (400 / 456),
        display: "inline-flex",
        flexShrink: 0,
        ...style,
      }}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
};

// 🌿 Laadscherm: het Limburgse Peel-logo kleurt mee met de echte voortgang
// (verbinden → team & instellingen → planning), zodat je ziet dat er iets gebeurt.
const LAAD_STAPPEN = [
  { pct: 6, tekst: "Verbinden…" },
  { pct: 35, tekst: "Team en instellingen laden…" },
  { pct: 65, tekst: "Planning van vandaag laden…" },
  { pct: 100, tekst: "Bijna klaar…" },
];
const LaadScherm = ({ stap = 0 }) => {
  const [sec, setSec] = React.useState(0);
  const [kruip, setKruip] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setSec((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  // Binnen een stap langzaam een beetje doorlopen (nooit voorbij de volgende stap)
  React.useEffect(() => {
    setKruip(0);
    const t = setInterval(() => setKruip((k) => Math.min(k + 2, 18)), 400);
    return () => clearInterval(t);
  }, [stap]);
  const i = Math.min(stap, LAAD_STAPPEN.length - 1);
  const huidig = LAAD_STAPPEN[i];
  const volgende = LAAD_STAPPEN[Math.min(i + 1, LAAD_STAPPEN.length - 1)];
  const pct = i >= 3 ? 100 : Math.min(huidig.pct + kruip, volgende.pct - 3);
  const tekst = huidig.tekst;
  // Inline stijlen: Tailwind is tijdens het laden mogelijk nog niet beschikbaar
  const vol = { position: "absolute", top: 0, right: 0, bottom: 0, left: 0 };
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 32px",
        backgroundColor: "#F0FDF4",
        fontFamily: "'Bagos', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
      role="status"
      aria-live="polite"
      aria-label={`App laden, ${Math.round(pct)} procent`}
    >
      <div style={{ position: "relative", width: "100%", maxWidth: 320, aspectRatio: "1718 / 463" }}>
        <CPLogo color="#CFE3DC" style={vol} />
        <div
          style={{
            ...vol,
            clipPath: `inset(0 ${100 - pct}% 0 0)`,
            WebkitClipPath: `inset(0 ${100 - pct}% 0 0)`,
            transition: "clip-path 0.6s ease-out, -webkit-clip-path 0.6s ease-out",
          }}
        >
          <CPLogo color="#045E51" style={{ width: "100%", height: "100%" }} />
        </div>
      </div>
      <div style={{ width: "100%", maxWidth: 320, marginTop: 32 }}>
        <div style={{ height: 6, borderRadius: 999, overflow: "hidden", backgroundColor: "#CFE3DC" }}>
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              borderRadius: 999,
              backgroundColor: "#045E51",
              transition: "width 0.6s ease-out",
            }}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 14, color: "#045E51" }}>
          <span>{tekst}</span>
          <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{Math.round(pct)}%</span>
        </div>
        {sec >= 6 && (
          <p style={{ fontSize: 12, textAlign: "center", marginTop: 16, color: "#7A5226" }}>
            {sec >= 9
              ? "Geen verbinding? De app opent zo met de laatst opgeslagen gegevens."
              : "Trage verbinding — nog even geduld."}
          </p>
        )}
      </div>
    </div>
  );
};

// CP Logo component - renders themed SVG inline
const CPLogo = ({ color = "#045e51", className = "", style = {} }) => {
  const svgContent =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1718 463" width="100%" height="100%" style="fill:__FILL_COLOR__"><g id="Limburgse_Peel"><g><path fill="__FILL_COLOR__" d="M374.19,289.23h15.25c-.44,4.05-.66,8.84-.66,14.37v53.5h37.36v11.16h-51.95v-79.04Z"/><path fill="__FILL_COLOR__" d="M434.44,287.47h14.26v12.82h-14.26v-12.82Zm.55,20.67h13.38v45.77c0,4.72,.22,9.51,.66,14.37h-14.04v-60.13Z"/><path fill="__FILL_COLOR__" d="M533.65,320.8c-1.51-1.95-3.7-2.93-6.58-2.93s-5.2,.99-7.41,2.98-3.92,4.75-5.14,8.29-1.82,7.52-1.82,11.94v27.19h-13.38v-38.8c0-3.83-.76-6.72-2.27-8.68-1.51-1.95-3.7-2.93-6.58-2.93s-5.29,.99-7.46,2.98c-2.17,1.99-3.87,4.75-5.08,8.29s-1.82,7.52-1.82,11.94v27.19h-13.38v-60.13h13.38v13.93c1.77-4.72,4.31-8.47,7.63-11.28,3.32-2.8,7.11-4.2,11.39-4.2,5.16,0,9.14,1.44,11.94,4.31,2.8,2.88,4.49,6.82,5.08,11.83,1.84-4.86,4.49-8.77,7.96-11.72,3.46-2.95,7.33-4.42,11.61-4.42,6.04,0,10.48,1.93,13.32,5.8,2.84,3.87,4.26,8.9,4.26,15.09v26.42c0,5.68,.22,10.46,.66,14.37h-14.04v-38.8c0-3.83-.76-6.72-2.27-8.68Z"/><path fill="__FILL_COLOR__" d="M584.44,366.45c-3.32-2.25-5.97-5.29-7.96-9.12v10.94h-13.38v-79.04h13.38v30.18c1.92-3.98,4.55-7.11,7.9-9.4,3.35-2.28,7.02-3.43,11-3.43,5.16,0,9.69,1.31,13.6,3.92,3.91,2.62,6.93,6.32,9.06,11.11,2.14,4.79,3.21,10.32,3.21,16.58s-1.09,11.68-3.26,16.47c-2.17,4.79-5.23,8.51-9.17,11.16-3.94,2.65-8.46,3.98-13.54,3.98-3.91,0-7.52-1.12-10.83-3.37Zm-5.86-16.41c1.4,2.73,3.28,4.88,5.64,6.47,2.36,1.58,4.9,2.38,7.63,2.38,4.49,0,8.14-1.9,10.94-5.69s4.2-8.79,4.2-14.98-1.4-11.18-4.2-14.98c-2.8-3.79-6.45-5.69-10.94-5.69-2.8,0-5.38,.83-7.74,2.49-2.36,1.66-4.22,3.89-5.58,6.69-1.36,2.8-2.04,5.82-2.04,9.06v5.3c0,3.24,.7,6.23,2.1,8.96Z"/><path fill="__FILL_COLOR__" d="M648.06,356.17c1.73,2.03,4.22,3.04,7.46,3.04,2.95,0,5.77-1.03,8.46-3.09,2.69-2.06,4.86-4.92,6.52-8.57,1.66-3.65,2.49-7.68,2.49-12.1v-27.3h13.38v45.77c0,4.72,.22,9.51,.66,14.37h-14.04v-15.47c-1.99,5.08-4.96,9.19-8.9,12.32-3.94,3.13-8.24,4.7-12.88,4.7-6.34,0-11.11-1.95-14.31-5.86-3.21-3.91-4.81-8.88-4.81-14.92v-40.9h13.38v39.02c0,3.98,.87,6.98,2.6,9.01Z"/><path fill="__FILL_COLOR__" d="M700.62,308.14h13.49v14.26c1.92-4.71,4.33-8.53,7.24-11.44s5.88-4.37,8.9-4.37c1.77,0,3.32,.28,4.64,.83s2.62,1.46,3.87,2.71l-5.42,12.16c-1.18-1.11-2.3-1.9-3.37-2.38-1.07-.48-2.3-.72-3.7-.72-1.92,0-3.81,.79-5.69,2.38-1.88,1.59-3.43,3.87-4.64,6.85s-1.82,6.39-1.82,10.22v16.8c0,5.31,.18,9.58,.55,12.82h-14.04v-60.13Z"/><path fill="__FILL_COLOR__" d="M746.55,387.78c-4.09-2.4-6.14-6.03-6.14-10.89,0-4.57,2.47-8.48,7.41-11.72-1.7-.96-3.04-2.21-4.03-3.76-1-1.55-1.49-3.32-1.49-5.3,0-2.51,.92-4.77,2.76-6.8,1.84-2.03,4.35-3.78,7.52-5.25-2.73-1.91-4.83-4.29-6.3-7.13-1.48-2.84-2.21-5.99-2.21-9.45,0-3.98,.99-7.55,2.98-10.72,1.99-3.17,4.77-5.65,8.35-7.46,3.57-1.81,7.64-2.71,12.21-2.71,3.02,0,5.54,.33,7.57,.99,2.03,.66,4,1.51,5.91,2.54,.81,.44,1.6,.81,2.38,1.11,.77,.29,1.49,.44,2.16,.44,3.09,0,4.64-2.47,4.64-7.41l10.94,3.54c-.08,3.02-1.11,5.27-3.1,6.74-1.99,1.47-4.46,2.21-7.41,2.21-.44,0-1.33-.07-2.65-.22,2.14,3.17,3.21,6.82,3.21,10.94s-1.01,7.57-3.04,10.78c-2.03,3.21-4.83,5.71-8.4,7.52-3.58,1.81-7.65,2.71-12.21,2.71-3.83,0-7.3-.62-10.39-1.88-2.58,1.47-3.87,3.24-3.87,5.31,0,1.33,.51,2.3,1.55,2.93,1.03,.63,2.43,.94,4.2,.94,1.47,0,3.1-.18,4.86-.55s3.5-.77,5.2-1.22c2.87-.74,5.38-1.31,7.52-1.71,2.14-.4,4.42-.61,6.85-.61,4.79,0,8.64,1.2,11.55,3.59,2.91,2.4,4.37,5.77,4.37,10.12,0,5.45-1.92,10.13-5.75,14.04-3.83,3.9-8.66,6.87-14.48,8.9-5.82,2.03-11.61,3.04-17.36,3.04-6.12,0-11.22-1.2-15.31-3.59Zm9.84-8.35c2.1,1.25,4.84,1.88,8.24,1.88s6.82-.55,10.06-1.66c3.24-1.11,5.86-2.62,7.85-4.53,1.99-1.92,2.98-4.09,2.98-6.52,0-1.69-.61-3.06-1.82-4.09-1.22-1.03-2.89-1.55-5.03-1.55-1.33,0-2.82,.18-4.48,.55s-3,.68-4.03,.94c-1.03,.26-1.77,.46-2.21,.61-2.43,.74-4.51,1.29-6.25,1.66-1.73,.37-3.37,.55-4.92,.55h-1.11c-1.62,1.77-2.43,3.87-2.43,6.3,0,2.65,1.05,4.61,3.15,5.86Zm3.76-43.11c1.88,2.36,4.37,3.54,7.46,3.54s5.49-1.18,7.41-3.54c1.92-2.36,2.87-5.31,2.87-8.84s-.96-6.47-2.87-8.79c-1.92-2.32-4.38-3.48-7.41-3.48s-5.58,1.16-7.46,3.48c-1.88,2.32-2.82,5.25-2.82,8.79s.94,6.48,2.82,8.84Z"/><path fill="__FILL_COLOR__" d="M816.68,365.84c-4.94-2.65-8.84-6.34-11.72-11.05l9.51-7.63c1.77,4.05,4.31,7.24,7.63,9.56,3.32,2.32,6.96,3.48,10.94,3.48,2.95,0,5.38-.68,7.3-2.04,1.92-1.36,2.87-3.26,2.87-5.69,0-1.7-.61-3.13-1.82-4.31s-2.69-2.12-4.42-2.82c-1.73-.7-4.29-1.56-7.68-2.6-4.42-1.33-8.03-2.62-10.83-3.87-2.8-1.25-5.21-3.09-7.24-5.53-2.03-2.43-3.04-5.6-3.04-9.51,0-3.32,.9-6.28,2.71-8.9,1.81-2.62,4.35-4.66,7.63-6.13,3.28-1.47,6.98-2.21,11.11-2.21,5.31,0,10.08,.94,14.32,2.82,4.24,1.88,7.9,5.03,11,9.45l-9.62,7.74c-1.4-3.39-3.58-5.97-6.52-7.74-2.95-1.77-6.01-2.65-9.17-2.65-2.65,0-4.81,.57-6.47,1.71-1.66,1.14-2.49,2.67-2.49,4.59,0,2.14,1.03,3.78,3.09,4.92,2.06,1.14,5.38,2.38,9.95,3.71,4.49,1.33,8.24,2.65,11.22,3.98,2.99,1.33,5.54,3.3,7.68,5.91,2.14,2.62,3.21,6.03,3.21,10.22,0,3.83-1.03,7.15-3.09,9.95-2.06,2.8-4.85,4.94-8.35,6.41-3.5,1.47-7.35,2.21-11.55,2.21-5.82,0-11.2-1.33-16.14-3.98Z"/><path fill="__FILL_COLOR__" d="M877.76,366.23c-4.39-2.39-7.85-5.86-10.39-10.39-2.54-4.53-3.81-9.93-3.81-16.19s1.22-12.23,3.65-17.25c2.43-5.01,5.82-8.9,10.17-11.66,4.35-2.76,9.32-4.14,14.92-4.14,8.77,0,15.37,3.08,19.79,9.23,4.42,6.15,6.63,14.5,6.63,25.04h-41.45c.29,5.9,1.86,10.54,4.7,13.93,2.84,3.39,6.39,5.09,10.67,5.09,3.83,0,7.15-1.25,9.95-3.76,2.8-2.51,4.75-5.6,5.86-9.29l10.94,7.19c-3.17,5.23-7.06,9.18-11.66,11.83-4.61,2.65-9.64,3.98-15.09,3.98s-10.48-1.2-14.87-3.59Zm27.36-34.77c-.44-4.64-1.77-8.31-3.98-11-2.21-2.69-5.16-4.04-8.84-4.04s-6.78,1.33-9.29,3.98c-2.51,2.65-4.2,6.34-5.08,11.05h27.19Z"/><path fill="__FILL_COLOR__" d="M950.55,289.23h31.39c5.38,0,10.19,.96,14.43,2.88,4.24,1.92,7.55,4.59,9.95,8.01,2.39,3.43,3.59,7.35,3.59,11.77s-1.24,8.49-3.7,11.99c-2.47,3.5-5.84,6.23-10.12,8.18-4.27,1.95-9.06,2.93-14.37,2.93h-16.58v18.9c0,5.68,.22,10.46,.66,14.37h-15.25v-79.04Zm28.96,34.6c4.35,0,7.92-1.11,10.72-3.32,2.8-2.21,4.2-5.08,4.2-8.62s-1.38-6.25-4.15-8.35c-2.76-2.1-6.25-3.15-10.45-3.15h-14.7v23.44h14.37Z"/><path fill="__FILL_COLOR__" d="M1027.98,366.23c-4.39-2.39-7.85-5.86-10.39-10.39-2.54-4.53-3.81-9.93-3.81-16.19s1.22-12.23,3.65-17.25c2.43-5.01,5.82-8.9,10.17-11.66,4.35-2.76,9.32-4.14,14.92-4.14,8.77,0,15.37,3.08,19.79,9.23,4.42,6.15,6.63,14.5,6.63,25.04h-41.45c.29,5.9,1.86,10.54,4.7,13.93,2.84,3.39,6.39,5.09,10.67,5.09,3.83,0,7.15-1.25,9.95-3.76,2.8-2.51,4.75-5.6,5.86-9.29l10.94,7.19c-3.17,5.23-7.06,9.18-11.66,11.83-4.61,2.65-9.64,3.98-15.09,3.98s-10.48-1.2-14.87-3.59Zm27.36-34.77c-.44-4.64-1.77-8.31-3.98-11-2.21-2.69-5.16-4.04-8.84-4.04s-6.78,1.33-9.29,3.98c-2.51,2.65-4.2,6.34-5.08,11.05h27.19Z"/><path fill="__FILL_COLOR__" d="M1092.42,366.23c-4.39-2.39-7.85-5.86-10.39-10.39-2.54-4.53-3.81-9.93-3.81-16.19s1.22-12.23,3.65-17.25c2.43-5.01,5.82-8.9,10.17-11.66,4.35-2.76,9.32-4.14,14.92-4.14,8.77,0,15.37,3.08,19.79,9.23,4.42,6.15,6.63,14.5,6.63,25.04h-41.45c.29,5.9,1.86,10.54,4.7,13.93,2.84,3.39,6.39,5.09,10.67,5.09,3.83,0,7.15-1.25,9.95-3.76,2.8-2.51,4.75-5.6,5.86-9.29l10.94,7.19c-3.17,5.23-7.06,9.18-11.66,11.83-4.61,2.65-9.64,3.98-15.09,3.98s-10.48-1.2-14.87-3.59Zm27.36-34.77c-.44-4.64-1.77-8.31-3.98-11-2.21-2.69-5.16-4.04-8.84-4.04s-6.78,1.33-9.29,3.98c-2.51,2.65-4.2,6.34-5.08,11.05h27.19Z"/><path fill="__FILL_COLOR__" d="M1145.21,289.23h13.38v64.67c0,4.72,.22,9.51,.66,14.37h-14.04v-79.04Z"/></g><g id="Center_Parcs"><path fill="__FILL_COLOR__" d="M122.46,193.29c-28.68-4.87-49.94-29.79-49.94-59.19s21.26-54.32,49.94-59.19c.49-.08,.85-.5,.85-.99v-25.76c0-.61-.53-1.09-1.14-1.01-20.6,2.45-39.63,12.12-53.86,27.49-15.05,16.25-23.33,37.37-23.33,59.47s8.29,43.22,23.33,59.47c14.22,15.36,33.23,25.03,53.86,27.49,.6,.07,1.14-.41,1.14-1.01v-25.76c0-.49-.36-.91-.85-.99Z"/><path fill="__FILL_COLOR__" d="M195.97,46.5h-44.1c-.56,0-1.01,.45-1.01,1.01v99.93c0,.54-.42,.99-.96,1.01h-.09c-.48,.03-.92-.3-1.03-.77-5.89-23.72-15.46-33.43-32.78-33.43h-10.04c-.64,0-1.12,.59-.99,1.22l8.17,29.53c.1,.47,.51,.81,.99,.81h3.97c23.14,0,32.76,8.1,32.76,29.88v43.91c-.01,.72-.02,1.49-.04,2.27-.13,5.06-.68,17.48-3.05,25.58-.19,.64,.3,1.28,.98,1.28h31.78c.67,0,1.16-.64,.98-1.28-2.18-7.42-2.82-18.45-3.01-24.15-.03-1.72-.06-3.34-.08-4.76v-42.9c.01-21.75,9.64-29.84,32.76-29.84h3.97c.48,0,.9-.34,.99-.81l8.17-29.53c.13-.63-.35-1.22-.99-1.22h-10.04c-17.32,0-26.89,9.71-32.78,33.43-.12,.47-.55,.8-1.03,.77h-.09c-.54-.03-.95-.47-.96-1.01h0V75.06c0-.56,.45-1.01,1.01-1.01h16.55c33.49,0,60.75,26.93,60.77,60.02,0,16.05-6.24,31.14-17.58,42.49-8.93,8.94-20.07,14.69-32.36,16.75-.49,.08-.85,.5-.85,.99v25.75c0,.61,.53,1.09,1.14,1.02,19.42-2.3,37.62-11.1,51.55-25.04,16.55-16.56,25.66-38.57,25.64-61.98-.02-48.28-39.64-87.56-88.31-87.56Z"/><path fill="__FILL_COLOR__" d="M581.96,88.35c-35.67,0-61.05,26.07-61.05,62.88s25.38,63.34,61.05,63.34c27.85,0,48.86-16.33,53.07-38.36,.12-.62-.37-1.2-1-1.2h-24.75c-.46,0-.86,.32-.98,.76-2.42,8.85-12.11,16.84-26.33,16.84-19.96,0-31.34-12.76-33.28-31.81-.06-.6,.42-1.12,1.02-1.12h85.44c.56,0,1.01-.45,1.01-1.01v-8.82c0-38.87-24.24-61.51-54.19-61.51Zm-32.18,52.82c-.63,0-1.11-.57-1-1.19,3.03-17.17,13.72-28.54,32.27-28.54,16.12,0,26.97,11.83,28.28,28.64,.05,.59-.43,1.09-1.02,1.09h-58.53Z"/><path fill="__FILL_COLOR__" d="M1498.37,111.45c14.31,0,25.12,8.74,27.52,20.67,.1,.48,.5,.82,.99,.82h24.72c.62,0,1.11-.56,1.01-1.18-4.14-25.01-25.41-43.41-53.32-43.41-35.9,0-61.05,26.07-61.05,62.88s25.15,63.34,61.05,63.34c28.14,0,49.18-18.17,53.32-42.95,.1-.62-.38-1.18-1.01-1.18h-24.71c-.49,0-.9,.36-.99,.84-2.19,11.69-12.79,20.2-27.53,20.2-19.21,0-32.93-16.46-32.93-40.24s13.72-39.79,32.93-39.79Z"/><path fill="__FILL_COLOR__" d="M1337.2,179.2v-44.16c0-29.59-14.67-45.77-49.06-45.77-29.9,0-46.79,13.99-51.64,35.93-.12,.54,.23,1.09,.77,1.21l23.83,5.37c.54,.12,1.08-.22,1.21-.76,3.2-13.26,11.23-20.01,24.81-20.01,16.69,0,23.01,8.85,23.01,24.28v7.08c0,.56-.45,1.01-1.01,1.01h-35.66c-30.1,0-42.99,15.43-42.99,36.67,0,23.27,14.92,33.38,36.16,33.38s34.02-11.11,40.29-29.55c.26-.77,.98-1.3,1.79-1.3h0c1.04,0,1.89,.83,1.89,1.88,.03,6.47,1.84,27.48,23.98,27.48h0c3.75,.04,8-.51,12.81-1.73,.45-.11,.77-.52,.77-.98l.21-17.09c0-.59-.49-1.06-1.07-1.02-8.27,.53-10.05-2.88-10.11-11.92Zm-28.33-13.05c0,14.92-12.64,26.05-31.61,26.05-15.43,0-20.74-7.33-20.74-15.93,0-9.86,7.84-14.92,21.5-14.92h29.84c.56,0,1.01,.45,1.01,1.01v3.79Z"/><path fill="__FILL_COLOR__" d="M1168.63,59.17h-59.94c-.56,0-1.01,.45-1.01,1.01V209.9c0,.56,.45,1.01,1.01,1.01h26.8c.56,0,1.01-.45,1.01-1.01v-48.04c0-.56,.45-1.01,1.01-1.01h32.11c35.66,0,55.13-21.75,55.13-50.58,0-33.89-18.46-51.09-56.14-51.09Zm-2.28,77.89h-28.83c-.56,0-1.01-.45-1.01-1.01v-51.33c0-.56,.45-1.01,1.01-1.01h28.57c22.51,0,30.35,8.35,30.35,26.81s-8.85,26.55-30.1,26.55Z"/><path fill="__FILL_COLOR__" d="M924.54,88.35c-35.54,0-60.82,25.97-60.82,62.65s25.29,63.1,60.82,63.1c27.74,0,48.68-16.26,52.87-38.21,.12-.62-.37-1.2-1-1.2h-24.65c-.46,0-.86,.32-.98,.76-2.41,8.81-12.06,16.78-26.23,16.78-19.89,0-31.22-12.71-33.16-31.68-.06-.6,.42-1.12,1.02-1.12h85.11c.56,0,1.01-.45,1.01-1.01v-8.78c0-38.73-24.15-61.28-53.99-61.28Zm-32.06,52.62c-.63,0-1.11-.57-1-1.19,3.02-17.11,13.67-28.43,32.15-28.43,16.06,0,26.87,11.79,28.18,28.52,.05,.59-.43,1.09-1.02,1.09h-58.3Z"/><path fill="__FILL_COLOR__" d="M856.12,186.69c0-.68-.65-1.16-1.3-.97-5.09,1.47-9.9,2.48-15.3,2.48-11.83,0-16.33-6.04-16.33-17.63v-54.51c0-.56,.45-1.01,1.01-1.01h30.9c.56,0,1.01-.45,1.01-1.01v-20.97c0-.56-.45-1.01-1.01-1.01h-30.9c-.56,0-1.01-.45-1.01-1.01v-29.98c0-.56-.45-1.01-1.01-1.01h-24.95c-.56,0-1.01,.45-1.01,1.01v29.98c0,.56-.45,1.01-1.01,1.01h-23.35c-.56,0-1.01,.45-1.01,1.01v20.97c0,.56,.45,1.01,1.01,1.01h23.35c.56,0,1.01,.45,1.01,1.01v49.1h0v9.19c0,22.16,11.08,39.28,37.01,39.28,9.7,0,15.52-1.02,22.15-2.91,.44-.12,.74-.53,.74-.98v-23.05Z"/><path fill="__FILL_COLOR__" d="M721.85,89.01c-19.7,0-34.72,12.04-41.45,33.47-.17,.55-.67,.92-1.25,.92h0c-.73,0-1.31-.59-1.31-1.31v-29.05c0-.56-.45-1.01-1.01-1.01h-25.03c-.56,0-1.01,.45-1.01,1.01v116.83c0,.56,.45,1.01,1.01,1.01h25.03c.56,0,1.01-.45,1.01-1.01v-66.24c0-17.96,12.65-30.1,30.85-30.1,16.44,0,22.76,10.62,22.76,25.04v71.31c0,.56,.45,1.01,1.01,1.01h25.03c.56,0,1.01-.45,1.01-1.01v-78.4c0-25.8-11.89-42.49-36.67-42.49Z"/><path fill="__FILL_COLOR__" d="M506.2,155.37h-26.91c-.48,0-.89,.35-.99,.82-.19,.93-.41,1.82-.65,2.68h0c-.14,.5-.29,1-.45,1.49-.03,.09-.06,.18-.09,.26-5.41,16.49-20.99,26.88-41.07,26.88-28.86,0-47.52-21.19-47.52-53.98,0-15.68,4.77-28.71,13.8-37.68,8.42-8.36,20.2-12.78,34.07-12.78h.45c19.23,.19,34.25,10.48,39.07,26.48h0c.21,.68,.4,1.38,.58,2.12,.11,.46,.51,.79,.99,.79h27.06c.63,0,1.1-.56,1-1.18-.11-.69-.27-1.61-.47-2.56h-.02c-3.02-15.16-10.53-28.01-21.9-37.31-12.3-10.07-28.19-15.39-45.93-15.39-21.93,0-41.73,7.82-55.75,22.01-14.03,14.2-21.75,34.25-21.75,56.47,0,47.13,32.03,80.05,77.89,80.05,17.46,0,33.33-5.61,45.88-16.22,10.11-8.55,17.42-19.82,21.51-32.96h.03c.87-2.44,1.77-6.72,2.18-8.8,.12-.63-.36-1.21-1-1.21Z"/><path fill="__FILL_COLOR__" d="M1673.03,177.53c0-50.99-77.06-25.38-77.06-52.36,0-10.06,8.69-17.15,23.78-17.15,16.08,0,26.06,7.86,27.15,21.01,.04,.53,.47,.94,1.01,.94h23.79c.6,0,1.07-.52,1.02-1.11-2.31-24.96-21.61-40.51-50.9-40.51-32.7,0-52.59,15.32-52.59,37.96,0,49.62,77.06,23.55,77.06,51.91,0,3.88-1.57,7.41-4.52,10.21-1.72,1.56-6.72,5.17-16.72,5.92-2.25,.12-4.32,.06-6.22-.13h0c-22.87-1.61-26.01-18.64-26.44-22.81-.05-.52-.49-.92-1.01-.92h-24.41c-.62,0-1.09,.55-1,1.16,.19,1.26,.52,3.32,.92,4.92h0c1.28,5.54,3.93,13.81,9.17,20.2h.01c5.25,6.61,12.95,11.55,22.69,14.54h0s7.91,2.7,19.54,3.22h0c.12,.01,.24,.01,.35,.01,6.94,.28,15.18-.23,23.84-2.55,.39-.1,.77-.22,1.16-.33,.32-.09,.64-.18,.97-.27h0c18.03-5.43,28.43-17.49,28.43-33.86Z"/><path fill="__FILL_COLOR__" d="M1389.11,92.03c.56,0,1.01,.45,1.01,1.01v33.36c0,.66,.49,1.21,1.14,1.28h0c.64,.07,1.24-.35,1.39-.98,6.82-27.83,17.84-35.93,34.75-35.93h8.64c.67,0,1.16,.64,.98,1.29l-7.94,28.46c-.12,.44-.52,.74-.98,.74h-2.97c-24.94,0-35.02,8.82-35.02,33.01v55.17c0,.56-.45,1.01-1.01,1.01h-24.93c-.56,0-1.01-.45-1.01-1.01V93.05c0-.56,.45-1.01,1.01-1.01h24.93Z"/><path fill="__FILL_COLOR__" d="M1019.54,92.03c.56,0,1.01,.45,1.01,1.01v33.36c0,.66,.49,1.21,1.14,1.28h0c.64,.07,1.24-.35,1.39-.98,6.82-27.83,17.84-35.93,34.75-35.93h8.64c.67,0,1.16,.64,.98,1.29l-7.94,28.46c-.12,.44-.52,.74-.98,.74h-2.97c-24.94,0-35.02,8.82-35.02,33.01v55.17c0,.56-.45,1.01-1.01,1.01h-24.93c-.56,0-1.01-.45-1.01-1.01V93.05c0-.56,.45-1.01,1.01-1.01h24.93Z"/></g></g></svg>`.replace(
      /__FILL_COLOR__/g,
      color
    );
  return (
    <div
      className={className}
      style={{ display: "block", overflow: "hidden", ...style }}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
};

// ── 1 April Grap Overlay ─────────────────────────────────────
// ── 1 April foto viewer (alleen voor Maik) ───────────────────

// ═══════════════════════════════════════════════════════════════════
// 🧩 DAGBORD — één dagoverzicht: programma + taken + verslagen +
// FM-briefing met leesbevestiging + open punten + gesprek van de dag.
// Oude overdracht-data (appData.handover) wordt alleen-lezen meegetoond.
//
// Datamodel (concurrency-safe via DEEP_MERGE_FIELDS):
//   dagbord[YYYY-MM-DD] = {
//     briefing:            { text, author, updatedAt }
//     read_<naam>:         { name, at }                 leesbevestiging briefing
//     rep_<actId>_<naam>:  { actId, actTitle, author, at, crowd, colleagues,
//                            notes, songs, bingoQs, quiz, story, finaleQs }
//     c_<id>:              { id, target, author, text, at, deleted? }
//   }                      target = "day" | "act:<actId>"
//   openPunten[id] = { id, title, afdeling, author, createdAt, assignee,
//                      status: "open"|"opgelost", resolvedBy, resolvedAt,
//                      deleted?, reacties: { [rid]: { author, text, at } } }
// ═══════════════════════════════════════════════════════════════════

const DB_AFDELINGEN = [
  { id: "alle", label: "Alles", bg: C.PineLight, text: C.Pine },
  { id: "entertainment", label: "Entertainment", bg: C.HoneyLight, text: C.Bark },
  { id: "crea", label: "Crea", bg: C.SkyLight, text: C.Lagoon },
  { id: "hotspot", label: "Hotspot", bg: C.GrassLight, text: C.Pine },
  { id: "springkussen", label: "Springkussen", bg: C.BlossomLight, text: C.Sunset },
];
const dbAfdeling = (id) =>
  DB_AFDELINGEN.find((a) => a.id === id) || DB_AFDELINGEN[0];

const DB_CROWD = [
  { label: "Erg rustig", bg: C.GrassLight, text: C.Pine },
  { label: "Rustig", bg: C.Grass, text: C.Pine },
  { label: "Normaal", bg: C.HoneyLight, text: C.Bark },
  { label: "Druk", bg: C.Honey, text: C.Lagoon },
  { label: "Erg druk", bg: C.BlossomLight, text: C.Sunset },
];

const dbSlug = (name = "") => String(name).trim().replace(/[^A-Za-z0-9]/g, "_");
const dbNewId = () =>
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
const dbTime = (ts) =>
  ts
    ? new Date(ts).toLocaleTimeString("nl-NL", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
// "14:05" als het op de getoonde dag was, anders "di 29 sep · 14:05"
const dbWhen = (ts, dateKey) => {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";
  if (toIsoDate(d) === dateKey) return dbTime(ts);
  const dag = d.toLocaleDateString("nl-NL", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  return `${dag} · ${dbTime(ts)}`;
};
const dbAgo = (ts) => {
  if (!ts) return "";
  const d0 = new Date(ts);
  d0.setHours(0, 0, 0, 0);
  const d1 = new Date();
  d1.setHours(0, 0, 0, 0);
  const days = Math.round((d1 - d0) / 86400000);
  if (days <= 0) return `vandaag ${dbTime(ts)}`;
  if (days === 1) return "gisteren";
  return `${days} dagen geleden`;
};
const dbStartMin = (t = "") => {
  const [h, m] = String(t).split("-")[0].trim().split(":").map(Number);
  return isNaN(h) ? 9999 : h * 60 + (m || 0);
};
const dbActAfdeling = (act) => {
  const t = (act.type || "").toLowerCase();
  const title = (act.title || "").toLowerCase();
  if (t === "crea") return "crea";
  if (t === "hotspot" || title.includes("hotspot")) return "hotspot";
  if (t === "springkussen" || title.includes("springkussen"))
    return "springkussen";
  return "entertainment";
};
const dbActType = (title = "") => {
  const t = title.toLowerCase();
  if (t.includes("disco")) return "disco";
  if (t.includes("bingo") && !t.includes("natuur")) return "bingo";
  if (t.includes("prize")) return "prizenight";
  if (t.includes("quiz")) return "quiz";
  if (t.includes("verhaal") || t.includes("voorlees")) return "verhaal";
  return "generic";
};
const DB_TYPE_FIELD = {
  disco: { key: "songs", label: "Liedjes gedraaid", multi: true },
  bingo: { key: "bingoQs", label: "Bingovragen", multi: true },
  prizenight: { key: "finaleQs", label: "Finalevragen", multi: true },
  quiz: { key: "quiz", label: "Welke quiz", multi: false },
  verhaal: { key: "story", label: "Welk verhaal", multi: false },
};
const DB_FIELD_LABELS = {
  songs: "Liedjes",
  bingoQs: "Bingovragen",
  finaleQs: "Finale",
  quiz: "Quiz",
  story: "Verhaal",
};

// 🎵 Kids Disco: vaste liedjeslijst (uit eerdere overdrachten) om af te vinken.
// Opgeslagen als gewone tekst ("Medley, Trein, …") zodat oude data blijft werken.
const DISCO_LIEDJES = [
  ["Openingslied", /^(opening(slied)?( volledig)?|opkomst)$/],
  ["Medley", /^medley$/],
  ["Hokey Cokey", /^(ho(c)?key\s*(c|sn)?o(c)?key|hokey|hockey|hockey coke|hokey cokey medley)$/],
  ["Trein (Tjoek Tjoek)", /^(de )?trein( medley)?$|^tjoek( tjoek)?$/],
  ["Tsjoe Tsjoe Wa", /^(tsjoe tsjoe wa|tjoe tjoe wah?)( medley)?$/],
  ["Bewegen is gezond", /^bewegen is gezond$/],
  ["Muzikant", /^muzi(e)?kant$/],
  ["Berenstap", /^berenstap$/],
  ["Tante Rita", /^(dansen met )?tante rita$|^rita$/],
  ["Olifant", /^olifant$/],
  ["Apenstop", /^apenstop(dans)?$/],
  ["Kippenstop", /^kippenstop(dans)?$/],
  ["Krabbenstop", /^krabbenstop$/],
  ["Baby Shark", /^baby (shark|haai)( ?2x)?$|^babyhaai$/],
  ["Ze kunnen zeggen", /^ze kunnen zeggen( wat ze willen)?$/],
  ["Wielen van de bus", /^(wielen (van de|vd) bus|wielen ?bus|bus)( dance)?$/],
  ["Kopje", /^kopje$/],
  ["Superman", /^superman$/],
  ["Rock de Klok", /^rock de (clock|klok)$/],
  ["Vliegerlied", /^(vlieger|flieger)(lied)?$/],
  ["Soco Bate Vira", /^soco ba(te|ti)( vira)?$/],
  ["Aya Jippie", /^(aya ?jippie|ayayippie|aiajippie|ai ?a ?jippie)$/],
  ["Maya dans", /^maya( dans)?$/],
  ["Ola Olé", /^ola ole$/],
  ["Baila Baila", /^baila baila$/],
  ["Ritmo Vuelta", /^ritmo v(u)?elta$/],
  ["Hoofd-schouders-knie-teen", /^hoofd( schouders.*)?$/],
  ["Waterval", /^waterval$/],
  ["Toeter waterscooter", /^toeter waterscooter$/],
  ["Happy Birthday", /^happy birthday$/],
  ["Sterretjes", /^sterretjes$/],
  ["Maneschijn", /^man(e|en|an)schijn( medley)?$/],
  ["Bella Ciao", /^bella c(iao|hau)$/],
  ["Bedpret", /^bedpret$/],
  ["Slotlied (finale)", /^(slot(lied)?|finale( ?lied)?|finalelied)$/],
];
const VERHALEN = [
  ["Op een slaapfeestje slaap je niet", /slaap ?feest|^orry$/],
  ["Pech onderweg", /pech onderweg/],
  ["Woops en de zeemeermin", /zeemeermin/],
  ["Woops de dansende inktvis", /^woops|inktvis|octop/],
  ["Bing in de jungle", /bing in de jungle/],
  ["Kamperen met Bing", /kamperen/],
  ["Vallende sterren (Bing)", /vallende sterren/],
  ["Vuur in de boomhut (Bing)", /boomhut/],
  ["Baas van Luilekkerland", /luilekkerland/],
  ["Rep's Springmachine", /springmachine/],
  ["Kleine boekje", /(kleine|mini) boekje/],
];
// Instellingen per soort lijst (liedjes bij Kids Disco, verhalen bij voorlezen)
const KIEZER_CFG = {
  songs: {
    items: DISCO_LIEDJES,
    split: (tekst) =>
      String(tekst || "")
        .replace(/ho(c)?key\.\s*co(c)?key/gi, "hokey cokey")
        .replace(/\.\s+(?=\S)/g, ", ")
        .split(/,|\n|\/| en (?=[a-z])/i),
    eerst: "Openingslied",
    laatst: "Slotlied (finale)",
    nieuwTekst: "Ander liedje toevoegen…",
    eenheid: ["liedje", "liedjes"],
    volgorde: true,
  },
  story: {
    items: VERHALEN,
    split: (tekst) => String(tekst || "").split(/,|\n|\+/),
    nieuwTekst: "Ander verhaal toevoegen…",
    eenheid: ["verhaal", "verhalen"],
    volgorde: false,
  },
};
const kiezerNormaliseer = (cfg, naam) => {
  const k = String(naam || "")
    .toLowerCase()
    .replace(/[.!]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!k) return null;
  for (const [label, re] of cfg.items) if (k === label.toLowerCase() || re.test(k)) return label;
  // onbekend: netjes met hoofdletter
  return String(naam).trim().replace(/\.$/, "").replace(/\s+/g, " ").replace(/^./, (c) => c.toUpperCase());
};
const kiezerDelen = (cfg, tekst) => cfg.split(tekst).map((x) => x.trim()).filter(Boolean);
// Hoe vaak is iets gekozen (oude + nieuwe overdrachten) → volgorde van de lijst
const kiezerTelling = (cfg, veld, appData) => {
  const tel = {};
  const add = (txt) =>
    kiezerDelen(cfg, txt).forEach((n) => {
      const l = kiezerNormaliseer(cfg, n);
      if (l) tel[l] = (tel[l] || 0) + 1;
    });
  for (const day of Object.values(appData?.handover || {}))
    for (const arr of Object.values(day || {}))
      if (Array.isArray(arr))
        for (const e of arr) for (const af of Object.values(e?.actFields || {})) if (af?.[veld]) add(af[veld]);
  for (const day of Object.values(appData?.dagbord || {}))
    for (const v of Object.values(day || {})) if (v && !v.deleted && v[veld]) add(v[veld]);
  return tel;
};
const LijstKiezer = ({ veld, value, onChange, appData }) => {
  const cfg = KIEZER_CFG[veld];
  const [nieuw, setNieuw] = useState("");
  const gekozen = React.useMemo(() => {
    const out = [];
    kiezerDelen(cfg, value).forEach((n) => {
      const l = kiezerNormaliseer(cfg, n);
      if (l && !out.includes(l)) out.push(l);
    });
    return out;
  }, [value, cfg]);
  const lijst = React.useMemo(() => {
    const tel = kiezerTelling(cfg, veld, appData);
    const vast = cfg.items.map(([l]) => l);
    // eigen keuzes die vaker dan één keer voorkwamen ook tonen
    const extra = Object.keys(tel).filter((l) => !vast.includes(l) && tel[l] >= 2);
    const volg = (l) => (l === cfg.eerst ? -1e6 : l === cfg.laatst ? 1e6 : -(tel[l] || 0));
    return [...vast, ...extra].sort((x, y) => volg(x) - volg(y));
  }, [appData, cfg, veld]);
  const zet = (arr) => onChange(arr.join(", "));
  const wissel = (l) => zet(gekozen.includes(l) ? gekozen.filter((x) => x !== l) : [...gekozen, l]);
  const voegToe = () => {
    const delen = kiezerDelen(cfg, nieuw).map((n) => kiezerNormaliseer(cfg, n)).filter(Boolean);
    if (!delen.length) return;
    zet([...gekozen, ...delen.filter((d) => !gekozen.includes(d))]);
    setNieuw("");
  };
  const zichtbaar = [...lijst, ...gekozen.filter((g) => !lijst.includes(g))];
  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {zichtbaar.map((l) => {
          const nr = gekozen.indexOf(l);
          const aan = nr >= 0;
          return (
            <button
              key={l}
              type="button"
              onClick={() => wissel(l)}
              aria-pressed={aan}
              className="min-h-[34px] px-2.5 rounded-full text-xs font-semibold border flex items-center gap-1 text-left"
              style={
                aan
                  ? { backgroundColor: C.Pine, color: "white", borderColor: C.Pine }
                  : { backgroundColor: "white", color: "#374151", borderColor: "#E5E7EB" }
              }
            >
              {aan &&
                (cfg.volgorde ? (
                  <span className="w-4 h-4 rounded-full bg-white/25 text-[10px] flex items-center justify-center">
                    {nr + 1}
                  </span>
                ) : (
                  <Check size={12} />
                ))}
              {l}
            </button>
          );
        })}
      </div>
      <div className="flex gap-1.5 mt-2">
        <input
          value={nieuw}
          onChange={(e) => setNieuw(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              voegToe();
            }
          }}
          placeholder={cfg.nieuwTekst}
          className="flex-1 min-w-0 border border-gray-200 rounded-lg px-2.5 py-1.5 text-base sm:text-sm outline-none bg-white"
        />
        <button
          type="button"
          onClick={voegToe}
          aria-label="Toevoegen"
          className="px-3 rounded-lg text-sm font-semibold text-white flex-shrink-0"
          style={{ backgroundColor: C.Pine, opacity: nieuw.trim() ? 1 : 0.4 }}
        >
          <Plus size={15} />
        </button>
      </div>
      {gekozen.length > 0 && (
        <p className="text-xs text-gray-500 mt-1.5">
          {gekozen.length} {gekozen.length === 1 ? cfg.eenheid[0] : cfg.eenheid[1]}
          {cfg.volgorde ? " · nummer = volgorde" : ""}
        </p>
      )}
    </div>
  );
};

const DbCrowdChip = ({ lvl }) => {
  const c = DB_CROWD[(lvl || 0) - 1];
  if (!c) return null;
  return (
    <span
      className="text-xs font-semibold px-2 py-0.5 rounded-full"
      style={{ backgroundColor: c.bg, color: c.text }}
    >
      {c.label}
    </span>
  );
};

// Vaste statuslabels met vaste kleuren: straks / nu / klaar / mist
const DB_STATUS = {
  straks: { bg: "#F3F4F6", fg: "#4B5563", label: "straks" },
  nu: { bg: C.Honey, fg: C.Lagoon, label: "nu" },
  klaar: { bg: C.Grass, fg: C.Pine, label: "klaar" },
  mist: { bg: C.Sunset, fg: "white", label: "mist" },
  // Zelfde labels in de rest van de app
  nieuw: { bg: C.SkyLight || "#E8EEFD", fg: C.Lagoon, label: "nieuw" },
  besteld: { bg: C.Honey, fg: C.Lagoon, label: "besteld" },
  binnen: { bg: C.Grass, fg: C.Pine, label: "binnen" },
  wacht: { bg: C.Honey, fg: C.Lagoon, label: "wacht op FM" },
  ingediend: { bg: C.Honey, fg: C.Lagoon, label: "ingediend" },
  verwerkt: { bg: C.Grass, fg: C.Pine, label: "verwerkt" },
  goedgekeurd: { bg: C.Grass, fg: C.Pine, label: "goedgekeurd" },
  afgekeurd: { bg: C.Sunset, fg: "white", label: "afgekeurd" },
};
const DbStatus = ({ kind, extra }) => {
  const st = DB_STATUS[kind] || DB_STATUS.straks;
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0"
      style={{ backgroundColor: st.bg, color: st.fg }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: st.fg, opacity: 0.8 }} />
      {st.label}
      {extra ? <span className="font-normal">{extra}</span> : null}
    </span>
  );
};

const DbPill = ({ children, bg, text }) => (
  <span
    className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
    style={{ backgroundColor: bg, color: text }}
  >
    {children}
  </span>
);

// 📷 Foto's bij berichten: als base64 in Firestore (zelfde collectie als de
// urenlijst-PDF's), zodat het ook werkt op netwerken die Firebase Storage blokkeren.
const dbPhotoCache = new Map(); // id → dataURL
const dbBlobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
const dbUploadPhoto = async (file) => {
  let dataUrl = await dbBlobToDataUrl(await compressImage(file, 1200, 0.7));
  if (dataUrl.length > 850000)
    dataUrl = await dbBlobToDataUrl(await compressImage(file, 800, 0.55));
  if (dataUrl.length > 950000) throw new Error("Foto is te groot");
  const id = `dbfoto-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  await setDoc(doc(collection(db, "pdf_chunks"), `${id}-chunk-0`), {
    pdfId: id,
    chunkIndex: 0,
    totalChunks: 1,
    data: dataUrl,
    kind: "dagbord-foto",
    createdAt: new Date().toISOString(),
  });
  dbPhotoCache.set(id, dataUrl);
  return id;
};
const dbLoadPhoto = async (id) => {
  if (dbPhotoCache.has(id)) return dbPhotoCache.get(id);
  const snap = await getDoc(doc(db, "pdf_chunks", `${id}-chunk-0`));
  const data = snap.exists() ? snap.data().data : null;
  if (data) dbPhotoCache.set(id, data);
  return data;
};
const DbPhoto = ({ id, size = 64 }) => {
  const [src, setSrc] = useState(() => dbPhotoCache.get(id) || null);
  const [failed, setFailed] = useState(false);
  const [big, setBig] = useState(false);
  useEffect(() => {
    let alive = true;
    if (!src)
      dbLoadPhoto(id)
        .then((d) => alive && (d ? setSrc(d) : setFailed(true)))
        .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [id]); // eslint-disable-line
  return (
    <>
      <button
        onClick={() => src && setBig(true)}
        className="rounded-lg overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center flex-shrink-0"
        style={{ width: size, height: size }}
      >
        {src ? (
          <img src={src} alt="" className="w-full h-full object-cover" />
        ) : failed ? (
          <ImageIcon size={16} className="text-gray-500" />
        ) : (
          <Loader2 size={14} className="animate-spin text-gray-500" />
        )}
      </button>
      {big && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.85)" }}
          onClick={() => setBig(false)}
        >
          <img src={src} alt="" className="max-w-full max-h-full rounded-xl" />
          <button aria-label="Sluiten"
            onClick={() => setBig(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center"
          >
            <X size={20} />
          </button>
        </div>
      )}
    </>
  );
};

// Leesbevestiging: knop "Gelezen" + wie het al gezien heeft
const DbReceipt = ({ reads, author, me, staffList, onRead, small = false }) => {
  const names = Object.values(reads || {})
    .filter((r) => r && r.name)
    .map((r) => r.name);
  const iRead = author === me || names.includes(me);
  return (
    <div className="flex items-center gap-2 flex-wrap mt-1.5">
      {!iRead && onRead && (
        <button
          onClick={onRead}
          className={`rounded-lg font-semibold flex items-center gap-1 ${
            small ? "px-2 py-0.5 text-xs border" : "px-3 py-1.5 text-xs text-white"
          }`}
          style={
            small
              ? { backgroundColor: "white", color: C.Pine, borderColor: `${C.Pine}66` }
              : { backgroundColor: C.Pine }
          }
        >
          <Eye size={small ? 11 : 13} /> Gelezen
        </button>
      )}
      {names.length > 0 && (
        <div className="flex -space-x-1.5">
          {names.slice(0, 8).map((n) => (
            <div key={n} className="rounded-full ring-2 ring-white" title={n}>
              <ProfileAvatar name={n} staffList={staffList} size={small ? 18 : 22} />
            </div>
          ))}
        </div>
      )}
      <span className="text-xs text-gray-500">
        {names.length === 0
          ? "Nog door niemand gelezen"
          : `Gelezen door ${names.slice(0, 8).join(", ")}${
              names.length > 8 ? ` +${names.length - 8}` : ""
            }`}
      </span>
    </div>
  );
};

// Reactie-draad (gebruikt bij activiteiten, gesprek van de dag en open punten)
const DbThread = ({
  items,
  staffList,
  loggedInUserName,
  onSend,
  onDelete,
  onRead,
  placeholder = "Schrijf een reactie… (@naam om te taggen)",
  emptyText,
  compact = false,
  groupOf, // (bericht) => kopje; toont een kopje zodra de groep wisselt
  inputExtra, // extra knoppen boven het invoerveld
  onEdit, // (bericht, nieuweTekst, categorie?) => opslaan
  canModerate = false, // FM mag ook andermans berichten bewerken/verwijderen
  categoryOf, // (bericht) => afdeling-id; toont een label + kiezen bij bewerken
  allowPhotos = false, // foto's toevoegen aan een bericht
  receiptAll = false, // leesbevestiging bij elk bericht (niet alleen "Belangrijk")
}) => {
  const [text, setText] = useState("");
  const [important, setImportant] = useState(false);
  const [files, setFiles] = useState([]); // [{ file, url }]
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const addFiles = (list) => {
    const nieuw = Array.from(list || [])
      .filter((f) => f.type?.startsWith("image/"))
      .map((file) => ({ file, url: URL.createObjectURL(file) }));
    setFiles((prev) => [...prev, ...nieuw].slice(0, 4));
  };
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editCat, setEditCat] = useState(null);
  const startEdit = (m) => {
    setEditId(m.id);
    setEditText(m.text || "");
    setEditCat(categoryOf ? categoryOf(m) : null);
  };
  const saveEdit = (m) => {
    const t = editText.trim();
    if (!t) return;
    onEdit(m, t, editCat);
    setEditId(null);
  };
  const send = async () => {
    const t = text.trim();
    if ((!t && files.length === 0) || sending) return;
    let photos = [];
    if (files.length > 0) {
      setSending(true);
      try {
        photos = await Promise.all(files.map((f) => dbUploadPhoto(f.file)));
      } catch (e) {
        setSending(false);
        cpToast("Foto uploaden lukt even niet: " + (e?.message || "onbekende fout"), "error");
        return;
      }
      setSending(false);
    }
    onSend(t, important, photos);
    files.forEach((f) => URL.revokeObjectURL(f.url));
    setFiles([]);
    setText("");
    setImportant(false);
  };
  return (
    <div className="space-y-2">
      {items.length === 0 && emptyText && (
        <p className="text-xs text-gray-500">{emptyText}</p>
      )}
      {items.map((m, idx) => (
        <React.Fragment key={m.id}>
        {groupOf && (idx === 0 || groupOf(items[idx - 1]) !== groupOf(m)) && (
          <p
            className={`text-xs font-semibold uppercase tracking-wide text-gray-500 ${
              idx === 0 ? "" : "pt-2 border-t border-gray-100"
            }`}
          >
            {groupOf(m)}
          </p>
        )}
        <div
          className={`flex gap-2 group ${m.important ? "rounded-xl p-2 -mx-1" : ""}`}
          style={m.important ? { backgroundColor: C.HoneyLight } : undefined}
        >
          <ProfileAvatar
            name={m.author}
            staffList={staffList}
            size={compact ? 22 : 26}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-semibold" style={{ color: C.Lagoon }}>
                {m.author}
              </span>
              {m.important && (
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: C.Bark }}>
                  Belangrijk
                </span>
              )}
              {categoryOf && (() => {
                const a = dbAfdeling(categoryOf(m));
                return (
                  <span
                    className="text-xs font-semibold px-1.5 py-0.5 rounded-full"
                    style={{ backgroundColor: a.bg, color: a.text }}
                  >
                    {a.id === "alle" ? "Iedereen" : a.label}
                  </span>
                );
              })()}
              <span className="text-xs text-gray-500">
                {dbAgo(m.at)}
                {m.editedAt ? " · bewerkt" : ""}
              </span>
              <span className="ml-auto flex items-center gap-1.5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                {onEdit && (m.author === loggedInUserName || canModerate) && editId !== m.id && (
                  <button
                    onClick={() => startEdit(m)}
                    className="text-gray-500 hover:text-[#045E51]"
                    aria-label="Bewerken" title="Bewerken"
                  >
                    <Edit2 size={12} />
                  </button>
                )}
                {onDelete && (m.author === loggedInUserName || canModerate) && (
                  <button
                    onClick={() => onDelete(m)}
                    className="text-gray-500 hover:text-[#700606]"
                    aria-label="Verwijderen" title="Verwijderen"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </span>
            </div>
            {editId === m.id ? (
              <div className="space-y-1.5 mt-1">
                <textarea
                  autoFocus
                  rows={2}
                  className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#045E51] resize-y bg-white"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                />
                {categoryOf && (
                  <div className="flex flex-wrap gap-1">
                    {DB_AFDELINGEN.map((a) => (
                      <button
                        key={a.id}
                        onClick={() => setEditCat(a.id)}
                        className="text-xs font-semibold px-2 py-0.5 rounded-full border"
                        style={
                          editCat === a.id
                            ? { backgroundColor: a.text, color: "white", borderColor: a.text }
                            : { backgroundColor: "white", color: a.text, borderColor: `${a.text}33` }
                        }
                      >
                        {a.id === "alle" ? "Iedereen" : a.label}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex gap-1.5">
                  <button
                    onClick={() => saveEdit(m)}
                    className="px-3 py-1 rounded-lg text-xs font-semibold text-white"
                    style={{ backgroundColor: C.Pine }}
                  >
                    Opslaan
                  </button>
                  <button
                    onClick={() => setEditId(null)}
                    className="px-3 py-1 rounded-lg text-xs text-gray-500 bg-gray-100"
                  >
                    Annuleren
                  </button>
                </div>
              </div>
            ) : (
              m.text && (
                <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                  {highlightMentions(m.text, loggedInUserName)}
                </p>
              )
            )}
            {Array.isArray(m.photos) && m.photos.length > 0 && (
              <div className="flex gap-1.5 flex-wrap mt-1">
                {m.photos.map((pid) => (
                  <DbPhoto key={pid} id={pid} />
                ))}
              </div>
            )}
            {(m.important || receiptAll) && (
              <DbReceipt
                small
                reads={m.reads}
                author={m.author}
                me={loggedInUserName}
                staffList={staffList}
                onRead={onRead ? () => onRead(m) : null}
              />
            )}
          </div>
        </div>
        </React.Fragment>
      ))}
      {inputExtra}
      {/* Schrijfvak: tekst bovenaan, knoppen eronder */}
      <div
        className="rounded-2xl border bg-white transition-colors focus-within:ring-2 focus-within:ring-[#045E51]/40"
        style={{ borderColor: important ? C.Honey : "#E5E7EB" }}
      >
        {files.length > 0 && (
          <div className="flex gap-1.5 flex-wrap px-3 pt-3">
            {files.map((f, i) => (
              <div key={f.url} className="relative">
                <img src={f.url} alt="" className="w-16 h-16 object-cover rounded-lg border" />
                <button
                  onClick={() => {
                    URL.revokeObjectURL(f.url);
                    setFiles((prev) => prev.filter((_, j) => j !== i));
                  }}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#700606] text-white flex items-center justify-center"
                  aria-label="Foto verwijderen"
                >
                  <X size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
        <MentionTextarea
          className="w-full px-3 pt-2.5 pb-1 text-sm outline-none resize-none bg-transparent min-h-[48px] rounded-2xl"
          placeholder={placeholder}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && window.innerWidth >= 768) {
              e.preventDefault();
              send();
            }
          }}
          staffList={staffList}
          loggedInUserName={loggedInUserName}
        />
        <div className="flex items-center gap-1 px-2 pb-2 flex-wrap">
          {allowPhotos && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              <input
                ref={camRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              <button
                onClick={() => camRef.current?.click()}
                className="h-8 px-2 rounded-lg flex items-center gap-1 text-xs font-medium text-gray-500 hover:bg-gray-100"
                aria-label="Foto maken" title="Foto maken"
              >
                <Camera size={16} />
                <span className="hidden sm:inline">Foto</span>
              </button>
              <button
                onClick={() => fileRef.current?.click()}
                className="h-8 px-2 rounded-lg flex items-center gap-1 text-xs font-medium text-gray-500 hover:bg-gray-100"
                aria-label="Foto uit galerij" title="Foto uit galerij"
              >
                <ImageIcon size={16} />
                <span className="hidden sm:inline">Galerij</span>
              </button>
            </>
          )}
          <button
            onClick={() => setImportant((v) => !v)}
            className="h-8 px-2.5 rounded-full flex items-center gap-1.5 text-xs font-semibold border transition-colors"
            style={
              important
                ? { backgroundColor: C.HoneyLight, color: C.Bark, borderColor: C.Honey }
                : { backgroundColor: "white", color: "#6B7280", borderColor: "#E5E7EB" }
            }
            aria-pressed={important}
            aria-label="Belangrijk: iedereen moet bevestigen dat ze het gelezen hebben" title="Belangrijk: iedereen moet bevestigen dat ze het gelezen hebben"
          >
            {important ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
            Belangrijk
          </button>
          <div className="flex-1" />
          <button
            onClick={send}
            disabled={sending}
            className="h-9 pl-3 pr-3.5 rounded-xl flex items-center gap-1.5 text-sm font-semibold transition-opacity"
            style={{
              backgroundColor: C.Pine,
              color: "white",
              opacity: text.trim() || files.length ? 1 : 0.45,
            }}
          >
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            {sending ? "Bezig…" : "Plaatsen"}
          </button>
        </div>
      </div>
      {(!compact || important) && (
      <p className="text-xs px-1" style={{ color: important ? C.Bark : "#6B7280" }}>
        {important
          ? "Belangrijk aan: het bericht wordt geel en iedereen krijgt een knop 'Gelezen'. Jij ziet wie het gelezen heeft."
          : "Tip: tag iemand met @naam of iedereen met @team. Moet iedereen het zeker lezen? Zet dan 'Belangrijk' aan."}
      </p>
      )}
    </div>
  );
};

// Verslag-formulier per activiteit
const DbReportForm = ({
  act,
  initial,
  staffList,
  loggedInUserName,
  onSave,
  onCancel,
  appData = null,
}) => {
  const actType = dbActType(act.title);
  const typeField = DB_TYPE_FIELD[actType];
  const [f, setF] = useState(() => ({
    crowd: initial?.crowd || null,
    colleagues:
      initial?.colleagues ??
      (act.staff || []).filter((n) => n && n !== loggedInUserName).join(", "),
    notes: initial?.notes || "",
    songs: initial?.songs || "",
    bingoQs: initial?.bingoQs || "",
    quiz: initial?.quiz || "",
    story: initial?.story || "",
    finaleQs: initial?.finaleQs || "",
  }));
  const [crowdError, setCrowdError] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const colleagues = (f.colleagues || "").split(", ").filter(Boolean);
  const save = () => {
    if (!f.crowd) {
      setCrowdError(true);
      return;
    }
    onSave(f);
  };
  return (
    <div className="space-y-3 pt-1">
      <div>
        <label
          className="text-xs font-semibold block mb-1"
          style={{ color: crowdError ? C.Sunset : C.Lagoon }}
        >
          Drukte {crowdError && <span className="font-normal">— kies eerst de drukte</span>}
        </label>
        <div className={crowdError ? "ring-2 ring-[#FF699B] rounded-lg" : ""}>
          <CrowdBar
            value={f.crowd}
            onChange={(v) => {
              set("crowd", v);
              setCrowdError(false);
            }}
            compact
            type={act.type === "crea" ? "crea" : "ent"}
          />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold block mb-1" style={{ color: C.Lagoon }}>
          Met wie
        </label>
        <div className="flex flex-wrap gap-1.5 items-center">
          {colleagues.map((n) => (
            <span
              key={n}
              className="flex items-center gap-1 text-xs font-semibold pl-1 pr-2 py-0.5 rounded-full"
              style={{ backgroundColor: C.PineLight, color: C.Pine }}
            >
              <ProfileAvatar name={n} staffList={staffList} size={18} />
              {n}
              <button aria-label="Sluiten"
                onClick={() =>
                  set(
                    "colleagues",
                    colleagues.filter((x) => x !== n).join(", ")
                  )
                }
                className="opacity-60 hover:opacity-100"
              >
                <X size={11} />
              </button>
            </span>
          ))}
          <StyledSelect
            className="border border-gray-200 rounded-lg text-xs px-2 py-1 bg-white"
            value=""
            onChange={(e) => {
              const n = e.target.value;
              if (n && !colleagues.includes(n))
                set("colleagues", [...colleagues, n].join(", "));
            }}
          >
            <option value="">+ collega</option>
            {(staffList || [])
              .filter((s) => !s.inactive && !teamVerborgen(s) && s.name !== loggedInUserName)
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((s) => (
                <option key={s.name} value={s.name}>
                  {s.name}
                </option>
              ))}
          </StyledSelect>
        </div>
      </div>
      {typeField && (
        <div>
          <label className="text-xs font-semibold block mb-1" style={{ color: C.Lagoon }}>
            {typeField.label}
          </label>
          {KIEZER_CFG[typeField.key] ? (
            <LijstKiezer veld={typeField.key} value={f[typeField.key] || ""} onChange={(v) => set(typeField.key, v)} appData={appData} />
          ) : typeField.multi ? (
            <textarea
              rows={3}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#045E51] resize-y bg-white"
              value={f[typeField.key]}
              onChange={(e) => set(typeField.key, e.target.value)}
            />
          ) : (
            <input
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#045E51] bg-white"
              value={f[typeField.key]}
              onChange={(e) => set(typeField.key, e.target.value)}
            />
          )}
        </div>
      )}
      <div>
        <label className="text-xs font-semibold block mb-1" style={{ color: C.Lagoon }}>
          Bijzonderheden
        </label>
        <MentionTextarea
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#045E51] resize-none bg-white h-20"
          placeholder="Hoe ging het? Incidenten, tips voor de volgende keer… (@naam om te taggen)"
          value={f.notes}
          onChange={(e) => set("notes", e.target.value)}
          staffList={staffList}
          loggedInUserName={loggedInUserName}
        />
      </div>
      <div className="flex gap-2">
        <button
          onClick={save}
          className="flex-1 py-2 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-1.5"
          style={{ backgroundColor: C.Pine }}
        >
          <Check size={15} /> Verslag opslaan
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 rounded-xl text-sm text-gray-500 bg-gray-100 hover:bg-gray-200"
        >
          Annuleren
        </button>
      </div>
    </div>
  );
};

const DbReportView = ({ rep, staffList, loggedInUserName, legacy, onEdit }) => (
  <div className="rounded-xl bg-gray-50 border border-gray-100 p-2.5 space-y-1">
    <div className="flex items-center gap-2 flex-wrap">
      <ProfileAvatar name={rep.author} staffList={staffList} size={20} />
      <span className="text-xs font-semibold" style={{ color: C.Lagoon }}>
        {rep.author}
      </span>
      {rep.colleagues && (
        <span className="text-xs text-gray-500">met {rep.colleagues}</span>
      )}
      <DbCrowdChip lvl={rep.crowd} />
      {legacy && <DbPill bg="#F3F4F6" text="#6B7280">oude overdracht</DbPill>}
      {onEdit && (
        <button
          onClick={onEdit}
          className="ml-auto text-gray-500 hover:text-[#045E51]"
          aria-label="Bewerken" title="Bewerken"
        >
          <Edit2 size={13} />
        </button>
      )}
    </div>
    {Object.entries(DB_FIELD_LABELS).map(([k, label]) =>
      rep[k] ? (
        <p key={k} className="text-xs text-gray-800 whitespace-pre-wrap">
          <span className="font-semibold">{label}:</span> {rep[k]}
        </p>
      ) : null
    )}
    {rep.notes && (
      <p className="text-xs text-gray-800 whitespace-pre-wrap">
        {highlightMentions(rep.notes, loggedInUserName)}
      </p>
    )}
  </div>
);

// Eén open punt
const DbPuntItem = ({
  punt,
  staffList,
  loggedInUserName,
  userRole,
  onReact,
  onResolve,
  onReopen,
  onAssign,
  onDelete,
  onReadReactie,
  onEditPunt, // ({ title, afdeling }) => opslaan
  onEditReactie, // (reactie, tekst) => opslaan
}) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [eTitle, setETitle] = useState(punt.title || "");
  const [eAfd, setEAfd] = useState(punt.afdeling || "alle");
  const canEditPunt =
    !!onEditPunt &&
    (punt.author === loggedInUserName || userRole === "fm" || userRole === "admin");
  const af = dbAfdeling(punt.afdeling);
  const reacties = Object.entries(punt.reacties || {})
    .map(([id, r]) => ({ id, ...r }))
    .filter((r) => !r.deleted)
    .sort((a, b) => (a.at || 0) - (b.at || 0));
  const resolved = punt.status === "opgelost";
  const mine = punt.assignee === loggedInUserName;
  return (
    <div
      className="border-t border-gray-100 first:border-t-0 scroll-mt-40"
      style={mine && !resolved ? { backgroundColor: `${C.Honey}14` } : undefined}
      data-focus={punt.id}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left flex gap-2.5 items-start py-2.5 px-1"
      >
        {resolved ? (
          <CheckCircle size={17} className="mt-0.5 flex-shrink-0" style={{ color: C.Pine }} />
        ) : (
          <AlertTriangle size={17} className="mt-0.5 flex-shrink-0" style={{ color: C.Bark }} />
        )}
        <div className="flex-1 min-w-0">
          <p
            className={`text-sm font-medium ${resolved ? "line-through text-gray-500" : "text-gray-800"}`}
          >
            {punt.title}
          </p>
          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
            <DbPill bg={af.bg} text={af.text}>
              {af.id === "alle" ? "Hele team" : af.label}
            </DbPill>
            <span className="text-xs text-gray-500">
              {punt.author} · {dbAgo(punt.createdAt)}
            </span>
            {punt.assignee && (
              <span className="text-xs font-semibold" style={{ color: mine ? C.Bark : C.Pine }}>
                → {mine ? "jij" : punt.assignee}
              </span>
            )}
            {reacties.length > 0 && (
              <span className="text-xs text-gray-500 flex items-center gap-0.5">
                <MessageCircle size={11} /> {reacties.length}
              </span>
            )}
          </div>
        </div>
        {open ? (
          <ChevronUp size={16} className="text-gray-500 mt-0.5" />
        ) : (
          <ChevronDown size={16} className="text-gray-500 mt-0.5" />
        )}
      </button>
      {open && (
        <div className="pl-8 pr-1 pb-3 space-y-3">
          {resolved && (
            <p className="text-xs" style={{ color: C.Pine }}>
              Opgelost door {punt.resolvedBy} · {dbAgo(punt.resolvedAt)}
            </p>
          )}
          {editing ? (
            <div className="space-y-1.5 rounded-xl border border-gray-200 p-2.5 bg-gray-50">
              <input
                autoFocus
                className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#045E51] bg-white"
                value={eTitle}
                onChange={(e) => setETitle(e.target.value)}
              />
              <div className="flex flex-wrap gap-1">
                {DB_AFDELINGEN.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => setEAfd(a.id)}
                    className="text-xs font-semibold px-2 py-0.5 rounded-full border"
                    style={
                      eAfd === a.id
                        ? { backgroundColor: a.text, color: "white", borderColor: a.text }
                        : { backgroundColor: "white", color: a.text, borderColor: `${a.text}33` }
                    }
                  >
                    {a.id === "alle" ? "Hele team" : a.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-1.5">
                <button
                  onClick={() => {
                    if (!eTitle.trim()) return;
                    onEditPunt({ title: eTitle.trim(), afdeling: eAfd });
                    setEditing(false);
                  }}
                  className="px-3 py-1 rounded-lg text-xs font-semibold text-white"
                  style={{ backgroundColor: C.Pine }}
                >
                  Opslaan
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="px-3 py-1 rounded-lg text-xs text-gray-500 bg-white border border-gray-200"
                >
                  Annuleren
                </button>
              </div>
            </div>
          ) : (
            canEditPunt && (
              <button
                onClick={() => {
                  setETitle(punt.title || "");
                  setEAfd(punt.afdeling || "alle");
                  setEditing(true);
                }}
                className="text-xs font-semibold flex items-center gap-1"
                style={{ color: C.Pine }}
              >
                <Edit2 size={12} /> Titel of afdeling aanpassen
              </button>
            )
          )}
          <DbThread
            compact
            items={reacties}
            staffList={staffList}
            loggedInUserName={loggedInUserName}
            onSend={onReact}
            onRead={onReadReactie}
            onEdit={onEditReactie ? (r, t) => onEditReactie(r, t) : undefined}
            canModerate={userRole === "fm" || userRole === "admin"}
            placeholder="Reageer of geef een update…"
          />
          <div className="flex flex-wrap gap-2 items-center">
            {resolved ? (
              <button
                onClick={onReopen}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 flex items-center gap-1"
              >
                <RotateCcw size={12} /> Heropenen
              </button>
            ) : (
              <button
                onClick={onResolve}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white flex items-center gap-1"
                style={{ backgroundColor: C.Pine }}
              >
                <Check size={12} /> Opgelost
              </button>
            )}
            {!resolved && (
              <StyledSelect
                className="border border-gray-200 rounded-lg text-xs px-2 py-1.5 bg-white"
                value={punt.assignee || ""}
                onChange={(e) => onAssign(e.target.value)}
              >
                <option value="">Niemand toegewezen</option>
                <option value="Maik">Maik</option>
                {(staffList || [])
                  .filter((s) => !s.inactive && !teamVerborgen(s) && s.name !== "Maik")
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((s) => (
                    <option key={s.name} value={s.name}>
                      {s.name}
                    </option>
                  ))}
              </StyledSelect>
            )}
            {(punt.author === loggedInUserName || userRole === "fm" || userRole === "admin") && (
              <button
                onClick={onDelete}
                className="ml-auto text-xs text-gray-500 hover:text-[#700606] flex items-center gap-1"
              >
                <Trash2 size={12} /> Verwijderen
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DbCard = ({ icon: Icon, title, subtitle, right, children, accent = C.Pine, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden ${className}`}>
    <div className="px-4 py-3 flex items-center gap-2.5 border-b border-gray-100">
      <div
        className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${accent}1A` }}
      >
        <Icon size={16} style={{ color: accent }} />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-[15px] font-semibold" style={{ color: C.Lagoon }}>
          {title}
        </h3>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
      {right}
    </div>
    <div className="px-4 py-3">{children}</div>
  </div>
);

// ── Dienst / aanwezigheid (timeClock[datum][naam] = { in, out } als ISO) ──
const dbHM = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ""
    : d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
};
const dbToMin = (hhmm) => {
  if (!hhmm || !String(hhmm).includes(":")) return null; // leeg ≠ 00:00
  const [h, m] = String(hhmm).split(":").map(Number);
  return isNaN(h) ? null : h * 60 + (m || 0);
};
const dbIsoMin = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d.getHours() * 60 + d.getMinutes();
};
const dbIsoAt = (dateKey, hhmm) => {
  const [y, mo, d] = dateKey.split("-").map(Number);
  const [h, mi] = String(hhmm).split(":").map(Number);
  return new Date(y, mo - 1, d, h, mi || 0, 0, 0).toISOString();
};
const dbDur = (ms) => {
  const m = Math.max(0, Math.round(ms / 60000));
  return `${Math.floor(m / 60)}u ${String(m % 60).padStart(2, "0")}m`;
};
// Ingeroosterde dienst van iemand op een datum (roostermaker of CSV-rooster)
const dbPlanned = (appData, dateKey, name) => {
  const rd = (appData.roosterDiensten?.[dateKey] || []).find((d) =>
    (d.assigned || []).includes(name)
  );
  if (rd && (rd.start || rd.end))
    return {
      start: rd.start || "",
      end: rd.end || "",
      code: rd.urencode || "",
      breaks: Array.isArray(rd.breaks) ? rd.breaks : [],
    };
  const ws = (appData.workSchedule?.[dateKey] || []).find(
    (x) => x.matchedUser === name
  );
  if (ws && (ws.start || ws.end))
    return {
      start: ws.start || "",
      end: ws.end || "",
      code: "",
      breaks:
        ws.pauzeStart && ws.pauzeEnd
          ? [{ start: ws.pauzeStart, end: ws.pauzeEnd }]
          : Array.isArray(ws.breaks)
          ? ws.breaks
          : [],
    };
  return null;
};
const DB_PAUZE_VANAF_MIN = 330; // 5,5 uur
const DB_PAUZE_MIN = 30;
// "23 min" of "1u 05m"
const dbMinLabel = (m) => {
  const v = Math.max(0, Math.round(m));
  return v >= 60
    ? `${Math.floor(v / 60)}u ${String(v % 60).padStart(2, "0")}m`
    : `${v} min`;
};
// Gewerkte tijd minus de ingeroosterde pauze(s) die binnen de dienst vallen
// Uren tellen: start wordt naar boven afgerond op het volgende kwartier
// (17:53 → 18:00), de eindtijd telt op de minuut.
const dbWorked = (clock, planned) => {
  if (!clock?.in) return null;
  const start = new Date(clock.in);
  const end = clock.out ? new Date(clock.out) : new Date();
  const sMin = start.getHours() * 60 + start.getMinutes();
  const s0 = Math.ceil(sMin / 15) * 15; // telt vanaf het eerste kwartier
  // Eindtijd op de minuut: echte verstreken tijd vanaf de starttijd. (Rekenen
  // vanaf middernacht gaf op de dag van de zomer-/wintertijdwissel een uur te
  // veel of te weinig.)
  const sf = new Date(start);
  sf.setSeconds(0, 0);
  const e0 = sMin + Math.floor((end - sf) / 60000);
  const total = Math.max(0, e0 - s0);
  const startTxt = `${String(Math.floor(s0 / 60) % 24).padStart(2, "0")}:${String(
    s0 % 60
  ).padStart(2, "0")}`;
  let pauze = 0;
  const pauzes = [];
  for (const b of planned?.breaks || []) {
    const bs = dbToMin(b?.start);
    const be = dbToMin(b?.end);
    if (bs == null || be == null || be <= bs) continue;
    const ov = Math.max(0, Math.min(e0, be) - Math.max(s0, bs));
    if (ov > 0) {
      pauze += ov;
      pauzes.push(`${b.start}–${b.end}`);
    }
  }
  // Pauzeregel: dienst van 5,5 uur of langer → minimaal 30 min pauze eraf
  // (ook als er geen of een kortere pauze is ingeroosterd)
  if (total >= DB_PAUZE_VANAF_MIN && pauze < DB_PAUZE_MIN) pauze = DB_PAUZE_MIN;
  return { min: Math.max(0, total - pauze), pauze, pauzes, startTxt, rounded: s0 !== sMin };
};
// Gewerkte tijd als decimale uren: 3u45 → "3,75 uur"
const dbUren = (min) =>
  `${(Math.max(0, min) / 60).toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} uur`;
const DB_TOLERANTIE = 5;
// Afdelingskleur bij een vrije tekst (locatie/titel), voor de rand links in lijsten
const AFD_KLEUR = { entertainment: "#FFCA55", crea: "#9DB4F6", springkussen: "#FF699B", hotspot: "#FF8A3D" };
const afdKleurVanTekst = (t) =>
  AFD_KLEUR[dbAfdFromText(t) || (/podium|dome|theater|show/i.test(String(t || "")) ? "entertainment" : "")] || "#045E51";
const dbAfdFromText = (t) => {
  const x = String(t || "").toLowerCase();
  if (x.includes("hotspot")) return "hotspot";
  if (x.includes("spring") || x.includes("kussen")) return "springkussen";
  if (x.includes("crea") || x.includes("atelier")) return "crea";
  if (x.includes("entertain") || x.includes("ent ")) return "entertainment";
  return null;
};
const DB_WERK_TYPES = ["entertainment", "crea", "springkussen", "overig"];
// Afdeling van een roostermaker-dienst (null = onbekend / verlof)
const dbDienstAfdeling = (d) => {
  if (!d || !DB_WERK_TYPES.includes(d.type)) return null;
  // De titel ("Hotspot", "Crea ochtend") zegt meer dan het diensttype:
  // een ochtenddienst van type entertainment met titel Hotspot is géén Entertainment.
  const uitTitel = dbAfdFromText(d.title);
  if (uitTitel) return uitTitel;
  return d.type !== "overig" ? d.type : null;
};
// Mijn diensten van een dag: [{ van, tot, afd }] in minuten (afd null = onbekend → alle).
// Uit de Roostermaker (roosterDiensten) én het geïmporteerde rooster (workSchedule).
const dbMijnDiensten = (appData, dateKey, name) => {
  const out = [];
  for (const d of appData.roosterDiensten?.[dateKey] || []) {
    if (!(d.assigned || []).includes(name)) continue;
    const van = dbToMin(d.start);
    let tot = dbToMin(d.end);
    if (van == null || tot == null) continue;
    if (tot <= van) tot += 1440;
    // Kantoordienst (bijv. FM "Kantoor"): geen afdelingstaken
    const kantoor = /kantoor|office|administratie/i.test(d.title || "");
    out.push({ van, tot, afd: kantoor ? "kantoor" : dbDienstAfdeling(d) });
  }
  if (!out.length) {
    for (const w of appData.workSchedule?.[dateKey] || []) {
      if (w.matchedUser !== name) continue;
      const van = dbToMin(w.start);
      let tot = dbToMin(w.end);
      if (van == null || tot == null) continue;
      if (tot <= van) tot += 1440;
      const tekst = `${w.title || ""} ${w.location || ""} ${w.loc || ""} ${w.role || ""} ${w.dept || ""} ${w.type || ""}`;
      const afd = dbAfdFromText(tekst) || (/podium|dome|theater|show|entertain/i.test(tekst) ? "entertainment" : null);
      out.push({ van, tot, afd });
    }
  }
  return out;
};
// Eerstvolgende echte dienst van een afdeling ná (datum, minuut).
// afd "alle" = eerstvolgende dienst van welke afdeling dan ook.
const dbNextDienst = (appData, afd, fromDateKey, fromMin) => {
  const [y, m, d0] = fromDateKey.split("-").map(Number);
  for (let i = 0; i <= 21; i++) {
    const dk = toIsoDate(new Date(y, m - 1, d0 + i, 12));
    const list = (appData.roosterDiensten?.[dk] || [])
      .filter((d) => {
        const a = dbDienstAfdeling(d);
        if (!a && !(afd === "alle" && d && DB_WERK_TYPES.includes(d.type))) return false;
        if (afd !== "alle" && a !== afd) return false;
        const st = dbToMin(d.start);
        if (st == null) return false;
        return i > 0 || st > fromMin;
      })
      .sort((a, b) => dbToMin(a.start) - dbToMin(b.start));
    if (list.length) return { dateKey: dk, dienst: list[0] };
  }
  return null;
};
// Wie stond waar op een dag, per afdeling: [{ name, van, tot }] (minuten).
// Uit de Roostermaker, het geïmporteerde rooster en de springkussen-planning,
// zodat wie springkussen staat er altijd automatisch bij staat.
const dbBezetting = (appData, dk) => {
  const out = { entertainment: [], crea: [], springkussen: [], hotspot: [] };
  const zet = (afd, name, van, tot) => {
    if (!name || !out[afd]) return;
    const al = out[afd].find((x) => x.name === name);
    if (al) {
      if (van != null && (al.van == null || van < al.van)) al.van = van;
      if (tot != null && (al.tot == null || tot > al.tot)) al.tot = tot;
      return;
    }
    out[afd].push({ name, van, tot });
  };
  const rd = appData.roosterDiensten?.[dk] || [];
  for (const d of rd) {
    if (!d || /kantoor|office|administratie/i.test(d.title || "")) continue;
    const afd = dbDienstAfdeling(d);
    for (const n of d.assigned || []) zet(afd, n, dbToMin(d.start), dbToMin(d.end));
  }
  const inRooster = new Set(rd.flatMap((d) => (d && d.assigned) || []));
  for (const w of appData.workSchedule?.[dk] || []) {
    if (!w || !w.matchedUser || inRooster.has(w.matchedUser)) continue;
    const tekst = `${w.title || ""} ${w.location || ""} ${w.loc || ""} ${w.role || ""} ${w.dept || ""} ${w.type || ""}`;
    const afd = dbAfdFromText(tekst) || (/podium|dome|theater|show|entertain/i.test(tekst) ? "entertainment" : null);
    zet(afd, w.matchedUser, dbToMin(w.start), dbToMin(w.end));
  }
  const sp = appData.dailySpringkussen?.[dk];
  if (sp && sp.open === true) {
    for (const n of sp.staff || []) {
      if (typeof n !== "string" || !n) continue;
      const e = out.entertainment.find((x) => x.name === n);
      out.entertainment = out.entertainment.filter((x) => x.name !== n);
      zet("springkussen", n, e?.van ?? dbToMin(sp.start), e?.tot ?? dbToMin(sp.end));
    }
  }
  for (const k of Object.keys(out)) out[k].sort((a, b) => (a.van ?? 9999) - (b.van ?? 9999) || a.name.localeCompare(b.name));
  return out;
};
// Afdeling waar iemand die dag stond volgens de bezetting (rooster + springkussenplanning).
// Springkussen gaat voor; anders de eerste afdeling, anders dbMyAfdeling.
const dbAfdVanAuteur = (appData, dk, name) => {
  if (!appData || !dk || !name) return null;
  const bez = dbBezetting(appData, dk);
  const afds = Object.keys(bez).filter((a) => bez[a].some((p) => p.name === name));
  if (afds.includes("springkussen")) return "springkussen";
  if (afds.length) return afds[0];
  return dbMyAfdeling(appData, dk, name);
};
// Afdeling waarop iemand die dag ingeroosterd staat (null = onbekend)
const dbMyAfdeling = (appData, dateKey, name) => {
  const fromText = dbAfdFromText;
  const rd = (appData.roosterDiensten?.[dateKey] || []).find((d) =>
    (d.assigned || []).includes(name)
  );
  if (rd) {
    const t = fromText(rd.title);
    if (t) return t;
    if (["entertainment", "crea", "springkussen"].includes(rd.type)) return rd.type;
  }
  const ws = (appData.workSchedule?.[dateKey] || []).find(
    (x) => x.matchedUser === name
  );
  if (ws) {
    const t = fromText(`${ws.location || ""} ${ws.loc || ""} ${ws.role || ""} ${ws.dept || ""}`);
    if (t) return t;
  }
  // Anders: afdeling van de activiteiten waar je op staat
  const act = (appData.schedule || []).find(
    (a) => a.date === dateKey && (a.staff || []).includes(name)
  );
  return act ? dbActAfdeling(act) : null;
}; // minuten speling voor te laat / te vroeg weg

// 📋 Verslagen verzamelen over meerdere dagen: nieuwe dagbord-verslagen én
// verslagen uit de oude overdracht. Leesbevestigingen: bij nieuwe verslagen op
// het verslag zelf (rep_…​.reads), bij oude in dagbord[datum].lread_<act>_<naam>.
const dbCollectReports = (appData, fromKey, toKey) => {
  const map = new Map();
  const dates = new Set([
    ...Object.keys(appData.dagbord || {}),
    ...Object.keys(appData.handover || {}),
  ]);
  for (const dk of dates) {
    if (dk < fromKey || dk > toKey) continue;
    const acts = (appData.schedule || []).filter((a) => a.date === dk);
    const actOf = (id) => acts.find((a) => String(a.id) === String(id));
    const dd = appData.dagbord?.[dk] || {};
    for (const [k, v] of Object.entries(dd)) {
      if (!k.startsWith("rep_") || !v || v.deleted || !v.author || v.nietsBijzonders) continue;
      const act = actOf(v.actId);
      map.set(`${dk}|${k}`, {
        ...v,
        _date: dk,
        _readKey: k,
        _reads: v.reads || {},
        _title: act?.title || v.actTitle || "Activiteit",
        _time: act?.time || "",
        _afd: act ? dbActAfdeling(act) : dbAfdFromText(v.actTitle) || "entertainment",
        _legacy: false,
      });
    }
    const ho = appData.handover?.[dk] || {};
    for (const t of ["ent", "crea"]) {
      const arr = Array.isArray(ho[t]) ? ho[t] : [];
      for (const e of arr) {
        for (const [aid, af] of Object.entries(e?.actFields || {})) {
          const author = af?.author || e.author || "?";
          const rk = `lread_${dbSlug(aid)}_${dbSlug(author)}`;
          const act = actOf(aid);
          map.set(`${dk}|${rk}`, {
            ...af,
            author,
            actId: String(aid),
            _date: dk,
            _readKey: rk,
            _reads: dd[rk]?.reads || {},
            _title: act?.title || "Activiteit",
            _time: act?.time || "",
            _afd: act ? dbActAfdeling(act) : t === "crea" ? "crea" : "entertainment",
            _legacy: true,
          });
        }
      }
    }
  }
  return [...map.values()].sort(
    (a, b) => b._date.localeCompare(a._date) || dbStartMin(b._time) - dbStartMin(a._time)
  );
};
const dbReportRead = (r, name) => r.author === name || !!r._reads?.[dbSlug(name)];
// Teamberichten (algemene overdracht) over meerdere dagen
const dbCollectTeam = (appData, fromKey, toKey) => {
  const out = [];
  for (const [dk, dd] of Object.entries(appData.dagbord || {})) {
    if (dk < fromKey || dk > toKey) continue;
    for (const [k, v] of Object.entries(dd || {})) {
      if (!k.startsWith("c_") || !v || v.deleted || v.target !== "day" || !v.author) continue;
      out.push({
        ...v,
        _date: dk,
        _readKey: k,
        _reads: v.reads || {},
        _afd: v.afdeling || dbMyAfdeling(appData, dk, v.author) || "alle",
      });
    }
  }
  return out.sort((a, b) => b._date.localeCompare(a._date) || (b.at || 0) - (a.at || 0));
};
// Vanaf welke dag tonen we "nieuw sinds je laatste dienst"
const dbNewFrom = (appData, name, todayKey) => {
  const [y, m, d] = todayKey.split("-").map(Number);
  for (let i = 1; i <= 14; i++) {
    const dk = toIsoDate(new Date(y, m - 1, d - i, 12));
    if (appData.timeClock?.[dk]?.[name]?.in || dbPlanned(appData, dk, name)) return dk;
  }
  return toIsoDate(new Date(y, m - 1, d - 3, 12)); // geen vorige dienst gevonden → 3 dagen
};
// Eerste regel van de bijzonderheden (of het typeveld) als korte samenvatting
const dbReportSnippet = (r) => {
  const t = r.notes || r.finaleQs || r.bingoQs || r.songs || r.quiz || r.story || "";
  const line = String(t).split("\n")[0].trim();
  return line.length > 80 ? line.slice(0, 77) + "…" : line;
};

// ═══════════════════════════════════════════════════════════════════
// 📋 OVERDRACHT-FEED — één soort item: de overdracht.
//   • met activiteit (verslag): dagbord rep_… of c_ met target "act:<id>",
//     of uit de oude overdracht (handover actFields)
//   • algemeen: dagbord c_ met target "day", of losse tekst uit de oude overdracht
//   • doorgegeven: dagbord door_… (bij afsluiten voor de volgende dienst)
// Leesbevestiging: reads op het item zelf; oude overdracht → lread_… sleutel.
// ═══════════════════════════════════════════════════════════════════
const dbAfdDot = { entertainment: C.Honey, crea: C.Sky, hotspot: "#FF8A3D", springkussen: C.Blossom, alle: C.Pine };
// Officieel CP-icoon per afdeling (zelfde in Dagbord, takenlijst, rooster en overdracht)
const DB_AFD_ICOON = { entertainment: IcEntertainment, crea: IcCrea, hotspot: IcHotspot, springkussen: IcSpringkussen, alle: IcAlles };
// Afdelingsicoon in een rondje met het kleurpaar van de afdeling
const DbAfdIcoon = ({ id, size = 16, actief = false }) => {
  const I = DB_AFD_ICOON[id] || IcAlles;
  const a = dbAfdeling(id);
  return (
    <span
      className="inline-flex items-center justify-center rounded-full flex-shrink-0"
      style={{ width: size + 6, height: size + 6, backgroundColor: actief ? "rgba(255,255,255,0.55)" : a.bg, color: a.text }}
      aria-hidden="true"
    >
      <I size={size} />
    </span>
  );
};
const dbCollectFeed = (appData, fromKey, toKey) => {
  const items = new Map();
  const dates = new Set([
    ...Object.keys(appData.dagbord || {}),
    ...Object.keys(appData.handover || {}),
  ]);
  for (const dk of dates) {
    if (dk < fromKey || dk > toKey) continue;
    const acts = (appData.schedule || []).filter((a) => a.date === dk);
    const actOf = (id) => acts.find((a) => String(a.id) === String(id));
    const dd = appData.dagbord?.[dk] || {};
    const base = (k, v) => ({ _date: dk, _readKey: k, _reads: v?.reads || {} });
    for (const [k, v] of Object.entries(dd)) {
      if (!v || v.deleted || !v.author) continue;
      if (k.startsWith("rep_")) {
        const act = actOf(v.actId);
        items.set(`${dk}|${k}`, {
          ...v,
          ...base(k, v),
          kind: "act",
          text: v.notes || "",
          at: v.at || v.updatedAt || 0,
          _act: act,
          _title: act?.title || v.actTitle || "Activiteit",
          _time: act?.time || "",
          _afd: v.afdeling || (act ? dbActAfdeling(act) : dbAfdFromText(v.actTitle) || "entertainment"),
        });
      } else if (k.startsWith("c_")) {
        const actId = String(v.target || "").startsWith("act:") ? String(v.target).slice(4) : null;
        const act = actId ? actOf(actId) : null;
        items.set(`${dk}|${k}`, {
          ...v,
          ...base(k, v),
          kind: actId ? "act" : "algemeen",
          actId,
          _act: act,
          _title: act?.title || (actId ? "Activiteit" : ""),
          _time: act?.time || "",
          _afd:
            v.afdeling && (v.afdeling !== "alle" || v.afdGekozen || v.vooraf || v.author === "Maik")
              ? v.afdeling
              : act
              ? dbActAfdeling(act)
              : dbAfdVanAuteur(appData, dk, v.author) || "alle",
        });
      } else if (k.startsWith("door_")) {
        items.set(`${dk}|${k}`, {
          ...v,
          ...base(k, v),
          kind: "door",
          _afd: v.afdeling || "alle",
        });
      }
    }
    // Oude overdracht (alleen-lezen inhoud, wél te markeren als gelezen)
    const ho = appData.handover?.[dk] || {};
    const TABS = { fm: "alle", ent: "entertainment", crea: "crea", springkussen: "springkussen", hotspot: "hotspot" };
    for (const [t, afd] of Object.entries(TABS)) {
      const raw = ho[t];
      if (typeof raw === "string" && raw.trim()) {
        const rk = `lread_txt_${t}`;
        items.set(`${dk}|${rk}`, {
          ...base(rk, dd[rk]),
          kind: "algemeen",
          _legacy: true,
          author: "Archief",
          text: raw,
          at: 0,
          _afd: afd,
        });
        continue;
      }
      (Array.isArray(raw) ? raw : []).forEach((e, ei) => {
        if (!e) return;
        for (const [aid, af] of Object.entries(e.actFields || {})) {
          const author = af?.author || e.author || "?";
          const rk = `lread_${dbSlug(aid)}_${dbSlug(author)}`;
          const act = actOf(aid);
          items.set(`${dk}|${rk}`, {
            ...af,
            ...base(rk, dd[rk]),
            _legacyReadBy: e.readBy || [],
            kind: "act",
            _legacy: true,
            author,
            actId: String(aid),
            text: af?.notes || "",
            at: e.timestamp || 0,
            _act: act,
            _title: act?.title || "Activiteit",
            _time: act?.time || "",
            _afd: act ? dbActAfdeling(act) : t === "crea" ? "crea" : "entertainment",
          });
        }
        const txt = [e.text, e.extraNotes || e.remarks].filter(Boolean).join("\n");
        const crea = (e.creaActs || []).map((c) => `${c.name}${c.notes ? ` — ${c.notes}` : ""}`).join("\n");
        if (txt || crea || (e.photos || []).length) {
          const rk = `lread_txt_${t}_${dbSlug(e.id ?? e.timestamp ?? ei)}`;
          items.set(`${dk}|${rk}`, {
            ...base(rk, dd[rk]),
            _legacyReadBy: e.readBy || [],
            kind: "algemeen",
            _legacy: true,
            author: e.author || "Archief",
            text: [txt, crea].filter(Boolean).join("\n"),
            legacyPhotos: (e.photos || []).map((p) => p?.url).filter(Boolean),
            fmRemark: e.fmRemark,
            at: e.timestamp || 0,
            _afd: afd,
          });
        }
      });
    }
  }
  return [...items.values()].sort(
    (a, b) => b._date.localeCompare(a._date) || (b.at || 0) - (a.at || 0)
  );
};
const dbFeedRead = (it, name) =>
  !!it.nietsBijzonders ||
  it.author === name ||
  !!it._reads?.[dbSlug(name)] ||
  (Array.isArray(it._legacyReadBy) && it._legacyReadBy.includes(name)); // gelezen in de oude overdracht
// Open overdrachten: alles wat je nog niet gelezen hebt van de afgelopen 14 dagen
const DB_OPEN_DAGEN = 14;
const dbOpenFrom = (todayKey) => {
  const [y, m, d] = todayKey.split("-").map(Number);
  return toIsoDate(new Date(y, m - 1, d - DB_OPEN_DAGEN, 12));
};

// Eén overdracht in de lijst
// "Niets bijzonders" bij een activiteit: één regel, geen kaart
const DbNietsRegel = ({ it, magWeg, onDelete }) => (
  <div className="flex items-center gap-1.5 py-1.5 text-xs" style={{ color: C.Pine }}>
    <CheckCircle size={13} />
    <span className="font-semibold">Niets bijzonders</span>
    <span className="text-gray-500">· {it.author}</span>
    {magWeg && onDelete && (
      <button onClick={() => onDelete(it)} className="ml-auto text-gray-400 hover:text-[#700606] p-1" aria-label="Weghalen" title="Weghalen">
        <X size={12} />
      </button>
    )}
  </div>
);

const DbFeedItem = ({
  it,
  me,
  staffList,
  canModerate,
  isNew,
  onRead,
  onEdit,
  onDelete,
  showDate = false,
  reacties = [],
  onReact,
  onDeleteReact,
  verwacht = null, // namen die het zouden moeten lezen (wie die dag werkt)
  compact = false, // rustige weergave in het tabblad Overdracht
  inContext = false, // activiteit/afdeling staat al als kopje erboven
}) => {
  const [editing, setEditing] = useState(false);
  const [heel, setHeel] = useState(false); // lange tekst helemaal tonen
  const [draft, setDraft] = useState("");
  const [lezersOpen, setLezersOpen] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [reply, setReply] = useState("");
  const sendReply = () => {
    const t = reply.trim();
    if (!t || !onReact) return;
    onReact(it, t);
    setReply("");
    setReplyOpen(false);
  };
  const af = dbAfdeling(it._afd);
  // Lezers: nieuwe leesbevestigingen (met tijd) + gelezen in de oude overdracht
  const lezers = (() => {
    const m = new Map();
    Object.values(it._reads || {}).forEach((r) => r && r.name && m.set(r.name, r.at || null));
    (it._legacyReadBy || []).forEach((n) => n && !m.has(n) && m.set(n, null));
    return [...m.entries()]
      .map(([name, at]) => ({ name, at }))
      .sort((a, b) => (b.at || 0) - (a.at || 0) || a.name.localeCompare(b.name));
  })();
  const names = lezers.map((l) => l.name);
  const nogNiet = verwacht
    ? [...verwacht].filter((n) => n && n !== it.author && !names.includes(n)).sort((a, b) => a.localeCompare(b))
    : [];
  const read = dbFeedRead(it, me);
  const mayEdit = !it._legacy && (it.author === me || canModerate);
  const typeLines = Object.entries(DB_FIELD_LABELS).filter(([k]) => it[k]);
  return (
    <div
      className={`flex ${compact ? "gap-2.5 py-2.5" : "gap-3 py-3"} border-t border-gray-100 first:border-t-0 scroll-mt-40`}
      style={isNew ? { boxShadow: `inset 3px 0 0 ${C.Blossom}`, paddingLeft: 10, marginLeft: -12 } : undefined}
      data-noswipe
      data-focus={it._readKey}
    >
      <ProfileAvatar name={it.author} staffList={staffList} size={compact ? 26 : 30} />
      <div className="flex-1 min-w-0">
        <div className={`flex ${compact ? "items-start" : "items-baseline"} gap-1.5 ${compact ? "" : "flex-wrap"}`}>
          <span className={compact ? "flex items-center gap-1.5 flex-wrap flex-1 min-w-0 min-h-[28px]" : "contents"}>
          <span className="text-sm font-semibold" style={{ color: C.Lagoon }}>
            {it.author}
          </span>
          <span className="text-xs text-gray-500">
            {showDate ? `${parseDateKey(it._date).toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" })} · ` : ""}
            {it.at && !it.vooraf ? dbTime(it.at) : ""}
            {it.editedAt ? " · bewerkt" : ""}
          </span>
          {it.kind === "door" && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: C.HoneyLight, color: C.Bark }}>
              Doorgegeven{it.voorDienst?.start ? ` · voor ${it.voorDienst.start}` : ""}
            </span>
          )}
          {it.vooraf && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: C.PineLight, color: C.Pine }}>
              FM-opmerking voor deze dag
            </span>
          )}
          {it.important && (
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: C.Bark }}>
              Belangrijk
            </span>
          )}
          {it.soort && it.soort !== "volgende" ? <DbSoortChip k={it.soort} /> : null}
          {dbSoortDetails(it).map((d) => (
            <span key={d} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white border" style={{ borderColor: dbSoort(it.soort)?.bg || "#E5E7EB", color: dbSoort(it.soort)?.fg || C.Lagoon }}>
              {d}
            </span>
          ))}
          {compact && inContext && it.crowd ? <DbCrowdChip lvl={it.crowd} /> : null}
          </span>
          {compact && !read && onRead && (
            <button
              onClick={() => onRead(it)}
              className="w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 active:scale-90 transition-transform"
              style={{ borderColor: C.Pine, color: C.Pine }}
              aria-label="Markeer als gelezen"
              title="Gelezen"
            >
              <Check size={15} />
            </button>
          )}
          {mayEdit && !editing && (
            <span className={`${compact && !read && onRead ? "" : "ml-auto"} flex items-center gap-2`}>
              {onEdit && (
                <button
                  onClick={() => {
                    setDraft(it.text || "");
                    setEditing(true);
                  }}
                  className="text-gray-500 hover:text-[#045E51]"
                  aria-label="Bewerken" title="Bewerken"
                >
                  <Edit2 size={13} />
                </button>
              )}
              {onDelete && (
                <button onClick={() => onDelete(it)} className="text-gray-500 hover:text-[#700606]" aria-label="Verwijderen" title="Verwijderen">
                  <Trash2 size={13} />
                </button>
              )}
            </span>
          )}
        </div>
        {!inContext && (it.kind === "act" || it._afd !== "alle") && (
          <div className="flex items-center gap-1.5 flex-wrap mt-1">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full bg-gray-50 border border-gray-100 text-gray-500">
              <DbAfdIcoon id={it._afd} size={11} />
              {it.kind === "act"
                ? `${it._title}${it._time ? ` ${it._time.split("-")[0].trim()}` : ""}`
                : af.label}
            </span>
            {it.crowd ? <DbCrowdChip lvl={it.crowd} /> : null}
            {it.colleagues ? <span className="text-xs text-gray-500">met {it.colleagues}</span> : null}
          </div>
        )}
        {it.colleagues && (inContext || !(it.kind === "act" || it._afd !== "alle")) && (
          <p className="text-xs text-gray-500 mt-0.5">met {it.colleagues}</p>
        )}
        {editing ? (
          <div className="mt-1.5 space-y-1.5">
            <textarea
              autoFocus
              rows={3}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#045E51]/40 resize-y"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (!draft.trim()) return; // leeg opslaan kan niet
                  onEdit(it, draft.trim());
                  setEditing(false);
                }}
                className="px-3 py-1 rounded-lg text-xs font-semibold text-white"
                style={{ backgroundColor: C.Pine }}
              >
                Opslaan
              </button>
              <button onClick={() => setEditing(false)} className="px-3 py-1 rounded-lg text-xs text-gray-500 bg-gray-100">
                Annuleren
              </button>
            </div>
          </div>
        ) : (
          <>
            {it.text && (
              <p
                className={`text-sm text-gray-800 whitespace-pre-wrap break-words ${compact ? "mt-0.5 leading-snug" : "mt-1 leading-relaxed"} ${
                  compact && !heel && it.text.length > 200 ? "line-clamp-3" : ""
                }`}
              >
                {highlightMentions(it.text, me)}
              </p>
            )}
            {compact && !heel && it.text && it.text.length > 200 && (
              <button onClick={() => setHeel(true)} className="text-xs font-semibold mt-0.5" style={{ color: C.Pine }}>
                meer lezen
              </button>
            )}
            {typeLines.map(([k, label]) => (
              <p key={k} className="text-sm text-gray-800 whitespace-pre-wrap mt-0.5">
                <span className="font-semibold">{label}:</span> {it[k]}
              </p>
            ))}
          </>
        )}
        {Array.isArray(it.photos) && it.photos.length > 0 && (
          <div className="flex gap-1.5 flex-wrap mt-1.5">
            {it.photos.map((pid) => (
              <DbPhoto key={pid} id={pid} size={56} />
            ))}
          </div>
        )}
        {Array.isArray(it.legacyPhotos) && it.legacyPhotos.length > 0 && (
          <div className="flex gap-1.5 flex-wrap mt-1.5">
            {it.legacyPhotos.map((u) => (
              <a key={u} href={u} target="_blank" rel="noreferrer">
                <img src={u} alt="" loading="lazy" className="w-14 h-14 object-cover rounded-lg border" />
              </a>
            ))}
          </div>
        )}
        {it.fmRemark && (
          <p className="text-xs mt-1 px-2 py-1 rounded-lg bg-[#FFE2A1] text-[#7A5226]">FM: {it.fmRemark}</p>
        )}
        <div className={compact ? "flex items-center gap-3 mt-1" : ""}>
        <button
          type="button"
          onClick={() => setLezersOpen((v) => !v)}
          aria-expanded={lezersOpen}
          className={`${compact ? "min-h-[32px] min-w-0" : "mt-1.5 min-h-[36px] max-w-full"} -mx-1 px-1 rounded-lg flex items-center gap-2 text-xs text-gray-500 active:bg-gray-50`}
        >
          {names.length > 0 ? (
            <span className="flex -space-x-1.5 flex-shrink-0">
              {names.slice(0, compact ? 3 : 5).map((n) => (
                <span key={n} className="rounded-full ring-2 ring-white">
                  <ProfileAvatar name={n} staffList={staffList} size={20} />
                </span>
              ))}
            </span>
          ) : (
            <Eye size={13} />
          )}
          <span className="truncate">
            {compact
              ? names.length > 0
                ? `${names.length} gelezen`
                : "Nog niemand gelezen"
              : names.length > 0
              ? `Gelezen door ${names.length}`
              : "Nog door niemand gelezen"}
            {nogNiet.length > 0 ? ` · ${nogNiet.length} nog niet` : ""}
          </span>
          {(names.length > 0 || nogNiet.length > 0) &&
            (lezersOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
        </button>
        {compact && onReact && (
          <button
            onClick={() => setReplyOpen(true)}
            className="min-h-[32px] px-1 text-xs font-semibold flex items-center gap-1 flex-shrink-0"
            style={{ color: C.Lagoon }}
          >
            <MessageCircle size={13} /> Reageer{reacties.length > 0 ? ` (${reacties.length})` : ""}
          </button>
        )}
        </div>
        {lezersOpen && (names.length > 0 || nogNiet.length > 0) && (
          <div className="mt-1 rounded-xl border border-gray-100 bg-gray-50/70 p-2.5 space-y-2" data-noswipe>
            {names.length > 0 && (
              <div>
                <p className="text-xs font-semibold mb-1" style={{ color: C.Pine }}>
                  Gelezen ({names.length})
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
                  {lezers.map((l) => (
                    <li key={l.name} className="flex items-center gap-2 text-sm text-gray-800 min-w-0">
                      <ProfileAvatar name={l.name} staffList={staffList} size={22} />
                      <span className="truncate flex-1">{l.name}</span>
                      {l.at ? <span className="text-xs text-gray-500 tabular-nums flex-shrink-0">{dbAgo(l.at)}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {nogNiet.length > 0 && (
              <div>
                <p className="text-xs font-semibold mb-1" style={{ color: C.Bark }}>
                  Nog niet gelezen ({nogNiet.length})
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
                  {nogNiet.map((n) => (
                    <li key={n} className="flex items-center gap-2 text-sm text-gray-500 min-w-0">
                      <span className="opacity-60">
                        <ProfileAvatar name={n} staffList={staffList} size={22} />
                      </span>
                      <span className="truncate">{n}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        {!compact && ((!read && onRead) || onReact) && (
          <div className="flex items-center gap-2 mt-1.5">
            {!read && onRead && (
              <button
                onClick={() => onRead(it)}
                className="h-10 px-3.5 rounded-xl text-sm font-semibold flex items-center gap-1.5 text-white active:scale-95 transition-transform"
                style={{ backgroundColor: C.Pine }}
              >
                <Check size={15} /> Gelezen
              </button>
            )}
            {onReact && (
              <button
                onClick={() => setReplyOpen(true)}
                className="h-10 px-3.5 rounded-xl text-sm font-semibold flex items-center gap-1.5 border border-gray-200 bg-white active:scale-95 transition-transform"
                style={{ color: C.Lagoon }}
              >
                <MessageCircle size={15} /> Reageer
                {reacties.length > 0 ? ` (${reacties.length})` : ""}
              </button>
            )}
          </div>
        )}
        {reacties.length > 0 && (
          <div className="mt-2 space-y-2 pl-3 border-l-2" style={{ borderColor: C.PineLight }}>
            {reacties.map((r) => (
              <div key={r.id} className="flex gap-2">
                <ProfileAvatar name={r.author} staffList={staffList} size={22} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-xs font-semibold" style={{ color: C.Lagoon }}>{r.author}</span>
                    <span className="text-xs text-gray-500">{dbTime(r.at)}</span>
                    {onDeleteReact && (r.author === me || canModerate) && (
                      <button
                        onClick={() => onDeleteReact(it, r)}
                        className="ml-auto text-gray-500 hover:text-[#700606]"
                        aria-label="Verwijderen" title="Verwijderen"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                    {highlightMentions(r.text, me)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
        {replyOpen &&
          createPortal(
            <div
              className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center bg-[#00192F]/45 sm:p-4"
              onClick={() => {
                setReplyOpen(false);
              }}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
              data-noswipe
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={`reageer-${it._readKey || it.id}`}
                className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-xl flex flex-col animate-fade-in-up"
                style={{ maxHeight: "88vh", paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-gray-100">
                  <ProfileAvatar name={it.author} staffList={staffList} size={36} />
                  <div className="flex-1 min-w-0">
                    <p id={`reageer-${it._readKey || it.id}`} className="text-base font-semibold" style={{ color: C.Lagoon }}>
                      Reageren op {it.author}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {it.kind === "act" ? it._title : "Overdracht"}
                      {it.at ? ` · ${dbTime(it.at)}` : ""}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setReplyOpen(false)}
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: "#F3F4F6" }}
                    aria-label="Sluiten"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="px-4 py-3 overflow-y-auto space-y-3">
                  {it.text && (
                    <div
                      className="rounded-xl px-3 py-2 text-sm text-gray-800 whitespace-pre-wrap break-words max-h-40 overflow-y-auto"
                      style={{ backgroundColor: C.PineLight }}
                    >
                      {highlightMentions(it.text, me)}
                    </div>
                  )}
                  {reacties.length > 0 && (
                    <div className="space-y-2">
                      {reacties.map((r) => (
                        <div key={r.id} className="flex gap-2">
                          <ProfileAvatar name={r.author} staffList={staffList} size={22} />
                          <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                            <span className="font-semibold" style={{ color: C.Lagoon }}>{r.author}</span>{" "}
                            {r.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  <MentionTextarea
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-base outline-none focus:ring-2 focus:ring-[#045E51]/40 resize-none h-32"
                    placeholder={`Schrijf je reactie aan ${it.author}… (@naam om iemand te taggen)`}
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    autoFocus
                    staffList={staffList}
                    loggedInUserName={me}
                  />
                  <p className="text-xs text-gray-500">
                    {it.author !== me ? `${it.author} krijgt hier een melding van.` : "Je reageert op je eigen overdracht."}
                  </p>
                </div>
                <div className="px-4 pt-2 flex gap-2 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => {
                      setReplyOpen(false);
                      setReply("");
                    }}
                    className="cp-btn-secondary flex-1"
                  >
                    Annuleren
                  </button>
                  <button
                    type="button"
                    onClick={sendReply}
                    disabled={!reply.trim()}
                    className="flex-1 h-11 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-1.5 transition-opacity active:scale-[0.98]"
                    style={{ backgroundColor: C.Pine, opacity: reply.trim() ? 1 : 0.4 }}
                  >
                    <Send size={15} /> Plaatsen
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
      </div>
    </div>
  );
};

// Schrijfvak voor een overdracht (algemeen of bij een activiteit)
// Prijs van een activiteit in het programma aangepast? Zet die door naar alle
// ingeplande keren van dezelfde activiteit (zelfde titel, vandaag en later) en
// naar de bibliotheek, zodat hij bij nieuw inplannen terugkomt.
// Geeft { patch, aantal } terug (patch = { schedule, activityLibrary? }).
const prijsDoorzetten = (appData, schedule, act) => {
  const titel = String(act?.title || "").trim().toLowerCase();
  if (!titel) return { patch: { schedule }, aantal: 0 };
  const vandaag = toIsoDate(new Date());
  let aantal = 0;
  const nieuwSchedule = (schedule || []).map((s) => {
    if (!s || s.id === act.id) return s;
    if (String(s.title || "").trim().toLowerCase() !== titel) return s;
    if (String(s.date || "").slice(0, 10) < vandaag) return s;
    if (String(s.price || "") === String(act.price || "") && !!s.priceVanaf === !!act.priceVanaf) return s;
    aantal++;
    return { ...s, price: act.price || "", priceVanaf: !!act.priceVanaf };
  });
  const patch = { schedule: nieuwSchedule };
  const lib = appData?.activityLibrary;
  if (lib && typeof lib === "object") {
    let gewijzigd = false;
    const nieuwLib = {};
    Object.entries(lib).forEach(([cat, lijst]) => {
      nieuwLib[cat] = Array.isArray(lijst)
        ? lijst.map((t) => {
            if (!t || String(t.title || t.name || "").trim().toLowerCase() !== titel) return t;
            if (String(t.price || "") === String(act.price || "") && !!t.priceVanaf === !!act.priceVanaf) return t;
            gewijzigd = true;
            return { ...t, price: act.price || "", priceVanaf: !!act.priceVanaf };
          })
        : lijst;
    });
    if (gewijzigd) patch.activityLibrary = nieuwLib;
  }
  return { patch, aantal };
};

// Bibliotheek per afdeling. Seizoensactiviteiten (en andere eigen categorieën)
// staan in hun eigen lijst, maar horen bij de afdeling in hun `type`
// (kleur, taken, promoscherm). Gebruik dit overal waar crea/entertainment/orry
// gelezen wordt, zodat seizoensactiviteiten gewoon meedoen.
const BIB_AFDELINGEN = ["crea", "entertainment", "orry"];
const bibPerAfdeling = (lib) => {
  const uit = { crea: [], entertainment: [], orry: [] };
  Object.entries(lib || {}).forEach(([cat, lijst]) => {
    if (!Array.isArray(lijst)) return;
    lijst.forEach((t) => {
      if (!t) return;
      const afd = BIB_AFDELINGEN.includes(cat)
        ? cat
        : BIB_AFDELINGEN.includes(t.type)
        ? t.type
        : "entertainment";
      uit[afd].push(t);
    });
  });
  return uit;
};

// Welke versie draait er? (Vercel geeft de commit mee bij het bouwen)
const APP_VERSIE = (() => {
  const sha = String(process.env.REACT_APP_VERCEL_GIT_COMMIT_SHA || "").slice(0, 7);
  const tekst = String(process.env.REACT_APP_VERCEL_GIT_COMMIT_MESSAGE || "").split("\n")[0].slice(0, 60);
  return sha ? `Versie ${sha}${tekst ? ` · ${tekst}` : ""}` : "Versie lokaal";
})();
// 📧 Automatische mail via /api/mail (Gmail kidsentertainment.lp@gmail.com).
// aan = adres of lijst; geeft { ok } of { ok: false, fout } terug.
const MAIL_AFZENDER = "kidsentertainment.lp@gmail.com";
const stuurMail = async ({ aan, onderwerp, tekst, html }) => {
  try {
    const r = await fetch("/api/mail", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ aan, onderwerp, tekst, html }),
    });
    const d = await r.json().catch(() => ({}));
    return r.ok ? { ok: true } : { ok: false, fout: d.fout || `Fout ${r.status}` };
  } catch (e) {
    return { ok: false, fout: "Geen verbinding met de mailserver" };
  }
};

// 📎 Weekoverzicht (A3, zoals "Weekoverzicht printen") als pdf-bijlage mailen via /api/weekpdf.
// Lukt de pdf niet, dan gaat de mail zonder bijlage (met het rooster als tekst) en melden we dat.
const stuurWeekPdf = async ({ appData, vrijdag, aan, onderwerp, tekst, html }) => {
  const { html: printHtml, weekLabel } = weekPrintHtml(appData, vrijdag, { basis: window.location.origin, seed: SEED_PROFILE_PHOTOS });
  try {
    const r = await fetch("/api/weekpdf", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ aan, onderwerp, tekst, html, printHtml, bestandsnaam: `${weekLabel.replace(/[^\w –-]/g, "")}.pdf` }),
    });
    const d = await r.json().catch(() => ({}));
    if (r.ok) return { ok: true, pdf: true };
    const fout = d.fout || `fout ${r.status}`;
    const melding = `De pdf-bijlage kon niet gemaakt worden: ${fout}`;
    const zonder = await stuurMail({
      aan,
      onderwerp,
      tekst: `${tekst}\n\n(${melding})`,
      html: html ? html.replace("</body>", `<p style="font:14px Arial;color:#700606;padding:12px 16px">${melding}</p></body>`) : undefined,
    });
    return { ...zonder, pdf: false, fout };
  } catch (e) {
    const zonder = await stuurMail({ aan, onderwerp, tekst, html });
    return { ...zonder, pdf: false, fout: "Geen verbinding met de pdf-server" };
  }
};

// ✉️ Nette mail in CP-stijl: geeft { html, tekst } (tekst = reserve voor
// mailprogramma's zonder opmaak). Kleuren uit de officiële paren.
const MAIL_PAREN = {
  groen: { donker: "#045E51", licht: "#B1EDA6", tint: "#DAFFD1" },
  blauw: { donker: "#00192F", licht: "#9DB4F6", tint: "#C4D4FF" },
  roze: { donker: "#700606", licht: "#FF699B", tint: "#FFB0D6" },
  geel: { donker: "#7A5226", licht: "#FFCA55", tint: "#FFE2A1" },
};
const mailOpmaak = ({ kleur = "groen", label = "", titel = "", aanhef = "", intro = "", rijen = [], notitie = "", knop = null, afsluiter = "Team Kids & Entertainment" }) => {
  const p = MAIL_PAREN[kleur] || MAIL_PAREN.groen;
  const e = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  const br = (s) => e(s).replace(/\n/g, "<br>");
  const font = "Bagoss, 'Helvetica Neue', Arial, sans-serif";
  // Effen donkere achtergrond in het kleurpaar met lichte tekst (zoals het
  // promoscherm): mail-apps in donkere modus kleuren dit niet om.
  const vlak = (k) => `background-color:${k};background-image:linear-gradient(${k},${k})`;
  const glas = "rgba(255,255,255,0.10)";
  const rijHtml = rijen
    .filter(([, w]) => String(w ?? "").trim())
    .map(
      ([k, w], i, arr) => `<tr>
        <td style="padding:9px 0;${i < arr.length - 1 ? `border-bottom:1px solid rgba(255,255,255,0.18);` : ""}font:500 13px ${font};color:${p.licht};width:38%;vertical-align:top">${e(k)}</td>
        <td style="padding:9px 0;${i < arr.length - 1 ? `border-bottom:1px solid rgba(255,255,255,0.18);` : ""}font:500 15px ${font};color:#ffffff;vertical-align:top">${br(w)}</td>
      </tr>`
    )
    .join("");
  const html = `<!doctype html><html lang="nl"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<meta name="color-scheme" content="light dark"><meta name="supported-color-schemes" content="light dark">
<style>@font-face{font-family:Bagoss;src:url(https://www.kidsentertainmentlp.nl/fonts/bagos-medium.woff2) format("woff2");font-weight:500}</style></head>
<body style="margin:0;padding:0;${vlak(p.donker)}">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${vlak(p.donker)};padding:28px 14px"><tr><td align="center">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:560px">
  <tr><td style="padding:4px 6px 18px">
    <div style="font:500 12px ${font};letter-spacing:.04em;text-transform:uppercase;color:${p.licht}">Kids &amp; Entertainment · Limburgse Peel</div>
    ${label ? `<div style="display:inline-block;margin-top:14px;padding:5px 13px;border-radius:999px;${vlak(p.licht)};color:${p.donker};font:500 12px ${font}">${e(label)}</div>` : ""}
    <div style="margin-top:12px;font:500 26px/1.25 ${font};color:#ffffff">${e(titel)}</div>
  </td></tr>
  <tr><td style="padding:0 6px">
    ${aanhef ? `<p style="margin:0 0 10px;font:500 16px ${font};color:${p.licht}">${e(aanhef)}</p>` : ""}
    ${intro ? `<p style="margin:0 0 18px;font:400 15px/1.55 ${font};color:#ffffff">${br(intro)}</p>` : ""}
    ${rijHtml ? `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:${glas};border-radius:16px;padding:8px 18px">${rijHtml}</table>` : ""}
    ${notitie ? `<p style="margin:18px 0 0;font:400 14px/1.5 ${font};color:${p.licht}">${br(notitie)}</p>` : ""}
    ${knop ? `<p style="margin:22px 0 4px"><a href="${e(knop.url)}" style="display:inline-block;padding:12px 22px;border-radius:12px;${vlak(p.licht)};color:${p.donker};text-decoration:none;font:500 15px ${font}">${e(knop.label)}</a></p>` : ""}
  </td></tr>
  <tr><td style="padding:22px 6px 6px">
    <p style="margin:0;font:500 15px ${font};color:#ffffff">Alvast bedankt!</p>
    <p style="margin:2px 0 0;font:400 14px ${font};color:${p.licht}">${e(afsluiter)}</p>
    <p style="margin:22px 0 0;font:400 11px ${font};color:${p.licht};opacity:.8">Automatisch verstuurd vanuit de Kids &amp; Entertainment-app.</p>
  </td></tr>
</table></td></tr></table></body></html>`;
  const tekst = [
    titel,
    "",
    aanhef,
    intro,
    "",
    ...rijen.filter(([, w]) => String(w ?? "").trim()).map(([k, w]) => `${k}: ${w}`),
    ...(notitie ? ["", notitie] : []),
    ...(knop ? ["", `${knop.label}: ${knop.url}`] : []),
    "",
    "Alvast bedankt!",
    afsluiter,
  ]
    .filter((x, i, a) => !(x === "" && a[i - 1] === ""))
    .join("\n");
  return { html, tekst };
};

// 📅 Weekrooster per mail (vrijdagochtend): de week vr t/m do die vandaag begint
const weekroosterMail = (appData, vrijdag) => {
  const dagen = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(vrijdag);
    d.setDate(d.getDate() + i);
    return d;
  });
  const tijd = (x) => String(x || "").slice(0, 5);
  const rijen = dagen.map((d) => {
    const dk = toIsoDate(d);
    const label = d.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "short" });
    const ws = (appData.workSchedule?.[dk] || []).filter((x) => x && x.matchedUser);
    // Per dienst (zelfde tijd + soort) de namen bij elkaar
    const groep = new Map();
    ws.forEach((x) => {
      const soort = x.title || RM_TYPES[x.type]?.label || x.type || "";
      const k = `${tijd(x.start)}|${tijd(x.end)}|${soort}`;
      if (!groep.has(k)) groep.set(k, { start: tijd(x.start), end: tijd(x.end), soort, namen: [] });
      if (!groep.get(k).namen.includes(x.matchedUser)) groep.get(k).namen.push(x.matchedUser);
    });
    const regels = [...groep.values()]
      .sort((a, b) => a.start.localeCompare(b.start) || a.soort.localeCompare(b.soort))
      .map((g) => `${g.start}–${g.end} ${g.soort}: ${g.namen.join(", ")}`);
    // Open diensten (nog niemand ingepland) uit de roostermaker
    (appData.roosterDiensten?.[dk] || [])
      .filter((x) => x && !(x.assigned || []).filter(Boolean).length)
      .forEach((x) => regels.push(`${tijd(x.start)}–${tijd(x.end)} ${x.title || RM_TYPES[x.type]?.label || x.type || "Dienst"}: OPEN`));
    const dv = appData.dailyDv?.[dk];
    if (dv) regels.unshift(`Dagverantwoordelijke: ${dv}`);
    return [label.charAt(0).toUpperCase() + label.slice(1), regels.length ? regels.join("\n") : "Geen diensten"];
  });
  const open = rijen.reduce((n, [, w]) => n + (w.match(/: OPEN/g) || []).length, 0);
  const van = dagen[0].toLocaleDateString("nl-NL", { day: "numeric", month: "long" });
  const tot = dagen[6].toLocaleDateString("nl-NL", { day: "numeric", month: "long" });
  return {
    onderwerp: `Rooster ${van} – ${tot}`,
    ...mailOpmaak({
      kleur: "groen",
      label: "Weekrooster",
      titel: `Rooster ${van} – ${tot}`,
      aanhef: "Goedemorgen Maik,",
      intro: "Hier is het rooster van deze week (vrijdag t/m donderdag). Het weekoverzicht om te printen zit als pdf (A3) in de bijlage.",
      rijen,
      notitie: open ? `Let op: er ${open === 1 ? "staat nog 1 open dienst" : `staan nog ${open} open diensten`}.` : "Alle diensten zijn ingevuld.",
      knop: { label: "Open het rooster in de app", url: "https://www.kidsentertainmentlp.nl/?view=rooster" },
      afsluiter: "De Kids & Entertainment-app",
    }),
  };
};

// Gratis = "gratis"/"free" of een bedrag van 0 (0, 0,00, € 0, …)
const isGratis = (p) => {
  const t = String(p ?? "").trim();
  if (!t) return false;
  if (/gratis|free/i.test(t)) return true;
  return /^[€\s0.,-]+$/.test(t) && /0/.test(t);
};
// "Vanaf"-prijs: toont "vanaf " voor de prijs (niet bij gratis)
const vanafVoor = (x) =>
  x && x.priceVanaf && x.price && !isGratis(x.price) ? "vanaf " : "";
// Prijs zoals hij getoond wordt: "Gratis" bij 0, anders (vanaf) het bedrag
const prijsTekst = (x) => (isGratis(x?.price) ? "Gratis" : `${vanafVoor(x)}${x?.price ?? ""}`);

// Alle datums van..t/m (max. 62 dagen), als "YYYY-MM-DD"
const dbDagenTussen = (van, tot) => {
  const uit = [];
  const [y, m, d] = String(van).split("-").map(Number);
  const eind = String(tot || van);
  for (let i = 0; i < 62; i++) {
    const k = toIsoDate(new Date(y, m - 1, d + i, 12));
    if (k > eind) break;
    uit.push(k);
  }
  return uit;
};

// 📝 Concept bewaren: wat je typt blijft op dit toestel bewaard (3 dagen) tot
// je het verstuurt of wist. Geeft [waarde, zet, hersteld, wis].
const CONCEPT_DAGEN = 3;
const laadConcept = (sleutel, begin) => {
  try {
    const r = JSON.parse(localStorage.getItem(sleutel) || "null");
    if (r && r.t && Date.now() - r.t < CONCEPT_DAGEN * 864e5) return { w: r.w, hersteld: true };
  } catch (e) {}
  return { w: begin, hersteld: false };
};
const useConcept = (sleutel, begin) => {
  const [staat, setStaat] = useState(() => ({ k: sleutel, ...laadConcept(sleutel, begin) }));
  let huidig = staat;
  if (staat.k !== sleutel) {
    huidig = { k: sleutel, ...laadConcept(sleutel, begin) };
    setStaat(huidig);
  }
  const beginJson = JSON.stringify(begin);
  useEffect(() => {
    try {
      if (JSON.stringify(staat.w) === beginJson) localStorage.removeItem(staat.k);
      else localStorage.setItem(staat.k, JSON.stringify({ w: staat.w, t: Date.now() }));
    } catch (e) {}
  }, [staat, beginJson]);
  const zet = useCallback(
    (v) => setStaat((s) => ({ k: s.k, w: typeof v === "function" ? v(s.w) : v, hersteld: false })),
    []
  );
  const wis = useCallback(() => setStaat((s) => ({ k: s.k, w: JSON.parse(beginJson), hersteld: false })), [beginJson]);
  return [huidig.w, zet, huidig.hersteld, wis];
};
// Klein regeltje "Concept hersteld · Wissen"
const ConceptMelding = ({ zichtbaar, onWis }) =>
  zichtbaar ? (
    <p className="text-xs flex items-center gap-2" style={{ color: C.Bark }}>
      📝 Concept van eerder hersteld
      <button type="button" onClick={onWis} className="underline font-semibold">
        Wissen
      </button>
    </p>
  ) : null;

// Soort overdracht (optioneel, één per bericht)
const DB_SOORTEN = [
  { k: "materiaal", l: "Materiaal op", bg: C.HoneyLight, fg: C.Bark, Ic: ShoppingCart },
  { k: "gast", l: "Gast", bg: C.SkyLight, fg: C.Lagoon, Ic: Users },
  { k: "tip", l: "Tip", bg: C.PineLight, fg: C.Pine, Ic: Star },
  { k: "incident", l: "Incident", bg: C.BlossomLight, fg: C.Sunset, Ic: AlertTriangle },
  { k: "volgende", l: "Voor volgende dienst", bg: C.SkyLight, fg: C.Lagoon, Ic: ArrowRight },
  { k: "schoonmaak", l: "Schoonmaak/opruimen", bg: C.PineLight, fg: C.Pine, Ic: Sparkles },
  { k: "idee", l: "Idee", bg: C.HoneyLight, fg: C.Bark, Ic: Lightbulb },
];
const dbSoort = (k) => DB_SOORTEN.find((x) => x.k === k) || null;
// Extra menuutje per soort (alles optioneel). t: chips | janee | tekst | schakel; kort = label in het bericht
const DB_SOORT_MENU = {
};
// Bij het schrijven kies je alleen nog "Materiaal op"; oude soorten blijven zichtbaar bij oude berichten
const DB_SOORTEN_KIES = DB_SOORTEN.filter((x) => x.k === "materiaal");
// Ingevulde extra's als korte labels (voor in het bericht en zoeken)
const dbSoortDetails = (it) => {
  const info = it?.soortInfo || {};
  return (DB_SOORT_MENU[it?.soort] || [])
    .map((v) => {
      const w = info[v.k];
      if (w === undefined || w === null || w === "" || w === false) return null;
      if (v.t === "janee") return `${v.kort || v.l}: ${w}`;
      if (v.t === "schakel") return v.kort || v.l;
      return String(w);
    })
    .filter(Boolean);
};
const DbSoortMenu = ({ soort, info, zet, kaal = false }) => {
  const velden = DB_SOORT_MENU[soort];
  const s = dbSoort(soort);
  if (!velden || !s) return null;
  const knop = (aan) =>
    aan ? { backgroundColor: s.fg, borderColor: s.fg, color: "white" } : { backgroundColor: "white", borderColor: "#E5E7EB", color: s.fg };
  return (
    <div className={kaal ? "mt-2 space-y-2" : "mt-2 rounded-xl px-3 py-2 space-y-2"} style={kaal ? undefined : { backgroundColor: s.bg }}>
      {velden.map((v) => {
        const w = info[v.k];
        const hint = v.hint && w ? v.hint[w] : null;
        return (
          <div key={v.k}>
            {v.t === "schakel" ? (
              <label className="flex items-center gap-2 text-xs font-semibold min-h-[32px]" style={{ color: s.fg }}>
                <input type="checkbox" checked={!!w} onChange={(e) => zet(v.k, e.target.checked)} className="w-4 h-4" style={{ accentColor: s.fg }} />
                {v.l}
              </label>
            ) : (
              <>
                <p className="text-xs font-semibold mb-1" style={{ color: s.fg }}>
                  {v.l}
                </p>
                {v.t === "tekst" ? (
                  <input
                    value={w || ""}
                    onChange={(e) => zet(v.k, e.target.value)}
                    placeholder={v.ph}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white"
                  />
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {(v.t === "janee" ? ["ja", "nee"] : v.o).map((o) => (
                      <button
                        key={o}
                        type="button"
                        onClick={() => zet(v.k, w === o ? "" : o)}
                        aria-pressed={w === o}
                        className="h-8 px-3 rounded-full border text-xs font-semibold active:scale-95 transition-transform"
                        style={knop(w === o)}
                      >
                        {v.t === "janee" ? (o === "ja" ? "Ja" : "Nee") : o}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
            {hint && (
              <p className="text-[11px] mt-1" style={{ color: s.fg }}>
                {hint}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};
const DbSoortChip = ({ k }) => {
  const s = dbSoort(k);
  if (!s) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: s.bg, color: s.fg }}>
      <s.Ic size={11} /> {s.l}
    </span>
  );
};

const DbComposer = ({
  activities,
  actId,
  setActId,
  staffList,
  me,
  defaultAfd = "alle",
  onPost,
  appData = null,
  werkendeNamen = [],
  isFM = false,
  dateKey = null,
}) => {
  const [text, setText, conceptHersteld, wisConcept] = useConcept(
    `cp_concept_overdracht_${dbSlug(me || "")}_${actId || "dag"}`,
    ""
  );
  // FM: opmerking vooruit plaatsen op een andere dag (of periode)
  const [voorVan, setVoorVan] = useState(dateKey || "");
  const [voorTot, setVoorTot] = useState("");
  useEffect(() => {
    setVoorVan(dateKey || "");
    setVoorTot("");
  }, [dateKey]);
  const [met, setMet] = useState([]); // met wie stond je
  const [metAnder, setMetAnder] = useState(false);
  const eigenAfd = !isFM && defaultAfd === "alle" ? dbAfdVanAuteur(appData, dateKey, me) : null;
  const [afd, setAfd] = useState(eigenAfd || defaultAfd);
  const [afdGekozen, setAfdGekozen] = useState(false); // zelf een afdeling gekozen (bv. bewust "Iedereen")
  const [important, setImportant] = useState(false);
  const [soort, setSoort] = useState("");
  const [bestel, setBestel] = useState(""); // bij Materiaal op: wat moet er besteld worden
  const [soortInfo, setSoortInfo] = useState({}); // extra menuutje per soort
  const zetInfo = (k, w) => setSoortInfo((o) => ({ ...o, [k]: w }));
  const [crowd, setCrowd] = useState(null);
  const [extra, setExtra] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const act = actId ? activities.find((a) => String(a.id) === String(actId)) : null;
  const typeField = act ? DB_TYPE_FIELD[dbActType(act.title)] : null;
  useEffect(() => {
    setAfd(act ? dbActAfdeling(act) : eigenAfd || defaultAfd);
    setAfdGekozen(false);
    setCrowd(null);
    setExtra("");
    setErr("");
    // Bij een activiteit: wie erop ingepland stond alvast aanvinken
    setMet(act ? (act.staff || []).filter((n) => n && n !== me) : []);
  }, [actId]); // eslint-disable-line
  // Keuzes: wie er vandaag werkt + wie op de activiteit staat (zonder jezelf)
  const metOpties = [
    ...new Set([...(act?.staff || []), ...werkendeNamen, ...met].filter((n) => n && n !== me)),
  ].sort((a, b) => a.localeCompare(b));
  const anderen = (staffList || [])
    .filter((x) => x && x.name && !x.inactive && !teamVerborgen(x) && x.name !== me && !metOpties.includes(x.name))
    .map((x) => x.name)
    .sort((a, b) => a.localeCompare(b));
  const wisselMet = (n) => setMet((m) => (m.includes(n) ? m.filter((x) => x !== n) : [...m, n]));
  const addFiles = (list) => {
    const nieuw = Array.from(list || [])
      .filter((f) => f.type?.startsWith("image/"))
      .map((file) => ({ file, url: URL.createObjectURL(file) }));
    setFiles((p) => [...p, ...nieuw].slice(0, 4));
  };
  const post = async () => {
    const t = text.trim();
    if (!t && !files.length && !extra.trim()) return;
    if (act && !crowd) {
      setErr("Kies eerst hoe druk het was");
      return;
    }
    let photos = [];
    if (files.length) {
      setBusy(true);
      try {
        photos = await Promise.all(files.map((f) => dbUploadPhoto(f.file)));
      } catch (e) {
        setBusy(false);
        cpToast("Foto uploaden lukt even niet: " + (e?.message || "onbekende fout"), "error");
        return;
      }
      setBusy(false);
    }
    if (soort === "materiaal" && !bestel.trim() && !t) {
      setErr("Vul in wat er op is");
      return;
    }
    onPost({
      text: t,
      afdeling: afd,
      afdGekozen,
      important,
      soort,
      bestel: soort === "materiaal" ? (bestel.trim() || t) : "",
      soortInfo: soort
        ? Object.fromEntries(
            Object.entries(soortInfo).filter(([k, w]) => (DB_SOORT_MENU[soort] || []).some((v) => v.k === k) && w !== "" && w !== false && w != null).map(([k, w]) => [k, typeof w === "string" ? w.trim() : w])
          )
        : {},
      photos,
      act,
      crowd,
      typeKey: typeField?.key,
      typeValue: extra.trim(),
      colleagues: met.join(", "),
      ...(isFM && !act && voorVan && (voorVan !== dateKey || voorTot)
        ? { dagen: dbDagenTussen(voorVan, voorTot || voorVan) }
        : {}),
    });
    setVoorVan(dateKey || "");
    setVoorTot("");
    setMet([]);
    setMetAnder(false);
    files.forEach((f) => URL.revokeObjectURL(f.url));
    setFiles([]);
    setText("");
    setExtra("");
    setCrowd(null);
    setImportant(false);
    setSoort("");
    setBestel("");
    setSoortInfo({});
    setActId(null);
  };
  const chip = "h-8 px-2.5 rounded-full border text-xs font-medium flex items-center gap-1.5 bg-white";
  return (
    <div
      className="bg-white rounded-2xl border shadow-sm p-3 space-y-2"
      style={{ borderColor: important ? C.Honey : "#F3F4F6" }}
    >
      <MentionTextarea
        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#045E51]/40 resize-none min-h-[64px]"
        placeholder={act ? `Hoe ging ${act.title}? Bijzonderheden, tips…` : "Schrijf een overdracht…"}
        value={text}
        onChange={(e) => setText(e.target.value)}
        staffList={staffList}
        loggedInUserName={me}
      />
      <ConceptMelding zichtbaar={conceptHersteld && !!String(text).trim()} onWis={wisConcept} />
      <div>
        <p className="text-xs font-semibold mb-1" style={{ color: C.Lagoon }}>
          Voorraad <span className="font-normal text-gray-500">(mag leeg)</span>
        </p>
        <div className="flex flex-wrap gap-1.5">
          {DB_SOORTEN_KIES.map((x) => {
            const aan = soort === x.k;
            return (
              <button
                key={x.k}
                type="button"
                onClick={() => {
                  setSoort(aan ? "" : x.k);
                  setSoortInfo({});
                }}
                aria-pressed={aan}
                className="h-9 px-3 rounded-full border text-xs font-semibold flex items-center gap-1.5 active:scale-95 transition-transform"
                style={aan ? { backgroundColor: x.fg, borderColor: x.fg, color: "white" } : { backgroundColor: x.bg, borderColor: x.bg, color: x.fg }}
              >
                <x.Ic size={13} /> {x.l}
              </button>
            );
          })}
        </div>
        {soort === "materiaal" && (
          <div className="mt-2 rounded-xl px-3 py-2" style={{ backgroundColor: C.HoneyLight }}>
            <label className="block text-xs font-semibold mb-1" style={{ color: C.Bark }}>
              Wat moet er besteld worden? Dit komt op de bestellijst.
            </label>
            <input
              value={bestel}
              onChange={(e) => setBestel(e.target.value)}
              placeholder="Bijv. groene en roze kralen"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white"
            />
          </div>
        )}
        {/* Extra vragen per soort (nu leeg; zie DB_SOORT_MENU) */}
        <DbSoortMenu soort={soort} info={soortInfo} zet={zetInfo} kaal />
      </div>
      {(metOpties.length > 0 || anderen.length > 0) && (
        <div>
          <p className="text-xs font-semibold mb-1" style={{ color: C.Lagoon }}>
            Met wie stond je?{met.length ? ` (${met.length})` : ""}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {metOpties.map((n) => {
              const aan = met.includes(n);
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => wisselMet(n)}
                  aria-pressed={aan}
                  className="h-9 pl-1 pr-3 rounded-full border text-xs font-semibold flex items-center gap-1.5 active:scale-95 transition-transform"
                  style={
                    aan
                      ? { backgroundColor: C.Pine, borderColor: C.Pine, color: "white" }
                      : { backgroundColor: "white", borderColor: "#E5E7EB", color: C.Lagoon }
                  }
                >
                  <ProfileAvatar name={n} staffList={staffList} size={24} />
                  {String(n).split(" ")[0]}
                  {aan && <Check size={13} strokeWidth={3} />}
                </button>
              );
            })}
            {anderen.length > 0 &&
              (metAnder ? (
                <select
                  autoFocus
                  value=""
                  onChange={(e) => {
                    if (e.target.value) wisselMet(e.target.value);
                    setMetAnder(false);
                  }}
                  onBlur={() => setMetAnder(false)}
                  className="h-9 px-2 rounded-full border border-gray-200 text-xs bg-white outline-none"
                >
                  <option value="">Kies collega…</option>
                  {anderen.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              ) : (
                <button
                  type="button"
                  onClick={() => setMetAnder(true)}
                  className="h-9 px-3 rounded-full border border-dashed text-xs font-semibold flex items-center gap-1 active:scale-95"
                  style={{ borderColor: "#D1D5DB", color: "#6B7280" }}
                >
                  <Plus size={13} /> Ander
                </button>
              ))}
          </div>
        </div>
      )}
      {act && (
        <div className="rounded-xl bg-gray-50 p-2.5 space-y-2">
          <div>
            <p className="text-xs font-semibold mb-1" style={{ color: err ? C.Sunset : C.Lagoon }}>
              Drukte {err && `— ${err.toLowerCase()}`}
            </p>
            <CrowdBar value={crowd} onChange={(v) => { setCrowd(v); setErr(""); }} compact type={act.type === "crea" ? "crea" : "ent"} />
          </div>
          {typeField && (
            <div>
              <p className="text-xs font-semibold mb-1" style={{ color: C.Lagoon }}>{typeField.label}</p>
              {KIEZER_CFG[typeField.key] ? (
                <LijstKiezer veld={typeField.key} value={extra} onChange={setExtra} appData={appData} />
              ) : typeField.multi ? (
                <textarea rows={2} className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm outline-none bg-white resize-y" value={extra} onChange={(e) => setExtra(e.target.value)} />
              ) : (
                <input className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm outline-none bg-white" value={extra} onChange={(e) => setExtra(e.target.value)} />
              )}
            </div>
          )}
        </div>
      )}
      {isFM && !act && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs font-semibold" style={{ color: C.Lagoon }}>Voor dag</span>
          <input
            type="date"
            value={voorVan}
            onChange={(e) => {
              setVoorVan(e.target.value);
              if (voorTot && e.target.value > voorTot) setVoorTot("");
            }}
            className="h-8 px-2 rounded-full border border-gray-200 text-xs bg-white outline-none"
          />
          <span className="text-xs text-gray-500">t/m</span>
          <input
            type="date"
            value={voorTot}
            min={voorVan || undefined}
            onChange={(e) => setVoorTot(e.target.value)}
            className="h-8 px-2 rounded-full border border-gray-200 text-xs bg-white outline-none"
          />
          {voorTot && (
            <button
              type="button"
              onClick={() => setVoorTot("")}
              className="text-xs text-gray-500"
              aria-label="Einddatum wissen"
            >
              <X size={12} />
            </button>
          )}
          {voorVan && voorVan !== dateKey && (
            <span className="text-xs" style={{ color: C.Bark }}>
              Verschijnt pas op {voorTot && voorTot !== voorVan ? "die dagen" : "die dag"} in de overdracht
            </span>
          )}
        </div>
      )}
      {files.length > 0 && (
        <div className="flex gap-1.5 flex-wrap">
          {files.map((f, i) => (
            <div key={f.url} className="relative">
              <img src={f.url} alt="" className="w-14 h-14 object-cover rounded-lg border" />
              <button
                onClick={() => { URL.revokeObjectURL(f.url); setFiles((p) => p.filter((_, j) => j !== i)); }}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#700606] text-white flex items-center justify-center"
                aria-label="Foto verwijderen"
              >
                <X size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-1.5 flex-wrap">
        <label className={chip} style={{ borderColor: act ? C.Pine : "#E5E7EB", color: act ? C.Pine : "#6B7280" }}>
          <CalendarIcon size={13} />
          <select
            value={actId || ""}
            onChange={(e) => setActId(e.target.value || null)}
            className="bg-transparent outline-none text-xs max-w-[140px]"
          >
            <option value="">Algemeen</option>
            {activities.map((a) => (
              <option key={a.id} value={String(a.id)}>
                {(a.time || "").split("-")[0].trim()} {a.title}
              </option>
            ))}
          </select>
        </label>
        <label className={chip} style={{ borderColor: "#E5E7EB", color: "#6B7280" }}>
          <Users size={13} />
          <select value={afd} onChange={(e) => { setAfd(e.target.value); setAfdGekozen(true); }} className="bg-transparent outline-none text-xs">
            {DB_AFDELINGEN.map((a) => (
              <option key={a.id} value={a.id}>{a.id === "alle" ? "Iedereen" : a.label}</option>
            ))}
          </select>
        </label>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        <button onClick={() => camRef.current?.click()} className={chip} style={{ borderColor: "#E5E7EB", color: "#6B7280" }} aria-label="Foto maken" title="Foto maken">
          <Camera size={14} />
        </button>
        <button onClick={() => fileRef.current?.click()} className={chip} style={{ borderColor: "#E5E7EB", color: "#6B7280" }} aria-label="Foto uit galerij" title="Foto uit galerij">
          <ImageIcon size={14} />
        </button>
        <button
          onClick={() => setImportant((v) => !v)}
          className={chip}
          style={important ? { borderColor: C.Honey, color: C.Bark, backgroundColor: C.HoneyLight } : { borderColor: "#E5E7EB", color: "#6B7280" }}
          aria-pressed={important}
        >
          <AlertTriangle size={13} /> Belangrijk
        </button>
        <div className="flex-1" />
        <button
          onClick={post}
          disabled={busy}
          className="h-9 px-4 rounded-xl text-sm font-semibold text-white flex items-center gap-1.5"
          style={{ backgroundColor: C.Pine, opacity: text.trim() || files.length || extra.trim() ? 1 : 0.45 }}
        >
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={14} />}
          Plaatsen
        </button>
      </div>
      {important && (
        <p className="text-xs" style={{ color: C.Bark }}>
          Belangrijk: iedereen ziet het bovenaan en bevestigt dat ze het gelezen hebben.
        </p>
      )}
    </div>
  );
};

// ── Uren invullen (vervangt "Dienst starten") ────────────────────────
// Vanaf een half uur voor het geplande einde vraagt de app telkens om de echte
// werktijden in te vullen, tot het gedaan is (ook de dag erna nog). Zo heeft de
// FM altijd kloktijden. "Later" = 20 minuten rust. Openen met de hand kan via
// window.dispatchEvent(new CustomEvent("uren-invullen", { detail: { datum } })).
const UREN_RUST_MIN = 20;
const urenIngevuld = (klok) => !!(klok && klok.in && klok.out);
// Welke dag vraagt om uren? Vandaag (vanaf eind-30) of gisteren (nog open)
const urenTeVragen = (appData, me) => {
  const nu = new Date();
  const nuMin = nu.getHours() * 60 + nu.getMinutes();
  const vandaag = toIsoDate(nu);
  const g = new Date(nu);
  g.setDate(g.getDate() - 1);
  const gisteren = toIsoDate(g);
  const vraag = (datum, isVandaag) => {
    const pl = dbPlanned(appData, datum, me);
    if (!pl || !pl.start) return null;
    if (urenIngevuld(appData.timeClock?.[datum]?.[me])) return null;
    if (isVandaag) {
      const s0 = dbToMin(pl.start);
      let e0 = dbToMin(pl.end);
      if (e0 == null) e0 = (s0 || 0) + 240;
      if (s0 != null && e0 < s0) e0 += 1440;
      if (nuMin < e0 - 30) return null;
    }
    return { datum, pl };
  };
  return vraag(gisteren, false) || vraag(vandaag, true);
};
const UrenHerinnering = ({ appData, me, updateData }) => {
  const [open, setOpen] = React.useState(null); // { datum, pl, handmatig }
  const [tik, setTik] = React.useState(0);
  const [tijd, setTijd] = React.useState({ in: "", out: "" });
  React.useEffect(() => {
    const t = setInterval(() => setTik((n) => n + 1), 60000);
    const h = (e) => {
      const datum = e.detail?.datum || toIsoDate(new Date());
      const pl = dbPlanned(appData, datum, me) || {};
      const klok = appData.timeClock?.[datum]?.[me] || {};
      setTijd({ in: klok.in ? dbHM(klok.in) : pl.start || "", out: klok.out ? dbHM(klok.out) : pl.end || "" });
      setOpen({ datum, pl, handmatig: true });
    };
    window.addEventListener("uren-invullen", h);
    return () => {
      clearInterval(t);
      window.removeEventListener("uren-invullen", h);
    };
  }, [appData, me]);
  // Automatisch tonen
  React.useEffect(() => {
    if (!me || open) return;
    const v = urenTeVragen(appData, me);
    if (!v) return;
    let rust = 0;
    try {
      rust = Number(sessionStorage.getItem(`urenRust_${v.datum}`) || 0);
    } catch (e) {}
    if (Date.now() < rust) return;
    const nuHM = dbHM(new Date().toISOString());
    const isVandaag = v.datum === toIsoDate(new Date());
    // eindtijd: gepland, of "nu" als de dienst al voorbij is (afgerond op 5 min)
    let out = v.pl.end || "";
    if (isVandaag && dbToMin(nuHM) > dbToMin(v.pl.end || "00:00")) {
      const m = Math.round(dbToMin(nuHM) / 5) * 5;
      out = `${String(Math.floor(m / 60) % 24).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
    }
    setTijd({ in: v.pl.start || "", out });
    setOpen({ ...v, handmatig: false });
  }, [appData, me, tik, open]); // eslint-disable-line
  if (!open || !me) return null;
  const later = () => {
    try {
      sessionStorage.setItem(`urenRust_${open.datum}`, String(Date.now() + UREN_RUST_MIN * 60000));
    } catch (e) {}
    setOpen(null);
  };
  const opslaan = () => {
    if (!tijd.in || !tijd.out) return;
    const [y, mo, d] = open.datum.split("-").map(Number);
    const volgende = toIsoDate(new Date(y, mo - 1, d + 1));
    const overMiddernacht = dbToMin(tijd.out) < dbToMin(tijd.in);
    const bestaand = appData.timeClock?.[open.datum]?.[me] || {};
    updateData({
      timeClock: {
        [open.datum]: {
          [me]: {
            in: dbIsoAt(open.datum, tijd.in),
            out: dbIsoAt(overMiddernacht ? volgende : open.datum, tijd.out),
            ingevuld: Date.now(),
            ...(bestaand.in ? { correctedBy: me, correctedAt: Date.now() } : {}),
          },
        },
      },
    });
    try {
      sessionStorage.removeItem(`urenRust_${open.datum}`);
    } catch (e) {}
    setOpen(null);
    window.dispatchEvent(new CustomEvent("uren-opgeslagen", { detail: { datum: open.datum } }));
    cpToast("Uren opgeslagen. Dank je!", "success");
  };
  const isVandaag = open.datum === toIsoDate(new Date());
  const datumTekst = isVandaag
    ? "vandaag"
    : new Date(open.datum + "T12:00:00").toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" });
  const pl = open.pl || {};
  const afwijking =
    tijd.in && tijd.out && pl.start && pl.end && (Math.abs(dbToMin(tijd.in) - dbToMin(pl.start)) >= 15 || Math.abs(dbToMin(tijd.out) - dbToMin(pl.end)) >= 15);
  return (
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-3"
      style={{ background: "rgba(0,25,47,0.45)" }}
      onClick={later}
    >
      <div
        className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl shadow-xl p-5"
        role="dialog"
        aria-modal="true"
        aria-labelledby="uren-titel"
        onClick={(ev) => ev.stopPropagation()}
        style={{ marginBottom: "max(0px, env(safe-area-inset-bottom))" }}
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: C.PineLight }}>
            <Clock size={20} style={{ color: C.Pine }} />
          </div>
          <div className="min-w-0">
            <p id="uren-titel" className="text-base font-semibold" style={{ color: C.Lagoon }}>
              Hoe laat heb je {datumTekst} gewerkt?
            </p>
            <p className="text-sm text-gray-500">
              {pl.start ? `Ingeroosterd ${pl.start}–${pl.end}` : "Niet ingeroosterd"} · pas aan als het anders was
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <label className="block">
            <span className="text-xs font-semibold text-gray-500">Begonnen om</span>
            <input
              type="time"
              value={tijd.in}
              onChange={(e) => setTijd((o) => ({ ...o, in: e.target.value }))}
              className="mt-1 w-full border border-gray-200 rounded-xl px-3 h-12 text-lg font-semibold tabular-nums"
              style={{ fontSize: 18 }}
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-gray-500">Klaar om</span>
            <input
              type="time"
              value={tijd.out}
              onChange={(e) => setTijd((o) => ({ ...o, out: e.target.value }))}
              className="mt-1 w-full border border-gray-200 rounded-xl px-3 h-12 text-lg font-semibold tabular-nums"
              style={{ fontSize: 18 }}
            />
          </label>
        </div>
        {afwijking && (
          <p className="text-xs mt-2 font-semibold" style={{ color: C.Bark }}>
            Wijkt af van het rooster. Dat mag, de FM ziet het bij de aanwezigheid.
          </p>
        )}
        <div className="flex gap-2 mt-4">
          <button onClick={later} className="flex-1 min-h-[44px] rounded-xl text-sm font-semibold text-gray-500 bg-gray-100">
            {open.handmatig ? "Annuleren" : "Later"}
          </button>
          <button
            onClick={opslaan}
            disabled={!tijd.in || !tijd.out}
            className="flex-1 min-h-[44px] rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-1.5"
            style={{ backgroundColor: C.Pine, opacity: tijd.in && tijd.out ? 1 : 0.5 }}
          >
            <Check size={16} /> Opslaan
          </button>
        </div>
      </div>
    </div>
  );
};

const getDagbordAttention = (appData, loggedInUserName) => {
  if (!loggedInUserName) return 0;
  const today = toIsoDate(new Date());
  const day = appData?.dagbord?.[today] || {};
  let n = 0;
  const b = day.briefing;
  if (b && b.text && !b.deleted && b.author !== loggedInUserName) {
    const r = day[`read_${dbSlug(loggedInUserName)}`];
    if (!r || (r.at || 0) < (b.updatedAt || 0)) n++;
  }
  for (const p of Object.values(appData?.openPunten || {})) {
    if (p && !p.deleted && p.status !== "opgelost" && p.assignee === loggedInUserName)
      n++;
  }
  n += dbCollectFeed(appData, dbOpenFrom(today), today).filter(
    (it) => !dbFeedRead(it, loggedInUserName)
  ).length;
  return n;
};

// Openstaand "ga naar" vanuit een melding (blijft staan tot het Dagbord het oppakt)
let _dagbordGoto = null;
const gaNaarDagbord = (doel) => {
  _dagbordGoto = doel;
  setTimeout(() => window.dispatchEvent(new CustomEvent("dagbord-goto", { detail: doel })), 60);
};

// Begroeting bovenaan het Dagbord: dagdeel, naam, datum en een seizoens-icoon
// in een officiële Sunset-vorm.
const DB_ZINNEN = [
  "Maak er een mooie dag van.",
  "Klaar voor een dag vol plezier?",
  "Samen maken we er iets moois van.",
  "Beste. Dag. Ooit.",
  "Vandaag maken we herinneringen.",
];
// Voortgangsring: hoeveel taken van vandaag zijn klaar (alle afdelingen)
const DbVoortgangRing = ({ klaar, totaal }) => {
  const pct = totaal ? Math.min(1, klaar / totaal) : 0;
  const vol = totaal > 0 && klaar >= totaal;
  const vorige = useRef(vol);
  const [feest, setFeest] = useState(false);
  useEffect(() => {
    if (vol && !vorige.current) {
      setFeest(true);
      const t = setTimeout(() => setFeest(false), 1600);
      vorige.current = vol;
      return () => clearTimeout(t);
    }
    vorige.current = vol;
    return undefined;
  }, [vol]);
  const r = 24;
  const omtrek = 2 * Math.PI * r;
  return (
    <div className={`relative flex-shrink-0 ${feest ? "db-ring-feest" : ""}`} style={{ width: 60, height: 60 }} title={`${klaar} van ${totaal} klaar: jouw taken en verslagen in je dienst`}>
      <svg viewBox="0 0 60 60" className="w-full h-full" style={{ transform: "rotate(-90deg)" }} aria-hidden="true">
        <circle cx="30" cy="30" r={r} fill="none" stroke={C.PineLight} strokeWidth="7" />
        <circle
          cx="30" cy="30" r={r} fill="none"
          stroke={vol ? C.Grass : C.Pine}
          strokeWidth="7" strokeLinecap="round"
          strokeDasharray={omtrek}
          strokeDashoffset={omtrek * (1 - pct)}
          style={{ transition: "stroke-dashoffset .8s cubic-bezier(.2,.8,.2,1), stroke .3s" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none" style={{ color: C.Pine }}>
        {vol ? (
          <IcTaken size={24} color={C.Pine} />
        ) : (
          <>
            <span className="text-sm font-semibold tabular-nums">{klaar}/{totaal}</span>
            <span className="text-[9px] font-semibold uppercase tracking-wide mt-0.5">klaar</span>
          </>
        )}
      </div>
      {feest && (
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <span key={i} className="db-ring-vonk" style={{ "--hoek": `${i * 60}deg`, background: [C.Honey, C.Blossom, C.Sky, C.Grass, C.Honey, C.Blossom][i] }} />
          ))}
        </div>
      )}
    </div>
  );
};

const DbBegroeting = ({ naam, bezig, klaar = 0, totaal = 0 }) => {
  const nu = new Date();
  const uur = nu.getHours();
  const groet = uur < 6 ? "Goedenacht" : uur < 12 ? "Goedemorgen" : uur < 18 ? "Goedemiddag" : "Goedenavond";
  const maand = nu.getMonth();
  const Seizoen = maand >= 2 && maand <= 4 ? IcLente : maand >= 5 && maand <= 7 ? IcZomer : maand >= 8 && maand <= 10 ? IcHerfst : IcWinter;
  const datum = (() => {
    const t = nu.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" });
    return t.charAt(0).toUpperCase() + t.slice(1);
  })();
  const allesKlaar = totaal > 0 && klaar >= totaal;
  const zin = allesKlaar
    ? "Alles klaar. Top gedaan!"
    : bezig
    ? "Fijne dienst! Je staat erop."
    : DB_ZINNEN[(nu.getDate() + nu.getMonth()) % DB_ZINNEN.length];
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3 flex items-center gap-3 overflow-hidden">
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500">{datum}</p>
        <p className="text-lg font-semibold leading-tight truncate" style={{ color: C.Lagoon }}>
          {groet}, {String(naam || "").split(" ")[0]}!
        </p>
        <p className="text-sm" style={{ color: C.Pine }}>{zin}</p>
      </div>
      {totaal > 0 && <DbVoortgangRing klaar={klaar} totaal={totaal} />}
      <div className={`relative flex-shrink-0 items-end justify-center ${totaal > 0 ? "hidden sm:flex" : "flex"}`} style={{ width: 92, height: 64 }}>
        <svg viewBox={`0 0 ${CP_STORY.sunset.w} ${CP_STORY.sunset.h}`} className="absolute inset-0 w-full h-full" aria-hidden="true">
          <path d={CP_STORY.sunset.d} fill={C.PineLight} />
        </svg>
        <Seizoen size={34} color={C.Pine} style={{ position: "relative", marginBottom: 8 }} />
      </div>
    </div>
  );
};

const PROMO_FEEST = { licht: "#FFCA55", donker: "#7A5226" }; // CP-paar geel (jarig)
const DagbordView = ({
  currentDay,
  appData,
  updateData,
  loggedInUserName,
  userRole,
  getDailyTasks,
  onCheckTask,
  onNvtTask,
  onPrev,
  onNext,
  onCalendar,
  chatUnreadCount = 0,
  onOpenChat,
  onSetDay,
  onOpenFeestdagen,
  onOpenAfscheid,
}) => {
  const dateKey = toIsoDate(currentDay);
  const todayKey = toIsoDate(new Date());
  const isToday = dateKey === todayKey;
  const isPastDay = dateKey < todayKey;
  const staffList = appData.staffList || [];
  const day = appData.dagbord?.[dateKey] || {};
  const isFM = userRole === "fm" || userRole === "admin";
  const me = loggedInUserName;

  // 🎂 Jarigen op deze dag (geboortedatum uit Personeel; FM uit Instellingen)
  const jarigen = (() => {
    const [, mm, dd] = dateKey.split("-");
    const jaarNu = Number(dateKey.slice(0, 4));
    const uit = [];
    const kijk = (naam, dob) => {
      const m = String(dob || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m || m[2] !== mm || m[3] !== dd) return;
      const leeftijd = jaarNu - Number(m[1]);
      uit.push({ naam, leeftijd: leeftijd > 0 && leeftijd < 100 ? leeftijd : null });
    };
    staffList.forEach((s) => s && s.name && !s.inactive && !teamVerborgen(s) && kijk(s.name, s.dob));
    if (!uit.some((j) => j.naam === "Maik")) kijk("Maik", appData.settings?.fmDob);
    return uit;
  })();
  const [feestConfetti, setFeestConfetti] = useState(null);
  // Confetti één keer per dag (per toestel) als er vandaag iemand jarig is
  useEffect(() => {
    if (!isToday || !jarigen.length) return undefined;
    const sleutel = `cp_feest_${todayKey}`;
    try {
      if (localStorage.getItem(sleutel)) return undefined;
      localStorage.setItem(sleutel, "1");
    } catch (e) {
      return undefined;
    }
    setFeestConfetti(Date.now());
    return undefined;
  }, [isToday, jarigen.length]); // eslint-disable-line
  useEffect(() => {
    if (!feestConfetti) return undefined;
    const t = setTimeout(() => setFeestConfetti(null), 5500);
    return () => clearTimeout(t);
  }, [feestConfetti]);

  const [afdeling, setAfdeling] = useState("alle");
  const chooseAfdeling = (id) => {
    handKeuzeDag.current = dateKey;
    setAfdeling(id);
    try {
      localStorage.setItem(`cp_dagbord_afd_${me}`, id);
    } catch (e) {}
  };
  const matchAfd = (a) => afdeling === "alle" || !a || a === "alle" || a === afdeling;

  // Automatisch openen op de afdeling waar je vandaag op ingepland staat
  const ingeplandOp = dbMyAfdeling(appData, dateKey, me);
  // Standaard altijd alle afdelingen (filter blijft handmatig te kiezen).
  // Alleen afdelingen die deze dag open zijn worden als keuze getoond.
  const openAfds = (() => {
    const heeftWerk = (afd) =>
      (appData.schedule || []).some((a) => a.date === dateKey && dbActAfdeling(a) === afd) ||
      (appData.roosterDiensten?.[dateKey] || []).some((d) => dbDienstAfdeling(d) === afd);
    const open = {
      entertainment: true,
      crea: creaOpenOp(appData, dateKey) || heeftWerk("crea"),
      hotspot: hotspotOpenOp(appData, dateKey) || heeftWerk("hotspot"),
      springkussen: appData.dailySpringkussen?.[dateKey]?.open === true || heeftWerk("springkussen"),
    };
    return DB_AFDELINGEN.filter((a) => a.id !== "alle" && open[a.id]).map((a) => a.id);
  })();
  // Open meteen op de afdeling waar je die dag op staat (Crea → Crea enz.),
  // tenzij je die dag zelf al een andere afdeling hebt gekozen.
  const handKeuzeDag = useRef(null);
  useEffect(() => {
    if (handKeuzeDag.current === dateKey) {
      if (afdeling !== "alle" && !openAfds.includes(afdeling)) setAfdeling("alle");
      return;
    }
    const doel = ingeplandOp && openAfds.includes(ingeplandOp) ? ingeplandOp : "alle";
    if (doel !== afdeling) setAfdeling(doel);
  }, [dateKey, ingeplandOp, openAfds.join(",")]); // eslint-disable-line

  const [openAct, setOpenAct] = useState(null); // actId dat uitgeklapt is
  const [editingRep, setEditingRep] = useState(null); // actId waarvoor formulier open is
  const [editingBriefing, setEditingBriefing] = useState(false);
  const [briefingDraft, setBriefingDraft] = useState("");
  const [showResolved, setShowResolved] = useState(false);
  const [showExtraDone, setShowExtraDone] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);
  const [newPunt, setNewPunt] = useState(null); // { title, afdeling, assignee, note }
  const [showClose, setShowClose] = useState(false); // "Dienst afsluiten"-venster
  const [overgedragen, setOvergedragen] = useState(false); // open taken al doorgegeven aan collega's
  const [closeHidden, setCloseHidden] = useState(false); // herinnering weggeklikt
  const [doorText, setDoorText] = useState("");
  const [doorAsPunt, setDoorAsPunt] = useState(false);
  const [doorAfd, setDoorAfd] = useState(null); // null = automatisch (eigen afdeling)
  const [teamAfd, setTeamAfd] = useState("alle"); // afdeling voor een nieuw teambericht
  const [editDoor, setEditDoor] = useState(null); // { id, dk, text }
  const [newOpen, setNewOpen] = useState(true); // lijst "nog te lezen" open
  const [tab, setTab] = useState(() => (_dagbordGoto && _dagbordGoto.tab) || "planning"); // planning | overdracht | actiepunten | aanwezigheid
  // Overdracht: filter op afdeling (onthouden) en welke gelezen dagen uitgeklapt zijn
  const [ovdAfd, setOvdAfdState] = useState(() => {
    try {
      return localStorage.getItem("cp_ovd_afd") || "alle";
    } catch (e) {
      return "alle";
    }
  });
  const setOvdAfd = (v) => {
    setOvdAfdState(v);
    try {
      localStorage.setItem("cp_ovd_afd", v);
    } catch (e) {}
  };
  const [ovdDag, setOvdDag] = useState(null); // teruglezen: welke dag (null = dag van het bord)
  const [ovdFilters, setOvdFilters] = useState(false); // activiteit/soort-filters tonen
  const [composerAfd, setComposerAfd] = useState("alle"); // afdeling voor een nieuwe overdracht
  useEffect(() => {
    setOvdDag(null);
  }, [dateKey]);
  const [ovdZoek, setOvdZoek] = useState(""); // zoeken in overdrachten
  const [ovdAct, setOvdAct] = useState(""); // filter op activiteit (titel, kleine letters)
  const [ovdSoort, setOvdSoort] = useState(""); // filter op soort (materiaal, gast, …)
  // 🔔 Vanuit een melding: naar het juiste tabblad en het juiste bericht
  useEffect(() => {
    const ga = (doel) => {
      if (!doel) return;
      _dagbordGoto = null;
      if (doel.tab) setTab(doel.tab);
      if (doel.focus === "briefing") {
        setBriefOpen(true);
        return;
      }
      if (!doel.focus) return;
      let pogingen = 0;
      const zoek = () => {
        const sel = `[data-focus="${String(doel.focus).replace(/["\\]/g, "")}"]`;
        const el = [...document.querySelectorAll(sel)].find((x) => x.offsetParent);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("cp-focus-flits");
          setTimeout(() => el.classList.remove("cp-focus-flits"), 2600);
        } else if (pogingen++ < 25) setTimeout(zoek, 160);
      };
      setTimeout(zoek, 150);
    };
    if (_dagbordGoto) ga(_dagbordGoto);
    const opEvent = (e) => ga(e.detail);
    window.addEventListener("dagbord-goto", opEvent);
    return () => window.removeEventListener("dagbord-goto", opEvent);
  }, []); // eslint-disable-line
  const [composerAct, setComposerAct] = useState(null); // activiteit gekoppeld aan nieuwe overdracht
  const [showBez, setShowBez] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false); // briefing uitgeklapt (mobiel)
  const [briefSheet, setBriefSheet] = useState(false); // briefing als venster (na dienst starten / tik in kop)
  const [showPast, setShowPast] = useState(false); // afgelopen activiteiten tonen
  const [doneOpen, setDoneOpen] = useState({}); // per taakblok: afgevinkte tonen
  const [rolSheet, setRolSheet] = useState(null); // FM: { focus: "dv" | rol } — rollen van de dag wijzigen
  const [rolAlle, setRolAlle] = useState(false); // ook collega's tonen die niet ingeroosterd zijn
  useEffect(() => {
    if (!rolSheet) return;
    const onKey = (e) => e.key === "Escape" && setRolSheet(null);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [!!rolSheet]);
  const [sheet, setSheet] = useState(null); // mobiel: null | menu | overdracht | taak
  const [taakTekst, setTaakTekst] = useState("");
  const [editTime, setEditTime] = useState(null); // { id, val } — FM past tijd van een taak aan
  const [tijdenPlannen, setTijdenPlannen] = useState(false); // FM: alle taaktijden in één scherm
  const swipeRef = useRef(null);
  const composerRef = useRef(null);
  const [newShowAll, setNewShowAll] = useState(false); // ook gelezen verslagen tonen
  const [openRep, setOpenRep] = useState(null); // uitgeklapt verslag in paneel
  const [corrReason, setCorrReason] = useState("");
  const [corrSent, setCorrSent] = useState(false);
  const [editClock, setEditClock] = useState(null); // { name, in, out }
  const [showWeek, setShowWeek] = useState(false);

  useEffect(() => {
    setOpenAct(null);
    setEditingRep(null);
    setEditingBriefing(false);
    setCorrSent(false);
    setEditClock(null);
    setUndo(null); // "ongedaan maken" hoort bij de vorige dag
  }, [dateKey]);

  // ── Opslaan-helpers ────────────────────────────────────────────────
  // doel = waar een tik op de melding naartoe gaat: { tab, focus }
  const mentionNotifs = (text, label, doel = {}) => {
    const names = extractMentions(text, staffList).filter((n) => n !== me);
    return names.map((name) => ({
      ...doel,
      id: `mention_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      type: "mention",
      mentionedUser: name,
      author: me,
      source: "dagbord",
      sourceLabel: label,
      dateKey,
      text: text.length > 120 ? text.slice(0, 117) + "…" : text,
      createdAt: new Date().toISOString(),
      readBy: [],
    }));
  };
  const save = (dayDelta, punten, notifs = []) => {
    const updates = {};
    if (dayDelta) updates.dagbord = { [dateKey]: dayDelta };
    if (punten) updates.openPunten = punten;
    if (notifs.length > 0)
      updates.notifications = [...(appData.notifications || []), ...notifs];
    updateData(updates);
  };

  // ── Data voor deze dag ─────────────────────────────────────────────
  const activities = (appData.schedule || [])
    .filter((a) => a.date === dateKey)
    .sort((a, b) => dbStartMin(a.time) - dbStartMin(b.time));

  const comments = Object.entries(day)
    .filter(([k, v]) => k.startsWith("c_") && v && !v.deleted)
    .map(([, v]) => v)
    .sort((a, b) => (a.at || 0) - (b.at || 0));
  const commentsFor = (target) => comments.filter((c) => c.target === target);

  const newReports = Object.entries(day)
    .filter(([k, v]) => k.startsWith("rep_") && v && !v.deleted)
    .map(([key, v]) => ({ ...v, _key: key }));

  // Oude overdracht (alleen-lezen)
  const legacyDay = appData.handover?.[dateKey] || {};
  const legacyArr = (k) => (Array.isArray(legacyDay[k]) ? legacyDay[k] : []);
  const legacyReportsFor = (actId) => {
    const out = [];
    for (const k of ["ent", "crea"]) {
      for (const e of legacyArr(k)) {
        const af = e?.actFields?.[String(actId)];
        if (af) out.push({ ...af, author: af.author || e.author, colleagues: af.colleagues || "" });
      }
    }
    return out;
  };
  const reportsFor = (actId) => ({
    nieuw: newReports.filter((r) => String(r.actId) === String(actId)),
    oud: legacyReportsFor(actId),
  });
  const actIds = new Set(activities.map((a) => String(a.id)));
  const LEGACY_TABS = [
    { key: "fm", label: "FM" },
    { key: "ent", label: "Entertainment" },
    { key: "crea", label: "Crea" },
    { key: "springkussen", label: "Springkussen" },
    { key: "hotspot", label: "Hotspot" },
  ];
  const legacyItems = [];
  for (const t of LEGACY_TABS) {
    const raw = legacyDay[t.key];
    if (typeof raw === "string" && raw.trim()) {
      legacyItems.push({ id: `str_${t.key}`, tab: t.label, author: "Archief", text: raw });
      continue;
    }
    for (const e of legacyArr(t.key)) {
      if (!e) continue;
      const orphanActs = Object.entries(e.actFields || {}).filter(([id]) => !actIds.has(id));
      const text = [e.text, e.extraNotes || e.remarks].filter(Boolean).join("\n");
      if (!text && orphanActs.length === 0 && !(e.creaActs || []).length && !(e.photos || []).length)
        continue;
      legacyItems.push({
        id: `${t.key}_${e.id}`,
        tab: t.label,
        author: e.author || "Archief",
        time: e.time,
        text,
        orphanActs,
        creaActs: e.creaActs || [],
        photos: e.photos || [],
        fmRemark: e.fmRemark,
      });
    }
  }

  // Wie werkt er vandaag
  const workingNames = new Set();
  (appData.roosterDiensten?.[dateKey] || []).forEach((d) =>
    (d.assigned || []).forEach((n) => n && workingNames.add(n))
  );
  (appData.workSchedule?.[dateKey] || []).forEach((s) => {
    if (s.matchedUser) workingNames.add(s.matchedUser);
  });

  // Taken
  const TASK_TYPES = [
    { type: "entertainment", afd: "entertainment" },
    { type: "crea", afd: "crea" },
    { type: "hotspot", afd: "hotspot" },
    { type: "springkussen", afd: "springkussen" },
  ];
  // Taken van de dag: afdeling uit de taak zelf (dagspecifieke categorie
  // wint van de lijst waar hij toevallig in staat), dubbelingen eruit, en
  // alleen afdelingen die vandaag open zijn (zoals in de Takenlijst).
  const CAT_AFD = {
    daySpecificSpringkussen: "springkussen",
    daySpecificCrea: "crea",
    daySpecificEntertainment: "entertainment",
  };
  const dayTasks = (() => {
    const seen = new Set();
    const out = [];
    const sjDag = appData.checklistTemplates?.[currentDay.getDay()] || {};
    for (const { type, afd } of TASK_TYPES) {
      for (const t0 of getDailyTasks ? getDailyTasks(type) : []) {
        // Een afgevinkte/n.v.t.-taak is een kopie van het sjabloon van dat
        // moment; categorie, tijd en afdeling komen altijd uit het sjabloon.
        const sj = (sjDag[type] || []).find((x) => String(x.id) === String(t0.id));
        const t = sj
          ? {
              ...t0,
              category: sj.category || t0.category,
              time: sj.time !== undefined ? sj.time : t0.time,
              afdeling: sj.afdeling !== undefined ? sj.afdeling : t0.afdeling,
            }
          : t0;
        const echteAfd =
          (t.afdeling && TASK_TYPES.some((x) => x.afd === t.afdeling) ? t.afdeling : null) ||
          CAT_AFD[t.category] ||
          (t.type && t.type !== type && TASK_TYPES.some((x) => x.type === t.type) ? t.type : afd);
        const key = `${t.id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        if (!openAfds.includes(echteAfd)) continue;
        out.push({ ...t, _afd: echteAfd, _src: type });
      }
    }
    return out;
  })();
  // Kantoordienst (alleen kantoor-diensten vandaag): geen afdelingstaken tonen,
  // tenzij je zelf een afdeling kiest in de balk
  const kantoorDienst = (() => {
    const d = dbMijnDiensten(appData, dateKey, me);
    return d.length > 0 && d.every((x) => x.afd === "kantoor");
  })();
  const allTasks = dayTasks.filter((t) => matchAfd(t._afd) && !(kantoorDienst && afdeling === "alle"));
  // Extra taken (lopen over meerdere dagen) staan los van de dagplanning
  const extraTasks = (getDailyTasks ? getDailyTasks("extra") : []).map((t) => ({
    ...t,
    _afd: "alle",
  }));
  const extraOpen = extraTasks.filter((t) => !t.done && !t.nvt);
  const extraDone = extraTasks
    .filter((t) => t.done || t.nvt)
    .sort((a, b) => new Date(b.doneAt || 0) - new Date(a.doneAt || 0));
  const phaseOf = (t) =>
    t.category === "startup" ? "opstart" : t.category === "closing" ? "afsluiting" : "gedurende";
  const tasksIn = (ph) => allTasks.filter((t) => phaseOf(t) === ph);
  const countable = allTasks.filter((t) => !t.nvt);
  const tasksDone = countable.filter((t) => t.done).length;

  const visibleActs = activities.filter((a) => matchAfd(dbActAfdeling(a)));
  const actsDue = visibleActs.filter(
    (a) => isPastDay || (isToday && isActivityPast(a.time, currentDay))
  );
  const actsWithReport = actsDue.filter((a) => {
    const r = reportsFor(a.id);
    return r.nieuw.length + r.oud.length > 0;
  });

  // Open punten
  const allPunten = Object.values(appData.openPunten || {}).filter((p) => p && p.id && !p.deleted);
  const openPunten = allPunten
    .filter((p) => p.status !== "opgelost" && matchAfd(p.afdeling))
    .sort((a, b) => {
      const ma = a.assignee === me ? 0 : 1;
      const mb = b.assignee === me ? 0 : 1;
      if (ma !== mb) return ma - mb;
      return (b.createdAt || 0) - (a.createdAt || 0);
    });
  const resolvedPunten = allPunten
    .filter(
      (p) =>
        p.status === "opgelost" &&
        matchAfd(p.afdeling) &&
        Date.now() - (p.resolvedAt || 0) < 14 * 86400000
    )
    .sort((a, b) => (b.resolvedAt || 0) - (a.resolvedAt || 0));

  // Briefing
  const briefing = day.briefing && !day.briefing.deleted && day.briefing.text ? day.briefing : null;
  const readers = Object.entries(day)
    .filter(([k, v]) => k.startsWith("read_") && v && briefing && (v.at || 0) >= (briefing.updatedAt || 0))
    .map(([, v]) => v.name);
  const iHaveRead = !briefing || briefing.author === me || readers.includes(me);
  const notYetRead = [...workingNames].filter(
    (n) => n !== briefing?.author && !readers.includes(n)
  );

  // ── Acties ─────────────────────────────────────────────────────────
  const saveBriefing = () => {
    const text = briefingDraft.trim();
    save(
      { briefing: { text, author: me, updatedAt: Date.now(), deleted: !text } },
      null,
      text ? mentionNotifs(text, "FM-briefing", { tab: "planning", focus: "briefing" }) : []
    );
    setEditingBriefing(false);
    setBriefOpen(false);
  };
  const markRead = () =>
    save({ [`read_${dbSlug(me)}`]: { name: me, at: Date.now() } });

  const myRead = () => ({ [dbSlug(me)]: { name: me, at: Date.now() } });
  const addComment = (target, text, label, important = false, afd = null, photos = [], colleagues = "", soort = "", soortInfo = null, afdGekozen = false) => {
    const id = dbNewId();
    const doel = { tab: "overdracht", focus: `c_${id}` };
    save(
      {
        [`c_${id}`]: {
          id,
          target,
          author: me,
          text,
          at: Date.now(),
          important: !!important,
          ...(soort ? { soort } : {}),
          ...(soortInfo && Object.keys(soortInfo).length ? { soortInfo } : {}),
          ...(colleagues ? { colleagues } : {}),
          ...(afd ? { afdeling: afd } : {}),
          ...(afd === "alle" && afdGekozen ? { afdGekozen: true } : {}),
          ...(photos && photos.length ? { photos } : {}),
        },
      },
      null,
      mentionNotifs(text, label, doel)
    );
  };
  const deleteComment = (c) => save({ [`c_${c.id}`]: { deleted: true } });
  const editComment = (c, text, afd) =>
    save({
      [`c_${c.id}`]: {
        text,
        editedAt: Date.now(),
        ...(afd ? { afdeling: afd } : {}),
      },
    });
  // Categorie van een teambericht. Oude berichten (zonder categorie) krijgen
  // de afdeling waar de schrijver die dag op stond, anders "Iedereen".
  const commentAfd = (c) =>
    c.afdeling || dbAfdVanAuteur(appData, dateKey, c.author) || "alle";
  const readComment = (c) => save({ [`c_${c.id}`]: { reads: myRead() } });

  // ── Verslagen: nieuw sinds je laatste dienst + leesbevestiging ─────
  const newFromKey = dbNewFrom(appData, me, todayKey);
  const recentReports = dbCollectReports(appData, newFromKey, todayKey).filter((r) =>
    matchAfd(r._afd)
  );
  const unreadReports = recentReports.filter((r) => !dbReportRead(r, me));
  const recentTeam = dbCollectTeam(appData, newFromKey, todayKey).filter(
    (c) => c._afd === "alle" || matchAfd(c._afd)
  );
  const unreadTeam = recentTeam.filter((c) => !dbReportRead(c, me));
  const unreadTotal = unreadReports.length + unreadTeam.length;
  // Teamberichten van vandaag staan in de draad; in "nog te lezen" alleen eerdere dagen
  const recentTeamEarlier = recentTeam.filter((c) => c._date !== dateKey);
  const unreadTeamEarlier = unreadTeam.filter((c) => c._date !== dateKey);
  const dayReports = dbCollectReports(appData, dateKey, dateKey);
  // ── Overdracht-feed ────────────────────────────────────────────────
  const shiftKey = (k, d) => {
    const [y, m, dd] = k.split("-").map(Number);
    return toIsoDate(new Date(y, m - 1, dd + d, 12));
  };
  // Overdracht-tab: dagoverzicht per dag (zie dbBezetting) + nog te lezen van eerdere dagen
  const dayFeed = dbCollectFeed(appData, dateKey, dateKey);
  // Alle open (nog niet gelezen) overdrachten, van alle afdelingen en dagen
  const unreadFeed = dbCollectFeed(appData, dbOpenFrom(todayKey), todayKey).filter(
    (it) => !dbFeedRead(it, me)
  );
  // Zoeken / per activiteit: 90 dagen terug (wat er in de app geladen is)
  const zoekFeed = useMemo(
    () => dbCollectFeed(appData, shiftKey(todayKey, -90), todayKey),
    [appData.dagbord, appData.handover, appData.schedule, todayKey] // eslint-disable-line
  );
  const actSleutel = (it) => String(it._title || it.actTitle || "").trim().toLowerCase();
  const actOpties = useMemo(() => {
    const tel = new Map();
    zoekFeed.forEach((it) => {
      if (it.kind !== "act") return;
      const k = actSleutel(it);
      if (!k) return;
      const o = tel.get(k) || { titel: String(it._title || it.actTitle).trim(), n: 0 };
      o.n++;
      tel.set(k, o);
    });
    return [...tel.entries()].sort((a, b) => b[1].n - a[1].n || a[1].titel.localeCompare(b[1].titel));
  }, [zoekFeed]); // eslint-disable-line
  const openComposerFor = (actId) => {
    setComposerAct(actId);
    if (typeof window !== "undefined" && window.innerWidth < 1024) {
      setSheet("overdracht");
      return;
    }
    setTab("overdracht");
    setTimeout(() => composerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
  };
  const postOverdracht = ({ text, afdeling: afd, afdGekozen = false, important, photos, act, crowd, typeKey, typeValue, colleagues, dagen, soort = "", bestel = "", soortInfo = {} }) => {
    const id = dbNewId();
    const now = Date.now();
    const info = soortInfo && Object.keys(soortInfo).length ? soortInfo : null;
    // Materiaal op → op de bestellijst (+ mail naar de FM, zoals bij Bestellen)
    if (soort === "materiaal" && bestel) {
      const nu = new Date();
      updateData({
        orders: [
          ...(appData.orders || []),
          {
            id: now,
            item: `${bestel}${act ? ` (${act.title})` : ""}${info?.hoe ? ` · ${info.hoe.toLowerCase()}` : ""}`,
            who: me,
            date: toDutchDate(nu),
            dateKey: toIsoDate(nu),
            status: "open",
            bron: "overdracht",
          },
        ],
      });
      const aan = String(appData.settings?.bestelMail || "maik.vandenbroek@groupepvcp.com").trim();
      if (aan)
        stuurMail({
          aan,
          onderwerp: `Nieuwe bestelling van ${me || "een collega"}`,
          ...mailOpmaak({
            kleur: "groen",
            label: "Bestelling",
            titel: `Nieuwe bestelling van ${me || "een collega"}`,
            aanhef: "Hoi Maik,",
            intro: "Er is iets op, gemeld in een overdracht.",
            rijen: [
              ["Wat", bestel],
              ...(info?.hoe ? [["Hoe dringend", info.hoe]] : []),
              ...(act ? [["Bij", act.title]] : []),
              ["Gemeld door", me || "onbekend"],
              ["Datum", toDutchDate(nu)],
            ],
            knop: { label: "Open Bestellingen in de app", url: "https://www.kidsentertainmentlp.nl/" },
            afsluiter: "De Kids & Entertainment-app",
          }),
        }).then((r) => !r.ok && console.warn("Bestelmail niet verstuurd:", r.fout));
    }
    // Voor volgende dienst → doorgeven aan de eerstvolgende dienst van die afdeling
    if (soort === "volgende" && !(dagen && dagen.length)) {
      const doelAfd = afd || (act ? dbActAfdeling(act) : "alle");
      const vanMin = isToday ? new Date().getHours() * 60 + new Date().getMinutes() : 24 * 60;
      const nd = dbNextDienst(appData, doelAfd, dateKey, vanMin);
      const doelDag = nd ? nd.dateKey : toIsoDate(new Date(parseDateKey(dateKey).getTime() + 864e5));
      const dd = nd?.dienst;
      updateData({
        dagbord: {
          [doelDag]: {
            [`door_${id}`]: {
              id,
              author: me,
              text: act ? `${act.title}: ${text}` : text,
              fromDate: dateKey,
              at: now,
              afdeling: doelAfd,
              soort: "volgende",
              important: !!important,
              ...(photos?.length ? { photos } : {}),
              voorDienst: dd ? { start: dd.start || "", end: dd.end || "", assigned: dd.assigned || [] } : null,
            },
          },
        },
        ...(() => {
          const nf = mentionNotifs(text, "Doorgegeven aan de volgende dienst", { tab: "overdracht" });
          return nf.length ? { notifications: [...(appData.notifications || []), ...nf] } : {};
        })(),
      });
      cpToast(`Doorgegeven aan de volgende dienst (${parseDateKey(doelDag).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "short" })})`, "success");
      return;
    }
    // FM: vooruit geplaatste opmerking(en) op gekozen dag(en)
    if (!act && dagen && dagen.length) {
      const delta = {};
      dagen.forEach((dk, i) => {
        const cid = `${id}${i}`;
        const [y, m, d] = dk.split("-").map(Number);
        delta[dk] = {
          [`c_${cid}`]: {
            id: cid,
            target: "day",
            author: me,
            text,
            // Bovenaan de dag in de overdracht
            at: new Date(y, m - 1, d, 0, 1).getTime(),
            geplaatstOp: now,
            vooraf: true,
            important: !!important,
            ...(afd ? { afdeling: afd } : {}),
            ...(photos && photos.length ? { photos } : {}),
          },
        };
      });
      updateData({ dagbord: delta });
      const eerste = parseDateKey(dagen[0]).toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" });
      cpToast(
        dagen.length === 1
          ? `Opmerking staat klaar voor ${eerste}.`
          : `Opmerking staat klaar voor ${dagen.length} dagen (vanaf ${eerste}).`,
        "success"
      );
      return;
    }
    if (act) {
      save(
        {
          [`rep_${dbSlug(act.id)}_${dbSlug(me)}_${id}`]: {
            actId: String(act.id),
            actTitle: act.title,
            author: me,
            at: now,
            crowd,
            notes: text,
            ...(colleagues ? { colleagues } : {}),
            ...(typeKey && typeValue ? { [typeKey]: typeValue } : {}),
            ...(photos?.length ? { photos } : {}),
            important: !!important,
            afdeling: afd,
            ...(soort ? { soort } : {}),
            ...(info ? { soortInfo: info } : {}),
          },
        },
        null,
        mentionNotifs(text, `Overdracht ${act.title}`, { tab: "overdracht" })
      );
    } else {
      addComment("day", text, "Overdracht", important, afd, photos, colleagues, soort, info, afdGekozen);
    }
    cpToast(soort === "materiaal" && bestel ? "Overdracht staat erin en het staat op de bestellijst." : "Overdracht staat erin. Iedereen is bij!", "success");
  };
  const feedPatch = (it, patch) =>
    updateData({ dagbord: { [it._date]: { [it._readKey]: patch } } });
  const editFeed = (it, text) => {
    if (it._readKey.startsWith("rep_")) feedPatch(it, { notes: text, editedAt: Date.now() });
    else feedPatch(it, { text, editedAt: Date.now() });
  };
  const deleteFeed = (it) => {
    feedPatch(it, { deleted: true });
    offerUndo("Overdracht verwijderd", () =>
      updRef.current({ dagbord: { [it._date]: { [it._readKey]: { deleted: false } } } })
    );
  };
  // Altijd de nieuwste functies gebruiken (voor "Ongedaan maken" na een paar seconden)
  const updRef = useRef(updateData);
  updRef.current = updateData;
  const checkRef = useRef(onCheckTask);
  checkRef.current = onCheckTask;
  const nvtRef = useRef(onNvtTask);
  nvtRef.current = onNvtTask;
  const [undo, setUndo] = useState(null); // { label, fn, id }
  const undoTimer = useRef(null);
  const offerUndo = (label, fn, actie) => {
    clearTimeout(undoTimer.current);
    const id = Date.now();
    setUndo({ label, fn, id, actie });
    undoTimer.current = setTimeout(() => setUndo((u) => (u && u.id === id ? null : u)), actie ? 7000 : 5000);
  };
  const appDataNu = useRef(appData);
  appDataNu.current = appData;
  const markReportsRead = (list, metUndo = true) => {
    if (!list.length) return;
    const delta = {};
    const terug = {};
    for (const r of list) {
      if (!delta[r._date]) delta[r._date] = {};
      if (!terug[r._date]) terug[r._date] = {};
      delta[r._date][r._readKey] = { reads: myRead() };
      terug[r._date][r._readKey] = { reads: { [dbSlug(me)]: null } };
    }
    updateData({ dagbord: delta });
    if (metUndo)
      offerUndo(list.length === 1 ? "Gemarkeerd als gelezen" : `${list.length} gemarkeerd als gelezen`, () =>
        updRef.current({ dagbord: terug })
      );
  };
  // Overdracht: openen = gelezen. Wat je in het dagoverzicht ziet telt direct (stil) als gelezen
  const ovdToonDag = (() => {
    const basis = dateKey > todayKey ? todayKey : dateKey;
    return ovdDag && ovdDag <= todayKey ? ovdDag : basis;
  })();
  const ovdTeLezen = (ovdToonDag === dateKey ? dayFeed : zoekFeed.filter((it) => it._date === ovdToonDag)).filter(
    (it) => !dbFeedRead(it, me)
  );
  const ovdTeLezenSleutel = ovdTeLezen.map((it) => it._readKey).join(",");
  useEffect(() => {
    if (!ovdTeLezenSleutel || !me) return;
    const zichtbaar = tab === "overdracht" || (typeof window !== "undefined" && window.innerWidth >= 1024 && tab === "planning");
    if (!zichtbaar) return;
    const t = setTimeout(() => markReportsRead(ovdTeLezen, false), 250);
    return () => clearTimeout(t);
  }, [ovdTeLezenSleutel, tab, ovdToonDag, me]); // eslint-disable-line
  const [justDone, setJustDone] = useState({}); // id → tijdstip van afvinken
  // Live: taken die een collega net wijzigde kort laten oplichten
  const eigenWijziging = useRef({}); // id → tijdstip van mijn eigen klik
  const vorigeTaken = useRef(null);
  const [flits, setFlits] = useState({}); // id → { at, door }
  const takenSleutel = dayTasks
    .map((t) => `${t.id}:${t.done ? 1 : 0}${t.nvt ? 1 : 0}`)
    .join(",");
  useEffect(() => {
    const nu = {};
    dayTasks.forEach((t) => {
      nu[String(t.id)] = { st: `${t.done ? 1 : 0}${t.nvt ? 1 : 0}`, door: t.done ? t.doneBy : t.nvt ? t.nvtBy : null };
    });
    const oud = vorigeTaken.current;
    vorigeTaken.current = { dk: dateKey, map: nu };
    if (!oud || oud.dk !== dateKey) return;
    const tijd = Date.now();
    const nieuw = {};
    for (const [id, v] of Object.entries(nu)) {
      const was = oud.map[id];
      if (was && was.st === v.st) continue;
      if (tijd - (eigenWijziging.current[id] || 0) < 15000) continue; // mijn eigen klik
      if (v.door && v.door === me) continue;
      nieuw[id] = { at: tijd, door: v.door || null };
    }
    if (!Object.keys(nieuw).length) return;
    setFlits((f) => ({ ...f, ...nieuw }));
    setTimeout(
      () =>
        setFlits((f) => {
          const n = { ...f };
          for (const id of Object.keys(nieuw)) if (n[id]?.at === tijd) delete n[id];
          return n;
        }),
      4000
    );
  }, [takenSleutel, dateKey]); // eslint-disable-line
  const [, setJustTick] = useState(0);
  // Opslaan onder de lijst waar de taak echt in staat (oude sjablonen hebben
  // soms geen of een afwijkend type → afvinken bleef dan niet staan)
  const metType = (t) => ({ ...t, type: t._src || t.type });
  const taakAfvinken = (t0) => {
    const t = metType(t0);
    eigenWijziging.current[String(t.id)] = Date.now();
    if (!t.done && !t.nvt) {
      const ts = Date.now();
      setJustDone((p) => ({ ...p, [t.id]: ts }));
      setTimeout(
        () =>
          setJustDone((p) => {
            if (p[t.id] !== ts) return p;
            const n = { ...p };
            delete n[t.id];
            return n;
          }),
        1700
      );
    }
    onCheckTask && onCheckTask(t);
    offerUndo(t.done ? "Taak weer open" : "Taak afgevinkt", () => checkRef.current && checkRef.current(t));
  };
  const [nvtVraag, setNvtVraag] = useState(null); // { t, keuze, tekst }
  const taakNvt = (t0) => {
    const t = metType(t0);
    eigenWijziging.current[String(t.id)] = Date.now();
    if (!t.nvt) {
      setNvtVraag({ t, tekst: "" });
      return;
    }
    onNvtTask && onNvtTask(t);
    offerUndo("Weer open gezet", () => nvtRef.current && nvtRef.current(t));
  };
  const nvtDialoogRef = useRef(null);
  // Toegankelijkheid: Escape sluit vensters, focus naar het geopende venster
  useEffect(() => {
    if (!nvtVraag && !showClose) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (nvtVraag) setNvtVraag(null);
      else setShowClose(false);
    };
    document.addEventListener("keydown", onKey);
    if (nvtVraag && nvtDialoogRef.current) nvtDialoogRef.current.focus({ preventScroll: true });
    return () => document.removeEventListener("keydown", onKey);
  }, [!!nvtVraag, showClose]);
  const nvtBevestig = () => {
    if (!nvtVraag) return;
    const { t, tekst } = nvtVraag;
    eigenWijziging.current[String(t.id)] = Date.now();
    const reden = tekst.trim();
    if (!reden) return;
    onNvtTask && onNvtTask(t, reden);
    setNvtVraag(null);
    offerUndo("Niet gedaan", () => nvtRef.current && nvtRef.current({ ...t, nvt: true }));
  };
  const renderNvtVraag = () => {
    if (!nvtVraag) return null;
    const { t, tekst } = nvtVraag;
    const klaar = !!tekst.trim();
    return createPortal(
      <div
        className="fixed inset-0 z-[300] flex items-start justify-center px-4"
        style={{ background: "rgba(0,25,47,0.5)", paddingTop: "max(12vh, env(safe-area-inset-top))" }}
        onClick={() => setNvtVraag(null)}
        onKeyDown={(e) => e.key === "Escape" && setNvtVraag(null)}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        data-noswipe
      >
        <div
          className="bg-white w-full max-w-md rounded-2xl shadow-xl pb-4 db-cheer"
          role="dialog"
          aria-modal="true"
          aria-labelledby="nvt-titel"
          ref={nvtDialoogRef}
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-5 pt-4 pb-2">
            <p id="nvt-titel" className="text-base font-semibold" style={{ color: C.Lagoon }}>
              Waarom niet gedaan?
            </p>
            <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{t.text}</p>
          </div>
          <div className="px-5 py-2">
            <textarea
              rows={3}
              aria-label="Reden waarom de taak niet gedaan is"
              value={tekst}
              onChange={(e) => setNvtVraag((v) => ({ ...v, tekst: e.target.value }))}
              placeholder="Schrijf kort waarom deze taak niet gedaan is…"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-base outline-none focus:ring-2 focus:ring-[#045E51] resize-none"
            />
          </div>
          <div className="px-5 pt-1 flex gap-2">
            <button
              onClick={() => setNvtVraag(null)}
              className="flex-1 min-h-[44px] rounded-xl text-sm font-semibold text-gray-500 bg-gray-100"
            >
              Annuleren
            </button>
            <button
              onClick={nvtBevestig}
              disabled={!klaar}
              className="flex-1 min-h-[44px] rounded-xl text-sm font-semibold text-white transition-opacity"
              style={{ backgroundColor: C.Pine, opacity: klaar ? 1 : 0.4 }}
            >
              Opslaan
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // ── Reacties op overdrachten (voor iedereen) ───────────────────────
  // dagbord[datum]["r_<id>"] = { id, target: <sleutel van de overdracht>, author, text, at }
  const reactiesFor = (it) =>
    Object.entries(appData.dagbord?.[it._date] || {})
      .filter(([k, v]) => k.startsWith("r_") && v && !v.deleted && v.target === it._readKey)
      .map(([, v]) => v)
      .sort((a, b) => (a.at || 0) - (b.at || 0));
  const reactOn = (it, text) => {
    const id = dbNewId();
    const doel = { tab: "overdracht", focus: it._readKey };
    const notifs = mentionNotifs(text, `Reactie op overdracht van ${it.author}`, doel);
    if (it.author && it.author !== me && it.author !== "Archief" && !notifs.some((n) => n.mentionedUser === it.author)) {
      notifs.push({
        ...doel,
        id: `mention_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        type: "mention",
        mentionedUser: it.author,
        author: me,
        source: "dagbord",
        sourceLabel: "Reactie op je overdracht",
        dateKey: it._date,
        text: text.length > 120 ? text.slice(0, 117) + "…" : text,
        createdAt: new Date().toISOString(),
        readBy: [],
      });
    }
    // Ook wie eerder in dit gesprek reageerde krijgt een seintje
    const eerder = [...new Set(reactiesFor(it).map((r) => r.author))].filter(
      (n) => n && n !== me && n !== it.author && !notifs.some((x) => x.mentionedUser === n)
    );
    eerder.forEach((naam) =>
      notifs.push({
        ...doel,
        id: `mention_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        type: "mention",
        mentionedUser: naam,
        author: me,
        source: "dagbord",
        sourceLabel: `Nieuwe reactie bij de overdracht van ${it.author}`,
        dateKey: it._date,
        text: text.length > 120 ? text.slice(0, 117) + "…" : text,
        createdAt: new Date().toISOString(),
        readBy: [],
      })
    );
    const updates = {
      dagbord: { [it._date]: { [`r_${id}`]: { id, target: it._readKey, author: me, text, at: Date.now() } } },
    };
    if (notifs.length) updates.notifications = [...(appData.notifications || []), ...notifs];
    updateData(updates);
  };
  const deleteReact = (it, r) => {
    updateData({ dagbord: { [it._date]: { [`r_${r.id}`]: { deleted: true } } } });
    offerUndo("Reactie verwijderd", () =>
      updRef.current({ dagbord: { [it._date]: { [`r_${r.id}`]: { deleted: false } } } })
    );
  };

  // ── Klok voor nu-lijn en aftellen ─────────────────────────────────
  const [nuTick, setNuTick] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNuTick(Date.now()), 30000);
    return () => clearInterval(iv);
  }, []);
  const nuMin = (() => {
    const d = new Date(nuTick);
    return d.getHours() * 60 + d.getMinutes();
  })();
  const actEind = (a) => {
    const parts = String(a.time || "").split("-");
    const e = dbToMin((parts[1] || "").trim());
    const st = dbStartMin(a.time);
    return e != null ? e : st + 60;
  };
  const aftellen = (() => {
    if (!isToday) return null;
    const mijn = activities.filter((a) => (a.staff || []).includes(me));
    const bron = mijn.length ? mijn : activities.filter((a) => matchAfd(dbActAfdeling(a)));
    const nu = bron.find((a) => dbStartMin(a.time) <= nuMin && actEind(a) > nuMin);
    if (nu) return { a: nu, nu: true, min: actEind(nu) - nuMin, mijn: mijn.length > 0 };
    const volgende = bron
      .filter((a) => dbStartMin(a.time) > nuMin)
      .sort((x, y) => dbStartMin(x.time) - dbStartMin(y.time))[0];
    return volgende ? { a: volgende, nu: false, min: dbStartMin(volgende.time) - nuMin, mijn: mijn.length > 0 } : null;
  })();

  // ── Bij openen van vandaag automatisch naar "nu" in de tijdlijn ────
  // Pas scrollen als het programma geladen is (anders schoof de nu-lijn
  // daarna weer weg), en na het opbouwen van de pagina nog één keer bijstellen.
  const naarNuGedaan = useRef("");
  const [eerderOpenOpen, setEerderOpenOpen] = useState(false);
  useEffect(() => {
    if (!isToday || tab !== "planning") return;
    if (naarNuGedaan.current === dateKey) return;
    if (activities.length === 0 && allTasks.length === 0) return; // nog aan het laden
    const scroll = (gedrag) => {
      const el = document.getElementById("db-nu-lijn");
      if (el) el.scrollIntoView({ behavior: gedrag, block: "center" });
      return !!el;
    };
    const t1 = setTimeout(() => {
      if (scroll("smooth")) naarNuGedaan.current = dateKey;
    }, 450);
    const t2 = setTimeout(() => scroll("smooth"), 1400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [isToday, dateKey, tab, activities.length, allTasks.length]);

  // ── Herinnering: overdracht schrijven meteen na je activiteit ─────
  // Tot 3 uur na afloop, zolang er nog geen overdracht bij die activiteit staat.
  const [ovLater, setOvLater] = useState({});
  const ovHerinnering = (() => {
    if (!isToday) return null;
    const kandidaten = activities
      .filter((a) => (a.staff || []).includes(me))
      .filter((a) => {
        const eind = actEind(a);
        return eind <= nuMin && nuMin - eind <= 180;
      })
      .filter((a) => !ovLater[String(a.id)])
      .filter((a) => !dayFeed.some((it) => it.kind === "act" && String(it.actId) === String(a.id)))
      .sort((x, y) => actEind(y) - actEind(x));
    return kandidaten[0] || null;
  })();

  // ── Doorgeven aan de volgende dienst van de afdeling ──────────────
  const dagNaam = (d) =>
    d.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "short" });
  const dagNaamKey = (k) => dagNaam(parseDateKey(k));
  const incoming = Object.entries(day)
    .filter(([k, v]) => k.startsWith("door_") && v && !v.deleted && matchAfd(v.afdeling))
    .map(([, v]) => v)
    .sort((a, b) => (a.at || 0) - (b.at || 0));
  const doorTargetAfd =
    doorAfd || ingeplandOp || (afdeling !== "alle" ? afdeling : "alle");
  const doorFromMin = isToday
    ? new Date().getHours() * 60 + new Date().getMinutes()
    : 24 * 60; // eerdere dag afsluiten → vanaf de dag erna zoeken
  const doorTarget = (() => {
    const nd = dbNextDienst(appData, doorTargetAfd, dateKey, doorFromMin);
    if (nd) return nd;
    // Nog geen dienst ingepland → de volgende kalenderdag
    const t = new Date(currentDay);
    t.setDate(t.getDate() + 1);
    return { dateKey: toIsoDate(t), dienst: null };
  })();
  const doorTargetLabel = (() => {
    const dname = dagNaamKey(doorTarget.dateKey);
    const sameDay = doorTarget.dateKey === dateKey;
    if (!doorTarget.dienst)
      return `Nog geen ${doorTargetAfd === "alle" ? "" : dbAfdeling(doorTargetAfd).label.toLowerCase() + "-"}dienst ingepland — komt op ${dname}`;
    const dd = doorTarget.dienst;
    const wie = (dd.assigned || []).filter(Boolean);
    return `Komt bij de ${doorTargetAfd === "alle" ? "volgende" : dbAfdeling(doorTargetAfd).label.toLowerCase()}dienst van ${
      sameDay ? "vandaag" : dname
    } ${dd.start}${dd.end ? `–${dd.end}` : ""}${wie.length ? ` (${wie.join(", ")})` : ""}`;
  })();
  // Wat ik vanaf deze dag heb doorgegeven (kan op een latere datum staan)
  const outgoing = Object.entries(appData.dagbord || {})
    .filter(([dk]) => dk >= dateKey)
    .flatMap(([dk, dd]) =>
      Object.entries(dd || {})
        .filter(
          ([k, v]) =>
            k.startsWith("door_") && v && !v.deleted && v.author === me && v.fromDate === dateKey
        )
        .map(([, v]) => ({ ...v, _dk: dk }))
    );
  const sendDoorgeven = () => {
    const text = doorText.trim();
    if (!text) return;
    const id = dbNewId();
    const now = Date.now();
    const dd = doorTarget.dienst;
    const updates = {
      dagbord: {
        [doorTarget.dateKey]: {
          [`door_${id}`]: {
            id,
            author: me,
            text,
            fromDate: dateKey,
            at: now,
            afdeling: doorTargetAfd,
            voorDienst: dd
              ? { start: dd.start || "", end: dd.end || "", assigned: dd.assigned || [] }
              : null,
          },
        },
      },
    };
    if (doorAsPunt) {
      const pid = dbNewId();
      updates.openPunten = {
        [pid]: {
          id: pid,
          title: text.length > 90 ? text.slice(0, 87) + "…" : text,
          afdeling: doorTargetAfd,
          author: me,
          createdAt: now,
          assignee: "",
          status: "open",
          reacties:
            text.length > 90 ? { [dbNewId()]: { author: me, text, at: now } } : {},
        },
      };
    }
    const notifs = mentionNotifs(text, "Doorgegeven aan de volgende dienst", { tab: "overdracht" });
    if (notifs.length > 0)
      updates.notifications = [...(appData.notifications || []), ...notifs];
    updateData(updates);
    setDoorText("");
    setDoorAsPunt(false);
    cpToast(`Doorgegeven · ${dagNaamKey(doorTarget.dateKey)}`, "success");
  };
  const saveDoorEdit = () => {
    if (!editDoor || !editDoor.text.trim()) return;
    updateData({
      dagbord: {
        [editDoor.dk]: {
          [`door_${editDoor.id}`]: { text: editDoor.text.trim(), editedAt: Date.now() },
        },
      },
    });
    setEditDoor(null);
  };
  const renderDoorEdit = () => (
    <div className="space-y-1.5 mt-1">
      <textarea
        autoFocus
        rows={2}
        className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#045E51] resize-y bg-white"
        value={editDoor.text}
        onChange={(e) => setEditDoor({ ...editDoor, text: e.target.value })}
      />
      <div className="flex gap-1.5">
        <button
          onClick={saveDoorEdit}
          className="px-3 py-1 rounded-lg text-xs font-semibold text-white"
          style={{ backgroundColor: C.Pine }}
        >
          Opslaan
        </button>
        <button
          onClick={() => setEditDoor(null)}
          className="px-3 py-1 rounded-lg text-xs text-gray-500 bg-gray-100"
        >
          Annuleren
        </button>
      </div>
    </div>
  );
  const readDoor = (d) => save({ [`door_${d.id}`]: { reads: myRead() } });
  const deleteDoorFrom = (d, key) =>
    updateData({ dagbord: { [key]: { [`door_${d.id}`]: { deleted: true } } } });

  // Mijn dienst vandaag (voor de afsluit-herinnering)
  const myShiftEnd = (() => {
    const rd = (appData.roosterDiensten?.[dateKey] || []).find((d) =>
      (d.assigned || []).includes(me)
    );
    if (rd?.end) return rd.end;
    const ws = (appData.workSchedule?.[dateKey] || []).find(
      (x) => x.matchedUser === me || x.name === me
    );
    return ws?.end || null;
  })();
  const shiftAlmostDone = (() => {
    if (!isToday || !myShiftEnd) return false;
    const [h, m] = String(myShiftEnd).split(":").map(Number);
    if (isNaN(h)) return false;
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes() >= h * 60 + (m || 0) - 30;
  })();

  // ── Dienst starten / afsluiten (gekoppeld aan timeClock) ───────────
  const clockDay = appData.timeClock?.[dateKey] || {};
  const myClock = clockDay[me] || {};
  const myPlanned = dbPlanned(appData, dateKey, me);
  // Alleen de gewijzigde velden sturen; de opslag voegt per persoon/veld samen
  const setClockFor = (name, patch, dk = dateKey) =>
    updateData({
      timeClock: {
        [dk]: { [name]: patch },
      },
    });
  // Uren invullen (pop-up, zie UrenHerinnering); vervangt "Dienst starten/afsluiten"
  const urenOpenen = () => window.dispatchEvent(new CustomEvent("uren-invullen", { detail: { datum: dateKey } }));
  const urenKlaar = !!(myClock.in && myClock.out);
  // "Aan het werk" = binnen de geplande tijden (voor de begroeting)
  const nuAanHetWerk = (() => {
    if (!isToday || !myPlanned?.start) return false;
    const n = new Date().getHours() * 60 + new Date().getMinutes();
    const s0 = dbToMin(myPlanned.start);
    let e0 = dbToMin(myPlanned.end);
    if (s0 == null || e0 == null) return false;
    if (e0 < s0) e0 += 1440;
    return n >= s0 && n <= e0;
  })();
  // Dienst van gisteren nog open (bv. gewerkt tot na middernacht)?
  const gisterKey = (() => {
    const x = new Date();
    x.setDate(x.getDate() - 1);
    return toIsoDate(x);
  })();
  const gisterOpen = (() => {
    if (!isToday) return null;
    const c = appData.timeClock?.[gisterKey]?.[me];
    if (!c?.in || c.out) return null;
    const uren = (Date.now() - new Date(c.in).getTime()) / 3600000;
    return uren > 0 && uren < 16 ? c : null;
  })();
  const sluitGisterAf = () => {
    setClockFor(me, { out: new Date().toISOString() }, gisterKey);
    cpToast("Dienst van gisteren is netjes afgesloten.", "success");
  };
  const [afsluiter, setAfsluiter] = useState(null);
  const afsluiterRef = useRef(null);
  afsluiterRef.current = () => {
    const k = appData.timeClock?.[dateKey]?.[me];
    const gewerkt = k?.in && k?.out ? dbWorked({ in: k.in, out: k.out }, myPlanned) : null;
    setAfsluiter({
      uren: gewerkt && gewerkt.min > 0 ? dbUren(gewerkt.min) : null,
      taken: dayTasks.filter((t) => t.done && t.doneBy === me).length,
      overdrachten: dayFeed.filter((it) => it.author === me && !it._legacy).length,
      activiteiten: activities.length,
    });
    setTimeout(() => setAfsluiter(null), 5300);
  };
  useEffect(() => {
    const h = (e) => {
      if (e.detail?.datum === dateKey && isToday) setTimeout(() => afsluiterRef.current && afsluiterRef.current(), 300);
    };
    window.addEventListener("uren-opgeslagen", h);
    return () => window.removeEventListener("uren-opgeslagen", h);
  }, [dateKey, isToday]);
  // Afwijking t.o.v. rooster (voor urencorrectie)
  const myDeviation = (() => {
    if (!myClock.in || !myClock.out || !myPlanned?.start || !myPlanned?.end)
      return null;
    const ps = dbToMin(myPlanned.start);
    let pe = dbToMin(myPlanned.end);
    const ai = dbIsoMin(myClock.in);
    let ao = dbIsoMin(myClock.out);
    if ([ps, pe, ai, ao].some((x) => x == null)) return null;
    if (ao < ai) ao += 1440; // over middernacht
    if (pe < ps) pe += 1440;
    const diff = Math.abs(ai - ps) + Math.abs(ao - pe);
    return diff >= 15
      ? {
          start: dbWorked(myClock, myPlanned)?.startTxt || dbHM(myClock.in),
          end: dbHM(myClock.out),
        }
      : null;
  })();
  const alreadyCorrected = (appData.hourCorrections || []).some(
    (c) => c.name === me && c.date === dateKey && c.source === "dagbord"
  );
  const sendCorrection = () => {
    if (!myDeviation) return;
    updateData({
      hourCorrections: [
        ...(appData.hourCorrections || []),
        {
          id: Date.now(),
          name: me,
          date: dateKey,
          start: myDeviation.start,
          end: myDeviation.end,
          code: myPlanned?.code || "",
          reason:
            corrReason.trim() ||
            `Gewerkt ${myDeviation.start}–${myDeviation.end}, gepland ${myPlanned.start}–${myPlanned.end}`,
          processed: false,
          source: "dagbord",
        },
      ],
    });
    setCorrSent(true);
    cpToast("Correctie is verstuurd. Top!", "success");
  };

  // Aanwezigheid van het hele team (FM)
  const presenceNames = [
    ...new Set([
      ...workingNames,
      // alleen wie echt (nog) een starttijd heeft; gewiste regels verdwijnen
      ...Object.keys(clockDay).filter((n) => clockDay[n]?.in),
    ]),
  ].sort((a, b) => a.localeCompare(b));
  // FM: per ongeluk gestart → tijden wissen
  const clearClock = (name) => {
    // eslint-disable-next-line no-use-before-define
    return cpConfirm({ message: `Start- en eindtijd van ${name} op deze dag wissen?`, confirmLabel: "Wissen" }).then((ok) => ok && clearClockNu(name));
  };
  const clearClockNu = (name) => {
    setClockFor(name, {
      in: null,
      out: null,
      clearedBy: me,
      clearedAt: Date.now(),
      correctedBy: null,
    });
    setEditClock(null);
    cpToast(`Tijden van ${name} gewist`, "success");
  };
  const presenceRow = (name) => {
    const c = clockDay[name] || {};
    const pl = dbPlanned(appData, dateKey, name);
    const ai = dbIsoMin(c.in);
    let ao = dbIsoMin(c.out);
    const ps = dbToMin(pl?.start);
    let pe = dbToMin(pl?.end);
    // Over middernacht: tijden na 00:00 tellen als +24 uur
    if (ai != null && ao != null && ao < ai) ao += 1440;
    if (ps != null && pe != null && pe < ps) pe += 1440;
    const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
    const lateMin = ai != null && ps != null ? ai - ps : 0;
    const earlyMin = ao != null && pe != null ? pe - ao : 0;
    const late = lateMin > DB_TOLERANTIE;
    const early = earlyMin > DB_TOLERANTIE;
    // Uren horen ingevuld te zijn na het einde van de dienst (of op een eerdere dag)
    const missing = !c.in && ps != null && (isPastDay || (isToday && pe != null && nowMin > pe + 30));
    const notOut = c.in && !c.out && pe != null && (isPastDay || (isToday && nowMin > pe + 30));
    const worked = c.in && (c.out || isToday) ? dbWorked(c, pl) : null;
    return { c, pl, late, early, lateMin, earlyMin, missing, notOut, worked };
  };
  const saveClockEdit = () => {
    if (!editClock) return;
    // Eindtijd vóór de starttijd = dienst over middernacht → eindtijd volgende dag
    const volgendeDag = (() => {
      const [y, mo, d] = dateKey.split("-").map(Number);
      return toIsoDate(new Date(y, mo - 1, d + 1));
    })();
    const overMiddernacht =
      editClock.in && editClock.out && dbToMin(editClock.out) < dbToMin(editClock.in);
    const patch = {
      in: editClock.in ? dbIsoAt(dateKey, editClock.in) : null,
      out: editClock.out
        ? dbIsoAt(overMiddernacht ? volgendeDag : dateKey, editClock.out)
        : null,
      correctedBy: me,
      correctedAt: Date.now(),
    };
    setClockFor(editClock.name, patch);
    setEditClock(null);
  };
  // Weekoverzicht (ma–zo van de getoonde week)
  const weekDays = (() => {
    const d = new Date(currentDay);
    const dow = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - dow);
    return Array.from({ length: 7 }, (_, i) => {
      const x = new Date(d);
      x.setDate(d.getDate() + i);
      return x;
    });
  })();

  const saveReport = (act, f) => {
    const key = `rep_${dbSlug(act.id)}_${dbSlug(me)}`;
    const existing = day[key];
    save(
      {
        [key]: {
          ...f,
          actId: String(act.id),
          actTitle: act.title,
          author: me,
          at: existing?.at || Date.now(),
          updatedAt: Date.now(),
          deleted: false,
        },
      },
      null,
      mentionNotifs(f.notes || "", `Verslag ${act.title}`, { tab: "planning" })
    );
    setEditingRep(null);
    cpToast("Verslag staat erin. Dank je!", "success");
  };

  const createPunt = () => {
    const title = (newPunt?.title || "").trim();
    if (!title) return;
    const id = dbNewId();
    const now = Date.now();
    const reacties = {};
    const note = (newPunt.note || "").trim();
    if (note) reacties[dbNewId()] = { author: me, text: note, at: now };
    const notifs = mentionNotifs(`${title} ${note}`, "Actiepunt", { tab: "actiepunten", focus: id });
    if (newPunt.assignee && newPunt.assignee !== me && !notifs.some((n) => n.mentionedUser === newPunt.assignee)) {
      notifs.push({
        tab: "actiepunten",
        focus: id,
        id: `mention_${now}_${Math.random().toString(36).slice(2)}`,
        type: "mention",
        mentionedUser: newPunt.assignee,
        author: me,
        source: "dagbord",
        sourceLabel: "Actiepunt voor jou",
        dateKey,
        text: title,
        createdAt: new Date().toISOString(),
        readBy: [],
      });
    }
    save(
      null,
      {
        [id]: {
          id,
          title,
          afdeling: newPunt.afdeling || "alle",
          author: me,
          createdAt: now,
          assignee: newPunt.assignee || "",
          status: "open",
          reacties,
        },
      },
      notifs
    );
    setNewPunt(null);
  };
  const reactPunt = (p, text, important = false) => {
    const rid = dbNewId();
    save(
      null,
      {
        [p.id]: {
          reacties: {
            [rid]: { author: me, text, at: Date.now(), important: !!important },
          },
        },
      },
      mentionNotifs(text, `Actiepunt: ${p.title}`, { tab: "actiepunten", focus: p.id })
    );
  };
  const editReactie = (p, r, text) =>
    save(null, { [p.id]: { reacties: { [r.id]: { text, editedAt: Date.now() } } } });
  const editPunt = (p, patch) =>
    save(null, { [p.id]: { ...patch, editedAt: Date.now() } });
  const readReactie = (p, r) =>
    save(null, { [p.id]: { reacties: { [r.id]: { reads: myRead() } } } });
  const resolvePunt = (p) =>
    save(null, { [p.id]: { status: "opgelost", resolvedBy: me, resolvedAt: Date.now() } });
  const reopenPunt = (p) =>
    save(null, { [p.id]: { status: "open", resolvedBy: "", resolvedAt: 0 } });
  const assignPunt = (p, name) => {
    const notifs =
      name && name !== me
        ? [
            {
              tab: "actiepunten",
              focus: p.id,
              id: `mention_${Date.now()}_${Math.random().toString(36).slice(2)}`,
              type: "mention",
              mentionedUser: name,
              author: me,
              source: "dagbord",
              sourceLabel: "Actiepunt voor jou",
              dateKey,
              text: p.title,
              createdAt: new Date().toISOString(),
              readBy: [],
            },
          ]
        : [];
    save(null, { [p.id]: { assignee: name } }, notifs);
  };
  const deletePunt = (p) => {
    // Direct verwijderen met "ongedaan maken" i.p.v. een bevestigingsvraag
    save(null, { [p.id]: { deleted: true } });
    offerUndo("Actiepunt verwijderd", () => save(null, { [p.id]: { deleted: false } }));
  };

  // ── Weergave-blokken ───────────────────────────────────────────────
  const renderTask = (t) => {
    const af = dbAfdeling(t._afd);
    const bewerk = isFM && editTime && String(editTime.id) === String(t.id);
    const vers = t.done && justDone[t.id];
    return (
      <React.Fragment key={`${t.type}_${t.id}`}>
      {/* Afvinken kan alleen via de vakjes rechts (niet door op de regel te tikken of te vegen) */}
      <div
        data-noswipe
        className={`flex items-center gap-3 py-2 min-h-[52px] rounded-lg -mx-1 px-1 ${flits[String(t.id)] ? "db-flits" : ""}`}
      >
        <div className="flex-1 min-w-0">
          <p className={`text-sm leading-6 ${t.done || t.nvt ? "line-through text-gray-500" : "text-gray-800"}`}>
            {t.text}
          </p>
          {(t._min != null || (t.done && t.doneBy) || t.nvt) && (
            <p className="text-xs text-gray-500 flex items-center gap-1.5 flex-wrap">
              {t._min != null && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isFM) setEditTime({ id: t.id, val: hhmm(t._min) });
                  }}
                  className={`font-semibold tabular-nums rounded px-1 -mx-1 ${isFM ? "underline decoration-dotted underline-offset-2" : "cursor-default"}`}
                  style={{ color: t._vast ? C.Pine : "#6B7280" }}
                  aria-label={isFM ? "Tijd of moment aanpassen" : t._vast ? "Tijd vastgezet door FM" : "Voorgestelde tijd"} title={isFM ? "Tijd of moment aanpassen" : t._vast ? "Tijd vastgezet door FM" : "Voorgestelde tijd"}
                >
                  {t._vast ? "" : "±"}{hhmm(t._min)}
                </button>
              )}
              {(t.nvt || (t.done && t.doneBy)) && t._min != null && <span>·</span>}
              {flits[String(t.id)]?.door && (
                <span className="font-semibold" style={{ color: C.Bark }}>
                  net door {String(flits[String(t.id)].door).split(" ")[0]} ·
                </span>
              )}
              {t.nvt ? (
                <span>
                  <span className="font-semibold" style={{ color: C.Bark }}>Niet gedaan</span>
                  {t.nvtReason ? `: ${t.nvtReason}` : ""}
                  {t.nvtBy ? ` · ${t.nvtBy}` : ""}
                </span>
              ) : t.done && t.doneBy ? (
                <span>
                  <span className="font-semibold" style={{ color: C.Pine }}>Gedaan</span>
                  {` door ${t.doneBy}${t.doneAt ? ` · ${dbWhen(t.doneAt, dateKey)}` : ""}`}
                </span>
              ) : null}
            </p>
          )}
        </div>
        {afdeling === "alle" && t._afd !== "alle" && (
          <span className="flex-shrink-0 mt-1 inline-flex items-center gap-1 text-xs font-medium text-gray-500">
            <DbAfdIcoon id={t._afd} size={11} />
            <span className="hidden sm:inline">{af.label}</span>
          </span>
        )}
        <TaakStatusKnoppen
          done={t.done}
          nvt={t.nvt}
          pop={!!vers}
          tekst={t.text}
          onDone={() => taakAfvinken(t)}
          onNiet={onNvtTask ? () => taakNvt(t) : null}
        />
      </div>
      {bewerk && (
        <div className="flex items-center gap-2 pl-9 pb-2 flex-wrap" data-noswipe>
          <div className="w-full flex gap-1">
            {["opstart", "gedurende", "afsluiting"].map((f) => {
              const actief = phaseOf(t) === f;
              return (
                <button
                  key={f}
                  onClick={() => !actief && saveTaskFase(t, f)}
                  className="flex-1 h-9 rounded-lg text-xs font-semibold border"
                  style={
                    actief
                      ? { backgroundColor: C.Pine, color: "white", borderColor: C.Pine }
                      : { backgroundColor: "white", color: C.Lagoon, borderColor: "#E5E7EB" }
                  }
                >
                  {FASE_LABEL[f]}
                </button>
              );
            })}
          </div>
          <input
            type="time"
            step={300}
            value={editTime.val}
            onChange={(e) => setEditTime({ ...editTime, val: e.target.value })}
            className="h-9 border border-gray-200 rounded-lg px-2 text-sm"
          />
          <button
            onClick={() => saveTaskTime(t, editTime.val)}
            className="h-9 px-3 rounded-lg text-xs font-semibold text-white"
            style={{ backgroundColor: C.Pine }}
          >
            Vastzetten
          </button>
          {t._vast && (
            <button
              onClick={() => saveTaskTime(t, null)}
              className="h-9 px-3 rounded-lg text-xs font-semibold"
              style={{ backgroundColor: C.HoneyLight, color: C.Bark }}
            >
              Voorstel
            </button>
          )}
          <button onClick={() => setEditTime(null)} className="h-9 w-9 rounded-lg flex items-center justify-center text-gray-500 bg-gray-100" aria-label="Sluiten">
            <X size={14} />
          </button>
          <span className="w-full text-xs text-gray-500">
            {sjabloonVan(t) ? `Geldt voor elke ${currentDay.toLocaleDateString("nl-NL", { weekday: "long" })}` : "Alleen voor deze dag"}
          </span>
        </div>
      )}
      </React.Fragment>
    );
  };

  const renderTaskGroup = (key, label, Icon, list, tijd) => {
    // Eigen afvinken: meteen weg. Door een collega afgevinkt: 4 s zichtbaar met "net door …"
    const net = (t) => !!flits[String(t.id)];
    const openList = list.filter((t) => (!t.done && !t.nvt) || net(t));
    const klaar = list.filter((t) => (t.done || t.nvt) && !net(t));
    const allesKlaar = list.every((t) => t.done || t.nvt);
    const juichen = allesKlaar && list.some((t) => justDone[t.id]);
    const toon = !!doneOpen[key];
    return (
      <div key={key} className="flex gap-3">
        <div className="w-10 flex-shrink-0 pt-3 text-right">
          <span className="block text-xs font-semibold text-gray-500 tabular-nums">±{tijd}</span>
          <Icon size={14} className="inline text-gray-500 mt-0.5" />
        </div>
        <div className="flex-1 min-w-0 rounded-xl border border-gray-100 bg-gray-50/60 px-3 py-2 my-1">
          <div className="flex items-center justify-between min-h-[28px]">
            <p className="text-xs font-semibold" style={{ color: C.Lagoon }}>
              {label}
            </p>
            {juichen ? (
              <span className="db-cheer text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: C.Grass, color: C.Pine }}>
                Alles klaar 🎉
              </span>
            ) : allesKlaar ? (
              <DbStatus kind="klaar" />
            ) : (
              <DbPill bg="#F3F4F6" text="#4B5563">
                {list.filter((t) => t.done || t.nvt).length}/{list.length}
              </DbPill>
            )}
          </div>
          {openList.map((t) => renderTask(t))}
          {klaar.length > 0 && (
            <>
              <button
                onClick={() => setDoneOpen((p) => ({ ...p, [key]: !p[key] }))}
                className="w-full min-h-[36px] text-xs font-semibold flex items-center gap-1"
                style={{ color: C.Pine }}
              >
                <CheckCheck size={14} />
                {openList.length === 0 ? "Alles klaar" : `${klaar.length} klaar`}
                {toon ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {toon && klaar.map((t) => renderTask(t))}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderTaskBlock = (phase, label, Icon) => {
    const list = tasksIn(phase);
    if (list.length === 0) return null;
    const openList = list.filter((t) => !t.done && !t.nvt);
    const klaar = list.filter((t) => t.done || t.nvt);
    const toon = !!doneOpen[phase];
    return (
      <div className="flex gap-3">
        <div className="w-10 flex-shrink-0 pt-3 flex justify-end">
          <Icon size={16} className="text-gray-500" />
        </div>
        <div className="flex-1 min-w-0 rounded-xl border border-gray-100 bg-gray-50/60 px-3 py-2 my-1">
          <div className="flex items-center justify-between min-h-[28px]">
            <p className="text-xs font-semibold" style={{ color: C.Lagoon }}>
              {label}
            </p>
            <DbPill
              bg={klaar.length === list.length ? C.GrassLight : "#F3F4F6"}
              text={klaar.length === list.length ? C.Pine : "#6B7280"}
            >
              {klaar.length}/{list.length}
            </DbPill>
          </div>
          {openList.map((t) => renderTask(t))}
          {klaar.length > 0 && (
            <>
              <button
                onClick={() => setDoneOpen((p) => ({ ...p, [phase]: !p[phase] }))}
                className="w-full min-h-[36px] text-xs font-semibold flex items-center gap-1"
                style={{ color: C.Pine }}
              >
                <CheckCheck size={14} />
                {openList.length === 0 ? "Alles klaar" : `${klaar.length} klaar`}
                {toon ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {toon && klaar.map((t) => renderTask(t))}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderActivity = (act) => {
    const id = String(act.id);
    const ov = dayFeed.filter((it) => it.kind === "act" && String(it.actId) === id);
    const unread = ov.filter((it) => !dbFeedRead(it, me)).length;
    const now = isActivityNow(act.time, currentDay);
    const past = isPastDay || (isToday && isActivityPast(act.time, currentDay));
    const afd = dbAfdeling(dbActAfdeling(act));
    const expanded = openAct === id;
    const first = ov[0];
    let status;
    if (ov.length > 0) status = <DbStatus kind="klaar" extra={`· ${ov.length}`} />;
    else if (now) status = <DbStatus kind="nu" />;
    else if (past) status = <DbStatus kind="mist" extra="overdracht" />;
    else status = <DbStatus kind="straks" />;
    return (
      <div key={act.id} className="flex gap-3">
        <div className="w-10 flex-shrink-0 pt-3 text-right">
          <span className={`text-xs font-semibold ${now ? "" : "text-gray-500"}`} style={now ? { color: C.Pine } : undefined}>
            {(act.time || "").split("-")[0].trim()}
          </span>
        </div>
        <div
          className="flex-1 min-w-0 rounded-xl border px-3 py-2 my-1 bg-white"
          style={{ borderColor: now ? C.Pine : "#F3F4F6", borderLeftWidth: 3, borderLeftColor: dbAfdDot[dbActAfdeling(act)] || afd.text }}
        >
          <button onClick={() => setOpenAct(expanded ? null : id)} className="w-full text-left flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <p className="text-sm font-semibold text-gray-800">{act.title}</p>
                {unread > 0 && (
                  <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: C.Blossom }}>
                    nieuw
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                {act.loc && (
                  <span className="text-xs text-gray-500 flex items-center gap-0.5">
                    <MapPin size={10} /> {act.loc}
                  </span>
                )}
                {(act.staff || []).slice(0, 4).map((n) => (
                  <ProfileAvatar key={n} name={n} staffList={staffList} size={18} />
                ))}
              </div>
              {first && !expanded && (
                first.nietsBijzonders && ov.length === 1 ? (
                  <DbNietsRegel it={first} />
                ) : (
                  <div className="flex items-start gap-1.5 mt-1 min-w-0">
                    {first.crowd ? <DbCrowdChip lvl={first.crowd} /> : null}
                    <span className="text-xs text-gray-500 line-clamp-2">
                      {first.author}
                      {first.text ? `: ${first.text.split("\n")[0]}` : ""}
                    </span>
                  </div>
                )
              )}
            </div>
            {status}
          </button>
          {expanded && (
            <div className="mt-2 border-t border-gray-100 pt-1">
              {ov.map((it) => it.nietsBijzonders ? (
                <DbNietsRegel key={it._readKey} it={it} magWeg={isFM || it.author === me} onDelete={deleteFeed} />
              ) : (
                <DbFeedItem
                  key={it._readKey}
                  it={it}
                  me={me}
                  staffList={staffList}
                  canModerate={isFM}
                  isNew={!dbFeedRead(it, me)}
                  onRead={(x) => markReportsRead([x])}
                  onEdit={editFeed}
                  onDelete={deleteFeed}
                  reacties={reactiesFor(it)}
                  onReact={reactOn}
                  onDeleteReact={deleteReact}
                  verwacht={workingNames}
                />
              ))}
            </div>
          )}
          {(() => {
            const nodig = ov.length === 0 && (now || past);
            const schrijfKnop = (
              <button
                onClick={() => openComposerFor(id)}
                className="flex-1 mt-2 min-h-[40px] rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 border transition-transform active:scale-[0.98]"
                style={
                  nodig
                    ? { backgroundColor: C.Pine, color: "white", borderColor: C.Pine }
                    : { backgroundColor: "white", color: C.Pine, borderColor: `${C.Pine}55` }
                }
              >
                {ov.length > 0 ? <Plus size={15} /> : <Edit2 size={15} />}
                {ov.length > 0 ? "Overdracht toevoegen" : "Overdracht schrijven"}
              </button>
            );
            if (ov.length > 0) return <div className="flex">{schrijfKnop}</div>;
            return (
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const rid = dbNewId();
                    const key = `rep_${dbSlug(act.id)}_${dbSlug(me)}_${rid}`;
                    save({
                      [key]: {
                        actId: String(act.id),
                        actTitle: act.title,
                        author: me,
                        at: Date.now(),
                        notes: "",
                        nietsBijzonders: true,
                        afdeling: dbActAfdeling(act),
                      },
                    });
                    offerUndo("Niets bijzonders genoteerd ✓", () =>
                      updRef.current({ dagbord: { [dateKey]: { [key]: { deleted: true } } } })
                    );
                  }}
                  className="mt-2 min-h-[40px] px-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 border transition-transform active:scale-[0.98]"
                  style={{ backgroundColor: C.PineLight, color: C.Pine, borderColor: `${C.Pine}33` }}
                  title="Activiteit liep normaal, geen bijzonderheden"
                >
                  <CheckCircle size={15} /> Niets bijzonders
                </button>
                {schrijfKnop}
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  const hasAnything = visibleActs.length + allTasks.length > 0;

  // ── Taken met ± tijd binnen de diensttijd ──────────────────────────
  // Voorstel: opstarten vanaf het begin van de dienst, afsluiten richting
  // het einde, tussendoor verdeeld over de dag. De FM kan een tijd vastzetten;
  // die wordt in het weekdag-sjabloon bewaard (geldt dan elke week).
  const wd = currentDay.getDay();
  const dagSjablonen = appData.checklistTemplates?.[wd] || {};
  const sjabloonVan = (t) =>
    (dagSjablonen[t._src] || []).find((x) => String(x.id) === String(t.id)) || null;
  // Wanneer is een afdeling vandaag echt aan het werk? Diensten uit de
  // roostermaker én het gepubliceerde rooster (per persoon), begrensd door het
  // programma: Entertainment die pas 's middags activiteiten heeft, start niet
  // om 9:00 alleen omdat iemand een ochtenddienst heeft.
  const afdVensterCache = {};
  const afdVenster = (afd) => {
    if (afdVensterCache[afd]) return afdVensterCache[afd];
    const tijden = [];
    (appData.roosterDiensten?.[dateKey] || []).forEach((d) => {
      if (dbDienstAfdeling(d) === afd && dbToMin(d.start) != null)
        tijden.push([dbToMin(d.start), dbToMin(d.end) ?? dbToMin(d.start) + 240]);
    });
    if (!tijden.length)
      (appData.workSchedule?.[dateKey] || []).forEach((w) => {
        const n = w.matchedUser;
        if (!n || dbToMin(w.start) == null) return;
        if (dbMyAfdeling(appData, dateKey, n) !== afd) return;
        tijden.push([dbToMin(w.start), dbToMin(w.end) ?? dbToMin(w.start) + 240]);
      });
    const ac = activities.filter((a) => dbActAfdeling(a) === afd && dbStartMin(a.time) < 9999);
    const actStart = ac.length ? Math.min(...ac.map((a) => dbStartMin(a.time))) : null;
    const actEnd = ac.length ? Math.max(...ac.map((a) => actEind(a))) : null;
    let v;
    if (tijden.length) {
      v = { start: Math.min(...tijden.map((x) => x[0])), end: Math.max(...tijden.map((x) => x[1])) };
      // Niet eerder dan 1,5 uur voor de eerste activiteit van die afdeling
      if (actStart != null && actStart - 90 > v.start && actStart - 90 < v.end)
        v.start = actStart - 90;
    } else if (ac.length) {
      v = { start: actStart - 30, end: actEnd + 30 };
    } else v = { start: 600, end: 1020 };
    // Crea sluit standaard om 15:45; alleen langer als er later nog
    // crea-activiteiten zijn (lange openingsdagen), dan tot het einde daarvan.
    if (afd === "crea") {
      const CREA_SLUIT = 15 * 60 + 45;
      v = { ...v, end: actEnd != null && actEnd > CREA_SLUIT ? actEnd : CREA_SLUIT };
      if (v.start >= v.end) v.start = Math.min(600, v.end - 60);
    }
    afdVensterCache[afd] = v;
    return v;
  };
  // Afdeling is (vandaag) nog niet begonnen → taken staan nog niet "open"
  const afdNogNietBegonnen = (afd) => isToday && afd && afd !== "alle" && nuMin < afdVenster(afd).start;
  const hhmm = (m) =>
    `${String(Math.floor(((m % 1440) + 1440) % 1440 / 60)).padStart(2, "0")}:${String(((m % 60) + 60) % 60).padStart(2, "0")}`;
  const timedTasks = (() => {
    const groepen = {};
    allTasks.forEach((t) => {
      const k = `${t._afd}|${phaseOf(t)}`;
      (groepen[k] = groepen[k] || []).push(t);
    });
    const out = [];
    for (const [k, list] of Object.entries(groepen)) {
      const [afd, ph] = k.split("|");
      const v = afdVenster(afd);
      const n = list.length;
      // Tijdens activiteiten van deze afdeling doe je geen taken.
      // Orry & Friends: ook 30 min ervoor (omkleden/klaarzetten) en 20 min erna vrijhouden.
      const isOrryShow = (a) => /orry/i.test(a.title || "") && /friends/i.test(a.title || "");
      const bezet = activities
        .filter((a) => dbActAfdeling(a) === afd && dbStartMin(a.time) < 9999)
        .map((a) =>
          isOrryShow(a)
            ? [dbStartMin(a.time) - 30, actEind(a) + 20]
            : [dbStartMin(a.time), actEind(a)]
        )
        .sort((x, y) => x[0] - y[0]);
      const inActiviteit = (m) => bezet.find(([b, e]) => m >= b && m < e);
      const vrij = (() => {
        const van = v.start + 30;
        const tot = v.end - 30;
        const out = [];
        let cur = van;
        for (const [b, e] of bezet) {
          if (e <= cur || b >= tot) continue;
          if (b > cur) out.push([cur, Math.min(b, tot)]);
          cur = Math.max(cur, e);
        }
        if (cur < tot) out.push([cur, tot]);
        return out;
      })();
      const vrijTotaal = vrij.reduce((a, [b, e]) => a + (e - b), 0);
      const opVrijePlek = (frac) => {
        let rest = vrijTotaal * frac;
        for (const [b, e] of vrij) {
          if (rest <= e - b) return b + rest;
          rest -= e - b;
        }
        return vrij.length ? vrij[vrij.length - 1][1] : v.start + 60;
      };
      list.forEach((t, i) => {
        const sjT = sjabloonVan(t);
        const vastTxt = sjT?.time || t.time || null;
        let vast = dbToMin(vastTxt);
        // Tijdregel (FM, "Tijden plannen"): schuift mee met de dienst of een activiteit
        const regel = sjT?.tijdRegel || t.tijdRegel || null;
        if (regel && regel.soort && regel.soort !== "vast") {
          const nMin = Number(regel.min) || 0;
          if (regel.soort === "start") vast = v.start + nMin;
          else if (regel.soort === "eind") vast = v.end - nMin;
          else if (regel.soort === "voorAct" || regel.soort === "naAct") {
            const zoek = String(regel.act || "").trim().toLowerCase();
            const kandidaten = zoek
              ? activities.filter((x) => dbStartMin(x.time) < 9999 && String(x.title || "").toLowerCase().includes(zoek)).sort((x, y) => dbStartMin(x.time) - dbStartMin(y.time))
              : [];
            const act = regel.soort === "voorAct" ? kandidaten[0] : kandidaten[kandidaten.length - 1];
            vast = act ? (regel.soort === "voorAct" ? dbStartMin(act.time) - nMin : actEind(act) + nMin) : null;
          }
          if (vast != null) vast = Math.round(vast / 5) * 5;
        }
        let voorstel;
        if (ph === "opstart") {
          voorstel = v.start + i * 5;
          let botst = inActiviteit(voorstel);
          while (botst) {
            voorstel = botst[1] + i * 5;
            botst = inActiviteit(voorstel);
          }
        } else if (ph === "afsluiting") {
          voorstel = Math.max(v.start, v.end - (n - i) * 5);
          let botst = inActiviteit(voorstel);
          while (botst && botst[0] - (n - i) * 5 >= v.start) {
            voorstel = botst[0] - (n - i) * 5;
            botst = inActiviteit(voorstel);
          }
        } else {
          voorstel = vrijTotaal > 0
            ? opVrijePlek((i + 1) / (n + 1))
            : v.start + 60 + Math.round(((v.end - 60 - (v.start + 60)) * (i + 1)) / (n + 1));
        }
        const ruw = voorstel;
        voorstel = Math.round(ruw / 5) * 5;
        if (inActiviteit(voorstel) && !inActiviteit(Math.floor(ruw / 5) * 5)) voorstel = Math.floor(ruw / 5) * 5;
        out.push({ ...t, _min: vast != null ? vast : voorstel, _vast: vast != null, _phase: ph });
      });
    }
    return out;
  })();
  // Tijd/fase van een taak geldt voor álle weekdagen waar dezelfde taak
  // (zelfde lijst, tekst en fase) in het sjabloon staat — zo blijven
  // gelijke dagen (bijv. wo/za/zo) gelijk. "Alleen vandaag" draait dat terug.
  const WD_KORT = ["zo", "ma", "di", "wo", "do", "vr", "za"];
  const zelfdeTaak = (a, b) =>
    String(a.text || "").trim().toLowerCase() === String(b.text || "").trim().toLowerCase() &&
    (a.category || "") === (b.category || "");
  const saveTaskPatch = (t, patch, melding) => {
    const sj = sjabloonVan(t);
    const updates = {};
    let andereDagen = [];
    if (sj) {
      const alle = appData.checklistTemplates || {};
      const nieuw = { ...alle };
      const vorige = {}; // weekdag -> [{ id, velden van vóór de wijziging }]
      for (let d = 0; d < 7; d++) {
        const lijst = alle[d]?.[t._src];
        if (!Array.isArray(lijst)) continue;
        const raak = (x) =>
          d === wd ? String(x.id) === String(t.id) : zelfdeTaak(x, sj);
        if (!lijst.some(raak)) continue;
        if (d !== wd) {
          andereDagen.push(d);
          vorige[d] = lijst
            .filter(raak)
            .map((x) => ({ id: x.id, oud: Object.fromEntries(Object.keys(patch).map((k) => [k, x[k]])) }));
        }
        nieuw[d] = { ...(alle[d] || {}), [t._src]: lijst.map((x) => (raak(x) ? { ...x, ...patch } : x)) };
      }
      updates.checklistTemplates = nieuw;
      if (andereDagen.length) {
        const src = t._src;
        const alleenVandaag = () => {
          const cur = appDataNu.current.checklistTemplates || {};
          const terug = { ...cur };
          andereDagen.forEach((d) => {
            const map = new Map(vorige[d].map((v) => [String(v.id), v.oud]));
            terug[d] = {
              ...(cur[d] || {}),
              [src]: (cur[d]?.[src] || []).map((x) =>
                map.has(String(x.id)) ? { ...x, ...map.get(String(x.id)) } : x
              ),
            };
          });
          updateData({ checklistTemplates: terug }, { direct: true });
          cpToast("Alleen voor vandaag (" + WD_KORT[wd] + ") aangepast", "success");
        };
        const dagen = [wd, ...andereDagen]
          .sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7))
          .map((d) => WD_KORT[d])
          .join(", ");
        if (melding) offerUndo(`${melding} · ${dagen}`, alleenVandaag, "Alleen vandaag");
        melding = null;
      }
    }
    const heeftRecord = (appData.checklists || []).some(
      (c) => String(c.id) === String(t.id) && c.date === dateKey
    );
    if (heeftRecord) {
      updates.checklists = (appData.checklists || []).map((c) =>
        String(c.id) === String(t.id) && c.date === dateKey ? { ...c, ...patch } : c
      );
    }
    if (Object.keys(updates).length) updateData(updates);
    if (melding) cpToast(melding, "success");
  };
  const FASE_CAT = { opstart: "startup", gedurende: "during", afsluiting: "closing" };
  const FASE_LABEL = { opstart: "Opstarten", gedurende: "Tussendoor", afsluiting: "Afsluiten" };
  const saveTaskFase = (t, fase) => {
    // Afdeling vastleggen als die uit een dagspecifieke categorie kwam
    const patch = { category: FASE_CAT[fase], time: null };
    if (CAT_AFD[t.category]) patch.afdeling = t._afd;
    saveTaskPatch(t, patch, `Verplaatst naar ${FASE_LABEL[fase]}`);
    setEditTime(null);
  };
  const saveTaskTime = (t, tijd) => {
    saveTaskPatch(t, { time: tijd || null, tijdRegel: null }, tijd ? `Tijd vastgezet op ${tijd}` : "Terug naar voorstel");
    setEditTime(null);
  };
  // Tijdregel (Tijden plannen): vast om / bij opstart +n / bij afsluiten −n / vóór of na een activiteit
  const REGEL_LABEL = { voorstel: "Voorstel", vast: "Vast om", start: "Bij opstart +", eind: "Bij afsluiten −", voorAct: "Vóór activiteit −", naAct: "Na activiteit +" };
  const saveTaskRegel = (t, regel) => {
    if (!regel || regel.soort === "voorstel") return saveTaskPatch(t, { time: null, tijdRegel: null }, "Terug naar voorstel");
    if (regel.soort === "vast") return saveTaskPatch(t, { time: regel.time || null, tijdRegel: null }, regel.time ? `Vast om ${regel.time}` : "Terug naar voorstel");
    const r = { soort: regel.soort, min: Math.max(0, Number(regel.min) || 0), act: regel.act || "" };
    const tekst =
      r.soort === "start" ? `Bij opstart +${r.min} min` : r.soort === "eind" ? `Bij afsluiten −${r.min} min` : `${r.soort === "voorAct" ? "Vóór" : "Na"} "${r.act}" ${r.soort === "voorAct" ? "−" : "+"}${r.min} min`;
    saveTaskPatch(t, { time: null, tijdRegel: r }, tekst);
  };

  // ── Mobiel: vegen tussen tabbladen ─────────────────────────────────
  const mobTabs = ["planning", "overdracht", "actiepunten", ...(isFM ? ["aanwezigheid"] : [])];
  const onSwipeStart = (e) => {
    if (e.target.closest && e.target.closest("[data-noswipe]")) return (swipeRef.current = null);
    const t = e.touches[0];
    swipeRef.current = { x: t.clientX, y: t.clientY };
  };
  const onSwipeEnd = (e) => {
    const st = swipeRef.current;
    swipeRef.current = null;
    if (!st || window.innerWidth >= 1024) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - st.x;
    const dy = t.clientY - st.y;
    if (Math.abs(dx) < 70 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    const i = mobTabs.indexOf(tab);
    const ni = dx < 0 ? Math.min(mobTabs.length - 1, i + 1) : Math.max(0, i - 1);
    if (ni !== i) setTab(mobTabs[ni]);
  };
  const addExtraTaak = () => {
    const text = taakTekst.trim();
    if (!text) return;
    updateData({
      checklists: [
        ...(appData.checklists || []),
        { id: Date.now(), text, type: "extra", category: "extra", priority: "medium", date: "global", done: false },
      ],
    });
    setTaakTekst("");
    setSheet(null);
    cpToast("Extra taak erbij. Komt goed!", "success");
  };

  // ── Tabbladen ──────────────────────────────────────────────────────
  const sideTab = tab === "planning" ? "overdracht" : tab;
  const overdrachtBadge = unreadFeed.length;
  const presenceAlerts = isFM
    ? presenceNames.filter((n) => {
        const r = presenceRow(n);
        return r.missing || r.notOut;
      }).length
    : 0;

  const renderAfdSelect = () => null;
  const DB_AFD_KORT = { alle: "Alles", entertainment: "Ent.", crea: "Crea", hotspot: "Hotspot", springkussen: "Spring" };
  const renderAfdBar = () => {
    const ids = ["alle", ...openAfds];
    return (
      <div
        className="grid gap-1 p-1 rounded-2xl bg-white border border-gray-100 shadow-sm"
        style={{ gridTemplateColumns: `repeat(${ids.length}, minmax(0, 1fr))` }}
      >
        {ids.map((id) => {
          const a = dbAfdeling(id);
          const active = afdeling === id;
          const kleur = id === "alle" ? C.Pine : dbAfdDot[id];
          const mijn = id !== "alle" && id === ingeplandOp;
          return (
            <button
              key={id}
              onClick={() => chooseAfdeling(id)}
              className="min-w-0 h-10 px-1.5 rounded-xl font-semibold flex flex-col items-center justify-center leading-tight transition-colors"
              style={
                active
                  ? { backgroundColor: id === "alle" ? C.Pine : kleur, color: id === "alle" ? "white" : C.Lagoon }
                  : { color: C.Lagoon }
              }
            >
              <span className="flex items-center gap-1 min-w-0 text-sm">
                {id !== "alle" && <DbAfdIcoon id={id} size={13} actief={active} />}
                <span className="truncate sm:hidden">{DB_AFD_KORT[id]}</span>
                <span className="truncate hidden sm:inline">{id === "alle" ? "Alles" : a.label}</span>
              </span>
              {mijn && (
                <span
                  className="text-[9px] font-semibold uppercase tracking-wide whitespace-nowrap"
                  style={{ color: active ? (id === "alle" ? "white" : C.Lagoon) : a.text }}
                >
                  <span className="sm:hidden">jij</span><span className="hidden sm:inline">jouw afdeling</span>
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  };
  const renderTabs = (mode) => {
    const tabs = [
      ...(mode === "mobiel" ? [{ id: "planning", label: "Planning", icon: Clock, badge: 0 }] : []),
      { id: "overdracht", label: "Overdracht", icon: MessageCircle, badge: overdrachtBadge },
      {
        id: "actiepunten",
        label: "Acties",
        full: "Actiepunten",
        icon: AlertTriangle,
        badge: openPunten.length,
        badgeColor: openPunten.some((p) => p.assignee === me) ? C.Bark : "#6B7280",
      },
      ...(isFM
        ? [{ id: "aanwezigheid", label: "Aanwezig", full: "Aanwezigheid", icon: Users, badge: presenceAlerts, badgeColor: C.Sunset }]
        : []),
    ];
    const activeId = mode === "mobiel" ? tab : sideTab;
    return (
      <div
        className={`${mode === "mobiel" ? "flex lg:hidden" : "hidden lg:flex"} gap-1 p-1 rounded-2xl bg-white border border-gray-100 shadow-sm`}
      >
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = t.id === activeId;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 min-w-0 flex items-center justify-center rounded-xl font-semibold transition-colors ${
                mode === "mobiel" ? "flex-col gap-0.5 py-1.5 text-xs" : "flex-row gap-1.5 py-2 text-sm"
              }`}
              style={active ? { backgroundColor: C.Pine, color: "white" } : { color: C.Lagoon }}
            >
              <span className="relative">
                <Icon size={mode === "mobiel" ? 18 : 15} />
                {mode === "mobiel" && t.badge > 0 && (
                  <span
                    className="absolute -top-1.5 -right-3 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold flex items-center justify-center"
                    style={
                      active
                        ? { backgroundColor: "white", color: C.Pine }
                        : { backgroundColor: t.badgeColor || C.Blossom, color: "white" }
                    }
                  >
                    {t.badge}
                  </span>
                )}
              </span>
              <span className="truncate">{mode === "mobiel" ? t.label : t.full || t.label}</span>
              {mode === "desktop" && t.badge > 0 && (
                <span
                  className="min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold flex items-center justify-center"
                  style={
                    active
                      ? { backgroundColor: "white", color: C.Pine }
                      : { backgroundColor: t.badgeColor || C.Blossom, color: "white" }
                  }
                >
                  {t.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  // Mobiel: rustige tabs met onderstreping (geen extra kaart)
  const renderMobielTabs = () => {
    const tabs = [
      { id: "planning", label: "Planning", badge: 0 },
      { id: "overdracht", label: "Overdracht", badge: overdrachtBadge },
      {
        id: "actiepunten",
        label: "Acties",
        badge: openPunten.length,
        badgeColor: openPunten.some((p) => p.assignee === me) ? C.Bark : "#9CA3AF",
      },
      ...(isFM ? [{ id: "aanwezigheid", label: "Aanwezig", badge: presenceAlerts, badgeColor: C.Sunset }] : []),
    ];
    return (
      <div className="flex border-b border-gray-200" role="tablist">
        {tabs.map((t) => {
          const active = t.id === tab;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.id)}
              className="flex-1 min-w-0 h-11 flex items-center justify-center gap-1.5 text-sm font-semibold relative"
              style={{ color: active ? C.Pine : "#6B7280" }}
            >
              <span className="truncate">{t.label}</span>
              {t.badge > 0 && (
                <span
                  className="min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold flex items-center justify-center text-white flex-shrink-0"
                  style={{ backgroundColor: t.badgeColor || C.Blossom }}
                >
                  {t.badge}
                </span>
              )}
              <span
                className="absolute left-2 right-2 -bottom-px h-[3px] rounded-full"
                style={{ backgroundColor: active ? C.Pine : "transparent" }}
              />
            </button>
          );
        })}
      </div>
    );
  };

  // Afsluit-overzicht: wat staat er nog open voor mij
  const myOpenActs = activities.filter((a) => {
    if (!(a.staff || []).includes(me)) return false;
    if (!(isPastDay || (isToday && isActivityPast(a.time, currentDay)))) return false;
    return !dayFeed.some((it) => it.kind === "act" && String(it.actId) === String(a.id));
  });
  // Afsluiten: alleen taken van je eigen dienst, los van het gekozen filter
  // (niet ingepland → alle afdelingen)
  const openDayTasks = dayTasks.filter(
    (t) => (!ingeplandOp || t._afd === ingeplandOp) && !t.done && !t.nvt
  );
  const myPunten = openPunten.filter((p) => p.assignee === me);

  // ── Afsluiten terwijl collega's van je afdeling nog werken ─────────
  // Taken die pas ná nu gepland staan neemt het team over; wat al had
  // moeten gebeuren blijft "jouw" open taak (met optie om over te dragen).
  const nuMinAfsl = (() => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  })();
  const nogAanWerk = !isToday
    ? []
    : [...workingNames]
        .filter((n) => n && n !== me && (!ingeplandOp || dbMyAfdeling(appData, dateKey, n) === ingeplandOp))
        .map((n) => ({
          naam: n,
          eind: dbToMin(dbPlanned(appData, dateKey, n)?.end),
          weg: !!(clockDay[n] || {}).out,
        }))
        .filter((c) => !c.weg && (c.eind == null || c.eind > nuMinAfsl))
        .sort((a, b) => (b.eind ?? 9999) - (a.eind ?? 9999));
  const laatsteEind = nogAanWerk.length ? nogAanWerk[0].eind : null;
  const tijdVanTaak = (t) => {
    const x = timedTasks.find((y) => String(y.id) === String(t.id));
    if (x && x._min != null) return x._min;
    const v = dbToMin(sjabloonVan(t)?.time || t.time);
    if (v != null) return v;
    return t.category === "closing" ? 1439 : null;
  };
  const takenVoorMij = openDayTasks.filter((t) => {
    if (!nogAanWerk.length) return true;
    const m = tijdVanTaak(t);
    return m == null || m <= nuMinAfsl;
  });
  const takenVoorTeam = openDayTasks.filter((t) => !takenVoorMij.includes(t));
  const namenLijst = (lijst) => {
    const v = lijst.map((c) => String(c.naam).split(" ")[0]);
    return v.length <= 1 ? v.join("") : `${v.slice(0, -1).join(", ")} en ${v[v.length - 1]}`;
  };
  const draagOver = () => {
    if (!nogAanWerk.length || !takenVoorMij.length) return;
    const teksten = takenVoorMij.map((t) => t.text);
    const tekst =
      teksten.slice(0, 6).join(" · ") + (teksten.length > 6 ? ` · +${teksten.length - 6} meer` : "");
    const notifs = nogAanWerk.map((c) => ({
      tab: "planning",
      id: `mention_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      type: "mention",
      mentionedUser: c.naam,
      author: me,
      source: "dagbord",
      sourceLabel: `${String(me).split(" ")[0]} sloot de dienst af — nog open voor jullie`,
      dateKey,
      text: tekst,
      createdAt: new Date().toISOString(),
      readBy: [],
    }));
    updateData({ notifications: [...(appData.notifications || []), ...notifs] }, { direct: true });
    setOvergedragen(true);
    cpToast(`Doorgegeven aan ${namenLijst(nogAanWerk)}`, "success");
  };

  const kopSt = appData.dailyStats?.[dateKey] || {};
  const kopTotal =
    kopSt.total > 0 ? kopSt.total : (kopSt.adults || 0) + (kopSt.kids || 0) + (kopSt.babies || 0);
  const kopCrowds = dayFeed.map((it) => it.crowd).filter((x) => x >= 1 && x <= 5);
  const kopCrowd = kopCrowds.length
    ? Math.round(kopCrowds.reduce((a, b) => a + b, 0) / kopCrowds.length)
    : null;
  const kopRegel = "w-full flex items-center gap-2.5 px-4 min-h-[48px] text-left border-t border-gray-100";

  // Dagvoortgang: taken + overdrachten van activiteiten die al geweest zijn
  const voortgang = (() => {
    const tk = dayTasks.filter((t) => !t.nvt);
    const tDone = tk.filter((t) => t.done).length;
    const due = isPastDay
      ? activities
      : isToday
      ? activities.filter((a) => isActivityPast(a.time, currentDay))
      : [];
    const aDone = due.filter((a) =>
      dayFeed.some((it) => it.kind === "act" && String(it.actId) === String(a.id))
    ).length;
    const totaal = tk.length + due.length;
    const klaar = tDone + aDone;
    return { totaal, klaar, pct: totaal ? Math.round((klaar / totaal) * 100) : 0 };
  })();
  // Persoonlijke voortgang (ring): alleen taken en verslagen van jouw afdeling
  // binnen jouw ingeroosterde tijden. Vrij = geen ring.
  const mijnVoortgang = (() => {
    const diensten = dbMijnDiensten(appData, dateKey, me);
    if (!diensten.length) return { totaal: 0, klaar: 0 };
    const past = (min, afd) =>
      min != null &&
      diensten.some((d) => min >= d.van && min < d.tot && (!d.afd || !afd || afd === "alle" || afd === d.afd));
    const tk = timedTasks.filter((t) => !t.nvt && past(t._min, t._afd));
    const due = activities.filter((a) => {
      if (!isActivityPast(a.time, currentDay)) return false;
      return past(dbToMin(String(a.time || "").split("-")[0].trim()), dbActAfdeling(a));
    });
    const klaar =
      tk.filter((t) => t.done).length +
      due.filter((a) => dayFeed.some((it) => it.kind === "act" && String(it.actId) === String(a.id))).length;
    return { totaal: tk.length + due.length, klaar };
  })();
  // ⭐ Wie heeft de leiding + karakters van de dag
  const dagFm = !!appData.dailyFmPresent?.[dateKey];
  const dagDv = appData.dailyDv?.[dateKey] || null;
  const dagChars = (appData.characters || []).filter(
    (c) => c && c.date === dateKey && c.actor && String(c.actor).trim()
  );
  const heeftDagRollen = dagFm || !!dagDv || dagChars.length > 0;
  // Rollen die de FM kan invullen: vaste karakters + extra rollen van deze dag
  const dagRolNamen = [
    ...BASE_ROLES,
    ...[...new Set(dagChars.map((c) => c.role).filter((r) => r && !BASE_ROLES.includes(r)))],
  ];
  const toonDagRollen = heeftDagRollen || isFM;

  // Opslaan
  const zetDv = (naam) =>
    updateData(
      { dailyDv: { ...(appData.dailyDv || {}), [dateKey]: naam || null } },
      { direct: true }
    );
  const zetFm = (aan) =>
    updateData(
      { dailyFmPresent: { ...(appData.dailyFmPresent || {}), [dateKey]: !!aan } },
      { direct: true }
    );
  const zetKarakter = (rol, naam) => {
    const alle = appData.characters || [];
    const bestaand = alle.find((c) => c && c.date === dateKey && c.role === rol);
    const rest = alle.filter((c) => !(c && c.date === dateKey && c.role === rol));
    if (naam) {
      rest.push({
        ...(bestaand || {}),
        id: bestaand?.id || Date.now(),
        date: dateKey,
        role: rol,
        actor: naam,
      });
    }
    updateData({ characters: rest }, { direct: true });
  };

  // Wie werkt er die dag (rooster = de waarheid), daarna de rest van het team
  const dagWerkers = [
    ...new Set(
      (appData.workSchedule?.[dateKey] || [])
        .map((x) => x && x.matchedUser)
        .filter(Boolean)
    ),
  ].sort((a, b) => a.localeCompare(b));
  const teamActief = staffList
    .filter((x) => x && x.name && !x.inactive && !teamVerborgen(x))
    .map((x) => x.name)
    .sort((a, b) => a.localeCompare(b));
  const kanSpelen = (naam, rol) => {
    const sc = appData.staffCharacters || {};
    const leeg = Object.values(sc).every((v) => !v || !v.length);
    return leeg || naam === "Maik" || (sc[naam] || []).includes(rol);
  };
  const kandidaten = (rol, huidig) => {
    let lijst = rolAlle ? teamActief : dagWerkers;
    if (rol) lijst = lijst.filter((n) => kanSpelen(n, rol));
    if (rol && !lijst.includes("Maik") && (rolAlle || dagFm)) lijst = [...lijst, "Maik"];
    if (!rol) lijst = lijst.filter((n) => n !== "Maik");
    if (huidig && !lijst.includes(huidig)) lijst = [huidig, ...lijst];
    return lijst;
  };

  const rolLabelStijl =
    "inline-flex items-center gap-1.5 text-xs font-semibold rounded-full";
  const openRol = (focus) => (isFM ? setRolSheet({ focus }) : null);
  const renderDagRollen = () =>
    toonDagRollen ? (
      <div className="px-4 py-2.5 flex flex-wrap items-center gap-1.5">
        {dagFm && (
          <button
            type="button"
            disabled={!isFM}
            onClick={() => openRol("fm")}
            className={`${rolLabelStijl} pl-1 pr-2.5 py-0.5 ${isFM ? "active:scale-95" : "cursor-default"}`}
            style={{ backgroundColor: C.HoneyLight, color: C.Bark }}
            title="Floormanager"
          >
            <ProfileAvatar name="Maik" staffList={staffList} size={20} color={C.Honey} />
            FM · Maik
          </button>
        )}
        {dagDv ? (
          <button
            type="button"
            disabled={!isFM}
            onClick={() => openRol("dv")}
            className={`${rolLabelStijl} pl-1 pr-2.5 py-0.5 ${isFM ? "active:scale-95" : "cursor-default"}`}
            style={{ backgroundColor: C.HoneyLight, color: C.Bark }}
            title={isFM ? "Dagverantwoordelijke wijzigen" : "Dagverantwoordelijke"}
          >
            <ProfileAvatar name={dagDv} staffList={staffList} size={20} color={C.Honey} />
            <Star size={12} style={{ color: C.Honey }} />
            DV · {dagDv}
          </button>
        ) : (
          isFM && (
            <button
              type="button"
              onClick={() => openRol("dv")}
              className={`${rolLabelStijl} px-2.5 py-1 border border-dashed active:scale-95`}
              style={{ borderColor: `${C.Honey}AA`, color: C.Bark }}
            >
              <Plus size={12} /> DV
            </button>
          )
        )}
        {dagChars.map((c, i) => {
          const kl = getCharColorInfo(c.role || "");
          return (
            <button
              type="button"
              disabled={!isFM}
              onClick={() => openRol(c.role)}
              key={c.id || `${c.role}_${i}`}
              className={`${rolLabelStijl} gap-1 px-2.5 py-1 max-w-[200px] truncate ${isFM ? "active:scale-95" : "cursor-default"}`}
              style={{ backgroundColor: kl.bg, color: kl.text }}
              title={isFM ? `${c.role} wijzigen` : `${c.role}: ${c.actor}`}
            >
              <Smile size={12} className="flex-shrink-0" />
              {c.role}: {c.actor}
            </button>
          );
        })}
        {isFM &&
          BASE_ROLES.filter((r) => !dagChars.some((c) => c.role === r)).length > 0 && (
            <button
              type="button"
              onClick={() =>
                openRol(BASE_ROLES.find((r) => !dagChars.some((c) => c.role === r)))
              }
              className={`${rolLabelStijl} px-2.5 py-1 border border-dashed active:scale-95`}
              style={{ borderColor: "#D1D5DB", color: "#6B7280" }}
            >
              <Plus size={12} /> Karakter
            </button>
          )}
      </div>
    ) : null;

  const renderRolKeuze = (titel, kleur, huidig, opties, onKies, focusKey) => (
    <div
      key={focusKey}
      className="rounded-2xl border p-3"
      style={{
        borderColor: rolSheet?.focus === focusKey ? kleur.text : "#F3F4F6",
        backgroundColor: huidig ? `${kleur.bg}22` : "white",
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0"
          style={{ backgroundColor: kleur.bg, color: kleur.text }}
        >
          {titel === "Dagverantwoordelijke" ? <Star size={13} /> : titel[0]}
        </span>
        <span className="text-sm font-semibold" style={{ color: C.Lagoon }}>
          {titel}
        </span>
        <span className="ml-auto text-xs font-semibold" style={{ color: huidig ? kleur.text : "#9CA3AF" }}>
          {huidig || "Niemand"}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => onKies(null)}
          className="min-h-[36px] px-3 rounded-xl text-xs font-semibold border"
          style={{
            borderColor: !huidig ? kleur.text : "#E5E7EB",
            backgroundColor: !huidig ? `${kleur.bg}55` : "white",
            color: !huidig ? kleur.text : "#6B7280",
          }}
        >
          Niemand
        </button>
        {opties.map((naam) => {
          const sel = naam === huidig;
          return (
            <button
              type="button"
              key={naam}
              onClick={() => onKies(naam)}
              className="min-h-[36px] flex items-center gap-1.5 pl-1 pr-3 rounded-xl text-xs font-semibold border"
              style={{
                borderColor: sel ? kleur.text : "#E5E7EB",
                backgroundColor: sel ? kleur.bg : "white",
                color: sel ? kleur.text : "#374151",
              }}
            >
              <ProfileAvatar name={naam} staffList={staffList} size={24} color={C.Lagoon} />
              {naam}
              {sel && <Check size={13} strokeWidth={3} />}
            </button>
          );
        })}
        {opties.length === 0 && (
          <span className="text-xs text-gray-500 self-center px-1">
            {rolAlle ? "Geen collega's beschikbaar" : "Niemand ingeroosterd — kies 'Hele team'"}
          </span>
        )}
      </div>
    </div>
  );

  const renderRolSheet = () => {
    if (!rolSheet || !isFM) return null;
    const dvKleur = { bg: C.HoneyLight, text: C.Bark };
    const dagLabel = (() => {
      const t = currentDay.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" });
      return t.charAt(0).toUpperCase() + t.slice(1);
    })();
    return (
      <div
        className="fixed inset-0 z-[90] flex items-end lg:items-center justify-center"
        style={{ backgroundColor: "rgba(0,25,47,0.45)" }}
        onClick={() => setRolSheet(null)}
        data-noswipe
      >
        <div
          role="dialog"
          aria-label="Rollen van de dag"
          className="bg-white w-full lg:max-w-lg rounded-t-3xl lg:rounded-3xl shadow-xl flex flex-col"
          style={{ maxHeight: "88vh" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-2 px-4 pt-4 pb-3 border-b border-gray-100">
            <div className="flex-1 min-w-0">
              <p className="text-base font-semibold" style={{ color: C.Lagoon }}>
                Rollen van de dag
              </p>
              <p className="text-xs text-gray-500">{dagLabel}</p>
            </div>
            <button
              type="button"
              onClick={() => setRolSheet(null)}
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: "#F3F4F6" }}
              aria-label="Sluiten"
            >
              <X size={18} />
            </button>
          </div>
          <div className="px-4 pt-3 flex items-center gap-2">
            <div className="flex rounded-xl p-0.5" style={{ backgroundColor: "#F3F4F6" }}>
              {[
                { v: false, l: `Ingeroosterd (${dagWerkers.length})` },
                { v: true, l: "Hele team" },
              ].map((o) => (
                <button
                  type="button"
                  key={String(o.v)}
                  onClick={() => setRolAlle(o.v)}
                  className="px-3 h-8 rounded-lg text-xs font-semibold"
                  style={
                    rolAlle === o.v
                      ? { backgroundColor: "white", color: C.Pine, boxShadow: "0 1px 2px rgba(0,0,0,0.08)" }
                      : { color: "#6B7280" }
                  }
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <div className="px-4 py-3 space-y-2.5 overflow-y-auto">
            <button
              type="button"
              onClick={() => zetFm(!dagFm)}
              className="w-full flex items-center justify-between gap-2 px-3 min-h-[44px] rounded-2xl border"
              style={{
                backgroundColor: dagFm ? C.HoneyLight : "white",
                borderColor: dagFm ? `${C.Honey}AA` : "#F3F4F6",
              }}
            >
              <span className="flex items-center gap-2 text-sm font-semibold" style={{ color: C.Bark }}>
                <ProfileAvatar name="Maik" staffList={staffList} size={24} color={C.Honey} />
                Ik ben aanwezig als FM
              </span>
              <span
                className="w-10 h-6 rounded-full flex items-center px-0.5"
                style={{
                  backgroundColor: dagFm ? C.Honey : "#E5E7EB",
                  justifyContent: dagFm ? "flex-end" : "flex-start",
                }}
              >
                <span className="w-5 h-5 rounded-full bg-white shadow-sm" />
              </span>
            </button>
            {renderRolKeuze("Dagverantwoordelijke", dvKleur, dagDv, kandidaten(null, dagDv), zetDv, "dv")}
            {dagRolNamen.map((rol) => {
              const huidig = dagChars.find((c) => c.role === rol)?.actor || null;
              return renderRolKeuze(
                rol,
                getCharColorInfo(rol),
                huidig,
                kandidaten(rol, huidig),
                (naam) => zetKarakter(rol, naam),
                rol
              );
            })}
          </div>
          <div className="px-4 pb-4 pt-2 border-t border-gray-100" style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}>
            <button
              type="button"
              onClick={() => setRolSheet(null)}
              className="w-full h-11 rounded-xl text-sm font-semibold text-white"
              style={{ backgroundColor: C.Pine }}
            >
              Klaar
            </button>
          </div>
        </div>
      </div>
    );
  };
  // Teamvoortgang (hele dag) als balk, alleen voor de FM; medewerkers zien hun
  // persoonlijke ring in de begroeting (en niks als ze vrij zijn)
  const renderVoortgang = () =>
    isFM && voortgang.totaal > 0 && (isToday || isPastDay) ? (
      <div className="px-4 py-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-semibold" style={{ color: C.Lagoon }}>
            {voortgang.pct === 100 ? "Dag afgerond 🎉" : isToday ? "Voortgang team vandaag" : "Voortgang team"}
          </span>
          <span className="text-xs font-semibold tabular-nums" style={{ color: C.Pine }}>
            {voortgang.pct}% · {voortgang.klaar}/{voortgang.totaal}
          </span>
        </div>
        <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: C.PineLight }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${voortgang.pct}%`, backgroundColor: voortgang.pct === 100 ? C.Grass : C.Pine }}
          />
        </div>
      </div>
    ) : null;

  // Weekstrip (ma–zo) met stip voor ongelezen overdrachten
  const weekStrip = (() => {
    const d = new Date(currentDay);
    const dow = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - dow);
    return Array.from({ length: 7 }, (_, i) => {
      const x = new Date(d);
      x.setDate(d.getDate() + i);
      const dk = toIsoDate(x);
      const ongelezen =
        dk <= todayKey && dbCollectFeed(appData, dk, dk).some((it) => !dbFeedRead(it, me));
      return { date: x, dk, ongelezen };
    });
  })();
  const stripSwipe = useRef(null);
  const naarDag = (d) => (onSetDay ? onSetDay(d) : null);

  return (
    <div className="space-y-4" onTouchStart={onSwipeStart} onTouchEnd={onSwipeEnd}>
      <style>{`
        @keyframes dbPop { 0% { transform: scale(1) } 40% { transform: scale(1.3) } 100% { transform: scale(1) } }
        @keyframes dbOut { 0%, 45% { opacity: 1; transform: translateX(0) } 100% { opacity: 0; transform: translateX(18px) } }
        @keyframes dbCheer { 0% { opacity: 0; transform: scale(.8) } 25% { opacity: 1; transform: scale(1.08) } 40% { transform: scale(1) } 100% { opacity: 1 } }
        .db-pop { animation: dbPop .35s ease-out }
        .db-out { animation: dbOut .7s ease-in forwards }
        .db-cheer { animation: dbCheer .8s ease-out }
        @keyframes dbFlits { 0%, 35% { background-color: #FFE2A1 } 100% { background-color: transparent } }
        .db-flits { animation: dbFlits 4s ease-out }
        @keyframes dbWelkom { 0% { opacity: 0; transform: translateY(12px) scale(.94) } 12%, 82% { opacity: 1; transform: none } 100% { opacity: 0; transform: translateY(-8px) scale(.97) } }
        .db-welkom { animation: dbWelkom 2.8s cubic-bezier(.2,.8,.2,1) forwards }
        @keyframes dbRingFeest { 0% { transform: scale(1) } 30% { transform: scale(1.18) rotate(-6deg) } 55% { transform: scale(.96) rotate(3deg) } 100% { transform: none } }
        .db-ring-feest { animation: dbRingFeest .9s cubic-bezier(.2,.8,.2,1) }
        @keyframes dbVonk { 0% { opacity: 1; transform: rotate(var(--hoek)) translateY(0) scale(1) } 100% { opacity: 0; transform: rotate(var(--hoek)) translateY(-40px) scale(.4) } }
        .db-ring-vonk { position: absolute; left: 50%; top: 50%; width: 9px; height: 9px; margin: -4.5px; border-radius: 999px; animation: dbVonk 1.1s cubic-bezier(.2,.8,.2,1) forwards }
        @keyframes dbAfsluiter { 0% { opacity: 0; transform: translateY(14px) scale(.94) } 10%, 88% { opacity: 1; transform: none } 100% { opacity: 0; transform: translateY(-8px) scale(.97) } }
        .db-afsluiter { animation: dbAfsluiter 5.2s cubic-bezier(.2,.8,.2,1) forwards }
        @keyframes dbTelIn { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: none } }
        .db-tel-in { animation: dbTelIn .5s cubic-bezier(.2,.8,.2,1) backwards }
      `}</style>
      {/* Feest: wie is er (op deze dag) jarig */}
      {jarigen.length > 0 && (
        <div className="rounded-2xl px-4 py-3 relative overflow-hidden db-tel-in" style={{ backgroundColor: PROMO_FEEST.licht, color: PROMO_FEEST.donker }} data-noswipe>
          {feestConfetti && <Confetti />}
          <div className="flex items-center gap-3 relative">
            <div className="flex -space-x-3 flex-shrink-0">
              {jarigen.slice(0, 3).map((j) => (
                <span key={j.naam} className="rounded-full" style={{ boxShadow: `0 0 0 3px ${PROMO_FEEST.licht}` }}>
                  <ProfileAvatar name={j.naam} staffList={staffList} size={52} />
                </span>
              ))}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold uppercase">{isToday ? "Hoera, feest!" : dateKey < todayKey ? "Was jarig" : "Jarig"}</p>
              <p className="text-base font-semibold leading-tight">
                {jarigen.map((j) => (j.naam === me ? "Jij" : j.naam.split(" ")[0])).join(" en ")} {jarigen.length > 1 || jarigen[0].naam === me ? "zijn" : "is"}{" "}
                {isToday ? "vandaag " : ""}jarig!
              </p>
              {jarigen.some((j) => j.leeftijd) && (
                <p className="text-xs">
                  {jarigen
                    .filter((j) => j.leeftijd)
                    .map((j) => `${j.naam.split(" ")[0]} ${isToday || dateKey > todayKey ? "wordt" : "werd"} ${j.leeftijd}`)
                    .join(" · ")}
                </p>
              )}
            </div>
          </div>
          {/* Feliciteren (op de dag zelf), met wie al feliciteerde */}
          {isToday &&
            jarigen.map((j) => {
              const felic = Object.values(day[`jarig_${dbSlug(j.naam)}`]?.felic || {}).filter((f) => f && f.name);
              const al = felic.some((f) => f.name === me);
              const zelf = j.naam === me;
              return (
                <div key={j.naam} className="mt-2 flex items-center gap-2 relative flex-wrap">
                  {!zelf && (
                    <button
                      type="button"
                      disabled={al}
                      onClick={() => {
                        save({ [`jarig_${dbSlug(j.naam)}`]: { felic: myRead() } });
                        setFeestConfetti(Date.now());
                      }}
                      className="h-9 px-3 rounded-xl text-xs font-semibold active:scale-95 transition-transform"
                      style={al ? { backgroundColor: "rgba(255,255,255,.55)", color: PROMO_FEEST.donker } : { backgroundColor: PROMO_FEEST.donker, color: PROMO_FEEST.licht }}
                    >
                      {al ? `Je feliciteerde ${j.naam.split(" ")[0]}` : `Feliciteer ${j.naam.split(" ")[0]}!`}
                    </button>
                  )}
                  {felic.length > 0 && (
                    <span className="flex items-center gap-1.5 text-xs">
                      <span className="flex -space-x-2">
                        {felic.slice(0, 6).map((f) => (
                          <span key={f.name} className="rounded-full" style={{ boxShadow: `0 0 0 2px ${PROMO_FEEST.licht}` }}>
                            <ProfileAvatar name={f.name} staffList={staffList} size={24} />
                          </span>
                        ))}
                      </span>
                      {zelf ? `${felic.length} ${felic.length === 1 ? "felicitatie" : "felicitaties"} voor jou!` : `${felic.length} ${felic.length === 1 ? "felicitatie" : "felicitaties"}`}
                    </span>
                  )}
                </div>
              );
            })}
        </div>
      )}
      {/* Afscheidsboek: bovenaan zolang je mag schrijven en nog niets schreef */}
      {onOpenAfscheid && (() => {
        const sl = (x) => String(x || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "_");
        const ik = sl(me);
        const berichten = appData.afscheid?.berichten || {};
        const vandaag = toIsoDate(new Date());
        const open = Object.values(appData.afscheid?.boeken || {}).filter((b) => {
          if (!b || b.weg || !b.open) return false;
          const voor = sl(b.naam);
          if (!voor || voor === ik || voor.split("_")[0] === ik.split("_")[0]) return false; // niet voor wie afscheid neemt
          if (Array.isArray(b.wie) && !b.wie.some((w) => sl(w) === ik)) return false;
          if (b.tot && vandaag > b.tot) return false;
          const mijn = berichten[`${b.id}_${ik}`];
          return !(mijn && !mijn.weg);
        });
        if (!open.length) return null;
        const b = open[0];
        return (
          <button
            type="button"
            onClick={onOpenAfscheid}
            className="w-full text-left rounded-2xl px-4 py-3 flex items-center gap-3 shadow-sm active:scale-[0.99] transition-transform"
            style={{ backgroundColor: C.Blossom, color: C.Sunset }}
            data-noswipe
          >
            <span className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "white" }}>
              <IcAfscheid size={22} color={C.Sunset} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold">Schrijf iets voor {b.naam}!</span>
              <span className="block text-xs">
                Afscheidsboek · je hebt nog niets geschreven
                {b.tot ? ` · kan tot ${parseDateKey(b.tot).toLocaleDateString("nl-NL", { day: "numeric", month: "long" })}` : ""}
              </span>
            </span>
            <span className="flex-shrink-0 h-9 px-3 rounded-xl text-xs font-semibold flex items-center" style={{ backgroundColor: "white", color: C.Sunset }}>
              Schrijven
            </span>
          </button>
        );
      })()}
      {/* Afsluiter bij Dienst afsluiten: korte samenvatting van je dag */}
      {afsluiter && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center px-6" onClick={() => setAfsluiter(null)}>
          <div className="db-afsluiter bg-white rounded-3xl shadow-lg border border-gray-100 px-7 py-7 flex flex-col items-center text-center gap-3 w-full max-w-sm">
            <CpLogoAnimatie kleur={C.Pine} style={{ width: 72, height: 61 }} />
            <p className="text-lg font-semibold" style={{ color: C.Lagoon }}>
              Goed gedaan, {String(me || "").split(" ")[0]}!
            </p>
            <div className="grid grid-cols-3 gap-2 w-full">
              {[
                [afsluiter.taken, afsluiter.taken === 1 ? "taak" : "taken", IcTaken],
                [afsluiter.overdrachten, afsluiter.overdrachten === 1 ? "overdracht" : "overdrachten", IcOverdracht],
                [afsluiter.activiteiten, afsluiter.activiteiten === 1 ? "activiteit" : "activiteiten", IcProgramma],
              ].map(([n, l, I], i) => (
                <div key={l} className="db-tel-in rounded-2xl px-2 py-3 flex flex-col items-center gap-1" style={{ backgroundColor: C.PineLight, animationDelay: `${0.9 + i * 0.18}s` }}>
                  <I size={20} color={C.Pine} />
                  <span className="text-xl font-semibold tabular-nums leading-none" style={{ color: C.Pine }}>{n}</span>
                  <span className="text-xs font-semibold" style={{ color: C.Pine }}>{l}</span>
                </div>
              ))}
            </div>
            {afsluiter.uren && (
              <p className="db-tel-in text-sm" style={{ color: C.Pine, animationDelay: "1.5s" }}>
                {afsluiter.uren} gewerkt. Rust lekker uit!
              </p>
            )}
          </div>
        </div>
      )}
      {/* ── Begroeting met een CP-vorm (vandaag) ── */}
      {isToday && (
        <DbBegroeting
          naam={me}
          bezig={nuAanHetWerk}
          klaar={mijnVoortgang.klaar}
          totaal={mijnVoortgang.totaal}
        />
      )}
      {isToday && onOpenFeestdagen && (() => {
        // 🎄 Feestdagen: herinnering zolang invullen open is en je nog niet alles hebt
        const fd = appData.feestdagen || {};
        const inst = fd.instellingen || {};
        if (!inst.open || !fd.diensten) return null;
        const deadline = inst.deadline || "";
        if (deadline && todayKey > deadline) return null;
        const diensten = Object.entries(fd.diensten).filter(([, d]) => d && !d.weg).map(([id, d]) => ({ ...d, id }));
        if (fd.uitgesloten?.[dbSlug(me)]) return null;
        if (!diensten.length || fd.voorkeur?.[dbSlug(me)]?.at) return null;
        return (
          <button
            type="button"
            onClick={onOpenFeestdagen}
            className="w-full text-left rounded-2xl px-4 py-3 flex items-center gap-3 shadow-sm"
            style={{ backgroundColor: C.HoneyLight, color: C.Bark }}
          >
            <span className="text-2xl" aria-hidden="true">🎄</span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold">Feestdagen: geef je voorkeur door</span>
              <span className="block text-xs">
                Kerst en Oud &amp; Nieuw
                {deadline ? ` · invullen kan tot ${parseDateKey(deadline).toLocaleDateString("nl-NL", { day: "numeric", month: "long" })}` : ""}
              </span>
            </span>
            <ChevronRight size={18} />
          </button>
        );
      })()}
      {/* ── Briefing bovenaan (mobiel): CP-roze tot je hem gelezen hebt ── */}
      {briefing && (
        <button
          type="button"
          onClick={() => setBriefSheet(true)}
          className="lg:hidden w-full text-left rounded-2xl px-3 py-3 flex items-center gap-3 shadow-sm active:scale-[0.99] transition-transform"
          style={
            !iHaveRead
              ? { backgroundColor: C.Blossom, color: C.Lagoon }
              : { backgroundColor: "white", border: "1px solid #F3F4F6", color: C.Lagoon }
          }
          aria-label={!iHaveRead ? "Nieuwe briefing lezen" : "Briefing bekijken"}
          data-noswipe
        >
          <span
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: !iHaveRead ? "white" : C.BlossomLight }}
          >
            <Megaphone size={18} style={{ color: !iHaveRead ? C.Sunset : C.Blossom }} />
          </span>
          <span className="flex-1 min-w-0">
            <span
              className="block text-xs font-semibold"
              style={{ color: !iHaveRead ? C.Lagoon : "#6B7280" }}
            >
              {!iHaveRead ? "Nieuwe briefing" : "Briefing"} · {briefing.author}
              {iHaveRead && briefing.author !== me ? " · gelezen" : ""}
            </span>
            <span
              className={`block text-sm leading-snug ${!iHaveRead ? "font-semibold line-clamp-2" : "truncate"}`}
              style={{ color: !iHaveRead ? C.Lagoon : "#374151" }}
            >
              {briefing.text.split("\n")[0]}
            </span>
          </span>
          {!iHaveRead ? (
            <span
              className="flex-shrink-0 h-9 px-3 rounded-xl text-xs font-semibold flex items-center"
              style={{ backgroundColor: "white", color: C.Sunset }}
            >
              Lezen
            </span>
          ) : (
            <ChevronRight size={16} className="text-gray-500 flex-shrink-0" />
          )}
        </button>
      )}

      {/* ── Compacte kop (mobiel): datum, dienst, bezetting ── */}
      <div className="lg:hidden bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div
          className="px-2 pt-2 pb-2"
          data-noswipe
          onTouchStart={(e) => (stripSwipe.current = e.touches[0].clientX)}
          onTouchEnd={(e) => {
            const sx = stripSwipe.current;
            stripSwipe.current = null;
            if (sx == null) return;
            const dx = e.changedTouches[0].clientX - sx;
            if (Math.abs(dx) < 50) return;
            const n = new Date(currentDay);
            n.setDate(n.getDate() + (dx < 0 ? 7 : -7));
            naarDag(n);
          }}
        >
          <div className="flex items-center justify-between px-2 pb-1.5">
            <button onClick={onCalendar} className="text-sm font-semibold flex items-center gap-1" style={{ color: C.Lagoon }}>
              {(() => {
                const t = currentDay.toLocaleDateString("nl-NL", { month: "long", year: "numeric" });
                return t.charAt(0).toUpperCase() + t.slice(1);
              })()}
              <ChevronDown size={15} />
            </button>
            {!isToday && (
              <button
                onClick={() => naarDag(new Date())}
                className="h-8 px-3 rounded-lg text-xs font-semibold"
                style={{ backgroundColor: C.PineLight, color: C.Pine }}
              >
                Vandaag
              </button>
            )}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {weekStrip.map(({ date, dk, ongelezen }) => {
              const sel = dk === dateKey;
              const vandaag = dk === todayKey;
              return (
                <button
                  key={dk}
                  onClick={() => naarDag(date)}
                  className="h-14 rounded-xl flex flex-col items-center justify-center gap-0.5 relative"
                  style={
                    sel
                      ? { backgroundColor: C.Pine, color: "white" }
                      : vandaag
                      ? { color: C.Pine, boxShadow: `inset 0 0 0 2px ${C.Pine}` }
                      : { color: C.Lagoon }
                  }
                >
                  <span className="text-xs" style={{ opacity: sel ? 0.85 : 0.7 }}>
                    {date.toLocaleDateString("nl-NL", { weekday: "short" }).replace(".", "")}
                  </span>
                  <span className="text-[15px] font-semibold leading-none">{date.getDate()}</span>
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: ongelezen ? (sel ? C.Honey : C.Blossom) : "transparent" }}
                  />
                </button>
              );
            })}
          </div>
        </div>
        {toonDagRollen && <div className="border-t border-gray-100">{renderDagRollen()}</div>}
        <div className="border-t border-gray-100">{renderVoortgang()}</div>
        <div className="hidden">
          <button
            onClick={onPrev}
            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: C.PineLight, color: C.Pine }}
            aria-label="Vorige dag"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={onCalendar}
            className="flex-1 h-11 rounded-xl flex flex-col items-center justify-center leading-tight"
          >
            <span className="text-sm font-semibold" style={{ color: C.Lagoon }}>
              {(() => {
                const t = currentDay.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" });
                return t.charAt(0).toUpperCase() + t.slice(1);
              })()}
            </span>
            <span className="text-xs font-semibold" style={{ color: isToday ? C.Pine : C.Bark }}>
              {isToday ? "Vandaag" : isPastDay ? "Eerdere dag" : "Komende dag"} · Dagbord
            </span>
          </button>
          <button
            onClick={onNext}
            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: C.PineLight, color: C.Pine }}
            aria-label="Volgende dag"
          >
            <ChevronRight size={20} />
          </button>
        </div>
        {isToday && (myPlanned || isFM || urenKlaar) && (
          <div className="flex items-center gap-3 px-4 py-3 border-t border-gray-100" style={{ backgroundColor: urenKlaar ? C.PineLight : "white" }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: urenKlaar ? C.Pine : C.PineLight }}>
              <Clock size={19} color={urenKlaar ? "white" : C.Pine} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold leading-5" style={{ color: C.Lagoon }}>
                {urenKlaar
                  ? `Gewerkt ${dbHM(myClock.in)}–${dbHM(myClock.out)} · ${dbUren(dbWorked(myClock, myPlanned)?.min || 0)}`
                  : myPlanned
                  ? "Uren nog niet ingevuld"
                  : "Vandaag vrij 🌿"}
              </p>
              <p className="text-xs text-gray-500 truncate flex items-center gap-1.5">
                {ingeplandOp && (
                  <span className="inline-flex items-center gap-1 font-semibold" style={{ color: dbAfdeling(ingeplandOp).text }}>
                    <DbAfdIcoon id={ingeplandOp} size={11} />
                    {dbAfdeling(ingeplandOp).label}
                  </span>
                )}
                {myPlanned?.start
                  ? `${ingeplandOp ? "· " : ""}${myPlanned.start}–${myPlanned.end}`
                  : !ingeplandOp
                  ? "Niet ingeroosterd"
                  : ""}
              </p>
            </div>
            <button
              onClick={urenOpenen}
              className={`h-11 px-4 rounded-xl text-sm font-semibold flex items-center gap-1.5 flex-shrink-0 ${urenKlaar ? "" : "text-white"}`}
              style={urenKlaar ? { backgroundColor: "white", color: C.Pine } : { backgroundColor: C.Pine }}
            >
              <Clock size={16} /> {urenKlaar ? "Wijzigen" : "Uren invullen"}
            </button>
          </div>
        )}
        {aftellen && (
          <button
            onClick={() => {
              setTab("planning");
              setOpenAct(String(aftellen.a.id));
            }}
            className={kopRegel}
            style={{ backgroundColor: aftellen.nu ? C.HoneyLight : "white" }}
          >
            <Clock3 size={17} style={{ color: aftellen.nu ? C.Bark : C.Pine }} className="flex-shrink-0 self-start mt-3" />
            <span className="flex-1 min-w-0 py-2">
              {/* Bovenregel: wanneer · resterende tijd; daaronder de volledige titel */}
              <span className="block text-xs font-semibold" style={{ color: aftellen.nu ? C.Bark : C.Pine }}>
                {aftellen.nu ? "Nu bezig" : `Over ${dbMinLabel(aftellen.min)}`}
                {" · "}
                {aftellen.nu
                  ? `nog ${dbMinLabel(aftellen.min)}`
                  : aftellen.mijn
                  ? "jij staat erbij"
                  : (aftellen.a.time || "").split("-")[0].trim()}
              </span>
              <span className="block text-sm font-semibold leading-snug" style={{ color: C.Lagoon }}>
                {aftellen.a.title}
              </span>
              {aftellen.a.loc && (
                <span className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                  <MapPin size={11} className="flex-shrink-0" /> {aftellen.a.loc}
                </span>
              )}
            </span>
          </button>
        )}
        {(kopTotal > 0 || kopSt.villas > 0) && (
          <button onClick={() => setShowBez((v) => !v)} className={kopRegel}>
            <Users size={17} style={{ color: C.Pine }} className="flex-shrink-0" />
            <span className="text-sm font-semibold" style={{ color: C.Lagoon }}>
              {kopTotal.toLocaleString("nl-NL")} gasten
            </span>
            <span className="text-xs text-gray-500 truncate flex-1">
              {kopSt.kids ? `${kopSt.kids} kinderen` : ""}
            </span>
            {kopCrowd && <DbCrowdChip lvl={kopCrowd} />}
            {showBez ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
          </button>
        )}
        {!briefing && isFM && (
        <button
          onClick={() => setBriefOpen((v) => !v)}
          className={kopRegel}
        >
          <Megaphone size={17} style={{ color: C.Pine }} className="flex-shrink-0" />
          <span className="flex-1 min-w-0 py-2.5">
            <span className="block text-xs font-semibold" style={{ color: C.Pine }}>
              Briefing{briefing ? ` · ${briefing.author}` : ""}
            </span>
            <span className="block text-sm truncate" style={{ color: briefing ? C.Lagoon : "#6B7280" }}>
              {briefing ? briefing.text.split("\n")[0] : "Geen briefing vandaag"}
            </span>
          </span>
          {briefing && !iHaveRead && (
            <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: C.Blossom, color: "white" }}>
              nieuw
            </span>
          )}
          {briefing ? (
            <ChevronRight size={16} className="text-gray-500 flex-shrink-0" />
          ) : briefOpen ? (
            <ChevronUp size={16} className="text-gray-500" />
          ) : (
            <ChevronDown size={16} className="text-gray-500" />
          )}
        </button>
        )}
      </div>

      {/* Vrij vandaag (niet ingeroosterd, niet gestart; FM werkt vaak buiten het rooster) */}
      {isToday && !myClock.in && !myPlanned && !isFM && (
        <div
          className="hidden lg:flex rounded-2xl px-4 py-3 items-center gap-3 flex-wrap"
          style={{ backgroundColor: C.GrassLight, border: `1px solid ${C.Grass}` }}
        >
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: "white" }}
          >
            <Sun size={20} style={{ color: C.Pine }} />
          </div>
          <div className="flex-1 min-w-[180px]">
            <p className="text-sm font-semibold" style={{ color: C.Pine }}>
              Je bent vandaag vrij 🌿
            </p>
            <p className="text-xs text-gray-500">
              Geniet ervan! Je kunt hier gewoon meelezen wat er speelt.
            </p>
          </div>
          <button
            onClick={urenOpenen}
            className="text-xs font-semibold underline"
            style={{ color: C.Pine }}
          >
            Toch gewerkt? Uren invullen
          </button>
        </div>
      )}

      {/* Uren van vandaag */}
      {isToday && (myClock.in || myPlanned || isFM) && (
        <div
          className="hidden lg:flex rounded-2xl px-4 py-3 flex-col sm:flex-row sm:items-center gap-3"
          style={{ backgroundColor: urenKlaar ? C.PineLight : "white", border: `1px solid ${urenKlaar ? `${C.Pine}40` : "#F3F4F6"}` }}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: urenKlaar ? C.Pine : `${C.Pine}14` }}>
              <Clock size={20} color={urenKlaar ? "white" : C.Pine} />
            </div>
            <div className="flex-1 min-w-0">
              {urenKlaar ? (
                <>
                  <p className="text-sm font-semibold" style={{ color: C.Pine }}>
                    Gewerkt {dbHM(myClock.in)}–{dbHM(myClock.out)} · {dbUren(dbWorked(myClock, myPlanned)?.min || 0)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {dbWorked(myClock, myPlanned)?.pauze ? `Excl. ${dbMinLabel(dbWorked(myClock, myPlanned).pauze)} pauze · ` : ""}
                    {myPlanned?.start ? `ingeroosterd ${myPlanned.start}–${myPlanned.end}` : "niet ingeroosterd"}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-semibold" style={{ color: C.Lagoon }}>
                    Je uren van vandaag zijn nog niet ingevuld
                  </p>
                  <p className="text-xs text-gray-500">
                    {myPlanned?.start ? `Ingeroosterd ${myPlanned.start}–${myPlanned.end} · ` : "Niet ingeroosterd vandaag · "}
                    tegen het einde van je dienst vraagt de app er vanzelf om
                  </p>
                </>
              )}
              {ingeplandOp && (
                <span className="inline-flex items-center gap-1.5 mt-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${dbAfdDot[ingeplandOp]}1F`, color: dbAfdeling(ingeplandOp).text }}>
                  <DbAfdIcoon id={ingeplandOp} size={10} />
                  Jouw afdeling: {dbAfdeling(ingeplandOp).label}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={urenOpenen}
            className={`w-full sm:w-auto px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 flex-shrink-0 ${urenKlaar ? "" : "text-white"}`}
            style={urenKlaar ? { backgroundColor: "white", color: C.Pine, border: `1px solid ${C.Pine}40` } : { backgroundColor: C.Pine }}
          >
            <Clock size={16} /> {urenKlaar ? "Uren wijzigen" : "Uren invullen"}
          </button>
          {!urenKlaar && (
            <button
              onClick={() => setShowClose(true)}
              className="w-full sm:w-auto px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 flex-shrink-0"
              style={{ backgroundColor: `${C.Pine}12`, color: C.Pine }}
            >
              <Moon size={16} /> Overdragen
            </button>
          )}
        </div>
      )}

      {/* Herinnering: dienst zit er (bijna) op */}
      {shiftAlmostDone && !urenKlaar && !closeHidden && !showClose && (
        <div
          className="rounded-2xl px-4 py-3 flex items-center gap-3 flex-wrap"
          style={{ backgroundColor: C.HoneyLight, border: `1px solid ${C.Honey}` }}
        >
          <Moon size={18} style={{ color: C.Bark }} />
          <p className="text-sm flex-1 min-w-[200px]" style={{ color: C.Bark }}>
            Je dienst zit er bijna op ({myShiftEnd}). Vul je uren in en geef door wat de
            volgende dienst moet weten.
          </p>
          <button onClick={urenOpenen} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white" style={{ backgroundColor: C.Bark }}>
            Uren invullen
          </button>
          <button onClick={() => setShowClose(true)} className="px-3 py-1.5 rounded-lg text-xs font-semibold" style={{ backgroundColor: "white", color: C.Bark }}>
            Overdragen
          </button>
          <button
            onClick={() => setCloseHidden(true)}
            className="text-xs"
            style={{ color: C.Bark }}
          >
            Later
          </button>
        </div>
      )}

      {toonDagRollen && (
        <div className="hidden lg:block bg-white rounded-2xl border border-gray-100 shadow-sm">
          {renderDagRollen()}
        </div>
      )}
      {renderRolSheet()}

      {voortgang.totaal > 0 && (isToday || isPastDay) && (
        <div className="hidden lg:block bg-white rounded-2xl border border-gray-100 shadow-sm">
          {renderVoortgang()}
        </div>
      )}

      {/* Bezetting van de dag (mobiel: via de compacte kop) */}
      <div className={`${showBez ? "" : "hidden"} lg:block`}>
      {(() => {
        const st = appData.dailyStats?.[dateKey] || {};
        const total =
          st.total > 0 ? st.total : (st.adults || 0) + (st.kids || 0) + (st.babies || 0);
        if (!total && !st.villas) {
          return isFM ? (
            <p className="text-xs text-gray-500 px-1">
              Nog geen bezetting ingevoerd voor deze dag.
            </p>
          ) : null;
        }
        const barTotal = (st.adults || 0) + (st.kids || 0) + (st.babies || 0) || 1;
        const segs = [
          { label: "Volwassenen", count: st.adults || 0, color: C.Pine },
          { label: "Kinderen", count: st.kids || 0, color: C.Honey },
          { label: "Baby's", count: st.babies || 0, color: C.Blossom },
        ].filter((x) => x.count > 0);
        const nats = NATIONALITIES.filter((n) => (st.breakdown?.[n.k] || 0) > 0)
          .map((n) => ({ ...n, pct: Math.round((st.breakdown[n.k] / (total || 1)) * 100) }))
          .sort((a, b) => b.pct - a.pct)
          .slice(0, 4);
        // Drukte zoals het team die in de verslagen van deze dag gaf
        const crowds = [
          ...newReports.map((r) => r.crowd),
          ...["ent", "crea"].flatMap((k) =>
            legacyArr(k).flatMap((e) => Object.values(e?.actFields || {}).map((af) => af.crowd))
          ),
        ].filter((x) => x >= 1 && x <= 5);
        const avgCrowd = crowds.length
          ? Math.round(crowds.reduce((a, b) => a + b, 0) / crowds.length)
          : null;
        return !showBez ? (
          <button
            onClick={() => setShowBez(true)}
            className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-2.5 flex items-center gap-2.5 text-left"
          >
            <Users size={16} style={{ color: C.Pine }} />
            <span className="text-sm font-semibold" style={{ color: C.Lagoon }}>
              {total.toLocaleString("nl-NL")} gasten
            </span>
            <span className="text-xs text-gray-500 truncate flex-1">
              {st.kids ? `${st.kids} kinderen` : ""}
              {st.villas ? `${st.kids ? " · " : ""}${st.villas} cottages` : ""}
            </span>
            {avgCrowd && <DbCrowdChip lvl={avgCrowd} />}
            <ChevronDown size={16} className="text-gray-500" />
          </button>
        ) : (
          <div
            onClick={() => setShowBez(false)}
            className="cursor-pointer bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-2">
            <div className="flex items-center gap-2.5">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${C.Pine}1A` }}
              >
                <Users size={18} style={{ color: C.Pine }} />
              </div>
              <div>
                <p className="text-lg font-semibold leading-tight" style={{ color: C.Lagoon }}>
                  {total.toLocaleString("nl-NL")}{" "}
                  <span className="text-xs font-semibold text-gray-500">gasten</span>
                </p>
                <p className="text-xs text-gray-500">
                  {st.kids ? `${st.kids} kinderen` : ""}
                  {st.kids && st.babies ? " · " : ""}
                  {st.babies ? `${st.babies} baby's` : ""}
                  {st.villas ? `${st.kids || st.babies ? " · " : ""}${st.villas} cottages` : ""}
                </p>
              </div>
            </div>
            {segs.length > 0 && (
              <div className="flex-1 min-w-[140px]">
                <div className="flex h-2 rounded-full overflow-hidden bg-gray-100">
                  {segs.map((g) => (
                    <div
                      key={g.label}
                      title={`${g.label}: ${g.count}`}
                      style={{ width: `${(g.count / barTotal) * 100}%`, backgroundColor: g.color }}
                    />
                  ))}
                </div>
                <div className="flex gap-3 mt-1">
                  {segs.map((g) => (
                    <span key={g.label} className="text-xs text-gray-500 flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: g.color }} />
                      {g.label} {Math.round((g.count / barTotal) * 100)}%
                    </span>
                  ))}
                </div>
              </div>
            )}
            {nats.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {nats.map((n) => (
                  <span
                    key={n.k}
                    className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-50 border border-gray-100 text-gray-500"
                  >
                    {n.i} {n.pct}%
                  </span>
                ))}
              </div>
            )}
            {avgCrowd && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">Drukte volgens team</span>
                <DbCrowdChip lvl={avgCrowd} />
              </div>
            )}
          </div>
        );
      })()}
      </div>

      {/* FM-briefing (mobiel: uitklappen via de compacte kop) */}
      <div className={`${briefOpen || editingBriefing ? "" : "hidden"} lg:block`}>
      {/* FM-briefing */}
      <div
        className="rounded-2xl border overflow-hidden"
        style={{
          backgroundColor: briefing && !iHaveRead ? C.BlossomLight : "white",
          borderColor: briefing && !iHaveRead ? C.Blossom : "#F3F4F6",
        }}
      >
        <div className="px-4 py-3 flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: briefing ? "white" : `${C.Pine}14` }}
          >
            <Megaphone size={18} style={{ color: C.Pine }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2 min-h-[40px]">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold leading-5" style={{ color: C.Pine }}>
                  Briefing van de dag
                </p>
                <p className="text-xs text-gray-500">
                  {briefing
                    ? `${briefing.author} · ${dbAgo(briefing.updatedAt)}`
                    : isFM
                    ? "Nog geen briefing voor deze dag"
                    : "Er is nog geen briefing voor deze dag"}
                </p>
              </div>
              {isFM && !editingBriefing && (
                <button
                  onClick={() => {
                    setBriefingDraft(briefing?.text || "");
                    setEditingBriefing(true);
                  }}
                  className="flex-shrink-0 h-8 text-xs font-semibold flex items-center gap-1 px-3 rounded-lg border"
                  style={{ backgroundColor: "white", color: C.Pine, borderColor: `${C.Pine}33` }}
                >
                  {briefing ? <Edit2 size={12} /> : <Plus size={12} />}
                  {briefing ? "Bewerken" : "Schrijven"}
                </button>
              )}
            </div>
            {editingBriefing ? (
              <div className="mt-2 space-y-2">
                <MentionTextarea
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#045E51] resize-none bg-white h-28"
                  placeholder="Wat moet iedereen vandaag weten? Bijzonderheden, wijzigingen, aandachtspunten… (@naam of @team)"
                  value={briefingDraft}
                  onChange={(e) => setBriefingDraft(e.target.value)}
                  autoFocus
                  staffList={staffList}
                  loggedInUserName={me}
                />
                <div className="flex gap-2">
                  <button
                    onClick={saveBriefing}
                    className="px-4 py-1.5 rounded-lg text-sm font-semibold text-white"
                    style={{ backgroundColor: C.Pine }}
                  >
                    Publiceren
                  </button>
                  <button
                    onClick={() => setEditingBriefing(false)}
                    className="px-4 py-1.5 rounded-lg text-sm text-gray-500 bg-white"
                  >
                    Annuleren
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  Na het publiceren moet iedereen de briefing opnieuw als gelezen markeren.
                </p>
              </div>
            ) : briefing ? (
              <>
                <p className="text-sm text-gray-800 whitespace-pre-wrap mt-1">
                  {highlightMentions(briefing.text, me)}
                </p>
                <div className="flex items-center gap-2 flex-wrap mt-2.5">
                  {!iHaveRead && (
                    <button
                      onClick={markRead}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white flex items-center gap-1"
                      style={{ backgroundColor: C.Pine }}
                    >
                      <Eye size={13} /> Gelezen
                    </button>
                  )}
                  <div className="flex -space-x-1.5">
                    {readers.slice(0, 8).map((n) => (
                      <div key={n} className="rounded-full ring-2 ring-white" title={n}>
                        <ProfileAvatar name={n} staffList={staffList} size={22} />
                      </div>
                    ))}
                  </div>
                  <span className="text-xs text-gray-500">
                    {readers.length === 0
                      ? "Nog door niemand gelezen"
                      : `Gelezen door ${readers.length}${workingNames.size ? ` van ${[...workingNames].filter((n) => n !== briefing.author).length}` : ""}`}
                  </span>
                  {isFM && notYetRead.length > 0 && (
                    <span className="text-xs font-semibold" style={{ color: C.Bark }}>
                      Nog niet: {notYetRead.join(", ")}
                    </span>
                  )}
                </div>
              </>
                        ) : null}
          </div>
        </div>
      </div>
      </div>

      {/* Mobiel: onderdelen van het Dagbord + plusknop, blijft bovenin staan */}
      <div
        className="lg:hidden sticky top-0 z-20 -mx-4 px-4 pt-2 pb-2 space-y-2"
        style={{ backgroundColor: "var(--pagina-bg, #F0FDF4)" }}
        data-noswipe
      >
        {renderMobielTabs()}
        {tab === "planning" && openAfds.length > 1 && renderAfdBar()}
      </div>
      {/* Mobiel: zwevende plusknop rechtsonder, boven de tabbalk */}
      <button
        onClick={() => setSheet("menu")}
        className="lg:hidden fixed right-4 z-30 w-14 h-14 rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform"
        style={{
          backgroundColor: C.Honey,
          color: C.Lagoon,
          bottom: "calc(92px + max(16px, env(safe-area-inset-bottom)))",
        }}
        aria-label="Nieuw: overdracht, actiepunt of extra taak"
        aria-haspopup="dialog"
        data-noswipe
      >
        <Plus size={26} />
      </button>
      <div className="hidden lg:block">{openAfds.length > 1 && renderAfdBar()}</div>

      {renderNvtVraag()}
      {briefSheet && briefing && (
        <div
          className="fixed inset-0 z-[90] flex items-end lg:items-center justify-center"
          style={{ backgroundColor: "rgba(0,25,47,0.45)" }}
          onClick={() => setBriefSheet(false)}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          data-noswipe
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="briefing-titel"
            className="bg-white w-full lg:max-w-lg rounded-t-3xl lg:rounded-3xl shadow-xl flex flex-col db-cheer"
            style={{ maxHeight: "85vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-gray-100">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: C.PineLight }}
              >
                <Megaphone size={18} style={{ color: C.Pine }} />
              </div>
              <div className="flex-1 min-w-0">
                <p id="briefing-titel" className="text-base font-semibold" style={{ color: C.Lagoon }}>
                  Briefing van vandaag
                </p>
                <p className="text-xs text-gray-500 flex items-center gap-1.5">
                  <ProfileAvatar name={briefing.author} staffList={staffList} size={16} />
                  {briefing.author} · {dbAgo(briefing.updatedAt)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setBriefSheet(false)}
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: "#F3F4F6" }}
                aria-label="Sluiten"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-4 py-4 overflow-y-auto">
              <p className="text-base leading-relaxed text-gray-800 whitespace-pre-wrap">
                {highlightMentions(briefing.text, me)}
              </p>
              <div className="flex items-center gap-2 flex-wrap mt-4">
                <div className="flex -space-x-1.5">
                  {readers.slice(0, 8).map((n) => (
                    <div key={n} className="rounded-full ring-2 ring-white" title={n}>
                      <ProfileAvatar name={n} staffList={staffList} size={22} />
                    </div>
                  ))}
                </div>
                <span className="text-xs text-gray-500">
                  {readers.length === 0 ? "Nog door niemand gelezen" : `Gelezen door ${readers.length}`}
                </span>
              </div>
            </div>
            <div
              className="px-4 pt-3 flex gap-2 border-t border-gray-100"
              style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}
            >
              {isFM && (
                <button
                  type="button"
                  onClick={() => {
                    setBriefSheet(false);
                    setBriefingDraft(briefing.text || "");
                    setEditingBriefing(true);
                  }}
                  className="h-11 px-4 rounded-xl text-sm font-semibold bg-white border flex items-center gap-1.5"
                  style={{ color: C.Pine, borderColor: `${C.Pine}55` }}
                >
                  <Edit2 size={14} /> Bewerken
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  if (!iHaveRead) markRead();
                  setBriefSheet(false);
                }}
                className="flex-1 h-11 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-1.5 active:scale-[0.98]"
                style={{ backgroundColor: C.Pine }}
              >
                {iHaveRead ? "Sluiten" : (<><Check size={16} /> Gelezen</>)}
              </button>
            </div>
          </div>
        </div>
      )}
      {!gisterOpen && ovHerinnering && (
        <div
          className="fixed left-3 right-3 lg:left-auto lg:right-6 lg:w-96 bottom-[176px] lg:bottom-8 z-[65] rounded-2xl shadow-lg p-3 bg-white border"
          style={{ borderColor: `${C.Pine}40` }}
          role="alert"
          data-noswipe
        >
          <div className="flex items-start gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: C.HoneyLight }}
            >
              <Edit2 size={16} style={{ color: C.Bark }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold" style={{ color: C.Lagoon }}>
                Overdracht schrijven?
              </p>
              <p className="text-sm text-gray-500 leading-snug">
                {ovHerinnering.title} is afgelopen
                {(() => {
                  const e = String(ovHerinnering.time || "").split("-")[1];
                  return e ? ` (${e.trim()})` : "";
                })()}
                . Hoe ging het?
              </p>
            </div>
          </div>
          <div className="flex gap-2 mt-2.5">
            <button
              onClick={() => setOvLater((p) => ({ ...p, [String(ovHerinnering.id)]: true }))}
              className="flex-1 min-h-[40px] rounded-xl text-sm font-semibold text-gray-500 bg-gray-100"
            >
              Later
            </button>
            <button
              onClick={() => openComposerFor(String(ovHerinnering.id))}
              className="flex-1 min-h-[40px] rounded-xl text-sm font-semibold text-white"
              style={{ backgroundColor: C.Pine }}
            >
              Nu schrijven
            </button>
          </div>
        </div>
      )}
      {gisterOpen && (
        <div
          className="fixed left-3 right-3 lg:left-auto lg:right-6 lg:w-96 bottom-[176px] lg:bottom-8 z-[65] rounded-2xl shadow-lg p-3 flex items-center gap-3"
          style={{ backgroundColor: C.Bark, color: "white" }}
          role="alert"
        >
          <Moon size={18} className="flex-shrink-0" />
          <p className="text-sm flex-1">
            Je dienst van gisteren (gestart {dbHM(gisterOpen.in)}) staat nog open.
          </p>
          <button
            onClick={sluitGisterAf}
            className="min-h-[40px] px-3 rounded-xl text-sm font-semibold flex-shrink-0"
            style={{ backgroundColor: C.Honey, color: C.Lagoon }}
          >
            Afsluiten {dbHM(new Date().toISOString())}
          </button>
        </div>
      )}
      {/* FM: alle taaktijden van deze dag in één scherm */}
      {tijdenPlannen && isFM && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ background: "rgba(0,0,0,0.45)" }} onClick={() => setTijdenPlannen(false)}>
          <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto" role="dialog" aria-modal="true" aria-label="Tijden plannen" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2 sticky top-0 bg-white z-10">
              <Clock size={18} style={{ color: C.Pine }} />
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold" style={{ color: C.Lagoon }}>
                  Tijden plannen · {currentDay.toLocaleDateString("nl-NL", { weekday: "long" })}
                </h3>
                <p className="text-xs text-gray-500">Geldt voor elke {currentDay.toLocaleDateString("nl-NL", { weekday: "long" })} (en dezelfde taak op andere dagen; "Alleen vandaag" kan daarna).</p>
              </div>
              <button aria-label="Sluiten" onClick={() => setTijdenPlannen(false)} className="text-gray-500">
                <X size={18} />
              </button>
            </div>
            <div className="px-5 py-3 text-xs text-gray-600 border-b border-gray-100" style={{ backgroundColor: C.PineLight }}>
              <b className="font-semibold">Vast om</b> = altijd die tijd. <b className="font-semibold">Bij opstart / afsluiten</b> = schuift mee met de diensttijden van de afdeling.{" "}
              <b className="font-semibold">Vóór / na activiteit</b> = schuift mee met die activiteit (op naam, bijv. "disco"); staat die er een dag niet, dan krijgt de taak een voorstel.
            </div>
            <div className="divide-y divide-gray-100">
              {[...timedTasks]
                .sort((a, b) => (a._afd || "").localeCompare(b._afd || "") || a._min - b._min)
                .map((t) => {
                  const sjT = sjabloonVan(t);
                  const regel = sjT?.tijdRegel || t.tijdRegel || null;
                  const soort = regel?.soort || (t._vast ? "vast" : "voorstel");
                  const acts = [...new Set(activities.filter((a) => dbStartMin(a.time) < 9999).map((a) => String(a.title || "").trim()).filter(Boolean))];
                  const zet = (patch) => saveTaskRegel(t, { soort, min: regel?.min ?? 0, act: regel?.act || "", time: sjT?.time || t.time || hhmm(t._min), ...patch });
                  return (
                    <div key={`${t.type}_${t.id}`} className="px-5 py-3">
                      <div className="flex items-start gap-3">
                        <span className="text-sm font-semibold tabular-nums w-12 flex-shrink-0 pt-1" style={{ color: t._vast ? C.Pine : "#6B7280" }}>
                          {hhmm(t._min)}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-800">{t.text}</p>
                          <p className="text-[11px] text-gray-500">
                            {dbAfdeling(t._afd).label} · {FASE_LABEL[t._phase] || t._phase}
                            {!t._vast ? " · voorstel" : ""}
                          </p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                            <select
                              value={soort}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === "voorstel") saveTaskRegel(t, { soort: "voorstel" });
                                else if (v === "vast") saveTaskRegel(t, { soort: "vast", time: hhmm(t._min) });
                                else saveTaskRegel(t, { soort: v, min: v === "start" || v === "eind" ? Math.max(0, v === "start" ? t._min - afdVenster(t._afd).start : afdVenster(t._afd).end - t._min) : 15, act: regel?.act || acts[0] || "" });
                              }}
                              className="h-9 border border-gray-200 rounded-lg px-2 text-xs bg-white"
                            >
                              {Object.entries(REGEL_LABEL).map(([k, l]) => (
                                <option key={k} value={k}>
                                  {l.replace(/ [+−]$/, "")}
                                </option>
                              ))}
                            </select>
                            {soort === "vast" && (
                              <input type="time" step={300} defaultValue={sjT?.time || t.time || hhmm(t._min)} onBlur={(e) => e.target.value && e.target.value !== (sjT?.time || t.time) && zet({ time: e.target.value })} className="h-9 border border-gray-200 rounded-lg px-2 text-xs" />
                            )}
                            {(soort === "start" || soort === "eind" || soort === "voorAct" || soort === "naAct") && (
                              <span className="flex items-center gap-1 text-xs text-gray-600">
                                {soort === "eind" || soort === "voorAct" ? "−" : "+"}
                                <input type="number" min={0} step={5} defaultValue={regel?.min ?? 0} onBlur={(e) => Number(e.target.value) !== Number(regel?.min ?? 0) && zet({ min: Number(e.target.value) })} className="h-9 w-16 border border-gray-200 rounded-lg px-2 text-xs" />
                                min
                              </span>
                            )}
                            {(soort === "voorAct" || soort === "naAct") && (
                              <input
                                list={`db-acts-${t.id}`}
                                defaultValue={regel?.act || ""}
                                placeholder="naam van de activiteit"
                                onBlur={(e) => e.target.value.trim() !== (regel?.act || "") && zet({ act: e.target.value.trim() })}
                                className="h-9 flex-1 min-w-[8rem] border border-gray-200 rounded-lg px-2 text-xs"
                              />
                            )}
                            {(soort === "voorAct" || soort === "naAct") && (
                              <datalist id={`db-acts-${t.id}`}>
                                {acts.map((a) => (
                                  <option key={a} value={a} />
                                ))}
                              </datalist>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
            <div className="px-5 py-3 border-t border-gray-100">
              <button onClick={() => setTijdenPlannen(false)} className="w-full py-2.5 rounded-xl text-sm font-semibold text-white" style={{ backgroundColor: C.Pine }}>
                Klaar
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Dienst afsluiten */}
      {showClose && (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ background: "rgba(0,0,0,0.45)" }}
          onClick={() => setShowClose(false)}
          onKeyDown={(e) => e.key === "Escape" && setShowClose(false)}
        >
          <div
            className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-label="Overdragen en afronden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
              <Moon size={18} style={{ color: C.Pine }} />
              <h3 className="text-base font-semibold flex-1" style={{ color: C.Lagoon }}>
                Overdragen en afronden
              </h3>
              <button aria-label="Sluiten" onClick={() => setShowClose(false)} className="text-gray-500">
                <X size={18} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              {myOpenActs.length > 0 && (
                <div>
                  <p className="text-xs font-semibold mb-1.5" style={{ color: C.Sunset }}>
                    Nog geen overdracht van jouw activiteiten
                  </p>
                  {myOpenActs.map((a) => (
                    <div key={a.id} className="flex items-center gap-2 py-1">
                      <span className="text-xs text-gray-500 w-10">
                        {(a.time || "").split("-")[0].trim()}
                      </span>
                      <span className="text-sm flex-1">{a.title}</span>
                      <button
                        onClick={() => {
                          setShowClose(false);
                          openComposerFor(String(a.id));
                        }}
                        className="text-xs font-semibold px-2.5 py-1 rounded-lg"
                        style={{ backgroundColor: `${C.Pine}12`, color: C.Pine }}
                      >
                        Overdracht schrijven
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {takenVoorMij.length > 0 && (
                <div>
                  <p className="text-xs font-semibold mb-1" style={{ color: C.Sunset }}>
                    {nogAanWerk.length
                      ? "Nog open uit jouw dienst"
                      : isToday && ingeplandOp
                      ? `Nog open — je bent de laatste van ${dbAfdeling(ingeplandOp).label}`
                      : "Taken die nog openstaan"}{" "}
                    ({takenVoorMij.length})
                  </p>
                  {takenVoorMij.slice(0, 10).map((t) => renderTask(t))}
                  {takenVoorMij.length > 10 && (
                    <p className="text-xs text-gray-500">
                      + {takenVoorMij.length - 10} meer in de dagplanning
                    </p>
                  )}
                  {nogAanWerk.length > 0 && (
                    <button
                      onClick={draagOver}
                      disabled={overgedragen}
                      className="mt-2 w-full h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 border active:scale-[0.98]"
                      style={
                        overgedragen
                          ? { backgroundColor: C.GrassLight, color: C.Pine, borderColor: C.Grass }
                          : { backgroundColor: "white", color: C.Pine, borderColor: `${C.Pine}55` }
                      }
                    >
                      {overgedragen ? (
                        <>
                          <Check size={15} /> Doorgegeven aan {namenLijst(nogAanWerk)}
                        </>
                      ) : (
                        <>
                          <Send size={15} /> Overdragen aan {namenLijst(nogAanWerk)}
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
              {takenVoorTeam.length > 0 && (
                <div className="rounded-xl px-3 py-2.5" style={{ backgroundColor: "#F9FAFB" }}>
                  <p className="text-xs font-semibold text-gray-500">
                    Neemt het team over ({takenVoorTeam.length})
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {namenLijst(nogAanWerk)} {nogAanWerk.length === 1 ? "werkt" : "werken"} nog
                    {laatsteEind != null ? ` tot ${hhmm(laatsteEind)}` : ""}. Deze taken staan later gepland.
                  </p>
                  <ul className="mt-1.5 space-y-0.5">
                    {takenVoorTeam.slice(0, 6).map((t) => {
                      const m = tijdVanTaak(t);
                      return (
                        <li key={`${t._src || t.type}_${t.id}`} className="text-sm text-gray-500 flex gap-2">
                          <span className="tabular-nums w-11 flex-shrink-0">
                            {m != null && m < 1439 ? hhmm(m) : "later"}
                          </span>
                          <span className="truncate">{t.text}</span>
                        </li>
                      );
                    })}
                    {takenVoorTeam.length > 6 && (
                      <li className="text-xs text-gray-500">+ {takenVoorTeam.length - 6} meer</li>
                    )}
                  </ul>
                </div>
              )}
              {myPunten.length > 0 && (
                <p className="text-xs font-semibold" style={{ color: C.Bark }}>
                  {myPunten.length} open {myPunten.length === 1 ? "actiepunt staat" : "actiepunten staan"} op jouw naam.
                </p>
              )}
              {myOpenActs.length === 0 && takenVoorMij.length === 0 && (
                <p className="text-sm" style={{ color: C.Pine }}>
                  {takenVoorTeam.length
                    ? "Jouw deel is af en je verslagen staan erin."
                    : "Alles is af en je verslagen staan erin."}
                </p>
              )}
              <div className="rounded-xl p-3 space-y-2" style={{ backgroundColor: C.HoneyLight }}>
                <p className="text-sm font-semibold" style={{ color: C.Bark }}>
                  Moet de volgende dienst iets weten?
                </p>
                <div className="flex flex-wrap gap-1">
                  {DB_AFDELINGEN.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => setDoorAfd(a.id)}
                      className="text-xs font-semibold px-2.5 py-1 rounded-full border"
                      style={
                        doorTargetAfd === a.id
                          ? { backgroundColor: a.text, color: "white", borderColor: a.text }
                          : { backgroundColor: "white", color: a.text, borderColor: `${a.text}33` }
                      }
                    >
                      {a.id === "alle" ? "Hele team" : a.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs" style={{ color: C.Bark }}>
                  {doorTargetLabel}. Bovenaan het dagbord, met leesbevestiging.
                </p>
                {outgoing.map((d) => (
                  <div key={d.id} className="flex gap-2 items-start bg-white rounded-lg px-2.5 py-1.5">
                    <div className="text-xs text-gray-800 flex-1 whitespace-pre-wrap">
                      <span className="text-xs text-gray-500">
                        {dagNaamKey(d._dk)}
                        {d.afdeling && d.afdeling !== "alle" ? ` · ${dbAfdeling(d.afdeling).label}` : ""}:{" "}
                      </span>
                      {editDoor?.id === d.id ? renderDoorEdit() : d.text}
                    </div>
                    {editDoor?.id !== d.id && (
                      <button
                        onClick={() => setEditDoor({ id: d.id, dk: d._dk, text: d.text || "" })}
                        className="text-gray-500 hover:text-[#045E51]"
                        aria-label="Bewerken" title="Bewerken"
                      >
                        <Edit2 size={12} />
                      </button>
                    )}
                    <button
                      onClick={() => deleteDoorFrom(d, d._dk)}
                      className="text-gray-500 hover:text-[#700606]"
                      aria-label="Verwijderen" title="Verwijderen"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
                <MentionTextarea
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#045E51] resize-none bg-white h-20"
                  placeholder="Bv. kist met prijzen staat in de berging, nog 2 bandjes over, @Emma bel even de receptie…"
                  value={doorText}
                  onChange={(e) => setDoorText(e.target.value)}
                  staffList={staffList}
                  loggedInUserName={me}
                />
                <label className="flex items-center gap-2 text-xs" style={{ color: C.Bark }}>
                  <input
                    type="checkbox"
                    checked={doorAsPunt}
                    onChange={(e) => setDoorAsPunt(e.target.checked)}
                  />
                  Ook als actiepunt (blijft staan tot het is opgelost)
                </label>
                <button
                  onClick={sendDoorgeven}
                  className="w-full py-2 rounded-xl text-sm font-semibold text-white"
                  style={{ backgroundColor: C.Bark, opacity: doorText.trim() ? 1 : 0.5 }}
                >
                  Doorgeven
                  {doorTarget.dienst
                    ? ` aan ${doorTargetAfd === "alle" ? "volgende dienst" : dbAfdeling(doorTargetAfd).label}`
                    : ""}
                </button>
              </div>
              {isToday && !urenKlaar ? (
                <button
                  onClick={() => {
                    setShowClose(false);
                    urenOpenen();
                  }}
                  className="w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                  style={{ backgroundColor: C.Pine, color: "white" }}
                >
                  <Clock size={16} /> Uren van vandaag invullen
                </button>
              ) : (
                <>
                  {myClock.in && myClock.out && (
                    <p className="text-sm text-center" style={{ color: C.Pine }}>
                      Gewerkt {dbHM(myClock.in)}–{dbHM(myClock.out)} ·{" "}
                      {dbUren(dbWorked(myClock, myPlanned)?.min || 0)}
                      {dbWorked(myClock, myPlanned)?.pauze
                        ? ` (excl. ${dbMinLabel(dbWorked(myClock, myPlanned).pauze)} pauze)`
                        : ""}
                    </p>
                  )}
                  {myDeviation && !alreadyCorrected && !corrSent && (
                    <div className="rounded-xl p-3 space-y-2 border border-gray-200">
                      <p className="text-sm font-semibold" style={{ color: C.Lagoon }}>
                        Je tijden wijken af van het rooster
                      </p>
                      <p className="text-xs text-gray-500">
                        Gepland {myPlanned.start}–{myPlanned.end}, gewerkt{" "}
                        {myDeviation.start}–{myDeviation.end}. Dien direct een
                        urencorrectie in.
                      </p>
                      <input
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#045E51]"
                        placeholder="Reden, bv. langer gebleven voor opruimen Prize Night"
                        value={corrReason}
                        onChange={(e) => setCorrReason(e.target.value)}
                      />
                      <button
                        onClick={sendCorrection}
                        className="w-full py-2 rounded-lg text-sm font-semibold"
                        style={{ backgroundColor: `${C.Pine}12`, color: C.Pine }}
                      >
                        Urencorrectie indienen
                      </button>
                    </div>
                  )}
                  {(alreadyCorrected || corrSent) && myDeviation && (
                    <p className="text-xs text-center text-gray-500">
                      Urencorrectie is ingediend.
                    </p>
                  )}
                  <button
                    onClick={() => setShowClose(false)}
                    className="w-full py-2.5 rounded-xl text-sm font-semibold"
                    style={{ backgroundColor: C.Pine, color: "white" }}
                  >
                    Klaar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] gap-4 items-start">
        {/* Links: dagplanning */}
        <div className={`space-y-4 ${tab === "planning" ? "" : "hidden"} lg:block`}>
        {/* Tijdlijn */}
        <div id="db-tijdlijn" className="scroll-mt-40" />
        <DbCard
          icon={Clock}
          title="Dagplanning"
          subtitle="Programma, taken en overdrachten"
          right={
            isFM && timedTasks.length > 0 ? (
              <button
                onClick={() => setTijdenPlannen(true)}
                className="h-8 px-2.5 rounded-lg text-xs font-semibold flex items-center gap-1"
                style={{ backgroundColor: C.PineLight, color: C.Pine }}
              >
                <Clock size={13} /> Tijden plannen
              </button>
            ) : null
          }
        >
          {!hasAnything && (
            <EmptyState
              icon={IcProgramma}
              message={afdeling !== "alle" ? "Hier is het vandaag rustig." : "Nog niks gepland voor vandaag."}
              sub={afdeling !== "alle" ? "Geen programma of taken in deze afdeling." : "Geen programma of taken voor deze dag."}
            />
          )}
          {(() => {
            // Afgelopen activiteiten mét overdracht klappen in tot één regel
            const klaarActs = isToday
              ? visibleActs.filter(
                  (a) =>
                    isActivityPast(a.time, currentDay) &&
                    !isActivityNow(a.time, currentDay) &&
                    dayFeed.some((it) => it.kind === "act" && String(it.actId) === String(a.id))
                )
              : [];
            const rest = visibleActs.filter((a) => showPast || !klaarActs.includes(a));
            // Taken groeperen per fase en per uur
            const PH = {
              opstart: { label: "Opstarten", icon: Sun, orde: 0 },
              gedurende: { label: "Tussendoor", icon: ClipboardList, orde: 1 },
              afsluiting: { label: "Afsluiten", icon: Moon, orde: 2 },
            };
            const groepen = {};
            timedTasks
              .slice()
              .sort((x, y) => x._min - y._min)
              .forEach((t) => {
                const k = `${t._phase}|${Math.floor(t._min / 60)}`;
                (groepen[k] = groepen[k] || []).push(t);
              });
            const items = [
              ...rest.map((a) => ({ min: dbStartMin(a.time), orde: 1, el: () => renderActivity(a) })),
              ...Object.entries(groepen).map(([k, list]) => {
                const ph = PH[k.split("|")[0]] || PH.gedurende;
                const min = Math.min(...list.map((t) => t._min));
                return {
                  min,
                  orde: ph.orde === 2 ? 2 : 0,
                  el: () => renderTaskGroup(k, ph.label, ph.icon, list, hhmm(min)),
                };
              }),
            ].sort((x, y) => x.min - y.min || x.orde - y.orde);
            // Wat er eerder vandaag nog open staat (subtiel boven de nu-lijn)
            const openTakenLijst = isToday
              ? timedTasks.filter(
                  (t) => t._min < nuMin && !t.done && !t.nvt && !afdNogNietBegonnen(t._afd)
                )
              : [];
            const misOvLijst = isToday
              ? visibleActs.filter(
                  (a) =>
                    isActivityPast(a.time, currentDay) &&
                    !isActivityNow(a.time, currentDay) &&
                    !dayFeed.some((it) => it.kind === "act" && String(it.actId) === String(a.id))
                )
              : [];
            const openTaken = openTakenLijst.length;
            const misOv = misOvLijst.length;
            const eerderOpen =
              openTaken + misOv > 0 ? (
                <div key="eerder-open" className="flex gap-3">
                  <div className="w-10 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => setEerderOpenOpen((v) => !v)}
                      aria-expanded={eerderOpenOpen}
                      className="w-full min-h-[34px] flex items-center gap-1.5 text-xs text-left"
                      style={{ color: C.Bark }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: C.Bark }} />
                      <span className="flex-1">
                        Eerder vandaag nog open:{" "}
                        <span className="font-semibold">
                          {[
                            openTaken ? `${openTaken} ${openTaken === 1 ? "taak" : "taken"}` : null,
                            misOv ? `${misOv} ${misOv === 1 ? "overdracht" : "overdrachten"}` : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </span>
                      {eerderOpenOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                    {eerderOpenOpen && (
                      <div className="mb-1.5 rounded-xl border px-2.5 py-1" style={{ borderColor: `${C.Bark}33`, backgroundColor: "#FFFBF2" }}>
                        {misOvLijst.map((a) => (
                          <button
                            key={`ov-${a.id}`}
                            onClick={() => openComposerFor(String(a.id))}
                            className="w-full min-h-[40px] flex items-center gap-2 text-sm text-left border-b last:border-b-0"
                            style={{ borderColor: `${C.Bark}1A` }}
                          >
                            <Edit2 size={14} style={{ color: C.Bark }} className="flex-shrink-0" />
                            <span className="flex-1" style={{ color: C.Lagoon }}>
                              Overdracht: {a.title}
                            </span>
                            <span className="text-xs font-semibold flex-shrink-0" style={{ color: C.Pine }}>
                              Schrijven
                            </span>
                          </button>
                        ))}
                        {openTakenLijst.map((t) => (
                          <button
                            key={`t-${t.id}`}
                            onClick={() => taakAfvinken(t)}
                            className="w-full min-h-[40px] flex items-center gap-2 text-sm text-left border-b last:border-b-0"
                            style={{ borderColor: `${C.Bark}1A` }}
                          >
                            <span className="w-5 h-5 rounded-md border-2 flex-shrink-0" style={{ borderColor: "#6B7280" }} />
                            <span className="flex-1 text-gray-800">{t.text}</span>
                            <span className="text-xs text-gray-500 tabular-nums flex-shrink-0">{t._vast ? "" : "±"}{hhmm(t._min)}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : null;
            const lijn = (
              <div key="nu-lijn" id="db-nu-lijn" className="flex items-center gap-3 my-1.5 scroll-mt-40" aria-label="Nu">
                <div className="w-10 flex-shrink-0 text-right">
                  <span className="text-xs font-semibold" style={{ color: C.Pine }}>
                    {dbTime(nuTick)}
                  </span>
                </div>
                <div className="flex-1 flex items-center">
                  <span className="w-2.5 h-2.5 rounded-full -ml-1" style={{ backgroundColor: C.Pine }} />
                  <span className="flex-1 h-0.5" style={{ backgroundColor: C.Pine }} />
                </div>
              </div>
            );
            const els = items.map((x) => x.el());
            if (isToday) {
              const idx = items.findIndex((x) => x.min > nuMin);
              const blok = eerderOpen ? [eerderOpen, lijn] : [lijn];
              if (idx === -1) els.push(...blok);
              else els.splice(idx, 0, ...blok);
            }
            return (
              <>
                {klaarActs.length > 0 && (
                  <div className="flex gap-3">
                    <div className="w-10 flex-shrink-0" />
                    <button
                      onClick={() => setShowPast((v) => !v)}
                      className="flex-1 my-1 min-h-[40px] rounded-xl border border-dashed px-3 text-xs font-semibold flex items-center gap-1.5"
                      style={{ borderColor: `${C.Pine}40`, color: C.Pine, backgroundColor: C.PineLight }}
                    >
                      <CheckCheck size={14} />
                      {klaarActs.length} eerder vandaag, met overdracht
                      <span className="ml-auto">{showPast ? "Verbergen" : "Tonen"}</span>
                    </button>
                  </div>
                )}
                {els}
              </>
            );
          })()}
        </DbCard>
          {/* Extra taken */}
          {extraTasks.length > 0 && (
            <DbCard
              icon={CheckSquare}
              accent={C.Lagoon}
              title="Extra taken"
              subtitle={
                extraOpen.length > 0
                  ? `${extraOpen.length} nog te doen · blijven staan tot ze af zijn`
                  : "Alles afgerond"
              }
            >
              {extraOpen.length === 0 && (
                <EmptyState compact icon={IcTaken} message="Alles afgevinkt. Top gedaan!" />
              )}
              {extraOpen.map((t) => renderTask(t))}
              {extraDone.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <button
                    onClick={() => setShowExtraDone((v) => !v)}
                    className="text-xs font-semibold text-gray-500 hover:text-gray-500 flex items-center gap-1"
                  >
                    {showExtraDone ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    Afgerond ({extraDone.length})
                  </button>
                  {showExtraDone && extraDone.map((t) => renderTask(t))}
                </div>
              )}
            </DbCard>
          )}

        </div>

        {/* Rechts: gekozen tabblad */}
        <div className={`space-y-4 ${tab === "planning" ? "hidden" : ""} lg:block`}>
          {renderTabs("desktop")}
          {sideTab === "overdracht" && (
            <div className="flex flex-col gap-3">
              {(() => {
                const opties = ["alle", "entertainment", "crea", "springkussen", "hotspot"];
                return (
                  <div className="space-y-2">
                    <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-0.5" data-noswipe>
                      {opties.map((a) => {
                        const aan = ovdAfd === a;
                        const info = dbAfdeling(a);
                        return (
                          <button
                            key={a}
                            onClick={() => setOvdAfd(a)}
                            className="flex-shrink-0 min-h-[36px] px-3 rounded-full text-xs font-semibold border flex items-center gap-1.5"
                            style={aan ? { backgroundColor: C.Pine, borderColor: C.Pine, color: "white" } : { backgroundColor: "white", borderColor: "#F3F4F6", color: "#4B5563" }}
                          >
                            {a !== "alle" && <DbAfdIcoon id={a} size={12} />}
                            {a === "alle" ? "Alles" : info.label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex gap-2">
                      <label className="flex-1 min-w-0 h-10 bg-white rounded-xl border border-gray-100 shadow-sm flex items-center gap-2 px-3">
                        <Search size={15} className="text-gray-500 flex-shrink-0" />
                        <input
                          value={ovdZoek}
                          onChange={(e) => setOvdZoek(e.target.value)}
                          placeholder="Zoek in overdrachten"
                          className="flex-1 min-w-0 bg-transparent text-sm outline-none border-0 p-0 shadow-none focus:ring-0"
                          style={{ boxShadow: "none" }}
                          aria-label="Zoek in overdrachten"
                        />
                        {ovdZoek && (
                          <button onClick={() => setOvdZoek("")} aria-label="Zoekterm wissen" className="text-gray-500 -mr-1 p-1">
                            <X size={15} />
                          </button>
                        )}
                      </label>
                      <button
                        onClick={() => setOvdFilters((v) => !v)}
                        className="w-10 h-10 rounded-xl border shadow-sm flex items-center justify-center flex-shrink-0 active:scale-95"
                        style={
                          ovdFilters || ovdAct || ovdSoort
                            ? { backgroundColor: C.Pine, borderColor: C.Pine, color: "white" }
                            : { backgroundColor: "white", borderColor: "#F3F4F6", color: C.Pine }
                        }
                        aria-label="Filter op activiteit of soort"
                        aria-expanded={ovdFilters}
                        title="Filters"
                      >
                        <SlidersHorizontal size={16} />
                      </button>
                    </div>
                    {(ovdFilters || ovdAct || ovdSoort) && (
                      <select
                        value={ovdAct}
                        onChange={(e) => setOvdAct(e.target.value)}
                        className="w-full h-10 bg-white rounded-xl border border-gray-100 shadow-sm px-2 text-sm font-semibold"
                        style={{ color: ovdAct ? C.Pine : "#4B5563" }}
                        aria-label="Filter op activiteit"
                      >
                        <option value="">Alle activiteiten</option>
                        {actOpties.map(([k, o]) => (
                          <option key={k} value={k}>
                            {o.titel} ({o.n})
                          </option>
                        ))}
                      </select>
                    )}
                    {(ovdFilters || ovdSoort) && (() => {
                      const aanwezig = DB_SOORTEN.filter((x) => zoekFeed.some((it) => it.soort === x.k));
                      if (!aanwezig.length) return null;
                      return (
                        <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-0.5" data-noswipe>
                          {aanwezig.map((x) => {
                            const aan = ovdSoort === x.k;
                            return (
                              <button
                                key={x.k}
                                onClick={() => setOvdSoort(aan ? "" : x.k)}
                                className="flex-shrink-0 h-8 px-2.5 rounded-full text-xs font-semibold flex items-center gap-1"
                                style={aan ? { backgroundColor: x.fg, color: "white" } : { backgroundColor: x.bg, color: x.fg }}
                              >
                                <x.Ic size={12} /> {x.l}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}
              <button
                onClick={() => {
                  setComposerAct(null);
                  setComposerAfd("alle");
                  setSheet("overdracht");
                }}
                className="lg:hidden order-last w-full min-h-[48px] bg-white rounded-2xl border border-gray-100 shadow-sm px-4 text-left text-sm text-gray-500 flex items-center gap-2"
              >
                <Edit2 size={15} style={{ color: C.Pine }} /> Schrijf een overdracht…
              </button>
              <div ref={composerRef} className="hidden lg:block">
                <DbComposer
                  key={`ovd_${composerAfd}`}
                  defaultAfd={composerAfd}
                  appData={appData}
                  activities={activities}
                  actId={composerAct}
                  setActId={setComposerAct}
                  staffList={staffList}
                  me={me}
                  werkendeNamen={[...workingNames]}
                  onPost={(d) => {
                    postOverdracht(d);
                    setComposerAfd("alle");
                  }}
                  isFM={isFM}
                  dateKey={dateKey}
                />
              </div>
              {isFM && (() => {
                // Vooruit geplaatste FM-opmerkingen voor komende dagen
                const lijst = Object.entries(appData.dagbord || {})
                  .filter(([dk]) => dk > todayKey)
                  .flatMap(([dk, dd]) =>
                    Object.entries(dd || {})
                      .filter(([k, v]) => k.startsWith("c_") && v && v.vooraf && !v.deleted)
                      .map(([k, v]) => ({ dk, k, v }))
                  )
                  .sort((x, y) => x.dk.localeCompare(y.dk));
                if (!lijst.length) return null;
                return (
                  <details className="bg-white rounded-2xl border border-gray-100 shadow-sm">
                    <summary className="px-4 min-h-[44px] flex items-center text-sm font-semibold cursor-pointer" style={{ color: C.Lagoon }}>
                      Vooruit geplaatst ({lijst.length})
                    </summary>
                    <div className="px-4 pb-3 space-y-2">
                      {lijst.map(({ dk, k, v }) => (
                        <div key={dk + k} className="flex items-start gap-2 py-2 border-t border-gray-100">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-xs font-semibold" style={{ color: C.Pine }}>
                                {parseDateKey(dk).toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" })}
                              </span>
                              <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                <DbAfdIcoon id={v.afdeling || "alle"} size={11} />
                                {v.afdeling && v.afdeling !== "alle" ? dbAfdeling(v.afdeling).label : "Iedereen"}
                              </span>
                              {v.important && (
                                <span className="text-xs font-semibold" style={{ color: C.Bark }}>Belangrijk</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">{v.text}</p>
                          </div>
                          <button
                            onClick={() => {
                              updateData({ dagbord: { [dk]: { [k]: { deleted: true } } } });
                              offerUndo("Opmerking verwijderd", () =>
                                updRef.current({ dagbord: { [dk]: { [k]: { deleted: false } } } })
                              );
                            }}
                            className="text-gray-500 hover:text-[#700606] p-1"
                            aria-label="Verwijderen"
                            title="Verwijderen"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </details>
                );
              })()}
              {(ovdZoek.trim() || ovdAct || ovdSoort) && (() => {
                // ── Zoek-/activiteitresultaten: plat, nieuwste eerst, per dag ──
                const q = ovdZoek.trim().toLowerCase();
                const pastAfd = (it) => ovdAfd === "alle" || (it._afd || "alle") === "alle" || it._afd === ovdAfd;
                const tekstVan = (it) =>
                  [it.text, it.author, it._title, it.actTitle, it.colleagues, dbSoort(it.soort)?.l, ...dbSoortDetails(it), ...Object.keys(DB_FIELD_LABELS).map((k) => it[k])]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                const alle = zoekFeed
                  .filter(pastAfd)
                  .filter((it) => !ovdAct || (it.kind === "act" && actSleutel(it) === ovdAct))
                  .filter((it) => !ovdSoort || it.soort === ovdSoort)
                  .filter((it) => !q || tekstVan(it).includes(q))
                  .sort((a, b) => b._date.localeCompare(a._date) || (b.at || 0) - (a.at || 0));
                const niets = alle.filter((it) => it.nietsBijzonders).length;
                const res = alle.filter((it) => !it.nietsBijzonders);
                const crowds = alle.map((it) => it.crowd).filter((x) => x >= 1 && x <= 5);
                const gem = crowds.length ? Math.round(crowds.reduce((t, x) => t + x, 0) / crowds.length) : 0;
                const dagen = [...new Set(res.map((it) => it._date))];
                const dagKop = (dk) => {
                  if (dk === todayKey) return "Vandaag";
                  if (dk === shiftKey(todayKey, -1)) return "Gisteren";
                  const t = parseDateKey(dk).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" });
                  return t.charAt(0).toUpperCase() + t.slice(1);
                };
                return (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 px-1 flex-wrap">
                      <p className="text-xs font-semibold flex-1" style={{ color: C.Lagoon }}>
                        {res.length} overdracht{res.length === 1 ? "" : "en"}
                        {niets > 0 ? ` · ${niets}× niets bijzonders` : ""}
                        {ovdAct ? " · laatste 90 dagen" : ""}
                      </p>
                      {ovdAct && gem > 0 && <DbCrowdChip lvl={gem} />}
                      <button
                        onClick={() => {
                          setOvdZoek("");
                          setOvdAct("");
                          setOvdSoort("");
                        }}
                        className="min-h-[32px] text-xs font-semibold flex items-center gap-1"
                        style={{ color: C.Pine }}
                      >
                        <X size={13} /> Wissen
                      </button>
                    </div>
                    {!res.length && (
                      <EmptyState icon={Search} message="Niets gevonden." sub="Probeer een ander woord, of zet de afdeling op Alles." />
                    )}
                    {dagen.map((dk) => (
                      <div key={dk} className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 pb-1">
                        <p className="text-xs font-semibold text-gray-500 pt-2.5">{dagKop(dk)}</p>
                        {res
                          .filter((it) => it._date === dk)
                          .map((it) => (
                            <DbFeedItem
                              key={`${it._date}|${it._readKey}`}
                              it={it}
                              me={me}
                              staffList={staffList}
                              canModerate={isFM}
                              isNew={false}
                              compact
                              inContext={!!ovdAct}
                              onRead={null}
                              onEdit={editFeed}
                              onDelete={deleteFeed}
                              reacties={reactiesFor(it)}
                              onReact={reactOn}
                              onDeleteReact={deleteReact}
                            />
                          ))}
                      </div>
                    ))}
                  </div>
                );
              })()}
              {!(ovdZoek.trim() || ovdAct || ovdSoort) && (() => {
                const pastFilter = (it) => ovdAfd === "alle" || (it._afd || "alle") === "alle" || it._afd === ovdAfd;
                // ── Teruglezen: één dag tegelijk, standaard de dag van het bord ──
                const basisDag = dateKey > todayKey ? todayKey : dateKey;
                const toonDag = ovdDag && ovdDag <= todayKey ? ovdDag : basisDag;
                const itemsVanDag = (dk) =>
                  (dk === dateKey ? dayFeed : zoekFeed.filter((it) => it._date === dk)).filter(pastFilter);
                const dagItems = itemsVanDag(toonDag);
                const hoofdletter = (t) => t.charAt(0).toUpperCase() + t.slice(1);
                const dagKop = (dk) => {
                  if (dk === todayKey) return "Vandaag";
                  if (dk === shiftKey(todayKey, -1)) return "Gisteren";
                  return hoofdletter(parseDateKey(dk).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" }));
                };
                const fmtTijd = (m) =>
                  m == null ? "" : `${String(Math.floor(m / 60) % 24).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
                const actsVan = (dk) =>
                  (appData.schedule || [])
                    .filter((a) => a.date === dk)
                    .sort((a, b) => dbStartMin(a.time) - dbStartMin(b.time));
                const actIdVan = (it) => String(it.actId ?? it._act?.id ?? "");
                const isVoorbij = (dk, act) => dk < todayKey || (dk === todayKey && isActivityPast(act.time, new Date()));
                const renderRegel = (it) =>
                  it.nietsBijzonders ? (
                    <DbNietsRegel key={`${it._date}|${it._readKey}`} it={it} magWeg={isFM || it.author === me} onDelete={deleteFeed} />
                  ) : (
                    <DbFeedItem
                      key={`${it._date}|${it._readKey}`}
                      it={it}
                      me={me}
                      staffList={staffList}
                      canModerate={isFM}
                      isNew={false}
                      compact
                      inContext
                      onRead={null}
                      onEdit={editFeed}
                      onDelete={deleteFeed}
                      reacties={reactiesFor(it)}
                      onReact={reactOn}
                      onDeleteReact={deleteReact}
                      verwacht={it._date === dateKey ? workingNames : null}
                    />
                  );
                const schrijfVoor = (afd) => {
                  setComposerAct(null);
                  setComposerAfd(afd);
                  if (typeof window !== "undefined" && window.innerWidth < 1024) {
                    setSheet("overdracht");
                    return;
                  }
                  setTimeout(() => composerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
                };
                const AFD_LIJST = ["alle", "entertainment", "crea", "springkussen", "hotspot"].filter(
                  (a) => ovdAfd === "alle" || a === "alle" || a === ovdAfd
                );
                // Per afdeling één kaart: wie er stond, per activiteit de overdracht, daarna de rest.
                // volledig = dagoverzicht (met bezetting en wat nog ontbreekt); anders alleen de berichten.
                const renderAfdKaarten = (dk, items, volledig) => {
                  const acts = actsVan(dk);
                  const bez = volledig ? dbBezetting(appData, dk) : {};
                  return AFD_LIJST.map((afd) => {
                    const actsAfd = afd === "alle" ? [] : acts.filter((a) => dbActAfdeling(a) === afd);
                    const itemsAfd = items.filter((it) => (it._afd || "alle") === afd);
                    const mensen = bez[afd] || [];
                    if (!itemsAfd.length && !(volledig && mensen.length)) return null;
                    const actIds = new Set(actsAfd.map((a) => String(a.id)));
                    const metOv = actsAfd
                      .map((act) => ({ act, its: itemsAfd.filter((it) => it.kind === "act" && actIdVan(it) === String(act.id)) }))
                      .filter((x) => x.its.length);
                    const overig = itemsAfd.filter((it) => !(it.kind === "act" && actIds.has(actIdVan(it))));
                    const zonder = volledig
                      ? actsAfd.filter(
                          (act) => isVoorbij(dk, act) && !itemsAfd.some((it) => it.kind === "act" && actIdVan(it) === String(act.id))
                        )
                      : [];
                    const info = dbAfdeling(afd);
                    const magSchrijven =
                      volledig &&
                      afd !== "alle" &&
                      dk === dateKey &&
                      dk <= todayKey &&
                      (isFM || mensen.some((p) => p.name === me)) &&
                      !itemsAfd.some((it) => it.author === me && it.kind !== "act");
                    return (
                      <div
                        key={afd}
                        className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 pb-2"
                        style={{ borderLeftWidth: 3, borderLeftColor: dbAfdDot[afd] || C.Pine }}
                      >
                        <div className="flex items-center gap-2 min-h-[44px]">
                          <DbAfdIcoon id={afd} size={16} />
                          <span className="text-sm font-semibold flex-1 min-w-0 truncate" style={{ color: C.Lagoon }}>
                            {afd === "alle" ? "Voor iedereen" : info.label}
                          </span>
                        </div>
                        {mensen.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 pb-2">
                            {mensen.map((p) => (
                              <span
                                key={p.name}
                                className="inline-flex items-center gap-1.5 h-7 pl-0.5 pr-2.5 rounded-full text-xs font-semibold"
                                style={{ backgroundColor: info.bg, color: info.text }}
                              >
                                <ProfileAvatar name={p.name} staffList={staffList} size={22} />
                                {String(p.name).split(" ")[0]}
                                {p.van != null && (
                                  <span className="font-medium tabular-nums opacity-80">
                                    {fmtTijd(p.van)}
                                    {p.tot != null ? `–${fmtTijd(p.tot)}` : ""}
                                  </span>
                                )}
                              </span>
                            ))}
                          </div>
                        )}
                        {metOv.map(({ act, its }) => (
                          <div key={act.id} className="border-t border-gray-100 pt-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-gray-500 w-11 flex-shrink-0 tabular-nums">
                                {(act.time || "").split("-")[0].trim()}
                              </span>
                              <span className="text-sm font-semibold text-gray-800 truncate">{act.title}</span>
                            </div>
                            <div className="pl-[3.25rem]">{its.map(renderRegel)}</div>
                          </div>
                        ))}
                        {overig.length > 0 && (
                          <div className="border-t border-gray-100">
                            {metOv.length > 0 && <p className="text-xs font-semibold text-gray-500 pt-2">Algemeen</p>}
                            {overig.map(renderRegel)}
                          </div>
                        )}
                        {volledig && !itemsAfd.length && (
                          <p className="text-xs text-gray-500 py-2 border-t border-gray-100">Nog geen overdracht</p>
                        )}
                        {zonder.length > 0 && (
                          <p className="text-xs text-gray-500 py-2 border-t border-gray-100">
                            Zonder overdracht: {zonder.map((a) => a.title).join(", ")}
                          </p>
                        )}
                        {magSchrijven && (
                          <button
                            onClick={() => schrijfVoor(afd)}
                            className="mt-1 mb-1 w-full min-h-[40px] rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 border bg-white active:scale-[0.98]"
                            style={{ borderColor: `${C.Pine}55`, color: C.Pine }}
                          >
                            <Edit2 size={14} /> Overdracht {info.label} schrijven
                          </button>
                        )}
                      </div>
                    );
                  });
                };
                // ── Samenvatting van de getoonde dag ──
                const renderSamenvatting = (dk, alleDag) => {
                  if (!alleDag.length) return null;
                  const acts = actsVan(dk);
                  const metTekst = alleDag.filter((it) => !it.nietsBijzonders && it.kind !== "door");
                  const belangrijk = metTekst.filter((it) => it.important);
                  const crowds = alleDag.map((it) => it.crowd).filter((x) => x >= 1 && x <= 5);
                  const drukte = crowds.length ? Math.round(crowds.reduce((t, x) => t + x, 0) / crowds.length) : 0;
                  const eerste = (it) => String(it.text || "").split("\n")[0].slice(0, 110);
                  if (!belangrijk.length && !drukte) return null;
                  return (
                    <div className="rounded-2xl px-4 py-3" style={{ backgroundColor: C.PineLight, color: C.Pine }}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-semibold uppercase tracking-wide flex-1">Belangrijk die dag</span>
                        {drukte > 0 && <DbCrowdChip lvl={drukte} />}
                      </div>
                      {belangrijk.length > 0 ? (
                        <ul className="mt-1.5 space-y-0.5">
                          {belangrijk.slice(0, 5).map((it) => (
                            <li key={it._readKey} className="text-sm flex gap-1.5">
                              <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" style={{ color: C.Bark }} />
                              <span>
                                <span className="font-semibold">{it._title || "Algemeen"}:</span> {eerste(it)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm mt-1">
                          {acts.length} activiteit{acts.length === 1 ? "" : "en"}, niets dringends gemeld.
                        </p>
                      )}
                    </div>
                  );
                };
                // ── Weekbalk: ma t/m zo van de week van de gekozen dag ──
                const wd = (parseDateKey(toonDag).getDay() + 6) % 7;
                const weekStart = shiftKey(toonDag, -wd);
                const weekDagen = Array.from({ length: 7 }, (_, i) => shiftKey(weekStart, i));
                const telDag = (dk) => itemsVanDag(dk).filter((it) => !it.nietsBijzonders).length;
                const dagN = telDag(toonDag);
                const dagBez = dbBezetting(appData, toonDag);
                const iemandGewerkt = Object.values(dagBez).some((l) => l.length);
                const relatief =
                  toonDag === todayKey ? "Vandaag" : toonDag === shiftKey(todayKey, -1) ? "Gisteren" : null;
                const volledigeDatum = hoofdletter(
                  parseDateKey(toonDag).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" })
                );
                const weekNr = (() => {
                  const d = parseDateKey(toonDag);
                  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
                  t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7));
                  const j1 = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
                  return Math.ceil(((t - j1) / 86400000 + 1) / 7);
                })();
                return (
                  <>
                    <div className="order-first bg-white rounded-2xl border border-gray-100 shadow-sm p-2" data-noswipe>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setOvdDag(shiftKey(toonDag, -1))}
                          className="w-11 h-11 rounded-xl flex items-center justify-center active:bg-gray-50 flex-shrink-0"
                          aria-label="Vorige dag"
                        >
                          <ChevronLeft size={20} style={{ color: C.Pine }} />
                        </button>
                        <label className="relative flex-1 min-w-0 min-h-[52px] flex flex-col items-center justify-center cursor-pointer">
                          <span className="flex items-center gap-1.5 min-w-0">
                            {relatief && (
                              <span
                                className="text-[11px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: C.Pine, color: "white" }}
                              >
                                {relatief}
                              </span>
                            )}
                            <span className="text-base font-semibold truncate" style={{ color: C.Lagoon }}>
                              {volledigeDatum}
                            </span>
                          </span>
                          <span className="text-xs text-gray-500 tabular-nums">
                            {dagN ? `${dagN} overdracht${dagN === 1 ? "" : "en"}` : "Nog geen overdrachten"} · week {weekNr}
                          </span>
                          <input
                            type="date"
                            max={todayKey}
                            value={toonDag}
                            onChange={(e) => e.target.value && setOvdDag(e.target.value)}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            aria-label="Kies een dag"
                          />
                        </label>
                        <button
                          onClick={() => setOvdDag(shiftKey(toonDag, 1))}
                          disabled={toonDag >= todayKey}
                          className="w-11 h-11 rounded-xl flex items-center justify-center active:bg-gray-50 flex-shrink-0 disabled:opacity-30"
                          aria-label="Volgende dag"
                        >
                          <ChevronRight size={20} style={{ color: C.Pine }} />
                        </button>
                      </div>
                      <div className="grid grid-cols-7 gap-1 pt-1.5">
                        {weekDagen.map((dk) => {
                          const aan = dk === toonDag;
                          const toekomst = dk > todayKey;
                          const n = toekomst ? 0 : telDag(dk);
                          const d = parseDateKey(dk);
                          return (
                            <button
                              key={dk}
                              onClick={() => !toekomst && setOvdDag(dk)}
                              disabled={toekomst}
                              className="h-14 rounded-xl flex flex-col items-center justify-center active:scale-95 disabled:opacity-30"
                              style={
                                aan
                                  ? { backgroundColor: C.Pine, color: "white" }
                                  : dk === todayKey
                                  ? { boxShadow: `inset 0 0 0 2px ${C.Pine}`, color: C.Pine }
                                  : { color: n ? C.Lagoon : "#9CA3AF" }
                              }
                              aria-label={`${hoofdletter(d.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" }))}: ${n} overdracht${n === 1 ? "" : "en"}`}
                              aria-pressed={aan}
                            >
                              <span className="text-[10px] font-semibold uppercase">
                                {d.toLocaleDateString("nl-NL", { weekday: "short" }).slice(0, 2)}
                              </span>
                              <span className="text-sm font-semibold tabular-nums">{d.getDate()}</span>
                              <span
                                className="w-1.5 h-1.5 rounded-full mt-0.5"
                                style={{ backgroundColor: n ? (aan ? "white" : C.Pine) : "transparent" }}
                              />
                            </button>
                          );
                        })}
                      </div>
                      {toonDag !== todayKey && (
                        <button
                          onClick={() => setOvdDag(todayKey)}
                          className="mt-1.5 w-full min-h-[36px] rounded-xl text-xs font-semibold flex items-center justify-center gap-1"
                          style={{ backgroundColor: C.PineLight, color: C.Pine }}
                        >
                          Terug naar vandaag
                        </button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {renderSamenvatting(toonDag, dagItems)}
                      {renderAfdKaarten(toonDag, dagItems, true)}
                      {!dagItems.length && !iemandGewerkt && (
                        <EmptyState
                          icon={IcChat}
                          message="Geen overdrachten op deze dag."
                          sub={toonDag === todayKey ? "Iets bijzonders gezien? Zet het erin voor de volgende dienst." : "Kies een andere dag in de balk hierboven."}
                        />
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
          {sideTab === "actiepunten" && (
            <>
          {/* Actiepunten */}
          <DbCard
            icon={AlertTriangle}
            accent={C.Bark}
            title="Actiepunten"
            subtitle={
              openPunten.length > 0
                ? `${openPunten.length} nog op te lossen`
                : "Belangrijke lopende zaken, bv. iets gemeld bij TD"
            }
            right={
              !newPunt && (
                <button
                  onClick={() =>
                    setNewPunt({ title: "", afdeling: afdeling, assignee: "", note: "" })
                  }
                  className="text-xs font-semibold px-2.5 py-1.5 rounded-lg flex items-center gap-1"
                  style={{ backgroundColor: `${C.Pine}12`, color: C.Pine }}
                >
                  <Plus size={13} /> Toevoegen
                </button>
              )
            }
          >
            {newPunt && (
              <div className="rounded-xl border border-gray-200 p-3 mb-3 space-y-2 bg-gray-50">
                <input
                  autoFocus
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#045E51] bg-white"
                  placeholder="Wat loopt er? Bv. lekkage podium gemeld bij TD"
                  value={newPunt.title}
                  onChange={(e) => setNewPunt({ ...newPunt, title: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && createPunt()}
                />
                <div className="grid grid-cols-2 gap-2">
                  <StyledSelect
                    className="border border-gray-200 rounded-lg text-xs px-2 py-1.5 bg-white"
                    value={newPunt.afdeling}
                    onChange={(e) => setNewPunt({ ...newPunt, afdeling: e.target.value })}
                  >
                    {DB_AFDELINGEN.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.id === "alle" ? "Hele team" : a.label}
                      </option>
                    ))}
                  </StyledSelect>
                  <StyledSelect
                    className="border border-gray-200 rounded-lg text-xs px-2 py-1.5 bg-white"
                    value={newPunt.assignee}
                    onChange={(e) => setNewPunt({ ...newPunt, assignee: e.target.value })}
                  >
                    <option value="">Niemand toewijzen</option>
                    <option value="Maik">Maik</option>
                    {staffList
                      .filter((s) => !s.inactive && !teamVerborgen(s) && s.name !== "Maik")
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((s) => (
                        <option key={s.name} value={s.name}>
                          {s.name}
                        </option>
                      ))}
                  </StyledSelect>
                </div>
                <MentionTextarea
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#045E51] resize-none bg-white h-16"
                  placeholder="Toelichting (optioneel)"
                  value={newPunt.note}
                  onChange={(e) => setNewPunt({ ...newPunt, note: e.target.value })}
                  staffList={staffList}
                  loggedInUserName={me}
                />
                <div className="flex gap-2">
                  <button
                    onClick={createPunt}
                    className="flex-1 py-1.5 rounded-lg text-sm font-semibold text-white"
                    style={{ backgroundColor: C.Pine, opacity: newPunt.title.trim() ? 1 : 0.5 }}
                  >
                    Toevoegen
                  </button>
                  <button
                    onClick={() => setNewPunt(null)}
                    className="px-3 py-1.5 rounded-lg text-sm text-gray-500 bg-white border border-gray-200"
                  >
                    Annuleren
                  </button>
                </div>
              </div>
            )}
            {openPunten.length === 0 && !newPunt && (
              <p className="text-xs text-gray-500 py-1">
                Alles is geregeld.
              </p>
            )}
            {openPunten.map((p) => (
              <DbPuntItem
                key={p.id}
                punt={p}
                staffList={staffList}
                loggedInUserName={me}
                userRole={userRole}
                onReact={(t, imp) => reactPunt(p, t, imp)} onReadReactie={(r) => readReactie(p, r)} onEditPunt={(patch) => editPunt(p, patch)} onEditReactie={(r, t) => editReactie(p, r, t)}
                onResolve={() => resolvePunt(p)}
                onReopen={() => reopenPunt(p)}
                onAssign={(n) => assignPunt(p, n)}
                onDelete={() => deletePunt(p)}
              />
            ))}
            {resolvedPunten.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-100">
                <button
                  onClick={() => setShowResolved((s) => !s)}
                  className="text-xs font-semibold text-gray-500 hover:text-gray-500 flex items-center gap-1"
                >
                  {showResolved ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  Opgelost, laatste 2 weken ({resolvedPunten.length})
                </button>
                {showResolved &&
                  resolvedPunten.map((p) => (
                    <DbPuntItem
                      key={p.id}
                      punt={p}
                      staffList={staffList}
                      loggedInUserName={me}
                      userRole={userRole}
                      onReact={(t, imp) => reactPunt(p, t, imp)} onReadReactie={(r) => readReactie(p, r)} onEditPunt={(patch) => editPunt(p, patch)} onEditReactie={(r, t) => editReactie(p, r, t)}
                      onResolve={() => resolvePunt(p)}
                      onReopen={() => reopenPunt(p)}
                      onAssign={(n) => assignPunt(p, n)}
                      onDelete={() => deletePunt(p)}
                    />
                  ))}
              </div>
            )}
          </DbCard>

            </>
          )}
          {sideTab === "aanwezigheid" && isFM && (
            <>
          {/* Aanwezigheid (FM) */}
          {isFM && (presenceNames.length > 0 || showWeek) && (
            <DbCard
              icon={Users}
              title="Aanwezigheid"
              subtitle="Dienst gestart en afgesloten, tegenover het rooster"
              right={
                <button
                  onClick={() => setShowWeek((v) => !v)}
                  className="text-xs font-semibold px-2.5 py-1.5 rounded-lg"
                  style={{ backgroundColor: `${C.Pine}12`, color: C.Pine }}
                >
                  {showWeek ? "Dag" : "Week"}
                </button>
              }
            >
              {!showWeek ? (
                <div className="divide-y divide-gray-100">
                  {presenceNames.map((name) => {
                    const r = presenceRow(name);
                    const editing = editClock?.name === name;
                    return (
                      <div key={name} className="py-2">
                        <div className="flex items-center gap-2">
                          <ProfileAvatar name={name} staffList={staffList} size={24} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate">
                              {name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {r.pl?.start ? `Gepland ${r.pl.start}–${r.pl.end}` : "Niet ingeroosterd"}
                            </p>
                          </div>
                          {!editing && (
                            <button
                              onClick={() =>
                                setEditClock({ name, in: dbHM(r.c.in), out: dbHM(r.c.out) })
                              }
                              className="text-right"
                              aria-label="Tijden aanpassen" title="Tijden aanpassen"
                            >
                              <p className="text-sm font-semibold tabular-nums">
                                <span style={{ color: r.late ? C.Sunset : r.c.in ? C.Pine : "#6B7280" }}>
                                  {r.c.in ? dbHM(r.c.in) : "--:--"}
                                </span>
                                <span className="text-gray-500"> – </span>
                                <span style={{ color: r.early ? C.Bark : r.c.out ? C.Pine : "#6B7280" }}>
                                  {r.c.out ? dbHM(r.c.out) : "--:--"}
                                </span>
                              </p>
                              <p className="text-xs text-gray-500">
                                {r.missing
                                  ? "uren nog niet ingevuld"
                                  : r.notOut
                                  ? "alleen starttijd"
                                  : r.worked
                                  ? `${dbUren(r.worked.min)} gewerkt${
                                      r.worked.rounded ? ` (telt vanaf ${r.worked.startTxt})` : ""
                                    }${r.worked.pauze ? ` · excl. ${dbMinLabel(r.worked.pauze)} pauze` : ""}`
                                  : ""}
                              </p>
                              {r.c.in && (r.c.inLoc?.onbekend || r.c.inLoc?.m != null) && (
                                <p className="text-xs" style={{ color: r.c.inLoc?.onbekend ? C.Bark : "#6B7280" }}>
                                  📍 {r.c.inLoc?.onbekend
                                    ? r.c.inLoc.reden === "geweigerd"
                                      ? "locatie geweigerd op de telefoon"
                                      : r.c.inLoc.reden === "geen-gps"
                                      ? "telefoon gaf geen locatie"
                                      : "locatie kon niet bepaald worden"
                                    : r.c.inLoc.ok
                                    ? `op het park (±${r.c.inLoc.acc || 0} m)`
                                    : `op ${r.c.inLoc.m >= 1000 ? `${(r.c.inLoc.m / 1000).toFixed(1).replace(".", ",")} km` : `${r.c.inLoc.m} m`} van het park`}
                                </p>
                              )}
                              {!r.c.in && r.c.locPoging && !r.c.locPoging.ok && (
                                <p className="text-xs" style={{ color: C.Sunset }}>
                                  📍 starten geweigerd {dbHM(new Date(r.c.locPoging.at).toISOString())}
                                  {r.c.locPoging.reden === "geweigerd"
                                    ? " · locatie niet toegestaan"
                                    : r.c.locPoging.m != null
                                    ? ` · ${r.c.locPoging.m} m (±${r.c.locPoging.acc} m)`
                                    : ""}
                                </p>
                              )}
                              {(r.late || r.early || r.c.correctedBy) && (
                                <p className="text-xs font-semibold">
                                  {r.late && (
                                    <span style={{ color: C.Sunset }}>
                                      {dbMinLabel(r.lateMin)} te laat
                                    </span>
                                  )}
                                  {r.late && r.early && <span className="text-gray-500"> · </span>}
                                  {r.early && (
                                    <span style={{ color: C.Bark }}>
                                      {dbMinLabel(r.earlyMin)} eerder weg
                                    </span>
                                  )}
                                  {r.c.correctedBy && (
                                    <span className="text-gray-500 font-normal">
                                      {r.late || r.early ? " · " : ""}aangepast door {r.c.correctedBy}
                                    </span>
                                  )}
                                </p>
                              )}
                            </button>
                          )}
                        </div>
                        {editing && (
                          <div className="flex items-center gap-2 mt-2 pl-8 flex-wrap">
                            <input
                              type="time"
                              className="border border-gray-200 rounded-lg px-2 py-1 text-sm"
                              value={editClock.in}
                              onChange={(e) => setEditClock({ ...editClock, in: e.target.value })}
                            />
                            <span className="text-gray-500">–</span>
                            <input
                              type="time"
                              className="border border-gray-200 rounded-lg px-2 py-1 text-sm"
                              value={editClock.out}
                              onChange={(e) => setEditClock({ ...editClock, out: e.target.value })}
                            />
                            <button
                              onClick={saveClockEdit}
                              className="px-2.5 py-1 rounded-lg text-xs font-semibold text-white"
                              style={{ backgroundColor: C.Pine }}
                            >
                              Opslaan
                            </button>
                            {r.c.in && (
                              <button
                                onClick={() => clearClock(name)}
                                className="px-2.5 py-1 rounded-lg text-xs font-semibold flex items-center gap-1"
                                style={{ backgroundColor: C.BlossomLight, color: C.Sunset }}
                                aria-label="Per ongeluk gestart? Tijden wissen" title="Per ongeluk gestart? Tijden wissen"
                              >
                                <Trash2 size={12} /> Wissen
                              </button>
                            )}
                            <button aria-label="Sluiten"
                              onClick={() => setEditClock(null)}
                              className="text-gray-500"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="overflow-x-auto -mx-1">
                  <table className="w-full text-xs tabular-nums">
                    <thead>
                      <tr className="text-gray-500">
                        <th className="text-left font-semibold py-1 px-1">Naam</th>
                        {weekDays.map((d) => (
                          <th key={d.toISOString()} className="font-semibold py-1 px-1 text-center whitespace-nowrap">
                            {d.toLocaleDateString("nl-NL", { weekday: "short", day: "numeric" })}
                          </th>
                        ))}
                        <th className="font-semibold py-1 px-1 text-right">Totaal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ...new Set(
                          weekDays.flatMap((d) =>
                            Object.entries(appData.timeClock?.[toIsoDate(d)] || {})
                              .filter(([, c]) => c?.in)
                              .map(([n]) => n)
                          )
                        ),
                      ]
                        .sort((a, b) => a.localeCompare(b))
                        .map((name) => {
                          let weekMin = 0;
                          const cells = weekDays.map((d) => {
                            const dk = toIsoDate(d);
                            const c = appData.timeClock?.[dk]?.[name];
                            const w = c?.in && c?.out ? dbWorked(c, dbPlanned(appData, dk, name)) : null;
                            if (w) weekMin += w.min;
                            return (
                              <td key={d.toISOString()} className="py-1.5 px-1 text-center whitespace-nowrap text-gray-500">
                                {c?.in ? (
                                  <>
                                    {dbHM(c.in)}
                                    <br />
                                    {c.out ? dbHM(c.out) : "…"}
                                    {w && (
                                      <>
                                        <br />
                                        <span className="font-semibold" style={{ color: C.Pine }}>
                                          {(w.min / 60).toLocaleString("nl-NL", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}
                                        </span>
                                      </>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-gray-500">–</span>
                                )}
                              </td>
                            );
                          });
                          return (
                            <tr key={name} className="border-t border-gray-100">
                              <td className="py-1.5 px-1 font-semibold text-gray-800 whitespace-nowrap">
                                {name}
                              </td>
                              {cells}
                              <td className="py-1.5 px-1 text-right font-semibold whitespace-nowrap" style={{ color: C.Lagoon }}>
                                {(weekMin / 60).toLocaleString("nl-NL", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </DbCard>
          )}

            </>
          )}
        </div>
      </div>

      {/* Ongedaan maken */}
      {undo && (
        <div
          role="status"
          aria-live="polite"
          className="fixed left-1/2 -translate-x-1/2 bottom-[176px] lg:bottom-8 z-[70] flex items-center gap-3 pl-4 pr-2 h-12 rounded-2xl shadow-lg max-w-[92vw]"
          style={{ backgroundColor: C.Lagoon, color: "white" }}
          data-noswipe
        >
          <span className="text-sm truncate">{undo.label}</span>
          <button
            onClick={() => {
              const u = undo;
              setUndo(null);
              u.fn && u.fn();
            }}
            className="h-9 px-3 rounded-xl text-sm font-semibold flex-shrink-0"
            style={{ color: C.Honey }}
          >
            {undo.actie || "Ongedaan maken"}
          </button>
        </div>
      )}
      {sheet && (
        <div
          className="lg:hidden fixed inset-0 z-[80] flex items-end"
          style={{ background: "rgba(0,25,47,0.45)" }}
          onClick={() => setSheet(null)}
        >
          <div
            className="w-full bg-white rounded-t-3xl p-4 pb-8 max-h-[88vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-noswipe
          >
            <div className="w-10 h-1.5 rounded-full bg-gray-200 mx-auto mb-3" />
            {sheet === "menu" && (
              <div className="space-y-2">
                {[
                  { k: "overdracht", icon: MessageCircle, label: "Overdracht schrijven", sub: "Algemeen of bij een activiteit", bg: C.PineLight, fg: C.Pine },
                  { k: "actiepunt", icon: AlertTriangle, label: "Actiepunt toevoegen", sub: "Iets dat opgelost moet worden", bg: C.HoneyLight, fg: C.Bark },
                  ...(isFM ? [{ k: "taak", icon: CheckSquare, label: "Extra taak", sub: "Blijft staan tot hij af is", bg: C.SkyLight, fg: C.Lagoon }] : []),
                  { k: "chat", icon: MessageSquare, label: "Chat", sub: chatUnreadCount ? `${chatUnreadCount} ongelezen` : "Naar de teamchat", bg: C.BlossomLight, fg: C.Sunset },
                ].map((o) => {
                  const Icon = o.icon;
                  return (
                    <button
                      key={o.k}
                      onClick={() => {
                        if (o.k === "overdracht") {
                          setComposerAct(null);
                          setSheet("overdracht");
                        } else if (o.k === "actiepunt") {
                          setSheet(null);
                          setTab("actiepunten");
                          setNewPunt({ title: "", afdeling: ingeplandOp || "alle", assignee: "", note: "" });
                        } else if (o.k === "taak") {
                          setSheet("taak");
                        } else if (o.k === "chat") {
                          setSheet(null);
                          onOpenChat && onOpenChat();
                        }
                      }}
                      className="w-full min-h-[60px] rounded-2xl px-3 flex items-center gap-3 text-left"
                      style={{ backgroundColor: o.bg }}
                    >
                      <span className="w-10 h-10 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
                        <Icon size={19} style={{ color: o.fg }} />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold" style={{ color: C.Lagoon }}>{o.label}</span>
                        <span className="block text-xs" style={{ color: o.fg }}>{o.sub}</span>
                      </span>
                      <ChevronRight size={18} style={{ color: o.fg }} />
                    </button>
                  );
                })}
              </div>
            )}
            {sheet === "overdracht" && (
              <>
                <p className="text-base font-semibold mb-2" style={{ color: C.Lagoon }}>Overdracht schrijven</p>
                <DbComposer
                  key={`ovdm_${composerAfd}`}
                  defaultAfd={composerAfd}
                  appData={appData}
                  activities={activities}
                  actId={composerAct}
                  setActId={setComposerAct}
                  staffList={staffList}
                  me={me}
                  werkendeNamen={[...workingNames]}
                  isFM={isFM}
                  dateKey={dateKey}
                  onPost={(d) => {
                    postOverdracht(d);
                    setComposerAfd("alle");
                    setSheet(null);
                    setTab("overdracht");
                  }}
                />
              </>
            )}
            {sheet === "taak" && (
              <div className="space-y-3">
                <p className="text-base font-semibold" style={{ color: C.Lagoon }}>Extra taak</p>
                <input
                  autoFocus
                  className="w-full h-12 border border-gray-200 rounded-xl px-3 text-sm outline-none focus:ring-2 focus:ring-[#045E51]/40"
                  placeholder="Bv. kast in de Crea Club opruimen"
                  value={taakTekst}
                  onChange={(e) => setTaakTekst(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addExtraTaak()}
                />
                <button
                  onClick={addExtraTaak}
                  className="w-full h-12 rounded-xl text-sm font-semibold text-white"
                  style={{ backgroundColor: C.Pine, opacity: taakTekst.trim() ? 1 : 0.5 }}
                >
                  Toevoegen
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AISummaryWidget = ({
    currentDay,
    loggedInUserName,
    appData,
    todaysSchedule,
    entTasks,
    creaTasks,
    extraTasks,
    currentTheme,
  }) => {
    const [summary, setSummary] = useState("");

    useEffect(() => {
      const generate = () => {
        try {
          // ✅ FIX: Gebruik toIsoDate in plaats van .toISOString()
          const dateKey = toIsoDate(currentDay);

          // Check of het vandaag, verleden of toekomst is
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          const selectedDate = new Date(currentDay);
          selectedDate.setHours(0, 0, 0, 0);

          const isToday = selectedDate.getTime() === now.getTime();
          const isPast = selectedDate < now;
          const isFuture = selectedDate > now;

          // 1. BEZETTING
          const stats = appData.dailyStats?.[dateKey] || {
            total: 0,
            adults: 0,
            kids: 0,
            babies: 0,
          };
          const totalGuests =
            stats.total > 0
              ? stats.total
              : (stats.adults || 0) + (stats.kids || 0) + (stats.babies || 0);

          // 2. TEAM DIE DAG — workSchedule.matchedUser is de waarheid,
          // zodat elke wijziging zichtbaar is (geen filter op roosterDiensten).
          const todaysShiftsRaw = appData.workSchedule?.[dateKey] || [];
          const todaysShiftsFiltered = todaysShiftsRaw.filter(
            (shift) => shift.matchedUser
          );
          const allStaffThatDay = new Set();
          todaysShiftsFiltered.forEach((shift) => {
            allStaffThatDay.add(shift.matchedUser);
          });

          const isFmPresent = appData.dailyFmPresent?.[dateKey] || false;
          if (isFmPresent && !allStaffThatDay.has("Maik")) {
            allStaffThatDay.add("Maik");
          }

          const dvName = appData.dailyDv?.[dateKey] || null;
          const staffArray = Array.from(allStaffThatDay).sort();

          // 3. MIJN ACTIVITEITEN
          const myActs = (todaysSchedule || [])
            .filter((s) => s.staff?.includes(loggedInUserName))
            .sort((a, b) => a.time.localeCompare(b.time));

          // 4. MIJN KARAKTERS
          const myChars = (appData.characters || []).filter(
            (c) => c.date === dateKey && c.actor === loggedInUserName
          );

          // 5. SPRINGKUSSEN CHECK
          const springData = appData.dailySpringkussen?.[dateKey];
          const onSpringkussen =
            springData?.open && springData?.staff?.includes(loggedInUserName);

          // 6. BEPAAL AFDELINGEN (zonder springkussen in entertainment)
          const myDepartments = new Set();
          myActs.forEach((act) => {
            if (act.type === "entertainment")
              myDepartments.add("entertainment");
            if (act.type === "crea") myDepartments.add("crea");
            if (act.type === "orry") myDepartments.add("entertainment");
          });

          // 7. TEL RELEVANTE TAKEN
          let openTasks = 0;

          // Entertainment taken (NIET als je alleen springkussen doet)
          if (myDepartments.has("entertainment") && !onSpringkussen) {
            openTasks += (entTasks || []).filter((t) => !t.done).length;
          }

          // Crea taken
          if (myDepartments.has("crea")) {
            openTasks += (creaTasks || []).filter((t) => !t.done).length;
          }

          // Springkussen taken (ALLEEN als je bij springkussen staat)
          if (onSpringkussen) {
            // Alle springkussen taken komen uit checklistTemplates
            const weekdayId = currentDay.getDay();
            const templateSpringTasks =
              appData.checklistTemplates?.[weekdayId]?.springkussen || [];
            const checklistSpringIds = (appData.checklists || [])
              .filter((c) => c.type === "springkussen" && c.date === dateKey)
              .map((c) => c.id);
            const openSpringTemplates = templateSpringTasks.filter(
              (t) => !checklistSpringIds.includes(t.id)
            ).length;

            openTasks += openSpringTemplates;
          }

          // Extra taken tellen altijd mee
          openTasks += (extraTasks || []).filter((t) => !t.done).length;

          // 8. CHECK OF MEDEWERKER INGEPLAND IS
          const isScheduled =
            myActs.length > 0 || myChars.length > 0 || onSpringkussen;
          const hasShift = todaysShiftsFiltered.some(
            (s) => s.matchedUser === loggedInUserName
          );

          let text = `Hey ${getFirstName(loggedInUserName)}! `;

          // VRIJE DAG — geen activiteiten, geen shift
          if (!isScheduled && !hasShift) {
            if (isPast) {
              setSummary({
                text: `Hey ${getFirstName(
                  loggedInUserName
                )}! Je had deze dag vrij. 😊`,
                data: {},
              });
            } else if (isToday) {
              setSummary({
                text: `Hey ${getFirstName(
                  loggedInUserName
                )}! Je hebt vandaag vrij — geniet ervan! 🌿`,
                data: {},
              });
            } else {
              setSummary({
                text: `Hey ${getFirstName(
                  loggedInUserName
                )}! Je bent deze dag vrij. 😊`,
                data: {},
              });
            }
            return;
          }

          // INGEPLAND — contextverhaaltje (programma staat al in de tijdlijn)
          // Shift + verantwoordelijke
          // Dienst/tijd staat al in "Mijn dag" — niet herhalen in de briefing.
          const isUserDV = dvName === loggedInUserName;
          const isUserFM = loggedInUserName === "Maik" && isFmPresent;
          if (isUserFM || isUserDV) {
            text += isPast
              ? `Je was verantwoordelijke. `
              : `Je bent vandaag verantwoordelijke. `;
          } else if (dvName || isFmPresent) {
            const v = isFmPresent ? "Maik (FM)" : dvName;
            text += isPast
              ? `**${v}** was verantwoordelijk. `
              : `**${v}** is verantwoordelijk. `;
          }

          // Team
          if (staffArray.length > 0) {
            const others = staffArray.filter((n) => n !== loggedInUserName);
            if (others.length > 0) {
              text += isPast
                ? `Je werkte samen met **${others.join(", ")}**. `
                : `Je werkt vandaag samen met **${others.join(", ")}**. `;
            }
          }

          // Gasten + nationaliteiten
          if (totalGuests > 0) {
            const kids = stats.kids || 0;
            const kidsText = kids > 0 ? `, waarvan **${kids} kinderen**` : "";
            text += isPast
              ? `Er waren **${totalGuests} gasten**${kidsText}. `
              : `Er zijn vandaag **${totalGuests} gasten**${kidsText}. `;

            const breakdown = stats.breakdown || {};
            const topNats = Object.entries(breakdown)
              .filter(([, v]) => v > 0)
              .sort(([, a], [, b]) => b - a)
              .slice(0, 3)
              .map(([k, v]) => {
                const nat = NATIONALITIES.find((n) => n.k === k);
                const pct = Math.round((v / totalGuests) * 100);
                return nat ? `${nat.i} ${nat.l} (${pct}%)` : null;
              })
              .filter(Boolean);
            if (topNats.length > 0) {
              text += `Grootste groepen: ${topNats.join(", ")}. `;
            }
          }

          // Taken (alleen vandaag/toekomst)
          if (!isPast && isScheduled) {
            if (openTasks > 0) {
              text += `Nog **${openTasks} ${
                openTasks === 1 ? "taak" : "taken"
              }** open. Werk ze! 💪`;
            } else {
              text += `Alle taken zijn af! 🎉`;
            }
          }

          // Bewaar gestructureerde data voor visuele chips
          const summaryData = {
            totalGuests,
            kids: stats.kids || 0,
            topNats:
              totalGuests > 0
                ? Object.entries(stats.breakdown || {})
                    .filter(([, v]) => v > 0)
                    .sort(([, a], [, b]) => b - a)
                    .map(([k, v]) => {
                      const nat = NATIONALITIES.find((n) => n.k === k);
                      const pct = Math.round((v / totalGuests) * 100);
                      return nat
                        ? { flag: nat.i, label: nat.l, pct, count: v }
                        : null;
                    })
                    .filter(Boolean)
                : [],
            openTasks,
            teamSize: staffArray.length,
          };

          setSummary({ text, data: summaryData });
        } catch (err) {
          console.error("Summary error:", err);
          setSummary({
            text: `Hey ${loggedInUserName}! Veel succes vandaag! 💪`,
            data: {},
          });
        }
      };
      generate();
    }, [
      currentDay,
      loggedInUserName,
      JSON.stringify(todaysSchedule?.map((a) => a.id + a.title)),
      JSON.stringify(entTasks?.map((t) => t.id + t.done)),
      JSON.stringify(creaTasks?.map((t) => t.id + t.done)),
      JSON.stringify(extraTasks?.map((t) => t.id + t.done)),
      appData.characters,
      appData.dailyStats,
      appData.dailySpringkussen,
      appData.springkussenTasks,
      appData.checklists,
      appData.dailyFmPresent,
      appData.dailyDv,
      appData.checklistTemplates,
      appData.workSchedule,
      appData.roosterDiensten,
    ]);

    if (!summary) return null;

    const { text, data = {} } = summary;
    const primary = C.Pine; // dagbord-stijl: vaste merkkleur
    const logoColor = CP_LOGO_COLORS[currentTheme?.id] || "#b4d6a1";

    return (
      <>
        {/* Widget header — zelfde stijl als andere widgets */}
        <div className="px-4 py-3 flex items-center gap-2.5 border-b border-gray-100">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${primary}1A` }}
          >
            <Sparkles size={18} style={{ color: primary }} />
          </div>
          <h3 className="text-sm font-semibold" style={{ color: C.Lagoon }}>
            Dagbriefing
          </h3>
        </div>
        <div className="px-4 pt-3 pb-3 flex flex-col gap-3">
          {/* Verhaaltje */}
          <p
            className="text-sm text-gray-800 leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>"),
            }}
          />

          {/* Stat-chips — alleen als er data is */}
          {(data.totalGuests > 0 || data.openTasks > 0) && (
            <div className="space-y-2">
              {/* Gasten */}
              {data.totalGuests > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <div
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold"
                    style={{ backgroundColor: `${primary}14`, color: primary }}
                  >
                    <Users size={13} />
                    {data.totalGuests} gasten
                  </div>
                  {data.kids > 0 && (
                    <div
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold"
                      style={{
                        backgroundColor: `${C.Blossom}18`,
                        color: C.Sunset,
                      }}
                    >
                      <Baby size={13} />
                      {data.kids} kinderen
                    </div>
                  )}
                  {data.openTasks > 0 && (
                    <div
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold"
                      style={{ backgroundColor: `${C.Honey}30`, color: C.Bark }}
                    >
                      <ClipboardList size={13} />
                      {data.openTasks} taken
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </>
    );
  };

function AppInner() {
  useEffect(() => {
    // Tailwind wordt nu bij het bouwen meegeleverd (src/tailwind.css): geen
    // extra script van een CDN meer en geen CSS-generatie in de browser.
    if (!document.getElementById("cp-global-styles")) {
      const style = document.createElement("style");
      style.id = "cp-global-styles";
      style.textContent = `
    @font-face {
      font-family: 'Bagos';
      src: url('/fonts/bagos-regular.woff2') format('woff2'), url('/fonts/bagos-regular.ttf') format('truetype');
      font-weight: 400;
      font-style: normal;
      font-display: swap;
    }
    
    @font-face {
      font-family: 'Bagos';
      src: url('/fonts/bagos-medium.woff2') format('woff2'), url('/fonts/bagos-medium.ttf') format('truetype');
      font-weight: 500;
      font-style: normal;
      font-display: swap;
    }
    
    @font-face {
      font-family: 'Bagos';
      src: url('/fonts/bagos-medium.woff2') format('woff2'), url('/fonts/bagos-medium.ttf') format('truetype');
      font-weight: 600;
      font-style: normal;
      font-display: swap;
    }
    
    @font-face {
      font-family: 'Bagos';
      src: url('/fonts/bagos-medium.woff2') format('woff2'), url('/fonts/bagos-medium.ttf') format('truetype');
      font-weight: 700;
      font-style: normal;
      font-display: swap;
    }
    
    @font-face {
      font-family: 'Bagos';
      src: url('/fonts/bagos-medium.woff2') format('woff2'), url('/fonts/bagos-medium.ttf') format('truetype');
      font-weight: 800;
      font-style: normal;
      font-display: swap;
    }
    
    @font-face {
      font-family: 'Bagos';
      src: url('/fonts/bagos-medium.woff2') format('woff2'), url('/fonts/bagos-medium.ttf') format('truetype');
      font-weight: 900;
      font-style: normal;
      font-display: swap;
    }
    
    /* Ruimte voor de vaste tabbalk onderin (mobiel) */
    .met-tabbalk { padding-bottom: calc(80px + max(16px, env(safe-area-inset-bottom))); }
    @media (min-width: 768px) { .met-tabbalk { padding-bottom: 0; } }

    /* Toegankelijkheid: duidelijke focusrand voor toetsenbord-gebruikers */
    :focus-visible {
      outline: 3px solid #FFCA55 !important;
      outline-offset: 2px !important;
      border-radius: 6px;
    }
    /* Melding → bericht: kort oplichten */
    @keyframes cpFocusFlits {
      0%, 35% { box-shadow: 0 0 0 3px #FFCA55; background-color: #FFE2A1; }
      100% { box-shadow: 0 0 0 0 rgba(255,202,85,0); background-color: transparent; }
    }
    .cp-focus-flits { animation: cpFocusFlits 2.6s ease-out; border-radius: 12px; }
    @keyframes cpVormZweef { 0%, 100% { transform: rotate(-6deg) scale(1); } 50% { transform: rotate(5deg) scale(1.05); } }
    .cp-vorm-zweef { animation: cpVormZweef 7s ease-in-out infinite; }
    @keyframes cpPaginaIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
    @keyframes cpPaginaVorm { 0% { opacity: .5; transform: translateX(-50%) scale(.2) rotate(-12deg); } 100% { opacity: 0; transform: translateX(-50%) scale(2.4) rotate(6deg); } }
    .cp-pagina-in > *:not(.cp-pagina-vorm-kader) { animation: cpPaginaIn .38s cubic-bezier(.2,.8,.2,1) backwards; }
    /* Het groeiende vormpje mag nooit buiten beeld komen (gaf horizontaal scrollen op iPhone) */
    .cp-pagina-vorm-kader { position: absolute; inset: 0; overflow: hidden; pointer-events: none; }
    /* Geen isolation/z-index hier: dat sluit vensters (position: fixed) op onder de tabbalk. */
    .cp-pagina-vorm { animation: cpPaginaVorm .55s cubic-bezier(.2,.8,.2,1) both; }
    /* Toegankelijkheid: minder beweging als dat in de telefoon is ingesteld */
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
        scroll-behavior: auto !important;
      }
    }

    /* Force Bagos font everywhere - mobile compatible */
    *, *::before, *::after {
      font-family: 'Bagos', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif !important;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      -webkit-text-size-adjust: 100%;
    }
    
    /* Bagoss utility class for explicit usage */
    .font-bagoss {
      font-family: 'Bagos', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif !important;
    }
    
    html {
      font-family: 'Bagos', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif !important;
      -webkit-text-size-adjust: 100%;
      overflow-x: hidden;
      overflow-x: clip;
      width: 100%;
      max-width: 100vw;
    }
    
    body { 
      font-family: 'Bagos', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif !important;
      background-color: #F0FDF4;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      overflow-x: hidden;
      overflow-x: clip;
      width: 100%;
      max-width: 100vw;
      margin: 0;
      padding: 0;
    }
    
    /* Prevent horizontal overflow on all elements */
    * {
      box-sizing: border-box;
    }
    
    /* Force all containers to fit */
    #root, #root > div {
      max-width: 100vw;
      overflow-x: hidden;
      overflow-x: clip;
    }
    
    /* Mobile viewport fix - alleen layout, GEEN font-size overrides */
    @media (max-width: 768px) {
      html, body, #root {
        max-width: 100vw !important;
        overflow-x: hidden !important;
        overflow-x: clip !important;
      }
      body > *, #root > *, main, main > * {
        max-width: 100vw !important;
      }
      .max-w-7xl, .max-w-6xl, .max-w-5xl, .max-w-4xl,
      .max-w-3xl, .max-w-2xl, .max-w-xl, .max-w-lg,
      .max-w-md, .max-w-sm, .max-w-xs {
        max-width: 100vw !important;
        width: 100% !important;
      }
      table { width: 100% !important; }
      th, td { padding: 0.25rem !important; }
      [style*="min-width"] { min-width: 0 !important; }
    }

    /* Extra small iPhones */
    @media (max-width: 390px) {
      *:not(.cp-a4-iframe) { max-width: 100vw !important; }
    }
    
    /* Programma dag-kaarten: vullen hun grid-cel, hoogte volgt de inhoud */
    .program-card-desktop {
      width: 100% !important;
      min-width: 0 !important;
      max-width: 100% !important;
      height: auto !important;
      box-sizing: border-box !important;
      overflow-x: hidden !important;
    }
    .program-card-desktop * {
      max-width: 100% !important;
      word-break: break-word !important;
      overflow-wrap: break-word !important;
    }
    
    /* Dashboard cards - semi-transparent so pattern shows through */
    .cp-dashboard-card {
      background-color: #ffffff !important;
    }
    
        /* Ensure inputs and buttons use Bagos */
    input, button, select, textarea {
      font-family: 'Bagos', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif !important;
    }
    
    /* 🆕 VERBETERDE SCROLLBAR - CP branded */
    .custom-scrollbar::-webkit-scrollbar { 
      width: 6px; 
      height: 6px; 
    } 
    
    .custom-scrollbar::-webkit-scrollbar-track { 
      background: #F3F4F6;
      border-radius: 3px;
    } 
    
    .custom-scrollbar::-webkit-scrollbar-thumb { 
      background: rgba(4, 94, 81, 0.3);
      border-radius: 3px;
      transition: background 0.2s ease;
    }
    
    .custom-scrollbar::-webkit-scrollbar-thumb:hover { 
      background: rgba(4, 94, 81, 0.5);
    }
    
    /* 🆕 PREMIUM GLASSMORPHISM */
    .glass-card {
      background: rgba(255, 255, 255, 0.85);
      backdrop-filter: blur(20px) saturate(180%);
      -webkit-backdrop-filter: blur(20px) saturate(180%);
      border: 1px solid rgba(255, 255, 255, 0.4);
      box-shadow: 
        0 4px 24px -1px rgba(0, 0, 0, 0.06),
        0 2px 8px -1px rgba(0, 0, 0, 0.04),
        inset 0 1px 0 rgba(255, 255, 255, 0.6);
    }
    
    .glass-card-dark {
      background: rgba(4, 94, 81, 0.95);
      backdrop-filter: blur(20px) saturate(180%);
      -webkit-backdrop-filter: blur(20px) saturate(180%);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    /* 🆕 VERBETERDE SHADOWS */
    .shadow-soft {
      box-shadow: 
        0 1px 3px rgba(0, 0, 0, 0.04),
        0 4px 12px rgba(0, 0, 0, 0.03);
    }
    
    .shadow-medium {
      box-shadow: 
        0 2px 4px rgba(0, 0, 0, 0.04),
        0 8px 24px rgba(0, 0, 0, 0.06);
    }
    
    .shadow-elevated {
      box-shadow: 
        0 4px 6px rgba(0, 0, 0, 0.04),
        0 12px 40px rgba(0, 0, 0, 0.08);
    }
    
    /* 🆕 CP-BRANDED HOVER EFFECT */
    .smooth-hover {
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    
    .smooth-hover:hover {
      transform: translateY(-3px);
      box-shadow: 
        0 8px 30px rgba(4, 94, 81, 0.15),
        0 4px 12px rgba(4, 94, 81, 0.08);
    }
    
    /* 🆕 ACTIEVE CARD STATE */
    .card-active {
      border: 2px solid #045E51;
      box-shadow: 0 0 0 4px rgba(4, 94, 81, 0.1);
    }
    
    /* 🆕 Native form-controls in CP-merkkleur (alleen accentkleur,
       sizing/gedrag blijven native — minimaal risico) */
    input[type="checkbox"],
    input[type="radio"] {
      accent-color: #045E51;
      cursor: pointer;
    }
    input[type="checkbox"]:focus-visible,
    input[type="radio"]:focus-visible {
      outline: 2px solid rgba(4, 94, 81, 0.45);
      outline-offset: 1px;
    }
    select:focus-visible {
      outline: 2px solid rgba(4, 94, 81, 0.35);
      outline-offset: 1px;
    }

    /* 🆕 CP-stijl select (via StyledSelect): eigen chevron, geen native pijl */
    .cp-select {
      appearance: none !important;
      -webkit-appearance: none !important;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23045E51' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 0.65rem center;
      background-size: 14px 14px;
      padding-right: 1.9rem !important;
      border-color: rgba(4, 94, 81, 0.25);
    }
    .cp-select::-ms-expand { display: none; }

    /* 🆕 Hover-affordance op rooster dag-cellen (inset overlay i.p.v.
       background, want de cel-bg staat inline en wint anders) */
    .rm-daycell { cursor: pointer; }
    .rm-daycell:hover {
      box-shadow: inset 0 0 0 9999px rgba(4, 94, 81, 0.045);
    }

    /* 🆕 Subtiele hover op rooster-tabs (overlay, want bg staat inline) */
    .rooster-tab:hover {
      box-shadow: inset 0 0 0 9999px rgba(4, 94, 81, 0.045);
    }

    /* 🆕 PRIMAIRE BUTTON - Gradient */
    .cp-btn-primary {
      background: #045E51;
      color: white;
      border: none;
      min-height: 44px;
      padding: 0 20px;
      border-radius: 12px;
      font-size: 14px;
      font-weight: 600;
      transition: transform 0.15s ease;
      cursor: pointer;
    }

    .cp-btn-primary:active {
      transform: scale(0.98);
    }
    
    /* 🆕 SECUNDAIRE BUTTON */
    .cp-btn-secondary {
      background: white;
      color: #045E51;
      border: 1px solid rgba(4, 94, 81, 0.33);
      min-height: 44px;
      padding: 0 20px;
      border-radius: 12px;
      font-size: 14px;
      font-weight: 600;
      transition: transform 0.15s ease;
      cursor: pointer;
    }

    .cp-btn-secondary:active {
      transform: scale(0.98);
    }
    
    /* 🆕 INPUT FIELDS */
    .cp-input {
      background: white;
      border: 2px solid #E5E7EB;
      border-radius: 12px;
      padding: 12px 16px;
      transition: all 0.2s ease;
      outline: none;
      width: 100%;
    }
    
    .cp-input:focus {
      border-color: #045E51;
      box-shadow: 0 0 0 4px rgba(4, 94, 81, 0.1);
    }
    
    .cp-input::placeholder {
      color: #9CA3AF;
    }
    
    /* 🆕 BADGES */
    .cp-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
    }
    
    .cp-badge-pine { background: #DAFFD1; color: #045E51; }
    .cp-badge-honey { background: rgba(255, 202, 85, 0.25); color: #7A5226; }
    .cp-badge-sky { background: rgba(157, 180, 246, 0.3); color: #00192F; }
    .cp-badge-blossom { background: rgba(255, 105, 155, 0.2); color: #700606; }
    .cp-badge-grass { background: #DAFFD1; color: #045E51; }
    .cp-badge-sunset { background: rgba(112, 6, 6, 0.1); color: #700606; }
    
    /* 🆕 PROGRESS BAR */
    .cp-progress {
      height: 8px;
      background: #F3F4F6;
      border-radius: 4px;
      overflow: hidden;
    }
    
    .cp-progress-bar {
      height: 100%;
      background: linear-gradient(90deg, #045E51 0%, #B1EDA6 100%);
      border-radius: 4px;
      transition: width 0.5s ease;
    }
    
    /* 🆕 CARD MET ACCENT */
    .cp-card-accent-left {
      border-left: 4px solid #045E51;
    }
    
    .cp-card-accent-top {
      border-top: 4px solid #045E51;
    }
    
    /* 🆕 VERBETERDE ANIMATIES */
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes slideInRight {
      from {
        opacity: 0;
        transform: translateX(20px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
    
    @keyframes pulse-glow {
      0%, 100% { 
        box-shadow: 0 0 0 0 rgba(4, 94, 81, 0.4);
      }
      50% { 
        box-shadow: 0 0 0 10px rgba(4, 94, 81, 0);
      }
    }
    
    @keyframes shimmer {
      0% { background-position: -1000px 0; }
      100% { background-position: 1000px 0; }
    }
    
    @keyframes float {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-5px); }
    }
    
    @keyframes bounce-soft {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-3px); }
    }
    
    .animate-fade-in-up {
      animation: fadeInUp 0.4s ease-out;
    }
    
    .animate-slide-in-right {
      animation: slideInRight 0.4s ease-out;
    }
    
    .animate-pulse-glow {
      animation: pulse-glow 2s infinite;
    }
    
    .animate-float {
      animation: float 3s ease-in-out infinite;
    }
    
    .animate-bounce-soft {
      animation: bounce-soft 2s ease-in-out infinite;
    }
    
    /* 🆕 STAGGER CHILDREN */
    .stagger-children > * {
      animation: fadeInUp 0.4s ease-out backwards;
    }
    .stagger-children > *:nth-child(1) { animation-delay: 0.05s; }
    .stagger-children > *:nth-child(2) { animation-delay: 0.1s; }
    .stagger-children > *:nth-child(3) { animation-delay: 0.15s; }
    .stagger-children > *:nth-child(4) { animation-delay: 0.2s; }
    .stagger-children > *:nth-child(5) { animation-delay: 0.25s; }
    .stagger-children > *:nth-child(6) { animation-delay: 0.3s; }
    
    /* 🆕 SKELETON LOADER */
    .skeleton {
      background: linear-gradient(
        90deg,
        #F3F4F6 0%,
        #E5E7EB 50%,
        #F3F4F6 100%
      );
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
      border-radius: 8px;
    }
    
    /* 🆕 TOOLTIP */
    [data-tooltip] {
      position: relative;
    }
    
    [data-tooltip]::after {
      content: attr(data-tooltip);
      position: absolute;
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      padding: 8px 12px;
      background: #00192F;
      color: white;
      font-size: 12px;
      border-radius: 8px;
      white-space: nowrap;
      opacity: 0;
      visibility: hidden;
      transition: all 0.2s ease;
      margin-bottom: 8px;
      z-index: 100;
    }
    
    [data-tooltip]:hover::after {
      opacity: 1;
      visibility: visible;
    }
    
    /* 🆕 FOCUS VISIBLE - Accessibility */
    *:focus-visible {
      outline: 2px solid #045E51;
      outline-offset: 2px;
    }
    
    /* 🆕 SELECTION COLOR */
    ::selection {
      background: rgba(4, 94, 81, 0.2);
      color: #045E51;
    }
    
    /* 🆕 DECORATIEF PATROON */
    .cp-pattern-dots {
      background-image: radial-gradient(rgba(4, 94, 81, 0.08) 1px, transparent 1px);
      background-size: 20px 20px;
    }
    
    .cp-pattern-leaves {
      background-image: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 5 Q25 15 20 20 Q15 15 20 5' fill='%23045E5108'/%3E%3C/svg%3E");
    }
    
    /* 🆕 DIVIDER */
    .cp-divider {
      height: 1px;
      background: linear-gradient(90deg, transparent, #E5E7EB, transparent);
      margin: 16px 0;
    }
    
    /* 🆕 NOTIFICATION DOT */
    .notification-dot {
      position: absolute;
      top: -2px;
      right: -2px;
      width: 10px;
      height: 10px;
      background: #700606;
      border: 2px solid white;
      border-radius: 50%;
      animation: pulse-glow 2s infinite;
    }
    
    /* 🆕 TOGGLE SWITCH */
    .cp-toggle {
      position: relative;
      width: 44px;
      height: 24px;
      background: #E5E7EB;
      border-radius: 12px;
      cursor: pointer;
      transition: background 0.2s ease;
    }
    
    .cp-toggle.active {
      background: #045E51;
    }
    
    .cp-toggle::after {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      width: 20px;
      height: 20px;
      background: white;
      border-radius: 50%;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      transition: transform 0.2s ease;
    }
    
    .cp-toggle.active::after {
      transform: translateX(20px);
    }
    
    /* 🆕 LOADER SPINNER */
    .cp-spinner {
      width: 24px;
      height: 24px;
      border: 3px solid #DAFFD1;
      border-top-color: #045E51;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
    
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    
    @keyframes task-flash {
      0%   { box-shadow: 0 0 0 0 rgba(34,197,94,0.6); background-color: rgba(34,197,94,0.12); }
      50%  { box-shadow: 0 0 0 8px rgba(34,197,94,0); background-color: rgba(34,197,94,0.06); }
      100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
    }
    .task-flash {
      animation: task-flash 0.5s ease-out;
    }

    @keyframes pin-shake {
      0%, 100% { transform: translateX(0); }
      15%       { transform: translateX(-6px); }
      30%       { transform: translateX(6px); }
      45%       { transform: translateX(-5px); }
      60%       { transform: translateX(5px); }
      75%       { transform: translateX(-3px); }
      90%       { transform: translateX(3px); }
    }
    .pin-shake {
      animation: pin-shake 0.5s ease-in-out;
    }
    
    /* 🆕 AVATAR GROUP */
    .avatar-group {
      display: flex;
    }
    
    .avatar-group > * {
      margin-left: -8px;
      border: 2px solid white;
      border-radius: 50%;
    }
    
    .avatar-group > *:first-child {
      margin-left: 0;
    }
    `;
      document.head.appendChild(style);
    } // end style guard

    // Dynamic favicon using CP symbol
    const updateFavicon = (themeId) => {
      const colors = {
        general: "#b4d6a1",
        winter: "#9fb2dc",
        spring: "#700606",
        summer: "#7A5226",
        autumn: "#fdc957",
      };
      const fill = colors[themeId] || "#b4d6a1";
      const svgFav = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 456 400"><path fill="${fill}" d="M163.72,269.63c-43.71-7.42-76.13-45.41-76.13-90.23s32.42-82.81,76.13-90.23c.74-.13,1.29-.76,1.29-1.51V48.38c0-.93-.81-1.66-1.73-1.55-31.41,3.73-60.41,18.48-82.11,41.91-22.94,24.77-35.57,56.97-35.57,90.66s12.63,65.89,35.57,90.66c21.68,23.41,50.66,38.15,82.11,41.91,.92,.11,1.73-.62,1.73-1.55v-39.27c0-.75-.55-1.39-1.29-1.51Z"/><path fill="${fill}" d="M275.78,45.85h-67.23c-.85,0-1.55,.69-1.55,1.55V199.74c0,.82-.64,1.5-1.47,1.55h-.13c-.74,.04-1.4-.46-1.58-1.17-8.98-36.16-23.56-50.96-49.97-50.96h-15.3c-.98,0-1.71,.9-1.52,1.86l12.45,45.01c.15,.72,.78,1.24,1.52,1.24h6.05c35.28,0,49.95,12.34,49.95,45.55v66.93c-.02,1.1-.04,2.26-.06,3.46-.19,7.72-1.03,26.65-4.65,39-.29,.98,.46,1.96,1.49,1.96h48.45c1.02,0,1.77-.98,1.49-1.96-3.32-11.31-4.3-28.13-4.59-36.81-.05-2.62-.09-5.08-.12-7.26v-65.39c.02-33.16,14.69-45.49,49.95-45.49h6.06c.73,0,1.37-.52,1.52-1.24l12.45-45.01c.2-.96-.54-1.86-1.52-1.86h-15.31c-26.41,0-40.99,14.8-49.97,50.96-.18,.72-.84,1.22-1.58,1.18h-.13c-.82-.05-1.45-.72-1.46-1.53h.01V89.39c-.01-.85,.68-1.55,1.53-1.55h25.23c51.05,0,92.61,41.05,92.63,91.5,.01,24.47-9.51,47.47-26.8,64.77-13.62,13.63-30.59,22.39-49.33,25.53-.74,.12-1.29,.76-1.29,1.51v39.26c0,.93,.81,1.66,1.73,1.55,29.61-3.51,57.36-16.93,78.59-38.17,25.23-25.24,39.11-58.79,39.09-94.48-.03-73.6-60.43-133.48-134.63-133.48Z"/></svg>`;
      const encoded = "data:image/svg+xml," + encodeURIComponent(svgFav);
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = encoded;
    };
    updateFavicon("general");
    window.__cpUpdateFavicon = updateFavicon;
  }, []);

  // Font laden via FontFace API - lettertypebestanden in /public/fonts (worden door de browser gecachet)
  useEffect(() => {
    const regularUri =
      "url('/fonts/bagos-regular.ttf')";
    const mediumUri =
      "url('/fonts/bagos-medium.ttf')";
    const loadFonts = async () => {
      try {
        if (typeof FontFace !== "undefined" && !window.__bagosLoaded) {
          window.__bagosLoaded = true;
          const fontDefs = [
            new FontFace("Bagos", regularUri, {
              weight: "400",
              style: "normal",
            }),
            new FontFace("Bagos", mediumUri, {
              weight: "500",
              style: "normal",
            }),
            new FontFace("Bagos", mediumUri, {
              weight: "600",
              style: "normal",
            }),
            new FontFace("Bagos", mediumUri, {
              weight: "700",
              style: "normal",
            }),
            new FontFace("Bagos", mediumUri, {
              weight: "800",
              style: "normal",
            }),
            new FontFace("Bagos", mediumUri, {
              weight: "900",
              style: "normal",
            }),
          ];
          const loaded = await Promise.all(fontDefs.map((f) => f.load()));
          loaded.forEach((f) => document.fonts.add(f));
          document.body.style.fontFamily = "'Bagos', system-ui, sans-serif";
          console.log("✅ Bagos Regular + Medium loaded");
        }
      } catch (err) {
        console.warn("FontFace fallback to @font-face CSS:", err);
      }
    };
    loadFonts();
  }, []);

  // ============================================================================
  // 📴 OFFLINE-FIRST STATE & INITIALIZATION
  // ============================================================================

  const [offlineOnline, setOfflineOnline] = useState(navigator.onLine);
  const [pendingMutations, setPendingMutations] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  // Initialize offline manager
  useEffect(() => {
    const initOffline = async () => {
      try {
        await offlineManager.init();
        console.log("✅ Offline manager ready");
      } catch (error) {
        console.error("❌ Offline manager failed:", error);
      }
    };

    initOffline();
  }, []);

  // Monitor connection status
  useEffect(() => {
    const handleOnline = () => {
      setOfflineOnline(true);
      offlineManager.setOnlineStatus(true);
    };

    const handleOffline = () => {
      setOfflineOnline(false);
      offlineManager.setOnlineStatus(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Monitor pending mutations
  useEffect(() => {
    const updatePendingCount = async () => {
      try {
        const mutations = await offlineManager.getPendingMutations();
        setPendingMutations(mutations.length);
      } catch (error) {
        // Ignore errors if DB not ready yet
      }
    };

    updatePendingCount();
    const interval = setInterval(updatePendingCount, 5000);

    return () => clearInterval(interval);
  }, []);

  // Listen to offline manager events
  useEffect(() => {
    let isMounted = true; // ✅ FIX: Track if component is still mounted

    const handleEvent = async (event, data) => {
      // ✅ FIX: Check if component is still mounted before updating state
      if (!isMounted) {
        console.log("⚠️ Component unmounted, skipping offline event handler");
        return;
      }

      switch (event) {
        case "syncComplete":
          if (isMounted) {
            setIsSyncing(false);
            setPendingMutations(0);
          }
          break;
        case "mutationSynced":
          if (isMounted) {
            setPendingMutations((prev) => Math.max(0, prev - 1));
          }
          break;
        case "connectionChange":
          if (isMounted) {
            setOfflineOnline(data.isOnline);
            if (data.isOnline) {
              setIsSyncing(true);
            }
          }
          break;
        case "mutationReady":
          // Offline gemaakte wijziging opnieuw door de normale opslag halen:
          // die voegt per veld/datum samen met de serverdata (transaction +
          // smartMerge) en zet statische velden in het juiste document.
          // Voorheen overschreef updateDoc hele velden (bv. alle overdrachten).
          if (data && data.type === "updateAppData" && data.data) {
            if (!saveDataRef.current) throw new Error("App nog niet klaar");
            await saveDataRef.current(data.data);
            console.log("✅ Offline wijziging opnieuw ingepland voor opslaan");
          }
          break;
        default:
          break;
      }
    };

    offlineManager.addListener(handleEvent);

    return () => {
      // ✅ FIX: Set isMounted to false on cleanup
      console.log("🧹 Cleaning up offline manager listener");
      isMounted = false;
      offlineManager.removeListener(handleEvent);
    };
  }, [db]); // Only depend on db, get loggedInUserName from localStorage when needed

  // Check if external promo mode via URL
  const isExternalPromoMode =
    new URLSearchParams(window.location.search).get("promo") === "fullscreen";

  // Check if DJ guest mode via URL (?dj=appId)
  const isGuestDJMode = !!new URLSearchParams(window.location.search).get("dj") || isDjGastAdres();

  // Ingelogd blijven zolang er binnen 8 uur iets gebeurt (tikken, typen, opslaan)
  const LOGIN_GELDIG_UREN = 8;
  const checkLoginValidity = () => {
    if (isExternalPromoMode) return true;
    const storedUser = localStorage.getItem("cp_loggedInUser");
    const loginTime = localStorage.getItem("cp_loginTime");
    if (storedUser && loginTime) {
      const hoursSinceLogin =
        (Date.now() - new Date(loginTime)) / (1000 * 60 * 60);
      return hoursSinceLogin < LOGIN_GELDIG_UREN; // uitloggen na 8 uur zonder activiteit
    }
    return false;
  };

  const [isLoggedIn, setIsLoggedIn] = useState(() => checkLoginValidity());

  const [view, setView] = useState(() => {
    if (isExternalPromoMode) return "promo_screen";
    // If logged in and session is valid, go to appropriate view
    if (checkLoginValidity()) {
      const role = localStorage.getItem("cp_userRole");
      // Snelkoppeling van het beginscherm (?view=rooster) of een melding
      const gevraagd = new URLSearchParams(window.location.search).get("view");
      if (gevraagd && ["dagbord", "rooster", "chat", "schedule", "checks", "dashboard", "promo_bediening"].includes(gevraagd))
        return gevraagd;
      return role === "co" ? "schedule" : "dagbord";
    }
    return "login";
  });

  const [adminSubView, setAdminSubView] = useState("menu");
  const [activeChecklistTab, setActiveChecklistTab] = useState("entertainment"); // 📑 Active tab voor takenlijsten
  const [isCopyChecklistModalOpen, setIsCopyChecklistModalOpen] =
    useState(false);
  const [copyChecklistTargetDay, setCopyChecklistTargetDay] = useState(null); // 0-6 (weekdag)
  const [skCheckInput, setSkCheckInput] = useState({
    type: "veilig",
    opmerking: "",
  }); // 🏰 Springkussen check state
  const [appData, setAppDataRuw] = useState(INITIAL_DATA);
  // Elke wijziging van appData gaat langs de Crea Club-naamvervanging
  const setAppData = useCallback(
    (v) => setAppDataRuw((prev) => vervangCreaAtelier(typeof v === "function" ? v(prev) : v)),
    []
  );
  // Schermen krijgen het programma mét bibliotheekteksten (zie scheduleMetLibTeksten)
  const appDataWeergave = useMemo(() => {
    const s = scheduleMetLibTeksten(appData.schedule, appData.activityLibrary);
    return s === appData.schedule ? appData : { ...appData, schedule: s };
  }, [appData]);
  const [loading, setLoading] = useState(true);
  const [laadStap, setLaadStap] = useState(0); // 0 start · 1 verbonden · 2 team/instellingen · 3 planning

  // 📊 SEGMENTED DATABASE - Initialize manager
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(() => {
    if (isExternalPromoMode) return "fm";
    if (!checkLoginValidity()) return null;
    return localStorage.getItem("cp_userRole") || null;
  });
  const [loggedInUserName, setLoggedInUserName] = useState(() => {
    if (isExternalPromoMode) return "Promotiescherm";
    // Restore from localStorage
    const stored = localStorage.getItem("cp_loggedInUser");
    return stored || "";
  });
  const [loginMode, setLoginMode] = useState("");
  const [selectedStaff, setSelectedStaff] = useState("");
  const [pin, setPin] = useState("");
  const [pinLoginError, setPinLoginError] = useState(false);
  const [alertParams, setAlertParams] = useState({
    isOpen: false,
    message: "",
  });
  const [activePopupNotif, setActivePopupNotif] = useState(null);
  const [activeMentionNotif, setActiveMentionNotif] = useState(null);
  const [activeVerlofNotif, setActiveVerlofNotif] = useState(null);
  const dismissedMentionIds = useRef(new Set());
  const dismissedVerlofIds = useRef(new Set());
  const [activeOpenNotif, setActiveOpenNotif] = useState(null);
  const dismissedOpenIds = useRef(new Set());
  const dismissedPopupIds = useRef(new Set());
  const [confirmParams, setConfirmParams] = useState({
    isOpen: false,
    message: "",
    onConfirm: null,
    onCancel: null,
  });
  const [currentDay, setCurrentDay] = useState(new Date());
  const [pendingHandoverTab, setPendingHandoverTab] = useState(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [programExpanded, setProgramExpanded] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showFmQuickAdd, setShowFmQuickAdd] = useState(false);
  const [quickAddText, setQuickAddText] = useState("");
  const [quickAddPriority, setQuickAddPriority] = useState("normal");
  // 🛡️ DATA PROTECTION - Extra states
  const [dataLossWarning, setDataLossWarning] = useState(false);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const autoSaveTimerRef = useRef(null);
  const lastSaveRef = useRef(null);

  // 🔒 MULTI-USER SYNC - States & Refs
  const pendingSaveRef = useRef(null); // Debounce timer
  const pendingChangesRef = useRef({}); // Verzamel wijzigingen
  const dagDocsRef = useRef({}); // ⚡ snelle daglaag: { datum: dagdocument }
  const isSavingRef = useRef(false); // Voorkom dubbele saves
  const saveStartedAtRef = useRef(0); // vangnet als een save ooit blijft hangen
  const saveDataRef = useRef(null); // altijd de nieuwste saveData (geen oude closure)
  const activeSaveRef = useRef({}); // Bijhouden wat er momenteel opgeslagen wordt
  const lastServerVersionRef = useRef(0);
  const lokaleKopieData = useRef(null);
  const lokaleKopieTimer = useRef(null); // Track server versie
  const lastDataHashRef = useRef(null); // 🔒 Track data hash om onnodige updates te voorkomen
  const updateDebounceRef = useRef(null); // 🔒 Debounce snelle updates
  const saveRetryCount = useRef(0); // Retry counter
  const [syncStatus, setSyncStatus] = useState("synced"); // 'synced' | 'saving' | 'offline' | 'conflict'
  const [showSyncIndicator, setShowSyncIndicator] = useState(false);
  // ⚡ Voorlopig: de app toont al de laatst bekende gegevens van dit toestel en
  // haalt intussen de nieuwste uit Firebase. Opslaan wacht tot dat klaar is.
  const [voorlopig, setVoorlopig] = useState(false);
  const voorlopigRef = useRef(false);
  voorlopigRef.current = voorlopig;
  const laatsteTikRef = useRef(0);
  // Vangnet: lukt Firebase niet (offline, traag netwerk), dan na 15 s gewoon
  // verder met de lokale gegevens, zoals de app offline altijd al deed.
  useEffect(() => {
    if (!voorlopig) return;
    const t = setTimeout(() => setVoorlopig(false), 15000);
    return () => clearTimeout(t);
  }, [voorlopig]);
  useEffect(() => {
    if (syncStatus === "offline") setVoorlopig(false);
  }, [syncStatus]);
  useEffect(() => {
    const tik = () => (laatsteTikRef.current = Date.now());
    window.addEventListener("pointerdown", tik, { passive: true });
    window.addEventListener("keydown", tik);
    return () => {
      window.removeEventListener("pointerdown", tik);
      window.removeEventListener("keydown", tik);
    };
  }, []);
  const [otherUserEditing, setOtherUserEditing] = useState(null); // Naam van andere user

  // 🌐 CONNECTION STATUS
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showConnectionToast, setShowConnectionToast] = useState(false);
  const [connectionMessage, setConnectionMessage] = useState("");
  const connectionToastTimeout = useRef(null);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setConnectionMessage("Verbonden ✓");
      setShowConnectionToast(true);

      // Verberg na 2 seconden
      if (connectionToastTimeout.current)
        clearTimeout(connectionToastTimeout.current);
      connectionToastTimeout.current = setTimeout(() => {
        setShowConnectionToast(false);
      }, 2000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setConnectionMessage("Geen verbinding");
      setShowConnectionToast(true);
      // Blijft zichtbaar tot online
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      if (connectionToastTimeout.current)
        clearTimeout(connectionToastTimeout.current);
    };
  }, []);

  // 🎯 AUTO-SELECT TAB: Open de juiste tab op basis van waar gebruiker werkt
  useEffect(() => {
    if (!loggedInUserName || view !== "checks") return;

    const dayKey = toIsoDate(currentDay);
    // Eerst: de afdeling waarop je vandaag ingeroosterd staat
    const opRooster = dbMyAfdeling(appData, dayKey, loggedInUserName);
    if (["entertainment", "crea", "springkussen", "hotspot"].includes(opRooster)) {
      setActiveChecklistTab(opRooster);
      return;
    }
    const todaysActivities = (appData.schedule || []).filter(
      (s) => s.date === dayKey
    );

    // Tel op welke afdelingen de gebruiker werkt vandaag
    let entCount = 0;
    let creaCount = 0;
    let extraCount = 0;

    todaysActivities.forEach((activity) => {
      if (activity.staff && activity.staff.includes(loggedInUserName)) {
        if (activity.type === "entertainment") entCount++;
        else if (activity.type === "crea") creaCount++;
        else if (activity.type === "extra") extraCount++;
      }
    });

    // Bepaal welke tab te openen (meeste activiteiten wint)
    if (entCount > 0 || creaCount > 0 || extraCount > 0) {
      if (entCount >= creaCount && entCount >= extraCount) {
        setActiveChecklistTab("entertainment");
      } else if (creaCount >= entCount && creaCount >= extraCount) {
        setActiveChecklistTab("crea");
      } else {
        setActiveChecklistTab("extra");
      }
    }
  }, [view, currentDay, loggedInUserName, appData.schedule, appData.roosterDiensten, appData.workSchedule]); // eslint-disable-line

  // Toon "Verbonden" toast na succesvol inloggen
  useEffect(() => {
    if (isLoggedIn && navigator.onLine) {
      // Korte delay zodat UI eerst rendert
      const timer = setTimeout(() => {
        setConnectionMessage("Verbonden ✓");
        setShowConnectionToast(true);
        connectionToastTimeout.current = setTimeout(() => {
          setShowConnectionToast(false);
        }, 2500);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isLoggedIn]);

  // 🕐 INACTIVITEIT TIMEOUT - Log uit na 1 uur geen activiteit
  useEffect(() => {
    if (!isLoggedIn || isExternalPromoMode) return;

    const TIMEOUT_MS = 60 * 60 * 1000; // 1 uur inactiviteit
    let inactivityTimer;

    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        console.log("Uitgelogd wegens inactiviteit");
        handleLogout();
        triggerAlert("Je bent uitgelogd wegens inactiviteit.");
      }, TIMEOUT_MS);
    };

    // Reset bij elke gebruikersactie
    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "touchstart",
      "scroll",
      "click",
    ];
    events.forEach((e) =>
      window.addEventListener(e, resetTimer, { passive: true })
    );

    // Start de timer direct
    resetTimer();

    return () => {
      clearTimeout(inactivityTimer);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [isLoggedIn]);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Promoscherm blijft op desktop (FM) na de eerste keer openen op de
  // achtergrond draaien, zodat het bij terugkomen verdergaat waar het was.
  const [promoAchtergrond, setPromoAchtergrond] = useState(false);
  const promoDesktopFM =
    !isExternalPromoMode &&
    isLoggedIn &&
    (userRole === "fm" || userRole === "admin") &&
    typeof window !== "undefined" &&
    window.matchMedia?.("(min-width: 1024px)").matches;
  const promoBlijft =
    promoDesktopFM && (promoAchtergrond || view === "promo_screen");
  useEffect(() => {
    if (promoBlijft && !promoAchtergrond) setPromoAchtergrond(true);
    if (!isLoggedIn && promoAchtergrond) setPromoAchtergrond(false);
  }, [promoBlijft, promoAchtergrond, isLoggedIn]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  // 👻 Onzichtbaar-modus (alleen FM/admin): je lijkt offline voor anderen
  const canGoInvisible = userRole === "fm" || userRole === "admin";
  const [presenceInvisible, setPresenceInvisible] = useState(() => {
    try {
      return localStorage.getItem("cp_presenceInvisible") === "1";
    } catch (e) {
      return false;
    }
  });
  // Verborgen medewerker (in Personeel op inactief) is nooit zichtbaar als online
  zetCpRol(userRole);
  // 📸 Reparatie (FM): collega's met een zelfgemaakte foto (fotoVrij) maar een oude/standaard
  // kleine profielfoto (teruggezet door een tab met een oude kopie) → kleine foto opnieuw maken.
  const fotoRepRef = useRef(new Set());
  useEffect(() => {
    if (loading || userRole !== "fm") return;
    const lijst = appData.staffList || [];
    // Niet op de standaardlijst (nog niet geladen) werken
    if (lijst.length === DEFAULT_STAFF_NAMES.length && lijst.every((s) => DEFAULT_STAFF_NAMES.includes(s.name) && s.pin === "0000")) return;
    // Eenmalig (tot fotoAt gezet is): kleine foto altijd opnieuw maken uit de vrijstaande foto,
    // want de kleine kan door een oude tab een eerdere (eigen) foto zijn geworden
    const kapot = lijst.filter((s) => s && s.name && s.fotoVrij && !s.fotoAt && !fotoRepRef.current.has(s.name));
    if (!kapot.length) return;
    let weg = false;
    (async () => {
      for (const s0 of kapot) {
        fotoRepRef.current.add(s0.name);
        try {
          const png = await dbLoadPhoto(s0.fotoVrij);
          if (!png || weg) continue;
          const kleur = FOTO_KLEUR_PAAR[s0.fotoKleur] || THEMA_FOTO_KLEUR[s0.theme] || "#B1EDA6";
          const klein = await opgemaakteFoto(png, { achtergrond: kleur, maat: 192 });
          if (!klein || weg) continue;
          const cur = appDataRef.current?.staffList || appData.staffList || [];
          saveData({ staffList: cur.map((x) => (x && x.name === s0.name ? { ...x, profilePhoto: klein, fotoAt: Date.now() } : x)) });
        } catch (e) {
          /* volgende keer opnieuw */
        }
      }
    })();
    return () => {
      weg = true;
    };
  }, [loading, userRole, appData.staffList]); // eslint-disable-line

  // 📅 Elke vrijdagochtend (vanaf 07:00) het weekrooster naar de FM mailen. De eerste app die
  // dan open is verstuurt hem; een vlag in de database zorgt dat het maar één keer gebeurt.
  // Testen: ?weekmail=test (alleen FM) verstuurt hem meteen.
  useEffect(() => {
    if (loading || !loggedInUserName || !db || !appId || isExternalPromoMode) return undefined;
    const nu = new Date();
    const test = new URLSearchParams(window.location.search).get("weekmail") === "test" && userRole === "fm";
    if (!test && (nu.getDay() !== 5 || nu.getHours() < 7)) return undefined;
    const vandaag = toIsoDate(nu);
    const vlag = doc(db, "artifacts", appId, "public", "data", "weekmail", "status");
    let gestopt = false;
    const t = setTimeout(async () => {
      try {
        if (!test) {
          const mag = await runTransaction(db, async (tr) => {
            const snap = await tr.get(vlag);
            const d = snap.exists() ? snap.data() : {};
            if (d.laatst === vandaag || (d.bezig === vandaag && Date.now() - (d.at || 0) < 10 * 60000)) return false;
            tr.set(vlag, { bezig: vandaag, door: loggedInUserName, at: Date.now() }, { merge: true });
            return true;
          });
          if (!mag || gestopt) return;
        }
        const vrijdag = getFridayOfWeek(nu);
        const m = weekroosterMail(appData, vrijdag);
        const aan = String(appData.settings?.roosterMail || appData.settings?.bestelMail || "maik.vandenbroek@groupepvcp.com").trim();
        const r = await stuurWeekPdf({ appData, vrijdag, aan, onderwerp: m.onderwerp, tekst: m.tekst, html: m.html });
        if (test) cpToast(r.ok ? `Weekrooster gemaild naar ${aan}${r.pdf ? " (met pdf)" : ` — zonder pdf: ${r.fout}`}` : `Mail mislukt: ${r.fout}`, r.ok && r.pdf ? "success" : "error");
        else
          await setDoc(vlag, r.ok ? { laatst: vandaag, bezig: null, at: Date.now() } : { bezig: null, fout: r.fout || "onbekend", at: Date.now() }, { merge: true });
      } catch (e) {
        console.warn("Weekroostermail:", e);
      }
    }, 8000);
    return () => {
      gestopt = true;
      clearTimeout(t);
    };
  }, [loading, loggedInUserName]); // eslint-disable-line

  const ikBenVerborgen = !!(appData.staffList || []).find((s) => s.name === loggedInUserName && (s.inactive || s.verborgen));
  const presenceInvisibleActive = (canGoInvisible && presenceInvisible) || ikBenVerborgen;
  const togglePresenceInvisible = async () => {
    if (!canGoInvisible || !loggedInUserName || !db || !appId) return;
    const next = !presenceInvisible;
    setPresenceInvisible(next);
    try {
      localStorage.setItem("cp_presenceInvisible", next ? "1" : "0");
    } catch (e) {}
    try {
      await setDoc(
        doc(db, "artifacts", appId, "public", "data", "presence", loggedInUserName),
        {
          name: loggedInUserName,
          invisible: next,
          lastSeen: next ? null : new Date().toISOString(),
        },
        { merge: true }
      );
    } catch (e) {}
  };

  // 👥 PRESENCE: schrijf heartbeat naar Firebase
  useEffect(() => {
    if (
      !isLoggedIn ||
      !loggedInUserName ||
      !db ||
      !appId ||
      isExternalPromoMode
    )
      return;

    const presenceRef = doc(
      db,
      "artifacts",
      appId,
      "public",
      "data",
      "presence",
      loggedInUserName
    );

    const writePresence = async () => {
      try {
        await setDoc(
          presenceRef,
          {
            name: loggedInUserName,
            lastSeen: presenceInvisibleActive ? null : new Date().toISOString(),
          },
          { merge: true }
        );
      } catch (e) {
        // silently ignore
      }
    };

    const clearPresence = async () => {
      try {
        await setDoc(
          presenceRef,
          {
            name: loggedInUserName,
            lastSeen: null,
          },
          { merge: true }
        );
      } catch (e) {}
    };

    writePresence();
    const interval = setInterval(writePresence, 30000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") writePresence();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearPresence();
    };
  }, [isLoggedIn, loggedInUserName, db, appId, presenceInvisibleActive]);

  // 👥 PRESENCE: luister naar online gebruikers
  useEffect(() => {
    if (!isLoggedIn || !db || !appId || isExternalPromoMode) return;

    const presenceCollection = collection(
      db,
      "artifacts",
      appId,
      "public",
      "data",
      "presence"
    );

    const unsubscribe = onSnapshot(
      presenceCollection,
      (snap) => {
        const now = Date.now();
        const ONLINE_THRESHOLD_MS = 2 * 60 * 1000; // 2 minuten
        const active = [];
        let selfInvisible = false;
        snap.forEach((d) => {
          const data = d.data();
          if (data.name === loggedInUserName && data.invisible) {
            selfInvisible = true;
          }
          if (data.invisible) return;
          if (!data.lastSeen) return;
          const diff = now - new Date(data.lastSeen).getTime();
          if (diff < ONLINE_THRESHOLD_MS) {
            active.push({ name: data.name });
          }
        });
        if (canGoInvisible) {
          setPresenceInvisible(selfInvisible);
          try {
            localStorage.setItem("cp_presenceInvisible", selfInvisible ? "1" : "0");
          } catch (e) {}
          if (selfInvisible && loggedInUserName) {
            active.push({ name: loggedInUserName, invisible: true });
          }
        }
        setOnlineUsers(active);
      },
      () => {}
    );

    return () => unsubscribe();
  }, [isLoggedIn, db, appId, loggedInUserName, canGoInvisible]);
  const hasBackedUp = useRef(false); // Ref to track backup status
  const weather = useWeather(currentDay, isLoggedIn);
  const greeting = getGreeting();
  const [searchPhone, setSearchPhone] = useState("");
  const [showCompletedTasks, setShowCompletedTasks] = useState({});
  const WeatherIcon = weather.icon;

  zetFmThema(appData.settings?.fmTheme, appData.settings?.fmFoto);
  let currentTheme = THEMES[appData.settings.theme] || THEMES.general;
  if (appData.settings.theme === "custom") {
    currentTheme = {
      ...THEMES.custom,
      primary: appData.settings.customPrimary || C.Pine,
      secondary: appData.settings.customSecondary || C.Grass,
    };
  }
  if (userRole === "staff" && loggedInUserName) {
    const staffUser = appData.staffList.find(
      (s) => s.name === loggedInUserName
    );
    if (staffUser && staffUser.theme && THEMES[staffUser.theme]) {
      currentTheme = THEMES[staffUser.theme];
    }
  } else if (
    userRole === "fm" &&
    appData.settings.fmTheme &&
    THEMES[appData.settings.fmTheme]
  ) {
    currentTheme = THEMES[appData.settings.fmTheme];
  }

  // Herfst heeft hetzelfde kleurpaar als Geel → altijd Geel (vier themakleuren)
  if (currentTheme?.id === "autumn") currentTheme = THEMES.summer;
  // 🎨 Lichte, schone achtergrondkleur per thema (app-breed). We gebruiken de
  // hand-getunede *Light tokens i.p.v. een mix met de (vaak donkere) primaire
  // kleur, want die mix oogt grauw/grijzig.
  // Pagina-achtergrond: heel zacht (rust); de officiële webtinten zijn voor chips/vakken
  const THEME_BG_BY_THEME = {
    general: "#EEFBEA",
    winter: "#EEF3FE",
    spring: "#FFF0F5",
    summer: "#FFF8E7",
    autumn: "#FBF5ED",
  };
  const [donkerStand, setDonkerStand] = useState(leesDonker);
  const [systeemDonker, setSysteemDonker] = useState(() => {
    try { return window.matchMedia("(prefers-color-scheme: dark)").matches; } catch (e) { return false; }
  });
  useEffect(() => {
    const opWissel = (e) => setDonkerStand(e.detail || leesDonker());
    window.addEventListener("cp-donker", opWissel);
    let mq = null;
    const opSysteem = (e) => setSysteemDonker(e.matches);
    try { mq = window.matchMedia("(prefers-color-scheme: dark)"); mq.addEventListener("change", opSysteem); } catch (e) { mq = null; }
    return () => { window.removeEventListener("cp-donker", opWissel); if (mq) mq.removeEventListener("change", opSysteem); };
  }, []);
  const modusActief = isLoggedIn && !isExternalPromoMode;
  const donkerAan = modusActief && (donkerStand === "aan" || (donkerStand === "auto" && systeemDonker));
  const toonLichtAan = modusActief && !donkerAan;
  const themaId = DONKER_PALET[currentTheme?.id] ? currentTheme.id : "general";
  const donkerPalet = DONKER_PALET[themaId];
  const toonPalet = TOONLICHT_PALET[themaId];
  useEffect(() => {
    const r = document.documentElement;
    r.classList.toggle("cp-donker", donkerAan);
    r.classList.toggle("cp-toonlicht", toonLichtAan);
    r.classList.toggle("cp-merk", modusActief && !donkerAan);
    r.classList.toggle("cp-thema-anders", modusActief && !!THEMA_ACCENT[themaId]);
    if (modusActief) {
      try { localStorage.setItem("cp_laatste_thema", themaId); } catch (e) { /* geen opslag */ }
    }
    MODUS_VARS.forEach((k) => r.style.removeProperty(k));
    if (!modusActief) return;
    const lt = toonLichtAan ? toonPalet : LICHT_PALET[themaId];
    const vars = donkerAan
      ? { "--dk-accent": donkerPalet.accent, "--dk-knop": donkerPalet.knop, "--dk-bg": donkerPalet.bg, "--dk-kaart": donkerPalet.kaart, "--dk-kaart2": donkerPalet.kaart2, "--dk-kaart3": donkerPalet.kaart3, "--dk-rand": donkerPalet.rand, "--dk-tekst": donkerPalet.tekst, "--dk-tekst2": donkerPalet.tekst2, "--dk-op-licht": donkerPalet.opLicht }
      : { "--lt-tekst": lt.tekst, "--lt-tekst2": lt.tekst2, "--lt-tekst3": lt.tekst3 };
    const acc = THEMA_ACCENT[themaId];
    if (acc) Object.assign(vars, { "--m-donker": acc.donker, "--m-licht": acc.licht, "--m-mid": acc.mid });
    if (toonLichtAan) Object.assign(vars, { "--tl-bg": toonPalet.bg, "--tl-kaart": toonPalet.kaart, "--tl-kaart2": toonPalet.kaart2, "--tl-kaart3": toonPalet.kaart3, "--tl-rand": toonPalet.rand });
    Object.entries(vars).forEach(([k, v]) => r.style.setProperty(k, v));
  }, [modusActief, donkerAan, toonLichtAan, themaId, donkerPalet, toonPalet]);
  const themeBg = donkerAan ? donkerPalet.bg : toonLichtAan ? toonPalet.bg : THEME_BG_BY_THEME[currentTheme?.id] || "#EEFBEA";
  // Dagbord-stijl: de inhoud van elke pagina gebruikt de vaste merkkleur.
  // Het gekozen thema kleurt alleen de 'omlijsting' (zijbalk, topbalk, achtergrond).
  const contentTheme = { ...currentTheme, primary: C.Pine, secondary: C.Grass };

  // ⚡ Luister live naar de kleine dagdocumenten (gisteren, vandaag, morgen)
  const vandaagKeyDaglaag = toIsoDate(new Date());
  useEffect(() => {
    if (!isLoggedIn || !db || !appId || isExternalPromoMode) return;
    const subs = DAG_HETE_DAGEN().map((dk) =>
      onSnapshot(
        doc(db, "artifacts", appId, "public", "data", "cp_dag", dk),
        (snap) => {
          dagDocsRef.current = {
            ...dagDocsRef.current,
            [dk]: snap.exists() ? snap.data({ serverTimestamps: "estimate" }) : null,
          };
          startTransition(() =>
            setAppData((prev) => pasDaglaagToe(prev, { [dk]: dagDocsRef.current[dk] }))
          );
        },
        (e) => console.warn("⚡ daglaag niet beschikbaar:", e?.message || e)
      )
    );
    return () => subs.forEach((u) => u && u());
  }, [isLoggedIn, vandaagKeyDaglaag]); // eslint-disable-line

  // 🔄 Nieuwe versie van de app? Vergelijk het bundel-bestand van deze pagina
  // met dat van de live index.html. Bij verschil: melding met "Vernieuwen".
  // Was de app langer dan 10 min op de achtergrond en staat er niets
  // onopgeslagen, dan vernieuwen we bij terugkomst vanzelf.
  const [nieuweVersie, setNieuweVersie] = useState(false);
  useEffect(() => {
    if (isExternalPromoMode) return;
    const bundelVan = (tekst) => {
      const m = /\/static\/js\/main\.([a-f0-9]+)\.js/.exec(tekst || "");
      return m ? m[1] : null;
    };
    // Huidige versie: uit de script-tags van deze pagina
    const huidig = bundelVan(
      [...document.querySelectorAll("script[src]")].map((x) => x.getAttribute("src")).join(" ")
    ) || bundelVan(document.documentElement.innerHTML);
    if (!huidig) return; // lokaal ontwikkelen: geen gehashte bundel
    let weg = null;
    let gevonden = false;
    // Live versie: eerst asset-manifest.json (altijd aanwezig bij deze build),
    // anders de startpagina zelf. Nooit uit de cache.
    const liveVersie = async () => {
      const t = Date.now();
      for (const pad of [`/asset-manifest.json?v=${t}`, `/?v=${t}`, `/index.html?v=${t}`]) {
        try {
          const r = await fetch(pad, { cache: "no-store", credentials: "same-origin" });
          if (!r.ok) continue;
          const v = bundelVan(await r.text());
          if (v) return v;
        } catch (e) {}
      }
      return null;
    };
    const kijk = async () => {
      const live = await liveVersie();
      if (live && live !== huidig) {
        gevonden = true;
        setNieuweVersie(true);
      }
      return gevonden;
    };
    const opZicht = async () => {
      if (document.visibilityState === "hidden") {
        weg = Date.now();
        return;
      }
      const lang = weg && Date.now() - weg > 10 * 60 * 1000;
      weg = null;
      const nieuw = (await kijk()) || gevonden;
      const nietsOpen = Object.keys(pendingChangesRef.current || {}).length === 0;
      if (nieuw && lang && nietsOpen) window.location.reload();
    };
    kijk();
    const iv = setInterval(kijk, 60 * 1000);
    document.addEventListener("visibilitychange", opZicht);
    window.addEventListener("focus", kijk);
    window.addEventListener("online", kijk);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", opZicht);
      window.removeEventListener("focus", kijk);
      window.removeEventListener("online", kijk);
    };
  }, []); // eslint-disable-line
  // Statusbalk van de telefoon laten aansluiten op de lichte achtergrond
  useEffect(() => {
    try {
      const m = document.querySelector('meta[name="theme-color"]');
      if (m) m.setAttribute("content", isLoggedIn && !isExternalPromoMode ? themeBg : "#045E51");
    } catch (e) {}
  }, [themeBg, isLoggedIn]);

  // Update favicon when theme changes
  React.useEffect(() => {
    if (window.__cpUpdateFavicon) window.__cpUpdateFavicon(currentTheme?.id);
  }, [currentTheme?.id]);

  // 🔒 Helper: Snelle hash van data om onnodige updates te detecteren
  const getDataHash = (data) => {
    // Hash over de VOLLEDIGE inhoud (zonder versie-/meta-velden).
    // Voorheen telde dit alleen aantallen (bv. checklists.length), waardoor
    // een afgevinkte taak, gewijzigde overdracht of inklok van een collega als
    // "identiek" werd gezien en nooit op andere telefoons verscheen.
    try {
      const { _version, _lastEditBy, lastUpdate, ...rest } = data || {};
      const str = JSON.stringify(rest);
      let h = 2166136261;
      for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
      }
      return `${str.length}:${h >>> 0}`;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    let mounted = true;
    const initAuth = async () => {
      try {
        if (
          typeof __initial_auth_token !== "undefined" &&
          __initial_auth_token
        ) {
          await signInWithCustomToken(auth, __initial_auth_token);
        } else {
          await signInAnonymously(auth);
        }
      } catch (error) {
        console.log(error);
      }
    };
    if (auth) initAuth();
    if (auth) {
      const unsubscribe = onAuthStateChanged(auth, (u) => {
        if (mounted) {
          setUser(u);
          if (!u) setLoading(false);
        }
      });
      return () => {
        mounted = false;
        unsubscribe();
      };
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let unsubscribe = null;
    let unsubscribeStatic = null; // 📡 listener op het static-doc
    let isMounted = true; // ✅ FIX: Track if component is still mounted

    const initializeData = async () => {
      // ⚡ Direct openen met de laatst bekende gegevens van dit toestel
      try {
        const snel = await offlineManager.getData("appData");
        if (
          isMounted &&
          snel &&
          Array.isArray(snel.staffList) &&
          snel.staffList.length > 0 &&
          !(snel.staffList.length === DEFAULT_STAFF_NAMES.length && snel.staffList.every((s) => s && s.pin === "0000"))
        ) {
          setAppData(sanitizeData(snel));
          setVoorlopig(true);
          setLoading(false);
        }
      } catch (e) {
        /* geen lokale kopie: gewoon het laadscherm */
      }
      try {
        await signInAnonymously(auth);
        if (isMounted) setLaadStap((s) => Math.max(s, 1));
        const masterDocRef = doc(
          db,
          "artifacts",
          appId,
          "public",
          "data",
          "cp_live_data",
          "master"
        );

        // 📦 Laad statische data eenmalig (apart document, zelden verandert)
        const staticDocRef = doc(
          db,
          "artifacts",
          appId,
          "public",
          "data",
          "cp_static_data",
          "master"
        );

        let staticData = {};
        let staticDocExists = false;
        try {
          const staticSnap = await getDoc(staticDocRef);
          staticDocExists = staticSnap.exists();
          if (staticDocExists) {
            staticData = staticSnap.data();
          }
        } catch (e) {
          console.warn("⚠️ Kon static doc niet laden:", e);
        }
        if (isMounted) setLaadStap((s) => Math.max(s, 2));

        unsubscribe = onSnapshot(
          masterDocRef,
          async (docSnapshot) => {
            // ✅ FIX: Check if component is still mounted before updating state
            if (!isMounted) {
              return;
            }
            setLaadStap(3);

            if (docSnapshot.exists()) {
              const firestoreData = docSnapshot.data();

              // Validation check
              if (firestoreData && Object.keys(firestoreData).length > 2) {
                const serverVersion = firestoreData._version || 0;
                const serverEditBy = firestoreData._lastEditBy || "unknown";
                const currentUser = loggedInUserName || "anonymous";

                // Merge live data met statische data.
                // Live master mag statische velden (staffList, settings, ...)
                // NIET overschrijven: die zijn leidend in het static-doc. Een
                // verouderde kopie in master wiste anders net-geüploade
                // profielfoto's, pincodes e.d. weer weg.
                const liveOnly = { ...firestoreData };
                for (const field of STATIC_FIELDS) {
                  if (field in staticData) delete liveOnly[field];
                }
                let finalData = { ...staticData, ...liveOnly };

                // 🔄 EENMALIGE MIGRATIE: als static doc leeg is maar master heeft
                // nog statische velden → verplaats ze naar static doc
                if (!staticDocExists || Object.keys(staticData).length === 0) {
                  const staticPayload = {};
                  const livePayload = {};
                  for (const [k, v] of Object.entries(firestoreData)) {
                    if (STATIC_FIELDS.has(k)) staticPayload[k] = v;
                    else livePayload[k] = v;
                  }
                  if (Object.keys(staticPayload).length > 0) {
                    // Sla statische data op in eigen document
                    setDoc(staticDocRef, staticPayload)
                      .then(() => {
                        staticData = staticPayload;
                        console.log(
                          "Statische data gemigreerd naar cp_static_data"
                        );
                      })
                      .catch(console.warn);
                  }
                  finalData = { ...staticPayload, ...livePayload };
                }

                // 🔒 Check of dit een update van IEMAND ANDERS is
                if (
                  serverVersion > lastServerVersionRef.current &&
                  lastServerVersionRef.current > 0
                ) {
                  const isOtherUserEdit =
                    serverEditBy !== currentUser &&
                    serverEditBy !== "anonymous";

                  // 🔒 Check of data ECHT veranderd is via hash
                  const newHash = getDataHash(finalData);
                  const dataActuallyChanged =
                    newHash !== lastDataHashRef.current;

                  if (!dataActuallyChanged) {
                    console.log(
                      "⏭️ Server update maar data is identiek - skip re-render"
                    );
                    lastServerVersionRef.current = serverVersion;
                    return; // Skip de update als data identiek is
                  }

                  if (isOtherUserEdit) {
                    // Toon wie er aan het bewerken is (alleen als data echt veranderd is)
                    // (Geen "X wijzigde iets"-melding meer: wijzigingen verschijnen live
                    // en lichten in het Dagbord kort op waar ze gebeurden.)
                    // 📦 Herlaad statische data bij update van iemand anders
                    // (statische doc heeft geen eigen listener)
                    getDoc(staticDocRef)
                      .then((snap) => {
                        if (snap.exists()) staticData = snap.data();
                      })
                      .catch(() => {});
                  }

                  // 🔒 Debounce snelle opeenvolgende updates
                  if (updateDebounceRef.current) {
                    clearTimeout(updateDebounceRef.current);
                  }

                  updateDebounceRef.current = setTimeout(() => {
                    if (
                      isSavingRef.current ||
                      Object.keys(pendingChangesRef.current).length > 0
                    ) {
                      // We hebben pending changes - smart merge
                      console.log(
                        "Server update while having local changes - smart merging..."
                      );
                      const merged = smartMerge(finalData, {
                        ...activeSaveRef.current,
                        ...pendingChangesRef.current,
                      });
                      // ✅ FIX: Check mounted before state update
                      if (isMounted) {
                        const volgende = pasDaglaagToe(sanitizeData(metArchief(merged)), dagDocsRef.current);
                        // Lage prioriteit: typen en tikken gaan vóór
                        startTransition(() => setAppData(volgende));
                      }
                      lastDataHashRef.current = getDataHash(merged);
                    } else {
                      // Geen pending changes - gewoon updaten
                      console.log(
                        `Server update (v${serverVersion}) by ${serverEditBy}`
                      );
                      // ✅ FIX: Check mounted before state update
                      if (isMounted) {
                        const volgende = pasDaglaagToe(sanitizeData(metArchief(finalData)), dagDocsRef.current);
                        startTransition(() => setAppData(volgende));
                      }
                      lastDataHashRef.current = newHash;
                    }
                    updateDebounceRef.current = null;
                  }, 50); // korte bundeling van updates van anderen (zo goed als live)
                } else if (lastServerVersionRef.current === 0) {
                  // Eerste load
                  console.log(`📥 Initial load (v${serverVersion})`);
                  // ✅ FIX: Check mounted before state update
                  if (isMounted) {
                    setAppData(pasDaglaagToe(sanitizeData(metArchief(finalData)), dagDocsRef.current));
                  }
                  lastDataHashRef.current = getDataHash(finalData);
                }
                // Als serverVersion === lastServerVersionRef is het onze eigen save, negeren

                lastServerVersionRef.current = serverVersion;
                // Lokale kopieën (localStorage + IndexedDB) samengevoegd en pas
                // als de telefoon even niets te doen heeft: niet bij elke
                // wijziging van een collega de hele app opnieuw wegschrijven.
                lokaleKopieData.current = finalData;
                if (!lokaleKopieTimer.current) {
                  lokaleKopieTimer.current = setTimeout(() => {
                    lokaleKopieTimer.current = null;
                    const schrijf = () => {
                      const sanitizedData = sanitizeData(lokaleKopieData.current);
                      DATA_PROTECTION.saveLastKnownGood(sanitizedData);
                      offlineManager
                        .saveData("appData", sanitizedData)
                        .catch((err) => console.warn("⚠️ Failed to cache in IndexedDB:", err));
                    };
                    if ("requestIdleCallback" in window) window.requestIdleCallback(schrijf, { timeout: 4000 });
                    else schrijf();
                  }, 4000);
                }
                // ✅ FIX: Check mounted before state update
                if (isMounted) {
                  setLoading(false);
                  setVoorlopig(false);
                  setSyncStatus("synced");
                }
              } else {
                console.warn("⚠️ Firestore data looks corrupted");
                await attemptRecovery();
              }
            } else {
              console.log("📝 No Firestore data, trying local recovery...");
              await attemptRecovery();
            }
          },
          (error) => {
            // ✅ FIX: Error handler added
            console.error("❌ Firestore error:", error);
            // ✅ FIX: Check mounted before state update
            if (isMounted) {
              setSyncStatus("offline");
            }
            attemptRecovery(); // Don't await in error callback
          }
        );

        // 📡 Realtime listener op het STATIC-doc (staffList, settings, ...).
        // Dit ontbrak: zonder listener raakte de lokale staffList verouderd,
        // waardoor een open tab bij opslaan de hele lijst overschreef en
        // nieuwe gebruikers + recente pincodes/geboortedata wegvielen.
        unsubscribeStatic = onSnapshot(
          staticDocRef,
          (snap) => {
            if (!isMounted || !snap.exists()) return;
            const fresh = snap.data();
            staticData = fresh; // houd closure-merge van master-listener actueel
            setAppData((prev) => {
              if (!prev || !prev.staffList) return prev; // wacht op eerste load
              let next = prev;
              for (const field of STATIC_FIELDS) {
                if (!(field in fresh)) continue;
                // Niet overschrijven wat de gebruiker nu lokaal bewerkt of opslaat
                if (
                  (pendingChangesRef.current &&
                    field in pendingChangesRef.current) ||
                  (activeSaveRef.current && field in activeSaveRef.current)
                )
                  continue;
                // Alleen updaten als de waarde echt verschilt (voorkomt re-render)
                if (
                  JSON.stringify(prev[field]) === JSON.stringify(fresh[field])
                )
                  continue;
                if (next === prev) next = { ...prev };
                next[field] = fresh[field];
              }
              return next;
            });
          },
          (error) => {
            console.warn("⚠️ Static doc listener error:", error);
          }
        );
      } catch (error) {
        console.error("❌ Firebase init error:", error);
        attemptRecovery(); // Don't await in catch block
      }
    };

    // RECOVERY FUNCTION
    const attemptRecovery = async () => {
      // ✅ FIX: Check if component is still mounted
      if (!isMounted) {
        console.log("⚠️ Component unmounted, skipping recovery");
        return;
      }

      // 🎯 PRIORITY 1: Check for full backup restore
      const fullBackupRestore = localStorage.getItem("cp_backup_restored");
      if (fullBackupRestore === "true") {
        console.log("🔄 Full backup restore detected!");
        const restoredData = DATA_PROTECTION.recoverFromLocal();
        if (
          restoredData &&
          restoredData.staffList &&
          restoredData.staffList.length > 0
        ) {
          console.log("✅ Loading restored backup from localStorage");
          // ✅ FIX: Check mounted before state update
          if (isMounted) {
            setAppData(restoredData);
            setRecoveryMode(false); // Not recovery mode, this is the real data!
            triggerAlert("Backup succesvol hersteld! Data geladen.");
            setLoading(false);
          }

          // Clear the restore flag after successful load
          localStorage.setItem("cp_backup_restored", "loaded");
          return;
        }
      }

      // 🎯 PRIORITY 2: Try IndexedDB first (most recent offline data)
      try {
        console.log("🔍 Checking IndexedDB for offline data...");
        const offlineData = await offlineManager.getData("appData");
        // ✅ FIX: Check mounted after async operation
        if (!isMounted) return;

        if (
          offlineData &&
          offlineData.staffList &&
          offlineData.staffList.length > 0
        ) {
          console.log("✅ Loading data from IndexedDB (offline storage)");
          // ✅ FIX: Check mounted before state update
          if (isMounted) {
            setAppData(sanitizeData(offlineData));
            setRecoveryMode(false);
            triggerAlert("Data geladen van offline opslag");
            setLoading(false);
          }
          DATA_PROTECTION.logRecovery("indexeddb", true);
          return;
        }
      } catch (error) {
        console.warn("⚠️ Failed to load from IndexedDB:", error);
      }

      // 🎯 PRIORITY 3: Try localStorage backup
      const localData = DATA_PROTECTION.recoverFromLocal();

      if (localData) {
        // ✅ FIX: Check mounted before state update
        if (isMounted) {
          setAppData(localData);
          setRecoveryMode(true);
          triggerAlert("Data hersteld van lokale backup");
          setLoading(false);
        }
        return;
      }

      console.log("ℹ️ Using initial data");
      // ✅ FIX: Check mounted before state update
      if (isMounted) {
        setAppData(INITIAL_DATA);
        setLoading(false);
      }
      DATA_PROTECTION.logRecovery("initial-data", true);
    };

    initializeData();

    return () => {
      // ✅ FIX: Set isMounted to false to prevent state updates
      console.log("🧹 Cleaning up initializeData useEffect");
      isMounted = false;

      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (error) {
          console.error("Error unsubscribing:", error);
        }
      }
      if (unsubscribeStatic) {
        try {
          unsubscribeStatic();
        } catch (error) {
          console.error("Error unsubscribing static:", error);
        }
      }
      // 🔒 Cleanup: cancel pending saves
      if (pendingSaveRef.current) {
        clearTimeout(pendingSaveRef.current);
      }
      // 🔒 Cleanup: cancel pending debounced updates
      if (updateDebounceRef.current) {
        clearTimeout(updateDebounceRef.current);
      }
    };
  }, []);

  // ✅ FIX: Loading timeout - prevent app from being stuck in loading state
  useEffect(() => {
    if (!loading) return;

    console.log("⏱️ Starting loading timeout (10 seconds)");
    const loadingTimeout = setTimeout(() => {
      // Gebruik functionele update om stale closure te vermijden
      setLoading((currentLoading) => {
        if (!currentLoading) return currentLoading; // Al geladen, niets doen
        console.warn("⚠️ Loading timeout - forcing ready state");
        // Laad cache alleen als we echt nog aan het laden zijn
        offlineManager
          .getData("appData")
          .then((cachedData) => {
            if (cachedData && Object.keys(cachedData).length > 5) {
              console.log("✅ Using cached data after timeout");
              setAppData(cachedData);
            }
          })
          .catch((err) => console.warn("Could not load cached data:", err));
        return false;
      });
    }, 10000); // 10 second timeout

    return () => {
      clearTimeout(loadingTimeout);
    };
  }, [loading]);

  // 🛡️ Ref voor auto-save (voorkomt stale closure)
  const appDataRef = useRef(appData);
  useEffect(() => {
    appDataRef.current = appData;
  }, [appData]);

  // 🔔 CHECK POPUP MELDINGEN - toon bij data-update als er een ongelezen popup is
  useEffect(() => {
    if (
      !isLoggedIn ||
      !loggedInUserName ||
      isExternalPromoMode ||
      !appData.settings?.popupNotifications
    )
      return;
    const now = new Date();
    const unread = (appData.settings.popupNotifications || []).find((n) => {
      if (n.kind === "update") return false; // alleen bedoeld voor oude app-versies
      if (n.dismissedBy?.includes(loggedInUserName)) return false;
      if (dismissedPopupIds.current.has(n.id)) return false;
      if (n.expiresAt && new Date(n.expiresAt) < now) return false;
      return true;
    });
    if (unread && (!activePopupNotif || activePopupNotif.id !== unread.id)) {
      setActivePopupNotif(unread);
    }
  }, [appData.settings?.popupNotifications, isLoggedIn, loggedInUserName]);

  // 👋 CHECK @MENTION NOTIFICATIES
  useEffect(() => {
    if (!isLoggedIn || !loggedInUserName || isExternalPromoMode) return;
    const unread = (appData.notifications || []).find(
      (n) =>
        n.type === "mention" &&
        n.mentionedUser === loggedInUserName &&
        !n.readBy?.includes(loggedInUserName) &&
        !dismissedMentionIds.current.has(n.id)
    );
    if (
      unread &&
      (!activeMentionNotif || activeMentionNotif.id !== unread.id)
    ) {
      setActiveMentionNotif(unread);
    }
  }, [appData.notifications, isLoggedIn, loggedInUserName]);

  // 🏖️ CHECK VERLOFAANVRAAG-MELDINGEN (alleen voor FM, nooit op het
  // onbemande promotiescherm — daar is userRole/isLoggedIn kunstmatig "fm")
  useEffect(() => {
    if (
      !isLoggedIn ||
      !loggedInUserName ||
      isExternalPromoMode ||
      userRole !== "fm"
    )
      return;
    const unread = (appData.notifications || []).find(
      (n) =>
        n.type === "verlof_aanvraag" &&
        n.employeeName !== loggedInUserName &&
        !n.readBy?.includes(loggedInUserName) &&
        !dismissedVerlofIds.current.has(n.id)
    );
    if (unread && (!activeVerlofNotif || activeVerlofNotif.id !== unread.id)) {
      setActiveVerlofNotif(unread);
    }
  }, [appData.notifications, isLoggedIn, loggedInUserName, userRole]);

  // 📣 OPEN DIENST: medewerker krijgt een aangeboden dienst te zien
  useEffect(() => {
    if (!isLoggedIn || !loggedInUserName || isExternalPromoMode) return;
    const vandaag = toIsoDate(new Date());
    const open = (appData.notifications || []).find((n) => {
      if (n.type !== "open_dienst") return false;
      if (!(n.aangebodenAan || []).includes(loggedInUserName)) return false;
      if (n.readBy?.includes(loggedInUserName)) return false;
      if (dismissedOpenIds.current.has(n.id)) return false;
      if (n.date < vandaag) return false;
      const d = (appData.roosterDiensten?.[n.date] || []).find((x) => x.id === n.dienstId);
      return d && d.open && (d.assigned || []).length < (d.slots || 1);
    });
    if (open && (!activeOpenNotif || activeOpenNotif.id !== open.id)) {
      setActiveOpenNotif(open);
    } else if (!open && activeOpenNotif) {
      setActiveOpenNotif(null);
    }
  }, [appData.notifications, appData.roosterDiensten, isLoggedIn, loggedInUserName]);

  // 📣 OPEN DIENST GEPAKT: korte melding voor de FM
  useEffect(() => {
    if (!isLoggedIn || !loggedInUserName || isExternalPromoMode || userRole !== "fm") return;
    const nieuw = (appData.notifications || []).filter(
      (n) =>
        n.type === "open_dienst_gepakt" &&
        !n.readBy?.includes(loggedInUserName) &&
        !dismissedOpenIds.current.has(n.id)
    );
    if (!nieuw.length) return;
    nieuw.forEach((n) => dismissedOpenIds.current.add(n.id));
    const n = nieuw[0];
    const dag = new Date(n.date + "T12:00:00").toLocaleDateString("nl-NL", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
    showAlert(
      nieuw.length === 1
        ? `${n.employeeName} heeft de open dienst gepakt: ${dag}, ${n.title} ${n.start}–${n.end}`
        : `${nieuw.length} open diensten zijn gepakt (o.a. ${n.employeeName}, ${dag})`
    );
    const ids = new Set(nieuw.map((x) => x.id));
    saveData({
      notifications: (appData.notifications || []).map((x) =>
        ids.has(x.id) ? { ...x, readBy: [...(x.readBy || []), loggedInUserName] } : x
      ),
    });
  }, [appData.notifications, isLoggedIn, loggedInUserName, userRole]);

  // 🛡️ AUTO-SAVE: Elke 10 seconden (FIXED: geen dependency op appData)
  useEffect(() => {
    autoSaveTimerRef.current = DATA_PROTECTION.startAutoSave(
      () => appDataRef.current
    );

    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
  }, []); // Lege dependency array - interval wordt maar 1x aangemaakt

  // 🔒 ONLINE/OFFLINE DETECTOR
  useEffect(() => {
    const handleOnline = () => {
      console.log("🌐 Back online");
      setSyncStatus("synced");
      // Als er pending changes zijn, probeer opnieuw te saven
      if (Object.keys(pendingChangesRef.current).length > 0) {
        saveData({});
      }
    };

    const handleOffline = () => {
      console.log("📴 Gone offline");
      setSyncStatus("offline");
      setShowSyncIndicator(true);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Check initiële status
    if (!navigator.onLine) {
      setSyncStatus("offline");
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // 🔧 BACKUP CHUNKING HELPERS (voor >1MB data)
  const MAX_DOC_SIZE = 900000; // 900KB per chunk (veilige marge onder 1MB)

  const splitIntoChunks = (data) => {
    const dataStr = JSON.stringify(data);
    const dataSize = new Blob([dataStr]).size;

    // Als kleiner dan limiet, geen chunking nodig
    if (dataSize < MAX_DOC_SIZE) {
      return { chunks: [data], metadata: { chunked: false, size: dataSize } };
    }

    console.log(`📦 Data is ${dataSize} bytes, splitting into chunks...`);

    // Split grote arrays in kleinere delen
    const chunks = [];
    const keys = Object.keys(data);
    let currentChunk = {};
    let currentSize = 0;

    for (const key of keys) {
      const value = data[key];
      const valueStr = JSON.stringify({ [key]: value });
      const valueSize = new Blob([valueStr]).size;

      // Als deze value alleen al te groot is, split het verder
      if (valueSize > MAX_DOC_SIZE && Array.isArray(value)) {
        // Split array in stukken
        const itemsPerChunk = Math.ceil(
          value.length / Math.ceil(valueSize / MAX_DOC_SIZE)
        );
        for (let i = 0; i < value.length; i += itemsPerChunk) {
          const arrayChunk = value.slice(i, i + itemsPerChunk);
          chunks.push({
            [`${key}_part${Math.floor(i / itemsPerChunk)}`]: arrayChunk,
          });
        }
        continue;
      }

      // Als toevoegen van deze key de chunk te groot maakt, start nieuwe chunk
      if (
        currentSize + valueSize > MAX_DOC_SIZE &&
        Object.keys(currentChunk).length > 0
      ) {
        chunks.push(currentChunk);
        currentChunk = {};
        currentSize = 0;
      }

      currentChunk[key] = value;
      currentSize += valueSize;
    }

    // Voeg laatste chunk toe
    if (Object.keys(currentChunk).length > 0) {
      chunks.push(currentChunk);
    }

    console.log(`✅ Split into ${chunks.length} chunks`);
    return {
      chunks,
      metadata: {
        chunked: true,
        totalChunks: chunks.length,
        originalSize: dataSize,
        timestamp: new Date().toISOString(),
      },
    };
  };

  // AUTOMATIC BACKUP LOGIC - FOR ALL USERS (Optimized with Chunking)
  useEffect(() => {
    const performAutoBackup = async () => {
      // Check if we should run: db ready, not loading, and haven't run yet this session
      if (db && appId && appData && !loading && !hasBackedUp.current) {
        const todayKey = toIsoDate(new Date());
        try {
          const backupRef = doc(
            db,
            "artifacts",
            appId,
            "public",
            "data",
            "backups",
            todayKey
          );
          // Bestaat de back-up van vandaag al? (bij een gesplitste back-up
          // bestaat alleen het _meta-document, niet het document zelf)
          const metaCheckRef = doc(db, "artifacts", appId, "public", "data", "backups", `${todayKey}_meta`);
          const [backupSnapMain, backupSnapMeta] = await Promise.all([getDoc(backupRef), getDoc(metaCheckRef)]);
          const backupSnap = { exists: () => backupSnapMain.exists() || backupSnapMeta.exists() };

          if (!backupSnap.exists()) {
            console.log("Creating automatic daily backup...");

            // Split data into chunks if needed
            const { chunks, metadata } = splitIntoChunks(appData);

            if (metadata.chunked) {
              // Save metadata
              const metaRef = doc(
                db,
                "artifacts",
                appId,
                "public",
                "data",
                "backups",
                `${todayKey}_meta`
              );
              await setDoc(metaRef, metadata);

              // Save each chunk
              for (let i = 0; i < chunks.length; i++) {
                const chunkRef = doc(
                  db,
                  "artifacts",
                  appId,
                  "public",
                  "data",
                  "backups",
                  `${todayKey}_chunk${i}`
                );
                await setDoc(chunkRef, chunks[i]);
              }

              console.log(
                `Backup saved in ${chunks.length} chunks (${metadata.originalSize} bytes total)`
              );
            } else {
              // Single document backup (< 900KB)
              await setDoc(backupRef, appData);

              // Save simple metadata
              const metaRef = doc(
                db,
                "artifacts",
                appId,
                "public",
                "data",
                "backups",
                `${todayKey}_meta`
              );
              await setDoc(metaRef, { chunked: false, size: metadata.size });

              console.log(
                `Backup saved as single document (${metadata.size} bytes)`
              );
            }
          }
          hasBackedUp.current = true; // Mark as done for this session
        } catch (e) {
          console.error("⚠️ Backup creation failed (non-critical):", e);
        }
      }
    };
    performAutoBackup();
  }, [db, appId, loading]); // Removed userRole restriction

  // Voeg nieuwe wijzigingen samen met wijzigingen die nog op opslaan wachten
  // (zelfde regels als bij het verzamelen: per datum / per persoon samenvoegen).
  const mergePending = (basePending, newData) => {
      // 🔒 STAP 1: Verzamel wijzigingen met DEEP MERGE (voor debouncing)
      // ✅ FIX: Voor objecten zoals workSchedule, merge de datums
      const updatedPending = { ...basePending };

      for (const key of Object.keys(newData)) {
        const newVal = newData[key];
        const pendingVal = basePending[key];

        // Voor objecten (zoals workSchedule): deep merge
        if (
          typeof pendingVal === "object" &&
          pendingVal !== null &&
          typeof newVal === "object" &&
          newVal !== null &&
          !Array.isArray(pendingVal) &&
          !Array.isArray(newVal)
        ) {
          if (DEEP_MERGE_FIELDS.has(key)) {
            updatedPending[key] = deepMergePlain(pendingVal, newVal);
          } else if (key === "handover" || key === "timeClock") {
            // handover/timeClock: 2-niveau merge (datum → type/persoon) zodat een
            // delta de al-gequeuede entries van dezelfde dag niet wegvaagt
            const merged = { ...pendingVal };
            for (const [dk, dv] of Object.entries(newVal)) {
              merged[dk] =
                dv &&
                typeof dv === "object" &&
                !Array.isArray(dv) &&
                pendingVal[dk] &&
                typeof pendingVal[dk] === "object" &&
                !Array.isArray(pendingVal[dk])
                  ? key === "timeClock"
                    ? mergeKlokDag(pendingVal[dk], dv)
                    : { ...pendingVal[dk], ...dv }
                  : dv;
            }
            updatedPending[key] = merged;
          } else if (
            key === "staffAvailability" ||
            key === "staffAvailabilityLocks"
          ) {
            // 2-niveau merge (persoon → datums): zonder dit overschrijft een
            // 2e goedkeuring/afwijzing voor dezelfde persoon, binnen dezelfde
            // debounce-window, de 1e wijziging van die persoon (shallow merge
            // zou de hele persoon-key vervangen i.p.v. per datum samen te
            // voegen) — dat was de oorzaak van "verdwijnende" goedkeuringen.
            const merged = { ...pendingVal };
            for (const [person, dates] of Object.entries(newVal)) {
              merged[person] =
                dates &&
                typeof dates === "object" &&
                !Array.isArray(dates) &&
                pendingVal[person] &&
                typeof pendingVal[person] === "object" &&
                !Array.isArray(pendingVal[person])
                  ? { ...pendingVal[person], ...dates }
                  : dates;
            }
            updatedPending[key] = merged;
          } else {
            updatedPending[key] = { ...pendingVal, ...newVal };
          }
        } else {
          updatedPending[key] = newVal;
        }
      }

      return updatedPending;
  };

  const saveData = async (newData, opts = {}) => {
    // Nog niet bijgewerkt met Firebase: niet opslaan (zou oudere gegevens
    // kunnen terugzetten). Alleen een melding als iemand net zelf iets deed.
    if (voorlopigRef.current) {
      if (Date.now() - laatsteTikRef.current < 2500 && view !== "login")
        cpToast("Even geduld, de app haalt nog de nieuwste gegevens op…");
      return;
    }
    // ✅ FIX: Wrap entire function in try-catch
    try {
      // ✅ FIX: Early return bij invalid data
      if (!newData || typeof newData !== "object") {
        console.error("❌ Invalid newData provided to saveData");
        return;
      }

      // 🔄 Vernieuw login tijd bij activiteit (voorkomt timeout tijdens werk)
      if (isLoggedIn && localStorage.getItem("cp_loggedInUser")) {
        localStorage.setItem("cp_loginTime", new Date().toISOString());
      }

      // ✅ Per-item velden: onthoud alleen wat deze save echt wijzigt (zie PATCH_FIELDS)
      for (const f of Object.keys(PATCH_FIELDS)) {
        if (newData[f] !== undefined && newData[f] !== null) {
          registerFieldDiff(f, appData[f], newData[f]);
        }
      }

      // 🔒 STAP 1: Verzamel wijzigingen met DEEP MERGE (voor debouncing)
      const updatedPending = mergePending(pendingChangesRef.current, newData);
      pendingChangesRef.current = updatedPending;

      // 🔒 STAP 2: Update lokale state DIRECT met DEEP MERGE (snelle UI)
      // ✅ FIX: Voor objecten zoals workSchedule, doe een deep merge
      const optimisticUpdate = { ...appData };

      for (const key of Object.keys(pendingChangesRef.current)) {
        const pendingVal = pendingChangesRef.current[key];
        const currentVal = appData[key];

        if (PATCH_FIELDS[key]) {
          optimisticUpdate[key] = applyFieldPatches(
            key,
            currentVal !== undefined && currentVal !== null ? currentVal : pendingVal
          );
          continue;
        }

        // Voor objecten (zoals workSchedule): deep merge
        if (
          typeof currentVal === "object" &&
          currentVal !== null &&
          typeof pendingVal === "object" &&
          pendingVal !== null &&
          !Array.isArray(currentVal) &&
          !Array.isArray(pendingVal)
        ) {
          // staffAvailability/staffAvailabilityLocks: 2-niveau merge (persoon → datums)
          if (DEEP_MERGE_FIELDS.has(key)) {
            optimisticUpdate[key] = deepMergePlain(currentVal, pendingVal);
          } else if (
            key === "staffAvailability" ||
            key === "staffAvailabilityLocks"
          ) {
            const merged = { ...currentVal };
            for (const [person, dates] of Object.entries(pendingVal)) {
              merged[person] =
                typeof dates === "object" && dates !== null
                  ? { ...(currentVal[person] || {}), ...dates }
                  : dates;
            }
            optimisticUpdate[key] = merged;
          } else if (key === "handover" || key === "timeClock") {
            // handover/timeClock: 2-niveau merge (datum → type/persoon)
            const merged = { ...currentVal };
            for (const [dk, dv] of Object.entries(pendingVal)) {
              merged[dk] =
                dv &&
                typeof dv === "object" &&
                !Array.isArray(dv) &&
                currentVal[dk] &&
                typeof currentVal[dk] === "object" &&
                !Array.isArray(currentVal[dk])
                  ? key === "timeClock"
                    ? mergeKlokDag(currentVal[dk], dv)
                    : { ...currentVal[dk], ...dv }
                  : dv;
            }
            optimisticUpdate[key] = merged;
          } else {
            optimisticUpdate[key] = { ...currentVal, ...pendingVal };
          }
        } else {
          optimisticUpdate[key] = pendingVal;
        }
      }

      optimisticUpdate.lastUpdate = new Date().toISOString();
      optimisticUpdate._lastEditBy = loggedInUserName || "anonymous";

      // Validatie check
      if (!optimisticUpdate || Object.keys(optimisticUpdate).length < 5) {
        console.error("🔥 ⚠️ Invalid data detected, aborting save");
        setDataLossWarning(true);
        return;
      }

      setAppData(optimisticUpdate);

      DATA_PROTECTION.saveLastKnownGoodLater(optimisticUpdate);
      setSyncStatus("saving");
      setShowSyncIndicator(true);

      // 🔒 STAP 3: Debounce - wacht 300ms voor meer wijzigingen
      if (pendingSaveRef.current) {
        clearTimeout(pendingSaveRef.current);
      }

      pendingSaveRef.current = setTimeout(async () => {
        // Voorkom dubbele saves (maar een save die > 30 s "loopt" is vastgelopen)
        if (isSavingRef.current && Date.now() - saveStartedAtRef.current < 30000) {
          console.log("⏳ Save already in progress, queuing...");
          return;
        }

        isSavingRef.current = true;
        saveStartedAtRef.current = Date.now();
        let saveFailed = false;
        const patchSnaps = {};
        for (const f of Object.keys(PATCH_FIELDS))
          patchSnaps[f] = new Map(fieldPatches[f]);
        const changesToSave = { ...pendingChangesRef.current };
        pendingChangesRef.current = {}; // Reset pending changes
        activeSaveRef.current = changesToSave; // Bijhouden voor snapshot merge

        // LAAG 2 + offline-kopie: direct ná het versturen (niet ervoor), zodat
        // een klik meteen naar de server gaat. Bij geen verbinding gebeurt het
        // gewoon even later.
        const lokaalBewaren = () => {
          DATA_PROTECTION.createEmergencySnapshot(optimisticUpdate);
          Promise.resolve()
            .then(() => offlineManager.saveData("appData", optimisticUpdate))
            .catch((error) => console.warn("⚠️ Local cache failed:", error));
        };
        if (!((isLoggedIn || user) && db && offlineOnline)) lokaalBewaren();

        // ⚡ Eerst (zonder wachten) het kleine dagdocument: collega's zien het meteen
        if ((isLoggedIn || user) && db && offlineOnline) {
          try {
            const perDag = bouwDaglaag(changesToSave, patchSnaps.checklists);
            for (const [dk, payload] of Object.entries(perDag)) {
              setDoc(
                doc(db, "artifacts", appId, "public", "data", "cp_dag", dk),
                payload,
                { merge: true }
              ).catch((e) => console.warn("⚡ daglaag niet opgeslagen:", e?.message || e));
            }
          } catch (e) {
            console.warn("⚡ daglaag overgeslagen:", e);
          }
        }

        // 🔒 STAP 4: Save naar Firebase met TRANSACTION (voorkomt race conditions)
        // Check of we ingelogd zijn (via isLoggedIn state) OF als we een Firebase user hebben
        if ((isLoggedIn || user) && db && offlineOnline) {
          console.log("💾 Saving to Firebase...", {
            isLoggedIn,
            hasUser: !!user,
            hasDb: !!db,
            isOnline: offlineOnline,
          });
          const docRef = doc(
            db,
            "artifacts",
            appId,
            "public",
            "data",
            "cp_live_data",
            "master"
          );

          // 📦 Static doc referentie voor zelden-veranderende data
          const staticDocRef = doc(
            db,
            "artifacts",
            appId,
            "public",
            "data",
            "cp_static_data",
            "master"
          );

          // 📦 Splits wijzigingen in statisch vs live
          const staticChanges = {};
          const liveChanges = {};
          for (const [k, v] of Object.entries(changesToSave)) {
            if (STATIC_FIELDS.has(k)) staticChanges[k] = v;
            else liveChanges[k] = v;
          }
          const hasStaticChanges = Object.keys(staticChanges).length > 0;
          const hasLiveChanges = Object.keys(liveChanges).length > 0;

          // Retry logic met exponential backoff
          const maxRetries = 3;
          let success = false;

          for (let attempt = 0; attempt < maxRetries && !success; attempt++) {
            try {
              if (attempt > 0) {
                // Exponential backoff: 500ms, 1000ms, 2000ms
                await new Promise((r) =>
                  setTimeout(r, Math.pow(2, attempt) * 250)
                );
              }

              // 📦 Sla statische wijzigingen op in eigen doc
              if (hasStaticChanges) {
                const cleanedStatic = removeUndefined(staticChanges);

                // 🔒 staffList per persoon mergen binnen een transaction.
                // Voorheen werd de hele array blind overschreven (merge:true op
                // een array = volledige vervanging). Een verouderde tab wiste zo
                // nieuwe gebruikers en recente pincodes/geboortedata. Nu lezen we
                // eerst de server-lijst en mergen we per naam.
                if (Array.isArray(cleanedStatic.staffList)) {
                  await runTransaction(db, async (transaction) => {
                    const snap = await transaction.get(staticDocRef);
                    const serverStatic = snap.exists() ? snap.data() : {};
                    const serverList = Array.isArray(serverStatic.staffList)
                      ? serverStatic.staffList
                      : [];
                    const localList = cleanedStatic.staffList;

                    let mergedList;
                    if (localList.length === 0 && serverList.length > 0) {
                      // Nooit per ongeluk de hele lijst legen
                      mergedList = serverList;
                    } else {
                      const serverByName = new Map(
                        serverList.map((s) => [s.name, s])
                      );
                      // Per persoon veld-merge zodat een gelijktijdige edit van
                      // iemand anders op dezelfde persoon niet verloren gaat.
                      mergedList = localList.map((local) => {
                        const srv = serverByName.get(local.name);
                        if (!srv) return local;
                        const uit = { ...srv, ...local };
                        // 📸 Profielfoto: de nieuwste wint (fotoAt), anders zette een tab met
                        // een oude kopie de nieuwe foto van een collega stilzwijgend terug
                        if ((srv.fotoAt || 0) > (local.fotoAt || 0)) {
                          uit.profilePhoto = srv.profilePhoto;
                          uit.fotoVrij = srv.fotoVrij;
                          uit.fotoKleur = srv.fotoKleur;
                          uit.fotoAt = srv.fotoAt;
                        }
                        return uit;
                      });
                      // 🛟 UNION-MERGE: de lokale lijst is NIET meer leidend voor
                      // het lidmaatschap. Wie op de server staat maar niet in deze
                      // tab, blijft staan — tenzij de FM hem hier expliciet via de
                      // prullenbak heeft verwijderd. Dit was de oorzaak van het
                      // verdwijnen van net-toegevoegde collega's + hun profielfoto.
                      const localNames = new Set(localList.map((s) => s.name));
                      const rescued = serverList.filter(
                        (s) =>
                          s &&
                          s.name &&
                          !localNames.has(s.name) &&
                          !staffRemovalIntent.has(s.name)
                      );
                      if (rescued.length > 0) {
                        console.warn(
                          `staffList: ${
                            rescued.length
                          } medewerker(s) behouden die in deze sessie ontbraken: ${rescued
                            .map((s) => s.name)
                            .join(", ")}`
                        );
                        mergedList = [...mergedList, ...rescued];
                      }
                    }

                    transaction.set(
                      staticDocRef,
                      { ...cleanedStatic, staffList: mergedList },
                      { merge: true }
                    );
                  });
                  // Intentie is verwerkt — pas NA de transaction leegmaken,
                  // want een transaction kan intern opnieuw uitgevoerd worden.
                  staffRemovalIntent.clear();
                } else {
                  await setDoc(staticDocRef, cleanedStatic, { merge: true });
                }
              }

              // 🔄 Sla live wijzigingen op via transaction
              if (hasLiveChanges) {
                await runTransaction(db, async (transaction) => {
                  // Lees HUIDIGE server data
                  const serverDoc = await transaction.get(docRef);
                  const serverData = serverDoc.exists() ? serverDoc.data() : {};

                  // 🔒 SMART MERGE: Deep merge voor arrays (voorkomt data verlies)
                  // Alleen live velden in de master transaction
                  const merged = smartMerge(serverData, liveChanges);
                  merged.lastUpdate = new Date().toISOString();
                  merged._version = (serverData._version || 0) + 1;
                  merged._lastEditBy = loggedInUserName || "anonymous";

                  // Verwijder eventuele statische velden die nog in master zitten
                  for (const field of STATIC_FIELDS) {
                    delete merged[field];
                  }

                  // Check of data geldig is
                  if (Object.keys(merged).length < 3) {
                    throw new Error("Merged data invalid");
                  }

                  // 🔥 CRITICAL: Remove all undefined values for Firebase compatibility
                  // 🗜️ Strip default velden uit schedule items voor kleinere payload
                  if (merged.schedule && Array.isArray(merged.schedule)) {
                    merged.schedule = merged.schedule.map(
                      stripScheduleDefaults
                    );
                  }
                  // 📦 Prune oude data vóór wegschrijven — zowel 1x per dag ALS
                  // altijd wanneer de payload richting de Firestore 1MB-limiet
                  // kruipt. Alleen op datum prunen was niet genoeg: het document
                  // groeit gedurende de dag weer aan (rooster/checklist/handover),
                  // waardoor latere writes diezelfde dag alsnog boven de 1MB
                  // uitkwamen en STIL faalden na 3 pogingen — terwijl de lokale
                  // (optimistic) UI al wél "opgeslagen" liet zien. Zodra daarna
                  // een andere update binnenkwam, sprong de net-goedgekeurde
                  // wijziging dus terug naar de oude serverstand.
                  const todayStr = new Date().toISOString().split("T")[0];
                  // Programma zonder dubbele bibliotheekteksten opslaan
                  if (Array.isArray(merged.schedule)) {
                    merged.schedule = scheduleZonderLibTeksten(merged.schedule, appDataRef.current?.activityLibrary);
                  }
                  let dataToWrite = merged;
                  const estimatedSize = new Blob([
                    JSON.stringify(removeUndefined(merged)),
                  ]).size;
                  const SAFE_SIZE_LIMIT = 900000; // ruime marge onder de 1.048.576 limiet
                  // Lukte het archiveren de vorige poging niet, dan alleen nog
                  // prunen als het document echt te groot wordt.
                  // (Indexregels: de zware velden staan in Firestore uitgezonderd van
                  // indexering, dus daar hoeft hier niet meer op gelet te worden.)
                  if (
                    (lastPruneRef.current !== todayStr && attempt === 0) ||
                    estimatedSize > SAFE_SIZE_LIMIT
                  ) {
                    lastPruneRef.current = todayStr;
                    // Begin met de normale bewaartermijn; is het document dan nog
                    // te groot of heeft het te veel indexregels, korter bewaren
                    // (de rest gaat naar het maand-archief en wordt op de
                    // achtergrond weer ingeladen, dus schermen missen niets).
                    let pruned = pruneDataForLiveDoc(merged);
                    if (estimatedSize > SAFE_SIZE_LIMIT) {
                      for (const dagen of [14, 7]) {
                        if (new Blob([JSON.stringify(pruned.liveData)]).size <= SAFE_SIZE_LIMIT) break;
                        pruned = pruneDataForLiveDoc(merged, dagen);
                      }
                    }
                    const { liveData, archiveByMonth } = pruned;
                    dataToWrite = liveData;
                    // Archief in DEZELFDE transaction wegschrijven: de live data
                    // wordt alleen ingekort als het archief ook echt opgeslagen is.
                    const schrijfArchief = await archiveInTransaction(
                      transaction,
                      archiveByMonth
                    );
                    schrijfArchief();
                    console.log(
                      `Live doc gepruned: ${PRUNE_DAYS} dagen bewaard (${Math.round(
                        estimatedSize / 1024
                      )}KB voor prune)`
                    );
                  }
                  const cleanedData = removeUndefined(dataToWrite);

                  // 🛡️ Laatste vangnet: als het document ondanks pruning nog
                  // steeds te groot is, direct falen met een duidelijke fout
                  // i.p.v. 3x stil te falen — zo zie je het probleem meteen in
                  // de syncindicator/toast in plaats van een verdwijnende
                  // goedkeuring achteraf.
                  const finalSize = new Blob([JSON.stringify(cleanedData)])
                    .size;
                  if (finalSize > 1048576) {
                    throw new Error(
                      `Document te groot (${Math.round(
                        finalSize / 1024
                      )}KB) na pruning — neem contact op met de ontwikkelaar`
                    );
                  }

                  transaction.set(docRef, { ...cleanedData, _serverAt: serverTimestamp() });

                  // Update lokale versie tracking
                  lastServerVersionRef.current = cleanedData._version;
                });
              } else if (hasStaticChanges && !hasLiveChanges) {
                // Alleen static changes: bump version in live doc voor sync-detectie
                await runTransaction(db, async (transaction) => {
                  const serverDoc = await transaction.get(docRef);
                  const serverData = serverDoc.exists() ? serverDoc.data() : {};
                  // Verwijder eventuele verouderde statische velden uit master,
                  // zodat ze de leidende static-doc-waarden niet meer overschrijven.
                  const liveClean = { ...serverData };
                  for (const field of STATIC_FIELDS) {
                    delete liveClean[field];
                  }
                  transaction.set(docRef, {
                    ...liveClean,
                    lastUpdate: new Date().toISOString(),
                    _version: (serverData._version || 0) + 1,
                    _lastEditBy: loggedInUserName || "anonymous",
                  });
                  lastServerVersionRef.current = (serverData._version || 0) + 1;
                });
              }
              success = true;
              saveRetryCount.current = 0;
              setSyncStatus("synced");
              // Intentie pas NA succesvolle save leegmaken (transaction kan
              // intern retryen) en alleen als 'orders' ook echt is meegestuurd.
              if (Object.prototype.hasOwnProperty.call(liveChanges, "orders")) {
                orderRemovalIntent.clear();
              }
              // Per-item wijzigingen die nu op de server staan zijn verwerkt
              for (const f of Object.keys(PATCH_FIELDS)) {
                if (!Object.prototype.hasOwnProperty.call(liveChanges, f)) continue;
                for (const [k, v] of patchSnaps[f]) {
                  if (fieldPatches[f].get(k) === v) fieldPatches[f].delete(k);
                }
              }

              // Verberg "synced" indicator na 1.5 seconden, maar laat "offline" staan
              setTimeout(() => {
                setSyncStatus((current) => {
                  if (current === "synced") {
                    setShowSyncIndicator(false);
                  }
                  return current;
                });
              }, 1500);

              // Timestamped backup (niet in transaction voor performance)
              createTimestampedBackup(optimisticUpdate); // niet-blokkerend
            } catch (err) {
              console.error(
                `Transaction attempt ${attempt + 1} failed:`,
                err
              );

              if (attempt === maxRetries - 1) {
                // Laatste poging gefaald: wijzigingen NIET weggooien, maar
                // terugzetten in de wachtrij (nieuwere wijzigingen winnen) en
                // over een paar seconden opnieuw proberen.
                // Blijvende fout (document te groot, ongeldige data, geen
                // rechten): opnieuw proberen helpt niet en hield de app voor
                // altijd op "Offline" (ook voor alle latere wijzigingen).
                const msg = String((err && err.message) || err || "");
                const blijvend =
                  /te groot|invalid-argument|permission-denied|exceeds|too large|maximum/i.test(
                    `${(err && err.code) || ""} ${msg}`
                  );
                if (blijvend) {
                  console.error("❌ Opslaan definitief mislukt, wijziging overgeslagen:", Object.keys(changesToSave), err);
                  setSyncStatus("error");
                  saveRetryCount.current = 0;
                  cpToast(`Opslaan mislukt (${Object.keys(changesToSave).join(", ")}): ${msg.slice(0, 140)}`, "error", 8000);
                } else {
                saveFailed = true;
                pendingChangesRef.current = mergePending(
                  changesToSave,
                  pendingChangesRef.current
                );
                setSyncStatus("offline");
                saveRetryCount.current++;

                if (saveRetryCount.current >= 3) {
                  cpToast(
                    `Synchronisatie problemen. Check je internetverbinding.${msg ? `\n(${msg.slice(0, 120)})` : ""}`
                  );
                  saveRetryCount.current = 0;
                }
                }
              }
            }
          }
        } else {
          console.log("📴 Offline mode - queueing mutation", {
            isLoggedIn,
            hasUser: !!user,
            hasDb: !!db,
            isOnline: offlineOnline,
          });

          // Queue mutation voor later sync
          if (!offlineOnline) {
            try {
              await offlineManager.addMutation({
                type: "updateAppData",
                data: changesToSave,
                appId: appId,
                timestamp: Date.now(),
              });
              console.log("📴 Mutation queued for sync");
              setSyncStatus("offline");
            } catch (error) {
              console.error("❌ Failed to queue mutation:", error);
            }
          } else {
            // Online maar niet ingelogd
            console.error("❌ Cannot save to Firebase:", {
              isLoggedIn,
              hasUser: !!user,
              hasDb: !!db,
              reason:
                !isLoggedIn && !user
                  ? "Not logged in"
                  : "No database connection",
            });
            setSyncStatus("offline");

            // Waarschuw gebruiker bij eerste keer
            if (!sessionStorage.getItem("sync_warning_shown")) {
              sessionStorage.setItem("sync_warning_shown", "true");
              cpToast(
                "Wijzigingen worden lokaal opgeslagen maar niet gesynchroniseerd.\nLog opnieuw in om te synchroniseren."
              );
            }
          }
        }

        isSavingRef.current = false;
        activeSaveRef.current = {}; // Reset na save
        if ((isLoggedIn || user) && db && offlineOnline) setTimeout(lokaalBewaren, 0);

        // Check of er nieuwe pending changes zijn tijdens save
        if (Object.keys(pendingChangesRef.current).length > 0) {
          if (saveFailed) {
            console.log("🔁 Opslaan mislukt — over 8 seconden opnieuw");
            setTimeout(() => saveDataRef.current && saveDataRef.current({}), 8000);
          } else {
            console.log("📝 Processing queued changes...");
            saveDataRef.current && saveDataRef.current({}, { direct: true }); // nieuwste versie van saveData
          }
        }
      }, opts.direct ? 0 : 120); // korte bundeling van snelle wijzigingen (afvinken: direct)
    } catch (error) {
      // ✅ FIX: Catch any uncaught errors
      console.error("❌ saveData error:", error);
      setSyncStatus("error");

      // ✅ FIX: Restore data from backup on critical error
      const backup = DATA_PROTECTION.recoverFromLocal();
      if (backup && Object.keys(backup).length > 5) {
        console.log("🔄 Restoring from backup after error");
        setAppData(backup);
      }
    }
    // Let op: isSavingRef wordt aan het eind van de save-timer vrijgegeven.
    // Hier niet meer na 2 s resetten — dan startte er een tweede save terwijl
    // de eerste nog liep.
  };
  saveDataRef.current = saveData;

  // 🔗 Kerstvakantie 2026: wat al geïmporteerd is koppelen aan de bibliotheek
  useEffect(() => {
    if (loading || voorlopig || userRole !== "fm") return;
    if (appData.settings?.koppelKerst2026v3) return;
    try { if (localStorage.getItem("cp_koppelKerst2026v3")) return; } catch (e) {}
    const t = setTimeout(() => {
      if (voorlopigRef.current) return;
      try { localStorage.setItem("cp_koppelKerst2026v3", "1"); } catch (e) {}
      const { schedule, aantal } = koppelKerst2026(appData);
      saveDataRef.current?.({
        ...(aantal ? { schedule } : {}),
        settings: { ...(appData.settings || {}), koppelKerst2026v3: true },
      });
      if (aantal) cpToast(`🔗 Kerstvakantie: ${aantal} activiteiten gekoppeld aan de bibliotheek`);
    }, 4000);
    return () => clearTimeout(t);
  }, [appData, loading, voorlopig, userRole]);

  // 🎄 Kerstvakantie 2026 in Programmabeheer zetten (alleen FM).
  // Herstelt zichzelf: ontbreekt het programma grotendeels (bijv. omdat een
  // eerdere save mislukte), dan wordt het opnieuw gezet — hooguit één poging
  // per keer openen. Losse activiteiten die je zelf weghaalt komen niet terug.
  useEffect(() => {
    if (loading || voorlopig || userRole !== "fm" || kerst2026Pogingen > 0) return;
    const t = setTimeout(() => {
      if (voorlopigRef.current || kerst2026Pogingen > 0) return;
      kerst2026Pogingen++;
      const { nieuw, spring, springAantal, aanwezig } = bouwKerst2026Import(appData);
      if (aanwezig >= KERST_2026_PROGRAMMA.length / 2 || nieuw.length === 0) return;
      saveDataRef.current?.({
        schedule: [...(appData.schedule || []), ...nieuw],
        ...(springAantal ? { dailySpringkussen: spring } : {}),
      });
      cpToast(`🎄 Kerstvakantie 2026: ${nieuw.length} activiteiten in Programmabeheer gezet`);
    }, 2500);
    return () => clearTimeout(t);
  }, [appData, loading, voorlopig, userRole]);


  // 👀 Ids die deze telefoon al eens gezien heeft. Staat een item wel op de
  // server maar niet in de lokale lijst, dan geldt: al gezien = bewust
  // verwijderd/opgeschoond; nooit gezien = net toegevoegd door een collega
  // (behouden). Zo gaan gelijktijdige berichten/overdrachten niet meer verloren.
  const seenIdsRef = useRef({ handover: new Set(), chats: new Set(), chatMsgs: new Set(), fmChecklist: new Set(), shiftSwapRequests: new Set() });
  useEffect(() => {
    const seen = seenIdsRef.current;
    for (const day of Object.values(appData?.handover || {})) {
      if (!day || typeof day !== "object") continue;
      for (const v of Object.values(day)) {
        if (Array.isArray(v)) v.forEach((i) => i?.id != null && seen.handover.add(String(i.id)));
      }
    }
    (appData?.chats || []).forEach((c) => {
      if (c?.id != null) seen.chats.add(String(c.id));
      (c?.messages || []).forEach((m) => m?.id != null && seen.chatMsgs.add(String(m.id)));
    });
    (appData?.fmChecklist || []).forEach((i) => i?.id != null && seen.fmChecklist.add(String(i.id)));
    (appData?.shiftSwapRequests || []).forEach((i) => i?.id != null && seen.shiftSwapRequests.add(String(i.id)));
  }, [appData?.handover, appData?.chats, appData?.fmChecklist, appData?.shiftSwapRequests]);

  const unionReadBy = (a, b) =>
    Array.isArray(a) || Array.isArray(b) ? [...new Set([...(a || []), ...(b || [])])] : undefined;

  // Lokale lijst is leidend, maar items die alleen op de server staan en die
  // deze telefoon nog nooit zag (nieuw van een collega) blijven behouden.
  const mergeById = (serverArr, localArr, seen, mergeItem) => {
    if (!Array.isArray(serverArr)) return localArr;
    if (!Array.isArray(localArr)) return serverArr;
    const serverById = new Map(serverArr.filter((i) => i?.id != null).map((i) => [String(i.id), i]));
    const localIds = new Set();
    const out = localArr.map((l) => {
      if (l?.id == null) return l;
      localIds.add(String(l.id));
      const srv = serverById.get(String(l.id));
      return srv && mergeItem ? mergeItem(srv, l) : l;
    });
    for (const srv of serverArr) {
      if (srv?.id == null) continue;
      const k = String(srv.id);
      if (localIds.has(k) || seen.has(k)) continue;
      out.push(srv);
    }
    return out;
  };
  const mergeMetGelezen = (srv, l) => {
    const rb = unionReadBy(srv.readBy, l.readBy);
    return rb ? { ...l, readBy: rb } : l;
  };
  const mergeChat = (srv, l) => ({
    ...srv,
    ...l,
    messages: mergeById(srv.messages, l.messages, seenIdsRef.current.chatMsgs, mergeMetGelezen),
  });

  // 🔒 SMART MERGE: Intelligente merge die arrays correct samenvoegt
  const smartMerge = (serverData, localChanges) => {
    const result = { ...serverData };

    for (const key of Object.keys(localChanges)) {
      const serverVal = serverData[key];
      const localVal = localChanges[key];

      if (PATCH_FIELDS[key]) {
        // ✅ Alleen eigen gewijzigde items toepassen op de server-versie
        const hasServer = PATCH_FIELDS[key].flatten
          ? isPlainObj(serverVal)
          : Array.isArray(serverVal);
        result[key] = applyFieldPatches(key, hasServer ? serverVal : localVal);
        continue;
      }

      if (key === "orders") {
        // 🔒 PER-ID MERGE (zelfde aanpak als staffList): "laatste schrijver
        // wint voor de hele array" liet gelijktijdig bestelde items van
        // collega's verdwijnen zodra deze tab — met een lokale array die de
        // real-time update van die collega nog niet bevatte — opsloeg. Nu
        // wordt server + lokaal samengevoegd op id; alleen ids die via
        // registerOrderRemoval bewust zijn verwijderd (knop of 2-weken
        // auto-clear) verdwijnen echt.
        const serverArr = Array.isArray(serverVal) ? serverVal : [];
        const localArr = Array.isArray(localVal) ? localVal : [];
        const localById = new Map(
          localArr.filter((o) => o?.id != null).map((o) => [o.id, o])
        );
        const seenIds = new Set();
        const merged = [];
        for (const s of serverArr) {
          if (s?.id == null) {
            merged.push(s);
            continue;
          }
          seenIds.add(s.id);
          if (orderRemovalIntent.has(s.id)) continue; // bewust gewist
          if (!localById.has(s.id)) {
            merged.push(s);
            continue;
          }
          // Reacties van server + lokaal samenvoegen op id (twee collega's
          // kunnen tegelijk reageren)
          const l = localById.get(s.id);
          const sr = Array.isArray(s.reacties) ? s.reacties : [];
          const lr = Array.isArray(l.reacties) ? l.reacties : [];
          if (sr.length || lr.length) {
            const byId = new Map();
            [...sr, ...lr].forEach((r) => r?.id && byId.set(r.id, r));
            merged.push({ ...l, reacties: [...byId.values()].sort((a, b) => (a.at || 0) - (b.at || 0)) });
          } else merged.push(l);
        }
        for (const l of localArr) {
          if (l?.id != null && !seenIds.has(l.id)) merged.push(l);
        }
        result[key] = merged;
      } else if (
        Array.isArray(localVal) &&
        (key === "chats" || key === "fmChecklist" || key === "shiftSwapRequests")
      ) {
        const seen = seenIdsRef.current[key];
        result[key] = mergeById(
          serverVal,
          localVal,
          seen,
          key === "chats" ? mergeChat : key === "fmChecklist" ? null : null
        );
      } else if (Array.isArray(localVal)) {
        if (Array.isArray(serverVal)) {
          // 🔒 VERBETERDE ARRAY MERGE voor fmChecklist, schedule, etc.
          // Combineer server + lokale items, voorkom duplicaten op basis van id
          const serverIds = new Set(
            serverVal.map((item) => item?.id).filter(Boolean)
          );
          const localIds = new Set(
            localVal.map((item) => item?.id).filter(Boolean)
          );

          // Items die ALLEEN op server staan (niet verwijderd lokaal)
          // Check: als lokaal item met zelfde id NIET bestaat, behoud server versie
          // TENZIJ lokale array expliciet dat item niet heeft (= verwijderd)

          // Strategie: Lokale array is "source of truth" voor dit veld
          // Maar we mergen met server voor items die lokaal NIET zijn aangeraakt

          // Als de lokale array leeg is maar server heeft data, behoud server
          // (beschermt tegen per ongeluk legen)
          // Uitzondering: guestFeedback mag wel leeg worden (intentioneel verwijderd)
          if (
            localVal.length === 0 &&
            serverVal.length > 0 &&
            key !== "guestFeedback"
          ) {
            console.warn(
              `smartMerge: ${key} zou leeg worden, server data behouden`
            );
            result[key] = serverVal;
          } else {
            // Gebruik lokale versie (laatste write wins voor hele array)
            result[key] = localVal;
          }
        } else {
          // Server heeft geen array, gebruik lokale
          result[key] = localVal;
        }
      } else if (
        typeof serverVal === "object" &&
        serverVal !== null &&
        typeof localVal === "object" &&
        localVal !== null &&
        !Array.isArray(serverVal)
      ) {
        // staffAvailability/staffAvailabilityLocks: 2-niveau merge (persoon → datums), voorkomt overschrijven
        if (DEEP_MERGE_FIELDS.has(key)) {
          result[key] = deepMergePlain(serverVal, localVal);
        } else if (
          key === "staffAvailability" ||
          key === "staffAvailabilityLocks"
        ) {
          const merged = { ...serverVal };
          for (const [person, dates] of Object.entries(localVal)) {
            if (typeof dates === "object" && dates !== null) {
              merged[person] = { ...(serverVal[person] || {}), ...dates };
            } else {
              merged[person] = dates;
            }
          }
          result[key] = merged;
        } else if (key === "vakantieplannen") {
          // Merge per persoon (laatste schrijver wint per persoon)
          result[key] = { ...serverVal, ...localVal };
          // handover/timeClock: 2-niveau merge (datum → type/persoon), voorkomt overschrijven
        } else if (key === "handover" || key === "timeClock") {
          const merged2 = { ...serverVal };
          for (const [dateK, dayData] of Object.entries(localVal)) {
            if (
              typeof dayData === "object" &&
              dayData !== null &&
              typeof merged2[dateK] === "object" &&
              merged2[dateK] !== null
            ) {
              const dag = { ...merged2[dateK], ...dayData };
              if (key === "handover") {
                // Per overdracht-lijst (ent/crea/…) op id samenvoegen, zodat een
                // gelijktijdige overdracht of "gelezen" van een collega blijft staan
                for (const [t, arr] of Object.entries(dayData)) {
                  if (Array.isArray(arr) && Array.isArray(merged2[dateK][t])) {
                    dag[t] = mergeById(merged2[dateK][t], arr, seenIdsRef.current.handover, mergeMetGelezen);
                  }
                }
              }
              if (key === "timeClock") Object.assign(dag, mergeKlokDag(merged2[dateK], dayData));
              merged2[dateK] = dag;
            } else {
              merged2[dateK] = dayData;
            }
          }
          result[key] = merged2;
        } else {
          // Voor overige objecten (dailyStats, settings): shallow merge
          result[key] = { ...serverVal, ...localVal };
        }
      } else {
        // Voor primitieven: laatste write wins
        result[key] = localVal;
      }
    }

    return result;
  };

  // 🔗 BETERE KOPPELING: Cleanup functie voor orphaned staff in programma
  // Verwijdert medewerkers uit activiteiten als ze niet meer ingeroosterd zijn op die datum
  const cleanupOrphanedStaff = () => {
    if (!appData.workSchedule || !appData.schedule) {
      console.log("⚠️ Geen workSchedule of schedule data om op te schonen");
      return { cleaned: false, message: "Geen data om op te schonen" };
    }

    let changesCount = 0;
    let detailsLog = [];

    const newSchedule = appData.schedule.map((activity) => {
      if (
        !activity.staff ||
        !Array.isArray(activity.staff) ||
        activity.staff.length === 0
      ) {
        return activity;
      }

      const dateKey = activity.date;
      const shiftsOnDay = appData.workSchedule[dateKey] || [];

      // Lijst van alle medewerkers die op deze dag een shift hebben (CSV of handmatig)
      const staffWithShifts = new Set(
        shiftsOnDay.map((shift) => shift.matchedUser).filter(Boolean)
      );
      // Ook handmatig ingeplande diensten (roosterDiensten) meenemen
      (appData.roosterDiensten?.[dateKey] || []).forEach((d) =>
        (d.assigned || []).forEach((n) => staffWithShifts.add(n))
      );

      // Filter staff: behoud alleen medewerkers die een shift hebben
      const validStaff = activity.staff.filter((name) => {
        // Bewust buiten het rooster om ingedeeld → laten staan
        const isValid =
          staffWithShifts.has(name) || (activity.staffExtra || []).includes(name);
        if (!isValid) {
          changesCount++;
          detailsLog.push({
            date: dateKey,
            activity: activity.title,
            removedStaff: name,
          });
        }
        return isValid;
      });

      // Return aangepaste activity als er wijzigingen zijn
      if (validStaff.length !== activity.staff.length) {
        return {
          ...activity,
          staff: validStaff,
        };
      }

      return activity;
    });

    if (changesCount > 0) {
      console.log(
        `Cleanup: ${changesCount} orphaned staff assignments verwijderd:`,
        detailsLog
      );
      saveData({ schedule: newSchedule });
      return {
        cleaned: true,
        count: changesCount,
        details: detailsLog,
        message: `${changesCount} orphaned staff assignment(s) verwijderd uit programma`,
      };
    }

    console.log("✅ Cleanup: Geen orphaned staff gevonden");
    return {
      cleaned: false,
      count: 0,
      message: "Geen orphaned staff gevonden",
    };
  };

  // 🧹 Auto-wis bestellingen die langer dan 2 weken "binnen" staan.
  // Bestaande 'binnen'-orders zonder tijdstempel krijgen er nu één
  // (klok start), zodat er niets onverwacht meteen verdwijnt.
  useEffect(() => {
    const orders = appData.orders;
    if (!Array.isArray(orders) || orders.length === 0) return;
    const TWO_WEEKS = 14 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    let changed = false;
    const next = [];
    for (const o of orders) {
      if (o.status === "received") {
        if (!o.receivedAt) {
          next.push({ ...o, receivedAt: new Date().toISOString() });
          changed = true;
          continue;
        }
        const ts = Date.parse(o.receivedAt);
        if (!isNaN(ts) && now - ts > TWO_WEEKS) {
          changed = true; // ouder dan 2 weken binnen → automatisch wissen
          registerOrderRemoval(o.id);
          continue;
        }
      }
      next.push(o);
    }
    if (changed) saveData({ orders: next });
    // eslint-disable-next-line
  }, [appData.orders]);

  // 🔒 FORCE REFRESH: Haal verse data van server (voor troubleshooting)
  const forceRefreshFromServer = async () => {
    try {
      setSyncStatus("saving");
      setShowSyncIndicator(true);

      const docRef = doc(
        db,
        "artifacts",
        appId,
        "public",
        "data",
        "cp_live_data",
        "master"
      );

      const serverDoc = await getDoc(docRef);
      if (serverDoc.exists()) {
        const serverData = serverDoc.data();
        setAppData(sanitizeData(metArchief(serverData)));
        lastServerVersionRef.current = serverData._version || 0;
        pendingChangesRef.current = {}; // Clear pending changes
        DATA_PROTECTION.saveLastKnownGood(sanitizeData(serverData));
        console.log("🔄 Forced refresh from server");
        setSyncStatus("synced");
        setTimeout(() => setShowSyncIndicator(false), 1500);
        return true;
      }
    } catch (error) {
      console.error("❌ Force refresh failed:", error);
      setSyncStatus("offline");
    }
    return false;
  };

  // 🗜️ DIRECTE DATABASE PRUNE — schrijft gepruned doc meteen naar Firestore
  const [isPruning, setIsPruning] = useState(false);
  const pruneNow = async () => {
    if (!db || !appId || isPruning) return;
    setIsPruning(true);
    try {
      // Haal verse data op van server
      const docRef = doc(
        db,
        "artifacts",
        appId,
        "public",
        "data",
        "cp_live_data",
        "master"
      );
      // Alles in één transaction: archief + ingekort live-document samen,
      // en wijzigingen van collega's tijdens het verkleinen gaan niet verloren.
      let beforeKB = 0;
      let afterKB = 0;
      let archiveByMonth = {};
      let nieuweVersie = null;
      await runTransaction(db, async (transaction) => {
        const serverDoc = await transaction.get(docRef);
        if (!serverDoc.exists()) return;
        const ruw = serverDoc.data();
        const serverData = Array.isArray(ruw.schedule)
          ? { ...ruw, schedule: scheduleZonderLibTeksten(ruw.schedule, appDataRef.current?.activityLibrary) }
          : ruw;
        const res = pruneDataForLiveDoc(serverData, PRUNE_DAYS);
        archiveByMonth = res.archiveByMonth;
        beforeKB = Math.round(new Blob([JSON.stringify(serverData)]).size / 1024);
        afterKB = Math.round(new Blob([JSON.stringify(res.liveData)]).size / 1024);
        const schrijfArchief = await archiveInTransaction(transaction, archiveByMonth);
        schrijfArchief();
        const cleaned = removeUndefined(res.liveData);
        cleaned._version = (serverData._version || 0) + 1;
        cleaned._lastEditBy = loggedInUserName || "pruner";
        cleaned.lastUpdate = new Date().toISOString();
        transaction.set(docRef, cleaned);
        nieuweVersie = cleaned._version;
      });
      if (nieuweVersie == null) {
        setIsPruning(false);
        return;
      }

      lastServerVersionRef.current = nieuweVersie;
      lastPruneRef.current = new Date().toISOString().split("T")[0];

      console.log(`🗜️ Prune voltooid: ${beforeKB}KB → ${afterKB}KB`);
      triggerAlert(
        `Database verkleind: ${beforeKB} KB → ${afterKB} KB\nGearchiveerde maanden: ${
          Object.keys(archiveByMonth).join(", ") || "geen"
        }`
      );
    } catch (err) {
      console.error("❌ Prune mislukt:", err);
      triggerAlert("Prune mislukt: " + err.message);
    }
    setIsPruning(false);
  };

  // 🔥 VOEG DEZE TWEE FUNCTIES TOE:
  // 📦 Schrijf archief-maanden weg naar Firestore (non-blocking)
  // 📦 Archief schrijven BINNEN een Firestore-transaction.
  // Eerst alle bestaande maand-shards lezen (transactions: eerst lezen, dan
  // schrijven); geeft een functie terug die de schrijfacties uitvoert.
  const archiveInTransaction = async (transaction, archiveByMonth) => {
    const plannen = [];
    if (!archiveByMonth || Object.keys(archiveByMonth).length === 0) return () => {};
    const base = ["artifacts", appId, "public", "data", "cp_archive"];
    for (const [mKey, incoming] of Object.entries(archiveByMonth)) {
      const metaRef = doc(db, ...base, `${mKey}__meta`);
      const metaSnap = await transaction.get(metaRef);
      const prevShards = metaSnap.exists() ? metaSnap.data().shards || 1 : null;
      let existing = {};
      const n = prevShards === null ? 1 : prevShards;
      for (let i = 0; i < n; i++) {
        const ref = i === 0 ? doc(db, ...base, mKey) : doc(db, ...base, `${mKey}__s${i}`);
        const snap = await transaction.get(ref);
        if (snap.exists()) existing = { ...existing, ...snap.data() };
      }
      plannen.push({ mKey, metaRef, prevShards, merged: mergeArchiveInto(existing, incoming) });
    }
    return () => {
      for (const { mKey, metaRef, prevShards, merged } of plannen) {
        const shards = splitObjectIntoShards(merged);
        shards.forEach((shard, i) => {
          const ref = i === 0 ? doc(db, ...base, mKey) : doc(db, ...base, `${mKey}__s${i}`);
          transaction.set(ref, shard);
        });
        if (prevShards && prevShards > shards.length) {
          for (let i = shards.length; i < prevShards; i++) {
            transaction.set(doc(db, ...base, `${mKey}__s${i}`), {});
          }
        }
        transaction.set(metaRef, { shards: shards.length, updatedAt: new Date().toISOString() });
      }
    };
  };

  const saveArchiveChunks = async (archiveByMonth) => {
    if (
      !db ||
      !appId ||
      !archiveByMonth ||
      Object.keys(archiveByMonth).length === 0
    )
      return;

    const base = ["artifacts", appId, "public", "data", "cp_archive"];

    for (const [mKey, incoming] of Object.entries(archiveByMonth)) {
      try {
        const metaRef = doc(db, ...base, `${mKey}__meta`);
        const metaSnap = await getDoc(metaRef);
        const prevShards = metaSnap.exists()
          ? metaSnap.data().shards || 1
          : null;

        // Lees bestaande maand-data (alle shards, of legacy enkel-doc)
        let existing = {};
        if (prevShards === null) {
          const mainSnap = await getDoc(doc(db, ...base, mKey));
          if (mainSnap.exists()) existing = mainSnap.data();
        } else {
          for (let i = 0; i < prevShards; i++) {
            const ref =
              i === 0
                ? doc(db, ...base, mKey)
                : doc(db, ...base, `${mKey}__s${i}`);
            const snap = await getDoc(ref);
            if (snap.exists()) existing = { ...existing, ...snap.data() };
          }
        }

        const merged = mergeArchiveInto(existing, incoming);
        const shards = splitObjectIntoShards(merged);

        for (let i = 0; i < shards.length; i++) {
          const ref =
            i === 0
              ? doc(db, ...base, mKey)
              : doc(db, ...base, `${mKey}__s${i}`);
          await setDoc(ref, shards[i]);
        }
        // Leeg eventueel overgebleven oude shards
        if (prevShards && prevShards > shards.length) {
          for (let i = shards.length; i < prevShards; i++) {
            await setDoc(doc(db, ...base, `${mKey}__s${i}`), {});
          }
        }
        await setDoc(metaRef, {
          shards: shards.length,
          updatedAt: new Date().toISOString(),
        });
        console.log(`📦 Archief ${mKey}: ${shards.length} shard(s)`);
      } catch (e) {
        console.warn(`⚠️ Archief save ${mKey} mislukt:`, e);
      }
    }
  };

  // 📚 Bij openen Kwaliteit: laad ALLE gearchiveerde ratings/visits/feedback in beeld.
  // Alleen in geheugen (setAppData) — het live-doc blijft ongemoeid en de dagelijkse
  // prune zet oude data later vanzelf weer netjes in het archief.
  const kwaliteitArchiveLoadedRef = React.useRef(false);
  useEffect(() => {
    if (!db || !appId || !isLoggedIn) return;
    if (view !== "quality_visits" && !(view === "admin" && adminSubView === "quality_visits")) return;
    if (kwaliteitArchiveLoadedRef.current) return;
    kwaliteitArchiveLoadedRef.current = true;
    (async () => {
      try {
        const snap = await getDocs(
          collection(db, "artifacts", appId, "public", "data", "cp_archive")
        );
        // Alleen de kwaliteitsvelden, en via archiefRef: dan blijven ze ook
        // staan nadat een collega iets opslaat (voorheen verdwenen ze dan
        // weer uit beeld bij de eerstvolgende server-update).
        const KWALITEIT_VELDEN = ["activityRatings", "guestFeedback", "qualityVisits", "npsScores"];
        const merged = {};
        snap.forEach((d) => {
          for (const [k, v] of Object.entries(d.data() || {})) {
            if (KWALITEIT_VELDEN.includes(k) && Array.isArray(v)) {
              if (!merged[k]) merged[k] = [];
              merged[k].push(...v);
            }
          }
        });
        if (Object.keys(merged).length === 0) return;
        archiefRef.current = mergeArchiveInto(archiefRef.current, merged);
        setAppData((prev) => metArchief(prev));
        console.log("📚 Kwaliteit: gearchiveerde ratings ingeladen");
      } catch (e) {
        console.warn("Kwaliteit-archief laden mislukt:", e);
        kwaliteitArchiveLoadedRef.current = false; // sta retry toe
      }
    })();
  }, [view, adminSubView, db, appId, isLoggedIn]); // eslint-disable-line

  // 📚 ARCHIEF — oude data blijft altijd zichtbaar.
  // Opgehaalde archiefmaanden worden in archiefRef bewaard en over elke
  // server-update heen gelegd (live data wint altijd). Zo verdwijnt oude data
  // niet meer uit beeld zodra een collega iets opslaat.
  const archiefRef = useRef({});
  const metArchief = (prev) => {
    const archData = archiefRef.current;
    if (!prev || !archData || Object.keys(archData).length === 0) return prev;
    const merged = { ...prev };

    for (const field of DATE_KEYED_FIELDS) {
      if (archData[field]) {
        merged[field] = { ...archData[field], ...(prev[field] || {}) };
      }
    }

    for (const field of NESTED_DATE_KEYED_FIELDS) {
      if (archData[field]) {
        const mf = { ...(prev[field] || {}) };
        for (const [outerKey, inner] of Object.entries(archData[field])) {
          mf[outerKey] = { ...inner, ...(mf[outerKey] || {}) };
        }
        merged[field] = mf;
      }
    }

    const dedupArr = (field) => {
      const prevArr = Array.isArray(prev[field]) ? prev[field] : [];
      const seen = new Set(prevArr.map((i) => archiveItemKey(field, i)).filter((x) => x != null));
      // Items zonder id: alleen toevoegen als exact hetzelfde item er nog niet staat
      const seenJson = new Set(prevArr.filter((i) => i?.id == null).map((i) => JSON.stringify(i)));
      const add = (archData[field] || []).filter((i) => {
        const k = archiveItemKey(field, i);
        return k == null ? !seenJson.has(JSON.stringify(i)) : !seen.has(k);
      });
      if (add.length) merged[field] = [...add, ...prevArr];
    };
    for (const field of DATE_ARRAY_FIELDS) {
      if (archData[field]) dedupArr(field);
    }
    for (const field of Object.keys(CUSTOM_DATE_ARRAY_FIELDS)) {
      if (archData[field]) dedupArr(field);
    }

    return merged;
  };
  const laadArchiefMaand = async (mKey) => {
    if (!db || !appId) return;
    if (loadedArchiveQuartersRef.current.has(mKey)) return; // al geladen
    loadedArchiveQuartersRef.current.add(mKey);
    const base = ["artifacts", appId, "public", "data", "cp_archive"];
    let archData = {};
    try {
      const metaSnap = await getDoc(doc(db, ...base, `${mKey}__meta`));
      if (metaSnap.exists()) {
        const n = metaSnap.data().shards || 1;
        for (let i = 0; i < n; i++) {
          const ref = i === 0 ? doc(db, ...base, mKey) : doc(db, ...base, `${mKey}__s${i}`);
          const snap = await getDoc(ref);
          if (snap.exists()) archData = { ...archData, ...snap.data() };
        }
      } else {
        const mainSnap = await getDoc(doc(db, ...base, mKey));
        if (mainSnap.exists()) archData = mainSnap.data();
      }
      // Legacy kwartaal-archief mee-mergen (oude data blijft bereikbaar)
      const qKey = getQuarterKeyFromDateStr(`${mKey}-15`);
      const qSnap = await getDoc(doc(db, ...base, qKey));
      if (qSnap.exists()) archData = mergeArchiveInto(archData, qSnap.data());
    } catch (e) {
      console.warn("⚠️ Archief laden mislukt:", e);
      loadedArchiveQuartersRef.current.delete(mKey); // sta retry toe
      return;
    }
    if (Object.keys(archData).length === 0) return;
    archiefRef.current = mergeArchiveInto(archiefRef.current, archData);
    setAppData((prev) => metArchief(prev));
    console.log(`📚 Archief geladen: ${mKey}`);
  };

  // Bladeren naar een oude datum → die maand uit het archief laden
  useEffect(() => {
    if (!db || !appId || !isLoggedIn) return;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 1); // live doc kan bij zware dagen korter bewaren
    const dayStart = new Date(currentDay);
    dayStart.setHours(0, 0, 0, 0);
    if (dayStart >= cutoff) return; // recente datum: live doc heeft alles
    laadArchiefMaand(getMonthKeyFromDateStr(toIsoDate(dayStart)));
  }, [currentDay, isLoggedIn, db, appId]);

  // 🎰 Bingo-winnaars: bij openen alle gearchiveerde winnaars erbij laden,
  // zodat CO (en FM) de volledige lijst blijven zien.
  const bingoArchiefRef = useRef(false);
  useEffect(() => {
    if (!db || !appId || !isLoggedIn || view !== "bingo" || bingoArchiefRef.current) return;
    bingoArchiefRef.current = true;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "artifacts", appId, "public", "data", "cp_archive"));
        const winnaars = [];
        snap.forEach((d) => {
          const v = d.data()?.bingoWinners;
          if (Array.isArray(v)) winnaars.push(...v);
        });
        if (winnaars.length === 0) return;
        archiefRef.current = mergeArchiveInto(archiefRef.current, { bingoWinners: winnaars });
        setAppData((prev) => metArchief(prev));
      } catch (e) {
        console.warn("Bingo-archief laden mislukt:", e);
        bingoArchiefRef.current = false;
      }
    })();
  }, [view, db, appId, isLoggedIn]);

  // De laatste 30 dagen compleet maken (velden die korter live staan) — maar
  // pas als iemand een scherm opent dat terugkijkt (uren, beheer, kwaliteit,
  // overdracht-archief, dashboard …). Voor Dagbord, Rooster, Programma, Chat en
  // Takenlijst is het live-document genoeg; dat scheelt bij openen soms
  // megabytes aan archief-downloads op mobiele data.
  const archiefNodig = !LICHTE_VIEWS.has(view);
  useEffect(() => {
    if (!db || !appId || !isLoggedIn || !archiefNodig) return;
    const t = setTimeout(() => {
      const maanden = new Set();
      for (let d = 1; d <= PRUNE_DAYS; d++) { // vanaf gisteren: live doc kan korter bewaren
        const x = new Date();
        x.setDate(x.getDate() - d);
        maanden.add(getMonthKeyFromDateStr(toIsoDate(x)));
      }
      maanden.forEach((m) => laadArchiefMaand(m));
    }, 600);
    return () => clearTimeout(t);
  }, [isLoggedIn, db, appId, archiefNodig]); // eslint-disable-line

  const showAlert = (message) => {
    setAlertParams({
      isOpen: true,
      message: message,
    });
  };

  const showConfirm = (message, onConfirm) => {
    setConfirmParams({
      isOpen: true,
      message: message,
      onConfirm: onConfirm,
    });
  };
  // 🔥 VOEG DEZE FUNCTIES TOE NA saveData
  const addQualityVisit = async (visitData) => {
    // Special case: alleen guestFeedback opslaan
    if (visitData._feedbackOnly) {
      const newAppData = { ...appData, guestFeedback: visitData.guestFeedback };
      setAppData(newAppData);
      try {
        const current = JSON.parse(localStorage.getItem("cp_app_data") || "{}");
        current.guestFeedback = visitData.guestFeedback;
        localStorage.setItem("cp_app_data", JSON.stringify(current));
      } catch {}
      // Sla alleen guestFeedback op (niet volledige appData — voorkomt merge-conflicten)
      await saveData({ guestFeedback: visitData.guestFeedback });
      return;
    }

    let updated;

    // Check if updating existing visit
    if (
      visitData.id &&
      appData.qualityVisits.find((v) => v.id === visitData.id)
    ) {
      updated = appData.qualityVisits.map((v) =>
        v.id === visitData.id ? visitData : v
      );
    } else {
      // Adding new visit
      const newVisit = {
        id: visitData.id || Date.now(),
        ...visitData,
        timestamp: visitData.timestamp || new Date().toISOString(),
      };
      updated = [...(appData.qualityVisits || []), newVisit];
    }

    console.log("📊 ADDING Quality Visit:", updated.length, "total");

    // 1. Update React state
    const newAppData = { ...appData, qualityVisits: updated };
    setAppData(newAppData);

    // 2. DIRECT NAAR LOCALSTORAGE
    try {
      const current = JSON.parse(localStorage.getItem("cp_app_data") || "{}");
      current.qualityVisits = updated;
      localStorage.setItem("cp_app_data", JSON.stringify(current));
      console.log("✅ localStorage saved");
    } catch (e) {
      console.error("❌ localStorage failed:", e);
    }

    // 3. DEDICATED BACKUP
    try {
      localStorage.setItem(
        "cp_quality_visits_backup",
        JSON.stringify({
          visits: updated,
          timestamp: Date.now(),
          count: updated.length,
        })
      );
      console.log("✅ Backup created");
    } catch (e) {
      console.error("❌ Backup failed:", e);
    }

    // 4. EMERGENCY SNAPSHOT
    DATA_PROTECTION.createEmergencySnapshot({
      ...appData,
      qualityVisits: updated,
    });

    // 5. Normal update (Firestore)
    saveData({ qualityVisits: updated });

    showAlert("Quality Visit opgeslagen!");
  };

  const deleteQualityVisit = (visitId) => {
    showConfirm("Deze quality visit verwijderen?", () => {
      // Markeer als "bezig met verwijderen"
      setDeletingId(visitId);

      const updated = (appData.qualityVisits || []).filter(
        (v) => v.id !== visitId
      );

      console.log("🗑️ DELETING Quality Visit, remaining:", updated.length);

      // 1. EERST React state updaten (SYNC)
      const newAppData = { ...appData, qualityVisits: updated };
      setAppData(newAppData);

      // 2. DIRECT NAAR LOCALSTORAGE
      try {
        const current = JSON.parse(localStorage.getItem("cp_app_data") || "{}");
        current.qualityVisits = updated;
        localStorage.setItem("cp_app_data", JSON.stringify(current));
      } catch (e) {}

      // 3. UPDATE BACKUP
      try {
        localStorage.setItem(
          "cp_quality_visits_backup",
          JSON.stringify({
            visits: updated,
            timestamp: Date.now(),
            count: updated.length,
          })
        );
      } catch (e) {}

      // 4. Normal update (Firebase sync)
      saveData({ qualityVisits: updated });

      // Reset na korte tijd
      setTimeout(() => {
        setDeletingId(null);
      }, 500);
      showAlert("Quality visit verwijderd");
    });
  };

  // 🔥 ACTIVITY RATINGS FUNCTIES (toegevoegd)
  const addActivityRating = async (ratingData) => {
    // Initialize if needed
    if (!appData.activityRatings) {
      appData.activityRatings = [];
    }

    let updated;
    // Check if updating existing rating
    if (
      ratingData.id &&
      appData.activityRatings.find((r) => r.id === ratingData.id)
    ) {
      updated = appData.activityRatings.map((r) =>
        r.id === ratingData.id ? ratingData : r
      );
    } else {
      // Adding new rating
      const newRating = {
        id: ratingData.id || Date.now(),
        ...ratingData,
        timestamp: ratingData.timestamp || new Date().toISOString(),
      };
      updated = [...appData.activityRatings, newRating];
    }

    console.log("⭐ ADDING Activity Rating:", updated.length, "total");

    // 1. Update React state
    const newAppData = { ...appData, activityRatings: updated };
    setAppData(newAppData);

    // 2. DIRECT NAAR LOCALSTORAGE
    try {
      const current = JSON.parse(localStorage.getItem("cp_app_data") || "{}");
      current.activityRatings = updated;
      localStorage.setItem("cp_app_data", JSON.stringify(current));
      console.log("✅ Activity Rating saved to localStorage");
    } catch (e) {
      console.error("❌ localStorage failed:", e);
    }

    // 3. Normal update (Firestore)
    saveData({ activityRatings: updated });

    showAlert("Activity Rating opgeslagen!");
  };

  const deleteActivityRating = (ratingId) => {
    const updated = (appData.activityRatings || []).filter(
      (r) => r.id !== ratingId
    );

    console.log("🗑️ DELETING Activity Rating, remaining:", updated.length);

    // 1. Update React state
    const newAppData = { ...appData, activityRatings: updated };
    setAppData(newAppData);

    // 2. DIRECT NAAR LOCALSTORAGE
    try {
      const current = JSON.parse(localStorage.getItem("cp_app_data") || "{}");
      current.activityRatings = updated;
      localStorage.setItem("cp_app_data", JSON.stringify(current));
    } catch (e) {}

    // 3. Normal update (Firestore)
    saveData({ activityRatings: updated });

    showAlert("Activity rating verwijderd");
  };

  const recoverQualityVisits = async () => {
    try {
      const currentCount = (appData.qualityVisits || []).length;
      const backup = localStorage.getItem("cp_quality_visits_backup");
      if (backup) {
        const parsed = JSON.parse(backup);
        const backupCount = parsed.count || 0;
        if (backupCount > currentCount) {
          if (
            await cpConfirm(
              `Quality Visits backup: ${backupCount} items (nu: ${currentCount}). Herstellen?`
            )
          ) {
            setAppData((prev) => ({ ...prev, qualityVisits: parsed.visits }));
            saveData({ qualityVisits: parsed.visits });
            showAlert(`${backupCount} Quality Visits hersteld!`);
            return;
          }
        }
      }
      showAlert("Geen nieuwere backup gevonden");
    } catch (e) {
      showAlert("Herstel mislukt: " + e.message);
    }
  };
  // 💾 SMART BACKUP STRATEGIE
  const lastPruneRef = useRef(null); // 📦 Track wanneer laatste prune was (max 1x per dag)
  const loadedArchiveQuartersRef = useRef(new Set()); // 📦 Track geladen archief-kwartalen
  const lastBackupRef = useRef({
    date: null,
    dataHash: null,
  });

  const shouldCreateBackup = (data) => {
    const now = new Date();
    const today = now.toISOString().split("T")[0]; // YYYY-MM-DD

    // Check 1: Is er vandaag al een backup gemaakt?
    if (lastBackupRef.current.date === today) {
      console.log("⏭️ Skip backup - already created today");
      return false;
    }

    // Check 2: Is de data significant veranderd?
    const currentHash = getDataHash(data);
    if (lastBackupRef.current.dataHash === currentHash) {
      console.log("⏭️ Skip backup - data unchanged");
      return false;
    }

    // Check 3: Zijn er belangrijke velden gewijzigd?
    const hasSignificantChanges =
      (data.staffList && data.staffList.length > 0) ||
      (data.workSchedule && Object.keys(data.workSchedule).length > 0) ||
      (data.schedule && data.schedule.length > 0);

    if (!hasSignificantChanges) {
      console.log("⏭️ Skip backup - no significant data");
      return false;
    }

    return true;
  };

  const createTimestampedBackup = async (data) => {
    if (!db || !appId) return;

    // ✅ SMART CHECK: Moet er een backup gemaakt worden?
    if (!shouldCreateBackup(data)) {
      return;
    }

    try {
      const timestamp = new Date().toISOString();
      const backupId = timestamp.replace(/[:.]/g, "-");

      const backupRef = doc(
        db,
        "artifacts",
        appId,
        "public",
        "data",
        "backups",
        backupId
      );
      await setDoc(backupRef, {
        ...data,
        backupCreatedAt: timestamp,
        backupCreatedBy: loggedInUserName || "system",
      });

      console.log("✅ Smart backup created:", backupId);

      // Update laatste backup tracking
      const today = new Date().toISOString().split("T")[0];
      lastBackupRef.current = {
        date: today,
        dataHash: getDataHash(data),
      };

      // Cleanup old backups in background (non-blocking)
      setTimeout(() => cleanupOldBackups(), 100);
    } catch (e) {
      console.error("⚠️ Backup creation failed (non-critical):", e);
    }
  };

  const cleanupOldBackups = async () => {
    if (!db || !appId) return;

    try {
      const backupsCollection = collection(
        db,
        "artifacts",
        appId,
        "public",
        "data",
        "backups"
      );
      const snapshot = await getDocs(backupsCollection);
      const backups = [];

      snapshot.forEach((doc) => {
        backups.push({
          id: doc.id,
          createdAt: doc.data().backupCreatedAt || doc.id,
        });
      });

      backups.sort((a, b) => b.createdAt.localeCompare(a.createdAt));

      // ✅ OPTIMALISATIE: Behoud maximaal 10 backups (was 100!)
      if (backups.length > 10) {
        const toDelete = backups.slice(10);
        console.log(
          `Cleaning up ${toDelete.length} old backups (keeping last 10)`
        );

        for (const backup of toDelete) {
          await deleteDoc(
            doc(db, "artifacts", appId, "public", "data", "backups", backup.id)
          );
          console.log("🗑️ Deleted old backup:", backup.id);
        }
        console.log(`✅ Cleanup complete: ${toDelete.length} backups removed`);
      } else {
        console.log(`✅ Backups OK: ${backups.length}/10 stored`);
      }
    } catch (e) {
      console.error("⚠️ Backup cleanup failed (non-critical):", e);
    }
  };

  const emergencyRestore = async () => {
    try {
      const localBackup = localStorage.getItem("cp_last_known_good");
      if (localBackup) {
        const lastSaveTime = localStorage.getItem("cp_last_save_time");
        const confirmMsg = `Er staat een back-up op dit apparaat van ${lastSaveTime}.\n\nTerugzetten?`;

        if (await cpConfirm(confirmMsg)) {
          const restoredData = JSON.parse(localBackup);
          setAppData(sanitizeData(restoredData));

          if (user && db) {
            const docRef = doc(
              db,
              "artifacts",
              appId,
              "public",
              "data",
              "cp_live_data",
              "master"
            );
            setDoc(docRef, restoredData);
          }

          cpToast("Alles is terug. Pfoe!");
          return true;
        }
      } else {
        cpToast("Geen back-up op dit apparaat gevonden.");
        return false;
      }
    } catch (e) {
      console.error("Emergency restore failed:", e);
      cpToast("Terugzetten lukt even niet: " + e.message);
      return false;
    }
  };

  const triggerAlert = (message) => setAlertParams({ isOpen: true, message });
  const triggerConfirm = (message, onConfirm) => {
    // If onConfirm is not provided, return a Promise
    if (!onConfirm) {
      return new Promise((resolve) => {
        setConfirmParams({
          isOpen: true,
          message,
          onConfirm: () => {
            resolve(true);
            setConfirmParams({
              isOpen: false,
              message: "",
              onConfirm: null,
              onCancel: null,
            });
          },
          onCancel: () => {
            resolve(false);
            setConfirmParams({
              isOpen: false,
              message: "",
              onConfirm: null,
              onCancel: null,
            });
          },
        });
      });
    }

    // Legacy callback support
    setConfirmParams({
      isOpen: true,
      message,
      onConfirm: () => {
        if (typeof onConfirm === "function") {
          onConfirm();
        }
        setConfirmParams({
          isOpen: false,
          message: "",
          onConfirm: null,
          onCancel: null,
        });
      },
      onCancel: () => {
        setConfirmParams({
          isOpen: false,
          message: "",
          onConfirm: null,
          onCancel: null,
        });
      },
    });
  };

  // 🔒 Pincode-blokkade: na 5 verkeerde pogingen 1 minuut wachten
  const PIN_MAX_FOUTEN = 5;
  const pinBlokkade = () => {
    try {
      const b = JSON.parse(localStorage.getItem("cp_pinFouten") || "{}");
      return b.tot && b.tot > Date.now() ? Math.ceil((b.tot - Date.now()) / 1000) : 0;
    } catch (e) {
      return 0;
    }
  };
  // De standaardlijst (oude namen, pincode 0000) staat alleen in de app tot de
  // echte medewerkers uit Firebase binnen zijn. Inlogscherm toont dan de laatst
  // bekende echte lijst van dit toestel, en inloggen wacht even.
  const staffLijstEcht = useMemo(() => {
    const l = appData.staffList || [];
    return !(l.length === DEFAULT_STAFF_NAMES.length && l.every((s) => DEFAULT_STAFF_NAMES.includes(s.name) && s.pin === "0000"));
  }, [appData.staffList]);
  useEffect(() => {
    if (!staffLijstEcht || !(appData.staffList || []).length) return;
    try {
      localStorage.setItem(
        "cp_loginStaff",
        JSON.stringify(
          appData.staffList.map((s) => ({
            name: s.name,
            inactive: !!s.inactive,
            verborgen: !!s.verborgen,
            theme: (s.name === "Maik" ? appData.settings?.fmTheme : undefined) || s.theme || undefined,
            fotoKleur: s.fotoKleur || undefined,
            // eigen profielfoto's zijn klein (±15 kB): die mogen mee voor het inlogscherm
            profilePhoto: typeof s.profilePhoto === "string" && s.profilePhoto.length < 40000 ? s.profilePhoto : undefined,
          }))
        )
      );
    } catch (e) {}
  }, [appData.staffList, staffLijstEcht, appData.settings?.fmTheme]);
  const loginStaffList = useMemo(() => {
    if (staffLijstEcht) return appData.staffList || [];
    try {
      const c = JSON.parse(localStorage.getItem("cp_loginStaff") || "null");
      if (Array.isArray(c)) return c;
    } catch (e) {}
    return [];
  }, [appData.staffList, staffLijstEcht]);

  const triggerPinError = () => {
    try {
      const b = JSON.parse(localStorage.getItem("cp_pinFouten") || "{}");
      const n = (b.tot && b.tot > Date.now() ? 0 : b.n || 0) + 1;
      localStorage.setItem(
        "cp_pinFouten",
        JSON.stringify(n >= PIN_MAX_FOUTEN ? { n: 0, tot: Date.now() + 60000 } : { n })
      );
      if (n >= PIN_MAX_FOUTEN) triggerAlert("Te vaak een verkeerde pincode. Probeer het over een minuut opnieuw.");
    } catch (e) {}
    setPinLoginError(true);
    setTimeout(() => setPinLoginError(false), 600);
    setPin("");
  };

  // Roostermaker-recht: FM altijd; medewerkers alleen als dat in Beheer aan staat
  // ("bekijken" of "bewerken")
  const rmRecht =
    userRole === "fm" || userRole === "admin"
      ? "bewerken"
      : userRole === "staff"
      ? (appData.staffList || []).find((s) => s.name === loggedInUserName)?.roostermaker || null
      : null;
  // Afscheidsboek: FM altijd; team alleen als er een open boek is dat niet voor henzelf is
  const afscheidZichtbaar =
    userRole === "fm" || userRole === "admin"
      ? true
      : userRole === "staff" &&
        Object.values(appData.afscheid?.boeken || {}).some((b) => {
          if (!b || b.weg || !b.open) return false;
          const sl = (x) => String(x || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "_");
          const a = sl(b.naam).split("_")[0];
          const ik = sl(loggedInUserName);
          if (!a || a === ik.split("_")[0]) return false;
          return !Array.isArray(b.wie) || b.wie.some((w) => sl(w) === ik);
        });
  // 🎁 Pietenservice: zichtbaar voor FM/CO altijd; voor het team zodra er een avond is
  const pietenZichtbaar =
    userRole === "fm" || userRole === "admin" || userRole === "co" || Object.values(appData.pietenservice?.avonden || {}).some((a) => a && !a.weg);
  const rmUpdate =
    rmRecht === "bekijken"
      ? () => triggerAlert("Je kunt de roostermaker alleen bekijken.")
      : saveData;

  const handleLogin = () => {
    const wacht = pinBlokkade();
    if (wacht) {
      triggerAlert(`Even wachten: je kunt over ${wacht} seconden opnieuw proberen.`);
      setPin("");
      return;
    }
    const fmPin = appData.settings?.pin || "2412";
    const coPin = appData.settings?.coPin || "1234";
    if (loginMode === "fm") {
      if (pin === fmPin) {
        setIsLoggedIn(true);
        setUserRole("fm");
        setLoggedInUserName("Maik");
        setView("dagbord");
        setCurrentDay(new Date());
        // Persist login
        localStorage.setItem("cp_loggedInUser", "Maik");
        localStorage.setItem("cp_userRole", "fm");
        localStorage.setItem("cp_loginTime", new Date().toISOString());
      } else triggerPinError();
    } else if (loginMode === "co") {
      if (pin === coPin) {
        setIsLoggedIn(true);
        setUserRole("co");
        setLoggedInUserName("Central Office");
        setView("schedule");
        setCurrentDay(new Date());
        // Persist login
        localStorage.setItem("cp_loggedInUser", "Central Office");
        localStorage.setItem("cp_userRole", "co");
        localStorage.setItem("cp_loginTime", new Date().toISOString());
      } else triggerPinError();
    } else if (loginMode === "staff") {
      if (!staffLijstEcht) {
        triggerAlert("Even geduld, de medewerkers worden nog geladen. Probeer het zo opnieuw.");
        setPin("");
        return;
      }
      const userObj = appData.staffList?.find((s) => s.name === selectedStaff);
      if (!userObj) {
        triggerPinError();
        return;
      }
      if (pin === userObj.pin) {
        if (userObj.forcePinChange) {
          setIsLoggedIn(true);
          setUserRole("staff");
          setLoggedInUserName(selectedStaff);
          setView("force_pin_change");
          // Persist login
          localStorage.setItem("cp_loggedInUser", selectedStaff);
          localStorage.setItem("cp_userRole", "staff");
          localStorage.setItem("cp_loginTime", new Date().toISOString());
        } else {
          setIsLoggedIn(true);
          setUserRole("staff");
          setLoggedInUserName(selectedStaff);
          setView("dagbord");
          setCurrentDay(new Date());
          // Persist login
          localStorage.setItem("cp_loggedInUser", selectedStaff);
          localStorage.setItem("cp_userRole", "staff");
          localStorage.setItem("cp_loginTime", new Date().toISOString());
          const newStaffList = appData.staffList.map((s) =>
            s.name === selectedStaff
              ? { ...s, lastLogin: new Date().toISOString() }
              : s
          );
          saveData({ staffList: newStaffList });
        }
      } else triggerPinError();
    }
    setPin("");
  };

  const resetAllData = async () => {
    triggerConfirm("Weet je zeker dat je ALLE data wilt wissen?", async () => {
      const invoer = window.prompt("Typ de FM-pincode om ALLE data te wissen:");
      if (invoer === null) return;
      if (invoer !== (appData.settings?.pin || "2412")) {
        triggerAlert("Verkeerde pincode. Er is niets gewist.");
        return;
      }
      // Eerst een back-up downloaden, zodat alles terug te zetten is
      try {
        const blob = new Blob([JSON.stringify(appData)], { type: "application/json" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `backup-voor-wissen-${toIsoDate(new Date())}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (e) {
        triggerAlert("Back-up maken lukte niet, daarom is er niets gewist.");
        return;
      }
      if (user && db) {
        const docRef = doc(
          db,
          "artifacts",
          appId,
          "public",
          "data",
          "cp_live_data",
          "master"
        );
        await setDoc(docRef, INITIAL_DATA);
      } else {
        setAppData(INITIAL_DATA);
      }
    });
  };
  const handleReadNotification = (id) => {
    const updatedNotifs = appData.notifications.map((n) =>
      n.id === id
        ? { ...n, readBy: [...(n.readBy || []), loggedInUserName] }
        : n
    );
    saveData({ notifications: updatedNotifs });
  };
  const handleDismissMentionNotif = () => {
    if (!activeMentionNotif) return;
    dismissedMentionIds.current.add(activeMentionNotif.id);
    const updatedNotifs = (appData.notifications || []).map((n) =>
      n.id === activeMentionNotif.id
        ? { ...n, readBy: [...(n.readBy || []), loggedInUserName] }
        : n
    );
    saveData({ notifications: updatedNotifs });
    setActiveMentionNotif(null);
  };
  const handleDismissVerlofNotif = () => {
    if (!activeVerlofNotif) return;
    dismissedVerlofIds.current.add(activeVerlofNotif.id);
    const updatedNotifs = (appData.notifications || []).map((n) =>
      n.id === activeVerlofNotif.id
        ? { ...n, readBy: [...(n.readBy || []), loggedInUserName] }
        : n
    );
    saveData({ notifications: updatedNotifs });
    setActiveVerlofNotif(null);
  };
  // Goedkeuren/afwijzen direct vanuit de verlofaanvraag-popup (zelfde
  // delta-per-datum patroon als setAvailStatus in BeschikbaarheidView).
  const handleVerlofDecision = (name, date, status) => {
    const existing = appData.staffAvailability?.[name]?.[date];
    if (!existing) return;
    const updatedEntry = {
      ...existing,
      status,
      ...(status === "approved"
        ? { approvedBy: loggedInUserName, approvedAt: new Date().toISOString() }
        : {}),
      ...(status === "rejected"
        ? { rejectedBy: loggedInUserName, rejectedAt: new Date().toISOString() }
        : {}),
    };
    // 🩹 FIX: bij goedkeuren/afwijzen via de popup-knoppen werd de melding
    // zelf nooit als gelezen gemarkeerd (dat gebeurde alleen bij het sluiten
    // via het kruisje) — daardoor bleef de aanvraag als "ongelezen" gelden
    // en verscheen 'm bij elke nieuwe login opnieuw, ook al was 'm al
    // afgehandeld. Nu markeren we 'm meteen als gelezen bij een beslissing.
    const updatedNotifs = activeVerlofNotif
      ? (appData.notifications || []).map((n) =>
          n.id === activeVerlofNotif.id && !n.readBy?.includes(loggedInUserName)
            ? { ...n, readBy: [...(n.readBy || []), loggedInUserName] }
            : n
        )
      : appData.notifications;
    if (activeVerlofNotif) {
      dismissedVerlofIds.current.add(activeVerlofNotif.id);
    }
    saveData({
      staffAvailability: { [name]: { [date]: updatedEntry } },
      notifications: updatedNotifs,
    });
  };
  const handleLogout = () => {
    setIsLoggedIn(false);
    setUserRole(null);
    setView("login");
    setLoginMode("");
    setSelectedStaff("");
    setPin("");
    // Clear persistent login
    localStorage.removeItem("cp_loggedInUser");
    localStorage.removeItem("cp_userRole");
    localStorage.removeItem("cp_loginTime");
  };
  // Na succesvol inloggen: foutteller op nul
  useEffect(() => {
    if (isLoggedIn) localStorage.removeItem("cp_pinFouten");
  }, [isLoggedIn]);
  // Tikken/typen telt als activiteit; elke minuut checken of 8 uur niets is gebeurd.
  // Promoscherm en DJ-gastpagina loggen nooit uit.
  useEffect(() => {
    if (!isLoggedIn || isExternalPromoMode || isGuestDJMode) return;
    let laatst = 0;
    const actief = () => {
      if (Date.now() - laatst < 60000) return;
      laatst = Date.now();
      if (localStorage.getItem("cp_loggedInUser")) localStorage.setItem("cp_loginTime", new Date().toISOString());
    };
    const check = setInterval(() => {
      if (view === "promo_screen") return actief();
      if (!checkLoginValidity()) handleLogout();
    }, 60000);
    window.addEventListener("pointerdown", actief, { passive: true });
    window.addEventListener("keydown", actief);
    return () => {
      clearInterval(check);
      window.removeEventListener("pointerdown", actief);
      window.removeEventListener("keydown", actief);
    };
  }, [isLoggedIn, view]);

  const handleLockApp = () => {
    setIsLoggedIn(false);
    setUserRole(null);
    setView("login");
    setLoginMode("");
    setSelectedStaff("");
    setPin("");
    // Clear persistent login (lock = logout)
    localStorage.removeItem("cp_loggedInUser");
    localStorage.removeItem("cp_userRole");
    localStorage.removeItem("cp_loginTime");
  };
  const handleGoToAdmin = () => {
    setAdminSubView("menu");
    setView("admin");
  };
  const nextDay = () => {
    const d = new Date(currentDay);
    if (view === "schedule") d.setDate(d.getDate() + 7);
    else d.setDate(d.getDate() + 1);
    setCurrentDay(d);
  };
  const prevDay = () => {
    const d = new Date(currentDay);
    if (view === "schedule") d.setDate(d.getDate() - 7);
    else d.setDate(d.getDate() - 1);
    setCurrentDay(d);
  };

  // #19: Luister naar "Vandaag/Nu"-knop vanuit DateControls
  React.useEffect(() => {
    const handler = () => setCurrentDay(new Date());
    window.addEventListener("datecontrols-goto-today", handler);
    // Weekbalk: specifieke dag kiezen
    const zet = (e) => {
      const d = new Date(e.detail);
      if (!isNaN(d.getTime())) setCurrentDay(d);
    };
    window.addEventListener("datecontrols-set-day", zet);
    return () => {
      window.removeEventListener("datecontrols-goto-today", handler);
      window.removeEventListener("datecontrols-set-day", zet);
    };
  }, []);

  // #20: Reset showCompletedTasks bij dagwisseling
  const dayKey = toIsoDate(currentDay);
  React.useEffect(() => {
    setShowCompletedTasks({});
  }, [dayKey]);
  const currentWeekdayId = currentDay.getDay();
  const todaysSchedule = appData.schedule.filter((s) => s.date === dayKey);
  const todaysCharacters = appData.characters.filter(
    (c) => c.date === dayKey && c.actor
  );
  const chatUnreadCount = (appData.chats || [])
    .filter((c) => c.participants && c.participants.includes(loggedInUserName))
    .reduce((acc, chat) => {
      const n = (chat.messages || []).filter((m) => !(m.readBy || []).includes(loggedInUserName)).length;
      // Zelf als ongelezen gemarkeerd telt als 1
      return acc + (n || ((chat.ongelezenVoor || []).includes(loggedInUserName) ? 1 : 0));
    }, 0);
  const bingoUnreadCount =
    userRole === "co"
      ? (appData.bingoWinners || []).filter((w) => !w.voucherSent && w.prijs !== "voucher25").length
      : 0;

  // 📬 Bereken ongelezen overdrachten voor alle tabs
  // 📬 Bereken ongelezen overdrachten vanaf VANDAAG + TOEKOMST (niet het verleden)
  const dagbordAttention = getDagbordAttention(appData, loggedInUserName);
  // 📦 Oude overdracht is nu archief: geen "ongelezen"-badges/meldingen meer
  // (aandacht loopt via dagbordAttention)
  const handoverUnreadCount = (() => {
    if (true) return 0; // eslint-disable-line no-constant-condition
    if (!appData.handover) return 0;
    const normalizeLogs = (data, dateKey) => {
      if (Array.isArray(data)) return data;
      if (typeof data === "string" && data.trim().length > 0)
        return [
          {
            id: `legacy-${dateKey}`,
            text: data,
            author: "Archief",
            time: "",
            readBy: [],
          },
        ];
      return [];
    };
    let unreadDays = 0;
    for (const dateKey in appData.handover) {
      const dayData = appData.handover[dateKey];
      if (!dayData) continue;
      const dagHeeftOngelezen = [
        "ent",
        "crea",
        "springkussen",
        "hotspot",
        "fm",
      ].some((tab) =>
        normalizeLogs(dayData[tab], dateKey).some(
          (log) =>
            log.author !== loggedInUserName &&
            !log.readBy?.includes(loggedInUserName)
        )
      );
      if (dagHeeftOngelezen) unreadDays++;
    }
    return unreadDays;
  })();

  // Kopieer takenlijst-templates van huidige weekdag naar een andere weekdag
  const handleCopyChecklistToDay = () => {
    if (copyChecklistTargetDay === null) return;
    const types = ["entertainment", "crea", "extra"];
    const newTemplates = { ...appData.checklistTemplates };
    const sourceDay = currentWeekdayId;
    const targetDay = copyChecklistTargetDay;
    const targetDayTemplates = { ...(newTemplates[targetDay] || {}) };
    for (const type of types) {
      const sourceTasks = newTemplates[sourceDay]?.[type] || [];
      if (sourceTasks.length === 0) continue;
      // Voeg toe, maar sla duplicaten over op basis van id
      const existingIds = new Set(
        (targetDayTemplates[type] || []).map((t) => t.id)
      );
      const toAdd = sourceTasks.filter((t) => !existingIds.has(t.id));
      targetDayTemplates[type] = [
        ...(targetDayTemplates[type] || []),
        ...toAdd,
      ];
    }
    newTemplates[targetDay] = targetDayTemplates;
    saveData({ checklistTemplates: newTemplates });
    const dagNamen = [
      "zondag",
      "maandag",
      "dinsdag",
      "woensdag",
      "donderdag",
      "vrijdag",
      "zaterdag",
    ];
    showAlert(`Takenlijst gekopieerd naar ${dagNamen[targetDay]}!`);
    setIsCopyChecklistModalOpen(false);
    setCopyChecklistTargetDay(null);
  };

  const getDailyTasks = (type) => {
    const specific = appData.checklists.filter((c) => {
      if (c.type !== type) return false;
      if (type === "extra") return c.date === "global" || c.date === dayKey;
      return c.date === dayKey;
    });
    const specificIds = new Set(specific.map((t) => String(t.id)));
    const templates = (
      appData.checklistTemplates?.[currentWeekdayId]?.[type] || []
    )
      .filter((t) => !specificIds.has(String(t.id)))
      .map((t) => ({
        ...t,
        done: false,
        isTemplate: true,
        category: t.category || "general",
      })); // ← Let op: twee sluithaakjes hier!

    // 🔧 FIX: Zorg dat ALLE taken een category hebben (ook oude taken)
    const allTasks = [...specific, ...templates].map((task) => ({
      ...task,
      category: task.category || "general",
    }));

    return allTasks;
  };
  const [lastCheckedId, setLastCheckedId] = React.useState(null);
  // Takenlijst: "Niet gedaan" vraagt eerst waarom (zelfde als op het Dagbord)
  const [nietGedaan, setNietGedaan] = React.useState(null); // { task, tekst }
  const nietGedaanBevestig = () => {
    if (!nietGedaan || !nietGedaan.tekst.trim()) return;
    handleNvtTask(nietGedaan.task, nietGedaan.tekst.trim());
    setNietGedaan(null);
  };

  const handleCheckTask = (task) => {
    let newChecklists = [...appData.checklists];
    const doneAt = new Date().toISOString();
    const existingIndex = newChecklists.findIndex(
      (c) =>
        String(c.id) === String(task.id) &&
        (c.date === dayKey || (c.type === "extra" && c.date === "global"))
    );
    if (existingIndex >= 0) {
      const wordtGedaan = !newChecklists[existingIndex].done;
      newChecklists[existingIndex] = {
        ...newChecklists[existingIndex],
        done: wordtGedaan,
        doneBy: wordtGedaan ? loggedInUserName : null,
        doneAt: wordtGedaan ? doneAt : null,
        // Gedaan en 'niet gedaan' sluiten elkaar uit
        ...(wordtGedaan ? { nvt: false, nvtReason: null, nvtBy: null, nvtAt: null } : {}),
      };
    } else {
      newChecklists.push({
        id: task.id,
        text: task.text,
        type: task.type,
        priority: task.priority,
        category: task.category,
        date: task.type === "extra" ? "global" : dayKey,
        done: true,
        doneBy: loggedInUserName,
        doneAt: doneAt,
      });
    }
    saveData({ checklists: newChecklists }, { direct: true });
    // #23: Flash animatie — alleen bij aftikken (niet bij un-checken)
    const isNowDone =
      existingIndex >= 0
        ? !newChecklists[existingIndex - 1]?.done // na mutatie
        : true;
    setLastCheckedId(task.id);
    setTimeout(() => setLastCheckedId(null), 600);
  };

  const handleNvtTask = (task, reden) => {
    const nvtInfo = (aan) =>
      aan
        ? { nvtReason: reden || null, nvtBy: loggedInUserName || null, nvtAt: new Date().toISOString() }
        : { nvtReason: null, nvtBy: null, nvtAt: null };
    let newChecklists = [...appData.checklists];
    const existingIndex = newChecklists.findIndex(
      (c) =>
        String(c.id) === String(task.id) &&
        (c.date === dayKey || (c.type === "extra" && c.date === "global"))
    );
    if (existingIndex >= 0) {
      newChecklists[existingIndex] = {
        ...newChecklists[existingIndex],
        nvt: !newChecklists[existingIndex].nvt,
        ...nvtInfo(!newChecklists[existingIndex].nvt),
        done: false,
        doneBy: null,
        doneAt: null,
      };
    } else {
      newChecklists.push({
        id: task.id,
        text: task.text,
        type: task.type,
        priority: task.priority,
        category: task.category,
        date: task.type === "extra" ? "global" : dayKey,
        done: false,
        nvt: true,
        ...nvtInfo(true),
        doneBy: null,
        doneAt: null,
      });
    }
    saveData({ checklists: newChecklists }, { direct: true });
  };
  const entTasks = getDailyTasks("entertainment");
  const creaTasks = getDailyTasks("crea");
  const extraTasks = getDailyTasks("extra");
  const hotspotTasks = getDailyTasks("hotspot");
  const groupedEntTasks = groupTasksByCategory(entTasks);
  const groupedCreaTasks = groupTasksByCategory(creaTasks);
  const groupedExtraTasks = groupTasksByCategory(extraTasks);
  const groupedHotspotTasks = groupTasksByCategory(hotspotTasks);
  const getDayCharacters = (date) => {
    const dKey = toIsoDate(date);
    const existing = appData.characters.filter((c) => c.date === dKey);
    const baseRolesMap = BASE_ROLES.reduce((acc, role) => {
      acc[role] = existing.find((c) => c.role === role) || {
        id: null,
        role,
        actor: "",
        pakCheck: false,
        date: dKey,
        isExtra: false,
      };
      return acc;
    }, {});
    const extraRoles = existing
      .filter((c) => !BASE_ROLES.includes(c.role))
      .map((c) => ({ ...c, isExtra: true }));
    return [...Object.values(baseRolesMap), ...extraRoles];
  };
  const getUpcomingSchedule = () => {
    const today = new Date(currentDay);
    today.setHours(0, 0, 0, 0);
    const allItems = appData.schedule
      .map((s) => ({ ...s, parsedDate: new Date(s.date) }))
      .filter((s) => s.parsedDate >= today)
      .sort(
        (a, b) => a.parsedDate - b.parsedDate || a.time.localeCompare(b.time)
      );
    const grouped = {};
    allItems.forEach((item) => {
      const dKey = toIsoDate(item.parsedDate);
      if (!grouped[dKey]) grouped[dKey] = [];
      grouped[dKey].push(item);
    });
    return grouped;
  };
  const groupedSchedule = getUpcomingSchedule();
  const todayStr = new Date().toISOString().split("T")[0];
  const isDayPlanned = appData.planningStatus?.[dayKey]?.isSystemReady || false;
  const toggleDayPlanned = () => {
    saveData({
      planningStatus: {
        ...appData.planningStatus,
        [dayKey]: { isSystemReady: !isDayPlanned },
      },
    });
  };
  const updateDailyRole = (dateKey, role, name) => {
    const currentRoles = appData.dailyRoles?.[dateKey] || {};
    saveData({
      dailyRoles: {
        ...appData.dailyRoles,
        [dateKey]: { ...currentRoles, [role]: name },
      },
    });
  };
  const sortedChecklists = {
    entertainment: entTasks,
    crea: creaTasks,
    extra: extraTasks,
  };

  const renderGroupedTasks = (groups, color, tabKey = "default") => {
    const sortedKeys = Object.keys(groups)
      .filter((cat) => TASK_CATS[cat])
      .sort((a, b) => {
        const orderA = TASK_CATS[a]?.sortOrder || 99;
        const orderB = TASK_CATS[b]?.sortOrder || 99;
        return orderA - orderB;
      });

    // Check if all tasks are done
    const allTasks = sortedKeys.flatMap((key) => groups[key] || []);
    const allDone =
      allTasks.length > 0 && allTasks.every((t) => t.done || t.nvt);

    if (allTasks.length === 0) {
      return (
        <EmptyState
          icon={ListChecks}
          message="Geen taken vandaag. Lekker rustig!"
          sub="Taken komen uit het weeksjabloon in FM Beheer → Takenbeheer."
        />
      );
    }

    if (allDone && !showCompletedTasks[tabKey]) {
      return (
        <div className="text-center py-10">
          <div
            className="inline-flex p-5 rounded-2xl mb-3"
            style={{ backgroundColor: `${color}15` }}
          >
            <PartyPopper size={40} style={{ color }} />
          </div>
          <h3 className="text-lg font-semibold mb-1" style={{ color: C.Lagoon }}>
            Alles afgevinkt! 🎉
          </h3>
          <p className="text-sm" style={{ color: C.TextSecondary }}>
            Geweldig werk! Alle taken voor deze afdeling zijn voltooid.
          </p>
          <button
            onClick={() =>
              setShowCompletedTasks((prev) => ({ ...prev, [tabKey]: true }))
            }
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all hover:opacity-80 active:scale-95"
            style={{
              borderColor: `${color}40`,
              color,
              backgroundColor: `${color}12`,
            }}
          >
            <Eye size={16} />
            Bekijk taken
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-5">
        {sortedKeys.map((cat) => {
          const tasks = groups[cat];
          if (!tasks || tasks.length === 0) return null;

          const catInfo = TASK_CATS[cat] || TASK_CATS.general;
          const Icon = catInfo.icon || Clipboard;
          const categoryColor = catInfo.color || color; // 🎨 Gebruik categorie-specifieke kleur

          // Sort: priority only, keep original order (no done-tasks-to-bottom)
          const sortedTasks = [...tasks].sort((a, b) => {
            const priorityOrder = { high: 0, normal: 1, low: 2 };
            return (
              (priorityOrder[a.priority || "normal"] || 1) -
              (priorityOrder[b.priority || "normal"] || 1)
            );
          });

          const doneCount = tasks.filter((t) => t.done || t.nvt).length;
          const totalCount = tasks.length;
          const progress = (doneCount / totalCount) * 100;

          return (
            <div key={cat}>
              {/* 📊 Category Header — clean icon-chip */}
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${categoryColor}22` }}
                  >
                    <Icon size={18} style={{ color: categoryColor }} />
                  </div>
                  <div className="min-w-0">
                    <h4
                      className="text-sm font-semibold truncate"
                      style={{ color: C.Lagoon }}
                    >
                      {catInfo.label}
                    </h4>
                    <p className="text-xs" style={{ color: C.TextMuted }}>
                      {doneCount} van {totalCount} voltooid
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {progress === 100 && (
                    <span
                      className="text-xs font-semibold px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: `${categoryColor}20`,
                        color: categoryColor,
                      }}
                    >
                      ✓ Klaar
                    </span>
                  )}
                  <div
                    className="w-20 h-1.5 rounded-full overflow-hidden"
                    style={{ backgroundColor: `${categoryColor}15` }}
                  >
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${progress}%`,
                        backgroundColor: categoryColor,
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* 📋 Tasks */}
              <div className="space-y-1.5">
                {sortedTasks.map((t) => (
                  <div
                    key={t.id}
                    className={`w-full text-left flex items-center gap-2.5 p-2.5 rounded-xl border transition-colors group relative ${
                      lastCheckedId === t.id ? "task-flash" : ""
                    }`}
                    style={{
                      backgroundColor: t.done
                        ? `${categoryColor}08`
                        : t.nvt
                        ? `${C.TextMuted}08`
                        : C.White,
                      borderColor: t.done
                        ? `${categoryColor}25`
                        : t.nvt
                        ? `${C.TextMuted}25`
                        : "#F0F1F3",
                      // Afdelingskleur als rand links (zoals op het Dagbord); hoge prioriteit = Sunset
                      borderLeftWidth: "3px",
                      borderLeftColor:
                        t.priority === "high" && !t.done && !t.nvt
                          ? C.Sunset
                          : t.done || t.nvt
                          ? `${color}55`
                          : color,
                    }}
                  >
                    {/* 📝 Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <span
                          className={`text-sm leading-snug ${
                            t.done || t.nvt ? "line-through" : ""
                          }`}
                          style={{
                            color:
                              t.done || t.nvt ? C.TextMuted : C.TextPrimary,
                          }}
                        >
                          {t.text}
                        </span>

                        {/* 🎯 Priority Badge */}
                        {!t.done && !t.nvt && t.priority === "high" && (
                          <CpPil kleur="blossom" icon={AlertCircle} className="shrink-0">
                            Prioriteit
                          </CpPil>
                        )}
                      </div>

                      {/* ✅ Gedaan door … (als pil) */}
                      {t.done && t.doneBy && (
                        <div className="mt-1">
                          <CpPil kleur="pine" icon={UserCheck}>
                            {getFirstName(t.doneBy)}
                            {t.doneAt
                              ? ` · ${new Date(t.doneAt).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}`
                              : ""}
                          </CpPil>
                        </div>
                      )}

                      {/* Niet gedaan + reden */}
                      {t.nvt && (
                        <p className="text-xs mt-1 text-gray-500 flex flex-wrap items-center gap-1.5">
                          <CpPil kleur="honey">
                            Niet gedaan{t.nvtBy ? ` · ${getFirstName(t.nvtBy)}` : ""}
                          </CpPil>
                          {t.nvtReason ? <span>{t.nvtReason}</span> : null}
                        </p>
                      )}
                    </div>

                    {/* ✓ gedaan / ✕ niet gedaan */}
                    <TaakStatusKnoppen
                      done={t.done}
                      nvt={t.nvt}
                      pop={lastCheckedId === t.id && t.done}
                      tekst={t.text}
                      onDone={() => handleCheckTask(t)}
                      onNiet={() =>
                        t.nvt ? handleNvtTask(t) : setNietGedaan({ task: t, tekst: "" })
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) return <LaadScherm stap={laadStap} />;

  

  // 🎯 WEEKFOCUS BLOCK — klikbare focuspunten met detail-popup
  const WeekFocusBlock = React.memo(
    ({ appData, theme, loggedInUserName, saveData }) => {
      const [openItem, setOpenItem] = useState(null); // { ...item, idx }
      const focusPunten = normalizeFocus(appData.settings?.weekFocus);

      const logoColor = CP_LOGO_COLORS[theme?.id] || "#b4d6a1";
      const primary = theme?.primary || "#2d6a4f";

      // 🔒 Lock body scroll op iOS wanneer modal open is
      useEffect(() => {
        if (openItem !== null) {
          const prev = document.body.style.overflow;
          document.body.style.overflow = "hidden";
          return () => {
            document.body.style.overflow = prev;
          };
        }
      }, [openItem !== null]);

      const closeModal = () => setOpenItem(null);

      // Niet renderen als er geen punten zijn én er geen popup open is
      if (focusPunten.length === 0 && !openItem) return null;

      const hasCommitted = (item) =>
        (item.commits || []).some((c) => c.name === loggedInUserName);

      const handleCommit = () => {
        if (!openItem || hasCommitted(openItem)) return;
        const idx = openItem.idx;
        const newCommit = { name: loggedInUserName, ts: Date.now() };
        const updatedItem = {
          ...openItem,
          commits: [...(openItem.commits || []), newCommit],
        };
        const updated = focusPunten.map((p, i) =>
          i === idx ? updatedItem : p
        );
        // Update popup state direct zodat de knop meteen verandert
        setOpenItem(updatedItem);
        saveData({ settings: { ...appData.settings, weekFocus: updated } });
      };

      return (
        <>
          <div
            className="mx-4 mb-4 rounded-2xl overflow-hidden"
            style={{
              border: `1px solid ${primary}25`,
              boxShadow: "0 2px 8px rgba(17,24,39,0.06)",
            }}
          >
            <div
              className="flex items-center justify-between px-3 py-2.5"
              style={{ backgroundColor: primary }}
            >
              <div className="flex items-center gap-2">
                <span className="text-base">🎯</span>
                <span
                  className="text-xs font-semibold uppercase tracking-wide"
                  style={{ color: logoColor }}
                >
                  Deze week focussen we ons op
                </span>
              </div>
              <span
                className="text-xs px-2 py-0.5 rounded-full font-medium"
                style={{ backgroundColor: "rgba(0,0,0,0.2)", color: logoColor }}
              >
                Tik voor meer info
              </span>
            </div>
            <div
              className="bg-white divide-y"
              style={{ borderColor: `${primary}15` }}
            >
              {focusPunten.map((item, i) => {
                const committed = hasCommitted(item);
                const commitCount = (item.commits || []).length;
                return (
                  <button
                    key={i}
                    onClick={() => setOpenItem({ ...item, idx: i })}
                    className="w-full flex items-center gap-3 px-3 py-3 text-left active:bg-gray-50 transition-colors"
                  >
                    <span
                      className="flex-shrink-0 text-xs font-semibold w-5 h-5 rounded-full flex items-center justify-center text-white"
                      style={{ backgroundColor: primary, fontSize: "10px" }}
                    >
                      {i + 1}
                    </span>
                    <span className="flex-1 text-sm text-gray-800 leading-snug">
                      {item.title}
                    </span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {committed && (
                        <span
                          className="text-xs font-semibold px-2 py-0.5 rounded-full flex items-center gap-1"
                          style={{
                            backgroundColor: `${primary}15`,
                            color: primary,
                          }}
                        >
                          <Check size={10} /> Ik doe het!
                        </span>
                      )}
                      {commitCount > 0 && !committed && (
                        <span className="text-xs text-gray-500">
                          {commitCount}✓
                        </span>
                      )}
                      <ChevronRight
                        size={14}
                        style={{ color: `${primary}60` }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* DETAIL POPUP — via Portal, buiten scroll-container */}
          {openItem &&
            createPortal(
              <div
                className="fixed inset-0 z-[75] flex items-end md:items-center justify-center sm:p-4"
                style={{ backgroundColor: "rgba(0,25,47,0.45)" }}
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) closeModal();
                }}
                onTouchStart={(e) => {
                  if (e.target === e.currentTarget) closeModal();
                }}
              >
                <div
                  className="absolute inset-0"
                  style={{
                    backdropFilter: "blur(4px)",
                    WebkitBackdropFilter: "blur(4px)",
                    pointerEvents: "none",
                  }}
                />
                <div
                  className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl"
                  style={{
                    cursor: "default",
                    maxHeight: "85vh",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    className="flex-shrink-0 relative p-5"
                    style={{ backgroundColor: primary }}
                  >
                    <div className="flex items-start gap-3 pr-8">
                      <span className="text-2xl mt-0.5">🎯</span>
                      <div>
                        <p
                          className="text-xs font-semibold uppercase tracking-widest mb-1 opacity-70"
                          style={{ color: logoColor }}
                        >
                          Focuspunt
                        </p>
                        <h3
                          className="font-semibold text-base leading-snug"
                          style={{ color: logoColor }}
                        >
                          {openItem.title}
                        </h3>
                      </div>
                    </div>
                    <button aria-label="Sluiten"
                      onClick={closeModal}
                      className="absolute top-4 right-4 p-1.5 rounded-lg opacity-70"
                      style={{ backgroundColor: "rgba(0,0,0,0.2)" }}
                    >
                      <CloseIcon size={16} style={{ color: logoColor }} />
                    </button>
                  </div>
                  <div
                    className="bg-white p-5 overflow-y-auto"
                    style={{
                      WebkitOverflowScrolling: "touch",
                      overscrollBehavior: "contain",
                    }}
                  >
                    {openItem.tips && openItem.tips.length > 0 && (
                      <>
                        <p
                          className="text-xs font-semibold uppercase tracking-wide mb-3"
                          style={{ color: primary }}
                        >
                          Hoe werken we hieraan?
                        </p>
                        <div className="space-y-2.5 mb-5">
                          {openItem.tips.map((tip, j) => (
                            <div key={j} className="flex items-start gap-3">
                              <div
                                className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mt-0.5"
                                style={{
                                  backgroundColor: `${primary}15`,
                                  color: primary,
                                }}
                              >
                                {j + 1}
                              </div>
                              <p className="text-sm text-gray-800 leading-relaxed flex-1">
                                {tip}
                              </p>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                    {hasCommitted(openItem) ? (
                      <div
                        className="w-full py-3.5 rounded-xl font-semibold text-center flex items-center justify-center gap-2 mb-3"
                        style={{
                          backgroundColor: `${primary}15`,
                          color: primary,
                        }}
                      >
                        <CheckCheck size={18} />
                        Jij gaat hieraan werken! 💪
                      </div>
                    ) : (
                      <button
                        onClick={() => handleCommit()}
                        className="w-full py-3.5 rounded-xl font-semibold text-white flex items-center justify-center gap-2 mb-3 shadow-sm"
                        style={{ backgroundColor: primary }}
                      >
                        <ArrowRight size={18} />
                        Hier ga ik aan werken!
                      </button>
                    )}
                    {(openItem.commits || []).length > 0 && (
                      <div
                        className="rounded-xl px-3 py-2.5 flex flex-wrap gap-1.5 mb-1"
                        style={{ backgroundColor: `${primary}08` }}
                      >
                        <span className="text-xs text-gray-500 w-full mb-0.5">
                          Ook aan het werk:
                        </span>
                        {openItem.commits.map((c, k) => (
                          <span
                            key={k}
                            className="text-xs px-2 py-0.5 rounded-full font-medium"
                            style={{
                              backgroundColor: `${primary}15`,
                              color: primary,
                            }}
                          >
                            {c.name.split(" ")[0]}
                          </span>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={closeModal}
                      className="w-full mt-3 py-2.5 rounded-xl font-medium text-gray-500 border border-gray-200"
                    >
                      Sluiten
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}
        </>
      );
    },
    (prev, next) =>
      JSON.stringify(prev.appData.settings?.weekFocus) ===
        JSON.stringify(next.appData.settings?.weekFocus) &&
      prev.loggedInUserName === next.loggedInUserName &&
      prev.theme?.id === next.theme?.id
  );

  // 🎵 DJ Night gast-modus: render gastview direct, geen login nodig
  if (isGuestDJMode) {
    return <DJGuestView />;
  }

  return (
    <div
      className="min-h-screen w-screen max-w-full font-sans flex flex-col md:flex-row"
      style={{ backgroundColor: themeBg, overflowX: "clip" }}
    >
      {/* 📴 OFFLINE STATUS INDICATOR */}
      <OfflineIndicator
        isOnline={offlineOnline}
        pendingMutations={pendingMutations}
        isSyncing={isSyncing}
      />

      {view !== "login" && !isExternalPromoMode && (
        <MobileMenu
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          setView={setView}
          handleLogout={handleLogout}
          isLoggedIn={isLoggedIn}
          userRole={userRole}
          setCurrentDay={setCurrentDay}
          theme={currentTheme}
          chatCount={chatUnreadCount}
          bingoBadge={bingoUnreadCount}
          handoverBadge={handoverUnreadCount}
          dagbordBadge={dagbordAttention}
          onGoToAdmin={handleGoToAdmin}
          appData={appDataWeergave}
          roostermakerRecht={rmRecht}
          afscheidZichtbaar={afscheidZichtbaar}
          pietenZichtbaar={pietenZichtbaar}
        />
      )}
      {isLoggedIn &&
        !isExternalPromoMode &&
        (userRole === "staff" || userRole === "fm") &&
        loggedInUserName &&
        !["login", "force_pin_change", "user_settings", "promo_screen"].includes(view) &&
        !(loggedInUserName === "Maik"
          ? appData.settings?.fmFoto?.fotoVrij
          : (appData.staffList || []).find((s) => s.name === loggedInUserName)?.fotoVrij) && (
          <ProfielFotoPopup naam={loggedInUserName} staffList={appData.staffList} onMaken={() => setView("user_settings")} />
        )}
      <AlertModal
        isOpen={alertParams.isOpen && !/[?&](voorbeeld=1|dia=)/.test(window.location.search)}
        message={alertParams.message}
        onClose={() => setAlertParams({ isOpen: false, message: "" })}
      />

      {/* 🔔 FM POPUP MELDING - volledig scherm */}
      {!isExternalPromoMode && (
        <NotificationPopup
          notification={activePopupNotif}
          onClose={() => {
            if (!activePopupNotif) return;
            dismissedPopupIds.current.add(activePopupNotif.id);
            // Sla dismiss op in Firebase
            const updated = (appData.settings?.popupNotifications || []).map(
              (n) =>
                n.id === activePopupNotif.id
                  ? {
                      ...n,
                      dismissedBy: [...(n.dismissedBy || []), loggedInUserName],
                    }
                  : n
            );
            saveData({
              settings: { ...appData.settings, popupNotifications: updated },
            });
            setActivePopupNotif(null);
          }}
        />
      )}

      {/* 👋 @MENTION NOTIFICATIE - toast vanuit onderkant */}
      {!activePopupNotif && !isExternalPromoMode && (
        <MentionNotificationPopup
          notification={activeMentionNotif}
          onClose={handleDismissMentionNotif}
          onGoTo={(notif) => {
            if (notif.dateKey)
              setCurrentDay(new Date(notif.dateKey + "T12:00:00"));
            if (notif.source === "dagbord") {
              setView("dagbord");
              gaNaarDagbord({ tab: notif.tab || "overdracht", focus: notif.focus || null });
              return;
            }
            if (notif.source === "orders") {
              setView("orders");
              return;
            }
            if (notif.source) setPendingHandoverTab(notif.source);
            setView("handover");
          }}
        />
      )}

      {/* 📣 OPEN DIENST - aangeboden aan deze medewerker */}
      {!activePopupNotif && !activeMentionNotif && !isExternalPromoMode && (
        <OpenDienstPopup
          notification={activeOpenNotif}
          onPak={() => {
            const n = activeOpenNotif;
            dismissedOpenIds.current.add(n.id);
            setActiveOpenNotif(null);
            const res = openDienstOppakken(appData, n.date, n.dienstId, loggedInUserName);
            if (!res.ok) {
              showAlert(res.reden);
              return;
            }
            saveData(res.patch);
            showAlert("Top, de dienst is van jou.");
          }}
          onNee={() => {
            const n = activeOpenNotif;
            dismissedOpenIds.current.add(n.id);
            setActiveOpenNotif(null);
            saveData({
              notifications: (appData.notifications || []).map((x) =>
                x.id === n.id ? { ...x, readBy: [...(x.readBy || []), loggedInUserName] } : x
              ),
            });
          }}
        />
      )}

      {/* 🏖️ NIEUWE VERLOFAANVRAAG - popup voor de FM */}
      {!activePopupNotif && !activeMentionNotif && !isExternalPromoMode && (
        <VerlofAanvraagPopup
          notification={activeVerlofNotif}
          appData={appDataWeergave}
          onClose={handleDismissVerlofNotif}
          onApprove={(name, date) =>
            handleVerlofDecision(name, date, "approved")
          }
          onReject={(name, date) =>
            handleVerlofDecision(name, date, "rejected")
          }
        />
      )}
      {promoBlijft && (
        <div
          aria-hidden={view !== "promo_screen"}
          ref={(el) => {
            // Verborgen: niet klikbaar/focusbaar
            if (el) el.inert = view !== "promo_screen";
          }}
          style={
            view === "promo_screen"
              ? undefined
              : { position: "fixed", inset: 0, zIndex: -1, opacity: 0, pointerEvents: "none" }
          }
        >
          <PromoScreenView
            appData={appDataWeergave}
            currentDay={currentDay}
            theme={currentTheme}
            updateData={saveData}
            showAlert={triggerAlert}
            isLoggedIn={isLoggedIn}
            userRole={userRole}
            loggedInUserName={loggedInUserName}
            onTerug={() => setView("dagbord")}
          />
        </div>
      )}
      <CpDialogHost />
      {nieuweVersie && (
        <div
          role="status"
          aria-live="polite"
          className="fixed left-1/2 -translate-x-1/2 bottom-[176px] lg:bottom-8 z-[125] flex items-center gap-3 pl-4 pr-2 min-h-[48px] rounded-2xl shadow-lg w-[calc(100%-2rem)] sm:w-auto sm:max-w-md"
          style={{ backgroundColor: C.Lagoon, color: "white" }}
          data-noswipe
        >
          <RefreshCw size={16} style={{ color: C.Grass }} className="flex-shrink-0" />
          <span className="text-sm flex-1 min-w-0">Nieuwe versie beschikbaar</span>
          <button
            onClick={() => {
              const nietsOpen = Object.keys(pendingChangesRef.current || {}).length === 0;
              if (nietsOpen) window.location.reload();
              else setTimeout(() => window.location.reload(), 1500); // eerst laten opslaan
            }}
            className="h-9 px-3 rounded-xl text-sm font-semibold flex-shrink-0"
            style={{ color: C.Honey }}
          >
            Vernieuwen
          </button>
        </div>
      )}
      {nietGedaan && (
        <div
          className="fixed inset-0 z-[130] flex items-end sm:items-center justify-center bg-[#00192F]/45 sm:p-4"
          onClick={() => setNietGedaan(null)}
          onKeyDown={(e) => e.key === "Escape" && setNietGedaan(null)}
          data-noswipe
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="niet-gedaan-titel"
            className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-xl animate-fade-in-up"
            style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-5 pb-2">
              <p id="niet-gedaan-titel" className="text-base font-semibold" style={{ color: C.Lagoon }}>
                Waarom niet gedaan?
              </p>
              <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{nietGedaan.task.text}</p>
            </div>
            <div className="px-5 py-2">
              <textarea
                autoFocus
                rows={3}
                aria-label="Reden waarom de taak niet gedaan is"
                value={nietGedaan.tekst}
                onChange={(e) => setNietGedaan((v) => ({ ...v, tekst: e.target.value }))}
                placeholder="Schrijf kort waarom deze taak niet gedaan is…"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-base outline-none focus:ring-2 focus:ring-[#045E51]/40 resize-none"
              />
            </div>
            <div className="px-5 pt-1 flex gap-2">
              <button onClick={() => setNietGedaan(null)} className="cp-btn-secondary flex-1">
                Annuleren
              </button>
              <button
                onClick={nietGedaanBevestig}
                disabled={!nietGedaan.tekst.trim()}
                className="flex-1 h-11 rounded-xl text-sm font-semibold text-white transition-opacity active:scale-[0.98]"
                style={{ backgroundColor: C.Pine, opacity: nietGedaan.tekst.trim() ? 1 : 0.4 }}
              >
                Opslaan
              </button>
            </div>
          </div>
        </div>
      )}
      {isLoggedIn && !loading && !isExternalPromoMode && view !== "login" && userRole !== "co" && (
        <UrenHerinnering appData={appDataWeergave} me={loggedInUserName} updateData={saveData} />
      )}
      <ConfirmModal
        isOpen={confirmParams.isOpen}
        message={confirmParams.message}
        onConfirm={confirmParams.onConfirm}
        onCancel={
          confirmParams.onCancel ||
          (() =>
            setConfirmParams({
              isOpen: false,
              message: "",
              onConfirm: null,
              onCancel: null,
            }))
        }
      />

      {/* 💬 ZWEVEND CHATBOLLETJE - altijd zichtbaar voor ingelogde gebruikers (niet op chat pagina en niet op promo) */}
      {isLoggedIn &&
        !isExternalPromoMode &&
        view !== "chat" &&
        view !== "login" && (
          // Mobiel zit chat in de tabbalk onderin
          <div className="hidden md:block">
            <FloatingChatBubble
              chatUnreadCount={chatUnreadCount}
              onClick={() => setView("chat")}
              theme={currentTheme}
              loggedInUserName={loggedInUserName}
              appData={appDataWeergave}
            />
          </div>
        )}

      {isLoggedIn && !isExternalPromoMode && view === "dagbord" && <InstallTip />}
      {isLoggedIn &&
        !isExternalPromoMode &&
        view !== "login" &&
        view !== "promo_screen" && (
          <AppTabBar
            view={view}
            setCurrentDay={setCurrentDay}
            userRole={userRole}
            onMenu={() => setIsMobileMenuOpen((v) => !v)}
            meerOpen={isMobileMenuOpen}
            setView={(v) => {
              setIsMobileMenuOpen(false);
              setView(v);
            }}
            chatCount={chatUnreadCount}
            dagbordBadge={dagbordAttention}
            roosterBadge={
              userRole === "fm" || userRole === "admin"
                ? (appData?.shiftSwapRequests || []).filter((r) => r.status === "pending").length
                : 0
            }
            meerBadge={handoverUnreadCount}
          />
        )}

      {/* 🔒 OPSLAGSTATUS: altijd zichtbaar (klein bolletje), groot bij opslaan/offline/bijwerken */}
      {isLoggedIn && !isExternalPromoMode && view !== "login" && view !== "promo_screen" && (() => {
        const groot = voorlopig || syncStatus !== "synced" || showSyncIndicator;
        const stijl =
          voorlopig || syncStatus === "saving"
            ? { backgroundColor: "#FFE2A1", color: C.Bark, borderColor: C.Honey }
            : syncStatus === "synced"
            ? { backgroundColor: "#DAFFD1", color: C.Pine, borderColor: "#B1EDA6" }
            : { backgroundColor: "#FFB0D6", color: C.Sunset, borderColor: "#FF699B" };
        return (
          <div className="fixed top-[76px] md:top-auto md:bottom-4 right-4 z-50 animate-in fade-in duration-300">
            {!groot ? (
              <div
                className="w-6 h-6 rounded-full border flex items-center justify-center shadow-sm opacity-80"
                style={stijl}
                title="Alles is opgeslagen"
                aria-label="Alles is opgeslagen"
              >
                <Check size={13} />
              </div>
            ) : (
              <div className="px-3 py-1.5 rounded-full shadow-sm flex items-center gap-1.5 text-xs font-semibold border" style={stijl}>
                {voorlopig ? (
                  <>
                    <RefreshCw size={14} className="animate-spin" />
                    <span>Bijwerken…</span>
                  </>
                ) : syncStatus === "synced" ? (
                  <>
                    <CheckCircle size={14} />
                    <span>Opgeslagen</span>
                  </>
                ) : syncStatus === "saving" ? (
                  <>
                    <RefreshCw size={14} className="animate-spin" />
                    <span>Opslaan…</span>
                  </>
                ) : syncStatus === "offline" ? (
                  <>
                    <WifiOff size={14} />
                    <span>
                      Offline{pendingMutations > 0 ? ` · ${pendingMutations} wijziging${pendingMutations === 1 ? "" : "en"} wacht${pendingMutations === 1 ? "" : "en"}` : " · lokaal bewaard"}
                    </span>
                    <button
                      onClick={() => saveData({})}
                      className="ml-1 px-2 py-0.5 rounded text-xs font-semibold text-white"
                      style={{ backgroundColor: C.Sunset }}
                    >
                      Opnieuw
                    </button>
                  </>
                ) : (
                  <>
                    <AlertCircle size={14} />
                    <span>Opslaan lukte niet</span>
                    <button
                      onClick={() => saveData({})}
                      className="ml-1 px-2 py-0.5 rounded text-xs font-semibold text-white"
                      style={{ backgroundColor: C.Sunset }}
                    >
                      Opnieuw
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* 🌐 CONNECTION STATUS TOAST */}
      {showConnectionToast && isLoggedIn && (
        <div className="fixed top-4 right-4 z-50 animate-in fade-in slide-in-from-right-2 duration-300">
          <div
            className={`px-4 py-2.5 rounded-xl shadow-lg flex items-center gap-2.5 text-sm font-medium ${
              isOnline ? "bg-white" : "bg-[#FFB0D6] border border-[#FF699B]/50"
            }`}
            style={{
              border: isOnline
                ? `2px solid ${currentTheme.secondary}`
                : undefined,
              boxShadow: isOnline
                ? `0 4px 12px ${currentTheme.primary}30`
                : "0 4px 12px rgba(239, 68, 68, 0.2)",
            }}
          >
            {isOnline ? (
              <>
                <div
                  className="w-2 h-2 rounded-full animate-pulse"
                  style={{ backgroundColor: currentTheme.primary }}
                />
                <span style={{ color: currentTheme.primary }}>
                  {connectionMessage}
                </span>
              </>
            ) : (
              <>
                <WifiOff size={16} className="text-[#700606]" />
                <span className="text-[#700606]">{connectionMessage}</span>
              </>
            )}
          </div>
        </div>
      )}

      {!isExternalPromoMode && isLoggedIn && view !== "planner" && view !== "promo_screen" && (
        <div
          className="hidden md:flex w-60 flex-col m-3 mr-0 p-2.5 bg-white rounded-2xl border border-gray-100 shadow-sm z-20 flex-shrink-0 relative overflow-hidden"
          style={{ height: "calc(100vh - 1.5rem)" }}
        >
          {/* Dagbord-stijl: geen patroon meer in de zijbalk */}
          {false && (
            <div
              className="absolute inset-0 pointer-events-none z-0"
              style={{
                backgroundImage: `url(${
                  PATTERN_BACKGROUNDS[currentTheme?.id]
                })`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                opacity: 0.04,
              }}
            />
          )}
          <div className="px-2 pt-2 pb-3 mb-1 flex-shrink-0 flex flex-col items-start gap-1 relative z-10 border-b border-gray-100">
            <CPLogo color={C.Pine} className="w-32" />
            <span className="text-[9px] font-semibold tracking-wide uppercase text-gray-500">
              Kids &amp; Entertainment
            </span>
          </div>
          <div className="flex-1 space-y-0.5 overflow-y-auto overflow-x-hidden custom-scrollbar relative z-10 pr-0.5">
            <>
              {userRole !== "co" && <SidebarKop>Vandaag</SidebarKop>}
              {userRole !== "co" && (
                <SidebarItem
                  id="dagbord"
                  icon={IcDagbord}
                  label="Dagbord"
                  onClick={() => {
                    setView("dagbord");
                    setCurrentDay(new Date());
                  }}
                  active={view === "dagbord"}
                  theme={currentTheme}
                  badge={dagbordAttention}
                />
              )}
              {userRole !== "co" && (
                <SidebarItem
                  id="dash"
                  icon={IcDashboard}
                  label="Dashboard"
                  onClick={() => {
                    setView("dashboard");
                    setCurrentDay(new Date());
                  }}
                  active={view === "dashboard"}
                  theme={currentTheme}
                />
              )}
              {userRole !== "co" && (appData?.feestdagen?.instellingen?.open || userRole === "fm" || userRole === "admin") && (
                <SidebarItem
                  id="feestdagen"
                  icon={IcFeest}
                  label="Feestdagen"
                  onClick={() => setView("feestdagen")}
                  active={view === "feestdagen"}
                  theme={currentTheme}
                />
              )}
              {userRole !== "co" && (
                <SidebarItem
                  id="rooster"
                  icon={IcRooster}
                  label="Rooster"
                  onClick={() => setView("rooster")}
                  active={view === "rooster"}
                  theme={currentTheme}
                  badge={
                    userRole === "admin" || userRole === "fm"
                      ? appData?.shiftSwapRequests?.filter(
                          (r) => r.status === "pending"
                        ).length || 0
                      : 0
                  }
                />
              )}
              <SidebarKop>Planning</SidebarKop>
              {rmRecht && (
                <SidebarItem
                  id="planner"
                  icon={IcRoostermaker}
                  label="Roostermaker"
                  onClick={() => setView("planner")}
                  active={view === "planner"}
                  theme={currentTheme}
                />
              )}
              <SidebarItem
                id="prog"
                icon={IcProgramma}
                label="Programma"
                onClick={() => {
                  setView("schedule");
                  setCurrentDay(new Date());
                }}
                active={view === "schedule"}
                theme={currentTheme}
              />
              {userRole !== "co" && (
                <SidebarItem
                  id="occ"
                  icon={IcBezetting}
                  label="Bezetting"
                  onClick={() => {
                    setView("occupancy");
                    setCurrentDay(new Date());
                  }}
                  active={view === "occupancy"}
                  theme={currentTheme}
                />
              )}
              {userRole !== "co" && (
                <SidebarItem
                  id="ho"
                  icon={IcOverdracht}
                  label="Overdracht (archief)"
                  onClick={() => {
                    setView("handover");
                    setCurrentDay(new Date());
                  }}
                  active={view === "handover"}
                  theme={currentTheme}
                  badge={handoverUnreadCount}
                />
              )}
              {userRole !== "co" && (
                <SidebarItem
                  id="checks"
                  icon={IcTaken}
                  label="Takenlijst"
                  onClick={() => {
                    setView("checks");
                    setCurrentDay(new Date());
                  }}
                  active={view === "checks"}
                  theme={currentTheme}
                />
              )}
              {userRole === "fm" && (
                <SidebarItem
                  id="fm_checklist"
                  icon={IcKwaliteit}
                  label="FM Checklist"
                  onClick={() => setView("fm_checklist")}
                  active={view === "fm_checklist"}
                  theme={currentTheme}
                  badge={(() => {
                    const open = (appData.fmChecklist || []).filter(
                      (i) => !i.completed
                    ).length;
                    return open > 0 ? open : 0;
                  })()}
                />
              )}
              {userRole !== "co" && (
                <SidebarItem
                  id="hours"
                  icon={IcUren}
                  label="Uren"
                  onClick={() => {
                    setView("hours");
                    setCurrentDay(new Date());
                  }}
                  active={view === "hours"}
                  theme={currentTheme}
                />
              )}
              {userRole !== "co" && (
                <SidebarItem
                  id="orders"
                  icon={IcBestellen}
                  label="Bestellen"
                  onClick={() => {
                    setView("orders");
                    setCurrentDay(new Date());
                  }}
                  active={view === "orders"}
                  theme={currentTheme}
                />
              )}
              {userRole !== "co" && <SidebarKop>Team</SidebarKop>}
              {userRole === "fm" && (
                <SidebarItem
                  id="polaroids"
                  icon={IcPolaroid}
                  label="Polaroids"
                  onClick={() => setView("polaroids")}
                  active={view === "polaroids"}
                  theme={currentTheme}
                />
              )}
              {pietenZichtbaar && (
                <SidebarItem
                  id="pietenservice"
                  icon={IcPieten}
                  label="Pietenservice"
                  onClick={() => setView("pietenservice")}
                  active={view === "pietenservice"}
                  theme={currentTheme}
                />
              )}
              {afscheidZichtbaar && (
                <SidebarItem
                  id="afscheid"
                  icon={IcAfscheid}
                  label="Afscheid"
                  onClick={() => setView("afscheid")}
                  active={view === "afscheid"}
                  theme={currentTheme}
                />
              )}
              {userRole !== "co" && (
                <SidebarItem
                  id="phone"
                  icon={IcTelefoon}
                  label="Telefoonlijst"
                  onClick={() => setView("phonelist")}
                  active={view === "phonelist"}
                  theme={currentTheme}
                />
              )}
              {userRole !== "co" && (
                <SidebarItem
                  id="handboek"
                  icon={IcHandboek}
                  label="Handboek"
                  onClick={() => setView("handboek")}
                  active={view === "handboek"}
                  theme={currentTheme}
                />
              )}
              <SidebarItem
                id="bingo"
                icon={IcBingo}
                label="Prize Night"
                onClick={() => setView("bingo")}
                active={view === "bingo"}
                theme={currentTheme}
                badge={bingoUnreadCount}
              />
              {userRole === "co" && (
                <SidebarItem
                  id="kaarten"
                  icon={IcKaarten}
                  label="Kaarten printen"
                  onClick={() => setView("kaarten")}
                  active={view === "kaarten"}
                  theme={currentTheme}
                />
              )}
              {(userRole === "staff" || userRole === "fm") && (
                <SidebarItem
                  id="dj_night"
                  icon={IcDj}
                  label="DJ Night"
                  onClick={() => setView("dj_night")}
                  active={view === "dj_night"}
                  theme={currentTheme}
                />
              )}
              <SidebarItem
                id="chat"
                icon={IcChat}
                label="FM Chat"
                onClick={() => setView("chat")}
                active={view === "chat"}
                theme={currentTheme}
                badge={chatUnreadCount}
              />
              {userRole !== "co" && (
                <SidebarItem
                  id="quality_visits"
                  icon={IcKwaliteit}
                  label="Kwaliteit"
                  onClick={() => setView("quality_visits")}
                  active={view === "quality_visits"}
                  theme={currentTheme}
                />
              )}
              {userRole !== "co" && <SidebarKop>Overig</SidebarKop>}
              {(userRole === "staff" || userRole === "fm") && (
                <SidebarItem
                  id="user_settings"
                  icon={IcThema}
                  label="Foto & thema"
                  onClick={() => setView("user_settings")}
                  active={view === "user_settings"}
                  theme={currentTheme}
                />
              )}
              {(userRole === "fm" || userRole === "admin") && (
                <SidebarItem
                  id="promo_screen"
                  icon={IcDisplay}
                  label="Display Screen"
                  onClick={() => setView("promo_screen")}
                  active={view === "promo_screen"}
                  theme={currentTheme}
                />
              )}
              {(userRole === "fm" || userRole === "admin") && (
                <SidebarItem
                  id="promo_bediening"
                  icon={IcDisplay}
                  label="Schermen bedienen"
                  onClick={() => setView("promo_bediening")}
                  active={view === "promo_bediening"}
                  theme={currentTheme}
                />
              )}
              {(userRole === "fm" || userRole === "admin") && (
                <SidebarItem
                  id="promo_designer"
                  icon={IcPromo}
                  label="Promo Designer"
                  onClick={() => setView("promo_designer")}
                  active={view === "promo_designer"}
                  theme={currentTheme}
                />
              )}
              {userRole === "staff" && (
                <SidebarItem
                  id="change_pin"
                  icon={IcPincode}
                  label="Wijzig Pincode"
                  onClick={() => setView("change_pin")}
                  active={view === "change_pin"}
                  theme={currentTheme}
                />
              )}
            </>
          </div>
          {isLoggedIn && (
            <div className="mt-auto relative z-10 pt-2 border-t border-gray-100">
              {/* 👥 Online gebruikers - desktop sidebar */}
              {onlineUsers.length > 0 && (
                <div className="mb-2 px-2 py-2 rounded-xl" style={{ backgroundColor: "#F9FAFB" }}>
                  <div className="text-[9px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                    Nu online
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                    {[...onlineUsers]
                      .sort((a, b) => {
                        if (a.name === loggedInUserName) return -1;
                        if (b.name === loggedInUserName) return 1;
                        return a.name.localeCompare(b.name);
                      })
                      .map((u) => {
                        const isMe = u.name === loggedInUserName;
                        const logoColor = C.Pine;
                        return (
                          <div
                            key={u.name}
                            title={
                              isMe && canGoInvisible
                                ? u.invisible
                                  ? "Je bent onzichtbaar — klik om weer online te verschijnen"
                                  : `${u.name} — klik om onzichtbaar te worden`
                                : u.name
                            }
                            onClick={
                              isMe && canGoInvisible
                                ? togglePresenceInvisible
                                : undefined
                            }
                            style={{
                              position: "relative",
                              flexShrink: 0,
                              cursor:
                                isMe && canGoInvisible ? "pointer" : "default",
                              opacity: u.invisible ? 0.5 : 1,
                            }}
                          >
                            <div
                              style={{
                                borderRadius: "50%",
                                border: isMe
                                  ? `2px ${u.invisible ? "dashed" : "solid"} ${logoColor}`
                                  : "2px solid white",
                              }}
                            >
                              <ProfileAvatar
                                name={u.name}
                                staffList={appData.staffList || []}
                                size={28}
                                color={
                                  currentTheme.secondary || currentTheme.primary
                                }
                              />
                            </div>
                            <span
                              style={{
                                position: "absolute",
                                bottom: 0,
                                right: 0,
                                width: 7,
                                height: 7,
                                backgroundColor: u.invisible
                                  ? "#9ca3af"
                                  : "#4ade80",
                                borderRadius: "50%",
                                border: "1.5px solid white",
                              }}
                            />
                          </div>
                        );
                      })}
                  </div>
                  {presenceInvisibleActive && (
                    <div className="text-xs text-gray-500 mt-1.5">Onzichtbaar</div>
                  )}
                </div>
              )}
              {userRole === "fm" && (
                <SidebarItem
                  id="beheer"
                  icon={IcBeheer}
                  label="Beheer"
                  onClick={handleGoToAdmin}
                  active={view === "admin"}
                />
              )}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2.5 px-1.5 min-h-[44px] rounded-xl transition-colors active:scale-[0.98] hover:bg-gray-50 text-left"
                style={{ color: C.Sunset }}
              >
                <span
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: C.BlossomLight }}
                >
                  <LogOut size={16} />
                </span>
                <span className="text-sm font-semibold">Uitloggen</span>
              </button>
              <p className="px-1.5 pt-1 text-[11px] text-gray-400">{APP_VERSIE}</p>
            </div>
          )}
        </div>
      )}
      <div
        className={`flex-1 flex flex-col h-screen overflow-hidden relative ${
          isLoggedIn && !isExternalPromoMode && view !== "login" && view !== "promo_screen"
            ? "met-tabbalk"
            : ""
        }`}
      >
        {isLoggedIn && !isExternalPromoMode && (
          <div className="md:hidden px-3 pt-2 pb-1 z-30 relative flex-shrink-0" style={{ backgroundColor: themeBg }}>
          <div className="flex items-center gap-1 h-14 px-1.5 bg-white rounded-2xl border border-gray-100 shadow-sm relative overflow-hidden">
            {/* Dagbord-stijl: geen patroon in de bovenbalk */}
            {false && (
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage: `url(${
                    PATTERN_BACKGROUNDS[currentTheme?.id]
                  })`,
                  backgroundSize: "cover",
                  backgroundPosition: "center top",
                  opacity: 0.07,
                }}
              />
            )}
            {/* Hamburger menu knop */}
            {userRole !== "co" ? (
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                aria-label="Menu openen"
                className="relative flex items-center justify-center w-11 h-11 rounded-xl flex-shrink-0 active:scale-95 transition-transform"
                style={{ backgroundColor: C.PineLight }}
              >
                <Menu size={20} color={C.Pine} />
                {(chatUnreadCount > 0 ||
                  handoverUnreadCount > 0 ||
                  dagbordAttention > 0) && (
                  <span
                    className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: C.Blossom, boxShadow: `0 0 0 2px ${C.PineLight}` }}
                  ></span>
                )}
              </button>
            ) : (
              <button aria-label="Uitloggen"
                onClick={handleLogout}
                className="flex items-center justify-center w-11 h-11 rounded-xl flex-shrink-0 active:scale-95 transition-transform"
                style={{ backgroundColor: C.PineLight }}
              >
                <LogOut size={18} color={C.Pine} />
              </button>
            )}
            {/* Logo gecentreerd */}
            <div className="flex-1 flex justify-center items-center gap-2.5">
              <CPLogo
                color={C.Pine}
                style={{ width: "7rem", height: "1.8rem", flexShrink: 0 }}
              />
              <div className="w-px h-6 bg-gray-200" />
              <span className="text-[9px] font-semibold uppercase tracking-wide leading-tight text-gray-500">
                Kids &amp;
                <br />
                Entertainment
              </span>
            </div>
            {/* Online gebruikers - rechts in topbar */}
            <div className="min-w-[44px] h-11 flex-shrink-0 flex items-center justify-center pr-1">
              {onlineUsers.length > 0 &&
                (() => {
                  const logoColor = C.Pine;
                  const sorted = [...onlineUsers]
                    .sort((a, b) => {
                      if (a.name === loggedInUserName) return -1;
                      if (b.name === loggedInUserName) return 1;
                      return a.name.localeCompare(b.name);
                    })
                    .slice(0, 3);
                  return (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {sorted.map((u, i) => {
                        const isMe = u.name === loggedInUserName;
                        return (
                          <div
                            key={u.name}
                            title={
                              isMe && canGoInvisible
                                ? u.invisible
                                  ? "Je bent onzichtbaar — tik om weer online te verschijnen"
                                  : `${u.name} — tik om onzichtbaar te worden`
                                : u.name
                            }
                            onClick={
                              isMe && canGoInvisible
                                ? togglePresenceInvisible
                                : undefined
                            }
                            style={{
                              cursor:
                                isMe && canGoInvisible ? "pointer" : "default",
                              opacity: u.invisible ? 0.5 : 1,
                              position: "relative",
                              marginLeft: i === 0 ? 0 : -8,
                              zIndex: sorted.length - i,
                              width: 28,
                              height: 28,
                              flexShrink: 0,
                            }}
                          >
                            <div
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                border: isMe
                                  ? `2px ${u.invisible ? "dashed" : "solid"} ${logoColor}`
                                  : "2px solid white",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                                overflow: "hidden",
                                flexShrink: 0,
                              }}
                            >
                              <ProfileAvatar
                                name={u.name}
                                staffList={appData.staffList || []}
                                size={24}
                                color={
                                  currentTheme.secondary || currentTheme.primary
                                }
                              />
                            </div>
                            <span
                              style={{
                                position: "absolute",
                                bottom: 0,
                                right: 0,
                                width: 7,
                                height: 7,
                                backgroundColor: u.invisible
                                  ? "#9ca3af"
                                  : "#4ade80",
                                borderRadius: "50%",
                                border: "1.5px solid white",
                              }}
                            />
                          </div>
                        );
                      })}
                      {onlineUsers.length > 3 && (
                        <div
                          style={{
                            marginLeft: -6,
                            zIndex: 0,
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            backgroundColor: C.PineLight,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 10,
                            fontWeight: 600,
                            color: C.Pine,
                            border: "2px solid white",
                            flexShrink: 0,
                          }}
                        >
                          +{onlineUsers.length - 3}
                        </div>
                      )}
                    </div>
                  );
                })()}
            </div>
          </div>
          </div>
        )}
        <main
          className="flex-1 overflow-y-auto p-0 md:p-6"
          style={{ backgroundColor: themeBg, "--pagina-bg": themeBg }}
        >
          {/* Zachte vorm-overgang bij het wisselen van pagina */}
          <div key={view} className="cp-pagina-in relative h-full">
            <div className="cp-pagina-vorm-kader" aria-hidden="true">
              <div className="cp-pagina-vorm pointer-events-none absolute left-1/2 top-24 -translate-x-1/2 w-72 h-72">
                <CpVorm vorm="pebbles" kleur={C.PineLight} className="w-full h-full" />
              </div>
            </div>
          {view === "login" ? (
            <div className="h-full overflow-y-auto p-4">
              <LoginScreen
                pin={pin}
                setPin={setPin}
                handleLogin={handleLogin}
                staffList={loginStaffList}
                loginMode={loginMode}
                setLoginMode={setLoginMode}
                selectedStaff={selectedStaff}
                setSelectedStaff={setSelectedStaff}
                theme={currentTheme}
                pinLoginError={pinLoginError}
              />
            </div>
          ) : view === "force_pin_change" ? (
            <div className="h-full overflow-y-auto p-4">
              <ChangePinView
                setView={(target) => {
                  if (target === "dashboard") setView(target);
                }}
                appData={appDataWeergave}
                updateData={saveData}
                loggedInUserName={loggedInUserName}
                showAlert={triggerAlert}
                theme={contentTheme}
              />
            </div>
          ) : view === "chat" ? (
            <div className="h-full p-2 md:p-0">
              <ChatView
                appData={appDataWeergave}
                updateData={saveData}
                loggedInUserName={loggedInUserName}
                userRole={userRole}
                theme={contentTheme}
              />
            </div>
          ) : view === "fm_checklist" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <FMChecklistView
                appData={appDataWeergave}
                setAppData={setAppData}
                saveData={saveData}
                currentDay={currentDay}
                loggedInUserName={loggedInUserName}
                theme={contentTheme}
                setView={setView}
                showAlert={showAlert}
                showConfirm={showConfirm}
              />
            </div>
          ) : view === "quality_visits" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <QualityVisitPage
                appData={appDataWeergave}
                updateData={saveData}
                addQualityVisit={addQualityVisit}
                deleteQualityVisit={deleteQualityVisit}
                addActivityRating={addActivityRating}
                deleteActivityRating={deleteActivityRating}
                deletingId={deletingId}
                loggedInUserName={loggedInUserName}
                userRole={userRole}
                theme={contentTheme}
              />
            </div>
          ) : view === "rooster" ? (
            <RoosterView
              appData={appDataWeergave}
              currentDay={currentDay}
              loggedInUserName={loggedInUserName}
              theme={contentTheme}
              onPrev={prevDay}
              onNext={nextDay}
              onCalendar={() => setIsCalendarOpen(true)}
              setCurrentDay={setCurrentDay}
              updateData={saveData}
              showAlert={triggerAlert}
              showConfirm={triggerConfirm}
              userRole={userRole}
              initialTab={view === "rooster" ? "mine" : "team"}
            />
          ) : view === "planner" ? (
            <div className="h-full overflow-y-auto">
              <RoosterMakerView
                appData={appDataWeergave}
                updateData={rmUpdate}
                alleenBekijken={rmRecht === "bekijken"}
                theme={contentTheme}
                showAlert={triggerAlert}
                showConfirm={triggerConfirm}
                onBack={() => setView("dashboard")}
                laadArchief={(maanden) => maanden.forEach((m) => laadArchiefMaand(m))}
              />
            </div>
          ) : view === "change_pin" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <ChangePinView
                setView={setView}
                appData={appDataWeergave}
                updateData={saveData}
                loggedInUserName={loggedInUserName}
                showAlert={triggerAlert}
                theme={contentTheme}
              />
            </div>
          ) : view === "phonelist" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <EditPhoneDirectory
                appData={appDataWeergave}
                updateData={saveData}
                showAlert={triggerAlert}
                showConfirm={triggerConfirm}
                backAction={() => setView("dashboard")}
                theme={contentTheme}
                readOnly={userRole !== "fm" && userRole !== "admin"}
              />
            </div>
          ) : view === "kaarten" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <TijdkaartenView
                appData={appDataWeergave}
                updateData={saveData}
                setAdminSubView={() => setView(userRole === "co" ? "schedule" : "admin")}
                showAlert={triggerAlert}
                theme={contentTheme}
              />
            </div>
          ) : view === "polaroids" && userRole === "fm" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <PolaroidsView appData={appDataWeergave} setView={setView} theme={contentTheme} />
            </div>
          ) : view === "pietenservice" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <PietenserviceView
                appData={appDataWeergave}
                updateData={saveData}
                loggedInUserName={loggedInUserName}
                userRole={userRole}
                setView={setView}
                theme={contentTheme}
              />
            </div>
          ) : view === "afscheid" && afscheidZichtbaar ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <AfscheidView
                appData={appDataWeergave}
                updateData={saveData}
                loggedInUserName={loggedInUserName}
                userRole={userRole}
                setView={setView}
                theme={contentTheme}
              />
            </div>
          ) : view === "feestdagen" && userRole !== "co" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <FeestdagenView
                appData={appDataWeergave}
                updateData={saveData}
                loggedInUserName={loggedInUserName}
                userRole={userRole}
                setView={setView}
                theme={contentTheme}
              />
            </div>
          ) : view === "bingo" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <BingoWinnerView
                setView={setView}
                appData={appDataWeergave}
                updateData={saveData}
                userRole={userRole}
                loggedInUserName={loggedInUserName}
                showAlert={triggerAlert}
                showConfirm={triggerConfirm}
                theme={contentTheme}
              />
            </div>
          ) : view === "user_settings" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <UserSettingsView
                setView={setView}
                appData={appDataWeergave}
                updateData={saveData}
                loggedInUserName={loggedInUserName}
                theme={contentTheme}
              />
            </div>
          ) : view === "admin" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              {adminSubView === "menu" ? (
                <AdminMenu
                  setAdminSubView={setAdminSubView}
                  setView={setView}
                  handleLogout={handleLogout}
                  appData={appDataWeergave}
                  updateData={saveData}
                  showAlert={triggerAlert}
                  handleLockApp={handleLockApp}
                  resetAllData={resetAllData}
                  showConfirm={triggerConfirm}
                  theme={contentTheme}
                  db={db}
                  appId={appId}
                  cleanupOrphanedStaff={cleanupOrphanedStaff}
                  pruneNow={pruneNow}
                  isPruning={isPruning}
                />
              ) : adminSubView === "edit_schedule" ? (
                <ScheduleBuilderView
                  currentDay={currentDay}
                  appData={appDataWeergave}
                  updateData={saveData}
                  showAlert={triggerAlert}
                  showConfirm={triggerConfirm}
                  setAdminSubView={setAdminSubView}
                  onPrev={prevDay}
                  onNext={nextDay}
                  onCalendar={() => setIsCalendarOpen(true)}
                  setCurrentDay={setCurrentDay}
                  theme={contentTheme}
                  loggedInUserName={loggedInUserName}
                />
              ) : adminSubView === "edit_stats" ? (
                <EditStats
                  appData={appDataWeergave}
                  updateData={saveData}
                  setAdminSubView={setAdminSubView}
                  setView={setView}
                  currentDay={currentDay}
                  onPrev={prevDay}
                  onNext={nextDay}
                  onCalendar={() => setIsCalendarOpen(true)}
                  theme={contentTheme}
                />
              ) : adminSubView === "manage_tasks" ? (
                <AdminTasksView
                  currentDay={currentDay}
                  backAction={() => setAdminSubView("menu")}
                  appData={appDataWeergave}
                  updateData={saveData}
                  showConfirm={triggerConfirm}
                  onPrev={prevDay}
                  onNext={nextDay}
                  onCalendar={() => setIsCalendarOpen(true)}
                  theme={contentTheme}
                  loggedInUserName={loggedInUserName}
                />
              ) : adminSubView === "quality_visits" ? (
                <AdminContainer
                  title="Kwaliteit"
                  backAction={() => setAdminSubView("menu")}
                  hideDateControls={true}
                  theme={contentTheme}
                >
                  <QualityVisitPage
                    appData={appDataWeergave}
                    updateData={saveData}
                    addQualityVisit={addQualityVisit}
                    deleteQualityVisit={deleteQualityVisit}
                    addActivityRating={addActivityRating}
                    deleteActivityRating={deleteActivityRating}
                    deletingId={deletingId}
                    loggedInUserName={loggedInUserName}
                    userRole={userRole}
                    theme={contentTheme}
                    hideTitle={true}
                  />
                </AdminContainer>
              ) : adminSubView === "monthly_schedule" ? (
                <SimpleMonthlyRooster
                  appData={appDataWeergave}
                  updateData={saveData}
                  showAlert={triggerAlert}
                  showConfirm={triggerConfirm}
                  theme={contentTheme}
                  setAdminSubView={setAdminSubView}
                />
              ) : adminSubView === "shift_changes" ? (
                <AdminContainer
                  title="Roosterwijzigingen"
                  backAction={() => setAdminSubView("menu")}
                  hideDateControls={true}
                  theme={contentTheme}
                >
                  <ShiftChangesView
                    appData={appDataWeergave}
                    theme={contentTheme}
                    setAdminSubView={setAdminSubView}
                    hideTitle={true}
                  />
                </AdminContainer>
              ) : adminSubView === "edit_hours" ? (
                <EditHours
                  setAdminSubView={setAdminSubView}
                  setView={setView}
                  appData={appDataWeergave}
                  updateData={saveData}
                  showConfirm={triggerConfirm}
                  onPrev={prevDay}
                  onNext={nextDay}
                  onCalendar={() => setIsCalendarOpen(true)}
                  currentDay={currentDay}
                  theme={contentTheme}
                  loggedInUserName={loggedInUserName}
                />
              ) : adminSubView === "edit_orders" ? (
                <EditOrders
                  setAdminSubView={setAdminSubView}
                  setView={setView}
                  appData={appDataWeergave}
                  updateData={saveData}
                  showConfirm={triggerConfirm}
                  onPrev={prevDay}
                  onNext={nextDay}
                  onCalendar={() => setIsCalendarOpen(true)}
                  currentDay={currentDay}
                  theme={contentTheme}
                />
              ) : adminSubView === "edit_phones" ? (
                <EditPhoneDirectory
                  appData={appDataWeergave}
                  updateData={saveData}
                  showAlert={triggerAlert}
                  showConfirm={triggerConfirm}
                  backAction={() => setAdminSubView("menu")}
                  theme={contentTheme}
                />
              ) : adminSubView === "personeel" ? (
                <PersoneelView
                  appData={appDataWeergave}
                  updateData={saveData}
                  showAlert={triggerAlert}
                  showConfirm={triggerConfirm}
                  theme={contentTheme}
                  setAdminSubView={setAdminSubView}
                />
              ) : adminSubView === "settings" ? (
                <EditSettings
                  setAdminSubView={setAdminSubView}
                  setView={setView}
                  appData={appDataWeergave}
                  updateData={saveData}
                  resetAllData={resetAllData}
                  showAlert={triggerAlert}
                  showConfirm={triggerConfirm}
                  theme={contentTheme}
                  db={db}
                  appId={appId}
                  recoverQualityVisits={recoverQualityVisits} // 🔥 VOEG DEZE TOE
                />
              ) : adminSubView === "activity_library" ? (
                <ActivityLibraryView
                  appData={appDataWeergave}
                  updateData={saveData}
                  showAlert={triggerAlert}
                  showConfirm={triggerConfirm}
                  setAdminSubView={setAdminSubView}
                  theme={contentTheme}
                  storage={storage}
                />
              ) : adminSubView === "kaarten" ? (
                <TijdkaartenView
                  appData={appDataWeergave}
                  updateData={saveData}
                  setAdminSubView={setAdminSubView}
                  showAlert={triggerAlert}
                  theme={contentTheme}
                />
              ) : adminSubView === "data_cleanup" ? (
                <DataCleanupView
                  appData={appDataWeergave}
                  updateData={saveData}
                  setAdminSubView={setAdminSubView}
                  showAlert={triggerAlert}
                  showConfirm={triggerConfirm}
                  theme={contentTheme}
                />
              ) : null}
            </div>
          ) : view === "force_pin_change" ? (
            <div className="h-full overflow-y-auto p-4">
              <ChangePinView
                setView={(target) => {
                  if (target === "dashboard") setView(target);
                }}
                appData={appDataWeergave}
                updateData={saveData}
                loggedInUserName={loggedInUserName}
                showAlert={triggerAlert}
                theme={contentTheme}
              />
            </div>
          ) : view === "chat" ? (
            <div className="h-full p-2 md:p-0">
              <ChatView
                appData={appDataWeergave}
                updateData={saveData}
                loggedInUserName={loggedInUserName}
                userRole={userRole}
                theme={contentTheme}
              />
            </div>
          ) : view === "fm_checklist" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <FMChecklistView
                appData={appDataWeergave}
                setAppData={setAppData}
                saveData={saveData}
                currentDay={currentDay}
                loggedInUserName={loggedInUserName}
                theme={contentTheme}
                setView={setView}
                showAlert={showAlert}
                showConfirm={showConfirm}
              />
            </div>
          ) : view === "quality_visits" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <QualityVisitPage
                appData={appDataWeergave}
                updateData={saveData}
                addQualityVisit={addQualityVisit}
                deleteQualityVisit={deleteQualityVisit}
                addActivityRating={addActivityRating}
                deleteActivityRating={deleteActivityRating}
                deletingId={deletingId}
                loggedInUserName={loggedInUserName}
                userRole={userRole}
                theme={contentTheme}
              />
            </div>
          ) : view === "rooster" ? (
            <RoosterView
              appData={appDataWeergave}
              currentDay={currentDay}
              loggedInUserName={loggedInUserName}
              theme={contentTheme}
              onPrev={prevDay}
              onNext={nextDay}
              onCalendar={() => setIsCalendarOpen(true)}
              setCurrentDay={setCurrentDay}
              updateData={saveData}
              showAlert={triggerAlert}
              showConfirm={triggerConfirm}
              userRole={userRole}
              initialTab={view === "rooster" ? "mine" : "team"}
            />
          ) : view === "planner" ? (
            <div className="h-full overflow-y-auto">
              <RoosterMakerView
                appData={appDataWeergave}
                updateData={rmUpdate}
                alleenBekijken={rmRecht === "bekijken"}
                theme={contentTheme}
                showAlert={triggerAlert}
                showConfirm={triggerConfirm}
                onBack={() => setView("dashboard")}
                laadArchief={(maanden) => maanden.forEach((m) => laadArchiefMaand(m))}
              />
            </div>
          ) : view === "change_pin" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <ChangePinView
                setView={setView}
                appData={appDataWeergave}
                updateData={saveData}
                loggedInUserName={loggedInUserName}
                showAlert={triggerAlert}
                theme={contentTheme}
              />
            </div>
          ) : view === "phonelist" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <EditPhoneDirectory
                appData={appDataWeergave}
                updateData={saveData}
                showAlert={triggerAlert}
                showConfirm={triggerConfirm}
                backAction={() => setView("dashboard")}
                theme={contentTheme}
                readOnly={userRole !== "fm" && userRole !== "admin"}
              />
            </div>
          ) : view === "kaarten" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <TijdkaartenView
                appData={appDataWeergave}
                updateData={saveData}
                setAdminSubView={() => setView(userRole === "co" ? "schedule" : "admin")}
                showAlert={triggerAlert}
                theme={contentTheme}
              />
            </div>
          ) : view === "polaroids" && userRole === "fm" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <PolaroidsView appData={appDataWeergave} setView={setView} theme={contentTheme} />
            </div>
          ) : view === "pietenservice" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <PietenserviceView
                appData={appDataWeergave}
                updateData={saveData}
                loggedInUserName={loggedInUserName}
                userRole={userRole}
                setView={setView}
                theme={contentTheme}
              />
            </div>
          ) : view === "afscheid" && afscheidZichtbaar ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <AfscheidView
                appData={appDataWeergave}
                updateData={saveData}
                loggedInUserName={loggedInUserName}
                userRole={userRole}
                setView={setView}
                theme={contentTheme}
              />
            </div>
          ) : view === "feestdagen" && userRole !== "co" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <FeestdagenView
                appData={appDataWeergave}
                updateData={saveData}
                loggedInUserName={loggedInUserName}
                userRole={userRole}
                setView={setView}
                theme={contentTheme}
              />
            </div>
          ) : view === "bingo" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <BingoWinnerView
                setView={setView}
                appData={appDataWeergave}
                updateData={saveData}
                userRole={userRole}
                loggedInUserName={loggedInUserName}
                showAlert={triggerAlert}
                showConfirm={triggerConfirm}
                theme={contentTheme}
              />
            </div>
          ) : view === "user_settings" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <UserSettingsView
                setView={setView}
                appData={appDataWeergave}
                updateData={saveData}
                loggedInUserName={loggedInUserName}
                theme={contentTheme}
              />
            </div>
          ) : view === "admin" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              {adminSubView === "menu" ? (
                <AdminMenu
                  setAdminSubView={setAdminSubView}
                  setView={setView}
                  handleLogout={handleLogout}
                  appData={appDataWeergave}
                  updateData={saveData}
                  showAlert={triggerAlert}
                  handleLockApp={handleLockApp}
                  resetAllData={resetAllData}
                  showConfirm={triggerConfirm}
                  theme={contentTheme}
                  db={db}
                  appId={appId}
                  cleanupOrphanedStaff={cleanupOrphanedStaff}
                  pruneNow={pruneNow}
                  isPruning={isPruning}
                />
              ) : adminSubView === "edit_schedule" ? (
                <ScheduleBuilderView
                  currentDay={currentDay}
                  appData={appDataWeergave}
                  updateData={saveData}
                  showAlert={triggerAlert}
                  showConfirm={triggerConfirm}
                  setAdminSubView={setAdminSubView}
                  onPrev={prevDay}
                  onNext={nextDay}
                  onCalendar={() => setIsCalendarOpen(true)}
                  setCurrentDay={setCurrentDay}
                  theme={contentTheme}
                  loggedInUserName={loggedInUserName}
                />
              ) : adminSubView === "edit_stats" ? (
                <EditStats
                  appData={appDataWeergave}
                  updateData={saveData}
                  setAdminSubView={setAdminSubView}
                  setView={setView}
                  currentDay={currentDay}
                  onPrev={prevDay}
                  onNext={nextDay}
                  onCalendar={() => setIsCalendarOpen(true)}
                  theme={contentTheme}
                />
              ) : adminSubView === "manage_tasks" ? (
                <AdminTasksView
                  currentDay={currentDay}
                  backAction={() => setAdminSubView("menu")}
                  appData={appDataWeergave}
                  updateData={saveData}
                  showConfirm={triggerConfirm}
                  onPrev={prevDay}
                  onNext={nextDay}
                  onCalendar={() => setIsCalendarOpen(true)}
                  theme={contentTheme}
                  loggedInUserName={loggedInUserName}
                />
              ) : adminSubView === "quality_visits" ? (
                <AdminContainer
                  title="Kwaliteit"
                  backAction={() => setAdminSubView("menu")}
                  hideDateControls={true}
                  theme={contentTheme}
                >
                  <QualityVisitPage
                    appData={appDataWeergave}
                    updateData={saveData}
                    addQualityVisit={addQualityVisit}
                    deleteQualityVisit={deleteQualityVisit}
                    addActivityRating={addActivityRating}
                    deleteActivityRating={deleteActivityRating}
                    deletingId={deletingId}
                    loggedInUserName={loggedInUserName}
                    userRole={userRole}
                    theme={contentTheme}
                    hideTitle={true}
                  />
                </AdminContainer>
              ) : adminSubView === "monthly_schedule" ? (
                <SimpleMonthlyRooster
                  appData={appDataWeergave}
                  updateData={saveData}
                  showAlert={triggerAlert}
                  showConfirm={triggerConfirm}
                  theme={contentTheme}
                  setAdminSubView={setAdminSubView}
                />
              ) : adminSubView === "shift_changes" ? (
                <AdminContainer
                  title="Roosterwijzigingen"
                  backAction={() => setAdminSubView("menu")}
                  hideDateControls={true}
                  theme={contentTheme}
                >
                  <ShiftChangesView
                    appData={appDataWeergave}
                    theme={contentTheme}
                    setAdminSubView={setAdminSubView}
                    hideTitle={true}
                  />
                </AdminContainer>
              ) : adminSubView === "edit_hours" ? (
                <EditHours
                  setAdminSubView={setAdminSubView}
                  setView={setView}
                  appData={appDataWeergave}
                  updateData={saveData}
                  showConfirm={triggerConfirm}
                  onPrev={prevDay}
                  onNext={nextDay}
                  onCalendar={() => setIsCalendarOpen(true)}
                  currentDay={currentDay}
                  theme={contentTheme}
                  loggedInUserName={loggedInUserName}
                />
              ) : adminSubView === "edit_orders" ? (
                <EditOrders
                  setAdminSubView={setAdminSubView}
                  setView={setView}
                  appData={appDataWeergave}
                  updateData={saveData}
                  showConfirm={triggerConfirm}
                  onPrev={prevDay}
                  onNext={nextDay}
                  onCalendar={() => setIsCalendarOpen(true)}
                  currentDay={currentDay}
                  theme={contentTheme}
                />
              ) : adminSubView === "edit_phones" ? (
                <EditPhoneDirectory
                  appData={appDataWeergave}
                  updateData={saveData}
                  showAlert={triggerAlert}
                  showConfirm={triggerConfirm}
                  backAction={() => setAdminSubView("menu")}
                  theme={contentTheme}
                />
              ) : adminSubView === "personeel" ? (
                <PersoneelView
                  appData={appDataWeergave}
                  updateData={saveData}
                  showAlert={triggerAlert}
                  showConfirm={triggerConfirm}
                  theme={contentTheme}
                  setAdminSubView={setAdminSubView}
                />
              ) : adminSubView === "settings" ? (
                <EditSettings
                  setAdminSubView={setAdminSubView}
                  setView={setView}
                  appData={appDataWeergave}
                  updateData={saveData}
                  resetAllData={resetAllData}
                  showAlert={triggerAlert}
                  showConfirm={triggerConfirm}
                  theme={contentTheme}
                  db={db}
                  appId={appId}
                  recoverQualityVisits={recoverQualityVisits} // 🔥 VOEG DEZE TOE
                />
              ) : adminSubView === "activity_library" ? (
                <ActivityLibraryView
                  appData={appDataWeergave}
                  updateData={saveData}
                  showAlert={triggerAlert}
                  showConfirm={triggerConfirm}
                  setAdminSubView={setAdminSubView}
                  theme={contentTheme}
                  storage={storage}
                />
              ) : adminSubView === "kaarten" ? (
                <TijdkaartenView
                  appData={appDataWeergave}
                  updateData={saveData}
                  setAdminSubView={setAdminSubView}
                  showAlert={triggerAlert}
                  theme={contentTheme}
                />
              ) : adminSubView === "data_cleanup" ? (
                <DataCleanupView
                  appData={appDataWeergave}
                  updateData={saveData}
                  setAdminSubView={setAdminSubView}
                  showAlert={triggerAlert}
                  showConfirm={triggerConfirm}
                  theme={contentTheme}
                />
              ) : null}
            </div>
          ) : view === "dashboard" ? (
            <div className="h-full overflow-y-auto pb-20 md:pb-24">
              <div className="space-y-3" style={{ padding: "12px" }}>
                {/* GEKLEURDE HEADER — vol breedte, zoals andere pagina's */}
                {(() => {
                  const logoColor = C.Lagoon; // dagbord-opmaak: lichte kaart
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const cur = new Date(currentDay);
                  cur.setHours(0, 0, 0, 0);
                  const isToday = cur.getTime() === today.getTime();
                  const dateLabel = isToday
                    ? "Vandaag"
                    : cur.toLocaleDateString("nl-NL", {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      });
                  const isoWeek = (() => {
                    const d = new Date(
                      Date.UTC(cur.getFullYear(), cur.getMonth(), cur.getDate())
                    );
                    const dn = d.getUTCDay() || 7;
                    d.setUTCDate(d.getUTCDate() + 4 - dn);
                    const ys = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
                    return Math.ceil(((d - ys) / 86400000 + 1) / 7);
                  })();
                  const arrowCls =
                    "w-11 h-11 rounded-xl flex items-center justify-center flex-none transition-all active:scale-95";
                  const arrowStyle = {
                    backgroundColor: C.PineLight,
                    color: C.Pine,
                  };
                  return (
                    <div className="relative overflow-hidden rounded-2xl shadow-sm bg-white border border-gray-100">
                      {false && (
                        <div
                          className="absolute inset-0 pointer-events-none"
                          style={{
                            backgroundImage: `url(${
                              PATTERN_BACKGROUNDS[currentTheme?.id]
                            })`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            opacity: 0.1,
                          }}
                        />
                      )}
                      <div className="relative z-10 px-4 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                        {/* Avatar + groet (+ weer naast groet op mobiel) */}
                        <div className="flex items-center gap-2.5 min-w-0 w-full sm:w-auto sm:flex-1">
                          <ProfileAvatar
                            name={loggedInUserName}
                            staffList={appData.staffList || []}
                            size={36}
                            color={logoColor}
                          />
                          <div className="min-w-0 flex-1">
                            <div
                              className="text-base font-semibold leading-tight truncate"
                              style={{ color: C.Lagoon }}
                            >
                              {greeting}, {loggedInUserName.split(" ")[0]}!
                            </div>
                            <div
                              className="text-xs truncate text-gray-500"
                            >
                              {currentDay.toLocaleDateString("nl-NL", {
                                weekday: "long",
                                day: "numeric",
                                month: "long",
                              })}
                            </div>
                          </div>
                          {/* Weer — mobiel, naast de groet */}
                          <div
                            className="sm:hidden flex items-center gap-1.5 flex-shrink-0 px-3 h-11 rounded-xl ml-auto"
                            style={{ backgroundColor: C.PineLight }}
                          >
                            <WeatherIcon
                              size={14}
                              style={{ color: C.Pine }}
                            />
                            <span
                              className="font-semibold text-sm"
                              style={{ color: C.Pine }}
                            >
                              {weather.temp}°
                            </span>
                          </div>
                        </div>
                        {/* Datumkiezer — groeit mee */}
                        <div
                          className="flex items-center gap-1 rounded-2xl p-1 w-full sm:flex-none sm:w-auto"
                          style={{ backgroundColor: "#F9FAFB" }}
                        >
                          <button
                            className={arrowCls}
                            style={arrowStyle}
                            onClick={prevDay}
                            aria-label="Vorige dag"
                          >
                            <ChevronLeft size={18} />
                          </button>
                          <button
                            className="flex-1 sm:flex-none sm:px-4 h-11 rounded-xl text-sm font-semibold transition-all active:scale-95 flex items-center justify-center gap-1.5"
                            style={{
                              backgroundColor: C.PineLight,
                              color: C.Pine,
                            }}
                            onClick={() => {
                              const d = new Date();
                              d.setHours(0, 0, 0, 0);
                              setCurrentDay(d);
                            }}
                          >
                            <span className="capitalize">{dateLabel}</span>
                            <span
                              className="text-xs font-semibold px-2 py-0.5 rounded-full leading-none tabular-nums"
                              style={{ backgroundColor: "white" }}
                            >
                              wk {isoWeek}
                            </span>
                          </button>
                          <button
                            className={arrowCls}
                            style={arrowStyle}
                            onClick={nextDay}
                            aria-label="Volgende dag"
                          >
                            <ChevronRight size={18} />
                          </button>
                          <button
                            className={arrowCls}
                            style={arrowStyle}
                            onClick={() => setIsCalendarOpen(true)}
                            aria-label="Kalender"
                          >
                            <CalendarIcon size={16} />
                          </button>
                        </div>
                        {/* Weer — desktop, rechts */}
                        <div
                          className="hidden sm:flex items-center gap-1.5 flex-shrink-0 px-3 h-11 rounded-xl"
                          style={{ backgroundColor: C.PineLight }}
                        >
                          <WeatherIcon size={14} style={{ color: C.Pine }} />
                          <span
                            className="font-semibold text-sm"
                            style={{ color: C.Pine }}
                          >
                            {weather.temp}°
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* ═══════════════════════════════════════════════════
                    DASHBOARD GRID — 3 kolommen, drag & drop, resize
                    ═══════════════════════════════════════════════════ */}
                <DashboardContent
                  loggedInUserName={loggedInUserName}
                  appData={appDataWeergave}
                  saveData={saveData}
                  currentTheme={contentTheme}
                  currentDay={currentDay}
                  setView={setView}
                  setCurrentDay={setCurrentDay}
                  handoverUnreadCount={handoverUnreadCount}
                  todaysSchedule={todaysSchedule}
                  entTasks={entTasks}
                  creaTasks={creaTasks}
                  extraTasks={extraTasks}
                  hotspotTasks={hotspotTasks}
                  programExpanded={programExpanded}
                  setProgramExpanded={setProgramExpanded}
                  userRole={userRole}
                  triggerAlert={triggerAlert}
                  triggerConfirm={triggerConfirm}
                  AISummaryWidgetComponent={AISummaryWidget}
                  setAdminSubView={setAdminSubView}
                />
              </div>
            </div>
          ) : view === "schedule" ? (
            <ProgrammaView
              {...{ appData, contentTheme, currentDay, loggedInUserName, nextDay, prevDay, setCurrentDay, setIsCalendarOpen, setView, userRole }}
            />
          ) : view === "occupancy" && userRole !== "co" ? (
            <div className="w-full max-w-7xl mx-auto overflow-y-auto p-4 md:p-0 pb-24">
              <div className="pb-4 mb-4">
                <ViewHeader
                  title="Bezetting"
                  subtitle={toDutchDate(currentDay)}
                  icon={IcBezetting}
                  theme={contentTheme}
                >
                  <DateControls
                    currentDay={currentDay}
                    onPrev={prevDay}
                    onNext={nextDay}
                    onCalendar={() => setIsCalendarOpen(true)}
                    theme={contentTheme}
                  />
                </ViewHeader>
              </div>
              <OccupancyView
                dailyStats={appData.dailyStats}
                currentDay={currentDay}
                theme={contentTheme}
                setCurrentDay={setCurrentDay}
              />
            </div>
          ) : view === "dagbord" && userRole !== "co" ? (
            <div className="w-full max-w-7xl mx-auto overflow-y-auto p-4 md:p-0 pb-6 lg:pb-24">
              <div className="pb-3 mb-3 hidden lg:block">
                <ViewHeader
                  title="Dagbord"
                  subtitle={toDutchDate(currentDay)}
                  icon={IcDagbord}
                  theme={contentTheme}
                >
                  <DateControls
                    currentDay={currentDay}
                    onPrev={prevDay}
                    onNext={nextDay}
                    onCalendar={() => setIsCalendarOpen(true)}
                    theme={contentTheme}
                  />
                </ViewHeader>
              </div>
              <DagbordView
                currentDay={currentDay}
                appData={appDataWeergave}
                updateData={saveData}
                loggedInUserName={loggedInUserName}
                userRole={userRole}
                getDailyTasks={getDailyTasks}
                onCheckTask={handleCheckTask}
                onNvtTask={handleNvtTask}
                onPrev={prevDay}
                onNext={nextDay}
                onCalendar={() => setIsCalendarOpen(true)}
                chatUnreadCount={chatUnreadCount}
                onOpenChat={() => setView("chat")}
                onOpenFeestdagen={() => setView("feestdagen")}
                onOpenAfscheid={() => setView("afscheid")}
                onSetDay={(d) => setCurrentDay(d)}
              />
            </div>
          ) : view === "handover" && userRole !== "co" ? (
            <div className="w-full max-w-7xl mx-auto overflow-y-auto p-4 md:p-0 pb-24">
              <div className="pb-3 mb-3">
                <ViewHeader
                  title="Overdracht (archief)"
                  subtitle={toDutchDate(currentDay)}
                  icon={IcChat}
                  theme={contentTheme}
                >
                  <DateControls
                    currentDay={currentDay}
                    onPrev={prevDay}
                    onNext={nextDay}
                    onCalendar={() => setIsCalendarOpen(true)}
                    theme={contentTheme}
                  />
                </ViewHeader>
              </div>
              <HandoverView
                currentDay={currentDay}
                appData={appDataWeergave}
                updateData={saveData}
                loggedInUserName={loggedInUserName}
                userRole={userRole}
                theme={contentTheme}
                setView={setView}
                setCurrentDay={setCurrentDay}
                pendingTab={pendingHandoverTab}
                onClearPendingTab={() => setPendingHandoverTab(null)}
              />
            </div>
          ) : view === "checks" && userRole !== "co" ? (
            <TakenlijstView
              {...{ activeChecklistTab, appData, contentTheme, copyChecklistTargetDay, creaTasks, currentDay, currentWeekdayId, dayKey, entTasks, extraTasks, groupedCreaTasks, groupedEntTasks, groupedExtraTasks, groupedHotspotTasks, handleCopyChecklistToDay, hotspotTasks, isCopyChecklistModalOpen, loggedInUserName, nextDay, prevDay, renderGroupedTasks, saveData, setActiveChecklistTab, setCopyChecklistTargetDay, setIsCalendarOpen, setIsCopyChecklistModalOpen, setShowCompletedTasks, setSkCheckInput, showCompletedTasks, skCheckInput, triggerAlert, triggerConfirm, userRole }}
            />
          ) : view === "hours" && userRole !== "co" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <UserHoursView
                setView={setView}
                updateData={saveData}
                appData={appDataWeergave}
                userRole={userRole}
                loggedInUserName={loggedInUserName}
                showAlert={triggerAlert}
                theme={contentTheme}
              />
            </div>
          ) : view === "orders" && userRole !== "co" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <UserOrderView
                setView={setView}
                updateData={saveData}
                appData={appDataWeergave}
                loggedInUserName={loggedInUserName}
                userRole={userRole}
                showConfirm={showConfirm}
                theme={contentTheme}
              />
            </div>
          ) : view === "springkussen" && userRole !== "co" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <SpringkussenView
                currentDay={currentDay}
                appData={appDataWeergave}
                updateData={saveData}
                userRole={userRole}
                loggedInUserName={loggedInUserName}
                showAlert={triggerAlert}
                showConfirm={triggerConfirm}
                theme={contentTheme}
                onPrev={prevDay}
                onNext={nextDay}
                onCalendar={() => setIsCalendarOpen(true)}
              />
            </div>
          ) : view === "handboek" && userRole !== "co" ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <HandboekView
                appData={appDataWeergave}
                updateData={saveData}
                userRole={userRole}
                showAlert={triggerAlert}
                showConfirm={triggerConfirm}
                theme={contentTheme}
              />
            </div>
          ) : view === "promo_screen" && promoBlijft ? null : view === "promo_screen" &&
            (isExternalPromoMode ||
              userRole === "fm" ||
              userRole === "admin") ? (
            <PromoScreenView
              appData={appDataWeergave}
              currentDay={currentDay}
              theme={currentTheme}
              updateData={saveData}
              showAlert={triggerAlert}
              isLoggedIn={isLoggedIn}
              userRole={userRole}
              loggedInUserName={loggedInUserName}
              onTerug={() => setView("dagbord")}
            />
          ) : view === "promo_bediening" && (userRole === "fm" || userRole === "admin") ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <PromoBediening theme={contentTheme} showAlert={triggerAlert} setView={setView} />
            </div>
          ) : view === "promo_designer" &&
            (userRole === "fm" || userRole === "admin") ? (
            <PromoDesigner
              theme={currentTheme}
              showAlert={triggerAlert}
              appData={appDataWeergave}
            />
          ) : view === "dj_night" &&
            (userRole === "staff" ||
              userRole === "fm" ||
              userRole === "admin") ? (
            <div className="h-full overflow-y-auto p-4 md:p-0">
              <DJNightTool
                theme={currentTheme}
                showAlert={triggerAlert}
                db={db}
                appId={appId}
                userRole={userRole}
                loggedInUserName={loggedInUserName}
              />
            </div>
          ) : null}

          {isCalendarOpen && (
            <CalendarModal
              currentDay={currentDay}
              onSelect={setCurrentDay}
              onClose={() => setIsCalendarOpen(false)}
              theme={contentTheme}
            />
          )}
          </div>
        </main>
      </div>
    </div>
  );
}

// ── ErrorBoundary ─────────────────────────────────────────────
// Vangt onverwachte render-fouten op zodat één kapotte widget niet
// de hele app wit/leeg maakt. Toont een herstelscherm i.p.v. een crash.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Oude versie die een nieuw onderdeel niet kan vinden: gewoon herladen
    if (isVersieFout(error)) herlaadVoorNieuweVersie();
    console.error(
      "App crash opgevangen door ErrorBoundary:",
      error,
      errorInfo
    );
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: C.Bg,
            fontFamily: "system-ui, sans-serif",
            padding: 24,
          }}
        >
          <div
            style={{
              maxWidth: 420,
              width: "100%",
              background: C.White,
              borderRadius: 20,
              padding: "32px 28px",
              textAlign: "center",
              boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
            }}
          >
            <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
            <h1
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: C.Pine,
                marginBottom: 8,
              }}
            >
              Er ging iets mis
            </h1>
            <p
              style={{
                fontSize: 14,
                color: C.Lagoon,
                opacity: 0.75,
                marginBottom: 20,
                lineHeight: 1.5,
              }}
            >
              De app kwam een onverwachte fout tegen. Je data is veilig
              opgeslagen — een herlaad lost dit meestal op.
            </p>
            {this.state.error && (
              <p style={{ fontSize: 11, color: C.Lagoon, opacity: 0.55, marginBottom: 16, wordBreak: "break-word" }}>
                {String(this.state.error?.message || this.state.error).slice(0, 160)}
              </p>
            )}
            <button
              onClick={this.handleReload}
              style={{
                background: C.Pine,
                color: C.White,
                border: "none",
                borderRadius: 999,
                padding: "10px 24px",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Opnieuw laden
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  // Terug van Spotify-inloggen (DJ-account koppelen)
  if (new URLSearchParams(window.location.search).get("djkoppel")) {
    return (
      <ErrorBoundary>
        <DJSpotifyKoppel />
      </ErrorBoundary>
    );
  }
  // DJ Night-gastscherm (beamer/tv): los venster met QR, battle, quiz en ranking
  if (new URLSearchParams(window.location.search).get("djscherm")) {
    return (
      <ErrorBoundary>
        <DJSchermView />
      </ErrorBoundary>
    );
  }
  // DJ Night-gasten: alleen de gastenpagina, de app zelf (en zijn data) wordt niet geladen
  if (isDjGastAdres() || new URLSearchParams(window.location.search).get("dj")) {
    return (
      <ErrorBoundary>
        <DJGuestView />
      </ErrorBoundary>
    );
  }
  return (
    <ErrorBoundary>
      <AppInner />
    </ErrorBoundary>
  );
}

// Gedeeld met de losse onderdelen in src/onderdelen/
export {
  creaOpenOp,
  hotspotOpenOp,
  heeftCreaActiviteit,
  stuurWeekPdf,
  weekroosterMail,
  DbStatus,
  dbUploadPhoto,
  dbLoadPhoto,
  auth,
  isDjGastAdres,
  prijsDoorzetten,
  vanafVoor,
  isGratis,
  prijsTekst,
  stuurMail,
  MAIL_AFZENDER,
  mailOpmaak,
  teamVerborgen,
  useConcept,
  ConceptMelding,
  bibPerAfdeling,
  BIB_AFDELINGEN,
  ROOSTER_SOURCE,
  dienstenToShifts,
  publishDienstenPatch,
  openDienstKandidaten,
  openDienstAanbiedenPatch,
  openDienstIntrekkenPatch,
  openDienstOppakken,
  openDienstenVoor,
  HULP_STANDAARD_DAGEN,
  isHulpkracht,
  isHulpkrachtVrijeDag,
  telendeStaf,
  vrijAangevraagdOp,
  CpPil,
  AFD_PIL,
  afdKleurVanTekst,
  DbAfdIcoon,
  IcEntertainment,
  IcCrea,
  IcSpringkussen,
  IcHotspot,
  IcAlles,
  IcPromo,
  IcTaken,
  IcOverdracht,
  ACTIVITY_OPTIONS,
  ACTIVITY_TYPES,
  AVAIL_COLORS,
  AdminContainer,
  AvailBadge,
  BASE_ROLES,
  C,
  CPLogo,
  Card,
  DATA_PROTECTION,
  DateControls,
  HandoverSection,
  LOCATIONS,
  NATIONALITIES,
  PATTERN_BACKGROUNDS,
  PRUNE_DAYS,
  PdfViewerModal,
  ProfileAvatar,
  RM_TYPES,
  SEED_PROFILE_PHOTOS,
  STATIC_FIELDS,
  StyledSelect,
  TASK_CATS,
  THEMES,
  ViewHeader,
  appId,
  calculateAge,
  cpConfirm,
  cpToast,
  db,
  dienstHours,
  dutchDaysShort,
  dutchMonths,
  fetchReportBlob,
  formatHours,
  getAvailStatus,
  getCharColorInfo,
  getFirstName,
  getFridayOfWeek,
  getPersonAvailLabel,
  groupTasksByCategory,
  normalizeFocus,
  offlineManager,
  parseDateKey,
  pruneDataForLiveDoc,
  staffRemovalIntent,
  storage,
  timeToMins,
  toDutchDate,
  toIsoDate,
  toMonthKey,
  lazyView,
  CROWD_TYPE_COLORS,
  extractMentions,
  interpolateCrowdColor,
  CP_LOGO_COLORS,
  EmptyState,
  dbSlug,
  getDagbordAttention,
  isActivityNow,
  isActivityPast,
  sortActivities,
  registerOrderRemoval,
  ActivityItem,
};
